"use client";
import {
  THEME_STORAGE_KEY,
  applyStoredTheme,
  applyTheme,
  getCurrentTheme,
  getStoredTheme,
  prefersDarkScheme,
  resolveTheme,
  setTheme,
  storeTheme,
  subscribeToSystemTheme,
  toggleTheme
} from "./chunk-FJ7RBXL5.mjs";

// src/primitives/Accordion.tsx
import { useId, useState } from "react";
import { ChevronDownIcon } from "lucide-react";

// src/typography/Heading.tsx
import { forwardRef } from "react";

// src/primitives/mergeUtilityClasses.ts
var TEXT_SIZE_NAME = /^(xs|sm|base|lg|xl|[2-9]xl|heading-(hero|page|feature|section|subsection|card|label|compact)(-md|-lg)?|heading-(xl|lg|md|sm|xs)|display-(lg|md|sm)|body-(lg|md|sm|xs)|label-(lg|md|sm|xs)|caption-(lg|md|sm|xs)|code-(lg|md|sm|xs)|stat(-md|-lg)?|lead(-md|-lg|-xl)?)$/;
var FONT_FAMILY_NAME = /^(serif|sans|mono|brand)$/;
var FONT_WEIGHT_NAME = /^(thin|extralight|light|normal|medium|semibold|bold|extrabold|black)$/;
var TEXT_ALIGN_NAME = /^(left|center|right|justify|start|end)$/;
var TEXT_WRAP_NAME = /^(wrap|nowrap|balance|pretty)$/;
var TEXT_OVERFLOW_NAME = /^(ellipsis|clip)$/;
var ARBITRARY_LENGTH = /^\[(length:|-?[\d.]+(px|rem|em|%|vw|vh|ch|ex)?\]$|(clamp|calc|min|max)\()/;
function splitVariant(token) {
  let depth = 0;
  let cut = -1;
  for (let i = 0; i < token.length; i += 1) {
    const ch = token[i];
    if (ch === "[") depth += 1;
    else if (ch === "]") depth = Math.max(0, depth - 1);
    else if (ch === ":" && depth === 0) cut = i;
  }
  const variant = cut >= 0 ? token.slice(0, cut + 1) : "";
  const base = (cut >= 0 ? token.slice(cut + 1) : token).replace(/^!/, "");
  return { variant, base };
}
function groupOf(base) {
  if (base.startsWith("font-")) {
    const name = base.slice(5);
    if (FONT_FAMILY_NAME.test(name)) return "font-family";
    if (FONT_WEIGHT_NAME.test(name)) return "font-weight";
    return null;
  }
  if (base.startsWith("text-")) {
    const name = base.slice(5).split("/")[0];
    if (name === "" || name.startsWith("opacity-")) return null;
    if (TEXT_ALIGN_NAME.test(name)) return "text-align";
    if (TEXT_WRAP_NAME.test(name)) return "text-wrap";
    if (TEXT_OVERFLOW_NAME.test(name)) return "text-overflow";
    if (TEXT_SIZE_NAME.test(name)) return "text-size";
    if (name.startsWith("[")) return ARBITRARY_LENGTH.test(name) ? "text-size" : "text-color";
    return "text-color";
  }
  if (base.startsWith("leading-")) return "leading";
  if (base.startsWith("tracking-")) return "tracking";
  const padding = /^(p|px|py|pt|pr|pb|pl|ps|pe)-/.exec(base);
  if (padding) return padding[1];
  return null;
}
var REPLACES = {
  p: ["p", "px", "py", "pt", "pr", "pb", "pl", "ps", "pe"],
  px: ["px", "pr", "pl", "ps", "pe"],
  py: ["py", "pt", "pb"]
};
function tokens(value) {
  return (value ?? "").split(/\s+/).filter(Boolean);
}
function mergeUtilityClasses(defaults2, overrides) {
  const overrideTokens = tokens(overrides);
  if (overrideTokens.length === 0) return tokens(defaults2).join(" ");
  const replaced = /* @__PURE__ */ new Set();
  for (const token of overrideTokens) {
    const { variant, base } = splitVariant(token);
    const group = groupOf(base);
    if (!group) continue;
    for (const target of REPLACES[group] ?? [group]) replaced.add(`${variant}${target}`);
  }
  const kept = tokens(defaults2).filter((token) => {
    const { variant, base } = splitVariant(token);
    const group = groupOf(base);
    return !group || !replaced.has(`${variant}${group}`);
  });
  return [...kept, ...overrideTokens].join(" ");
}

// src/typography/Heading.tsx
import { jsx } from "react/jsx-runtime";
var SIZE_CLASSES = {
  hero: "text-heading-hero sm:text-heading-hero-md lg:text-heading-hero-lg",
  page: "text-heading-page sm:text-heading-page-md lg:text-heading-page-lg",
  feature: "text-heading-feature sm:text-heading-feature-md lg:text-heading-feature-lg",
  section: "text-heading-section sm:text-heading-section-md lg:text-heading-section-lg",
  subsection: "text-heading-subsection sm:text-heading-subsection-md lg:text-heading-subsection-lg",
  card: "text-heading-card sm:text-heading-card-md lg:text-heading-card-lg",
  label: "text-heading-label sm:text-heading-label-md lg:text-heading-label-lg",
  compact: "text-heading-compact sm:text-heading-compact-md lg:text-heading-compact-lg"
};
var DEFAULT_SIZE_BY_LEVEL = {
  1: "page",
  2: "section",
  3: "card",
  4: "label",
  5: "compact",
  6: "compact"
};
var FONT_FAMILY_BY_LEVEL = {
  1: "font-serif",
  2: "font-serif",
  3: "font-sans",
  4: "font-sans",
  5: "font-sans",
  6: "font-sans"
};
var TRACKING_BY_LEVEL = {
  1: "tracking-headline-serif",
  2: "tracking-headline-serif",
  3: "tracking-headline-sans",
  4: "tracking-headline-sans",
  5: "tracking-headline-sans",
  6: "tracking-headline-sans"
};
var DEFAULT_WEIGHT_BY_LEVEL = {
  1: "semibold",
  2: "semibold",
  3: "semibold",
  4: "semibold",
  5: "semibold",
  6: "semibold"
};
var WEIGHT_CLASSES = {
  normal: "font-normal",
  semibold: "font-semibold"
};
var COLOR_CLASSES = {
  primary: "text-primary",
  secondary: "text-secondary",
  tertiary: "text-tertiary",
  accent: "text-accent",
  inherit: ""
};
var Heading = forwardRef(
  function Heading2({
    level,
    as,
    size,
    weight,
    color = "primary",
    className = "",
    children,
    ...props
  }, ref) {
    const styleLevel = as ?? level;
    const resolvedSize = size ?? DEFAULT_SIZE_BY_LEVEL[styleLevel];
    const resolvedWeight = weight ?? DEFAULT_WEIGHT_BY_LEVEL[styleLevel];
    const classes = mergeUtilityClasses(
      [
        SIZE_CLASSES[resolvedSize],
        FONT_FAMILY_BY_LEVEL[styleLevel],
        TRACKING_BY_LEVEL[styleLevel],
        WEIGHT_CLASSES[resolvedWeight],
        COLOR_CLASSES[color]
      ].filter(Boolean).join(" "),
      className
    );
    const Tag3 = `h${level}`;
    const ariaProps = as && as !== level ? { "aria-level": level } : {};
    return /* @__PURE__ */ jsx(Tag3, { ref, className: classes, ...ariaProps, ...props, children });
  }
);

// src/primitives/Accordion.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
function Accordion({ children, className = "" }) {
  return /* @__PURE__ */ jsx2(
    "div",
    {
      className: mergeUtilityClasses(
        "bg-surface border border-recessed rounded-sm shadow-sm divide-y divide-recessed",
        className
      ),
      children
    }
  );
}
function AccordionItem({
  title,
  meta,
  children,
  defaultOpen = false,
  headingLevel = 3,
  id,
  panelClassName = ""
}) {
  const generatedId = useId();
  const baseId = id ?? `accordion${generatedId.replace(/:/g, "")}`;
  const buttonId = `${baseId}-button`;
  const panelId = `${baseId}-panel`;
  const [open, setOpen] = useState(defaultOpen);
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx2(Heading, { level: headingLevel, size: "card", className: "m-0", children: /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        id: buttonId,
        "aria-expanded": open,
        "aria-controls": panelId,
        onClick: () => setOpen((prev) => !prev),
        className: "flex w-full items-center gap-4 px-6 py-4 text-left transition-colors hover:bg-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-selection",
        children: [
          /* @__PURE__ */ jsxs("span", { className: "flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1", children: [
            /* @__PURE__ */ jsx2("span", { children: title }),
            meta !== void 0 && meta !== null && meta !== false && /* @__PURE__ */ jsx2("span", { className: "flex flex-wrap items-center gap-2 font-sans text-sm font-normal text-tertiary", children: meta })
          ] }),
          /* @__PURE__ */ jsx2(
            ChevronDownIcon,
            {
              "aria-hidden": "true",
              className: `h-4 w-4 shrink-0 text-tertiary transition-transform ${open ? "rotate-180" : ""}`
            }
          )
        ]
      }
    ) }),
    /* @__PURE__ */ jsx2("div", { id: panelId, role: "region", "aria-labelledby": buttonId, hidden: !open, children: /* @__PURE__ */ jsx2("div", { className: panelClassName, children }) })
  ] });
}

// src/primitives/Alert.tsx
import { CheckIcon, AlertCircleIcon, XCircleIcon } from "lucide-react";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
function Alert({
  type,
  title,
  children,
  className = "",
  headingLevel,
  role
}) {
  const styles = {
    success: {
      bg: "bg-growth/10",
      border: "border-growth",
      icon: CheckIcon,
      iconColor: "text-growth"
    },
    warning: {
      bg: "bg-warning/10",
      border: "border-warning",
      icon: AlertCircleIcon,
      iconColor: "text-warning"
    },
    error: {
      bg: "bg-error-bg",
      border: "border-error-bright",
      icon: XCircleIcon,
      iconColor: "text-error-bright"
    }
  };
  const style = styles[type];
  const Icon = style.icon;
  const TitleTag = headingLevel ? `h${headingLevel}` : "p";
  return /* @__PURE__ */ jsx3("div", { role, className: `${style.bg} ${style.border} border-l-4 p-4 rounded-sm ${className}`, children: /* @__PURE__ */ jsxs2("div", { className: "flex items-start gap-3", children: [
    /* @__PURE__ */ jsx3(Icon, { "aria-hidden": "true", className: `w-5 h-5 ${style.iconColor} flex-shrink-0 mt-0.5` }),
    /* @__PURE__ */ jsxs2("div", { children: [
      title && /* @__PURE__ */ jsx3(TitleTag, { className: "text-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-1", children: title }),
      /* @__PURE__ */ jsx3("div", { className: "text-sm font-sans text-secondary", children })
    ] })
  ] }) });
}

// src/primitives/AspectRatio.tsx
import clsx from "clsx";
import { jsx as jsx4 } from "react/jsx-runtime";
function AspectRatio({
  ratio = 1,
  children,
  className
}) {
  return /* @__PURE__ */ jsx4(
    "div",
    {
      className: clsx("relative w-full", className),
      style: {
        paddingBottom: `${100 / ratio}%`
      },
      children: /* @__PURE__ */ jsx4("div", { className: "absolute inset-0 w-full h-full", children })
    }
  );
}

// src/primitives/BackLink.tsx
import { forwardRef as forwardRef2 } from "react";
import { ArrowLeftIcon } from "lucide-react";
import { Fragment, jsx as jsx5, jsxs as jsxs3 } from "react/jsx-runtime";
var BackLink = forwardRef2(
  function BackLink2({ href, children, LinkComponent, className = "" }, ref) {
    const classes = [
      "inline-flex items-center gap-2",
      "text-sm font-sans text-tertiary",
      "hover:text-primary transition-colors",
      className
    ].filter(Boolean).join(" ");
    const content = /* @__PURE__ */ jsxs3(Fragment, { children: [
      /* @__PURE__ */ jsx5(ArrowLeftIcon, { className: "w-4 h-4" }),
      children
    ] });
    if (LinkComponent) {
      return /* @__PURE__ */ jsx5(LinkComponent, { href, className: classes, children: content });
    }
    return /* @__PURE__ */ jsx5("a", { ref, href, className: classes, children: content });
  }
);

// src/primitives/Badge.tsx
import { jsxs as jsxs4 } from "react/jsx-runtime";
function Badge({
  children,
  variant = "neutral",
  size = "medium",
  icon,
  className = ""
}) {
  const normalizedSize = size === "sm" ? "small" : size;
  const baseStyles = "inline-flex items-center justify-center gap-1 font-sans font-semibold rounded-sm";
  const variantStyles = {
    success: "bg-growth/10 text-growth",
    warning: "bg-warning/10 text-warning-text",
    error: "bg-error/10 text-error",
    info: "bg-secondary/10 text-secondary",
    neutral: "bg-tertiary/10 text-tertiary"
  };
  const sizeStyles = {
    small: "text-xs px-2 py-0.5",
    medium: "text-xs px-3 py-1"
  };
  const combinedStyles = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[normalizedSize]} ${className}`;
  return /* @__PURE__ */ jsxs4("span", { className: combinedStyles, children: [
    icon,
    children
  ] });
}

// src/primitives/BuildVersion.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
function formatBuildId(buildInfo, format) {
  const { version, timestamp } = buildInfo;
  if (format === "short" || !timestamp) {
    return `v${version}`;
  }
  return `v${version}-${timestamp}`;
}
function BuildVersion({
  buildInfo,
  format = "full",
  className = "",
  subtle = false
}) {
  const displayText = formatBuildId(buildInfo, format);
  const baseClasses = subtle ? "text-xs font-mono text-tertiary/60" : "text-xs font-mono text-tertiary";
  return /* @__PURE__ */ jsx6(
    "span",
    {
      className: `${baseClasses} ${className}`,
      title: `Build: ${displayText}`,
      children: displayText
    }
  );
}

// src/primitives/Button.tsx
import { jsx as jsx7, jsxs as jsxs5 } from "react/jsx-runtime";
function Button({
  children,
  variant = "primary",
  size = "medium",
  icon: Icon,
  iconPosition = "right",
  disabled = false,
  fullWidth = false,
  onClick,
  type = "button",
  className = "",
  ...forwarded
}) {
  const normalizedVariant = variant === "ghost" ? "tertiary" : variant;
  const normalizedSize = size === "sm" ? "small" : size;
  const baseStyles = "inline-flex items-center justify-center gap-2 whitespace-nowrap font-sans font-semibold transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed";
  const variantStyles = {
    primary: "bg-[rgb(var(--color-accent-fill))] text-white hover:bg-[rgb(var(--color-accent-fill-hover))] shadow-sm hover:shadow-md",
    secondary: "bg-surface text-[rgb(var(--color-accent-text))] border-2 border-solid border-[rgb(var(--color-accent-text))] hover:bg-elevated",
    tertiary: "bg-transparent text-[rgb(var(--color-accent-text))] hover:underline"
  };
  const sizeStyles = {
    small: "text-sm py-2 px-4 rounded-sm h-8",
    medium: "text-sm py-3 px-6 rounded-sm h-10",
    large: "text-base py-4 px-8 rounded-sm h-12"
  };
  const widthStyles = fullWidth ? "w-full" : "";
  const combinedStyles = `${baseStyles} ${variantStyles[normalizedVariant]} ${sizeStyles[normalizedSize]} ${widthStyles} ${className}`;
  return /* @__PURE__ */ jsxs5("button", { ...forwarded, type, onClick, disabled, className: combinedStyles, children: [
    Icon && iconPosition === "left" && /* @__PURE__ */ jsx7(Icon, { className: "w-4 h-4" }),
    children,
    Icon && iconPosition === "right" && /* @__PURE__ */ jsx7(Icon, { className: "w-4 h-4" })
  ] });
}

// src/primitives/ButtonGroup.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
function ButtonGroup({
  children,
  equalWidth = false,
  align = "start",
  wrap = false,
  gap = "sm",
  className = ""
}) {
  const gapStyles = gap === "md" ? "gap-3" : "gap-2";
  if (equalWidth) {
    return /* @__PURE__ */ jsx8("div", { className: `inline-grid grid-flow-col auto-cols-fr w-fit items-center ${gapStyles} ${className}`, children });
  }
  const alignStyles = {
    start: "justify-start",
    end: "justify-end",
    between: "justify-between"
  };
  return /* @__PURE__ */ jsx8("div", { className: `flex items-center ${wrap ? "flex-wrap" : ""} ${alignStyles[align]} ${gapStyles} ${className}`, children });
}

// src/primitives/Card.tsx
import {
  LinkIcon,
  BrainIcon,
  ShieldCheckIcon,
  BanknoteIcon,
  PieChartIcon,
  CheckCircleIcon,
  RocketIcon,
  SettingsIcon,
  UsersIcon,
  LockIcon,
  ZapIcon,
  TrendingUpIcon,
  DatabaseIcon,
  GlobeIcon,
  CodeIcon,
  LayersIcon
} from "lucide-react";
import { Fragment as Fragment2, jsx as jsx9, jsxs as jsxs6 } from "react/jsx-runtime";
var ICON_REGISTRY = {
  link: LinkIcon,
  brain: BrainIcon,
  "shield-check": ShieldCheckIcon,
  banknote: BanknoteIcon,
  "pie-chart": PieChartIcon,
  "check-circle": CheckCircleIcon,
  rocket: RocketIcon,
  settings: SettingsIcon,
  users: UsersIcon,
  lock: LockIcon,
  zap: ZapIcon,
  "trending-up": TrendingUpIcon,
  database: DatabaseIcon,
  globe: GlobeIcon,
  code: CodeIcon,
  layers: LayersIcon
};
function Card({
  children,
  variant = "default",
  title,
  description,
  icon: IconProp,
  iconName,
  iconStyle = "accent",
  accentStyle = "none",
  hoverable = false,
  onClick,
  className = ""
}) {
  const Icon = iconName ? ICON_REGISTRY[iconName] : IconProp;
  const resolvedAccentStyle = accentStyle;
  const needsOverflow = ["top", "corner", "top-warm", "top-corner", "top-border"].includes(resolvedAccentStyle);
  const baseStyles = `rounded-sm shadow-sm transition-all duration-200 ${needsOverflow ? "overflow-hidden relative" : ""}`;
  const accentBgStyles = {
    // Default: surface with subtle border
    none: "bg-surface border border-recessed",
    // Top accent bar: surface background
    top: "bg-surface border border-recessed",
    // Corner triangle: surface with subtle border
    corner: "bg-surface border border-recessed",
    // Warm tint: elevated warm background with tan border
    tint: "bg-elevated-warm border border-tan-warm",
    // Inset shadow (Bloomberg): surface with inset accent ring + neutral drop shadow.
    // design-token-skip: 0_1px_3px_rgba(0,0,0,0.1) is the canonical boxShadow.sm
    // black-alpha drop shadow; no CSS-variable equivalent exists for neutral shadows.
    inset: "bg-surface shadow-[inset_0_0_0_1px_rgb(var(--color-accent)/0.15),0_1px_3px_rgba(0,0,0,0.1)]",
    // Top-warm: elevated warm background
    "top-warm": "bg-elevated-warm border border-recessed",
    // Top-corner (RECOMMENDED): elevated warm background
    "top-corner": "bg-elevated-warm",
    // Top-border: surface with warm tan border
    "top-border": "border-2 border-tan-warm"
  };
  const bgStyles = accentBgStyles[resolvedAccentStyle];
  const variantStyles = {
    default: "p-6",
    feature: "p-6 sm:p-8",
    metric: "p-6",
    highlighted: "p-6"
  };
  const hoverStyles = hoverable ? "hover:shadow-md hover:-translate-y-0.5 cursor-pointer" : "";
  const clickableStyles = onClick ? "cursor-pointer" : "";
  const combinedStyles = mergeUtilityClasses(
    `${baseStyles} ${bgStyles} ${variantStyles[variant]} ${hoverStyles} ${clickableStyles}`,
    className
  );
  const iconCircleStyles = {
    accent: {
      circle: "bg-accent/10",
      icon: "text-accent"
    },
    neutral: {
      circle: "bg-canvas",
      icon: "text-tertiary"
    },
    muted: {
      circle: "bg-transparent",
      icon: "text-tertiary"
    }
  };
  const currentIconStyle = iconCircleStyles[iconStyle];
  const handleKeyDown = onClick ? (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  } : void 0;
  return /* @__PURE__ */ jsxs6(
    "div",
    {
      className: combinedStyles,
      onClick,
      onKeyDown: handleKeyDown,
      role: onClick ? "button" : void 0,
      tabIndex: onClick ? 0 : void 0,
      children: [
        resolvedAccentStyle === "top" && /* @__PURE__ */ jsx9("div", { className: "absolute top-0 left-0 right-0 h-1 bg-accent" }),
        (resolvedAccentStyle === "top-warm" || resolvedAccentStyle === "top-border") && /* @__PURE__ */ jsx9("div", { className: "absolute top-0 left-0 right-0 h-[1px] bg-accent" }),
        resolvedAccentStyle === "top-corner" && /* @__PURE__ */ jsxs6(Fragment2, { children: [
          /* @__PURE__ */ jsx9("div", { className: "absolute top-0 left-0 right-0 h-[2px] bg-tan-dark dark:bg-secondary" }),
          /* @__PURE__ */ jsx9("div", { className: "absolute top-[2px] left-0 w-[2px] h-4 bg-tan-dark dark:bg-secondary" })
        ] }),
        resolvedAccentStyle === "corner" && /* @__PURE__ */ jsx9("div", { className: "absolute top-0 right-0 w-8 h-8 overflow-hidden", children: /* @__PURE__ */ jsx9("div", { className: "absolute top-0 right-0 w-12 h-12 bg-accent transform rotate-45 translate-x-6 -translate-y-6" }) }),
        Icon && variant === "feature" && /* @__PURE__ */ jsx9("div", { className: `w-10 h-10 sm:w-12 sm:h-12 ${currentIconStyle.circle} rounded-full flex items-center justify-center mb-4 sm:mb-6`, children: /* @__PURE__ */ jsx9(Icon, { className: `w-5 h-5 sm:w-6 sm:h-6 ${currentIconStyle.icon}`, strokeWidth: 1.5 }) }),
        title && /* @__PURE__ */ jsx9("h3", { className: variant === "metric" ? "text-3xl font-serif font-semibold text-accent mb-2 tabular-nums" : "text-base sm:text-lg font-sans font-semibold text-primary mb-3 leading-normal", children: title }),
        description && /* @__PURE__ */ jsx9("p", { className: variant === "metric" ? "text-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary" : "text-sm sm:text-base font-serif text-secondary mb-4", children: description }),
        children
      ]
    }
  );
}

// src/primitives/ComparisonBox.tsx
import { CheckIcon as CheckIcon2 } from "lucide-react";
import { jsx as jsx10, jsxs as jsxs7 } from "react/jsx-runtime";
function ComparisonBox({
  variant = "default",
  categoryLabel,
  title,
  icon: Icon,
  items,
  listStyle,
  children,
  className = ""
}) {
  const isRecommended = variant === "recommended";
  const resolvedListStyle = listStyle ?? (isRecommended ? "check" : "bullet");
  const baseStyles = "bg-elevated-warm border border-tan-warm rounded-sm p-8 lg:p-10 shadow-sm";
  const hoverStyles = isRecommended ? "hover:bg-surface hover:shadow-md transition-all duration-300" : "hover:shadow-md transition-shadow";
  const recommendedStyles = isRecommended ? "relative overflow-hidden" : "";
  const categoryColor = isRecommended ? "text-accent" : "text-tertiary";
  const iconContainerStyles = isRecommended ? "w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center border border-accent/30" : "w-14 h-14 bg-canvas-warm rounded-full flex items-center justify-center border border-tan-warm";
  const iconColor = isRecommended ? "text-accent" : "text-primary";
  return /* @__PURE__ */ jsxs7("div", { className: `${baseStyles} ${hoverStyles} ${recommendedStyles} ${className}`, children: [
    isRecommended && /* @__PURE__ */ jsx10("div", { className: "absolute top-0 right-0 w-16 h-16 overflow-hidden", children: /* @__PURE__ */ jsx10("div", { className: "absolute top-0 right-0 w-24 h-24 bg-accent transform rotate-45 translate-x-12 -translate-y-12" }) }),
    /* @__PURE__ */ jsxs7("div", { className: "flex items-center gap-4 mb-8", children: [
      Icon && /* @__PURE__ */ jsx10("div", { className: iconContainerStyles, children: /* @__PURE__ */ jsx10(Icon, { className: `w-7 h-7 ${iconColor}`, strokeWidth: 1.5 }) }),
      /* @__PURE__ */ jsxs7("div", { children: [
        categoryLabel && /* @__PURE__ */ jsx10("div", { className: `text-label-xs font-sans font-semibold uppercase ${categoryColor} mb-1`, children: categoryLabel }),
        /* @__PURE__ */ jsx10("h4", { className: "text-2xl font-sans font-semibold text-primary", children: title })
      ] })
    ] }),
    items && items.length > 0 && /* @__PURE__ */ jsx10("ul", { className: "space-y-4", children: items.map((item, i) => /* @__PURE__ */ jsxs7("li", { className: "flex items-start gap-3", children: [
      resolvedListStyle === "check" ? /* @__PURE__ */ jsx10(CheckIcon2, { className: "w-5 h-5 text-accent flex-shrink-0 mt-0.5", strokeWidth: 2 }) : /* @__PURE__ */ jsx10("div", { className: "w-1.5 h-1.5 rounded-full bg-primary mt-2.5 flex-shrink-0" }),
      /* @__PURE__ */ jsx10("span", { className: "text-base font-serif text-secondary leading-relaxed", children: item })
    ] }, i)) }),
    children
  ] });
}
function ComparisonBoxPair({
  children,
  className = ""
}) {
  return /* @__PURE__ */ jsx10("div", { className: `grid lg:grid-cols-2 gap-8 ${className}`, children });
}

// src/primitives/Section.tsx
import { forwardRef as forwardRef3 } from "react";
import { jsx as jsx11 } from "react/jsx-runtime";
var SPACING_CLASSES = {
  none: "",
  sm: "py-8 sm:py-12 lg:py-16",
  md: "py-12 sm:py-16 lg:py-20",
  lg: "py-16 sm:py-20 lg:py-24",
  xl: "py-20 sm:py-28 lg:py-32"
};
var BACKGROUND_CLASSES = {
  canvas: "bg-canvas",
  white: "bg-surface",
  muted: "",
  // Intentionally transparent - NO alternating section colors per design system
  transparent: ""
};
var Section = forwardRef3(
  function Section2({
    spacing = "md",
    background = "transparent",
    contained = true,
    className = "",
    children,
    ...props
  }, ref) {
    const sectionClasses = [
      SPACING_CLASSES[spacing],
      BACKGROUND_CLASSES[background],
      className
    ].filter(Boolean).join(" ");
    const content = contained ? /* @__PURE__ */ jsx11("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children }) : children;
    return /* @__PURE__ */ jsx11("section", { ref, className: sectionClasses, ...props, children: content });
  }
);

// src/primitives/SectionLabel.tsx
import { forwardRef as forwardRef4 } from "react";
import { jsx as jsx12 } from "react/jsx-runtime";
var SIZE_CLASSES2 = {
  xs: "text-label-xs",
  sm: "text-label-sm"
};
var COLOR_CLASSES2 = {
  tertiary: "text-tertiary",
  accent: "text-accent",
  inherit: ""
};
var SectionLabel = forwardRef4(
  function SectionLabel2({ size = "xs", color = "tertiary", withMargin = true, className = "", children, ...props }, ref) {
    const classes = [
      "font-sans font-semibold uppercase tracking-wider",
      SIZE_CLASSES2[size],
      COLOR_CLASSES2[color],
      withMargin ? "mb-4" : "",
      className
    ].filter(Boolean).join(" ");
    return /* @__PURE__ */ jsx12("div", { ref, className: classes, ...props, children });
  }
);

// src/primitives/Separator.tsx
import clsx2 from "clsx";
import { jsx as jsx13 } from "react/jsx-runtime";
function Separator({
  orientation = "horizontal",
  decorative = true,
  className,
  color = "default",
  spacing = "none"
}) {
  const colorStyles = {
    default: "bg-recessed",
    accent: "bg-accent",
    muted: "bg-tertiary/30"
  };
  const spacingStyles = {
    none: "",
    sm: orientation === "horizontal" ? "my-2" : "mx-2",
    md: orientation === "horizontal" ? "my-4" : "mx-4",
    lg: orientation === "horizontal" ? "my-8" : "mx-8"
  };
  const orientationStyles = orientation === "horizontal" ? "h-px w-full" : "w-px h-full self-stretch";
  return /* @__PURE__ */ jsx13(
    "div",
    {
      role: decorative ? "none" : "separator",
      "aria-orientation": decorative ? void 0 : orientation,
      "aria-hidden": decorative ? "true" : void 0,
      className: clsx2(
        orientationStyles,
        colorStyles[color],
        spacingStyles[spacing],
        className
      )
    }
  );
}

// src/primitives/Tabs.tsx
import { useEffect, useId as useId2, useRef, useState as useState2 } from "react";
import { Fragment as Fragment3, jsx as jsx14, jsxs as jsxs8 } from "react/jsx-runtime";
var OVERFLOW_FADE = "2rem";
function overflowMask({ start, end }) {
  if (!start && !end) return void 0;
  const head = start ? `transparent, black ${OVERFLOW_FADE}` : "black";
  const tail = end ? `black calc(100% - ${OVERFLOW_FADE}), transparent` : "black";
  return `linear-gradient(to right, ${head}, ${tail})`;
}
function idSegment(value) {
  return value.replace(/[^A-Za-z0-9_-]/g, "-");
}
function getTabIds(tabsId, tabId) {
  const segment = idSegment(tabId);
  return { tabId: `${tabsId}-tab-${segment}`, panelId: `${tabsId}-panel-${segment}` };
}
function Tabs({
  tabs,
  activeTab,
  onTabChange,
  className = "",
  id,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  children,
  panelClassName = "",
  externalPanels = false,
  activation = "automatic"
}) {
  const generatedId = useId2();
  const baseId = id ?? `tabs${generatedId.replace(/:/g, "")}`;
  const tabRefs = useRef([]);
  const listRef = useRef(null);
  const [overflow, setOverflow] = useState2({ start: false, end: false });
  const tabKey = tabs.map((tab) => `${tab.id}:${tab.label}`).join("|");
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const measure = () => {
      const maxScroll = list.scrollWidth - list.clientWidth;
      const scrolled = Math.abs(list.scrollLeft);
      const next = { start: maxScroll > 1 && scrolled > 1, end: maxScroll > 1 && scrolled < maxScroll - 1 };
      setOverflow((prev) => prev.start === next.start && prev.end === next.end ? prev : next);
    };
    measure();
    list.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(list);
    Array.from(list.children).forEach((tab) => observer?.observe(tab));
    return () => {
      list.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, [tabKey]);
  const mask = overflowMask(overflow);
  const overflowAttr = overflow.start && overflow.end ? "both" : overflow.start ? "start" : overflow.end ? "end" : "none";
  const hasInlinePanel = children !== void 0 && children !== null && children !== false;
  const panelLinked = hasInlinePanel || externalPanels;
  const enabledIndexes = tabs.reduce((acc, tab, index) => {
    if (!tab.disabled) acc.push(index);
    return acc;
  }, []);
  const activeIndex = tabs.findIndex((tab) => tab.id === activeTab);
  const focusableIndex = activeIndex >= 0 && !tabs[activeIndex].disabled ? activeIndex : enabledIndexes[0] ?? -1;
  const moveTo = (index) => {
    const target = tabs[index];
    if (!target || target.disabled) return;
    tabRefs.current[index]?.focus();
    if (activation === "automatic" && target.id !== activeTab) onTabChange(target.id);
  };
  const handleKeyDown = (event, index) => {
    if (enabledIndexes.length === 0) return;
    const position = enabledIndexes.indexOf(index);
    let next;
    switch (event.key) {
      case "ArrowRight":
        next = enabledIndexes[(position + 1) % enabledIndexes.length];
        break;
      case "ArrowLeft":
        next = enabledIndexes[(position - 1 + enabledIndexes.length) % enabledIndexes.length];
        break;
      case "Home":
        next = enabledIndexes[0];
        break;
      case "End":
        next = enabledIndexes[enabledIndexes.length - 1];
        break;
      default:
        return;
    }
    event.preventDefault();
    moveTo(next);
  };
  const activeIds = activeIndex >= 0 ? getTabIds(baseId, tabs[activeIndex].id) : null;
  return /* @__PURE__ */ jsxs8(Fragment3, { children: [
    /* @__PURE__ */ jsx14(
      "div",
      {
        ref: listRef,
        role: "tablist",
        "aria-orientation": "horizontal",
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledBy,
        "data-overflow": overflowAttr,
        style: mask ? { maskImage: mask, WebkitMaskImage: mask } : void 0,
        className: `flex overflow-x-auto scroll-px-8 border-b border-recessed ${className}`,
        children: tabs.map((tab, index) => {
          const isActive = tab.id === activeTab;
          const ids = getTabIds(baseId, tab.id);
          return /* @__PURE__ */ jsx14(
            "button",
            {
              ref: (node) => {
                tabRefs.current[index] = node;
              },
              type: "button",
              role: "tab",
              id: ids.tabId,
              "aria-selected": isActive,
              "aria-controls": isActive && panelLinked ? ids.panelId : void 0,
              tabIndex: index === focusableIndex ? 0 : -1,
              onClick: () => !tab.disabled && onTabChange(tab.id),
              onKeyDown: (event) => handleKeyDown(event, index),
              disabled: tab.disabled,
              className: `shrink-0 whitespace-nowrap px-4 py-3 text-sm font-sans border-b-2 border-solid transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-selection ${isActive ? "text-primary font-medium border-selection" : "text-secondary font-medium border-transparent hover:text-primary"} ${tab.disabled ? "opacity-50 cursor-not-allowed" : ""}`,
              children: tab.label
            },
            tab.id
          );
        })
      }
    ),
    hasInlinePanel && activeIds && /* @__PURE__ */ jsx14(
      "div",
      {
        role: "tabpanel",
        id: activeIds.panelId,
        "aria-labelledby": activeIds.tabId,
        tabIndex: 0,
        className: panelClassName,
        children
      }
    )
  ] });
}
function TabPanel({ tabsId, tabId, activeTab, children, className = "" }) {
  if (activeTab !== void 0 && activeTab !== tabId) return null;
  const ids = getTabIds(tabsId, tabId);
  return /* @__PURE__ */ jsx14("div", { role: "tabpanel", id: ids.panelId, "aria-labelledby": ids.tabId, tabIndex: 0, className, children });
}

// src/primitives/Tag.tsx
import { forwardRef as forwardRef5 } from "react";
import { jsx as jsx15 } from "react/jsx-runtime";
var VARIANT_CLASSES = {
  warm: "bg-warm-50 text-secondary",
  neutral: "bg-tertiary/10 text-tertiary",
  accent: "bg-accent/10 text-accent"
};
var Tag = forwardRef5(
  function Tag2({ children, variant = "warm", className = "", ...props }, ref) {
    const classes = [
      "inline-block px-3 py-1",
      "text-sm font-sans",
      "rounded-sm",
      VARIANT_CLASSES[variant],
      className
    ].filter(Boolean).join(" ");
    return /* @__PURE__ */ jsx15("span", { ref, className: classes, ...props, children });
  }
);

// src/primitives/ThemeToggle.tsx
import { useCallback, useEffect as useEffect2, useState as useState3 } from "react";
import { MoonIcon, SunIcon } from "lucide-react";
import { jsx as jsx16 } from "react/jsx-runtime";
var ICON_SIZE = {
  sm: "w-4 h-4",
  md: "w-5 h-5"
};
var PAD = {
  sm: "p-1.5",
  md: "p-2"
};
function ThemeToggle({ size = "md", className = "" }) {
  const [theme, setThemeState] = useState3(
    () => typeof document !== "undefined" && document.documentElement.classList.contains("dark") ? "dark" : "light"
  );
  useEffect2(() => {
    setThemeState(applyStoredTheme());
  }, []);
  useEffect2(() => {
    return subscribeToSystemTheme(() => {
      const stored = getStoredTheme();
      if (stored === null || stored === "system") {
        setThemeState(applyStoredTheme());
      }
    });
  }, []);
  const handleToggle = useCallback(() => {
    setThemeState((current) => applyTheme(current === "dark" ? "light" : "dark"));
  }, []);
  const isDark = theme === "dark";
  const Icon = isDark ? SunIcon : MoonIcon;
  return /* @__PURE__ */ jsx16(
    "button",
    {
      type: "button",
      onClick: handleToggle,
      "aria-pressed": isDark,
      "aria-label": isDark ? "Switch to light theme" : "Switch to dark theme",
      title: isDark ? "Switch to light theme" : "Switch to dark theme",
      className: `${PAD[size]} rounded-sm text-tertiary hover:text-primary hover:bg-elevated transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 ${className}`,
      children: /* @__PURE__ */ jsx16(Icon, { className: ICON_SIZE[size], strokeWidth: 2, "aria-hidden": "true" })
    }
  );
}

// src/primitives/TableLink.tsx
import { forwardRef as forwardRef6 } from "react";
import { jsx as jsx17 } from "react/jsx-runtime";
var TableLink = forwardRef6(
  function TableLink2({ href, children, LinkComponent, className = "", onClick }, ref) {
    const classes = [
      "font-sans font-medium",
      "text-secondary hover:text-primary",
      "underline underline-offset-2 decoration-recessed hover:decoration-secondary",
      "transition-colors cursor-pointer",
      className
    ].filter(Boolean).join(" ");
    if (LinkComponent) {
      return /* @__PURE__ */ jsx17(LinkComponent, { href, className: classes, onClick, children });
    }
    return /* @__PURE__ */ jsx17("a", { ref, href, className: classes, onClick, children });
  }
);

// src/primitives/VisuallyHidden.tsx
import { jsx as jsx18 } from "react/jsx-runtime";
function VisuallyHidden({
  children,
  as: Component = "span",
  ...props
}) {
  return /* @__PURE__ */ jsx18(
    Component,
    {
      ...props,
      style: {
        position: "absolute",
        width: "1px",
        height: "1px",
        padding: 0,
        margin: "-1px",
        overflow: "hidden",
        clip: "rect(0, 0, 0, 0)",
        whiteSpace: "nowrap",
        border: 0
      },
      children
    }
  );
}

// src/primitives/ContentCard.tsx
import { ArrowRightIcon } from "lucide-react";
import { Fragment as Fragment4, jsx as jsx19, jsxs as jsxs9 } from "react/jsx-runtime";
function truncateText(text, maxLength = 100) {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + "...";
}
function ContentCard({
  href,
  category,
  categoryLabel,
  title,
  excerpt,
  author,
  publishedAt,
  featuredImage,
  readMoreText = "Read article",
  onClick,
  LinkComponent,
  ImageComponent,
  className = ""
}) {
  const cardContent = /* @__PURE__ */ jsxs9(Fragment4, { children: [
    featuredImage ? /* @__PURE__ */ jsx19("div", { className: "bg-recessed aspect-video", children: ImageComponent ? /* @__PURE__ */ jsx19(
      ImageComponent,
      {
        src: featuredImage,
        alt: title,
        width: 800,
        height: 450,
        sizes: "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw",
        className: "w-full h-full object-cover"
      }
    ) : /* @__PURE__ */ jsx19(
      "img",
      {
        src: featuredImage,
        alt: title,
        className: "w-full h-full object-cover"
      }
    ) }) : /* @__PURE__ */ jsx19("div", { className: "bg-recessed aspect-video flex items-center justify-center", children: /* @__PURE__ */ jsx19("span", { className: "text-tertiary font-sans text-xs", children: "Image" }) }),
    /* @__PURE__ */ jsxs9("div", { className: "p-6", children: [
      /* @__PURE__ */ jsx19("div", { className: "text-label-xs font-sans font-semibold uppercase text-accent mb-3", children: categoryLabel || category }),
      /* @__PURE__ */ jsx19(
        Heading,
        {
          level: 3,
          size: "label",
          className: "text-lg mb-2 leading-normal group-hover:text-primary transition-colors",
          children: title
        }
      ),
      /* @__PURE__ */ jsx19("p", { className: "text-sm font-serif text-secondary leading-body mb-4", children: truncateText(excerpt, 100) }),
      (author || publishedAt) && /* @__PURE__ */ jsxs9("div", { className: "flex items-center gap-4 text-xs font-sans text-tertiary mb-4", children: [
        author && /* @__PURE__ */ jsx19("span", { children: author }),
        author && publishedAt && /* @__PURE__ */ jsx19("span", { children: "-" }),
        publishedAt && /* @__PURE__ */ jsx19("span", { children: publishedAt })
      ] }),
      /* @__PURE__ */ jsxs9("span", { className: "text-accent font-sans font-semibold text-sm flex items-center gap-2 group-hover:underline", children: [
        readMoreText,
        /* @__PURE__ */ jsx19(ArrowRightIcon, { className: "w-4 h-4" })
      ] })
    ] })
  ] });
  const cardClasses = `bg-surface border border-recessed rounded-sm overflow-hidden shadow-sm hover:shadow-md transition-shadow group ${className}`;
  if (LinkComponent) {
    return /* @__PURE__ */ jsx19(LinkComponent, { href, className: cardClasses, children: cardContent });
  }
  return /* @__PURE__ */ jsx19("a", { href, onClick, className: cardClasses, children: cardContent });
}

// src/primitives/Breadcrumbs.tsx
import { useState as useState4, useRef as useRef2, useEffect as useEffect3, useCallback as useCallback2 } from "react";
import { ChevronRightIcon, HomeIcon, MoreHorizontalIcon } from "lucide-react";
import { jsx as jsx20, jsxs as jsxs10 } from "react/jsx-runtime";
var SIZE_TEXT = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-base"
};
var SIZE_ICON = {
  sm: "w-3 h-3",
  md: "w-4 h-4",
  lg: "w-5 h-5"
};
function computeCollapse(items, maxItems) {
  const indexed = items.map((item, i) => ({
    item,
    originalIndex: i
  }));
  if (maxItems <= 0 || items.length <= maxItems) {
    return { before: indexed, collapsed: [], after: [] };
  }
  if (maxItems === 1) {
    return {
      before: [],
      collapsed: indexed.slice(0, -1),
      after: [indexed[indexed.length - 1]]
    };
  }
  const tailCount = maxItems - 1;
  return {
    before: [indexed[0]],
    collapsed: indexed.slice(1, -tailCount),
    after: indexed.slice(-tailCount)
  };
}
function truncateText2(text, maxLength) {
  if (maxLength <= 0 || text.length <= maxLength) {
    return { display: text, isTruncated: false };
  }
  return { display: `${text.slice(0, maxLength)}...`, isTruncated: true };
}
function DefaultLink({
  href,
  children,
  className,
  onClick,
  ...rest
}) {
  return /* @__PURE__ */ jsx20("a", { href, className, onClick, ...rest, children });
}
function CollapsedItemsDropdown({
  collapsedItems,
  size,
  maxLabelLength,
  onItemActivate
}) {
  const [isOpen, setIsOpen] = useState4(false);
  const triggerRef = useRef2(null);
  const menuRef = useRef2(null);
  const itemRefs = useRef2([]);
  useEffect3(() => {
    itemRefs.current = itemRefs.current.slice(0, collapsedItems.length);
  }, [collapsedItems.length]);
  useEffect3(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      const target = e.target;
      if (menuRef.current && !menuRef.current.contains(target) && triggerRef.current && !triggerRef.current.contains(target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);
  useEffect3(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);
  useEffect3(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        itemRefs.current[0]?.focus();
      });
    }
  }, [isOpen]);
  const handleToggle = useCallback2(() => {
    setIsOpen((prev) => !prev);
  }, []);
  const handleMenuKeyDown = useCallback2(
    (e) => {
      const focusedIndex = itemRefs.current.indexOf(
        document.activeElement
      );
      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          const next = focusedIndex < collapsedItems.length - 1 ? focusedIndex + 1 : 0;
          itemRefs.current[next]?.focus();
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          const prev = focusedIndex > 0 ? focusedIndex - 1 : collapsedItems.length - 1;
          itemRefs.current[prev]?.focus();
          break;
        }
        case "Home": {
          e.preventDefault();
          itemRefs.current[0]?.focus();
          break;
        }
        case "End": {
          e.preventDefault();
          itemRefs.current[collapsedItems.length - 1]?.focus();
          break;
        }
        case "Tab": {
          setIsOpen(false);
          break;
        }
      }
    },
    [collapsedItems.length]
  );
  return /* @__PURE__ */ jsxs10("div", { className: "relative", children: [
    /* @__PURE__ */ jsx20(
      "button",
      {
        ref: triggerRef,
        type: "button",
        onClick: handleToggle,
        className: `
          font-sans text-tertiary hover:text-primary transition-colors
          flex items-center p-0.5 rounded-sm
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tertiary/30
          ${SIZE_TEXT[size]}
        `,
        "aria-expanded": isOpen,
        "aria-haspopup": "menu",
        "aria-label": `Show ${collapsedItems.length} hidden breadcrumb${collapsedItems.length !== 1 ? "s" : ""}`,
        children: /* @__PURE__ */ jsx20(MoreHorizontalIcon, { className: SIZE_ICON[size], strokeWidth: 2 })
      }
    ),
    isOpen && /* @__PURE__ */ jsx20(
      "div",
      {
        ref: menuRef,
        role: "menu",
        "aria-label": "Hidden breadcrumbs",
        onKeyDown: handleMenuKeyDown,
        className: "absolute top-full left-0 mt-1 py-1 bg-surface border border-recessed rounded-sm shadow-md z-50 min-w-[160px] animate-scale-in",
        children: collapsedItems.map(({ item, originalIndex }, i) => {
          const { display, isTruncated } = truncateText2(
            item.label,
            maxLabelLength
          );
          return /* @__PURE__ */ jsx20(
            "button",
            {
              ref: (el) => {
                itemRefs.current[i] = el;
              },
              type: "button",
              role: "menuitem",
              tabIndex: -1,
              title: isTruncated ? item.label : void 0,
              className: `
                  block w-full text-left px-3 py-1.5 font-sans text-secondary
                  hover:text-primary hover:bg-elevated transition-colors
                  focus-visible:outline-none focus-visible:bg-elevated focus-visible:text-primary
                  ${SIZE_TEXT[size]}
                `,
              onClick: (e) => {
                onItemActivate(item, originalIndex, e);
                setIsOpen(false);
                triggerRef.current?.focus();
              },
              children: display
            },
            item.id || `collapsed-${originalIndex}`
          );
        })
      }
    )
  ] });
}
function Breadcrumbs({
  items,
  separator,
  maxItems = 4,
  showHomeIcon = true,
  linkComponent: LinkComponent = DefaultLink,
  maxLabelLength = 0,
  onNavigate,
  size = "md",
  className = ""
}) {
  if (items.length === 0) return null;
  const { before, collapsed, after } = computeCollapse(items, maxItems);
  const handleItemClick = (item, originalIndex, e) => {
    if (item.onClick) {
      item.onClick(e);
    }
    if (onNavigate) {
      e.preventDefault();
      onNavigate(item, originalIndex);
    }
  };
  const defaultSeparator = /* @__PURE__ */ jsx20(
    ChevronRightIcon,
    {
      className: `${SIZE_ICON[size]} text-tertiary flex-shrink-0`,
      strokeWidth: 2
    }
  );
  const renderSeparator = (key) => /* @__PURE__ */ jsx20("li", { className: "flex items-center", "aria-hidden": "true", children: separator || defaultSeparator }, key);
  const renderBreadcrumbItem = (item, originalIndex, isFirst) => {
    const isLastItem = originalIndex === items.length - 1;
    const { display, isTruncated } = truncateText2(item.label, maxLabelLength);
    const showTitle = isTruncated || item.label.length > 25;
    const showHome = isFirst && showHomeIcon && !item.icon;
    const iconElement = showHome ? /* @__PURE__ */ jsx20(
      HomeIcon,
      {
        className: `${SIZE_ICON[size]} flex-shrink-0`,
        strokeWidth: 2
      }
    ) : item.icon ? /* @__PURE__ */ jsx20("span", { className: `flex-shrink-0 ${SIZE_ICON[size]}`, children: item.icon }) : null;
    const labelContent = /* @__PURE__ */ jsxs10("span", { className: "flex items-center gap-1.5", children: [
      iconElement,
      /* @__PURE__ */ jsx20(
        "span",
        {
          className: "truncate max-w-[200px]",
          title: showTitle ? item.label : void 0,
          children: display
        }
      )
    ] });
    if (isLastItem) {
      return /* @__PURE__ */ jsx20(
        "span",
        {
          className: `font-sans font-medium text-primary ${SIZE_TEXT[size]}`,
          "aria-current": "page",
          children: labelContent
        }
      );
    }
    if (item.href) {
      return /* @__PURE__ */ jsx20(
        LinkComponent,
        {
          href: item.href,
          className: `
            font-sans text-tertiary hover:text-primary transition-colors
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tertiary/30 rounded-sm
            ${SIZE_TEXT[size]}
          `,
          onClick: (e) => handleItemClick(item, originalIndex, e),
          children: labelContent
        }
      );
    }
    return /* @__PURE__ */ jsx20(
      "button",
      {
        type: "button",
        className: `
          font-sans text-tertiary hover:text-primary transition-colors
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tertiary/30 rounded-sm
          ${SIZE_TEXT[size]}
        `,
        onClick: (e) => handleItemClick(item, originalIndex, e),
        children: labelContent
      }
    );
  };
  const listItems = [];
  let separatorCount = 0;
  before.forEach(({ item, originalIndex }) => {
    if (listItems.length > 0) {
      listItems.push(renderSeparator(`sep-${separatorCount++}`));
    }
    listItems.push(
      /* @__PURE__ */ jsx20(
        "li",
        {
          className: "flex items-center",
          children: renderBreadcrumbItem(item, originalIndex, originalIndex === 0)
        },
        item.id || `item-${originalIndex}`
      )
    );
  });
  if (collapsed.length > 0) {
    listItems.push(renderSeparator(`sep-${separatorCount++}`));
    listItems.push(
      /* @__PURE__ */ jsx20("li", { className: "flex items-center", children: /* @__PURE__ */ jsx20(
        CollapsedItemsDropdown,
        {
          collapsedItems: collapsed,
          size,
          maxLabelLength,
          onItemActivate: handleItemClick
        }
      ) }, "collapsed-trigger")
    );
  }
  after.forEach(({ item, originalIndex }) => {
    if (listItems.length > 0) {
      listItems.push(renderSeparator(`sep-${separatorCount++}`));
    }
    listItems.push(
      /* @__PURE__ */ jsx20(
        "li",
        {
          className: "flex items-center",
          children: renderBreadcrumbItem(item, originalIndex, originalIndex === 0)
        },
        item.id || `item-${originalIndex}`
      )
    );
  });
  return /* @__PURE__ */ jsx20("nav", { "aria-label": "Breadcrumb", className, children: /* @__PURE__ */ jsx20("ol", { className: "flex items-center gap-1.5 flex-wrap", children: listItems }) });
}
function BreadcrumbSeparator({
  children,
  className = ""
}) {
  return /* @__PURE__ */ jsx20("span", { className: `text-tertiary ${className}`, "aria-hidden": "true", children: children || "/" });
}

// src/charts/LineChart.tsx
import { jsx as jsx21, jsxs as jsxs11 } from "react/jsx-runtime";
function LineChart() {
  const data = [{
    month: "Jan",
    value: 2400,
    benchmark: 2200
  }, {
    month: "Feb",
    value: 2550,
    benchmark: 2300
  }, {
    month: "Mar",
    value: 2450,
    benchmark: 2350
  }, {
    month: "Apr",
    value: 2700,
    benchmark: 2400
  }, {
    month: "May",
    value: 2850,
    benchmark: 2500
  }, {
    month: "Jun",
    value: 2950,
    benchmark: 2600
  }];
  const maxValue = 3200;
  const minValue = 2e3;
  const range = maxValue - minValue;
  return /* @__PURE__ */ jsxs11("div", { className: "bg-surface border border-recessed p-4 sm:p-6 lg:p-8 rounded-sm shadow-sm", children: [
    /* @__PURE__ */ jsxs11("div", { className: "mb-4 sm:mb-6", children: [
      /* @__PURE__ */ jsx21("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-2", children: "Portfolio Performance" }),
      /* @__PURE__ */ jsxs11("div", { className: "flex flex-col lg:flex-row lg:items-start lg:justify-between mb-2 gap-4", children: [
        /* @__PURE__ */ jsxs11("div", { children: [
          /* @__PURE__ */ jsx21("h2", { className: "text-xl sm:text-2xl lg:text-heading-section font-serif font-semibold mb-2 leading-tight tracking-headline-serif", children: "Alternative assets drive portfolio diversification" }),
          /* @__PURE__ */ jsx21("p", { className: "text-xs sm:text-sm font-sans text-secondary", children: "Portfolio value vs. benchmark, January\u2013June 2024" })
        ] }),
        /* @__PURE__ */ jsxs11("div", { className: "text-left lg:text-right", children: [
          /* @__PURE__ */ jsx21("div", { className: "text-heading-section sm:text-heading-section-md font-serif font-semibold text-accent tabular-nums leading-none tracking-headline-serif", children: "$2,950" }),
          /* @__PURE__ */ jsx21("div", { className: "text-sm sm:text-base font-mono font-medium text-growth mt-1", children: "+22.9%" })
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsxs11("div", { className: "relative h-48 sm:h-56 lg:h-64", children: [
      [0, 1, 2, 3, 4].map((i) => {
        const value = maxValue - i * range / 4;
        const y = i * 100 / 4;
        return /* @__PURE__ */ jsx21("div", { className: "absolute w-full", style: {
          top: `${y}%`
        }, children: /* @__PURE__ */ jsxs11("div", { className: "flex items-center", children: [
          /* @__PURE__ */ jsxs11("span", { className: "text-caption-xs sm:text-xs font-sans text-tertiary tabular-nums w-8 sm:w-12", children: [
            "$",
            (value / 1e3).toFixed(1),
            "k"
          ] }),
          /* @__PURE__ */ jsx21("div", { className: "flex-1 border-t border-recessed opacity-40 ml-2" })
        ] }) }, i);
      }),
      /* @__PURE__ */ jsx21("div", { className: "absolute inset-0 pl-8 sm:pl-12", children: /* @__PURE__ */ jsxs11("svg", { width: "100%", height: "100%", preserveAspectRatio: "xMidYMid meet", className: "overflow-visible", children: [
        data.map((d, i) => {
          if (i === 0) return null;
          const x1 = (i - 1) / (data.length - 1) * 100;
          const y1 = (maxValue - data[i - 1].benchmark) / range * 100;
          const x2 = i / (data.length - 1) * 100;
          const y2 = (maxValue - d.benchmark) / range * 100;
          return /* @__PURE__ */ jsx21("line", { x1: `${x1}%`, y1: `${y1}%`, x2: `${x2}%`, y2: `${y2}%`, stroke: "rgb(var(--color-neutral-data))", strokeWidth: "2", strokeDasharray: "4,4", opacity: "0.6", vectorEffect: "non-scaling-stroke" }, `benchmark-${i}`);
        }),
        data.map((d, i) => {
          if (i === 0) return null;
          const x1 = (i - 1) / (data.length - 1) * 100;
          const y1 = (maxValue - data[i - 1].value) / range * 100;
          const x2 = i / (data.length - 1) * 100;
          const y2 = (maxValue - d.value) / range * 100;
          return /* @__PURE__ */ jsx21("line", { x1: `${x1}%`, y1: `${y1}%`, x2: `${x2}%`, y2: `${y2}%`, stroke: "rgb(var(--color-accent))", strokeWidth: "2.5", vectorEffect: "non-scaling-stroke" }, `value-${i}`);
        }),
        /* @__PURE__ */ jsx21("circle", { cx: "100%", cy: `${(maxValue - data[data.length - 1].value) / range * 100}%`, r: "5", fill: "rgb(var(--color-accent))" })
      ] }) })
    ] }),
    /* @__PURE__ */ jsx21("div", { className: "pl-8 sm:pl-12 flex justify-between text-caption-xs sm:text-xs font-sans text-tertiary mt-2", children: data.map((d, i) => /* @__PURE__ */ jsx21("span", { children: d.month }, i)) }),
    /* @__PURE__ */ jsxs11("div", { className: "mt-4 sm:mt-6 pt-4 border-t border-recessed flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4", children: [
      /* @__PURE__ */ jsxs11("div", { className: "flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 text-xs sm:text-sm font-sans", children: [
        /* @__PURE__ */ jsxs11("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx21("div", { className: "w-6 sm:w-8 h-0.5 bg-accent flex-shrink-0" }),
          /* @__PURE__ */ jsx21("span", { className: "text-secondary", children: "Portfolio value" })
        ] }),
        /* @__PURE__ */ jsxs11("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx21("div", { className: "w-6 sm:w-8 h-0.5 bg-neutral-data opacity-60 flex-shrink-0", style: {
            backgroundImage: "repeating-linear-gradient(to right, rgb(var(--color-neutral-data)) 0, rgb(var(--color-neutral-data)) 4px, transparent 4px, transparent 8px)"
          } }),
          /* @__PURE__ */ jsx21("span", { className: "text-secondary", children: "S&P 500 benchmark" })
        ] })
      ] }),
      /* @__PURE__ */ jsx21("p", { className: "text-caption-sm sm:text-caption-xs font-sans text-tertiary opacity-75", children: "Source: Aaim Platform Analytics" })
    ] })
  ] });
}

// src/charts/AreaChart.tsx
import { useMemo } from "react";
import { Fragment as Fragment5, jsx as jsx22, jsxs as jsxs12 } from "react/jsx-runtime";
var defaultFormatValue = (value) => value.toLocaleString("en-US", { maximumFractionDigits: 1 });
var TICK_SHARES = [0, 0.25, 0.5, 0.75, 1];
function AreaChart({
  title,
  data,
  dataKey,
  xAxisKey,
  color = "rgb(var(--color-growth))",
  height = 300,
  formatValue = defaultFormatValue
}) {
  const { maxValue, minValue, points } = useMemo(() => {
    const read = (row) => {
      const raw = row[dataKey];
      if (raw === void 0 || raw === null || raw === "") return null;
      const value = typeof raw === "number" ? raw : Number(raw);
      return Number.isFinite(value) ? value : null;
    };
    const measured = data.map((row, index) => ({ index, value: read(row) })).filter((entry) => entry.value !== null);
    const values = measured.map((entry) => entry.value);
    const max = Math.max(...values, 0) || 1;
    const min = Math.min(...values, 0);
    const range = max - min || 1;
    const pts = measured.map(({ index, value }) => ({
      x: data.length > 1 ? index / (data.length - 1) * 100 : 50,
      y: (max - value) / range * 100,
      label: String(data[index][xAxisKey]),
      value
    }));
    return { maxValue: max, minValue: min, points: pts };
  }, [data, dataKey, xAxisKey]);
  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaPath = `${linePath} L ${points[points.length - 1]?.x ?? 100} 100 L ${points[0]?.x ?? 0} 100 Z`;
  const gradientId = `area-gradient-${dataKey}`;
  const chartHeight = height - 50;
  const summary = useMemo(() => {
    if (points.length === 0) return `${title}: no values to chart.`;
    const first = points[0];
    const last = points[points.length - 1];
    const values = points.map((p) => p.value);
    return `${title}: ${points.length} value${points.length === 1 ? "" : "s"} from ${first.label} to ${last.label}. First ${formatValue(first.value)}, last ${formatValue(last.value)}, low ${formatValue(Math.min(...values))}, high ${formatValue(Math.max(...values))}.`;
  }, [points, title, formatValue]);
  return /* @__PURE__ */ jsxs12("div", { className: "bg-surface border border-recessed p-6 rounded-sm shadow-sm h-full", children: [
    /* @__PURE__ */ jsx22("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-4 leading-normal", children: title }),
    /* @__PURE__ */ jsxs12("div", { className: "w-full flex", style: { height: chartHeight }, children: [
      /* @__PURE__ */ jsx22("div", { className: "relative w-16 flex-shrink-0", "aria-hidden": "true", children: TICK_SHARES.map((share) => /* @__PURE__ */ jsx22(
        "span",
        {
          className: `absolute right-2 -translate-y-1/2 text-label-xs font-mono text-tertiary tabular-nums whitespace-nowrap ${share === 0 || share === 1 ? "" : "hidden sm:inline"}`,
          style: { top: `${share * 100}%` },
          children: formatValue(maxValue - share * (maxValue - minValue))
        },
        share
      )) }),
      /* @__PURE__ */ jsxs12("div", { className: "relative flex-1 min-w-0", children: [
        TICK_SHARES.map((share) => /* @__PURE__ */ jsx22(
          "div",
          {
            className: "absolute left-0 right-0 border-t border-dashed border-recessed opacity-40",
            style: { top: `${share * 100}%` }
          },
          share
        )),
        /* @__PURE__ */ jsxs12(
          "svg",
          {
            viewBox: "0 0 100 100",
            preserveAspectRatio: "none",
            className: "absolute inset-0 w-full h-full",
            role: "img",
            "aria-label": summary,
            children: [
              /* @__PURE__ */ jsx22("defs", { children: /* @__PURE__ */ jsxs12("linearGradient", { id: gradientId, x1: "0", y1: "0", x2: "0", y2: "1", children: [
                /* @__PURE__ */ jsx22("stop", { offset: "0%", stopColor: color, stopOpacity: 0.3 }),
                /* @__PURE__ */ jsx22("stop", { offset: "100%", stopColor: color, stopOpacity: 0.02 })
              ] }) }),
              points.length > 1 && /* @__PURE__ */ jsxs12(Fragment5, { children: [
                /* @__PURE__ */ jsx22("path", { d: areaPath, fill: `url(#${gradientId})` }),
                /* @__PURE__ */ jsx22(
                  "path",
                  {
                    d: linePath,
                    fill: "none",
                    stroke: color,
                    strokeWidth: "0.8",
                    vectorEffect: "non-scaling-stroke"
                  }
                )
              ] }),
              points.length > 0 && /* @__PURE__ */ jsx22(
                "circle",
                {
                  cx: points[points.length - 1].x,
                  cy: points[points.length - 1].y,
                  r: "1.5",
                  fill: color
                }
              )
            ]
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsx22("div", { className: "flex justify-between mt-2 ml-16", children: data.length <= 12 ? data.map((d, i) => /* @__PURE__ */ jsx22("span", { className: "text-label-xs font-sans text-tertiary", children: String(d[xAxisKey]) }, i)) : [0, Math.floor(data.length / 2), data.length - 1].map((i) => /* @__PURE__ */ jsx22("span", { className: "text-label-xs font-sans text-tertiary", children: String(data[i][xAxisKey]) }, i)) })
  ] });
}

// src/charts/BarChart.tsx
import { jsx as jsx23, jsxs as jsxs13 } from "react/jsx-runtime";
function BarChart({
  title,
  subtitle,
  data,
  height = 400
}) {
  const maxValue = Math.max(...data.map((d) => d.value));
  return /* @__PURE__ */ jsxs13("div", { className: "bg-surface border border-recessed p-8 rounded-sm shadow-sm", children: [
    subtitle && /* @__PURE__ */ jsx23("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-2", children: subtitle }),
    /* @__PURE__ */ jsx23("h3", { className: "text-heading-section font-serif font-semibold mb-6 leading-tight tracking-headline-serif", children: title }),
    /* @__PURE__ */ jsxs13("div", { className: "mb-4", children: [
      /* @__PURE__ */ jsx23("div", { className: "relative bg-gradient-to-t from-alternative/[0.08] to-alternative/[0.15] p-6 rounded-sm", style: {
        height: `${height}px`
      }, children: /* @__PURE__ */ jsx23("div", { className: "h-full flex items-end gap-4", children: data.map((item, index) => /* @__PURE__ */ jsx23("div", { className: `flex-1 ${item.color || "bg-alternative"} rounded-t-sm transition-all duration-200 hover:opacity-80`, style: {
        height: `${item.value / maxValue * 100}%`
      } }, index)) }) }),
      /* @__PURE__ */ jsx23("div", { className: "flex gap-4 mt-3 px-6", children: data.map((item, index) => /* @__PURE__ */ jsx23("div", { className: "flex-1 text-center", children: /* @__PURE__ */ jsx23("span", { className: "text-xs font-sans text-tertiary tracking-tight", children: item.label }) }, index)) })
    ] }),
    /* @__PURE__ */ jsx23("div", { className: "pt-4 border-t border-recessed", children: /* @__PURE__ */ jsx23("p", { className: "text-xs font-sans text-tertiary opacity-75", children: "Source: Aaim Platform Analytics" }) })
  ] });
}

// src/charts/GroupedBarChart.tsx
import { useMemo as useMemo2 } from "react";
import { jsx as jsx24, jsxs as jsxs14 } from "react/jsx-runtime";
var defaultFormatValue2 = (value) => value.toLocaleString("en-US", { maximumFractionDigits: 1 });
function niceStep(rough) {
  if (!(rough > 0) || !Number.isFinite(rough)) return 1;
  const exponent = Math.floor(Math.log10(rough));
  const magnitude = 10 ** exponent;
  const fraction = rough / magnitude;
  const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10;
  return nice * magnitude;
}
var clean = (value) => Number(value.toPrecision(12));
function readValue(row, key) {
  const raw = row[key];
  if (raw === void 0 || raw === null || raw === "") return null;
  const value = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(value) ? value : null;
}
function valueScale(data, dataKeys) {
  let min = 0;
  let max = 0;
  let measured = false;
  for (const row of data) {
    for (const dk of dataKeys) {
      const value = readValue(row, dk.key);
      if (value === null) continue;
      measured = true;
      if (value < min) min = value;
      if (value > max) max = value;
    }
  }
  if (!measured) return { domainMin: 0, domainMax: 1, ticks: [] };
  if (min === 0 && max === 0) max = 1;
  const step = niceStep((max - min) / 4);
  const domainMin = clean(Math.floor(min / step) * step);
  const domainMax = clean(Math.ceil(max / step) * step);
  const count = Math.round((domainMax - domainMin) / step);
  const ticks = Array.from({ length: count + 1 }, (_, i) => clean(domainMin + i * step));
  return { domainMin, domainMax, ticks };
}
function GroupedBarChart({
  title,
  data,
  dataKeys,
  xAxisKey,
  height = 300,
  formatValue = defaultFormatValue2
}) {
  const { domainMin, domainMax, ticks } = useMemo2(
    () => valueScale(data, dataKeys),
    [data, dataKeys]
  );
  const chartHeight = height - 60;
  const barGroupGap = 8;
  const range = domainMax - domainMin || 1;
  const yOf = (value) => (domainMax - value) / range * chartHeight;
  const zeroY = yOf(0);
  return /* @__PURE__ */ jsxs14("div", { className: "bg-surface border border-recessed p-6 rounded-sm shadow-sm h-full", children: [
    /* @__PURE__ */ jsx24("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-4 leading-normal", children: title }),
    /* @__PURE__ */ jsxs14("div", { className: "w-full", style: { height }, children: [
      /* @__PURE__ */ jsxs14("div", { className: "flex", children: [
        /* @__PURE__ */ jsx24(
          "div",
          {
            className: "relative w-14 flex-shrink-0",
            style: { height: chartHeight },
            "aria-hidden": "true",
            children: ticks.map((tick) => /* @__PURE__ */ jsx24(
              "span",
              {
                className: "absolute right-2 -translate-y-1/2 text-label-sm font-sans text-tertiary tabular-nums whitespace-nowrap",
                style: { top: `${yOf(tick)}px` },
                children: formatValue(tick)
              },
              tick
            ))
          }
        ),
        /* @__PURE__ */ jsxs14("div", { className: "relative flex-1 min-w-0", children: [
          /* @__PURE__ */ jsxs14("div", { className: "relative", style: { height: chartHeight }, children: [
            ticks.map((tick) => /* @__PURE__ */ jsx24(
              "div",
              {
                className: `absolute left-0 right-0 border-t ${tick === 0 ? "border-tertiary opacity-60" : "border-recessed opacity-40"}`,
                style: { top: `${yOf(tick)}px` }
              },
              tick
            )),
            /* @__PURE__ */ jsx24("div", { className: "absolute inset-0 flex z-10", children: data.map((row, groupIndex) => {
              const category = String(row[xAxisKey]);
              return /* @__PURE__ */ jsx24(
                "div",
                {
                  className: "flex-1 flex justify-center gap-0.5 h-full",
                  style: { paddingLeft: barGroupGap / 2, paddingRight: barGroupGap / 2 },
                  children: dataKeys.map((dk) => {
                    const value = readValue(row, dk.key);
                    const label = `${category}, ${dk.name}: ${value === null ? "not reported" : formatValue(value)}`;
                    if (value === null) {
                      return /* @__PURE__ */ jsx24(
                        "div",
                        {
                          className: "flex-1 max-w-8",
                          role: "img",
                          "aria-label": label
                        },
                        dk.key
                      );
                    }
                    const barHeight = Math.max(
                      Math.abs(value) / range * chartHeight,
                      value === 0 ? 0 : 2
                    );
                    const negative = value < 0;
                    return /* @__PURE__ */ jsx24("div", { className: "relative flex-1 max-w-8 h-full", children: /* @__PURE__ */ jsx24(
                      "div",
                      {
                        role: "img",
                        "aria-label": label,
                        title: `${dk.name}: ${formatValue(value)}`,
                        className: `absolute left-0 right-0 transition-opacity hover:opacity-80 ${negative ? "rounded-b-sm" : "rounded-t-sm"}`,
                        style: {
                          top: negative ? `${zeroY}px` : `${zeroY - barHeight}px`,
                          height: `${barHeight}px`,
                          backgroundColor: dk.color
                        }
                      }
                    ) }, dk.key);
                  })
                },
                groupIndex
              );
            }) })
          ] }),
          /* @__PURE__ */ jsx24("div", { className: "flex mt-2", "aria-hidden": "true", children: data.map((row, groupIndex) => /* @__PURE__ */ jsx24("div", { className: "flex-1 min-w-0 text-center px-1", children: /* @__PURE__ */ jsx24("span", { className: "text-label-sm font-sans text-tertiary", children: String(row[xAxisKey]) }) }, groupIndex)) })
        ] })
      ] }),
      /* @__PURE__ */ jsx24("div", { className: "flex items-center justify-center gap-4 mt-2", children: dataKeys.map((dk) => /* @__PURE__ */ jsxs14("div", { className: "flex items-center gap-1.5", children: [
        /* @__PURE__ */ jsx24(
          "div",
          {
            className: "w-3 h-3 rounded-sm",
            style: { backgroundColor: dk.color }
          }
        ),
        /* @__PURE__ */ jsx24("span", { className: "text-label-sm font-sans text-tertiary", children: dk.name })
      ] }, dk.key)) })
    ] })
  ] });
}

// src/charts/HorizontalBarChart.tsx
import { jsx as jsx25, jsxs as jsxs15 } from "react/jsx-runtime";
function HorizontalBarChart({
  title,
  subtitle,
  data
}) {
  const maxValue = Math.max(...data.map((d) => d.value));
  return /* @__PURE__ */ jsxs15("div", { className: "bg-surface border border-recessed p-10 shadow-sm rounded-sm", children: [
    subtitle && /* @__PURE__ */ jsx25("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-2", children: subtitle }),
    /* @__PURE__ */ jsx25("h3", { className: "text-heading-section font-serif font-semibold mb-8 leading-tight tracking-headline-serif", children: title }),
    /* @__PURE__ */ jsx25("div", { className: "space-y-10", children: data.map((item, index) => /* @__PURE__ */ jsxs15("div", { children: [
      /* @__PURE__ */ jsxs15("div", { className: "flex justify-between items-baseline mb-3", children: [
        /* @__PURE__ */ jsx25("span", { className: "text-lg font-serif text-primary tracking-tight", children: item.label }),
        item.sublabel && /* @__PURE__ */ jsx25("span", { className: "text-base font-sans text-accent", children: item.sublabel })
      ] }),
      /* @__PURE__ */ jsx25("div", { className: "relative h-3 bg-recessed rounded-sm", children: /* @__PURE__ */ jsx25("div", { className: `absolute left-0 top-0 h-full rounded-sm transition-all duration-200 ${item.color || "bg-neutral-data"}`, style: {
        width: `${item.value / maxValue * 100}%`
      } }) })
    ] }, index)) })
  ] });
}

// src/charts/SparklineTable.tsx
import { TrendingUpIcon as TrendingUpIcon2, TrendingDownIcon } from "lucide-react";
import { jsx as jsx26, jsxs as jsxs16 } from "react/jsx-runtime";
function SparklineTable() {
  const accounts = [{
    name: "Growth Portfolio A",
    value: 1247893,
    change: 12.4,
    trend: [85, 88, 87, 92, 95, 98, 100]
  }, {
    name: "Balanced Fund B",
    value: 892456,
    change: 8.7,
    trend: [90, 89, 91, 93, 92, 95, 96]
  }, {
    name: "Income Strategy C",
    value: 654321,
    change: 5.2,
    trend: [88, 90, 89, 91, 92, 93, 94]
  }, {
    name: "Alternative Mix D",
    value: 543210,
    change: -2.1,
    trend: [95, 93, 91, 89, 88, 87, 85]
  }];
  return /* @__PURE__ */ jsxs16("div", { className: "bg-surface border border-recessed shadow-sm overflow-hidden rounded-sm", children: [
    /* @__PURE__ */ jsxs16("div", { className: "p-8 pb-4", children: [
      /* @__PURE__ */ jsx26("h3", { className: "text-heading-section font-serif font-semibold text-primary mb-1 leading-tight tracking-headline-serif", children: "Portfolio performance with inline trends" }),
      /* @__PURE__ */ jsx26("p", { className: "text-sm font-sans text-secondary", children: "Sparklines show 90-day patterns at a glance" })
    ] }),
    /* @__PURE__ */ jsx26("div", { className: "overflow-x-auto", children: /* @__PURE__ */ jsxs16("table", { className: "w-full", children: [
      /* @__PURE__ */ jsx26("thead", { children: /* @__PURE__ */ jsxs16("tr", { className: "border-b border-recessed", children: [
        /* @__PURE__ */ jsx26("th", { className: "px-8 py-3 text-left text-xs font-sans font-bold text-tertiary uppercase tracking-wide", children: "Account" }),
        /* @__PURE__ */ jsx26("th", { className: "px-4 py-3 text-right text-xs font-sans font-bold text-tertiary uppercase tracking-wide", children: "Value" }),
        /* @__PURE__ */ jsx26("th", { className: "px-4 py-3 text-center text-xs font-sans font-bold text-tertiary uppercase tracking-wide", children: "90-Day Trend" }),
        /* @__PURE__ */ jsx26("th", { className: "px-8 py-3 text-right text-xs font-sans font-bold text-tertiary uppercase tracking-wide", children: "Change" })
      ] }) }),
      /* @__PURE__ */ jsx26("tbody", { children: accounts.map((account, index) => /* @__PURE__ */ jsxs16("tr", { className: "border-b border-recessed hover:bg-elevated transition-colors", children: [
        /* @__PURE__ */ jsx26("td", { className: "px-8 py-4 text-sm font-sans font-medium text-primary tracking-tight", children: account.name }),
        /* @__PURE__ */ jsxs16("td", { className: "px-4 py-4 text-sm font-mono text-right tabular-nums tracking-tight text-primary", children: [
          "$",
          account.value.toLocaleString()
        ] }),
        /* @__PURE__ */ jsx26("td", { className: "px-4 py-4", children: /* @__PURE__ */ jsx26("div", { className: "flex justify-center", children: /* @__PURE__ */ jsx26("svg", { width: "120", height: "32", className: "overflow-visible", children: /* @__PURE__ */ jsx26("polyline", { points: account.trend.map((v, i) => {
          const x = i / (account.trend.length - 1) * 120;
          const y = 32 - v / 100 * 32;
          return `${x},${y}`;
        }).join(" "), fill: "none", stroke: account.change >= 0 ? "rgb(var(--color-growth))" : "rgb(var(--color-error))", strokeWidth: "2" }) }) }) }),
        /* @__PURE__ */ jsx26("td", { className: "px-8 py-4 text-right", children: /* @__PURE__ */ jsxs16("div", { className: `inline-flex items-center gap-1 text-sm font-mono font-semibold tabular-nums tracking-tight ${account.change >= 0 ? "text-growth" : "text-error"}`, children: [
          account.change >= 0 ? /* @__PURE__ */ jsx26(TrendingUpIcon2, { className: "w-4 h-4" }) : /* @__PURE__ */ jsx26(TrendingDownIcon, { className: "w-4 h-4" }),
          account.change >= 0 ? "+" : "",
          account.change,
          "%"
        ] }) })
      ] }, index)) })
    ] }) }),
    /* @__PURE__ */ jsx26("div", { className: "px-8 py-4 bg-elevated border-t border-recessed", children: /* @__PURE__ */ jsx26("p", { className: "text-xs font-sans text-tertiary opacity-75", children: "Source: Aaim Platform Analytics" }) })
  ] });
}

// src/charts/HorizonChart.tsx
import { jsx as jsx27, jsxs as jsxs17 } from "react/jsx-runtime";
function HorizonChart({
  data,
  label,
  baseline,
  height = 32,
  bands = 3,
  positiveColor = "rgb(var(--color-success))",
  negativeColor = "rgb(var(--color-error-bright))"
}) {
  const calculatedBaseline = baseline ?? data.reduce((a, b) => a + b, 0) / data.length;
  const minVal = Math.min(...data);
  const maxVal = Math.max(...data);
  const deviations = data.map((v) => v - calculatedBaseline);
  const maxDeviation = Math.max(...deviations.map(Math.abs));
  return /* @__PURE__ */ jsx27("div", { className: "group", children: /* @__PURE__ */ jsxs17("div", { className: "flex items-center gap-3 mb-1", children: [
    /* @__PURE__ */ jsx27("div", { className: "text-xs font-sans text-secondary flex-shrink-0 w-32", children: label }),
    /* @__PURE__ */ jsxs17(
      "div",
      {
        className: "flex-1 relative",
        style: {
          height: `${height}px`
        },
        children: [
          /* @__PURE__ */ jsxs17(
            "svg",
            {
              width: "100%",
              height: "100%",
              viewBox: "0 0 100 100",
              preserveAspectRatio: "none",
              className: "overflow-visible",
              children: [
                Array.from({
                  length: bands
                }).map((_, bandIndex) => {
                  const bandThresholdStart = bandIndex / bands * maxDeviation;
                  const positivePoints = [];
                  data.forEach((value, i) => {
                    const deviation = value - calculatedBaseline;
                    const x = i / (data.length - 1) * 100;
                    if (deviation > bandThresholdStart) {
                      const bandContribution = Math.min(
                        deviation - bandThresholdStart,
                        maxDeviation / bands
                      );
                      const fillRatio = bandContribution / (maxDeviation / bands);
                      const y = 50 - fillRatio * (50 / bands);
                      positivePoints.push(i === 0 ? `M ${x},${y}` : `L ${x},${y}`);
                    } else {
                      positivePoints.push(i === 0 ? `M ${x},50` : `L ${x},50`);
                    }
                  });
                  const negativePoints = [];
                  data.forEach((value, i) => {
                    const deviation = value - calculatedBaseline;
                    const absDeviation = Math.abs(deviation);
                    const x = i / (data.length - 1) * 100;
                    if (deviation < -bandThresholdStart) {
                      const bandContribution = Math.min(
                        absDeviation - bandThresholdStart,
                        maxDeviation / bands
                      );
                      const fillRatio = bandContribution / (maxDeviation / bands);
                      const y = 50 + fillRatio * (50 / bands);
                      negativePoints.push(i === 0 ? `M ${x},${y}` : `L ${x},${y}`);
                    } else {
                      negativePoints.push(i === 0 ? `M ${x},50` : `L ${x},50`);
                    }
                  });
                  const opacity = 0.3 + bandIndex / bands * 0.5;
                  return /* @__PURE__ */ jsxs17("g", { children: [
                    /* @__PURE__ */ jsx27(
                      "path",
                      {
                        d: `${positivePoints.join(" ")} L 100,50 L 0,50 Z`,
                        fill: positiveColor,
                        opacity
                      }
                    ),
                    /* @__PURE__ */ jsx27(
                      "path",
                      {
                        d: `${negativePoints.join(" ")} L 100,50 L 0,50 Z`,
                        fill: negativeColor,
                        opacity
                      }
                    )
                  ] }, bandIndex);
                }),
                /* @__PURE__ */ jsx27(
                  "line",
                  {
                    x1: "0",
                    y1: "50",
                    x2: "100",
                    y2: "50",
                    stroke: "rgb(var(--color-recessed))",
                    strokeWidth: "0.5",
                    strokeDasharray: "2,2",
                    opacity: "0.5",
                    vectorEffect: "non-scaling-stroke"
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsx27("div", { className: "absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none", children: /* @__PURE__ */ jsxs17("div", { className: "absolute top-0 right-0 bg-primary/90 text-white text-xs px-2 py-1 rounded", children: [
            "Range: ",
            minVal.toFixed(1),
            "\u2013",
            maxVal.toFixed(1)
          ] }) })
        ]
      }
    ),
    /* @__PURE__ */ jsx27("div", { className: "text-sm font-mono tabular-nums text-primary flex-shrink-0 w-16 text-right", children: data[data.length - 1].toFixed(1) })
  ] }) });
}

// src/charts/WaterfallChart.tsx
import React4 from "react";
import { jsx as jsx28, jsxs as jsxs18 } from "react/jsx-runtime";
function WaterfallChart({
  title,
  subtitle,
  data,
  formatValue = (v) => v >= 0 ? `+${v.toLocaleString()}` : v.toLocaleString(),
  height = 320,
  showConnectors = true,
  source = "Aaim Platform Analytics"
}) {
  const svgWidth = 1e3;
  const svgHeight = 400;
  const chartPadding = { top: 40, right: 20, bottom: 10, left: 20 };
  const chartWidth = svgWidth - chartPadding.left - chartPadding.right;
  const chartHeight = svgHeight - chartPadding.top - chartPadding.bottom;
  const processedData = React4.useMemo(() => {
    let runningTotal = 0;
    const items = [];
    data.forEach((item) => {
      if (item.isTotal || item.isSubtotal) {
        items.push({
          label: item.label,
          value: item.value,
          start: 0,
          end: item.value,
          isPositive: item.value >= 0,
          isTotal: item.isTotal || false,
          isSubtotal: item.isSubtotal || false
        });
        runningTotal = item.value;
      } else {
        const start = runningTotal;
        runningTotal += item.value;
        items.push({
          label: item.label,
          value: item.value,
          start: item.value >= 0 ? start : runningTotal,
          end: item.value >= 0 ? runningTotal : start,
          isPositive: item.value >= 0,
          isTotal: false,
          isSubtotal: false
        });
      }
    });
    return items;
  }, [data]);
  const allValues = processedData.flatMap((d) => [d.start, d.end]);
  const minValue = Math.min(0, ...allValues);
  const maxValue = Math.max(0, ...allValues);
  const range = maxValue - minValue;
  const padding = range * 0.15;
  const scaleMin = minValue - padding;
  const scaleMax = maxValue + padding;
  const scaleRange = scaleMax - scaleMin;
  const barCount = processedData.length;
  const totalGapWidth = chartWidth * 0.3;
  const gapWidth = totalGapWidth / (barCount + 1);
  const barWidth = (chartWidth - totalGapWidth) / barCount;
  const valueToY = (value) => {
    return chartPadding.top + (scaleMax - value) / scaleRange * chartHeight;
  };
  const zeroY = valueToY(0);
  return /* @__PURE__ */ jsxs18("div", { className: "bg-surface border border-recessed p-8 shadow-sm rounded-sm", children: [
    subtitle && /* @__PURE__ */ jsx28("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-2", children: subtitle }),
    /* @__PURE__ */ jsx28("h3", { className: "text-heading-section font-serif font-semibold mb-6 leading-tight tracking-headline-serif", children: title }),
    /* @__PURE__ */ jsx28("div", { className: "relative", style: { height: `${height}px` }, children: /* @__PURE__ */ jsxs18(
      "svg",
      {
        viewBox: `0 0 ${svgWidth} ${svgHeight}`,
        className: "w-full h-full",
        preserveAspectRatio: "xMidYMid meet",
        children: [
          /* @__PURE__ */ jsx28(
            "line",
            {
              x1: chartPadding.left,
              y1: zeroY,
              x2: svgWidth - chartPadding.right,
              y2: zeroY,
              stroke: "currentColor",
              className: "text-tertiary",
              strokeWidth: "1",
              opacity: "0.3"
            }
          ),
          processedData.map((item, index) => {
            const x = chartPadding.left + gapWidth + index * (barWidth + gapWidth);
            const topY = valueToY(Math.max(item.start, item.end));
            const bottomY = valueToY(Math.min(item.start, item.end));
            const barHeight = bottomY - topY;
            const nextItem = processedData[index + 1];
            const connectorY = valueToY(item.end);
            const nextX = chartPadding.left + gapWidth + (index + 1) * (barWidth + gapWidth);
            const labelY = topY - 12;
            const labelX = x + barWidth / 2;
            let fillClass = "fill-growth";
            if (item.isTotal || item.isSubtotal) {
              fillClass = "fill-primary";
            } else if (!item.isPositive) {
              fillClass = "fill-error";
            }
            return /* @__PURE__ */ jsxs18("g", { children: [
              /* @__PURE__ */ jsx28(
                "rect",
                {
                  x,
                  y: topY,
                  width: barWidth,
                  height: Math.max(barHeight, 2),
                  className: fillClass,
                  opacity: item.isSubtotal ? 0.7 : 1,
                  rx: "3"
                }
              ),
              showConnectors && nextItem && !item.isTotal && /* @__PURE__ */ jsx28(
                "line",
                {
                  x1: x + barWidth,
                  y1: connectorY,
                  x2: nextX,
                  y2: connectorY,
                  stroke: "currentColor",
                  className: "text-tertiary",
                  strokeWidth: "1.5",
                  strokeDasharray: "4,4",
                  opacity: "0.4"
                }
              ),
              /* @__PURE__ */ jsx28(
                "text",
                {
                  x: labelX,
                  y: labelY,
                  textAnchor: "middle",
                  dominantBaseline: "auto",
                  className: "fill-secondary",
                  style: {
                    fontSize: "24px",
                    fontFamily: "'Source Code Pro', Consolas, Monaco, monospace",
                    fontVariantNumeric: "tabular-nums"
                  },
                  children: item.isTotal || item.isSubtotal ? item.value.toLocaleString() : formatValue(item.value)
                }
              ),
              /* @__PURE__ */ jsx28(
                "text",
                {
                  x: labelX,
                  y: svgHeight - 5,
                  textAnchor: "middle",
                  dominantBaseline: "auto",
                  className: item.isTotal || item.isSubtotal ? "fill-primary" : "fill-tertiary",
                  style: {
                    fontSize: item.isTotal || item.isSubtotal ? "22px" : "20px",
                    fontFamily: "'Lato', system-ui, -apple-system, sans-serif",
                    fontWeight: item.isTotal || item.isSubtotal ? 600 : 400
                  },
                  children: item.label
                }
              )
            ] }, index);
          })
        ]
      }
    ) }),
    /* @__PURE__ */ jsxs18("div", { className: "mt-4 pt-4 border-t border-recessed flex items-center justify-between text-xs font-sans", children: [
      /* @__PURE__ */ jsxs18("p", { className: "text-tertiary opacity-75", children: [
        "Source: ",
        source
      ] }),
      /* @__PURE__ */ jsxs18("div", { className: "flex items-center gap-6", children: [
        /* @__PURE__ */ jsxs18("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx28("div", { className: "w-3 h-3 bg-growth rounded-sm" }),
          /* @__PURE__ */ jsx28("span", { className: "text-secondary", children: "Increase" })
        ] }),
        /* @__PURE__ */ jsxs18("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx28("div", { className: "w-3 h-3 bg-error rounded-sm" }),
          /* @__PURE__ */ jsx28("span", { className: "text-secondary", children: "Decrease" })
        ] }),
        /* @__PURE__ */ jsxs18("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx28("div", { className: "w-3 h-3 bg-primary rounded-sm" }),
          /* @__PURE__ */ jsx28("span", { className: "text-secondary", children: "Total" })
        ] })
      ] })
    ] })
  ] });
}

// src/charts/TreeMap.tsx
import React5 from "react";
import { jsx as jsx29, jsxs as jsxs19 } from "react/jsx-runtime";
function worstAspectRatio(row, sideLength, _totalArea) {
  const rowArea = row.reduce((sum, v) => sum + v, 0);
  if (rowArea === 0 || sideLength === 0) return Infinity;
  const rowWidth = rowArea / sideLength;
  let worst = 0;
  for (const area of row) {
    const h = area / rowWidth;
    const ratio = Math.max(rowWidth / h, h / rowWidth);
    if (ratio > worst) worst = ratio;
  }
  return worst;
}
function squarifyLayout(values, rect) {
  if (values.length === 0) return [];
  const totalValue = values.reduce((sum, v) => sum + v, 0);
  if (totalValue === 0) return values.map(() => ({ ...rect, width: 0, height: 0 }));
  const totalArea = rect.width * rect.height;
  const areas = values.map((v) => v / totalValue * totalArea);
  const results = new Array(values.length);
  const indices = areas.map((_, i) => i).sort((a, b) => areas[b] - areas[a]);
  function layoutRow(row, rowIndices, r) {
    const rowArea = row.reduce((sum, v) => sum + v, 0);
    const isWide = r.width >= r.height;
    const sideLength = isWide ? r.height : r.width;
    const rowWidth = sideLength === 0 ? 0 : rowArea / sideLength;
    let offset = 0;
    for (let i = 0; i < row.length; i++) {
      const h = rowWidth === 0 ? 0 : row[i] / rowWidth;
      if (isWide) {
        results[rowIndices[i]] = {
          x: r.x,
          y: r.y + offset,
          width: rowWidth,
          height: h
        };
      } else {
        results[rowIndices[i]] = {
          x: r.x + offset,
          y: r.y,
          width: h,
          height: rowWidth
        };
      }
      offset += h;
    }
    if (isWide) {
      return { x: r.x + rowWidth, y: r.y, width: r.width - rowWidth, height: r.height };
    }
    return { x: r.x, y: r.y + rowWidth, width: r.width, height: r.height - rowWidth };
  }
  let remaining = { ...rect };
  let currentRow = [];
  let currentIndices = [];
  for (const idx of indices) {
    const area = areas[idx];
    const sideLength = remaining.width >= remaining.height ? remaining.height : remaining.width;
    if (currentRow.length === 0) {
      currentRow.push(area);
      currentIndices.push(idx);
      continue;
    }
    const withItem = [...currentRow, area];
    const currentWorst = worstAspectRatio(currentRow, sideLength, totalArea);
    const newWorst = worstAspectRatio(withItem, sideLength, totalArea);
    if (newWorst <= currentWorst) {
      currentRow.push(area);
      currentIndices.push(idx);
    } else {
      remaining = layoutRow(currentRow, currentIndices, remaining);
      currentRow = [area];
      currentIndices = [idx];
    }
  }
  if (currentRow.length > 0) {
    layoutRow(currentRow, currentIndices, remaining);
  }
  return results;
}
var categoryColors = [
  "bg-alternative",
  "bg-growth",
  "bg-accent",
  "bg-warning",
  "bg-tertiary",
  "bg-secondary"
];
function TreeMap({
  title,
  subtitle,
  data,
  height = 400,
  showChange: _showChange = true,
  formatValue = (v) => `$${(v / 1e9).toFixed(1)}B`,
  colorMode = "performance",
  source = "Aaim Platform Analytics"
}) {
  const containerRef = React5.useRef(null);
  const [dimensions, setDimensions] = React5.useState({ width: 0, height: 0 });
  React5.useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        if (width > 0) {
          setDimensions({ width, height });
        }
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [height]);
  const sortedData = React5.useMemo(
    () => [...data].sort((a, b) => b.value - a.value),
    [data]
  );
  const layout = React5.useMemo(() => {
    if (dimensions.width === 0) return [];
    const values = sortedData.map((n) => n.value);
    const rects = squarifyLayout(values, {
      x: 0,
      y: 0,
      width: dimensions.width,
      height: dimensions.height
    });
    return sortedData.map((node, i) => ({
      ...node,
      ...rects[i]
    }));
  }, [sortedData, dimensions]);
  const getBackgroundColor = (node, index) => {
    if (colorMode === "category") {
      return categoryColors[index % categoryColors.length];
    }
    if (node.change === void 0) return "bg-tertiary";
    if (node.change > 2) return "bg-growth";
    if (node.change > 0) return "bg-growth/70";
    if (node.change > -2) return "bg-error/70";
    return "bg-error";
  };
  const getTextColor = (node) => {
    if (colorMode === "performance" && node.change !== void 0) {
      return "text-white";
    }
    return "text-white";
  };
  return /* @__PURE__ */ jsxs19("div", { className: "bg-surface border border-recessed p-8 shadow-sm rounded-sm", children: [
    subtitle && /* @__PURE__ */ jsx29("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-2", children: subtitle }),
    /* @__PURE__ */ jsx29("h3", { className: "text-heading-section font-serif font-semibold mb-6 leading-tight tracking-headline-serif", children: title }),
    /* @__PURE__ */ jsx29(
      "div",
      {
        ref: containerRef,
        className: "relative rounded-sm overflow-hidden",
        style: { height: `${height}px` },
        children: layout.map((node, index) => {
          const minDimension = Math.min(node.width, node.height);
          const showLabel = minDimension > 40;
          const showValue = minDimension > 60;
          const showChange = minDimension > 80 && node.change !== void 0;
          return /* @__PURE__ */ jsx29(
            "div",
            {
              className: `absolute ${getBackgroundColor(node, index)} ${getTextColor(node)}
                         transition-opacity hover:opacity-90 cursor-pointer
                         border border-surface/20`,
              style: {
                left: `${node.x}px`,
                top: `${node.y}px`,
                width: `${node.width}px`,
                height: `${node.height}px`
              },
              title: `${node.label}: ${formatValue(node.value)}${node.change !== void 0 ? ` (${node.change >= 0 ? "+" : ""}${node.change.toFixed(1)}%)` : ""}`,
              children: showLabel && /* @__PURE__ */ jsxs19("div", { className: "absolute inset-2 flex flex-col justify-center items-center text-center overflow-hidden", children: [
                /* @__PURE__ */ jsx29("span", { className: "text-xs font-sans font-semibold tracking-tight truncate max-w-full", children: node.label }),
                showValue && /* @__PURE__ */ jsx29("span", { className: "text-caption-xs font-mono tabular-nums tracking-tight opacity-90 mt-0.5", children: formatValue(node.value) }),
                showChange && node.change !== void 0 && /* @__PURE__ */ jsxs19(
                  "span",
                  {
                    className: `text-caption-xs font-mono font-semibold tabular-nums tracking-tight mt-0.5
                                 ${node.change >= 0 ? "text-white" : "text-white"}`,
                    children: [
                      node.change >= 0 ? "+" : "",
                      node.change.toFixed(1),
                      "%"
                    ]
                  }
                )
              ] })
            },
            node.id
          );
        })
      }
    ),
    /* @__PURE__ */ jsxs19("div", { className: "mt-6 pt-4 border-t border-recessed flex items-center justify-between text-xs font-sans", children: [
      /* @__PURE__ */ jsxs19("p", { className: "text-tertiary opacity-75", children: [
        "Source: ",
        source
      ] }),
      colorMode === "performance" && /* @__PURE__ */ jsxs19("div", { className: "flex items-center gap-4", children: [
        /* @__PURE__ */ jsxs19("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx29("div", { className: "w-3 h-3 bg-growth rounded-sm" }),
          /* @__PURE__ */ jsx29("span", { className: "text-secondary tracking-tight", children: ">2%" })
        ] }),
        /* @__PURE__ */ jsxs19("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx29("div", { className: "w-3 h-3 bg-growth/70 rounded-sm" }),
          /* @__PURE__ */ jsx29("span", { className: "text-secondary tracking-tight", children: "0-2%" })
        ] }),
        /* @__PURE__ */ jsxs19("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx29("div", { className: "w-3 h-3 bg-error/70 rounded-sm" }),
          /* @__PURE__ */ jsx29("span", { className: "text-secondary tracking-tight", children: "0 to -2%" })
        ] }),
        /* @__PURE__ */ jsxs19("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx29("div", { className: "w-3 h-3 bg-error rounded-sm" }),
          /* @__PURE__ */ jsx29("span", { className: "text-secondary tracking-tight", children: "<-2%" })
        ] })
      ] })
    ] })
  ] });
}

// src/charts/DonutChart.tsx
import React6 from "react";
import { Fragment as Fragment6, jsx as jsx30, jsxs as jsxs20 } from "react/jsx-runtime";
var defaultFormatTotal = (total) => total.toLocaleString("en-US");
var WITHHELD_SHARE = "--";
var DONUT_ZERO_TOTAL_MESSAGE = "The values total zero, so no share of the total is drawn.";
var defaultColors = [
  "rgb(var(--color-alternative))",
  // alternative (muted green)
  "rgb(var(--color-growth))",
  // growth (dark green)
  "rgb(var(--color-neutral-data))",
  // neutral-data (warm gray)
  "rgb(var(--color-warning))",
  // warning (gold)
  "rgb(var(--color-text-tertiary))",
  // tertiary
  "rgb(var(--color-tan-dark))",
  // tan-dark
  "rgb(var(--color-text-secondary))",
  // secondary
  "rgb(var(--color-traditional))"
  // traditional (brown)
];
var FULL_SWEEP = 360 - 1e-6;
function fullRingPath(radius, innerRadius) {
  const c = radius;
  return `
        M ${c - radius} ${c}
        A ${radius} ${radius} 0 1 1 ${c + radius} ${c}
        A ${radius} ${radius} 0 1 1 ${c - radius} ${c}
        Z
        M ${c - innerRadius} ${c}
        A ${innerRadius} ${innerRadius} 0 1 0 ${c + innerRadius} ${c}
        A ${innerRadius} ${innerRadius} 0 1 0 ${c - innerRadius} ${c}
        Z
      `;
}
function DonutChart({
  title,
  subtitle,
  data,
  size = 240,
  thickness = 40,
  centerLabel,
  centerValue,
  showLegend = true,
  formatValue = (v, total) => `${(v / total * 100).toFixed(1)}%`,
  formatTotal = defaultFormatTotal
}) {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const hasShares = total !== 0;
  const radius = size / 2;
  const innerRadius = radius - thickness;
  const shareText = (value) => hasShares ? formatValue(value, total) : WITHHELD_SHARE;
  const segments = React6.useMemo(() => {
    let currentAngle = -90;
    return data.map((segment, index) => {
      const color = segment.color || defaultColors[index % defaultColors.length];
      if (!hasShares) {
        return { ...segment, path: null, percentage: null, color };
      }
      const percentage = segment.value / total;
      const angle = percentage * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;
      currentAngle = endAngle;
      const startRad = startAngle * Math.PI / 180;
      const endRad = endAngle * Math.PI / 180;
      const x1 = radius + radius * Math.cos(startRad);
      const y1 = radius + radius * Math.sin(startRad);
      const x2 = radius + radius * Math.cos(endRad);
      const y2 = radius + radius * Math.sin(endRad);
      const ix1 = radius + innerRadius * Math.cos(startRad);
      const iy1 = radius + innerRadius * Math.sin(startRad);
      const ix2 = radius + innerRadius * Math.cos(endRad);
      const iy2 = radius + innerRadius * Math.sin(endRad);
      const largeArc = angle > 180 ? 1 : 0;
      const path = angle >= FULL_SWEEP ? fullRingPath(radius, innerRadius) : `
        M ${x1} ${y1}
        A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}
        L ${ix2} ${iy2}
        A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1}
        Z
      `;
      return { ...segment, path, percentage, color };
    });
  }, [data, total, hasShares, radius, innerRadius]);
  const [hoveredIndex, setHoveredIndex] = React6.useState(null);
  const activeIndex = hasShares ? hoveredIndex : null;
  const hoverHandlers = (index) => hasShares ? {
    onMouseEnter: () => setHoveredIndex(index),
    onMouseLeave: () => setHoveredIndex(null)
  } : {};
  return /* @__PURE__ */ jsxs20("div", { className: "bg-surface p-6 shadow-sm rounded-sm h-full", children: [
    subtitle && /* @__PURE__ */ jsx30("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-2", children: subtitle }),
    /* @__PURE__ */ jsx30("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-4 leading-normal", children: title }),
    /* @__PURE__ */ jsxs20("div", { className: "flex flex-col lg:flex-row items-center gap-8", children: [
      /* @__PURE__ */ jsxs20("div", { className: "relative flex-shrink-0", style: { width: size, height: size }, children: [
        /* @__PURE__ */ jsx30("svg", { width: size, height: size, className: "transform -rotate-0", "aria-hidden": "true", children: segments.map(
          (segment, index) => segment.path === null ? null : /* @__PURE__ */ jsx30(
            "path",
            {
              d: segment.path,
              fill: segment.color,
              fillRule: "evenodd",
              className: `transition-opacity cursor-pointer ${activeIndex !== null && activeIndex !== index ? "opacity-40" : "opacity-100"}`,
              ...hoverHandlers(index)
            },
            index
          )
        ) }),
        /* @__PURE__ */ jsx30(
          "div",
          {
            className: "absolute inset-0 flex flex-col items-center justify-center pointer-events-none",
            style: {
              top: thickness,
              left: thickness,
              right: thickness,
              bottom: thickness
            },
            children: activeIndex !== null ? /* @__PURE__ */ jsxs20(Fragment6, { children: [
              /* @__PURE__ */ jsx30("span", { className: "text-xs font-sans text-tertiary tracking-tight text-center px-2", children: segments[activeIndex].label }),
              /* @__PURE__ */ jsx30("span", { className: "text-2xl font-serif font-semibold text-primary tracking-headline-serif", children: shareText(segments[activeIndex].value) })
            ] }) : /* @__PURE__ */ jsxs20(Fragment6, { children: [
              centerLabel && /* @__PURE__ */ jsx30("span", { className: "text-xs font-sans text-tertiary tracking-tight", children: centerLabel }),
              centerValue && /* @__PURE__ */ jsx30("span", { className: "text-2xl font-serif font-semibold text-primary tracking-headline-serif", children: centerValue })
            ] })
          }
        )
      ] }),
      showLegend && /* @__PURE__ */ jsx30("div", { className: "flex-1 space-y-2", role: "list", "aria-label": `${title} legend`, children: segments.map((segment, index) => /* @__PURE__ */ jsxs20(
        "div",
        {
          role: "listitem",
          className: `flex items-center justify-between py-1.5 px-2 rounded-sm transition-colors ${hasShares ? "cursor-pointer" : ""} ${activeIndex === index ? "bg-elevated" : ""}`,
          ...hoverHandlers(index),
          children: [
            /* @__PURE__ */ jsxs20("div", { className: "flex items-center gap-3", children: [
              /* @__PURE__ */ jsx30(
                "div",
                {
                  className: "w-3 h-3 rounded-sm flex-shrink-0",
                  style: { backgroundColor: segment.color }
                }
              ),
              /* @__PURE__ */ jsx30("span", { className: "text-sm font-sans text-primary tracking-tight", children: segment.label })
            ] }),
            /* @__PURE__ */ jsx30("div", { className: "flex items-center gap-4", children: /* @__PURE__ */ jsx30(
              "span",
              {
                className: "text-sm font-mono tabular-nums text-secondary tracking-tight",
                "aria-hidden": hasShares ? void 0 : true,
                children: shareText(segment.value)
              }
            ) })
          ]
        },
        index
      )) })
    ] }),
    !hasShares && data.length > 0 && /* @__PURE__ */ jsx30("p", { className: "mt-4 text-xs font-sans text-tertiary", children: DONUT_ZERO_TOTAL_MESSAGE }),
    /* @__PURE__ */ jsx30("div", { className: "mt-6 pt-4 border-t border-recessed", children: /* @__PURE__ */ jsxs20("p", { className: "text-xs font-sans text-tertiary", children: [
      "Total: ",
      formatTotal(total)
    ] }) })
  ] });
}

// src/charts/CorrelationMatrix.tsx
import { jsx as jsx31, jsxs as jsxs21 } from "react/jsx-runtime";
function CorrelationMatrix({
  title,
  subtitle,
  labels,
  data,
  showValues = true,
  cellSize = 48
}) {
  const getColor = (value) => {
    const v = Math.max(-1, Math.min(1, value));
    if (v === 1) {
      return "bg-tertiary/30";
    }
    if (v >= 0) {
      const intensity = v;
      if (intensity > 0.7) return "bg-growth";
      if (intensity > 0.5) return "bg-growth/80";
      if (intensity > 0.3) return "bg-growth/60";
      if (intensity > 0.1) return "bg-growth/40";
      return "bg-growth/20";
    } else {
      const intensity = Math.abs(v);
      if (intensity > 0.7) return "bg-accent";
      if (intensity > 0.5) return "bg-accent/80";
      if (intensity > 0.3) return "bg-accent/60";
      if (intensity > 0.1) return "bg-accent/40";
      return "bg-accent/20";
    }
  };
  const getTextColor = (value) => {
    const absValue = Math.abs(value);
    if (absValue > 0.5) return "text-white";
    return "text-primary";
  };
  const labelWidth = 100;
  return /* @__PURE__ */ jsxs21("div", { className: "bg-surface border border-recessed p-8 shadow-sm rounded-sm", children: [
    subtitle && /* @__PURE__ */ jsx31("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-2", children: subtitle }),
    /* @__PURE__ */ jsx31("h3", { className: "text-heading-section font-serif font-semibold mb-6 leading-tight tracking-headline-serif", children: title }),
    /* @__PURE__ */ jsx31("div", { className: "overflow-x-auto", children: /* @__PURE__ */ jsxs21("div", { className: "inline-block", children: [
      /* @__PURE__ */ jsx31("div", { className: "flex", style: { marginLeft: `${labelWidth}px` }, children: labels.map((label, i) => /* @__PURE__ */ jsx31(
        "div",
        {
          className: "flex items-end justify-center pb-2",
          style: { width: `${cellSize}px`, height: `${cellSize}px` },
          children: /* @__PURE__ */ jsx31(
            "span",
            {
              className: "text-caption-xs font-sans font-medium text-tertiary tracking-tight transform -rotate-45 origin-bottom-left whitespace-nowrap",
              style: { maxWidth: `${cellSize * 1.4}px` },
              children: label
            }
          )
        },
        i
      )) }),
      data.map((row, rowIndex) => /* @__PURE__ */ jsxs21("div", { className: "flex items-center", children: [
        /* @__PURE__ */ jsx31(
          "div",
          {
            className: "flex items-center justify-end pr-3",
            style: { width: `${labelWidth}px` },
            children: /* @__PURE__ */ jsx31("span", { className: "text-xs font-sans font-medium text-tertiary tracking-tight truncate", children: labels[rowIndex] })
          }
        ),
        row.map((value, colIndex) => {
          const isDiagonal = rowIndex === colIndex;
          return /* @__PURE__ */ jsx31(
            "div",
            {
              className: `
                      flex items-center justify-center
                      ${getColor(value)}
                      ${isDiagonal ? "opacity-50" : ""}
                      border border-surface/50
                      transition-transform hover:scale-105 hover:z-10
                      cursor-default
                    `,
              style: {
                width: `${cellSize}px`,
                height: `${cellSize}px`
              },
              title: `${labels[rowIndex]} / ${labels[colIndex]}: ${value.toFixed(2)}`,
              children: showValues && /* @__PURE__ */ jsx31(
                "span",
                {
                  className: `text-caption-xs font-mono tabular-nums tracking-tight ${getTextColor(
                    value
                  )}`,
                  children: isDiagonal ? "1.00" : value.toFixed(2)
                }
              )
            },
            colIndex
          );
        })
      ] }, rowIndex))
    ] }) }),
    /* @__PURE__ */ jsx31("div", { className: "mt-6 pt-4 border-t border-recessed", children: /* @__PURE__ */ jsxs21("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsxs21("div", { className: "flex items-center gap-1", children: [
        /* @__PURE__ */ jsx31("span", { className: "text-xs font-sans text-tertiary tracking-tight mr-2", children: "Correlation:" }),
        /* @__PURE__ */ jsxs21("div", { className: "flex items-center", children: [
          /* @__PURE__ */ jsx31("div", { className: "w-6 h-4 bg-accent rounded-l-sm" }),
          /* @__PURE__ */ jsx31("div", { className: "w-6 h-4 bg-accent/60" }),
          /* @__PURE__ */ jsx31("div", { className: "w-6 h-4 bg-accent/30" }),
          /* @__PURE__ */ jsx31("div", { className: "w-6 h-4 bg-canvas border-y border-recessed" }),
          /* @__PURE__ */ jsx31("div", { className: "w-6 h-4 bg-growth/30" }),
          /* @__PURE__ */ jsx31("div", { className: "w-6 h-4 bg-growth/60" }),
          /* @__PURE__ */ jsx31("div", { className: "w-6 h-4 bg-growth rounded-r-sm" })
        ] }),
        /* @__PURE__ */ jsxs21("div", { className: "flex justify-between text-caption-xs font-mono text-tertiary tracking-tight ml-2 gap-4", children: [
          /* @__PURE__ */ jsx31("span", { children: "-1.0" }),
          /* @__PURE__ */ jsx31("span", { children: "0" }),
          /* @__PURE__ */ jsx31("span", { children: "+1.0" })
        ] })
      ] }),
      /* @__PURE__ */ jsx31("span", { className: "text-xs font-sans text-tertiary opacity-75", children: "Hover for details" })
    ] }) })
  ] });
}

// src/charts/SlopeChart.tsx
import { jsx as jsx32, jsxs as jsxs22 } from "react/jsx-runtime";
function SlopeChart({
  title,
  subtitle,
  startPeriod,
  endPeriod,
  data,
  formatValue = (v) => v.toLocaleString(),
  height = 400,
  highlightChanges = true
}) {
  const svgWidth = 600;
  const svgHeight = 500;
  const chartPadding = { top: 50, right: 120, bottom: 30, left: 120 };
  const chartHeight = svgHeight - chartPadding.top - chartPadding.bottom;
  const allValues = data.flatMap((d) => [d.startValue, d.endValue]);
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const range = maxValue - minValue;
  const padding = range * 0.1 || 1;
  const scaleMin = minValue - padding;
  const scaleMax = maxValue + padding;
  const scaleRange = scaleMax - scaleMin;
  const valueToY = (value) => {
    return chartPadding.top + (scaleMax - value) / scaleRange * chartHeight;
  };
  const lineColors = [
    "rgb(var(--color-text-primary))",
    // primary dark
    "rgb(var(--color-growth))",
    // growth green
    "rgb(var(--color-neutral-data))",
    // warm neutral
    "rgb(var(--color-alternative))",
    // alternative teal
    "rgb(var(--color-error))",
    // error/decline
    "rgb(var(--color-text-secondary))",
    // secondary gray
    "rgb(var(--color-text-tertiary))",
    // tertiary
    "rgb(var(--color-text-quaternary))"
    // light gray
  ];
  return /* @__PURE__ */ jsxs22("div", { className: "bg-surface border border-recessed p-8 shadow-sm rounded-sm", children: [
    subtitle && /* @__PURE__ */ jsx32("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-2", children: subtitle }),
    /* @__PURE__ */ jsx32("h3", { className: "text-heading-section font-serif font-semibold mb-6 leading-tight tracking-headline-serif", children: title }),
    /* @__PURE__ */ jsx32("div", { className: "relative", style: { height: `${height}px` }, children: /* @__PURE__ */ jsxs22(
      "svg",
      {
        viewBox: `0 0 ${svgWidth} ${svgHeight}`,
        className: "w-full h-full",
        preserveAspectRatio: "xMidYMid meet",
        children: [
          /* @__PURE__ */ jsx32(
            "text",
            {
              x: chartPadding.left,
              y: chartPadding.top - 20,
              textAnchor: "end",
              className: "fill-primary",
              style: {
                fontSize: "18px",
                fontFamily: "'Lato', system-ui, -apple-system, sans-serif",
                fontWeight: 600
              },
              children: startPeriod
            }
          ),
          /* @__PURE__ */ jsx32(
            "text",
            {
              x: svgWidth - chartPadding.right,
              y: chartPadding.top - 20,
              textAnchor: "start",
              className: "fill-primary",
              style: {
                fontSize: "18px",
                fontFamily: "'Lato', system-ui, -apple-system, sans-serif",
                fontWeight: 600
              },
              children: endPeriod
            }
          ),
          /* @__PURE__ */ jsx32(
            "line",
            {
              x1: chartPadding.left,
              y1: chartPadding.top,
              x2: chartPadding.left,
              y2: chartPadding.top + chartHeight,
              stroke: "currentColor",
              className: "text-recessed",
              strokeWidth: "1"
            }
          ),
          /* @__PURE__ */ jsx32(
            "line",
            {
              x1: svgWidth - chartPadding.right,
              y1: chartPadding.top,
              x2: svgWidth - chartPadding.right,
              y2: chartPadding.top + chartHeight,
              stroke: "currentColor",
              className: "text-recessed",
              strokeWidth: "1"
            }
          ),
          data.map((item, index) => {
            const startY = valueToY(item.startValue);
            const endY = valueToY(item.endValue);
            const change = item.endValue - item.startValue;
            const changePercent = item.startValue !== 0 ? (change / item.startValue * 100).toFixed(1) : "0";
            const color = item.color || lineColors[index % lineColors.length];
            const isIncrease = change > 0;
            const isDecrease = change < 0;
            let lineColor = color;
            if (highlightChanges) {
              if (isIncrease) lineColor = "rgb(var(--color-growth))";
              else if (isDecrease) lineColor = "rgb(var(--color-error))";
            }
            return /* @__PURE__ */ jsxs22("g", { children: [
              /* @__PURE__ */ jsx32(
                "line",
                {
                  x1: chartPadding.left,
                  y1: startY,
                  x2: svgWidth - chartPadding.right,
                  y2: endY,
                  stroke: lineColor,
                  strokeWidth: "2",
                  opacity: 0.8
                }
              ),
              /* @__PURE__ */ jsx32(
                "circle",
                {
                  cx: chartPadding.left,
                  cy: startY,
                  r: "5",
                  fill: lineColor
                }
              ),
              /* @__PURE__ */ jsx32(
                "circle",
                {
                  cx: svgWidth - chartPadding.right,
                  cy: endY,
                  r: "5",
                  fill: lineColor
                }
              ),
              /* @__PURE__ */ jsx32(
                "text",
                {
                  x: chartPadding.left - 10,
                  y: startY,
                  textAnchor: "end",
                  dominantBaseline: "middle",
                  className: "fill-secondary",
                  style: {
                    fontSize: "14px",
                    fontFamily: "'Lato', system-ui, -apple-system, sans-serif"
                  },
                  children: item.label
                }
              ),
              /* @__PURE__ */ jsx32(
                "text",
                {
                  x: chartPadding.left - 10,
                  y: startY + 16,
                  textAnchor: "end",
                  dominantBaseline: "middle",
                  className: "fill-tertiary",
                  style: {
                    fontSize: "12px",
                    fontFamily: "'Source Code Pro', Consolas, Monaco, monospace",
                    fontVariantNumeric: "tabular-nums"
                  },
                  children: formatValue(item.startValue)
                }
              ),
              /* @__PURE__ */ jsx32(
                "text",
                {
                  x: svgWidth - chartPadding.right + 10,
                  y: endY,
                  textAnchor: "start",
                  dominantBaseline: "middle",
                  className: "fill-secondary",
                  style: {
                    fontSize: "14px",
                    fontFamily: "'Source Code Pro', Consolas, Monaco, monospace",
                    fontVariantNumeric: "tabular-nums"
                  },
                  children: formatValue(item.endValue)
                }
              ),
              highlightChanges && change !== 0 && /* @__PURE__ */ jsxs22(
                "text",
                {
                  x: svgWidth - chartPadding.right + 10,
                  y: endY + 16,
                  textAnchor: "start",
                  dominantBaseline: "middle",
                  style: {
                    fontSize: "11px",
                    fontFamily: "'Source Code Pro', Consolas, Monaco, monospace",
                    fontVariantNumeric: "tabular-nums",
                    fill: isIncrease ? "rgb(var(--color-growth))" : "rgb(var(--color-error))"
                  },
                  children: [
                    isIncrease ? "+" : "",
                    changePercent,
                    "%"
                  ]
                }
              )
            ] }, index);
          })
        ]
      }
    ) })
  ] });
}

// src/charts/ScatterPlot.tsx
import React7 from "react";
import { Fragment as Fragment7, jsx as jsx33, jsxs as jsxs23 } from "react/jsx-runtime";
var CATEGORY_FILL = {
  default: "rgb(var(--color-text-secondary))",
  equity: "rgb(var(--color-growth))",
  fixed_income: "rgb(var(--color-alternative))",
  alternatives: "rgb(var(--color-neutral-data))",
  commodities: "rgb(var(--color-warning))",
  blend: "rgb(var(--color-text-tertiary))",
  growth: "rgb(var(--color-growth))",
  value: "rgb(var(--color-traditional))"
};
function ScatterPlot({
  title,
  subtitle,
  xLabel,
  yLabel,
  data,
  formatX = (v) => v.toFixed(1),
  formatY = (v) => v.toFixed(1),
  height = 400,
  showTrendLine = false,
  showQuadrants = false,
  highlightOutliers = false,
  source = "Aaim Platform Analytics"
}) {
  const svgWidth = 800;
  const svgHeight = 500;
  const chartPadding = { top: 30, right: 30, bottom: 60, left: 50 };
  const chartWidth = svgWidth - chartPadding.left - chartPadding.right;
  const chartHeight = svgHeight - chartPadding.top - chartPadding.bottom;
  const xValues = data.map((d) => d.x);
  const yValues = data.map((d) => d.y);
  const xMin = Math.min(...xValues);
  const xMax = Math.max(...xValues);
  const yMin = Math.min(...yValues);
  const yMax = Math.max(...yValues);
  const xRange = xMax - xMin;
  const yRange = yMax - yMin;
  const xPadding = xRange * 0.1 || 1;
  const yPadding = yRange * 0.1 || 1;
  const scaleXMin = xMin - xPadding;
  const scaleXMax = xMax + xPadding;
  const scaleYMin = yMin - yPadding;
  const scaleYMax = yMax + yPadding;
  const xToPixel = (x) => {
    return chartPadding.left + (x - scaleXMin) / (scaleXMax - scaleXMin) * chartWidth;
  };
  const yToPixel = (y) => {
    return chartPadding.top + (scaleYMax - y) / (scaleYMax - scaleYMin) * chartHeight;
  };
  const trendLine = React7.useMemo(() => {
    if (!showTrendLine || data.length < 2) return null;
    const n = data.length;
    const sumX = data.reduce((acc, d) => acc + d.x, 0);
    const sumY = data.reduce((acc, d) => acc + d.y, 0);
    const sumXY = data.reduce((acc, d) => acc + d.x * d.y, 0);
    const sumX2 = data.reduce((acc, d) => acc + d.x * d.x, 0);
    const denominator = n * sumX2 - sumX * sumX;
    if (denominator === 0) return null;
    const slope = (n * sumXY - sumX * sumY) / denominator;
    const intercept = (sumY - slope * sumX) / n;
    const meanY = sumY / n;
    const ssTotal = data.reduce((acc, d) => acc + Math.pow(d.y - meanY, 2), 0);
    const ssResidual = data.reduce((acc, d) => acc + Math.pow(d.y - (slope * d.x + intercept), 2), 0);
    const rSquared = ssTotal === 0 ? 0 : 1 - ssResidual / ssTotal;
    return { slope, intercept, rSquared };
  }, [data, showTrendLine]);
  const outlierIndices = React7.useMemo(() => {
    if (!highlightOutliers) return /* @__PURE__ */ new Set();
    const meanX = xValues.reduce((a, b) => a + b, 0) / xValues.length;
    const meanY = yValues.reduce((a, b) => a + b, 0) / yValues.length;
    const stdX = Math.sqrt(xValues.reduce((acc, x) => acc + Math.pow(x - meanX, 2), 0) / xValues.length);
    const stdY = Math.sqrt(yValues.reduce((acc, y) => acc + Math.pow(y - meanY, 2), 0) / yValues.length);
    const indices = /* @__PURE__ */ new Set();
    data.forEach((d, i) => {
      const zX = stdX === 0 ? 0 : Math.abs((d.x - meanX) / stdX);
      const zY = stdY === 0 ? 0 : Math.abs((d.y - meanY) / stdY);
      if (zX > 2 || zY > 2) indices.add(i);
    });
    return indices;
  }, [data, xValues, yValues, highlightOutliers]);
  const xTicks = React7.useMemo(() => {
    const tickCount = 5;
    const step = (scaleXMax - scaleXMin) / (tickCount - 1);
    return Array.from({ length: tickCount }, (_, i) => scaleXMin + i * step);
  }, [scaleXMin, scaleXMax]);
  const yTicks = React7.useMemo(() => {
    const tickCount = 5;
    const step = (scaleYMax - scaleYMin) / (tickCount - 1);
    return Array.from({ length: tickCount }, (_, i) => scaleYMin + i * step);
  }, [scaleYMin, scaleYMax]);
  const xMid = (scaleXMin + scaleXMax) / 2;
  const yMid = (scaleYMin + scaleYMax) / 2;
  return /* @__PURE__ */ jsxs23("div", { className: "bg-surface border border-recessed p-8 shadow-sm rounded-sm", children: [
    subtitle && /* @__PURE__ */ jsx33("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-2", children: subtitle }),
    /* @__PURE__ */ jsx33("h3", { className: "text-heading-section font-serif font-semibold mb-6 leading-tight tracking-headline-serif", children: title }),
    /* @__PURE__ */ jsx33("div", { className: "relative", style: { height: `${height}px` }, children: /* @__PURE__ */ jsxs23(
      "svg",
      {
        viewBox: `0 0 ${svgWidth} ${svgHeight}`,
        className: "w-full h-full",
        preserveAspectRatio: "xMidYMid meet",
        children: [
          yTicks.map((tick, i) => /* @__PURE__ */ jsx33(
            "line",
            {
              x1: chartPadding.left,
              y1: yToPixel(tick),
              x2: chartPadding.left + chartWidth,
              y2: yToPixel(tick),
              stroke: "currentColor",
              className: "text-recessed",
              strokeWidth: "1",
              opacity: "0.3"
            },
            `y-grid-${i}`
          )),
          /* @__PURE__ */ jsx33(
            "line",
            {
              x1: chartPadding.left,
              y1: chartPadding.top + chartHeight,
              x2: chartPadding.left + chartWidth,
              y2: chartPadding.top + chartHeight,
              stroke: "currentColor",
              className: "text-tertiary",
              strokeWidth: "1",
              opacity: "0.5"
            }
          ),
          showQuadrants && /* @__PURE__ */ jsxs23(Fragment7, { children: [
            /* @__PURE__ */ jsx33(
              "line",
              {
                x1: xToPixel(xMid),
                y1: chartPadding.top,
                x2: xToPixel(xMid),
                y2: chartPadding.top + chartHeight,
                stroke: "currentColor",
                className: "text-tertiary",
                strokeWidth: "1",
                strokeDasharray: "4,4",
                opacity: "0.5"
              }
            ),
            /* @__PURE__ */ jsx33(
              "line",
              {
                x1: chartPadding.left,
                y1: yToPixel(yMid),
                x2: chartPadding.left + chartWidth,
                y2: yToPixel(yMid),
                stroke: "currentColor",
                className: "text-tertiary",
                strokeWidth: "1",
                strokeDasharray: "4,4",
                opacity: "0.5"
              }
            )
          ] }),
          trendLine && /* @__PURE__ */ jsxs23(Fragment7, { children: [
            /* @__PURE__ */ jsx33(
              "line",
              {
                x1: xToPixel(scaleXMin),
                y1: yToPixel(trendLine.slope * scaleXMin + trendLine.intercept),
                x2: xToPixel(scaleXMax),
                y2: yToPixel(trendLine.slope * scaleXMax + trendLine.intercept),
                stroke: "rgb(var(--color-growth))",
                strokeWidth: "2",
                strokeDasharray: "6,4",
                opacity: "0.6"
              }
            ),
            /* @__PURE__ */ jsxs23(
              "text",
              {
                x: chartPadding.left + chartWidth - 10,
                y: chartPadding.top + 20,
                textAnchor: "end",
                className: "fill-tertiary",
                style: {
                  fontSize: "12px",
                  fontFamily: "'Source Code Pro', Consolas, Monaco, monospace"
                },
                children: [
                  "R\xB2 = ",
                  trendLine.rSquared.toFixed(3)
                ]
              }
            )
          ] }),
          data.map((point, index) => {
            const px = xToPixel(point.x);
            const py = yToPixel(point.y);
            const isOutlier = outlierIndices.has(index);
            const size = point.size || (isOutlier ? 8 : 6);
            const color = point.category ? CATEGORY_FILL[point.category] || CATEGORY_FILL.default : CATEGORY_FILL.default;
            return /* @__PURE__ */ jsxs23("g", { children: [
              /* @__PURE__ */ jsx33(
                "circle",
                {
                  cx: px,
                  cy: py,
                  r: size,
                  fill: isOutlier ? "rgb(var(--color-error))" : color,
                  opacity: 0.8,
                  stroke: isOutlier ? "rgb(var(--color-error))" : "none",
                  strokeWidth: isOutlier ? 2 : 0
                }
              ),
              point.label && /* @__PURE__ */ jsx33(
                "text",
                {
                  x: px + size + 4,
                  y: py,
                  dominantBaseline: "middle",
                  className: "fill-secondary",
                  style: {
                    fontSize: "11px",
                    fontFamily: "'Lato', system-ui, -apple-system, sans-serif"
                  },
                  children: point.label
                }
              )
            ] }, index);
          }),
          xTicks.map((tick, i) => /* @__PURE__ */ jsx33(
            "text",
            {
              x: xToPixel(tick),
              y: chartPadding.top + chartHeight + 25,
              textAnchor: "middle",
              className: "fill-tertiary",
              style: {
                fontSize: "12px",
                fontFamily: "'Source Code Pro', Consolas, Monaco, monospace",
                fontVariantNumeric: "tabular-nums"
              },
              children: formatX(tick)
            },
            `x-label-${i}`
          )),
          yTicks.map((tick, i) => /* @__PURE__ */ jsx33(
            "text",
            {
              x: chartPadding.left + 8,
              y: yToPixel(tick) - 6,
              textAnchor: "start",
              dominantBaseline: "auto",
              className: "fill-tertiary",
              style: {
                fontSize: "11px",
                fontFamily: "'Source Code Pro', Consolas, Monaco, monospace",
                fontVariantNumeric: "tabular-nums"
              },
              children: formatY(tick)
            },
            `y-label-${i}`
          )),
          /* @__PURE__ */ jsx33(
            "text",
            {
              x: chartPadding.left + chartWidth / 2,
              y: svgHeight - 10,
              textAnchor: "middle",
              className: "fill-secondary",
              style: {
                fontSize: "14px",
                fontFamily: "'Lato', system-ui, -apple-system, sans-serif",
                fontWeight: 500
              },
              children: xLabel
            }
          ),
          /* @__PURE__ */ jsx33(
            "text",
            {
              x: 20,
              y: chartPadding.top + chartHeight / 2,
              textAnchor: "middle",
              dominantBaseline: "middle",
              transform: `rotate(-90, 20, ${chartPadding.top + chartHeight / 2})`,
              className: "fill-secondary",
              style: {
                fontSize: "14px",
                fontFamily: "'Lato', system-ui, -apple-system, sans-serif",
                fontWeight: 500
              },
              children: yLabel
            }
          )
        ]
      }
    ) }),
    /* @__PURE__ */ jsx33("div", { className: "mt-4 pt-4 border-t border-recessed", children: /* @__PURE__ */ jsxs23("p", { className: "text-xs font-sans text-tertiary opacity-75", children: [
      "Source: ",
      source
    ] }) })
  ] });
}

// src/charts/BoxPlot.tsx
import React8 from "react";
import { jsx as jsx34, jsxs as jsxs24 } from "react/jsx-runtime";
function BoxPlot({
  title,
  subtitle,
  data,
  formatValue = (v) => v.toFixed(1),
  height = 400,
  horizontal = false,
  showMean = false,
  yLabel
}) {
  const svgWidth = horizontal ? 800 : 700;
  const svgHeight = horizontal ? 400 : 500;
  const chartPadding = horizontal ? { top: 30, right: 30, bottom: 60, left: 120 } : { top: 40, right: 30, bottom: 80, left: 60 };
  const chartWidth = svgWidth - chartPadding.left - chartPadding.right;
  const chartHeight = svgHeight - chartPadding.top - chartPadding.bottom;
  const allValues = data.flatMap((d) => [
    d.min,
    d.max,
    ...d.outliers || []
  ]);
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const range = maxValue - minValue;
  const padding = range * 0.1 || 1;
  const scaleMin = minValue - padding;
  const scaleMax = maxValue + padding;
  const scaleRange = scaleMax - scaleMin;
  const valueToPixel = (value) => {
    if (horizontal) {
      return chartPadding.left + (value - scaleMin) / scaleRange * chartWidth;
    }
    return chartPadding.top + (scaleMax - value) / scaleRange * chartHeight;
  };
  const categoryCount = data.length;
  const categorySize = horizontal ? chartHeight / categoryCount : chartWidth / categoryCount;
  const boxSize = categorySize * 0.6;
  const boxOffset = (categorySize - boxSize) / 2;
  const getCategoryPosition = (index) => {
    if (horizontal) {
      return chartPadding.top + index * categorySize + boxOffset;
    }
    return chartPadding.left + index * categorySize + boxOffset;
  };
  const valueTicks = React8.useMemo(() => {
    const tickCount = 6;
    const step = scaleRange / (tickCount - 1);
    return Array.from({ length: tickCount }, (_, i) => scaleMin + i * step);
  }, [scaleMin, scaleRange]);
  return /* @__PURE__ */ jsxs24("div", { className: "bg-surface border border-recessed p-8 shadow-sm rounded-sm", children: [
    subtitle && /* @__PURE__ */ jsx34("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-2", children: subtitle }),
    /* @__PURE__ */ jsx34("h3", { className: "text-heading-section font-serif font-semibold mb-6 leading-tight tracking-headline-serif", children: title }),
    /* @__PURE__ */ jsx34("div", { className: "relative", style: { height: `${height}px` }, children: /* @__PURE__ */ jsxs24(
      "svg",
      {
        viewBox: `0 0 ${svgWidth} ${svgHeight}`,
        className: "w-full h-full",
        preserveAspectRatio: "xMidYMid meet",
        children: [
          valueTicks.map((tick, i) => /* @__PURE__ */ jsx34(
            "line",
            {
              x1: horizontal ? valueToPixel(tick) : chartPadding.left,
              y1: horizontal ? chartPadding.top : valueToPixel(tick),
              x2: horizontal ? valueToPixel(tick) : chartPadding.left + chartWidth,
              y2: horizontal ? chartPadding.top + chartHeight : valueToPixel(tick),
              stroke: "currentColor",
              className: "text-recessed",
              strokeWidth: "1",
              opacity: "0.3"
            },
            `grid-${i}`
          )),
          data.map((item, index) => {
            const categoryPos = getCategoryPosition(index);
            const boxCenter = categoryPos + boxSize / 2;
            if (horizontal) {
              const minX = valueToPixel(item.min);
              const q1X = valueToPixel(item.q1);
              const medianX = valueToPixel(item.median);
              const q3X = valueToPixel(item.q3);
              const maxX = valueToPixel(item.max);
              const meanX = item.mean !== void 0 ? valueToPixel(item.mean) : null;
              return /* @__PURE__ */ jsxs24("g", { children: [
                /* @__PURE__ */ jsx34(
                  "line",
                  {
                    x1: minX,
                    y1: boxCenter,
                    x2: maxX,
                    y2: boxCenter,
                    stroke: "currentColor",
                    className: "text-tertiary",
                    strokeWidth: "1"
                  }
                ),
                /* @__PURE__ */ jsx34(
                  "line",
                  {
                    x1: minX,
                    y1: categoryPos + boxSize * 0.25,
                    x2: minX,
                    y2: categoryPos + boxSize * 0.75,
                    stroke: "currentColor",
                    className: "text-tertiary",
                    strokeWidth: "1"
                  }
                ),
                /* @__PURE__ */ jsx34(
                  "line",
                  {
                    x1: maxX,
                    y1: categoryPos + boxSize * 0.25,
                    x2: maxX,
                    y2: categoryPos + boxSize * 0.75,
                    stroke: "currentColor",
                    className: "text-tertiary",
                    strokeWidth: "1"
                  }
                ),
                /* @__PURE__ */ jsx34(
                  "rect",
                  {
                    x: q1X,
                    y: categoryPos,
                    width: q3X - q1X,
                    height: boxSize,
                    fill: "rgb(var(--color-canvas))",
                    stroke: "currentColor",
                    className: "text-primary",
                    strokeWidth: "2"
                  }
                ),
                /* @__PURE__ */ jsx34(
                  "line",
                  {
                    x1: medianX,
                    y1: categoryPos,
                    x2: medianX,
                    y2: categoryPos + boxSize,
                    stroke: "currentColor",
                    className: "text-primary",
                    strokeWidth: "2"
                  }
                ),
                showMean && meanX !== null && /* @__PURE__ */ jsx34(
                  "circle",
                  {
                    cx: meanX,
                    cy: boxCenter,
                    r: "4",
                    fill: "rgb(var(--color-growth))"
                  }
                ),
                item.outliers?.map((outlier, oi) => /* @__PURE__ */ jsx34(
                  "circle",
                  {
                    cx: valueToPixel(outlier),
                    cy: boxCenter,
                    r: "4",
                    fill: "none",
                    stroke: "rgb(var(--color-error))",
                    strokeWidth: "1.5"
                  },
                  oi
                )),
                /* @__PURE__ */ jsx34(
                  "text",
                  {
                    x: chartPadding.left - 10,
                    y: boxCenter,
                    textAnchor: "end",
                    dominantBaseline: "middle",
                    className: "fill-secondary",
                    style: {
                      fontSize: "13px",
                      fontFamily: "'Lato', system-ui, -apple-system, sans-serif"
                    },
                    children: item.label
                  }
                )
              ] }, index);
            } else {
              const minY = valueToPixel(item.min);
              const q1Y = valueToPixel(item.q1);
              const medianY = valueToPixel(item.median);
              const q3Y = valueToPixel(item.q3);
              const maxY = valueToPixel(item.max);
              const meanY = item.mean !== void 0 ? valueToPixel(item.mean) : null;
              return /* @__PURE__ */ jsxs24("g", { children: [
                /* @__PURE__ */ jsx34(
                  "line",
                  {
                    x1: boxCenter,
                    y1: minY,
                    x2: boxCenter,
                    y2: maxY,
                    stroke: "currentColor",
                    className: "text-tertiary",
                    strokeWidth: "1"
                  }
                ),
                /* @__PURE__ */ jsx34(
                  "line",
                  {
                    x1: categoryPos + boxSize * 0.25,
                    y1: minY,
                    x2: categoryPos + boxSize * 0.75,
                    y2: minY,
                    stroke: "currentColor",
                    className: "text-tertiary",
                    strokeWidth: "1"
                  }
                ),
                /* @__PURE__ */ jsx34(
                  "line",
                  {
                    x1: categoryPos + boxSize * 0.25,
                    y1: maxY,
                    x2: categoryPos + boxSize * 0.75,
                    y2: maxY,
                    stroke: "currentColor",
                    className: "text-tertiary",
                    strokeWidth: "1"
                  }
                ),
                /* @__PURE__ */ jsx34(
                  "rect",
                  {
                    x: categoryPos,
                    y: q3Y,
                    width: boxSize,
                    height: q1Y - q3Y,
                    fill: "rgb(var(--color-canvas))",
                    stroke: "currentColor",
                    className: "text-primary",
                    strokeWidth: "2"
                  }
                ),
                /* @__PURE__ */ jsx34(
                  "line",
                  {
                    x1: categoryPos,
                    y1: medianY,
                    x2: categoryPos + boxSize,
                    y2: medianY,
                    stroke: "currentColor",
                    className: "text-primary",
                    strokeWidth: "2"
                  }
                ),
                showMean && meanY !== null && /* @__PURE__ */ jsx34(
                  "circle",
                  {
                    cx: boxCenter,
                    cy: meanY,
                    r: "4",
                    fill: "rgb(var(--color-growth))"
                  }
                ),
                item.outliers?.map((outlier, oi) => /* @__PURE__ */ jsx34(
                  "circle",
                  {
                    cx: boxCenter,
                    cy: valueToPixel(outlier),
                    r: "4",
                    fill: "none",
                    stroke: "rgb(var(--color-error))",
                    strokeWidth: "1.5"
                  },
                  oi
                )),
                /* @__PURE__ */ jsx34(
                  "text",
                  {
                    x: boxCenter,
                    y: svgHeight - chartPadding.bottom + 25,
                    textAnchor: "middle",
                    className: "fill-secondary",
                    style: {
                      fontSize: "13px",
                      fontFamily: "'Lato', system-ui, -apple-system, sans-serif"
                    },
                    children: item.label
                  }
                )
              ] }, index);
            }
          }),
          valueTicks.map((tick, i) => /* @__PURE__ */ jsx34(
            "text",
            {
              x: horizontal ? valueToPixel(tick) : chartPadding.left - 10,
              y: horizontal ? svgHeight - chartPadding.bottom + 25 : valueToPixel(tick),
              textAnchor: horizontal ? "middle" : "end",
              dominantBaseline: horizontal ? "auto" : "middle",
              className: "fill-tertiary",
              style: {
                fontSize: "12px",
                fontFamily: "'Source Code Pro', Consolas, Monaco, monospace",
                fontVariantNumeric: "tabular-nums"
              },
              children: formatValue(tick)
            },
            `value-label-${i}`
          )),
          yLabel && !horizontal && /* @__PURE__ */ jsx34(
            "text",
            {
              x: 15,
              y: chartPadding.top + chartHeight / 2,
              textAnchor: "middle",
              transform: `rotate(-90, 15, ${chartPadding.top + chartHeight / 2})`,
              className: "fill-secondary",
              style: {
                fontSize: "13px",
                fontFamily: "'Lato', system-ui, -apple-system, sans-serif",
                fontWeight: 500
              },
              children: yLabel
            }
          )
        ]
      }
    ) }),
    /* @__PURE__ */ jsxs24("div", { className: "mt-4 pt-4 border-t border-recessed flex items-center gap-6 text-xs font-sans", children: [
      /* @__PURE__ */ jsxs24("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx34("div", { className: "w-8 h-3 border border-primary bg-canvas" }),
        /* @__PURE__ */ jsx34("span", { className: "text-secondary", children: "IQR (Q1-Q3)" })
      ] }),
      /* @__PURE__ */ jsxs24("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx34("div", { className: "w-4 h-0.5 bg-primary" }),
        /* @__PURE__ */ jsx34("span", { className: "text-secondary", children: "Median" })
      ] }),
      showMean && /* @__PURE__ */ jsxs24("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx34("div", { className: "w-2 h-2 rounded-full bg-growth" }),
        /* @__PURE__ */ jsx34("span", { className: "text-secondary", children: "Mean" })
      ] }),
      /* @__PURE__ */ jsxs24("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx34("div", { className: "w-2 h-2 rounded-full border border-error" }),
        /* @__PURE__ */ jsx34("span", { className: "text-secondary", children: "Outlier" })
      ] })
    ] })
  ] });
}

// src/charts/BubbleChart.tsx
import React9 from "react";
import { jsx as jsx35, jsxs as jsxs25 } from "react/jsx-runtime";
function BubbleChart({
  title,
  subtitle,
  xLabel,
  yLabel,
  sizeLabel,
  data,
  formatX = (v) => v.toFixed(1),
  formatY = (v) => v.toFixed(1),
  formatSize = (v) => v.toLocaleString(),
  height = 450,
  minBubbleSize = 8,
  maxBubbleSize = 50
}) {
  const svgWidth = 900;
  const svgHeight = 550;
  const chartPadding = { top: 40, right: 40, bottom: 70, left: 70 };
  const chartWidth = svgWidth - chartPadding.left - chartPadding.right;
  const chartHeight = svgHeight - chartPadding.top - chartPadding.bottom;
  const xValues = data.map((d) => d.x);
  const yValues = data.map((d) => d.y);
  const sizeValues = data.map((d) => d.size);
  const xMin = Math.min(...xValues);
  const xMax = Math.max(...xValues);
  const yMin = Math.min(...yValues);
  const yMax = Math.max(...yValues);
  const sizeMin = Math.min(...sizeValues);
  const sizeMax = Math.max(...sizeValues);
  const xRange = xMax - xMin;
  const yRange = yMax - yMin;
  const xPadding = xRange * 0.15 || 1;
  const yPadding = yRange * 0.15 || 1;
  const scaleXMin = xMin - xPadding;
  const scaleXMax = xMax + xPadding;
  const scaleYMin = yMin - yPadding;
  const scaleYMax = yMax + yPadding;
  const xToPixel = (x) => {
    return chartPadding.left + (x - scaleXMin) / (scaleXMax - scaleXMin) * chartWidth;
  };
  const yToPixel = (y) => {
    return chartPadding.top + (scaleYMax - y) / (scaleYMax - scaleYMin) * chartHeight;
  };
  const sizeToRadius = (size) => {
    if (sizeMax === sizeMin) return (minBubbleSize + maxBubbleSize) / 2;
    const normalized = (size - sizeMin) / (sizeMax - sizeMin);
    const minArea = minBubbleSize * minBubbleSize;
    const maxArea = maxBubbleSize * maxBubbleSize;
    const area = minArea + normalized * (maxArea - minArea);
    return Math.sqrt(area);
  };
  const categoryColors2 = {
    default: "rgb(var(--color-text-secondary))",
    equity: "rgb(var(--color-growth))",
    fixed_income: "rgb(var(--color-alternative))",
    alternatives: "rgb(var(--color-neutral-data))",
    commodities: "rgb(var(--color-warning))",
    cash: "rgb(var(--color-text-tertiary))",
    growth: "rgb(var(--color-growth))",
    value: "rgb(var(--color-error))",
    blend: "rgb(var(--color-text-quaternary))"
  };
  const xTicks = React9.useMemo(() => {
    const tickCount = 6;
    const step = (scaleXMax - scaleXMin) / (tickCount - 1);
    return Array.from({ length: tickCount }, (_, i) => scaleXMin + i * step);
  }, [scaleXMin, scaleXMax]);
  const yTicks = React9.useMemo(() => {
    const tickCount = 6;
    const step = (scaleYMax - scaleYMin) / (tickCount - 1);
    return Array.from({ length: tickCount }, (_, i) => scaleYMin + i * step);
  }, [scaleYMin, scaleYMax]);
  const sizeLegendValues = [sizeMin, (sizeMin + sizeMax) / 2, sizeMax];
  return /* @__PURE__ */ jsxs25("div", { className: "bg-surface border border-recessed p-8 shadow-sm rounded-sm", children: [
    subtitle && /* @__PURE__ */ jsx35("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-2", children: subtitle }),
    /* @__PURE__ */ jsx35("h3", { className: "text-heading-section font-serif font-semibold mb-6 leading-tight tracking-headline-serif", children: title }),
    /* @__PURE__ */ jsx35("div", { className: "relative", style: { height: `${height}px` }, children: /* @__PURE__ */ jsxs25(
      "svg",
      {
        viewBox: `0 0 ${svgWidth} ${svgHeight}`,
        className: "w-full h-full",
        preserveAspectRatio: "xMidYMid meet",
        children: [
          xTicks.map((tick, i) => /* @__PURE__ */ jsx35(
            "line",
            {
              x1: xToPixel(tick),
              y1: chartPadding.top,
              x2: xToPixel(tick),
              y2: chartPadding.top + chartHeight,
              stroke: "currentColor",
              className: "text-recessed",
              strokeWidth: "1",
              opacity: "0.3"
            },
            `x-grid-${i}`
          )),
          yTicks.map((tick, i) => /* @__PURE__ */ jsx35(
            "line",
            {
              x1: chartPadding.left,
              y1: yToPixel(tick),
              x2: chartPadding.left + chartWidth,
              y2: yToPixel(tick),
              stroke: "currentColor",
              className: "text-recessed",
              strokeWidth: "1",
              opacity: "0.3"
            },
            `y-grid-${i}`
          )),
          scaleXMin < 0 && scaleXMax > 0 && /* @__PURE__ */ jsx35(
            "line",
            {
              x1: xToPixel(0),
              y1: chartPadding.top,
              x2: xToPixel(0),
              y2: chartPadding.top + chartHeight,
              stroke: "currentColor",
              className: "text-tertiary",
              strokeWidth: "1",
              opacity: "0.5"
            }
          ),
          scaleYMin < 0 && scaleYMax > 0 && /* @__PURE__ */ jsx35(
            "line",
            {
              x1: chartPadding.left,
              y1: yToPixel(0),
              x2: chartPadding.left + chartWidth,
              y2: yToPixel(0),
              stroke: "currentColor",
              className: "text-tertiary",
              strokeWidth: "1",
              opacity: "0.5"
            }
          ),
          [...data].sort((a, b) => b.size - a.size).map((point, index) => {
            const px = xToPixel(point.x);
            const py = yToPixel(point.y);
            const radius = sizeToRadius(point.size);
            const color = point.category ? categoryColors2[point.category] || categoryColors2.default : categoryColors2.default;
            return /* @__PURE__ */ jsxs25("g", { children: [
              /* @__PURE__ */ jsx35(
                "circle",
                {
                  cx: px,
                  cy: py,
                  r: radius,
                  fill: color,
                  opacity: 0.6,
                  stroke: color,
                  strokeWidth: "2",
                  strokeOpacity: "0.8"
                }
              ),
              point.label && radius >= 15 && /* @__PURE__ */ jsx35(
                "text",
                {
                  x: px,
                  y: py,
                  textAnchor: "middle",
                  dominantBaseline: "middle",
                  className: "fill-white",
                  style: {
                    fontSize: Math.min(12, radius * 0.6) + "px",
                    fontFamily: "'Lato', system-ui, -apple-system, sans-serif",
                    fontWeight: 500,
                    pointerEvents: "none"
                  },
                  children: point.label.length > 8 ? point.label.slice(0, 6) + "..." : point.label
                }
              )
            ] }, index);
          }),
          xTicks.map((tick, i) => /* @__PURE__ */ jsx35(
            "text",
            {
              x: xToPixel(tick),
              y: chartPadding.top + chartHeight + 25,
              textAnchor: "middle",
              className: "fill-tertiary",
              style: {
                fontSize: "12px",
                fontFamily: "'Source Code Pro', Consolas, Monaco, monospace",
                fontVariantNumeric: "tabular-nums"
              },
              children: formatX(tick)
            },
            `x-label-${i}`
          )),
          yTicks.map((tick, i) => /* @__PURE__ */ jsx35(
            "text",
            {
              x: chartPadding.left - 10,
              y: yToPixel(tick),
              textAnchor: "end",
              dominantBaseline: "middle",
              className: "fill-tertiary",
              style: {
                fontSize: "12px",
                fontFamily: "'Source Code Pro', Consolas, Monaco, monospace",
                fontVariantNumeric: "tabular-nums"
              },
              children: formatY(tick)
            },
            `y-label-${i}`
          )),
          /* @__PURE__ */ jsx35(
            "text",
            {
              x: chartPadding.left + chartWidth / 2,
              y: svgHeight - 15,
              textAnchor: "middle",
              className: "fill-secondary",
              style: {
                fontSize: "14px",
                fontFamily: "'Lato', system-ui, -apple-system, sans-serif",
                fontWeight: 500
              },
              children: xLabel
            }
          ),
          /* @__PURE__ */ jsx35(
            "text",
            {
              x: 18,
              y: chartPadding.top + chartHeight / 2,
              textAnchor: "middle",
              transform: `rotate(-90, 18, ${chartPadding.top + chartHeight / 2})`,
              className: "fill-secondary",
              style: {
                fontSize: "14px",
                fontFamily: "'Lato', system-ui, -apple-system, sans-serif",
                fontWeight: 500
              },
              children: yLabel
            }
          )
        ]
      }
    ) }),
    /* @__PURE__ */ jsx35("div", { className: "mt-4 pt-4 border-t border-recessed", children: /* @__PURE__ */ jsxs25("div", { className: "flex items-center gap-6", children: [
      /* @__PURE__ */ jsxs25("span", { className: "text-xs font-sans text-tertiary uppercase tracking-wider", children: [
        sizeLabel,
        ":"
      ] }),
      /* @__PURE__ */ jsx35("div", { className: "flex items-end gap-4", children: sizeLegendValues.map((size, i) => {
        const radius = sizeToRadius(size);
        return /* @__PURE__ */ jsxs25("div", { className: "flex flex-col items-center gap-1", children: [
          /* @__PURE__ */ jsx35("svg", { width: radius * 2 + 4, height: radius * 2 + 4, children: /* @__PURE__ */ jsx35(
            "circle",
            {
              cx: radius + 2,
              cy: radius + 2,
              r: radius,
              fill: "rgb(var(--color-text-secondary))",
              opacity: "0.3",
              stroke: "rgb(var(--color-text-secondary))",
              strokeWidth: "1"
            }
          ) }),
          /* @__PURE__ */ jsx35("span", { className: "text-xs font-mono text-tertiary tabular-nums", children: formatSize(size) })
        ] }, i);
      }) })
    ] }) })
  ] });
}

// src/charts/CalendarHeatmap.tsx
import React10 from "react";
import { Fragment as Fragment8, jsx as jsx36, jsxs as jsxs26 } from "react/jsx-runtime";
function CalendarHeatmap({
  title,
  subtitle,
  data,
  startDate,
  endDate,
  formatValue = (v) => v.toFixed(2),
  height = 200,
  colorScale = "diverging",
  thresholds = { negative: 0, positive: 0 }
}) {
  const dataMap = React10.useMemo(() => {
    const map = /* @__PURE__ */ new Map();
    data.forEach((d) => map.set(d.date, d.value));
    return map;
  }, [data]);
  const dateRange = React10.useMemo(() => {
    const dates = data.map((d) => new Date(d.date).getTime());
    const start = startDate ? new Date(startDate) : new Date(Math.min(...dates));
    const end = endDate ? new Date(endDate) : new Date(Math.max(...dates));
    return { start, end };
  }, [data, startDate, endDate]);
  const valueRange = React10.useMemo(() => {
    const values = data.map((d) => d.value);
    return {
      min: Math.min(...values),
      max: Math.max(...values)
    };
  }, [data]);
  const weeks = React10.useMemo(() => {
    const result = [];
    const current = new Date(dateRange.start);
    current.setDate(current.getDate() - current.getDay());
    while (current <= dateRange.end) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        const dateStr = current.toISOString().split("T")[0];
        const isInRange = current >= dateRange.start && current <= dateRange.end;
        week.push({
          date: new Date(current),
          value: isInRange ? dataMap.get(dateStr) ?? null : null
        });
        current.setDate(current.getDate() + 1);
      }
      result.push(week);
    }
    return result;
  }, [dateRange, dataMap]);
  const getColor = (value) => {
    if (value === null) return "rgb(var(--color-recessed) / 0.4)";
    if (colorScale === "diverging") {
      const negThreshold = thresholds.negative ?? 0;
      const posThreshold = thresholds.positive ?? 0;
      if (value < negThreshold) {
        const intensity = Math.min(
          1,
          Math.abs(value - negThreshold) / Math.abs(valueRange.min - negThreshold)
        );
        if (intensity < 0.25) return "rgb(var(--color-error) / 0.2)";
        if (intensity < 0.5) return "rgb(var(--color-error) / 0.45)";
        if (intensity < 0.75) return "rgb(var(--color-error) / 0.7)";
        return "rgb(var(--color-error) / 1)";
      } else if (value > posThreshold) {
        const intensity = Math.min(
          1,
          (value - posThreshold) / (valueRange.max - posThreshold)
        );
        if (intensity < 0.25) return "rgb(var(--color-success) / 0.2)";
        if (intensity < 0.5) return "rgb(var(--color-success) / 0.45)";
        if (intensity < 0.75) return "rgb(var(--color-success) / 0.7)";
        return "rgb(var(--color-success) / 1)";
      }
      return "rgb(var(--color-recessed) / 0.25)";
    } else {
      const normalized = (value - valueRange.min) / (valueRange.max - valueRange.min);
      if (normalized < 0.2) return "rgb(var(--color-growth) / 0.2)";
      if (normalized < 0.4) return "rgb(var(--color-growth) / 0.45)";
      if (normalized < 0.6) return "rgb(var(--color-growth) / 0.7)";
      if (normalized < 0.8) return "rgb(var(--color-growth) / 0.85)";
      return "rgb(var(--color-growth) / 1)";
    }
  };
  const monthLabels = React10.useMemo(() => {
    const labels = [];
    let lastMonth = -1;
    weeks.forEach((week, weekIndex) => {
      const firstDayOfWeek = week[0].date;
      const month = firstDayOfWeek.getMonth();
      if (month !== lastMonth) {
        labels.push({
          label: firstDayOfWeek.toLocaleDateString("en-US", { month: "short" }),
          weekIndex
        });
        lastMonth = month;
      }
    });
    return labels;
  }, [weeks]);
  const cellSize = 14;
  const cellGap = 3;
  const dayLabelWidth = 30;
  const monthLabelHeight = 20;
  const svgWidth = dayLabelWidth + weeks.length * (cellSize + cellGap);
  const svgHeight = monthLabelHeight + 7 * (cellSize + cellGap);
  const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return /* @__PURE__ */ jsxs26("div", { className: "bg-surface border border-recessed p-8 shadow-sm rounded-sm", children: [
    subtitle && /* @__PURE__ */ jsx36("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-2", children: subtitle }),
    /* @__PURE__ */ jsx36("h3", { className: "text-heading-section font-serif font-semibold mb-6 leading-tight tracking-headline-serif", children: title }),
    /* @__PURE__ */ jsx36("div", { className: "overflow-x-auto", style: { maxHeight: `${height}px` }, children: /* @__PURE__ */ jsxs26("svg", { width: svgWidth, height: svgHeight, className: "block", children: [
      monthLabels.map((label, i) => /* @__PURE__ */ jsx36(
        "text",
        {
          x: dayLabelWidth + label.weekIndex * (cellSize + cellGap),
          y: 12,
          className: "fill-tertiary",
          style: {
            fontSize: "11px",
            fontFamily: "'Lato', system-ui, -apple-system, sans-serif"
          },
          children: label.label
        },
        i
      )),
      [1, 3, 5].map((dayIndex) => /* @__PURE__ */ jsx36(
        "text",
        {
          x: 0,
          y: monthLabelHeight + dayIndex * (cellSize + cellGap) + cellSize / 2 + 4,
          className: "fill-tertiary",
          style: {
            fontSize: "10px",
            fontFamily: "'Lato', system-ui, -apple-system, sans-serif"
          },
          children: dayLabels[dayIndex]
        },
        dayIndex
      )),
      weeks.map(
        (week, weekIndex) => week.map((day, dayIndex) => {
          if (day.date < dateRange.start || day.date > dateRange.end) {
            return null;
          }
          return /* @__PURE__ */ jsx36(
            "rect",
            {
              x: dayLabelWidth + weekIndex * (cellSize + cellGap),
              y: monthLabelHeight + dayIndex * (cellSize + cellGap),
              width: cellSize,
              height: cellSize,
              rx: 2,
              fill: getColor(day.value),
              stroke: day.value !== null ? "none" : "rgb(var(--color-recessed))",
              strokeWidth: day.value !== null ? 0 : 0.5,
              children: /* @__PURE__ */ jsxs26("title", { children: [
                day.date.toLocaleDateString(),
                ":",
                " ",
                day.value !== null ? formatValue(day.value) : "No data"
              ] })
            },
            `${weekIndex}-${dayIndex}`
          );
        })
      )
    ] }) }),
    /* @__PURE__ */ jsxs26("div", { className: "mt-4 pt-4 border-t border-recessed flex items-center gap-4", children: [
      /* @__PURE__ */ jsx36("span", { className: "text-xs font-sans text-tertiary", children: "Less" }),
      /* @__PURE__ */ jsx36("div", { className: "flex gap-1", children: colorScale === "diverging" ? /* @__PURE__ */ jsxs26(Fragment8, { children: [
        /* @__PURE__ */ jsx36("div", { className: "w-3 h-3 rounded-sm bg-error" }),
        /* @__PURE__ */ jsx36("div", { className: "w-3 h-3 rounded-sm bg-error/[0.45]" }),
        /* @__PURE__ */ jsx36("div", { className: "w-3 h-3 rounded-sm bg-recessed/[0.25]" }),
        /* @__PURE__ */ jsx36("div", { className: "w-3 h-3 rounded-sm bg-success/[0.45]" }),
        /* @__PURE__ */ jsx36("div", { className: "w-3 h-3 rounded-sm bg-success" })
      ] }) : /* @__PURE__ */ jsxs26(Fragment8, { children: [
        /* @__PURE__ */ jsx36("div", { className: "w-3 h-3 rounded-sm bg-growth/[0.2]" }),
        /* @__PURE__ */ jsx36("div", { className: "w-3 h-3 rounded-sm bg-growth/[0.45]" }),
        /* @__PURE__ */ jsx36("div", { className: "w-3 h-3 rounded-sm bg-growth/[0.7]" }),
        /* @__PURE__ */ jsx36("div", { className: "w-3 h-3 rounded-sm bg-growth/[0.85]" }),
        /* @__PURE__ */ jsx36("div", { className: "w-3 h-3 rounded-sm bg-growth" })
      ] }) }),
      /* @__PURE__ */ jsx36("span", { className: "text-xs font-sans text-tertiary", children: "More" })
    ] })
  ] });
}

// src/charts/RadarChart.tsx
import React11 from "react";
import { jsx as jsx37, jsxs as jsxs27 } from "react/jsx-runtime";
function RadarChart({
  title,
  subtitle,
  axes,
  data,
  height = 400,
  maxValue,
  showLegend = true,
  gridLevels = 5
}) {
  const svgSize = 500;
  const center = svgSize / 2;
  const radius = svgSize * 0.35;
  const calculatedMax = React11.useMemo(() => {
    if (maxValue !== void 0) return maxValue;
    const allValues = data.flatMap((d) => d.values);
    return Math.max(...allValues) * 1.1;
  }, [data, maxValue]);
  const angleStep = 2 * Math.PI / axes.length;
  const getPoint = (value, axisIndex) => {
    const angle = -Math.PI / 2 + axisIndex * angleStep;
    const r = value / calculatedMax * radius;
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle)
    };
  };
  const seriesColors = [
    "rgb(var(--color-traditional))",
    // traditional
    "rgb(var(--color-growth))",
    // growth
    "rgb(var(--color-neutral-data))",
    // neutral-data
    "rgb(var(--color-alternative))",
    // alternative
    "rgb(var(--color-error))",
    // error
    "rgb(var(--color-text-secondary))"
    // neutral chrome
  ];
  const gridPolygons = React11.useMemo(() => {
    return Array.from({ length: gridLevels }, (_, level) => {
      const gridRadius = radius * ((level + 1) / gridLevels);
      const points = axes.map((_2, i) => {
        const angle = -Math.PI / 2 + i * angleStep;
        return `${center + gridRadius * Math.cos(angle)},${center + gridRadius * Math.sin(angle)}`;
      });
      return points.join(" ");
    });
  }, [axes, gridLevels, angleStep, center, radius]);
  return /* @__PURE__ */ jsxs27("div", { className: "bg-surface border border-recessed p-8 shadow-sm rounded-sm", children: [
    subtitle && /* @__PURE__ */ jsx37("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-2", children: subtitle }),
    /* @__PURE__ */ jsx37("h3", { className: "text-heading-section font-serif font-semibold mb-6 leading-tight tracking-headline-serif", children: title }),
    /* @__PURE__ */ jsx37("div", { className: "relative", style: { height: `${height}px` }, children: /* @__PURE__ */ jsxs27(
      "svg",
      {
        viewBox: `0 0 ${svgSize} ${svgSize}`,
        className: "w-full h-full",
        preserveAspectRatio: "xMidYMid meet",
        children: [
          gridPolygons.map((points, level) => /* @__PURE__ */ jsx37(
            "polygon",
            {
              points,
              fill: "none",
              stroke: "currentColor",
              className: "text-recessed",
              strokeWidth: "1",
              opacity: level === gridLevels - 1 ? 0.5 : 0.3
            },
            `grid-${level}`
          )),
          axes.map((_, i) => {
            const endPoint = getPoint(calculatedMax, i);
            return /* @__PURE__ */ jsx37(
              "line",
              {
                x1: center,
                y1: center,
                x2: endPoint.x,
                y2: endPoint.y,
                stroke: "currentColor",
                className: "text-recessed",
                strokeWidth: "1",
                opacity: "0.5"
              },
              `axis-${i}`
            );
          }),
          data.map((series, seriesIndex) => {
            const points = series.values.map((value, i) => {
              const point = getPoint(value, i);
              return `${point.x},${point.y}`;
            });
            const color = series.color || seriesColors[seriesIndex % seriesColors.length];
            return /* @__PURE__ */ jsxs27("g", { children: [
              /* @__PURE__ */ jsx37(
                "polygon",
                {
                  points: points.join(" "),
                  fill: color,
                  fillOpacity: "0.15",
                  stroke: color,
                  strokeWidth: "2"
                }
              ),
              series.values.map((value, i) => {
                const point = getPoint(value, i);
                return /* @__PURE__ */ jsx37(
                  "circle",
                  {
                    cx: point.x,
                    cy: point.y,
                    r: "4",
                    fill: color
                  },
                  `point-${i}`
                );
              })
            ] }, `series-${seriesIndex}`);
          }),
          axes.map((label, i) => {
            const angle = -Math.PI / 2 + i * angleStep;
            const labelRadius = radius + 30;
            const x = center + labelRadius * Math.cos(angle);
            const y = center + labelRadius * Math.sin(angle);
            let textAnchor = "middle";
            if (Math.abs(Math.cos(angle)) > 0.3) {
              textAnchor = Math.cos(angle) > 0 ? "start" : "end";
            }
            return /* @__PURE__ */ jsx37(
              "text",
              {
                x,
                y,
                textAnchor,
                dominantBaseline: "middle",
                className: "fill-secondary",
                style: {
                  fontSize: "13px",
                  fontFamily: "'Lato', system-ui, -apple-system, sans-serif"
                },
                children: label
              },
              `label-${i}`
            );
          }),
          Array.from({ length: gridLevels }, (_, level) => {
            const value = calculatedMax * (level + 1) / gridLevels;
            const point = getPoint(value, 0);
            return /* @__PURE__ */ jsx37(
              "text",
              {
                x: point.x + 5,
                y: point.y - 5,
                className: "fill-tertiary",
                style: {
                  fontSize: "10px",
                  fontFamily: "'Source Code Pro', Consolas, Monaco, monospace"
                },
                children: value.toFixed(0)
              },
              `value-${level}`
            );
          })
        ]
      }
    ) }),
    showLegend && data.length > 1 && /* @__PURE__ */ jsx37("div", { className: "mt-4 pt-4 border-t border-recessed flex flex-wrap gap-6", children: data.map((series, i) => {
      const color = series.color || seriesColors[i % seriesColors.length];
      return /* @__PURE__ */ jsxs27("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx37(
          "div",
          {
            className: "w-3 h-3 rounded-sm",
            style: { backgroundColor: color }
          }
        ),
        /* @__PURE__ */ jsx37("span", { className: "text-xs font-sans text-secondary", children: series.label })
      ] }, i);
    }) })
  ] });
}

// src/charts/ParallelCoordinates.tsx
import React12 from "react";
import { jsx as jsx38, jsxs as jsxs28 } from "react/jsx-runtime";
function ParallelCoordinates({
  title,
  subtitle,
  axes,
  data,
  height = 400,
  showLabels = true
}) {
  const svgWidth = 900;
  const svgHeight = 500;
  const chartPadding = { top: 60, right: 80, bottom: 60, left: 80 };
  const chartWidth = svgWidth - chartPadding.left - chartPadding.right;
  const chartHeight = svgHeight - chartPadding.top - chartPadding.bottom;
  const axisSpacing = chartWidth / (axes.length - 1);
  const axisRanges = React12.useMemo(() => {
    return axes.map((axis, axisIndex) => {
      const values = data.map((d) => d.values[axisIndex]);
      const min = axis.min ?? Math.min(...values);
      const max = axis.max ?? Math.max(...values);
      const range = max - min;
      const padding = range * 0.1 || 1;
      return {
        min: min - padding,
        max: max + padding,
        inverted: axis.inverted ?? false
      };
    });
  }, [axes, data]);
  const getY = (value, axisIndex) => {
    const range = axisRanges[axisIndex];
    const normalized = (value - range.min) / (range.max - range.min);
    const adjustedNormalized = range.inverted ? normalized : 1 - normalized;
    return chartPadding.top + adjustedNormalized * chartHeight;
  };
  const getX = (axisIndex) => {
    return chartPadding.left + axisIndex * axisSpacing;
  };
  const defaultColors2 = [
    "rgb(var(--color-text-primary))",
    // primary
    "rgb(var(--color-growth))",
    // growth
    "rgb(var(--color-neutral-data))",
    // neutral-data
    "rgb(var(--color-alternative))",
    // alternative
    "rgb(var(--color-error))",
    // error
    "rgb(var(--color-text-secondary))",
    // neutral
    "rgb(var(--color-text-tertiary))",
    // neutral
    "rgb(var(--color-text-quaternary))"
    // neutral
  ];
  const generatePath = (values) => {
    const points = values.map((value, i) => {
      const x = getX(i);
      const y = getY(value, i);
      return `${x},${y}`;
    });
    return `M ${points.join(" L ")}`;
  };
  const axisTicks = React12.useMemo(() => {
    return axes.map((_axis, i) => {
      const range = axisRanges[i];
      const tickCount = 5;
      const step = (range.max - range.min) / (tickCount - 1);
      return Array.from({ length: tickCount }, (_, j) => range.min + j * step);
    });
  }, [axes, axisRanges]);
  return /* @__PURE__ */ jsxs28("div", { className: "bg-surface border border-recessed p-8 shadow-sm rounded-sm", children: [
    subtitle && /* @__PURE__ */ jsx38("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-2", children: subtitle }),
    /* @__PURE__ */ jsx38("h3", { className: "text-heading-section font-serif font-semibold mb-6 leading-tight tracking-headline-serif", children: title }),
    /* @__PURE__ */ jsx38("div", { className: "relative", style: { height: `${height}px` }, children: /* @__PURE__ */ jsxs28(
      "svg",
      {
        viewBox: `0 0 ${svgWidth} ${svgHeight}`,
        className: "w-full h-full",
        preserveAspectRatio: "xMidYMid meet",
        children: [
          axes.map((axis, i) => {
            const x = getX(i);
            const format = axis.format || ((v) => v.toFixed(1));
            return /* @__PURE__ */ jsxs28("g", { children: [
              /* @__PURE__ */ jsx38(
                "line",
                {
                  x1: x,
                  y1: chartPadding.top,
                  x2: x,
                  y2: chartPadding.top + chartHeight,
                  stroke: "currentColor",
                  className: "text-recessed",
                  strokeWidth: "2"
                }
              ),
              /* @__PURE__ */ jsx38(
                "text",
                {
                  x,
                  y: chartPadding.top - 25,
                  textAnchor: "middle",
                  className: "fill-primary",
                  style: {
                    fontSize: "13px",
                    fontFamily: "'Lato', system-ui, -apple-system, sans-serif",
                    fontWeight: 500
                  },
                  children: axis.label
                }
              ),
              axis.inverted && /* @__PURE__ */ jsx38(
                "text",
                {
                  x,
                  y: chartPadding.top - 10,
                  textAnchor: "middle",
                  className: "fill-tertiary",
                  style: {
                    fontSize: "9px",
                    fontFamily: "'Lato', system-ui, -apple-system, sans-serif"
                  },
                  children: "(lower is better)"
                }
              ),
              axisTicks[i].map((tick, j) => {
                const y = getY(tick, i);
                return /* @__PURE__ */ jsxs28("g", { children: [
                  /* @__PURE__ */ jsx38(
                    "line",
                    {
                      x1: x - 4,
                      y1: y,
                      x2: x + 4,
                      y2: y,
                      stroke: "currentColor",
                      className: "text-tertiary",
                      strokeWidth: "1"
                    }
                  ),
                  (j === 0 || j === axisTicks[i].length - 1) && /* @__PURE__ */ jsx38(
                    "text",
                    {
                      x,
                      y: j === 0 ? y + 15 : y - 8,
                      textAnchor: "middle",
                      className: "fill-tertiary",
                      style: {
                        fontSize: "10px",
                        fontFamily: "'Source Code Pro', Consolas, Monaco, monospace",
                        fontVariantNumeric: "tabular-nums"
                      },
                      children: format(tick)
                    }
                  )
                ] }, `tick-${i}-${j}`);
              })
            ] }, `axis-${i}`);
          }),
          data.filter((d) => !d.highlighted).map((series, i) => {
            const color = series.color || defaultColors2[i % defaultColors2.length];
            return /* @__PURE__ */ jsx38(
              "path",
              {
                d: generatePath(series.values),
                fill: "none",
                stroke: color,
                strokeWidth: "1.5",
                opacity: "0.4"
              },
              `path-${series.label}`
            );
          }),
          data.filter((d) => d.highlighted).map((series) => {
            const color = series.color || "rgb(var(--color-text-primary))";
            return /* @__PURE__ */ jsx38(
              "path",
              {
                d: generatePath(series.values),
                fill: "none",
                stroke: color,
                strokeWidth: "3",
                opacity: "1"
              },
              `path-highlighted-${series.label}`
            );
          }),
          data.map((series, seriesIndex) => {
            const color = series.color || defaultColors2[seriesIndex % defaultColors2.length];
            const isHighlighted = series.highlighted;
            return /* @__PURE__ */ jsxs28("g", { children: [
              series.values.map((value, axisIndex) => {
                const x = getX(axisIndex);
                const y = getY(value, axisIndex);
                return /* @__PURE__ */ jsx38(
                  "circle",
                  {
                    cx: x,
                    cy: y,
                    r: isHighlighted ? 5 : 3,
                    fill: color,
                    opacity: isHighlighted ? 1 : 0.6
                  },
                  `point-${axisIndex}`
                );
              }),
              showLabels && /* @__PURE__ */ jsx38(
                "text",
                {
                  x: getX(axes.length - 1) + 10,
                  y: getY(series.values[series.values.length - 1], axes.length - 1),
                  dominantBaseline: "middle",
                  className: "fill-secondary",
                  style: {
                    fontSize: isHighlighted ? "12px" : "10px",
                    fontFamily: "'Lato', system-ui, -apple-system, sans-serif",
                    fontWeight: isHighlighted ? 600 : 400
                  },
                  children: series.label
                }
              )
            ] }, `points-${series.label}`);
          })
        ]
      }
    ) })
  ] });
}

// src/charts/GaugeCluster.tsx
import { jsx as jsx39, jsxs as jsxs29 } from "react/jsx-runtime";
function GaugeCluster({
  title,
  subtitle,
  gauges,
  columns = 3,
  arcThickness = 10,
  source = "Aaim Platform Analytics"
}) {
  const gridCols = {
    2: "grid-cols-2",
    3: "grid-cols-2 md:grid-cols-3",
    4: "grid-cols-2 md:grid-cols-4"
  };
  return /* @__PURE__ */ jsxs29("div", { className: "bg-surface border border-recessed p-8 shadow-sm rounded-sm", children: [
    subtitle && /* @__PURE__ */ jsx39("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-2", children: subtitle }),
    /* @__PURE__ */ jsx39("h3", { className: "text-heading-section font-serif font-semibold mb-6 leading-tight tracking-headline-serif", children: title }),
    /* @__PURE__ */ jsx39("div", { className: `grid ${gridCols[columns]} gap-6`, children: gauges.map((gauge) => /* @__PURE__ */ jsx39(
      SingleGauge,
      {
        gauge,
        arcThickness
      },
      gauge.id
    )) }),
    /* @__PURE__ */ jsx39("div", { className: "mt-6 pt-4 border-t border-recessed", children: /* @__PURE__ */ jsxs29("p", { className: "text-xs font-sans text-tertiary opacity-75", children: [
      "Source: ",
      source
    ] }) })
  ] });
}
function getStatusArcColor(status) {
  switch (status) {
    case "positive":
      return "rgb(var(--color-growth))";
    case "negative":
      return "rgb(var(--color-error))";
    case "warning":
      return "rgb(var(--color-warning))";
    default:
      return "rgb(var(--color-text-secondary))";
  }
}
function getStatusTextClass(status) {
  switch (status) {
    case "positive":
      return "text-growth";
    case "negative":
      return "text-error";
    case "warning":
      return "text-warning";
    default:
      return "text-primary";
  }
}
function SingleGauge({ gauge, arcThickness }) {
  const {
    label,
    value,
    min = 0,
    max = 100,
    target,
    formatValue = (v) => v.toFixed(1),
    status = "neutral"
  } = gauge;
  const size = 120;
  const center = size / 2;
  const radius = center - arcThickness / 2 - 2;
  const range = max - min;
  const normalizedValue = range === 0 ? 0 : Math.max(0, Math.min(1, (value - min) / range));
  const normalizedTarget = target !== void 0 && range !== 0 ? Math.max(0, Math.min(1, (target - min) / range)) : void 0;
  const startAngle = 135;
  const sweepAngle = 270;
  const endAngle = startAngle + sweepAngle;
  const angleToXY = (angleDeg) => {
    const rad = angleDeg * Math.PI / 180;
    return {
      x: center + radius * Math.cos(rad),
      y: center + radius * Math.sin(rad)
    };
  };
  const bgStart = angleToXY(startAngle);
  const bgEnd = angleToXY(endAngle);
  const bgLargeArc = sweepAngle > 180 ? 1 : 0;
  const bgPath = `M ${bgStart.x} ${bgStart.y} A ${radius} ${radius} 0 ${bgLargeArc} 1 ${bgEnd.x} ${bgEnd.y}`;
  const valueAngle = startAngle + normalizedValue * sweepAngle;
  const valueEnd = angleToXY(valueAngle);
  const valueSweep = normalizedValue * sweepAngle;
  const valueLargeArc = valueSweep > 180 ? 1 : 0;
  const valuePath = `M ${bgStart.x} ${bgStart.y} A ${radius} ${radius} 0 ${valueLargeArc} 1 ${valueEnd.x} ${valueEnd.y}`;
  const targetPos = normalizedTarget !== void 0 ? angleToXY(startAngle + normalizedTarget * sweepAngle) : null;
  const arcColor = getStatusArcColor(status);
  const textClass = getStatusTextClass(status);
  return /* @__PURE__ */ jsxs29("div", { className: "flex flex-col items-center", children: [
    /* @__PURE__ */ jsxs29("div", { className: "relative", style: { width: size, height: size * 0.75 }, children: [
      /* @__PURE__ */ jsxs29(
        "svg",
        {
          width: size,
          height: size,
          viewBox: `0 0 ${size} ${size}`,
          className: "overflow-visible",
          role: "img",
          "aria-label": `${label}: ${formatValue(value)}${target !== void 0 ? `, target ${formatValue(target)}` : ""}`,
          children: [
            /* @__PURE__ */ jsx39(
              "path",
              {
                d: bgPath,
                fill: "none",
                stroke: "rgb(var(--color-recessed))",
                strokeWidth: arcThickness,
                strokeLinecap: "round"
              }
            ),
            normalizedValue > 0 && /* @__PURE__ */ jsx39(
              "path",
              {
                d: valuePath,
                fill: "none",
                stroke: arcColor,
                strokeWidth: arcThickness,
                strokeLinecap: "round"
              }
            ),
            targetPos && /* @__PURE__ */ jsx39(
              "circle",
              {
                cx: targetPos.x,
                cy: targetPos.y,
                r: arcThickness / 2 + 2,
                fill: "none",
                stroke: "rgb(var(--color-text-tertiary))",
                strokeWidth: "2"
              }
            )
          ]
        }
      ),
      /* @__PURE__ */ jsx39(
        "div",
        {
          className: "absolute inset-0 flex flex-col items-center justify-end pb-1",
          style: { height: size * 0.75 },
          children: /* @__PURE__ */ jsx39("span", { className: `text-xl font-serif font-semibold tracking-headline-serif tabular-nums ${textClass}`, children: formatValue(value) })
        }
      )
    ] }),
    /* @__PURE__ */ jsxs29("div", { className: "text-center mt-1", children: [
      /* @__PURE__ */ jsx39("span", { className: "text-xs font-sans font-medium text-tertiary tracking-tight", children: label }),
      target !== void 0 && /* @__PURE__ */ jsxs29("div", { className: "text-caption-xs font-mono text-tertiary tabular-nums opacity-70 mt-0.5", children: [
        "Target: ",
        formatValue(target)
      ] })
    ] })
  ] });
}

// src/charts/TimelineChart.tsx
import { jsx as jsx40, jsxs as jsxs30 } from "react/jsx-runtime";
var STATUS_CONFIG = {
  completed: {
    dotClass: "bg-growth",
    lineClass: "bg-growth",
    labelClass: "text-growth",
    bgClass: "bg-growth/10"
  },
  active: {
    dotClass: "bg-accent",
    lineClass: "bg-accent",
    labelClass: "text-accent",
    bgClass: "bg-accent/10"
  },
  pending: {
    dotClass: "bg-recessed",
    lineClass: "bg-recessed",
    labelClass: "text-tertiary",
    bgClass: "bg-elevated"
  },
  overdue: {
    dotClass: "bg-error",
    lineClass: "bg-error",
    labelClass: "text-error",
    bgClass: "bg-error/10"
  },
  skipped: {
    dotClass: "bg-tertiary/50",
    lineClass: "bg-tertiary/30",
    labelClass: "text-tertiary opacity-60",
    bgClass: "bg-elevated/50"
  }
};
var STATUS_LABELS = {
  completed: "Complete",
  active: "In Progress",
  pending: "Pending",
  overdue: "Overdue",
  skipped: "Skipped"
};
function TimelineChart({
  title,
  subtitle,
  stages,
  orientation = "horizontal",
  showDurations = true,
  source = "Aaim Platform Analytics"
}) {
  if (orientation === "vertical") {
    return /* @__PURE__ */ jsx40(
      VerticalTimeline,
      {
        title,
        subtitle,
        stages,
        showDurations,
        source
      }
    );
  }
  return /* @__PURE__ */ jsx40(
    HorizontalTimeline,
    {
      title,
      subtitle,
      stages,
      showDurations,
      source
    }
  );
}
function HorizontalTimeline({ title, subtitle, stages, showDurations, source }) {
  return /* @__PURE__ */ jsxs30("div", { className: "bg-surface border border-recessed p-8 shadow-sm rounded-sm", children: [
    subtitle && /* @__PURE__ */ jsx40("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-2", children: subtitle }),
    /* @__PURE__ */ jsx40("h3", { className: "text-heading-section font-serif font-semibold mb-8 leading-tight tracking-headline-serif", children: title }),
    /* @__PURE__ */ jsx40("div", { className: "hidden md:block", children: /* @__PURE__ */ jsxs30("div", { className: "relative", children: [
      /* @__PURE__ */ jsx40("div", { className: "absolute top-4 left-4 right-4 h-px bg-recessed" }),
      /* @__PURE__ */ jsx40(
        "div",
        {
          className: "grid gap-2",
          style: { gridTemplateColumns: `repeat(${stages.length}, 1fr)` },
          children: stages.map((stage, index) => {
            const config = STATUS_CONFIG[stage.status];
            const isLast = index === stages.length - 1;
            return /* @__PURE__ */ jsxs30("div", { className: "relative flex flex-col items-center", children: [
              index > 0 && /* @__PURE__ */ jsx40(
                "div",
                {
                  className: `absolute top-4 h-0.5 ${stage.status === "completed" || stage.status === "active" ? "bg-growth" : "bg-recessed"}`,
                  style: {
                    right: "50%",
                    left: "-50%"
                  }
                }
              ),
              /* @__PURE__ */ jsx40(
                "div",
                {
                  className: `relative z-10 w-8 h-8 rounded-full ${config.bgClass} border-2 flex items-center justify-center`,
                  style: {
                    borderColor: stage.status === "pending" || stage.status === "skipped" ? "rgb(var(--color-recessed))" : void 0
                  },
                  children: /* @__PURE__ */ jsx40("div", { className: `w-3 h-3 rounded-full ${config.dotClass}` })
                }
              ),
              /* @__PURE__ */ jsxs30("div", { className: "mt-3 text-center", children: [
                /* @__PURE__ */ jsx40("div", { className: `text-xs font-sans font-semibold ${config.labelClass}`, children: stage.label }),
                stage.metric && /* @__PURE__ */ jsx40("div", { className: "text-caption-xs font-mono text-tertiary tabular-nums mt-0.5", children: stage.metric }),
                stage.detail && /* @__PURE__ */ jsx40("div", { className: "text-caption-xs font-sans text-tertiary mt-0.5", children: stage.detail }),
                /* @__PURE__ */ jsx40("div", { className: "text-caption-xs font-sans text-tertiary opacity-60 mt-1 uppercase tracking-wider", children: STATUS_LABELS[stage.status] })
              ] }),
              showDurations && !isLast && stage.durationDays !== void 0 && /* @__PURE__ */ jsx40("div", { className: "absolute -right-4 top-0 translate-x-1/2 -translate-y-full", children: /* @__PURE__ */ jsxs30("span", { className: "text-code-xs font-mono text-tertiary tabular-nums bg-surface px-1", children: [
                stage.durationDays,
                "d"
              ] }) })
            ] }, stage.id);
          })
        }
      )
    ] }) }),
    /* @__PURE__ */ jsx40("div", { className: "md:hidden", children: /* @__PURE__ */ jsx40(VerticalStages, { stages, showDurations }) }),
    /* @__PURE__ */ jsxs30("div", { className: "mt-8 pt-4 border-t border-recessed flex items-center justify-between text-xs font-sans", children: [
      /* @__PURE__ */ jsxs30("p", { className: "text-tertiary opacity-75", children: [
        "Source: ",
        source
      ] }),
      /* @__PURE__ */ jsxs30("div", { className: "flex items-center gap-4", children: [
        /* @__PURE__ */ jsxs30("div", { className: "flex items-center gap-1.5", children: [
          /* @__PURE__ */ jsx40("div", { className: "w-2 h-2 rounded-full bg-growth" }),
          /* @__PURE__ */ jsx40("span", { className: "text-tertiary", children: "Complete" })
        ] }),
        /* @__PURE__ */ jsxs30("div", { className: "flex items-center gap-1.5", children: [
          /* @__PURE__ */ jsx40("div", { className: "w-2 h-2 rounded-full bg-accent" }),
          /* @__PURE__ */ jsx40("span", { className: "text-tertiary", children: "Active" })
        ] }),
        /* @__PURE__ */ jsxs30("div", { className: "flex items-center gap-1.5", children: [
          /* @__PURE__ */ jsx40("div", { className: "w-2 h-2 rounded-full bg-recessed" }),
          /* @__PURE__ */ jsx40("span", { className: "text-tertiary", children: "Pending" })
        ] })
      ] })
    ] })
  ] });
}
function VerticalTimeline({ title, subtitle, stages, showDurations, source }) {
  return /* @__PURE__ */ jsxs30("div", { className: "bg-surface border border-recessed p-8 shadow-sm rounded-sm", children: [
    subtitle && /* @__PURE__ */ jsx40("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-2", children: subtitle }),
    /* @__PURE__ */ jsx40("h3", { className: "text-heading-section font-serif font-semibold mb-8 leading-tight tracking-headline-serif", children: title }),
    /* @__PURE__ */ jsx40(VerticalStages, { stages, showDurations }),
    /* @__PURE__ */ jsx40("div", { className: "mt-6 pt-4 border-t border-recessed", children: /* @__PURE__ */ jsxs30("p", { className: "text-xs font-sans text-tertiary opacity-75", children: [
      "Source: ",
      source
    ] }) })
  ] });
}
function VerticalStages({
  stages,
  showDurations
}) {
  return /* @__PURE__ */ jsxs30("div", { className: "relative pl-10", children: [
    /* @__PURE__ */ jsx40("div", { className: "absolute top-0 bottom-0 left-[15px] w-px bg-recessed" }),
    /* @__PURE__ */ jsx40("div", { className: "space-y-6", children: stages.map((stage, index) => {
      const config = STATUS_CONFIG[stage.status];
      const isLast = index === stages.length - 1;
      return /* @__PURE__ */ jsxs30("div", { className: "relative", children: [
        (stage.status === "completed" || stage.status === "active") && /* @__PURE__ */ jsx40(
          "div",
          {
            className: "absolute left-[-25px] w-0.5 bg-growth",
            style: {
              top: 0,
              height: isLast ? "16px" : "100%"
            }
          }
        ),
        /* @__PURE__ */ jsx40(
          "div",
          {
            className: `absolute -left-10 top-1 w-6 h-6 rounded-full ${config.bgClass} border-2 flex items-center justify-center`,
            style: {
              borderColor: stage.status === "pending" || stage.status === "skipped" ? "rgb(var(--color-recessed))" : void 0
            },
            children: /* @__PURE__ */ jsx40("div", { className: `w-2.5 h-2.5 rounded-full ${config.dotClass}` })
          }
        ),
        /* @__PURE__ */ jsxs30("div", { className: "pb-1", children: [
          /* @__PURE__ */ jsxs30("div", { className: "flex items-baseline gap-3", children: [
            /* @__PURE__ */ jsx40("span", { className: `text-sm font-sans font-semibold ${config.labelClass}`, children: stage.label }),
            /* @__PURE__ */ jsx40("span", { className: "text-caption-xs font-sans text-tertiary opacity-60 uppercase tracking-wider", children: STATUS_LABELS[stage.status] })
          ] }),
          stage.metric && /* @__PURE__ */ jsx40("div", { className: "text-xs font-mono text-tertiary tabular-nums mt-0.5", children: stage.metric }),
          stage.detail && /* @__PURE__ */ jsx40("div", { className: "text-xs font-sans text-secondary mt-0.5", children: stage.detail }),
          showDurations && !isLast && stage.durationDays !== void 0 && /* @__PURE__ */ jsxs30("div", { className: "text-caption-xs font-mono text-tertiary tabular-nums mt-1 opacity-70", children: [
            "Duration: ",
            stage.durationDays,
            " days"
          ] })
        ] })
      ] }, stage.id);
    }) })
  ] });
}

// src/charts/MetricComparison.tsx
import React13 from "react";
import { jsx as jsx41, jsxs as jsxs31 } from "react/jsx-runtime";
function MetricComparison({
  title,
  subtitle,
  entities,
  metrics,
  data,
  showRanking = false,
  source = "Aaim Platform Analytics"
}) {
  const groupedMetrics = React13.useMemo(() => {
    const groups = [];
    let currentCategory = null;
    let currentGroup = [];
    metrics.forEach((metric) => {
      const cat = metric.category || null;
      if (cat !== currentCategory) {
        if (currentGroup.length > 0) {
          groups.push({ category: currentCategory, metrics: currentGroup });
        }
        currentCategory = cat;
        currentGroup = [metric];
      } else {
        currentGroup.push(metric);
      }
    });
    if (currentGroup.length > 0) {
      groups.push({ category: currentCategory, metrics: currentGroup });
    }
    return groups;
  }, [metrics]);
  const bestValues = React13.useMemo(() => {
    const bests = {};
    metrics.forEach((metric) => {
      if (!metric.highlightBest) return;
      const values = entities.map((entity) => ({
        entityId: entity.id,
        value: data[metric.id]?.[entity.id]
      })).filter((v) => typeof v.value === "number");
      if (values.length === 0) return;
      const sorted = [...values].sort(
        (a, b) => metric.higherIsBetter !== false ? b.value - a.value : a.value - b.value
      );
      bests[metric.id] = sorted[0].entityId;
    });
    return bests;
  }, [metrics, entities, data]);
  const rankings = React13.useMemo(() => {
    if (!showRanking) return {};
    const result = {};
    metrics.forEach((metric) => {
      const values = entities.map((entity) => ({
        entityId: entity.id,
        value: data[metric.id]?.[entity.id]
      })).filter((v) => typeof v.value === "number");
      if (values.length === 0) return;
      const sorted = [...values].sort(
        (a, b) => metric.higherIsBetter !== false ? b.value - a.value : a.value - b.value
      );
      result[metric.id] = {};
      sorted.forEach((v, i) => {
        result[metric.id][v.entityId] = i + 1;
      });
    });
    return result;
  }, [metrics, entities, data, showRanking]);
  const formatCell = (metric, value) => {
    if (value === void 0 || value === null) return "--";
    if (metric.formatValue) return metric.formatValue(value);
    if (typeof value === "number") return value.toLocaleString();
    return String(value);
  };
  return /* @__PURE__ */ jsxs31("div", { className: "bg-surface border border-recessed p-8 shadow-sm rounded-sm", children: [
    subtitle && /* @__PURE__ */ jsx41("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-2", children: subtitle }),
    /* @__PURE__ */ jsx41("h3", { className: "text-heading-section font-serif font-semibold mb-6 leading-tight tracking-headline-serif", children: title }),
    /* @__PURE__ */ jsx41("div", { className: "overflow-x-auto -mx-8 px-8", children: /* @__PURE__ */ jsxs31("table", { className: "w-full border-collapse", children: [
      /* @__PURE__ */ jsx41("thead", { children: /* @__PURE__ */ jsxs31("tr", { className: "border-b-2 border-primary", children: [
        /* @__PURE__ */ jsx41("th", { className: "text-left py-3 pr-4 w-48" }),
        entities.map((entity) => /* @__PURE__ */ jsxs31("th", { className: "text-right py-3 px-3 min-w-[100px]", children: [
          /* @__PURE__ */ jsx41("div", { className: "text-sm font-sans font-semibold text-primary", children: entity.name }),
          entity.subtitle && /* @__PURE__ */ jsx41("div", { className: "text-caption-xs font-sans text-tertiary mt-0.5", children: entity.subtitle })
        ] }, entity.id))
      ] }) }),
      /* @__PURE__ */ jsx41("tbody", { children: groupedMetrics.map((group, groupIndex) => /* @__PURE__ */ jsxs31(React13.Fragment, { children: [
        group.category && /* @__PURE__ */ jsx41("tr", { children: /* @__PURE__ */ jsx41(
          "td",
          {
            colSpan: entities.length + 1,
            className: "pt-5 pb-2",
            children: /* @__PURE__ */ jsx41("span", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary", children: group.category })
          }
        ) }),
        group.metrics.map((metric) => {
          const bestEntityId = bestValues[metric.id];
          return /* @__PURE__ */ jsxs31(
            "tr",
            {
              className: "border-b border-recessed/60 hover:bg-elevated/30 transition-colors",
              children: [
                /* @__PURE__ */ jsx41("td", { className: "py-2.5 pr-4", children: /* @__PURE__ */ jsx41("span", { className: "text-sm font-sans text-secondary", children: metric.label }) }),
                entities.map((entity) => {
                  const rawValue = data[metric.id]?.[entity.id];
                  const isBest = bestEntityId === entity.id;
                  const rank = rankings[metric.id]?.[entity.id];
                  return /* @__PURE__ */ jsx41(
                    "td",
                    {
                      className: "py-2.5 px-3 text-right",
                      children: /* @__PURE__ */ jsxs31("div", { className: "flex items-center justify-end gap-2", children: [
                        showRanking && rank !== void 0 && /* @__PURE__ */ jsxs31(
                          "span",
                          {
                            className: `text-code-xs font-mono ${rank === 1 ? "text-growth font-semibold" : "text-tertiary opacity-50"}`,
                            children: [
                              "#",
                              rank
                            ]
                          }
                        ),
                        /* @__PURE__ */ jsx41(
                          "span",
                          {
                            className: `text-sm font-mono tabular-nums ${isBest ? "text-primary font-semibold" : "text-secondary"}`,
                            children: formatCell(metric, rawValue)
                          }
                        )
                      ] })
                    },
                    entity.id
                  );
                })
              ]
            },
            metric.id
          );
        })
      ] }, groupIndex)) })
    ] }) }),
    /* @__PURE__ */ jsx41("div", { className: "mt-6 pt-4 border-t border-recessed", children: /* @__PURE__ */ jsxs31("p", { className: "text-xs font-sans text-tertiary opacity-75", children: [
      "Source: ",
      source
    ] }) })
  ] });
}

// src/charts/WealthFlowDiagram.tsx
import { useState as useState8, useMemo as useMemo4 } from "react";

// src/charts/SankeyDiagram.tsx
import { useEffect as useEffect4, useMemo as useMemo3, useState as useState5, useRef as useRef3 } from "react";

// src/charts/sankey-utils/sankeyLayout.ts
function calculateSankeyLayout(data, width, height, nodeWidth = 24, nodePadding = 8, minNodeHeight = 4) {
  const columns = Array.from(new Set(data.nodes.map((n) => n.column))).sort((a, b) => a - b);
  const columnCount = columns.length;
  const columnWidth = (width - nodeWidth) / (columnCount - 1);
  const columnTotals = /* @__PURE__ */ new Map();
  data.nodes.forEach((node) => {
    const current = columnTotals.get(node.column) || 0;
    columnTotals.set(node.column, current + node.value);
  });
  const layoutNodes = [];
  const nodeMap = /* @__PURE__ */ new Map();
  columns.forEach((col) => {
    const colNodes = data.nodes.filter((n) => n.column === col);
    const totalValue = columnTotals.get(col) || 1;
    const baseAvailableHeight = height - (colNodes.length - 1) * nodePadding;
    let totalBoostNeeded = 0;
    colNodes.forEach((node) => {
      const nodeValue = isNaN(node.value) ? 0 : node.value;
      const naturalHeight = totalValue > 0 ? nodeValue / totalValue * baseAvailableHeight : 0;
      const safeNaturalHeight = isNaN(naturalHeight) || naturalHeight < 0 ? 0 : naturalHeight;
      if (safeNaturalHeight < minNodeHeight) {
        totalBoostNeeded += minNodeHeight - safeNaturalHeight;
      }
      return safeNaturalHeight;
    });
    const adjustedAvailableHeight = baseAvailableHeight - totalBoostNeeded;
    let currentY = 0;
    colNodes.forEach((node) => {
      const nodeValue = isNaN(node.value) ? 0 : node.value;
      const proportionalHeight = totalValue > 0 && adjustedAvailableHeight > 0 ? nodeValue / totalValue * adjustedAvailableHeight : 0;
      const safeHeight = isNaN(proportionalHeight) || proportionalHeight < 0 ? minNodeHeight : Math.max(proportionalHeight, minNodeHeight);
      const layoutNode = {
        ...node,
        x: col * columnWidth,
        y: currentY,
        height: safeHeight
      };
      layoutNodes.push(layoutNode);
      nodeMap.set(node.id, layoutNode);
      currentY += safeHeight + nodePadding;
    });
  });
  const outgoingLinkSums = /* @__PURE__ */ new Map();
  const incomingLinkSums = /* @__PURE__ */ new Map();
  data.links.forEach((link) => {
    const sourceVal = outgoingLinkSums.get(link.source) || 0;
    const targetVal = incomingLinkSums.get(link.target) || 0;
    outgoingLinkSums.set(link.source, sourceVal + (link.value || 0));
    incomingLinkSums.set(link.target, targetVal + (link.value || 0));
  });
  const sourceOffsets = /* @__PURE__ */ new Map();
  const targetOffsets = /* @__PURE__ */ new Map();
  data.links.forEach((link) => {
    sourceOffsets.set(link.source, 0);
    targetOffsets.set(link.target, 0);
  });
  const layoutLinks = data.links.map((link) => {
    const sourceNode = nodeMap.get(link.source);
    const targetNode = nodeMap.get(link.target);
    const sourceOffset = sourceOffsets.get(link.source) || 0;
    const targetOffset = targetOffsets.get(link.target) || 0;
    const sourceOutgoingSum = outgoingLinkSums.get(link.source) || sourceNode.value;
    const targetIncomingSum = incomingLinkSums.get(link.target) || targetNode.value;
    const sourceHeight = sourceOutgoingSum > 0 && !isNaN(sourceNode.height) ? link.value / sourceOutgoingSum * sourceNode.height : 0;
    const targetHeight = targetIncomingSum > 0 && !isNaN(targetNode.height) ? link.value / targetIncomingSum * targetNode.height : 0;
    const safeSourceHeight = isNaN(sourceHeight) ? 0 : sourceHeight;
    const safeTargetHeight = isNaN(targetHeight) ? 0 : targetHeight;
    const layoutLink = {
      ...link,
      sourceNode,
      targetNode,
      y0: sourceNode.y + sourceOffset,
      y1: targetNode.y + targetOffset
    };
    sourceOffsets.set(link.source, sourceOffset + safeSourceHeight);
    targetOffsets.set(link.target, targetOffset + safeTargetHeight);
    return layoutLink;
  });
  return {
    nodes: layoutNodes,
    links: layoutLinks
  };
}

// src/charts/SankeyDiagram.tsx
import { jsx as jsx42, jsxs as jsxs32 } from "react/jsx-runtime";
function SankeyDiagram({
  data,
  selectedNodes: externalSelectedNodes,
  onSelectionChange
}) {
  const [hoveredNode, setHoveredNode] = useState5(null);
  const [internalSelectedNodes, setInternalSelectedNodes] = useState5(/* @__PURE__ */ new Set());
  const [containerWidth, setContainerWidth] = useState5(1200);
  const containerRef = useRef3(null);
  const selectedNodes = externalSelectedNodes || internalSelectedNodes;
  useEffect4(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);
  const responsive = useMemo3(() => {
    const scaleFactor = Math.max(0, Math.min(1, (containerWidth - 320) / (1400 - 320)));
    const isCompact = containerWidth < 480;
    const outerPadding = containerWidth < 400 ? 8 : containerWidth < 640 ? 16 : containerWidth < 1024 ? 24 : 48;
    const width = Math.min(1350, containerWidth - outerPadding);
    const height = isCompact ? Math.round(550 + 150 * scaleFactor) : Math.round(600 + 100 * scaleFactor);
    const nodeWidth2 = isCompact ? Math.round(8 + 16 * scaleFactor) : Math.round(12 + 12 * scaleFactor);
    const leftLabelPadding2 = isCompact ? Math.round(60 + 120 * scaleFactor) : Math.round(80 + 100 * scaleFactor);
    const rightLabelPadding = isCompact ? Math.round(70 + 130 * scaleFactor) : Math.round(100 + 100 * scaleFactor);
    const verticalPadding2 = Math.round(16 + 24 * scaleFactor);
    const fontSize2 = isCompact ? Math.round(8 + 6 * scaleFactor) : Math.round(10 + 4 * scaleFactor);
    const valueFontSize2 = isCompact ? Math.round(7 + 5 * scaleFactor) : Math.round(9 + 3 * scaleFactor);
    const diagramWidth = Math.max(150, width - leftLabelPadding2 - rightLabelPadding);
    const diagramHeight = height - verticalPadding2 * 2;
    const minLinkHeight = 0.3 + 0.7 * scaleFactor;
    const labelHitWidth2 = isCompact ? Math.round(80 + 140 * scaleFactor) : Math.round(120 + 100 * scaleFactor);
    const labelOffset2 = Math.round(4 + 8 * scaleFactor);
    const isMobile2 = containerWidth < 640;
    return {
      width,
      height,
      nodeWidth: nodeWidth2,
      leftLabelPadding: leftLabelPadding2,
      rightLabelPadding,
      verticalPadding: verticalPadding2,
      diagramWidth,
      diagramHeight,
      fontSize: fontSize2,
      valueFontSize: valueFontSize2,
      minLinkHeight,
      labelHitWidth: labelHitWidth2,
      labelOffset: labelOffset2,
      isMobile: isMobile2,
      isCompact,
      scaleFactor
    };
  }, [containerWidth]);
  const {
    nodes,
    links
  } = calculateSankeyLayout(data, responsive.diagramWidth, responsive.diagramHeight, responsive.nodeWidth);
  const offsetNodes = nodes.map((node) => ({
    ...node,
    x: node.x + responsive.leftLabelPadding,
    y: node.y + responsive.verticalPadding
  }));
  const nodeLinkSums = useMemo3(() => {
    const outgoingSums = /* @__PURE__ */ new Map();
    const incomingSums = /* @__PURE__ */ new Map();
    links.forEach((link) => {
      const sourceId = link.sourceNode.id;
      const targetId = link.targetNode.id;
      const value = link.value || 0;
      outgoingSums.set(sourceId, (outgoingSums.get(sourceId) || 0) + value);
      incomingSums.set(targetId, (incomingSums.get(targetId) || 0) + value);
    });
    return { outgoingSums, incomingSums };
  }, [links]);
  const selectedByColumn = useMemo3(() => {
    const byColumn = /* @__PURE__ */ new Map();
    selectedNodes.forEach((nodeId) => {
      const node = offsetNodes.find((n) => n.id === nodeId);
      if (node) {
        if (!byColumn.has(node.column)) {
          byColumn.set(node.column, /* @__PURE__ */ new Set());
        }
        byColumn.get(node.column).add(nodeId);
      }
    });
    return byColumn;
  }, [selectedNodes, offsetNodes]);
  const hoveredNodeColumn = useMemo3(() => {
    if (!hoveredNode) return null;
    const node = offsetNodes.find((n) => n.id === hoveredNode);
    return node ? node.column : null;
  }, [hoveredNode, offsetNodes]);
  const leftmostSelectedColumn = useMemo3(() => {
    if (selectedNodes.size === 0) return null;
    return Math.min(...Array.from(selectedByColumn.keys()));
  }, [selectedNodes, selectedByColumn]);
  const hasSelectionsToLeft = useMemo3(() => {
    if (!hoveredNode || hoveredNodeColumn === null) return false;
    return Array.from(selectedNodes).some((nodeId) => {
      const node = offsetNodes.find((n) => n.id === nodeId);
      return node && node.column < hoveredNodeColumn;
    });
  }, [hoveredNode, hoveredNodeColumn, selectedNodes, offsetNodes]);
  const connectedElements = useMemo3(() => {
    if (selectedNodes.size === 0 && !hoveredNode) {
      return {
        nodes: /* @__PURE__ */ new Set(),
        links: /* @__PURE__ */ new Set()
      };
    }
    const startNodes = hoveredNode && selectedNodes.size === 0 ? /* @__PURE__ */ new Set([hoveredNode]) : selectedByColumn.get(leftmostSelectedColumn) || /* @__PURE__ */ new Set();
    const validNodes = new Set(startNodes);
    const validLinks = /* @__PURE__ */ new Set();
    const sortedColumns = Array.from(selectedByColumn.keys()).sort((a, b) => a - b);
    if (hoveredNode && selectedNodes.size === 0) {
      const upstreamNodes = /* @__PURE__ */ new Set();
      const toExploreUpstream = /* @__PURE__ */ new Set([hoveredNode]);
      while (toExploreUpstream.size > 0) {
        const current = Array.from(toExploreUpstream)[0];
        toExploreUpstream.delete(current);
        upstreamNodes.add(current);
        links.forEach((link) => {
          if (link.targetNode.id === current && !upstreamNodes.has(link.sourceNode.id)) {
            toExploreUpstream.add(link.sourceNode.id);
          }
        });
      }
      const downstreamNodes = /* @__PURE__ */ new Set();
      const toExploreDownstream = /* @__PURE__ */ new Set([hoveredNode]);
      while (toExploreDownstream.size > 0) {
        const current = Array.from(toExploreDownstream)[0];
        toExploreDownstream.delete(current);
        downstreamNodes.add(current);
        links.forEach((link) => {
          if (link.sourceNode.id === current && !downstreamNodes.has(link.targetNode.id)) {
            toExploreDownstream.add(link.targetNode.id);
          }
        });
      }
      upstreamNodes.forEach((n) => validNodes.add(n));
      downstreamNodes.forEach((n) => validNodes.add(n));
      links.forEach((link, index) => {
        const sourceInUpstream = upstreamNodes.has(link.sourceNode.id);
        const targetInUpstream = upstreamNodes.has(link.targetNode.id);
        const sourceInDownstream = downstreamNodes.has(link.sourceNode.id);
        const targetInDownstream = downstreamNodes.has(link.targetNode.id);
        if (sourceInUpstream && targetInUpstream || sourceInDownstream && targetInDownstream) {
          validLinks.add(index);
        }
      });
    } else {
      if (selectedNodes.size === 1) {
        const selectedNode = Array.from(selectedNodes)[0];
        const upstreamNodes = /* @__PURE__ */ new Set();
        const toExploreUpstream = /* @__PURE__ */ new Set([selectedNode]);
        while (toExploreUpstream.size > 0) {
          const current = Array.from(toExploreUpstream)[0];
          toExploreUpstream.delete(current);
          upstreamNodes.add(current);
          links.forEach((link) => {
            if (link.targetNode.id === current && !upstreamNodes.has(link.sourceNode.id)) {
              toExploreUpstream.add(link.sourceNode.id);
            }
          });
        }
        const downstreamNodes = /* @__PURE__ */ new Set();
        const toExploreDownstream = /* @__PURE__ */ new Set([selectedNode]);
        while (toExploreDownstream.size > 0) {
          const current = Array.from(toExploreDownstream)[0];
          toExploreDownstream.delete(current);
          downstreamNodes.add(current);
          links.forEach((link) => {
            if (link.sourceNode.id === current && !downstreamNodes.has(link.targetNode.id)) {
              toExploreDownstream.add(link.targetNode.id);
            }
          });
        }
        upstreamNodes.forEach((n) => validNodes.add(n));
        downstreamNodes.forEach((n) => validNodes.add(n));
        links.forEach((link, index) => {
          const sourceInUpstream = upstreamNodes.has(link.sourceNode.id);
          const targetInUpstream = upstreamNodes.has(link.targetNode.id);
          const sourceInDownstream = downstreamNodes.has(link.sourceNode.id);
          const targetInDownstream = downstreamNodes.has(link.targetNode.id);
          if (sourceInUpstream && targetInUpstream || sourceInDownstream && targetInDownstream) {
            validLinks.add(index);
          }
        });
      } else {
        selectedNodes.forEach((nodeId) => {
          validNodes.add(nodeId);
        });
        for (let i = 0; i < sortedColumns.length; i++) {
          const currentColumn = sortedColumns[i];
          const nextColumn = i < sortedColumns.length - 1 ? sortedColumns[i + 1] : null;
          const selectedInColumn = selectedByColumn.get(currentColumn);
          if (i === 0 && currentColumn > 0) {
            const upstreamNodes = new Set(selectedInColumn);
            const toExploreUpstream = new Set(selectedInColumn);
            while (toExploreUpstream.size > 0) {
              const current = Array.from(toExploreUpstream)[0];
              toExploreUpstream.delete(current);
              links.forEach((link, index) => {
                if (link.targetNode.id === current) {
                  validLinks.add(index);
                  validNodes.add(link.sourceNode.id);
                  if (!upstreamNodes.has(link.sourceNode.id)) {
                    upstreamNodes.add(link.sourceNode.id);
                    toExploreUpstream.add(link.sourceNode.id);
                  }
                }
              });
            }
          }
          if (nextColumn !== null) {
            const nextSelectedNodes = selectedByColumn.get(nextColumn);
            const reachableFromCurrent = /* @__PURE__ */ new Set();
            const toExplore = new Set(selectedInColumn);
            const explored = /* @__PURE__ */ new Set();
            while (toExplore.size > 0) {
              const current = Array.from(toExplore)[0];
              toExplore.delete(current);
              explored.add(current);
              links.forEach((link, index) => {
                if (link.sourceNode.id === current) {
                  if (nextSelectedNodes.has(link.targetNode.id)) {
                    reachableFromCurrent.add(link.targetNode.id);
                    validLinks.add(index);
                  } else if (link.targetNode.column < nextColumn && !explored.has(link.targetNode.id)) {
                    toExplore.add(link.targetNode.id);
                    reachableFromCurrent.add(link.targetNode.id);
                    validLinks.add(index);
                  }
                }
              });
            }
            reachableFromCurrent.forEach((n) => validNodes.add(n));
          } else {
            const toExplore = new Set(selectedInColumn);
            let foundNew = true;
            while (foundNew) {
              foundNew = false;
              const currentNodes = new Set(toExplore);
              links.forEach((link, index) => {
                if (currentNodes.has(link.sourceNode.id) && !toExplore.has(link.targetNode.id)) {
                  toExplore.add(link.targetNode.id);
                  validNodes.add(link.targetNode.id);
                  validLinks.add(index);
                  foundNew = true;
                } else if (currentNodes.has(link.sourceNode.id) && toExplore.has(link.targetNode.id)) {
                  validLinks.add(index);
                }
              });
            }
          }
        }
      }
    }
    return {
      nodes: validNodes,
      links: validLinks
    };
  }, [hoveredNode, selectedNodes, selectedByColumn, leftmostSelectedColumn, links]);
  const filteredNodeValues = useMemo3(() => {
    const nodeValues = /* @__PURE__ */ new Map();
    if (selectedNodes.size === 0 && !hoveredNode) {
      offsetNodes.forEach((node) => {
        nodeValues.set(node.id, node.value);
      });
      return nodeValues;
    }
    const allColumns = Array.from(new Set(offsetNodes.map((n) => n.column))).sort((a, b) => a - b);
    allColumns.forEach((col) => {
      const nodesInColumn = offsetNodes.filter((n) => n.column === col);
      nodesInColumn.forEach((node) => {
        if (leftmostSelectedColumn !== null && node.column < leftmostSelectedColumn) {
          nodeValues.set(node.id, node.value);
          return;
        }
        if (node.column === 0) {
          if (leftmostSelectedColumn === 0) {
            let outgoingFlow = 0;
            links.forEach((link, index) => {
              if (link.sourceNode.id === node.id) {
                const isVisible = connectedElements.links.has(index);
                if (isVisible && !isNaN(link.value)) {
                  outgoingFlow += link.value;
                }
              }
            });
            nodeValues.set(node.id, outgoingFlow);
          } else {
            nodeValues.set(node.id, node.value);
          }
          return;
        }
        let incomingFlow = 0;
        links.forEach((link, index) => {
          if (link.targetNode.id === node.id) {
            const isVisible = selectedNodes.size === 0 && !hoveredNode ? true : connectedElements.links.has(index);
            if (isVisible && !isNaN(link.value)) {
              const sourceOriginalValue = link.sourceNode.value;
              const sourceFilteredValue = nodeValues.get(link.sourceNode.id) ?? sourceOriginalValue;
              if (sourceOriginalValue > 0 && !isNaN(sourceFilteredValue)) {
                const scaleFactor = sourceFilteredValue / sourceOriginalValue;
                incomingFlow += link.value * scaleFactor;
              }
            }
          }
        });
        const finalValue = isNaN(incomingFlow) ? 0 : incomingFlow;
        nodeValues.set(node.id, finalValue);
      });
    });
    return nodeValues;
  }, [connectedElements, selectedNodes, hoveredNode, links, offsetNodes, leftmostSelectedColumn]);
  const generatePath = (link, offsetX, offsetY) => {
    const {
      sourceNode,
      targetNode,
      y0,
      y1,
      value
    } = link;
    const sourceOutgoingSum = nodeLinkSums.outgoingSums.get(sourceNode.id) || sourceNode.value;
    const targetIncomingSum = nodeLinkSums.incomingSums.get(targetNode.id) || targetNode.value;
    const calculatedSourceHeight = sourceOutgoingSum > 0 && !isNaN(sourceNode.height) ? value / sourceOutgoingSum * sourceNode.height : 0;
    const calculatedTargetHeight = targetIncomingSum > 0 && !isNaN(targetNode.height) ? value / targetIncomingSum * targetNode.height : 0;
    const sourceHeight = !isNaN(calculatedSourceHeight) ? Math.max(calculatedSourceHeight, responsive.minLinkHeight) : responsive.minLinkHeight;
    const targetHeight = !isNaN(calculatedTargetHeight) ? Math.max(calculatedTargetHeight, responsive.minLinkHeight) : responsive.minLinkHeight;
    const x0 = sourceNode.x + offsetX + responsive.nodeWidth;
    const x1 = targetNode.x + offsetX;
    const xi = (x0 + x1) / 2;
    const sy0 = y0 + offsetY;
    const sy1 = y1 + offsetY;
    if (isNaN(sy0) || isNaN(sy1) || isNaN(sourceHeight) || isNaN(targetHeight)) {
      return "";
    }
    return `
      M ${x0} ${sy0}
      C ${xi} ${sy0}, ${xi} ${sy1}, ${x1} ${sy1}
      L ${x1} ${sy1 + targetHeight}
      C ${xi} ${sy1 + targetHeight}, ${xi} ${sy0 + sourceHeight}, ${x0} ${sy0 + sourceHeight}
      Z
    `;
  };
  const formatValue = (value) => {
    if (isNaN(value)) {
      return "$0.0B";
    }
    const trillions = value / 1e3;
    if (trillions >= 1) {
      return `$${trillions.toFixed(1)}T`;
    } else if (trillions >= 1e-3) {
      return `$${value.toFixed(1)}B`;
    } else {
      const millions = value * 1e3;
      return `$${millions.toFixed(1)}M`;
    }
  };
  const handleNodeClick = (nodeId) => {
    const newSet = new Set(selectedNodes);
    if (newSet.has(nodeId)) {
      newSet.delete(nodeId);
    } else {
      newSet.add(nodeId);
    }
    if (onSelectionChange) {
      onSelectionChange(newSet);
    } else {
      setInternalSelectedNodes(newSet);
    }
  };
  const hasAnyFilter = hoveredNode !== null || selectedNodes.size > 0;
  const isHighlighted = (node) => {
    if (!hasAnyFilter) return true;
    if (selectedNodes.has(node.id)) return true;
    if (leftmostSelectedColumn !== null && node.column < leftmostSelectedColumn) {
      return true;
    }
    if (hoveredNode && !hasSelectionsToLeft && hoveredNodeColumn !== null && node.column < hoveredNodeColumn) {
      return true;
    }
    return connectedElements.nodes.has(node.id);
  };
  const isLinkHighlighted = (link) => {
    if (!hasAnyFilter) return true;
    const sourceHighlighted = selectedNodes.has(link.sourceNode.id) || leftmostSelectedColumn !== null && link.sourceNode.column < leftmostSelectedColumn || hoveredNode && !hasSelectionsToLeft && hoveredNodeColumn !== null && link.sourceNode.column < hoveredNodeColumn || connectedElements.nodes.has(link.sourceNode.id);
    const targetHighlighted = selectedNodes.has(link.targetNode.id) || leftmostSelectedColumn !== null && link.targetNode.column < leftmostSelectedColumn || hoveredNode && !hasSelectionsToLeft && hoveredNodeColumn !== null && link.targetNode.column < hoveredNodeColumn || connectedElements.nodes.has(link.targetNode.id);
    return sourceHighlighted && targetHighlighted;
  };
  const activeKey = `${hoveredNode || ""}-${Array.from(selectedNodes).sort().join(",")}`;
  const {
    width: svgWidth,
    height: svgHeight,
    nodeWidth,
    leftLabelPadding,
    verticalPadding,
    fontSize,
    valueFontSize,
    labelHitWidth,
    labelOffset,
    isMobile
  } = responsive;
  return /* @__PURE__ */ jsx42("div", { ref: containerRef, className: "w-full flex items-center justify-center px-2 py-4 md:px-4 md:py-6", role: "figure", "aria-label": "Wealth flow diagram showing generational wealth transfer from 2020-2050", children: /* @__PURE__ */ jsxs32(
    "svg",
    {
      width: svgWidth,
      height: svgHeight,
      viewBox: `0 0 ${svgWidth} ${svgHeight}`,
      preserveAspectRatio: "xMidYMid meet",
      style: {
        display: "block",
        maxWidth: "100%",
        height: "auto"
      },
      role: "img",
      "aria-labelledby": "sankey-title sankey-desc",
      children: [
        /* @__PURE__ */ jsx42("title", { id: "sankey-title", children: "Generational Wealth Flow Visualization" }),
        /* @__PURE__ */ jsx42("desc", { id: "sankey-desc", children: "Interactive Sankey diagram showing wealth transfer from generations through wealth tiers to investment philosophies and asset classes. Click nodes to filter paths." }),
        links.map((link, i) => {
          const highlighted = isLinkHighlighted(link);
          const linkOpacity = hasAnyFilter ? highlighted ? 0.5 : 0.08 : 0.3;
          return /* @__PURE__ */ jsx42("path", { d: generatePath(link, leftLabelPadding, verticalPadding), fill: "rgb(var(--color-recessed))", fillOpacity: linkOpacity, stroke: "none" }, `link-${i}`);
        }),
        offsetNodes.map((node) => {
          const highlighted = isHighlighted(node);
          const nodeOpacity = hasAnyFilter ? highlighted ? 1 : 0.15 : 1;
          const textOpacity = hasAnyFilter ? highlighted ? 1 : 0.3 : 1;
          const displayValue = filteredNodeValues.get(node.id) ?? node.value;
          const hitAreaX = node.column === 0 ? node.x - labelHitWidth - labelOffset : node.x;
          const hitAreaWidth = node.column === 0 ? labelHitWidth + nodeWidth + labelOffset : nodeWidth + labelHitWidth + labelOffset;
          return /* @__PURE__ */ jsxs32("g", { role: "button", "aria-label": `${node.label}: ${formatValue(displayValue)}. ${selectedNodes.has(node.id) ? "Selected." : "Click to select."}`, children: [
            /* @__PURE__ */ jsx42("rect", { x: hitAreaX, y: node.y, width: hitAreaWidth, height: Math.max(node.height, 20), fill: "transparent", cursor: "pointer", tabIndex: 0, onMouseEnter: () => {
              if (!isMobile) setHoveredNode(node.id);
            }, onMouseLeave: () => {
              if (!isMobile) setHoveredNode(null);
            }, onClick: () => handleNodeClick(node.id), onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleNodeClick(node.id);
              }
            }, onFocus: () => {
              if (!isMobile) setHoveredNode(node.id);
            }, onBlur: () => {
              if (!isMobile) setHoveredNode(null);
            } }),
            /* @__PURE__ */ jsx42("rect", { x: node.x, y: node.y, width: nodeWidth, height: node.height, fill: node.color, opacity: nodeOpacity, pointerEvents: "none" }),
            node.column === 0 && /* @__PURE__ */ jsxs32("text", { x: node.x - labelOffset, y: node.y + node.height / 2, textAnchor: "end", dominantBaseline: "middle", fill: "rgb(var(--color-text-secondary))", fontSize, fontWeight: "600", opacity: textOpacity, pointerEvents: "none", fontFamily: "Lato, system-ui, sans-serif", children: [
              node.label,
              " ",
              /* @__PURE__ */ jsx42("tspan", { fill: "rgb(var(--color-text-tertiary))", fontSize: valueFontSize, fontWeight: "normal", fontFamily: "Lato, system-ui, sans-serif", children: formatValue(displayValue) })
            ] }),
            node.column === 1 && /* @__PURE__ */ jsxs32("text", { x: node.x + nodeWidth + labelOffset, y: node.y + node.height / 2, textAnchor: "start", dominantBaseline: "middle", fill: "rgb(var(--color-text-secondary))", fontSize, fontWeight: "600", opacity: textOpacity, pointerEvents: "none", fontFamily: "Lato, system-ui, sans-serif", children: [
              node.label,
              " ",
              /* @__PURE__ */ jsx42("tspan", { fill: "rgb(var(--color-text-tertiary))", fontSize: valueFontSize, fontWeight: "normal", fontFamily: "Lato, system-ui, sans-serif", children: formatValue(displayValue) })
            ] }),
            node.column === 2 && /* @__PURE__ */ jsxs32("text", { x: node.x + nodeWidth + labelOffset, y: node.y + node.height / 2, textAnchor: "start", dominantBaseline: "middle", fill: "rgb(var(--color-text-primary))", fontSize, fontWeight: "600", opacity: textOpacity, pointerEvents: "none", fontFamily: "Lato, system-ui, sans-serif", children: [
              node.label,
              " ",
              /* @__PURE__ */ jsx42("tspan", { fill: "rgb(var(--color-text-tertiary))", fontSize: valueFontSize, fontWeight: "normal", fontFamily: "Lato, system-ui, sans-serif", children: formatValue(displayValue) })
            ] }),
            node.column === 3 && /* @__PURE__ */ jsxs32("text", { x: node.x + nodeWidth + labelOffset, y: node.y + node.height / 2, textAnchor: "start", dominantBaseline: "middle", fill: "rgb(var(--color-text-secondary))", fontSize, fontWeight: "600", opacity: textOpacity, pointerEvents: "none", fontFamily: "Lato, system-ui, sans-serif", children: [
              node.label,
              " ",
              /* @__PURE__ */ jsx42("tspan", { fill: "rgb(var(--color-text-tertiary))", fontSize: valueFontSize, fontWeight: "normal", fontFamily: "Lato, system-ui, sans-serif", children: formatValue(displayValue) })
            ] })
          ] }, `node-${node.id}`);
        })
      ]
    },
    activeKey
  ) });
}

// src/charts/Timeline.tsx
import { useEffect as useEffect5, useState as useState6, useRef as useRef4, useCallback as useCallback3 } from "react";
import { Play, Pause } from "lucide-react";
import { jsx as jsx43, jsxs as jsxs33 } from "react/jsx-runtime";
function Timeline({
  currentYear,
  onYearChange,
  minYear = 2020,
  maxYear = 2050,
  autoplaySpeed = 1e3 / 24,
  compact = false
}) {
  const [isPlaying, setIsPlaying] = useState6(false);
  const [isDragging, setIsDragging] = useState6(false);
  const [isPaused, setIsPaused] = useState6(false);
  const sliderRef = useRef4(null);
  const autoplayIntervalRef = useRef4(null);
  const getSpeedMultiplier = useCallback3((year) => {
    const slowSpeed = 0.25;
    const normalSpeed = 1;
    if (year <= minYear + 1) {
      const t = year - minYear;
      return slowSpeed + (normalSpeed - slowSpeed) * t;
    } else if (year >= maxYear - 1) {
      const t = maxYear - year;
      return slowSpeed + (normalSpeed - slowSpeed) * t;
    }
    return normalSpeed;
  }, [minYear, maxYear]);
  useEffect5(() => {
    if (isPlaying && !isPaused) {
      autoplayIntervalRef.current = setInterval(() => {
        if (currentYear >= maxYear) {
          setIsPaused(true);
          setTimeout(() => {
            onYearChange(minYear);
            setIsPaused(false);
          }, 2e3);
        } else {
          const speedMultiplier = getSpeedMultiplier(currentYear);
          const nextYear = Math.min(currentYear + 1 / 12 * speedMultiplier, maxYear);
          onYearChange(nextYear);
        }
      }, autoplaySpeed);
    } else {
      if (autoplayIntervalRef.current) {
        clearInterval(autoplayIntervalRef.current);
        autoplayIntervalRef.current = null;
      }
    }
    return () => {
      if (autoplayIntervalRef.current) {
        clearInterval(autoplayIntervalRef.current);
      }
    };
  }, [isPlaying, isPaused, currentYear, maxYear, minYear, autoplaySpeed, onYearChange, getSpeedMultiplier]);
  const handleSliderClick = (e) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage2 = Math.max(0, Math.min(1, x / rect.width));
    const year = minYear + percentage2 * (maxYear - minYear);
    onYearChange(year);
  };
  const handleMouseDown = () => {
    setIsDragging(true);
    setIsPlaying(false);
  };
  const handleMouseMove = useCallback3((e) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage2 = Math.max(0, Math.min(1, x / rect.width));
    const year = minYear + percentage2 * (maxYear - minYear);
    onYearChange(year);
  }, [minYear, maxYear, onYearChange]);
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  useEffect5(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
    return void 0;
  }, [isDragging, handleMouseMove]);
  const togglePlayPause = () => {
    if (currentYear >= maxYear) {
      onYearChange(minYear);
      setIsPlaying(true);
    } else {
      setIsPlaying(!isPlaying);
    }
  };
  const percentage = (currentYear - minYear) / (maxYear - minYear) * 100;
  if (compact) {
    const handleTimelineMouseEnter = () => {
      if (isPlaying) {
        setIsPaused(true);
      }
    };
    const handleTimelineMouseLeave = () => {
      if (isPlaying) {
        setIsPaused(false);
      }
    };
    const handlePlayheadClick = (e) => {
      e.stopPropagation();
      togglePlayPause();
    };
    const handlePlayheadDrag = (e) => {
      e.stopPropagation();
      e.preventDefault();
      setIsDragging(true);
    };
    return /* @__PURE__ */ jsx43(
      "div",
      {
        className: "select-none w-full max-w-sm lg:max-w-[400px]",
        onMouseEnter: handleTimelineMouseEnter,
        onMouseLeave: handleTimelineMouseLeave,
        children: /* @__PURE__ */ jsxs33("div", { className: "relative pt-5 -mt-[16px]", children: [
          /* @__PURE__ */ jsxs33(
            "div",
            {
              ref: sliderRef,
              className: "relative cursor-pointer py-3",
              onClick: handleSliderClick,
              children: [
                /* @__PURE__ */ jsx43("div", { className: "relative h-0.5 bg-recessed", children: /* @__PURE__ */ jsx43(
                  "div",
                  {
                    className: "absolute top-0 left-0 h-full bg-secondary",
                    style: { width: `${percentage}%` }
                  }
                ) }),
                /* @__PURE__ */ jsxs33(
                  "div",
                  {
                    className: "absolute top-1/2 -translate-y-1/2",
                    style: { left: `${percentage}%` },
                    children: [
                      /* @__PURE__ */ jsx43("div", { className: "absolute bottom-full left-0 -translate-x-1/2 mb-0.5", children: /* @__PURE__ */ jsx43("span", { className: "text-sm font-sans text-accent whitespace-nowrap", children: Math.round(currentYear) }) }),
                      /* @__PURE__ */ jsx43(
                        "button",
                        {
                          onClick: handlePlayheadClick,
                          onMouseDown: handlePlayheadDrag,
                          className: "w-6 h-6 -ml-3 flex items-center justify-center bg-secondary hover:bg-primary transition-colors cursor-grab active:cursor-grabbing",
                          "aria-label": isPlaying ? "Pause" : "Play",
                          children: isPlaying ? /* @__PURE__ */ jsx43(Pause, { className: "w-3 h-3 text-white" }) : /* @__PURE__ */ jsx43(Play, { className: "w-3 h-3 text-white ml-0.5" })
                        }
                      )
                    ]
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsx43(
            "div",
            {
              className: "absolute top-1/2 translate-y-2 left-0 transition-opacity duration-200",
              style: { opacity: percentage > 12 ? 1 : 0 },
              children: /* @__PURE__ */ jsx43("span", { className: "text-xs font-sans text-tertiary", children: minYear })
            }
          ),
          /* @__PURE__ */ jsx43(
            "div",
            {
              className: "absolute top-1/2 translate-y-2 -translate-x-1/2 transition-opacity duration-200",
              style: { left: "33.33%", opacity: Math.abs(percentage - 33.33) > 10 ? 1 : 0 },
              children: /* @__PURE__ */ jsx43("span", { className: "text-xs font-sans text-tertiary", children: "2030" })
            }
          ),
          /* @__PURE__ */ jsx43(
            "div",
            {
              className: "absolute top-1/2 translate-y-2 -translate-x-1/2 transition-opacity duration-200",
              style: { left: "66.67%", opacity: Math.abs(percentage - 66.67) > 10 ? 1 : 0 },
              children: /* @__PURE__ */ jsx43("span", { className: "text-xs font-sans text-tertiary", children: "2040" })
            }
          ),
          /* @__PURE__ */ jsx43(
            "div",
            {
              className: "absolute top-1/2 translate-y-2 right-0 transition-opacity duration-200",
              style: { opacity: percentage < 88 ? 1 : 0 },
              children: /* @__PURE__ */ jsx43("span", { className: "text-xs font-sans text-tertiary", children: maxYear })
            }
          )
        ] })
      }
    );
  }
  return /* @__PURE__ */ jsx43("div", { className: "w-full flex justify-center select-none", children: /* @__PURE__ */ jsx43("div", { className: "w-full px-4 md:px-8 lg:px-16 pt-8 pb-1", children: /* @__PURE__ */ jsxs33("div", { className: "flex items-center gap-6 mb-6", children: [
    /* @__PURE__ */ jsxs33("div", { className: "flex flex-col", children: [
      /* @__PURE__ */ jsx43("span", { className: "text-4xl text-primary tabular-nums leading-none mb-1 font-mono", children: Math.round(currentYear) }),
      /* @__PURE__ */ jsx43("span", { className: "text-xs text-tertiary uppercase tracking-wider font-sans", children: "Year" })
    ] }),
    /* @__PURE__ */ jsxs33("div", { className: "flex-1", children: [
      /* @__PURE__ */ jsxs33(
        "div",
        {
          ref: sliderRef,
          className: "relative cursor-pointer py-3",
          onClick: handleSliderClick,
          children: [
            /* @__PURE__ */ jsx43("div", { className: "relative h-0.5 bg-recessed", children: /* @__PURE__ */ jsx43(
              "div",
              {
                className: "absolute top-0 left-0 h-full bg-secondary",
                style: { width: `${percentage}%` }
              }
            ) }),
            /* @__PURE__ */ jsx43(
              "div",
              {
                className: "absolute top-1/2 cursor-grab active:cursor-grabbing",
                style: {
                  left: `${percentage}%`,
                  transform: "translate(-50%, -50%)",
                  padding: "12px"
                },
                onMouseDown: handleMouseDown,
                children: /* @__PURE__ */ jsx43("div", { className: "w-2.5 h-2.5 bg-secondary" })
              }
            )
          ]
        }
      ),
      /* @__PURE__ */ jsxs33("div", { className: "flex justify-between mt-2", children: [
        /* @__PURE__ */ jsx43("span", { className: "text-caption-xs text-tertiary tabular-nums font-mono", children: minYear }),
        /* @__PURE__ */ jsx43("span", { className: "text-caption-xs text-tertiary tabular-nums font-mono", children: maxYear })
      ] })
    ] }),
    /* @__PURE__ */ jsx43(
      "button",
      {
        onClick: togglePlayPause,
        className: "w-8 h-8 flex items-center justify-center hover:bg-recessed transition-colors",
        "aria-label": isPlaying ? "Pause" : "Play",
        children: isPlaying ? /* @__PURE__ */ jsx43(Pause, { className: "w-4 h-4 text-secondary" }) : /* @__PURE__ */ jsx43(Play, { className: "w-4 h-4 text-secondary" })
      }
    )
  ] }) }) });
}

// src/overlays/Tooltip.tsx
import { useState as useState7, useRef as useRef5, useEffect as useEffect6, useCallback as useCallback4 } from "react";
import { createPortal } from "react-dom";
import { clsx as clsx3 } from "clsx";
import { Fragment as Fragment9, jsx as jsx44, jsxs as jsxs34 } from "react/jsx-runtime";
function useTooltipPosition(triggerRef, tooltipRef, placement, offset) {
  const [position, setPosition] = useState7({ top: 0, left: 0 });
  const calculatePosition = useCallback4(() => {
    if (!triggerRef.current || !tooltipRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    let top = 0;
    let left = 0;
    switch (placement) {
      case "top":
        top = triggerRect.top - tooltipRect.height - offset;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case "bottom":
        top = triggerRect.bottom + offset;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case "left":
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.left - tooltipRect.width - offset;
        break;
      case "right":
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.right + offset;
        break;
    }
    const padding = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    if (left < padding) {
      left = padding;
    } else if (left + tooltipRect.width > viewportWidth - padding) {
      left = viewportWidth - tooltipRect.width - padding;
    }
    if (top < padding) {
      top = padding;
    } else if (top + tooltipRect.height > viewportHeight - padding) {
      top = viewportHeight - tooltipRect.height - padding;
    }
    setPosition({ top, left });
  }, [triggerRef, tooltipRef, placement, offset]);
  return { position, calculatePosition };
}
function useTooltipState(options) {
  const { delay, closeDelay, isDisabled } = options;
  const [isOpen, setIsOpen] = useState7(false);
  const [isMounted, setIsMounted] = useState7(false);
  const showTimeoutRef = useRef5(null);
  const hideTimeoutRef = useRef5(null);
  useEffect6(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);
  const show = useCallback4(() => {
    if (isDisabled) return;
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    showTimeoutRef.current = setTimeout(() => {
      setIsOpen(true);
    }, delay);
  }, [delay, isDisabled]);
  const hide = useCallback4(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    hideTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, closeDelay);
  }, [closeDelay]);
  useEffect6(() => {
    return () => {
      if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);
  return { isOpen, isMounted, show, hide };
}
function Tooltip({
  children,
  content,
  placement = "top",
  delay = 500,
  closeDelay = 0,
  offset = 8,
  isDisabled = false,
  className
}) {
  const triggerRef = useRef5(null);
  const tooltipRef = useRef5(null);
  const [isPositioned, setIsPositioned] = useState7(false);
  const { isOpen, isMounted, show, hide } = useTooltipState({
    delay,
    closeDelay,
    isDisabled,
    placement,
    offset
  });
  const { position, calculatePosition } = useTooltipPosition(
    triggerRef,
    tooltipRef,
    placement,
    offset
  );
  useEffect6(() => {
    if (!isOpen) {
      setIsPositioned(false);
    }
  }, [isOpen]);
  useEffect6(() => {
    if (!isOpen || !isMounted) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 20;
    const waitForRefAndPosition = () => {
      if (cancelled) return;
      attempts++;
      if (tooltipRef.current && triggerRef.current) {
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        if (tooltipRect.width > 0 && tooltipRect.height > 0) {
          calculatePosition();
          setIsPositioned(true);
          return;
        }
      }
      if (attempts < maxAttempts) {
        requestAnimationFrame(waitForRefAndPosition);
      }
    };
    requestAnimationFrame(waitForRefAndPosition);
    return () => {
      cancelled = true;
    };
  }, [isOpen, isMounted, calculatePosition]);
  const tooltipId = useRef5(`tooltip-${Math.random().toString(36).substr(2, 9)}`).current;
  const tooltipElement = isOpen && isMounted ? createPortal(
    /* @__PURE__ */ jsxs34(
      "div",
      {
        ref: tooltipRef,
        id: tooltipId,
        role: "tooltip",
        className: clsx3(
          "fixed z-[9999] px-2 py-1 text-xs rounded-sm shadow-md",
          "bg-primary text-white",
          "max-w-xs",
          className
        ),
        style: {
          top: position.top,
          left: position.left,
          visibility: isPositioned ? "visible" : "hidden",
          opacity: isPositioned ? 1 : 0,
          transition: "opacity 0.15s ease-in-out"
        },
        children: [
          /* @__PURE__ */ jsx44(
            "span",
            {
              className: clsx3(
                "absolute w-0 h-0",
                "border-4 border-transparent",
                {
                  "bottom-full left-1/2 -translate-x-1/2 border-b-primary": placement === "bottom",
                  "top-full left-1/2 -translate-x-1/2 border-t-primary": placement === "top",
                  "left-full top-1/2 -translate-y-1/2 border-l-primary": placement === "left",
                  "right-full top-1/2 -translate-y-1/2 border-r-primary": placement === "right"
                }
              )
            }
          ),
          content
        ]
      }
    ),
    document.body
  ) : null;
  return /* @__PURE__ */ jsxs34(Fragment9, { children: [
    /* @__PURE__ */ jsx44(
      "span",
      {
        ref: triggerRef,
        onMouseEnter: show,
        onMouseLeave: hide,
        onFocus: show,
        onBlur: hide,
        "aria-describedby": isOpen ? tooltipId : void 0,
        className: "inline-block",
        children
      }
    ),
    tooltipElement
  ] });
}
function TooltipCard({
  children,
  title,
  content,
  variant = "default",
  placement = "bottom",
  delay = 300,
  closeDelay = 100,
  offset = 10,
  isDisabled = false,
  maxWidth = 320,
  className
}) {
  const triggerRef = useRef5(null);
  const tooltipRef = useRef5(null);
  const [isPositioned, setIsPositioned] = useState7(false);
  const { isOpen, isMounted, show, hide } = useTooltipState({
    delay,
    closeDelay,
    isDisabled,
    placement,
    offset
  });
  const { position, calculatePosition } = useTooltipPosition(
    triggerRef,
    tooltipRef,
    placement,
    offset
  );
  useEffect6(() => {
    if (!isOpen) {
      setIsPositioned(false);
    }
  }, [isOpen]);
  useEffect6(() => {
    if (!isOpen || !isMounted) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 20;
    const waitForRefAndPosition = () => {
      if (cancelled) return;
      attempts++;
      if (tooltipRef.current && triggerRef.current) {
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        if (tooltipRect.width > 0 && tooltipRect.height > 0) {
          calculatePosition();
          setIsPositioned(true);
          return;
        }
      }
      if (attempts < maxAttempts) {
        requestAnimationFrame(waitForRefAndPosition);
      }
    };
    requestAnimationFrame(waitForRefAndPosition);
    return () => {
      cancelled = true;
    };
  }, [isOpen, isMounted, calculatePosition]);
  const tooltipId = useRef5(`tooltip-card-${Math.random().toString(36).substr(2, 9)}`).current;
  const variantStyles = {
    default: "bg-surface border border-recessed shadow-lg",
    warm: "bg-elevated-warm border border-tan-warm shadow-lg",
    accent: "bg-surface border border-recessed border-t-2 border-t-accent shadow-lg"
  };
  const maxWidthStyle = typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth;
  const tooltipElement = isOpen && isMounted ? createPortal(
    /* @__PURE__ */ jsx44(
      "div",
      {
        ref: tooltipRef,
        id: tooltipId,
        role: "tooltip",
        className: clsx3(
          "fixed z-[9999] rounded-sm",
          variantStyles[variant],
          className
        ),
        style: {
          top: position.top,
          left: position.left,
          maxWidth: maxWidthStyle,
          visibility: isPositioned ? "visible" : "hidden",
          opacity: isPositioned ? 1 : 0,
          transition: "opacity 0.15s ease-in-out"
        },
        onMouseEnter: show,
        onMouseLeave: hide,
        children: /* @__PURE__ */ jsxs34("div", { className: "p-3", children: [
          title && /* @__PURE__ */ jsx44("div", { className: "text-sm font-sans font-semibold text-primary mb-1", children: title }),
          /* @__PURE__ */ jsx44("div", { className: "text-xs font-sans text-secondary leading-relaxed", children: content })
        ] })
      }
    ),
    document.body
  ) : null;
  return /* @__PURE__ */ jsxs34(Fragment9, { children: [
    /* @__PURE__ */ jsx44(
      "span",
      {
        ref: triggerRef,
        onMouseEnter: show,
        onMouseLeave: hide,
        onFocus: show,
        onBlur: hide,
        "aria-describedby": isOpen ? tooltipId : void 0,
        className: "inline-block",
        children
      }
    ),
    tooltipElement
  ] });
}

// src/charts/PresetButtons.tsx
import { Fragment as Fragment10, jsx as jsx45, jsxs as jsxs35 } from "react/jsx-runtime";
var presets = [
  {
    id: "mainstream",
    label: "Mainstream Lending",
    description: "Mortgages and personal loans. No investment portfolios accepted.",
    nodes: [
      // All generations
      "silent",
      "boomers",
      "genx",
      "millennials",
      "genz",
      "genalpha",
      // Mass Market + Mass Affluent
      "bottom-50pct",
      "50th-90th",
      // Traditional Assets
      "traditional",
      // Specific asset classes
      "primary-real-estate",
      "cash-equivalents"
    ]
  },
  {
    id: "pledged-asset",
    label: "Pledged-Asset Lending",
    description: "Securities-backed credit lines. Stocks and bonds only. Typically SOFR + 2-4%.",
    nodes: [
      // All generations
      "silent",
      "boomers",
      "genx",
      "millennials",
      "genz",
      "genalpha",
      // Excludes mass market (bottom-50pct) - typically don't qualify for PAL
      "50th-90th",
      "90th-99th",
      "top-1pct",
      // Traditional Assets
      "traditional",
      // Securities only (no real estate or cash)
      "public-equities",
      "fixed-income"
    ]
  },
  {
    id: "uhnw",
    label: "UHNW Capital Access",
    description: "Private banking accepts alternatives, but only for the top 10%.",
    nodes: [
      // All generations
      "silent",
      "boomers",
      "genx",
      "millennials",
      "genz",
      "genalpha",
      // HNW and UHNW only
      "90th-99th",
      "top-1pct",
      // Both philosophies
      "traditional",
      "alternative",
      // Broader asset base
      "primary-real-estate",
      "cash-equivalents",
      "public-equities",
      "fixed-income",
      "private-equity-vc",
      "re-investments"
    ]
  },
  {
    id: "aaim",
    label: "Aaim-Enabled Lending",
    description: "Full-spectrum collateral recognition. Every tier, every asset class.",
    nodes: [
      // All generations
      "silent",
      "boomers",
      "genx",
      "millennials",
      "genz",
      "genalpha",
      // All wealth tiers
      "bottom-50pct",
      "50th-90th",
      "90th-99th",
      "top-1pct",
      // Both philosophies
      "traditional",
      "alternative",
      // All asset types
      "private-equity-vc",
      "digital-assets",
      "re-investments",
      "private-credit-hf",
      "other-alts",
      "public-equities",
      "fixed-income",
      "cash-equivalents",
      "primary-real-estate"
    ]
  }
];
function PresetButtons({ onPresetSelect, selectedNodes, compact }) {
  const isPresetActive = (preset) => {
    if (selectedNodes.size !== preset.nodes.length) return false;
    return preset.nodes.every((node) => selectedNodes.has(node));
  };
  const handlePresetClick = (preset) => {
    if (isPresetActive(preset)) {
      onPresetSelect(/* @__PURE__ */ new Set());
    } else {
      onPresetSelect(new Set(preset.nodes));
    }
  };
  const getButtonClasses = (preset, isActive) => {
    if (preset.id === "aaim" && isActive) {
      return "bg-accent border-accent shadow-sm";
    }
    if (isActive) {
      return "bg-secondary border-secondary shadow-sm";
    }
    return "bg-elevated border-recessed shadow-sm hover:shadow-md hover:-translate-y-0.5";
  };
  const isCompact = compact ?? true;
  return /* @__PURE__ */ jsxs35(Fragment10, { children: [
    /* @__PURE__ */ jsx45("div", { className: "flex justify-center w-full lg:hidden", children: /* @__PURE__ */ jsx45("div", { className: "grid grid-cols-2 gap-2", style: { width: "min(520px, 96%)" }, children: presets.map((preset) => {
      const isActive = isPresetActive(preset);
      return /* @__PURE__ */ jsxs35(
        "button",
        {
          onClick: () => handlePresetClick(preset),
          className: `group w-full flex flex-col items-center text-center px-2.5 py-2 rounded-sm border font-sans transition-all duration-200 focus:outline-none ${getButtonClasses(preset, isActive)}`,
          children: [
            /* @__PURE__ */ jsx45(
              "span",
              {
                className: `text-caption-sm font-semibold leading-tight ${isActive ? "text-white" : "text-primary group-hover:text-secondary"}`,
                children: preset.label
              }
            ),
            /* @__PURE__ */ jsx45(
              "span",
              {
                className: `text-caption-xs leading-tight mt-0.5 ${isActive ? "text-white/80" : "text-tertiary group-hover:text-secondary/70"}`,
                children: preset.description
              }
            )
          ]
        },
        preset.id
      );
    }) }) }),
    /* @__PURE__ */ jsx45("div", { className: "hidden lg:grid grid-cols-4 gap-3 w-full", children: presets.map((preset) => {
      const isActive = isPresetActive(preset);
      if (isCompact) {
        return /* @__PURE__ */ jsx45(
          TooltipCard,
          {
            title: preset.label,
            content: preset.description,
            placement: "bottom",
            delay: 200,
            variant: preset.id === "aaim" ? "accent" : "warm",
            children: /* @__PURE__ */ jsx45(
              "button",
              {
                onClick: () => handlePresetClick(preset),
                className: `group inline-flex items-center justify-center text-center px-3 py-2 rounded-sm border font-sans font-semibold text-xs leading-tight transition-all duration-200 focus:outline-none w-full ${getButtonClasses(preset, isActive)}`,
                children: /* @__PURE__ */ jsx45(
                  "span",
                  {
                    className: isActive ? "text-white" : "text-primary group-hover:text-secondary",
                    children: preset.label
                  }
                )
              }
            )
          },
          preset.id
        );
      }
      return /* @__PURE__ */ jsxs35(
        "button",
        {
          onClick: () => handlePresetClick(preset),
          className: `group flex flex-col items-center text-center px-3 py-2.5 rounded-sm border font-sans transition-all duration-200 focus:outline-none w-full ${getButtonClasses(preset, isActive)}`,
          children: [
            /* @__PURE__ */ jsx45(
              "span",
              {
                className: `text-xs font-semibold leading-tight ${isActive ? "text-white" : "text-primary group-hover:text-secondary"}`,
                children: preset.label
              }
            ),
            /* @__PURE__ */ jsx45(
              "span",
              {
                className: `text-caption-xs leading-tight mt-0.5 ${isActive ? "text-white/80" : "text-tertiary group-hover:text-secondary/70"}`,
                children: preset.description
              }
            )
          ]
        },
        preset.id
      );
    }) })
  ] });
}

// src/charts/sankey-types/timeseries.ts
function lerp(start, end, t) {
  return start + (end - start) * t;
}
function getDataForYear(dataset, year) {
  const clampedYear = Math.max(dataset.minYear, Math.min(dataset.maxYear, year));
  let beforeFrame = dataset.keyframes[0];
  let afterFrame = dataset.keyframes[dataset.keyframes.length - 1];
  for (let i = 0; i < dataset.keyframes.length - 1; i++) {
    if (dataset.keyframes[i].year <= clampedYear && dataset.keyframes[i + 1].year >= clampedYear) {
      beforeFrame = dataset.keyframes[i];
      afterFrame = dataset.keyframes[i + 1];
      break;
    }
  }
  if (beforeFrame.year === clampedYear) {
    return beforeFrame;
  }
  if (afterFrame.year === clampedYear) {
    return afterFrame;
  }
  const t = (clampedYear - beforeFrame.year) / (afterFrame.year - beforeFrame.year);
  const nodeValues = {};
  for (const nodeId of Object.keys(beforeFrame.nodeValues)) {
    nodeValues[nodeId] = lerp(beforeFrame.nodeValues[nodeId], afterFrame.nodeValues[nodeId], t);
  }
  const linkValues = {};
  for (const linkKey of Object.keys(beforeFrame.linkValues)) {
    linkValues[linkKey] = lerp(beforeFrame.linkValues[linkKey], afterFrame.linkValues[linkKey], t);
  }
  const metrics = {
    totalWealth: lerp(beforeFrame.metrics.totalWealth, afterFrame.metrics.totalWealth, t),
    alternativeAssets: lerp(beforeFrame.metrics.alternativeAssets, afterFrame.metrics.alternativeAssets, t),
    alternativeShare: lerp(beforeFrame.metrics.alternativeShare, afterFrame.metrics.alternativeShare, t)
  };
  return {
    year: clampedYear,
    nodeValues,
    linkValues,
    metrics
  };
}
function buildSankeyData(dataset, year) {
  const yearData = getDataForYear(dataset, year);
  const nodes = dataset.nodeStructure.map((node) => ({
    ...node,
    value: yearData.nodeValues[node.id] || 0
  }));
  const links = dataset.linkStructure.map((link) => ({
    ...link,
    value: yearData.linkValues[`${link.source}-${link.target}`] || 0
  }));
  return {
    sankeyData: {
      nodes,
      links
    },
    metrics: yearData.metrics
  };
}

// src/charts/sankey-utils/dataConverter.ts
function buildTimeSeriesDataset(keyframes, nodeStructure2, linkStructure2) {
  const sortedKeyframes = [...keyframes].sort((a, b) => a.year - b.year);
  return {
    keyframes: sortedKeyframes,
    nodeStructure: nodeStructure2,
    linkStructure: linkStructure2,
    minYear: sortedKeyframes[0].year,
    maxYear: sortedKeyframes[sortedKeyframes.length - 1].year
  };
}

// src/charts/sankey-data/keyframes-data.ts
var keyframe2020 = {
  year: 2020,
  nodeValues: {
    // Column 0: Generations (sum = 136,000)
    "silent": 22e3,
    "boomers": 7e4,
    "genx": 35e3,
    "millennials": 8e3,
    "genz": 1e3,
    "genalpha": 0,
    // Column 1: Wealth Tiers (sum = 136,000)
    "top-1pct": 50450,
    "90th-99th": 50900,
    "50th-90th": 28100,
    "bottom-50pct": 6550,
    // Column 2: Investment Philosophy (sum = 136,000)
    "traditional": 122400,
    // 90%
    "alternative": 13600,
    // 10%
    // Column 3: Asset Classes (sum = 136,000)
    "public-equities": 61200,
    "fixed-income": 42840,
    "cash-equivalents": 12240,
    "primary-real-estate": 6120,
    "private-equity-vc": 4080,
    "digital-assets": 1360,
    "re-investments": 4080,
    "private-credit-hf": 2720,
    "other-alts": 1360
  },
  linkValues: {
    // Generation -> Wealth Tier links
    "silent-top-1pct": 8800,
    "silent-90th-99th": 7700,
    "silent-50th-90th": 4400,
    "silent-bottom-50pct": 1100,
    "boomers-top-1pct": 27300,
    "boomers-90th-99th": 25200,
    "boomers-50th-90th": 14e3,
    "boomers-bottom-50pct": 3500,
    "genx-top-1pct": 12600,
    "genx-90th-99th": 14e3,
    "genx-50th-90th": 7e3,
    "genx-bottom-50pct": 1400,
    "millennials-top-1pct": 1600,
    "millennials-90th-99th": 3600,
    "millennials-50th-90th": 2400,
    "millennials-bottom-50pct": 400,
    "genz-top-1pct": 150,
    "genz-90th-99th": 400,
    "genz-50th-90th": 300,
    "genz-bottom-50pct": 150,
    // Wealth Tier -> Philosophy links
    "top-1pct-traditional": 43891.5,
    "top-1pct-alternative": 6558.5,
    "90th-99th-traditional": 46319,
    "90th-99th-alternative": 4581,
    "50th-90th-traditional": 27257,
    "50th-90th-alternative": 843,
    "bottom-50pct-traditional": 4932.5,
    "bottom-50pct-alternative": 1617.5,
    // Philosophy -> Asset Class links
    "traditional-public-equities": 61200,
    "traditional-fixed-income": 42840,
    "traditional-cash-equivalents": 12240,
    "traditional-primary-real-estate": 6120,
    "alternative-private-equity-vc": 4080,
    "alternative-digital-assets": 1360,
    "alternative-re-investments": 4080,
    "alternative-private-credit-hf": 2720,
    "alternative-other-alts": 1360
  },
  metrics: {
    totalWealth: 136e3,
    alternativeAssets: 13600,
    alternativeShare: 10
  }
};
var keyframe2025 = {
  year: 2025,
  nodeValues: {
    // Column 0: Generations (sum = 178,000)
    "silent": 19600,
    // 11% - declining
    "boomers": 89e3,
    // 50%
    "genx": 49840,
    // 28%
    "millennials": 16020,
    // 9%
    "genz": 3540,
    // 2%
    "genalpha": 0,
    // Column 1: Wealth Tiers (sum = 178,000)
    "top-1pct": 66044,
    "90th-99th": 66660,
    "50th-90th": 36054,
    "bottom-50pct": 9242,
    // Column 2: Investment Philosophy (sum = 178,000)
    "traditional": 151300,
    // 85%
    "alternative": 26700,
    // 15%
    // Column 3: Asset Classes (sum = 178,000)
    "public-equities": 72624,
    // 48% of traditional
    "fixed-income": 51693,
    // 34.2% of traditional
    "cash-equivalents": 15130,
    // 10% of traditional
    "primary-real-estate": 11853,
    // 7.8% of traditional
    "private-equity-vc": 6675,
    // 25% of alternative
    "digital-assets": 5340,
    // 20% of alternative
    "re-investments": 6675,
    // 25% of alternative
    "private-credit-hf": 5340,
    // 20% of alternative
    "other-alts": 2670
    // 10% of alternative
  },
  linkValues: {
    // Generation -> Wealth Tier links
    "silent-top-1pct": 7840,
    "silent-90th-99th": 6860,
    "silent-50th-90th": 3920,
    "silent-bottom-50pct": 980,
    "boomers-top-1pct": 34710,
    "boomers-90th-99th": 32040,
    "boomers-50th-90th": 17800,
    "boomers-bottom-50pct": 4450,
    "genx-top-1pct": 19936,
    "genx-90th-99th": 19936,
    "genx-50th-90th": 7968,
    "genx-bottom-50pct": 2e3,
    "millennials-top-1pct": 3204,
    "millennials-90th-99th": 6408,
    "millennials-50th-90th": 5127,
    "millennials-bottom-50pct": 1281,
    "genz-top-1pct": 354,
    "genz-90th-99th": 1416,
    "genz-50th-90th": 1239,
    "genz-bottom-50pct": 531,
    // Wealth Tier -> Philosophy links
    "top-1pct-traditional": 54666.44,
    "top-1pct-alternative": 11377.56,
    "90th-99th-traditional": 57327.6,
    "90th-99th-alternative": 9332.4,
    "50th-90th-traditional": 31727.52,
    "50th-90th-alternative": 4326.48,
    "bottom-50pct-traditional": 7578.44,
    "bottom-50pct-alternative": 1663.56,
    // Philosophy -> Asset Class links
    "traditional-public-equities": 72624,
    "traditional-fixed-income": 51693,
    "traditional-cash-equivalents": 15130,
    "traditional-primary-real-estate": 11853,
    "alternative-private-equity-vc": 6675,
    "alternative-digital-assets": 5340,
    "alternative-re-investments": 6675,
    "alternative-private-credit-hf": 5340,
    "alternative-other-alts": 2670
  },
  metrics: {
    totalWealth: 178e3,
    alternativeAssets: 26700,
    alternativeShare: 15
  }
};
var keyframe2030 = {
  year: 2030,
  nodeValues: {
    // Column 0: Generations (sum = 232,000)
    "silent": 13920,
    // 6% - declining fast
    "boomers": 104400,
    // 45%
    "genx": 69600,
    // 30%
    "millennials": 34800,
    // 15%
    "genz": 9280,
    // 4%
    "genalpha": 0,
    // Column 1: Wealth Tiers (sum = 232,000)
    "top-1pct": 86080,
    "90th-99th": 86800,
    "50th-90th": 47920,
    "bottom-50pct": 11200,
    // Column 2: Investment Philosophy (sum = 232,000)
    "traditional": 190240,
    // 82%
    "alternative": 41760,
    // 18%
    // Column 3: Asset Classes (sum = 232,000)
    "public-equities": 87387.2,
    // 45.9% of traditional
    "fixed-income": 64721.6,
    // 34% of traditional
    "cash-equivalents": 19024,
    // 10% of traditional
    "primary-real-estate": 19107.2,
    // 10.1% of traditional
    "private-equity-vc": 10440,
    // 25% of alternative
    "digital-assets": 8352,
    // 20% of alternative
    "re-investments": 10440,
    // 25% of alternative
    "private-credit-hf": 8352,
    // 20% of alternative
    "other-alts": 4176
    // 10% of alternative
  },
  linkValues: {
    // Generation -> Wealth Tier links
    "silent-top-1pct": 5568,
    "silent-90th-99th": 4872,
    "silent-50th-90th": 2784,
    "silent-bottom-50pct": 696,
    "boomers-top-1pct": 40716,
    "boomers-90th-99th": 37584,
    "boomers-50th-90th": 20880,
    "boomers-bottom-50pct": 5220,
    "genx-top-1pct": 27840,
    "genx-90th-99th": 27840,
    "genx-50th-90th": 11136,
    "genx-bottom-50pct": 2784,
    "millennials-top-1pct": 8700,
    "millennials-90th-99th": 13920,
    "millennials-50th-90th": 10440,
    "millennials-bottom-50pct": 1740,
    "genz-top-1pct": 1256,
    "genz-90th-99th": 2584,
    "genz-50th-90th": 2680,
    "genz-bottom-50pct": 2760,
    // Wealth Tier -> Philosophy links
    "top-1pct-traditional": 68784.4,
    "top-1pct-alternative": 17295.6,
    "90th-99th-traditional": 72104,
    "90th-99th-alternative": 14696,
    "50th-90th-traditional": 40833.6,
    "50th-90th-alternative": 7086.4,
    "bottom-50pct-traditional": 8518,
    "bottom-50pct-alternative": 2682,
    // Philosophy -> Asset Class links
    "traditional-public-equities": 87387.2,
    "traditional-fixed-income": 64721.6,
    "traditional-cash-equivalents": 19024,
    "traditional-primary-real-estate": 19107.2,
    "alternative-private-equity-vc": 10440,
    "alternative-digital-assets": 8352,
    "alternative-re-investments": 10440,
    "alternative-private-credit-hf": 8352,
    "alternative-other-alts": 4176
  },
  metrics: {
    totalWealth: 232e3,
    alternativeAssets: 41760,
    alternativeShare: 18
  }
};
var keyframe2035 = {
  year: 2035,
  nodeValues: {
    // Column 0: Generations (sum = 304,000)
    "silent": 6080,
    // 2% - nearly gone
    "boomers": 109440,
    // 36%
    "genx": 100320,
    // 33%
    "millennials": 63840,
    // 21%
    "genz": 24320,
    // 8%
    "genalpha": 0,
    // Column 1: Wealth Tiers (sum = 304,000)
    "top-1pct": 112784,
    "90th-99th": 113696,
    "50th-90th": 62816,
    "bottom-50pct": 14704,
    // Column 2: Investment Philosophy (sum = 304,000)
    "traditional": 237120,
    // 78%
    "alternative": 66880,
    // 22%
    // Column 3: Asset Classes (sum = 304,000)
    "public-equities": 102777.6,
    // 43.4% of traditional
    "fixed-income": 80620.8,
    // 34% of traditional
    "cash-equivalents": 23712,
    // 10% of traditional
    "primary-real-estate": 30009.6,
    // 12.6% of traditional
    "private-equity-vc": 16720,
    // 25% of alternative
    "digital-assets": 13376,
    // 20% of alternative
    "re-investments": 16720,
    // 25% of alternative
    "private-credit-hf": 13376,
    // 20% of alternative
    "other-alts": 6688
    // 10% of alternative
  },
  linkValues: {
    // Generation -> Wealth Tier links
    "silent-top-1pct": 2432,
    "silent-90th-99th": 2128,
    "silent-50th-90th": 1216,
    "silent-bottom-50pct": 304,
    "boomers-top-1pct": 42681.6,
    "boomers-90th-99th": 39398.4,
    "boomers-50th-90th": 21888,
    "boomers-bottom-50pct": 5472,
    "genx-top-1pct": 40128,
    "genx-90th-99th": 40128,
    "genx-50th-90th": 16051.2,
    "genx-bottom-50pct": 4012.8,
    "millennials-top-1pct": 19152,
    "millennials-90th-99th": 25536,
    "millennials-50th-90th": 15974.4,
    "millennials-bottom-50pct": 3177.6,
    "genz-top-1pct": 4864,
    "genz-90th-99th": 6505.6,
    "genz-50th-90th": 7686.4,
    "genz-bottom-50pct": 5264,
    // Wealth Tier -> Philosophy links
    "top-1pct-traditional": 85715.84,
    "top-1pct-alternative": 27068.16,
    "90th-99th-traditional": 90956.8,
    "90th-99th-alternative": 22739.2,
    "50th-90th-traditional": 51297.1,
    "50th-90th-alternative": 11518.9,
    "bottom-50pct-traditional": 9150.26,
    "bottom-50pct-alternative": 5553.74,
    // Philosophy -> Asset Class links
    "traditional-public-equities": 102777.6,
    "traditional-fixed-income": 80620.8,
    "traditional-cash-equivalents": 23712,
    "traditional-primary-real-estate": 30009.6,
    "alternative-private-equity-vc": 16720,
    "alternative-digital-assets": 13376,
    "alternative-re-investments": 16720,
    "alternative-private-credit-hf": 13376,
    "alternative-other-alts": 6688
  },
  metrics: {
    totalWealth: 304e3,
    alternativeAssets: 66880,
    alternativeShare: 22
  }
};
var keyframe2040 = {
  year: 2040,
  nodeValues: {
    // Column 0: Generations (sum = 397,000)
    "silent": 0,
    // 0% - gone
    "boomers": 79400,
    // 20% - declining
    "genx": 130810,
    // 33%
    "millennials": 119100,
    // 30%
    "genz": 67690,
    // 17%
    "genalpha": 0,
    // Column 1: Wealth Tiers (sum = 397,000)
    "top-1pct": 147287,
    "90th-99th": 148478,
    "50th-90th": 82016,
    "bottom-50pct": 19219,
    // Column 2: Investment Philosophy (sum = 397,000)
    "traditional": 293780,
    // 74%
    "alternative": 103220,
    // 26%
    // Column 3: Asset Classes (sum = 397,000)
    "public-equities": 120455.8,
    // 41% of traditional
    "fixed-income": 99885.2,
    // 34% of traditional
    "cash-equivalents": 29378,
    // 10% of traditional
    "primary-real-estate": 44061,
    // 15% of traditional
    "private-equity-vc": 25805,
    // 25% of alternative
    "digital-assets": 20644,
    // 20% of alternative
    "re-investments": 25805,
    // 25% of alternative
    "private-credit-hf": 20644,
    // 20% of alternative
    "other-alts": 10322
    // 10% of alternative
  },
  linkValues: {
    // Generation -> Wealth Tier links
    "boomers-top-1pct": 30894,
    "boomers-90th-99th": 28584,
    "boomers-50th-90th": 15880,
    "boomers-bottom-50pct": 4042,
    "genx-top-1pct": 52324,
    "genx-90th-99th": 52324,
    "genx-50th-90th": 20929.6,
    "genx-bottom-50pct": 5232.4,
    "millennials-top-1pct": 41685,
    "millennials-90th-99th": 47640,
    "millennials-50th-90th": 23820,
    "millennials-bottom-50pct": 5955,
    "genz-top-1pct": 16922.5,
    "genz-90th-99th": 19930,
    "genz-50th-90th": 21386.4,
    "genz-bottom-50pct": 9451.1,
    // Wealth Tier -> Philosophy links
    "top-1pct-traditional": 105566.42,
    "top-1pct-alternative": 41720.58,
    "90th-99th-traditional": 113002.72,
    "90th-99th-alternative": 35475.28,
    "50th-90th-traditional": 63804.44,
    "50th-90th-alternative": 18211.56,
    "bottom-50pct-traditional": 11406.42,
    "bottom-50pct-alternative": 7812.58,
    // Philosophy -> Asset Class links
    "traditional-public-equities": 120455.8,
    "traditional-fixed-income": 99885.2,
    "traditional-cash-equivalents": 29378,
    "traditional-primary-real-estate": 44061,
    "alternative-private-equity-vc": 25805,
    "alternative-digital-assets": 20644,
    "alternative-re-investments": 25805,
    "alternative-private-credit-hf": 20644,
    "alternative-other-alts": 10322
  },
  metrics: {
    totalWealth: 397e3,
    alternativeAssets: 103220,
    alternativeShare: 26
  }
};
var keyframe2045 = {
  year: 2045,
  nodeValues: {
    // Column 0: Generations (sum = 519,000)
    "silent": 0,
    // 0%
    "boomers": 36330,
    // 7% - minimal
    "genx": 145320,
    // 28%
    "millennials": 186840,
    // 36%
    "genz": 145320,
    // 28%
    "genalpha": 5190,
    // 1%
    // Column 1: Wealth Tiers (sum = 519,000)
    "top-1pct": 192546,
    "90th-99th": 194103,
    "50th-90th": 107226,
    "bottom-50pct": 25125,
    // Column 2: Investment Philosophy (sum = 519,000)
    "traditional": 373680,
    // 72%
    "alternative": 145320,
    // 28%
    // Column 3: Asset Classes (sum = 519,000)
    "public-equities": 145635.2,
    // 39% of traditional
    "fixed-income": 127051.2,
    // 34% of traditional
    "cash-equivalents": 37368,
    // 10% of traditional
    "primary-real-estate": 63625.6,
    // 17% of traditional
    "private-equity-vc": 36330,
    // 25% of alternative
    "digital-assets": 29064,
    // 20% of alternative
    "re-investments": 36330,
    // 25% of alternative
    "private-credit-hf": 29064,
    // 20% of alternative
    "other-alts": 14532
    // 10% of alternative
  },
  linkValues: {
    // Generation -> Wealth Tier links
    "boomers-top-1pct": 14168.7,
    "boomers-90th-99th": 13078.8,
    "boomers-50th-90th": 7266,
    "boomers-bottom-50pct": 1816.5,
    "genx-top-1pct": 58128,
    "genx-90th-99th": 58128,
    "genx-50th-90th": 23251.2,
    "genx-bottom-50pct": 5812.8,
    "millennials-top-1pct": 65394,
    "millennials-90th-99th": 74736,
    "millennials-50th-90th": 37368,
    "millennials-bottom-50pct": 9342,
    "genz-top-1pct": 47754.6,
    "genz-90th-99th": 46489.2,
    "genz-50th-90th": 37076.4,
    "genz-bottom-50pct": 14e3,
    "genalpha-top-1pct": 1076,
    "genalpha-90th-99th": 1671,
    "genalpha-50th-90th": 2264.4,
    "genalpha-bottom-50pct": 178.6,
    // Wealth Tier -> Philosophy links
    "top-1pct-traditional": 134623.2,
    "top-1pct-alternative": 57922.8,
    "90th-99th-traditional": 143476.1,
    "90th-99th-alternative": 50626.9,
    "50th-90th-traditional": 80164.6,
    "50th-90th-alternative": 27061.4,
    "bottom-50pct-traditional": 15416.1,
    "bottom-50pct-alternative": 9708.9,
    // Philosophy -> Asset Class links
    "traditional-public-equities": 145635.2,
    "traditional-fixed-income": 127051.2,
    "traditional-cash-equivalents": 37368,
    "traditional-primary-real-estate": 63625.6,
    "alternative-private-equity-vc": 36330,
    "alternative-digital-assets": 29064,
    "alternative-re-investments": 36330,
    "alternative-private-credit-hf": 29064,
    "alternative-other-alts": 14532
  },
  metrics: {
    totalWealth: 519e3,
    alternativeAssets: 145320,
    alternativeShare: 28
  }
};
var keyframe2050 = {
  year: 2050,
  nodeValues: {
    // Column 0: Generations (sum = 678,000)
    "silent": 0,
    // 0%
    "boomers": 0,
    // 0% - gone
    "genx": 122040,
    // 18%
    "millennials": 271200,
    // 40%
    "genz": 257640,
    // 38%
    "genalpha": 27120,
    // 4%
    // Column 1: Wealth Tiers (sum = 678,000)
    "top-1pct": 251532,
    "90th-99th": 253566,
    "50th-90th": 140088,
    "bottom-50pct": 32814,
    // Column 2: Investment Philosophy (sum = 678,000)
    "traditional": 474600,
    // 70%
    "alternative": 203400,
    // 30%
    // Column 3: Asset Classes (sum = 678,000)
    "public-equities": 175601.4,
    // 37% of traditional
    "fixed-income": 161364,
    // 34% of traditional
    "cash-equivalents": 47460,
    // 10% of traditional
    "primary-real-estate": 90174.6,
    // 19% of traditional
    "private-equity-vc": 50850,
    // 25% of alternative
    "digital-assets": 40680,
    // 20% of alternative
    "re-investments": 50850,
    // 25% of alternative
    "private-credit-hf": 40680,
    // 20% of alternative
    "other-alts": 20340
    // 10% of alternative
  },
  linkValues: {
    // Generation -> Wealth Tier links (no silent or boomers)
    "genx-top-1pct": 48816,
    "genx-90th-99th": 48816,
    "genx-50th-90th": 19526.4,
    "genx-bottom-50pct": 4881.6,
    "millennials-top-1pct": 94920,
    "millennials-90th-99th": 108480,
    "millennials-50th-90th": 54240,
    "millennials-bottom-50pct": 13560,
    "genz-top-1pct": 96358.2,
    "genz-90th-99th": 87092.4,
    "genz-50th-90th": 54410.4,
    "genz-bottom-50pct": 19779,
    "genalpha-top-1pct": 5437.8,
    "genalpha-90th-99th": 9177.6,
    "genalpha-50th-90th": 11911.2,
    "genalpha-bottom-50pct": 593.4,
    // Wealth Tier -> Philosophy links
    "top-1pct-traditional": 171042.76,
    "top-1pct-alternative": 80489.24,
    "90th-99th-traditional": 182007.4,
    "90th-99th-alternative": 71558.6,
    "50th-90th-traditional": 101783.9,
    "50th-90th-alternative": 38304.1,
    "bottom-50pct-traditional": 19766.04,
    "bottom-50pct-alternative": 13047.96,
    // Philosophy -> Asset Class links
    "traditional-public-equities": 175601.4,
    "traditional-fixed-income": 161364,
    "traditional-cash-equivalents": 47460,
    "traditional-primary-real-estate": 90174.6,
    "alternative-private-equity-vc": 50850,
    "alternative-digital-assets": 40680,
    "alternative-re-investments": 50850,
    "alternative-private-credit-hf": 40680,
    "alternative-other-alts": 20340
  },
  metrics: {
    totalWealth: 678e3,
    alternativeAssets: 203400,
    alternativeShare: 30
  }
};

// src/charts/sankey-data/dataset.ts
var nodeStructure = [
  // Column 0: Generations (6 nodes) - Youngest to Oldest
  { id: "genalpha", label: "Gen Alpha", color: "#4B3D2B", column: 0 },
  { id: "genz", label: "Gen Z", color: "#5B4D3B", column: 0 },
  { id: "millennials", label: "Millennials", color: "#6B5D4B", column: 0 },
  { id: "genx", label: "Gen X", color: "#7B6D5B", column: 0 },
  { id: "boomers", label: "Baby Boomers", color: "#8B7D6B", column: 0 },
  { id: "silent", label: "Silent Gen", color: "#9B8B7E", column: 0 },
  // Column 1: Wealth Tiers (4 nodes) - Poorest to Richest
  { id: "bottom-50pct", label: "Mass Market", color: "#85929E", column: 1 },
  { id: "50th-90th", label: "Mass Affluent", color: "#5D6D7E", column: 1 },
  { id: "90th-99th", label: "High Net Worth", color: "#34495E", column: 1 },
  { id: "top-1pct", label: "Ultra High Net Worth", color: "#2C3E50", column: 1 },
  // Column 2: Investment Philosophy (2 nodes) - THE KEY DIVIDE
  { id: "alternative", label: "Alternative Assets", color: "#6B8E7D", column: 2 },
  { id: "traditional", label: "Traditional Assets", color: "#8B6A47", column: 2 },
  // Column 3: Asset Classes (9 nodes) - Alternative first, then Traditional
  // Alternative Assets (5 nodes)
  { id: "private-equity-vc", label: "Private Equity/VC", color: "#5A9B8A", column: 3 },
  { id: "digital-assets", label: "Digital Assets", color: "#6BAB9A", column: 3 },
  { id: "re-investments", label: "RE Investments", color: "#7CBBAA", column: 3 },
  { id: "private-credit-hf", label: "Private Credit/HF", color: "#8DCBBA", column: 3 },
  { id: "other-alts", label: "Other Alternatives", color: "#9EDBCA", column: 3 },
  // Traditional Assets (4 nodes)
  { id: "public-equities", label: "Public Equities", color: "#9A7B4F", column: 3 },
  { id: "fixed-income", label: "Fixed Income", color: "#A68B5B", column: 3 },
  { id: "cash-equivalents", label: "Cash & Equivalents", color: "#B59B6B", column: 3 },
  { id: "primary-real-estate", label: "Primary Real Estate", color: "#C4AB7B", column: 3 }
];
var linkStructure = [
  // Column 0 → Column 1: Generations → Wealth Tiers (24 links: 6 gens × 4 tiers)
  { source: "silent", target: "top-1pct" },
  { source: "silent", target: "90th-99th" },
  { source: "silent", target: "50th-90th" },
  { source: "silent", target: "bottom-50pct" },
  { source: "boomers", target: "top-1pct" },
  { source: "boomers", target: "90th-99th" },
  { source: "boomers", target: "50th-90th" },
  { source: "boomers", target: "bottom-50pct" },
  { source: "genx", target: "top-1pct" },
  { source: "genx", target: "90th-99th" },
  { source: "genx", target: "50th-90th" },
  { source: "genx", target: "bottom-50pct" },
  { source: "millennials", target: "top-1pct" },
  { source: "millennials", target: "90th-99th" },
  { source: "millennials", target: "50th-90th" },
  { source: "millennials", target: "bottom-50pct" },
  { source: "genz", target: "top-1pct" },
  { source: "genz", target: "90th-99th" },
  { source: "genz", target: "50th-90th" },
  { source: "genz", target: "bottom-50pct" },
  { source: "genalpha", target: "top-1pct" },
  { source: "genalpha", target: "90th-99th" },
  { source: "genalpha", target: "50th-90th" },
  { source: "genalpha", target: "bottom-50pct" },
  // Column 1 → Column 2: Wealth Tiers → Investment Philosophy (8 links: 4 tiers × 2 philosophies)
  { source: "top-1pct", target: "traditional" },
  { source: "top-1pct", target: "alternative" },
  { source: "90th-99th", target: "traditional" },
  { source: "90th-99th", target: "alternative" },
  { source: "50th-90th", target: "traditional" },
  { source: "50th-90th", target: "alternative" },
  { source: "bottom-50pct", target: "traditional" },
  { source: "bottom-50pct", target: "alternative" },
  // Column 2 → Column 3: Investment Philosophy → Asset Classes (9 links)
  // Traditional → Traditional Asset Classes
  { source: "traditional", target: "public-equities" },
  { source: "traditional", target: "fixed-income" },
  { source: "traditional", target: "cash-equivalents" },
  { source: "traditional", target: "primary-real-estate" },
  // Alternative → Alternative Asset Classes
  { source: "alternative", target: "private-equity-vc" },
  { source: "alternative", target: "digital-assets" },
  { source: "alternative", target: "re-investments" },
  { source: "alternative", target: "private-credit-hf" },
  { source: "alternative", target: "other-alts" }
];
var wealthFlowDataset = buildTimeSeriesDataset(
  [
    keyframe2020,
    keyframe2025,
    keyframe2030,
    keyframe2035,
    keyframe2040,
    keyframe2045,
    keyframe2050
  ],
  nodeStructure,
  linkStructure
);
var datasetMetadata = {
  name: "Generational Wealth Transfer 2020-2050 (Conservative Scenario)",
  description: "$124T wealth transfer (Cerulli) driving shift to alternatives; conservative 30% penetration by 2050",
  sourceData: "generational-wealth-complete-2020-2050.csv (normalized to research-backed projections)",
  keyframeYears: [2020, 2025, 2030, 2035, 2040, 2045, 2050],
  interpolatedYears: 31,
  totalNodes: nodeStructure.length,
  totalLinks: linkStructure.length,
  structure: {
    generations: 6,
    wealthTiers: 4,
    investmentPhilosophies: 2,
    assetClasses: 9
  },
  keyInsights: {
    totalWealthTransfer: "$124T (2020-2048, Cerulli Associates)",
    wealthGrowthRate: "5.5% CAGR (conservative middle of 5-8% historical range)",
    alternativesGrowth: "$10.3T \u2192 $196.6T (2020-2050)",
    alternativeShareGrowth: "7.9% \u2192 30% (2020-2050, conservative scenario)",
    generationalAllocation: "31% younger vs 6% older (Bank of America 2022)"
  },
  validation: {
    normalized: true,
    matchesResearchProjections: true,
    baseline2020: 'Federal Reserve Z.1 Q4 2020 + Preqin "Alternatives in 2020"',
    totalWealth2020: "$130.2T",
    totalWealth2050: "$655.3T",
    alternatives2020: "$10.3T",
    alternatives2050: "$196.6T (30% penetration, industry consensus: 25-35%)"
  }
};

// src/charts/sankey-utils/calculateLendingBase.ts
var PLEDGED_ASSET_LENDING_NODES = [
  "silent",
  "boomers",
  "genx",
  "millennials",
  "genz",
  "genalpha",
  "50th-90th",
  "traditional",
  "primary-real-estate",
  "cash-equivalents"
];
function calculateLendingBase(sankeyData, selectedNodes, diagramWidth = 1e3, diagramHeight = 700, nodeWidth = 24) {
  const effectiveSelections = selectedNodes.size === 0 ? new Set(PLEDGED_ASSET_LENDING_NODES) : selectedNodes;
  if (effectiveSelections.size === 0) {
    return 0;
  }
  const { nodes, links } = calculateSankeyLayout(sankeyData, diagramWidth, diagramHeight, nodeWidth);
  const selectedByColumn = /* @__PURE__ */ new Map();
  effectiveSelections.forEach((nodeId) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (node) {
      if (!selectedByColumn.has(node.column)) {
        selectedByColumn.set(node.column, /* @__PURE__ */ new Set());
      }
      selectedByColumn.get(node.column).add(nodeId);
    }
  });
  const leftmostSelectedColumn = Math.min(...Array.from(selectedByColumn.keys()));
  const validNodes = /* @__PURE__ */ new Set();
  const validLinks = /* @__PURE__ */ new Set();
  if (effectiveSelections.size === 1) {
    const selectedNode = Array.from(effectiveSelections)[0];
    const upstreamNodes = /* @__PURE__ */ new Set();
    const toExploreUpstream = /* @__PURE__ */ new Set([selectedNode]);
    while (toExploreUpstream.size > 0) {
      const current = Array.from(toExploreUpstream)[0];
      toExploreUpstream.delete(current);
      upstreamNodes.add(current);
      links.forEach((link) => {
        if (link.targetNode.id === current && !upstreamNodes.has(link.sourceNode.id)) {
          toExploreUpstream.add(link.sourceNode.id);
        }
      });
    }
    const downstreamNodes = /* @__PURE__ */ new Set();
    const toExploreDownstream = /* @__PURE__ */ new Set([selectedNode]);
    while (toExploreDownstream.size > 0) {
      const current = Array.from(toExploreDownstream)[0];
      toExploreDownstream.delete(current);
      downstreamNodes.add(current);
      links.forEach((link) => {
        if (link.sourceNode.id === current && !downstreamNodes.has(link.targetNode.id)) {
          toExploreDownstream.add(link.targetNode.id);
        }
      });
    }
    upstreamNodes.forEach((n) => validNodes.add(n));
    downstreamNodes.forEach((n) => validNodes.add(n));
    links.forEach((link, index) => {
      const sourceInUpstream = upstreamNodes.has(link.sourceNode.id);
      const targetInUpstream = upstreamNodes.has(link.targetNode.id);
      const sourceInDownstream = downstreamNodes.has(link.sourceNode.id);
      const targetInDownstream = downstreamNodes.has(link.targetNode.id);
      if (sourceInUpstream && targetInUpstream || sourceInDownstream && targetInDownstream) {
        validLinks.add(index);
      }
    });
  } else {
    const sortedColumns = Array.from(selectedByColumn.keys()).sort((a, b) => a - b);
    const startNodes = selectedByColumn.get(leftmostSelectedColumn) || /* @__PURE__ */ new Set();
    startNodes.forEach((n) => validNodes.add(n));
    for (let i = 0; i < sortedColumns.length; i++) {
      const currentColumn = sortedColumns[i];
      const nextColumn = i < sortedColumns.length - 1 ? sortedColumns[i + 1] : null;
      const selectedInColumn = selectedByColumn.get(currentColumn);
      if (nextColumn !== null) {
        const nextSelectedNodes = selectedByColumn.get(nextColumn);
        const reachableFromCurrent = /* @__PURE__ */ new Set();
        const toExplore = new Set(selectedInColumn);
        const explored = /* @__PURE__ */ new Set();
        while (toExplore.size > 0) {
          const current = Array.from(toExplore)[0];
          toExplore.delete(current);
          explored.add(current);
          links.forEach((link, index) => {
            if (link.sourceNode.id === current) {
              if (nextSelectedNodes.has(link.targetNode.id)) {
                reachableFromCurrent.add(link.targetNode.id);
                validLinks.add(index);
              } else if (link.targetNode.column < nextColumn && !explored.has(link.targetNode.id)) {
                toExplore.add(link.targetNode.id);
                reachableFromCurrent.add(link.targetNode.id);
                validLinks.add(index);
              }
            }
          });
        }
        reachableFromCurrent.forEach((n) => validNodes.add(n));
      } else {
        const toExplore = new Set(selectedInColumn);
        let foundNew = true;
        while (foundNew) {
          foundNew = false;
          const currentNodes = new Set(toExplore);
          links.forEach((link, index) => {
            if (currentNodes.has(link.sourceNode.id) && !toExplore.has(link.targetNode.id)) {
              toExplore.add(link.targetNode.id);
              validNodes.add(link.targetNode.id);
              validLinks.add(index);
              foundNew = true;
            } else if (currentNodes.has(link.sourceNode.id) && toExplore.has(link.targetNode.id)) {
              validLinks.add(index);
            }
          });
        }
      }
    }
  }
  const nodeValues = /* @__PURE__ */ new Map();
  const allColumns = Array.from(new Set(nodes.map((n) => n.column))).sort((a, b) => a - b);
  allColumns.forEach((col) => {
    const nodesInColumn = nodes.filter((n) => n.column === col);
    nodesInColumn.forEach((node) => {
      if (node.column === 0) {
        let outgoingFlow = 0;
        links.forEach((link, index) => {
          if (link.sourceNode.id === node.id) {
            const isVisible = validLinks.has(index);
            if (isVisible && !isNaN(link.value)) {
              outgoingFlow += link.value;
            }
          }
        });
        nodeValues.set(node.id, isNaN(outgoingFlow) ? 0 : outgoingFlow);
      } else {
        let incomingFlow = 0;
        links.forEach((link, index) => {
          if (link.targetNode.id === node.id) {
            const isVisible = validLinks.has(index);
            if (isVisible) {
              const sourceFilteredValue = nodeValues.get(link.sourceNode.id) ?? link.sourceNode.value;
              const sourceOriginalValue = link.sourceNode.value;
              if (sourceOriginalValue > 0 && !isNaN(sourceFilteredValue) && !isNaN(link.value)) {
                const linkProportion = link.value / sourceOriginalValue;
                const linkFilteredValue = sourceFilteredValue * linkProportion;
                if (!isNaN(linkFilteredValue)) {
                  incomingFlow += linkFilteredValue;
                }
              }
            }
          }
        });
        nodeValues.set(node.id, isNaN(incomingFlow) ? 0 : incomingFlow);
      }
    });
  });
  let lendingBase = 0;
  nodes.forEach((node) => {
    if (node.column === 3) {
      const filteredValue = nodeValues.get(node.id) ?? 0;
      if (!isNaN(filteredValue)) {
        lendingBase += filteredValue;
      }
    }
  });
  return isNaN(lendingBase) ? 0 : lendingBase;
}

// src/charts/WealthFlowDiagram.tsx
import { jsx as jsx46, jsxs as jsxs36 } from "react/jsx-runtime";
function WealthFlowDiagram({ className = "" }) {
  const [currentYear, setCurrentYear] = useState8(2024);
  const [selectedNodes, setSelectedNodes] = useState8(/* @__PURE__ */ new Set());
  const { sankeyData, metrics } = buildSankeyData(wealthFlowDataset, currentYear);
  const lendingBase = useMemo4(() => {
    return calculateLendingBase(sankeyData, selectedNodes);
  }, [sankeyData, selectedNodes]);
  return /* @__PURE__ */ jsxs36("div", { className: `w-full ${className}`, children: [
    /* @__PURE__ */ jsx46("div", { className: "pb-4", children: /* @__PURE__ */ jsxs36("div", { className: "flex flex-col md:flex-row md:items-start md:justify-between gap-6", children: [
      /* @__PURE__ */ jsxs36("div", { className: "flex-1 max-w-2xl", children: [
        /* @__PURE__ */ jsx46("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-3", children: "Wealth Transfer Projections" }),
        /* @__PURE__ */ jsxs36("h2", { className: "text-2xl md:text-heading-page font-serif font-normal text-primary tracking-headline-serif mb-3 leading-tight", children: [
          "Investing Trends, ",
          /* @__PURE__ */ jsx46("span", { className: "whitespace-nowrap", children: "2020-2050" })
        ] }),
        /* @__PURE__ */ jsx46("p", { className: "text-base text-secondary leading-relaxed font-sans mb-4", children: "$124 trillion moving from Boomers to new generations, driving a shift from traditional 60/40 portfolios to alternative-heavy allocations. Watch how Aaim unlocks the future." }),
        /* @__PURE__ */ jsx46(
          PresetButtons,
          {
            onPresetSelect: setSelectedNodes,
            selectedNodes
          }
        )
      ] }),
      /* @__PURE__ */ jsxs36("div", { className: "flex flex-col items-center md:items-end w-full md:w-[400px] md:flex-shrink-0", children: [
        /* @__PURE__ */ jsxs36("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-3 text-center md:text-right w-full max-w-sm md:max-w-none", children: [
          "Estimations for year: ",
          /* @__PURE__ */ jsx46("span", { className: "text-accent", children: Math.round(currentYear) })
        ] }),
        /* @__PURE__ */ jsxs36("div", { className: "grid grid-cols-3 gap-3 mb-12 w-full max-w-sm md:max-w-none", children: [
          /* @__PURE__ */ jsxs36("div", { children: [
            /* @__PURE__ */ jsxs36("div", { className: "text-2xl md:text-heading-page font-serif text-primary leading-tight numeric-financial", children: [
              "$",
              Math.round(metrics.totalWealth / 1e3),
              "T"
            ] }),
            /* @__PURE__ */ jsxs36("div", { className: "text-label-xs md:text-xs font-sans text-tertiary uppercase mt-1", children: [
              "Total",
              /* @__PURE__ */ jsx46("br", { className: "md:hidden" }),
              " Wealth"
            ] })
          ] }),
          /* @__PURE__ */ jsxs36("div", { className: "text-center", children: [
            /* @__PURE__ */ jsxs36("div", { className: "text-2xl md:text-heading-page font-serif text-alternative leading-tight numeric-financial", children: [
              "$",
              Math.round(metrics.alternativeAssets / 1e3),
              "T"
            ] }),
            /* @__PURE__ */ jsx46("div", { className: "text-label-xs md:text-xs font-sans text-tertiary uppercase mt-1", children: "Alternatives" })
          ] }),
          /* @__PURE__ */ jsxs36("div", { className: "text-right", children: [
            /* @__PURE__ */ jsxs36("div", { className: "text-2xl md:text-heading-page font-serif text-accent leading-tight numeric-financial", children: [
              "$",
              (lendingBase / 1e3).toFixed(1),
              "T"
            ] }),
            /* @__PURE__ */ jsxs36("div", { className: "text-label-xs md:text-xs font-sans text-tertiary uppercase mt-1", children: [
              "Lending",
              /* @__PURE__ */ jsx46("br", { className: "md:hidden" }),
              " Base"
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsx46(
          Timeline,
          {
            currentYear,
            onYearChange: setCurrentYear,
            minYear: 2020,
            maxYear: 2050,
            compact: true
          }
        )
      ] })
    ] }) }),
    /* @__PURE__ */ jsx46(
      SankeyDiagram,
      {
        data: sankeyData,
        selectedNodes,
        onSelectionChange: setSelectedNodes
      }
    ),
    /* @__PURE__ */ jsx46("div", { className: "pt-4 pb-3", children: /* @__PURE__ */ jsx46("p", { className: "text-xs text-tertiary leading-relaxed font-sans", children: "Click nodes to highlight wealth flows. Select across columns left-to-right to progressively filter paths. Or just hit the play button and watch how money moves." }) }),
    /* @__PURE__ */ jsx46("div", { className: "pb-2", children: /* @__PURE__ */ jsxs36("p", { className: "text-caption-xs text-tertiary font-sans", children: [
      /* @__PURE__ */ jsxs36("strong", { children: [
        "THIS IS NOT INVESTMENT ADVICE. 2025 Aaim, Inc. and ReferenceModel",
        /* @__PURE__ */ jsx46("sup", { className: "text-[0.7em]", children: "TM" }),
        " by Aaim."
      ] }),
      ` Projections synthesize Federal Reserve Z.1 Financial Accounts establishing Q4 2020 baseline household wealth at $130.2 trillion (March 11, 2021 release, Table B.101) with Cerulli Associates' validated $124 trillion intergenerational transfer through 2048 (December 5, 2024 report). Alternative asset baseline derives from Preqin's authoritative $10.31 trillion year-end 2019 figure ("Alternatives in 2020," Q1 2020, page 4) with growth trajectories informed by McKinsey Global Private Markets Reviews (2020-2025), Bain's projection of $60-65 trillion private markets by 2032 (August 21, 2024), and Preqin's $32 trillion 2030 forecast (October 16, 2025). Generational allocation differentials validated through Bank of America Private Bank's 31% alternative allocation for younger investors versus 6% for older cohorts (2022 Study of Wealthy Americans), corroborated by Goldman Sachs Asset Management (October 2024) and zerohash research (November 20, 2025). Cryptocurrency baseline anchored to CoinGecko's official $732 billion December 31, 2020 market capitalization with conservative 20% household penetration assumption. Conservative scenario applies 5.5% wealth CAGR versus historical 6-8% range, 34.3% alternative asset penetration by 2050 versus industry outliers suggesting 52%, maintaining methodological alignment with Federal Reserve Distributional Financial Accounts methodology while incorporating Feiveson-Sabelhaus wealth concentration dynamics (FEDS Notes, June 1, 2018).`
    ] }) })
  ] });
}

// src/charts/RegulatoryTimeline.tsx
import { jsx as jsx47, jsxs as jsxs37 } from "react/jsx-runtime";
function RegulatoryTimeline({ events, className = "" }) {
  if (!events || events.length === 0) return null;
  return /* @__PURE__ */ jsxs37("div", { className: `${className}`, children: [
    /* @__PURE__ */ jsx47("div", { className: "hidden md:block", children: /* @__PURE__ */ jsxs37("div", { className: "relative", children: [
      /* @__PURE__ */ jsx47("div", { className: "absolute top-6 left-0 right-0 h-px bg-accent/30" }),
      /* @__PURE__ */ jsx47("div", { className: "grid gap-6", style: { gridTemplateColumns: `repeat(${events.length}, 1fr)` }, children: events.map((event, idx) => /* @__PURE__ */ jsxs37("div", { className: "relative pt-10", children: [
        /* @__PURE__ */ jsx47("div", { className: "absolute top-[14px] left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-canvas border-2 border-accent flex items-center justify-center", children: /* @__PURE__ */ jsx47("div", { className: "w-2 h-2 rounded-full bg-accent" }) }),
        /* @__PURE__ */ jsx47("div", { className: "text-xs font-sans font-semibold text-accent uppercase tracking-wider mb-3 text-center", children: event.year }),
        /* @__PURE__ */ jsxs37("div", { className: "bg-surface rounded-sm p-4 shadow-sm h-full", children: [
          /* @__PURE__ */ jsx47("h4", { className: "text-sm font-sans font-semibold text-primary mb-2 leading-snug", children: event.title }),
          /* @__PURE__ */ jsx47("p", { className: "text-xs font-serif text-secondary leading-relaxed", children: event.description }),
          event.source && /* @__PURE__ */ jsx47("p", { className: "text-caption-xs font-sans text-tertiary mt-2 opacity-70", children: event.source })
        ] })
      ] }, idx)) })
    ] }) }),
    /* @__PURE__ */ jsx47("div", { className: "md:hidden", children: /* @__PURE__ */ jsxs37("div", { className: "relative pl-8", children: [
      /* @__PURE__ */ jsx47("div", { className: "absolute top-0 bottom-0 left-[7px] w-px bg-accent/30" }),
      /* @__PURE__ */ jsx47("div", { className: "space-y-6", children: events.map((event, idx) => /* @__PURE__ */ jsxs37("div", { className: "relative", children: [
        /* @__PURE__ */ jsx47("div", { className: "absolute -left-8 top-4 w-4 h-4 rounded-full bg-canvas border-2 border-accent flex items-center justify-center", children: /* @__PURE__ */ jsx47("div", { className: "w-1.5 h-1.5 rounded-full bg-accent" }) }),
        /* @__PURE__ */ jsxs37("div", { className: "bg-surface rounded-sm p-4 shadow-sm", children: [
          /* @__PURE__ */ jsx47("div", { className: "text-xs font-sans font-semibold text-accent uppercase tracking-wider mb-1", children: event.year }),
          /* @__PURE__ */ jsx47("h4", { className: "text-sm font-sans font-semibold text-primary mb-2 leading-snug", children: event.title }),
          /* @__PURE__ */ jsx47("p", { className: "text-xs font-serif text-secondary leading-relaxed", children: event.description }),
          event.source && /* @__PURE__ */ jsx47("p", { className: "text-caption-xs font-sans text-tertiary mt-2 opacity-70", children: event.source })
        ] })
      ] }, idx)) })
    ] }) })
  ] });
}

// src/charts/BulletGraph.tsx
import { jsx as jsx48, jsxs as jsxs38 } from "react/jsx-runtime";
function BulletGraph({
  value,
  target,
  max,
  ranges = {
    poor: 0.33,
    satisfactory: 0.67,
    good: 1
  },
  label,
  unit = "",
  compact = false
}) {
  const valuePercent = value / max * 100;
  const targetPercent = target / max * 100;
  return /* @__PURE__ */ jsxs38("div", { className: compact ? "space-y-1" : "space-y-2", children: [
    label && /* @__PURE__ */ jsx48("div", { className: "text-xs font-sans text-tertiary", children: label }),
    /* @__PURE__ */ jsxs38("div", { className: "relative", children: [
      /* @__PURE__ */ jsxs38("div", { className: "relative h-6 flex rounded-sm overflow-hidden", children: [
        /* @__PURE__ */ jsx48("div", { className: "bg-error/10", style: {
          width: `${ranges.poor * 100}%`
        } }),
        /* @__PURE__ */ jsx48("div", { className: "bg-warning/10", style: {
          width: `${(ranges.satisfactory - ranges.poor) * 100}%`
        } }),
        /* @__PURE__ */ jsx48("div", { className: "bg-growth/10", style: {
          width: `${(ranges.good - ranges.satisfactory) * 100}%`
        } })
      ] }),
      /* @__PURE__ */ jsx48("div", { className: "absolute top-1/2 left-0 h-3 bg-primary -translate-y-1/2 rounded-sm", style: {
        width: `${Math.min(valuePercent, 100)}%`
      } }),
      /* @__PURE__ */ jsx48("div", { className: "absolute top-0 bottom-0 w-0.5 bg-accent", style: {
        left: `${Math.min(targetPercent, 100)}%`
      }, children: /* @__PURE__ */ jsx48("div", { className: "absolute top-1/2 left-1/2 w-2 h-2 bg-accent -translate-x-1/2 -translate-y-1/2 rotate-45" }) })
    ] }),
    !compact && /* @__PURE__ */ jsxs38("div", { className: "flex items-center justify-between text-xs font-mono tabular-nums", children: [
      /* @__PURE__ */ jsxs38("span", { className: "text-primary font-semibold", children: [
        value.toFixed(1),
        unit
      ] }),
      /* @__PURE__ */ jsxs38("span", { className: "text-tertiary", children: [
        "Target: ",
        target.toFixed(1),
        unit
      ] })
    ] })
  ] });
}

// src/charts/RiskGauge.tsx
import { jsx as jsx49, jsxs as jsxs39 } from "react/jsx-runtime";
function RiskGauge({
  currentScore = 2.4,
  previousScore = 2.7,
  targetScore = 3,
  maxScore = 5,
  title = "Portfolio risk score",
  subtitle = "Risk Assessment",
  trendData = [2.8, 2.9, 2.7, 2.6, 2.5, 2.4, 2.3, 2.4],
  inverted = false
}) {
  const change = currentScore - previousScore;
  const hasTrend = trendData.length > 0;
  const minTrend = hasTrend ? Math.min(...trendData) : 0;
  const maxTrend = hasTrend ? Math.max(...trendData) : 0;
  const domainLoRaw = hasTrend ? Math.min(minTrend, targetScore) : targetScore - 0.5;
  const domainHiRaw = hasTrend ? Math.max(maxTrend, targetScore) : targetScore + 0.5;
  const domainPad = Math.max(0.1, (domainHiRaw - domainLoRaw) * 0.15);
  const domainLo = domainLoRaw - domainPad;
  const domainHi = domainHiRaw + domainPad;
  const domainSpan = domainHi - domainLo;
  const trendY = (value) => 48 - (value - domainLo) / domainSpan * 48;
  const getColorForScore = (score) => {
    const position = score / maxScore;
    if (inverted) {
      if (position <= 0.33) return "rgb(var(--color-error-bright))";
      if (position <= 0.67) return "rgb(var(--color-warning-bright))";
      return "rgb(var(--color-success))";
    }
    if (position <= 0.33) return "rgb(var(--color-success))";
    if (position <= 0.67) return "rgb(var(--color-warning-bright))";
    return "rgb(var(--color-error-bright))";
  };
  const dotColor = getColorForScore(currentScore);
  const getBandForScore = (score) => {
    const position = score / maxScore;
    if (inverted) {
      if (position <= 0.33) return { label: "Critical", className: "text-error-bright" };
      if (position <= 0.67) return { label: "Warning", className: "text-warning-amber" };
      return { label: "Healthy", className: "text-success" };
    }
    if (position <= 0.33) return { label: "Low risk", className: "text-success" };
    if (position <= 0.67) return { label: "Medium risk", className: "text-warning-amber" };
    return { label: "High risk", className: "text-error-bright" };
  };
  const band = getBandForScore(currentScore);
  return /* @__PURE__ */ jsxs39("div", { className: "bg-surface border border-recessed p-8 rounded-sm shadow-sm", children: [
    /* @__PURE__ */ jsxs39("div", { className: "mb-6", children: [
      /* @__PURE__ */ jsx49("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-2 leading-normal", children: subtitle }),
      /* @__PURE__ */ jsx49("h3", { className: "text-heading-section font-sans font-semibold text-primary leading-normal", children: title })
    ] }),
    /* @__PURE__ */ jsxs39("div", { className: "mb-6", children: [
      /* @__PURE__ */ jsxs39("div", { className: "flex items-baseline gap-2 mb-1", children: [
        /* @__PURE__ */ jsx49("span", { className: "text-stat-lg font-serif font-semibold text-primary tracking-headline-serif tabular-nums leading-none", children: currentScore.toFixed(1) }),
        /* @__PURE__ */ jsxs39("span", { className: "text-base font-sans text-secondary", children: [
          "of ",
          maxScore.toFixed(1)
        ] })
      ] }),
      /* @__PURE__ */ jsx49("div", { className: `text-sm font-sans font-semibold ${band.className}`, children: band.label })
    ] }),
    /* @__PURE__ */ jsx49("div", { className: "mb-8 text-sm font-mono text-tertiary tabular-nums", children: previousScore > 0 ? `${change.toFixed(1)} (${(change / previousScore * 100).toFixed(1)}%) vs. previous quarter` : "-- vs. previous quarter (no prior data)" }),
    /* @__PURE__ */ jsxs39("div", { className: "mb-8", children: [
      /* @__PURE__ */ jsx49("div", { className: `relative h-2 ${inverted ? "bg-gradient-to-r from-error-bright/15 via-warning-amber/15 to-success-bright/15" : "bg-gradient-to-r from-success-bright/15 via-warning-amber/15 to-error-bright/15"} rounded-full`, children: /* @__PURE__ */ jsx49("div", { className: "absolute top-1/2 w-6 h-6 rounded-full shadow-md", style: {
        left: `${currentScore / maxScore * 100}%`,
        transform: "translate(-50%, -50%)",
        backgroundColor: dotColor
      } }) }),
      /* @__PURE__ */ jsx49("div", { className: "flex justify-between mt-2 text-xs font-mono text-tertiary tabular-nums", children: Array.from({ length: 6 }, (_, i) => maxScore / 5 * i).map((tick, i) => /* @__PURE__ */ jsx49("span", { children: tick % 1 === 0 ? String(tick) : tick.toFixed(1) }, i)) }),
      /* @__PURE__ */ jsxs39("div", { className: "flex justify-between mt-1 text-caption-xs font-sans text-tertiary", children: [
        /* @__PURE__ */ jsx49("span", { children: inverted ? "Critical" : "Low risk" }),
        /* @__PURE__ */ jsx49("span", { children: inverted ? "Warning" : "Medium" }),
        /* @__PURE__ */ jsx49("span", { children: inverted ? "Healthy" : "High risk" })
      ] }),
      /* @__PURE__ */ jsxs39("div", { className: "text-center mt-3 text-xs font-sans text-tertiary", children: [
        "Target \u2264",
        targetScore.toFixed(1)
      ] })
    ] }),
    /* @__PURE__ */ jsxs39("div", { className: "mb-6 pb-6 border-b border-recessed", children: [
      /* @__PURE__ */ jsxs39("div", { className: "flex items-center justify-between mb-3", children: [
        /* @__PURE__ */ jsx49("span", { className: "text-xs font-sans text-tertiary", children: "8-quarter trend" }),
        /* @__PURE__ */ jsxs39("span", { className: "text-xs font-mono text-tertiary tabular-nums", children: [
          minTrend.toFixed(1),
          "\u2013",
          maxTrend.toFixed(1)
        ] })
      ] }),
      /* @__PURE__ */ jsxs39("div", { className: "relative", children: [
        /* @__PURE__ */ jsxs39("div", { className: "absolute left-0 top-0 bottom-0 flex flex-col justify-between text-caption-xs font-mono text-tertiary tabular-nums pr-2", children: [
          /* @__PURE__ */ jsx49("span", { children: domainHi.toFixed(1) }),
          /* @__PURE__ */ jsx49("span", { children: ((domainHi + domainLo) / 2).toFixed(1) }),
          /* @__PURE__ */ jsx49("span", { children: domainLo.toFixed(1) })
        ] }),
        /* @__PURE__ */ jsx49("div", { className: "ml-8", children: /* @__PURE__ */ jsxs39("svg", { width: "100%", height: "48", viewBox: "0 0 100 48", preserveAspectRatio: "none", className: "w-full", children: [
          /* @__PURE__ */ jsx49("line", { x1: "0", y1: "0", x2: "100", y2: "0", stroke: "rgb(var(--color-recessed))", strokeWidth: "0.3", opacity: "0.3", vectorEffect: "non-scaling-stroke" }),
          /* @__PURE__ */ jsx49("line", { x1: "0", y1: "24", x2: "100", y2: "24", stroke: "rgb(var(--color-recessed))", strokeWidth: "0.3", opacity: "0.3", vectorEffect: "non-scaling-stroke" }),
          /* @__PURE__ */ jsx49("line", { x1: "0", y1: "48", x2: "100", y2: "48", stroke: "rgb(var(--color-recessed))", strokeWidth: "0.3", opacity: "0.3", vectorEffect: "non-scaling-stroke" }),
          /* @__PURE__ */ jsx49("line", { x1: "0", y1: trendY(targetScore), x2: "100", y2: trendY(targetScore), stroke: "rgb(var(--color-recessed))", strokeWidth: "0.5", strokeDasharray: "2,2", opacity: "0.4", vectorEffect: "non-scaling-stroke" }),
          trendData.length > 1 && /* @__PURE__ */ jsx49("polyline", { points: trendData.map((value, index) => {
            const x = index / Math.max(1, trendData.length - 1) * 100;
            return `${x},${trendY(value)}`;
          }).join(" "), fill: "none", stroke: "rgb(var(--color-text-primary))", strokeWidth: "0.5", vectorEffect: "non-scaling-stroke" }),
          trendData.length > 0 && /* @__PURE__ */ jsx49("circle", { cx: 100, cy: trendY(trendData[trendData.length - 1]), r: "1", fill: "rgb(var(--color-text-primary))", vectorEffect: "non-scaling-stroke" })
        ] }) })
      ] })
    ] }),
    /* @__PURE__ */ jsxs39("div", { className: "space-y-2", children: [
      /* @__PURE__ */ jsxs39("div", { className: "flex items-center justify-between text-sm", children: [
        /* @__PURE__ */ jsx49("span", { className: "font-sans text-tertiary", children: "Previous quarter" }),
        /* @__PURE__ */ jsx49("span", { className: "font-mono text-primary tabular-nums", children: previousScore.toFixed(1) })
      ] }),
      /* @__PURE__ */ jsxs39("div", { className: "flex items-center justify-between text-sm", children: [
        /* @__PURE__ */ jsx49("span", { className: "font-sans text-tertiary", children: "Target threshold" }),
        /* @__PURE__ */ jsxs39("span", { className: "font-mono text-primary tabular-nums", children: [
          "\u2264 ",
          targetScore.toFixed(1)
        ] })
      ] }),
      /* @__PURE__ */ jsxs39("div", { className: "flex items-center justify-between text-sm", children: [
        /* @__PURE__ */ jsx49("span", { className: "font-sans text-tertiary", children: "8-quarter range" }),
        /* @__PURE__ */ jsxs39("span", { className: "font-mono text-primary tabular-nums", children: [
          minTrend.toFixed(1),
          "\u2013",
          maxTrend.toFixed(1)
        ] })
      ] })
    ] })
  ] });
}

// src/metrics/metricLabel.ts
var METRIC_LABEL_CLASSES = "text-xs font-sans font-medium uppercase tracking-wider text-tertiary";

// src/charts/MetricGauge.tsx
import { jsx as jsx50, jsxs as jsxs40 } from "react/jsx-runtime";
function MetricGauge({
  title,
  value,
  max,
  unit = "%",
  thresholds,
  inverted = false
}) {
  const percentage = value / max * 100;
  const trimmedUnit = unit.trim();
  const showShare = trimmedUnit !== "" && !(max === 100 && trimmedUnit === "%");
  const getColorClass = () => {
    if (!thresholds) return "bg-neutral-data";
    if (inverted) {
      if (percentage <= thresholds.critical) return "bg-error";
      if (percentage <= thresholds.warning) return "bg-warning";
      return "bg-growth";
    }
    if (percentage >= thresholds.critical) return "bg-error";
    if (percentage >= thresholds.warning) return "bg-warning";
    return "bg-growth";
  };
  const getTextColorClass = (neutral) => {
    if (!thresholds) return neutral;
    if (inverted) {
      if (percentage <= thresholds.critical) return "text-error";
      if (percentage <= thresholds.warning) return "text-warning";
      return "text-growth";
    }
    if (percentage >= thresholds.critical) return "text-error";
    if (percentage >= thresholds.warning) return "text-warning";
    return "text-growth";
  };
  const getStatusText = () => {
    if (!thresholds) return null;
    if (inverted) {
      if (percentage <= thresholds.critical) return "CRITICAL";
      if (percentage <= thresholds.warning) return "WARNING";
      return "HEALTHY";
    }
    if (percentage >= thresholds.critical) return "CRITICAL";
    if (percentage >= thresholds.warning) return "WARNING";
    return "HEALTHY";
  };
  const statusText = getStatusText();
  return (
    // h-full keeps the tile the height of its grid cell where a page wraps
    // the gauge in a column with a trend chart under it.
    /* @__PURE__ */ jsxs40(Card, { className: "h-full", children: [
      /* @__PURE__ */ jsx50("div", { className: `${METRIC_LABEL_CLASSES} mb-1`, children: title }),
      /* @__PURE__ */ jsxs40("div", { className: "flex items-baseline mb-4", children: [
        /* @__PURE__ */ jsx50(
          "span",
          {
            className: `text-2xl font-serif font-semibold ${getTextColorClass("text-primary")} tracking-headline-serif tabular-nums leading-none mr-2`,
            children: value.toFixed(1)
          }
        ),
        /* @__PURE__ */ jsxs40("span", { className: "text-lg font-sans text-tertiary", children: [
          "/ ",
          max,
          unit
        ] })
      ] }),
      /* @__PURE__ */ jsx50(
        "div",
        {
          role: "meter",
          "aria-label": title,
          "aria-valuenow": Math.min(Math.max(value, 0), max),
          "aria-valuemin": 0,
          "aria-valuemax": max,
          "aria-valuetext": `${value.toFixed(1)} / ${max}${unit}${statusText !== null ? `, ${statusText.toLowerCase()}` : ""}`,
          className: "w-full bg-recessed rounded-full h-2.5 mb-3",
          children: /* @__PURE__ */ jsx50(
            "div",
            {
              className: `${getColorClass()} h-2.5 rounded-full transition-all duration-300`,
              style: { width: `${Math.min(percentage, 100)}%` }
            }
          )
        }
      ),
      (showShare || statusText !== null) && /* @__PURE__ */ jsxs40("div", { className: "flex justify-between items-center", children: [
        showShare ? /* @__PURE__ */ jsxs40("span", { className: "text-sm font-mono text-tertiary tabular-nums leading-normal", children: [
          percentage.toFixed(1),
          "%"
        ] }) : /* @__PURE__ */ jsx50("span", { "aria-hidden": "true" }),
        statusText !== null && /* @__PURE__ */ jsx50("span", { className: `text-label-xs font-sans font-semibold uppercase leading-normal ${getTextColorClass("text-tertiary")}`, children: statusText })
      ] })
    ] })
  );
}

// src/charts/RiskHeatMap.tsx
import { Fragment as Fragment11 } from "react";
import { jsx as jsx51, jsxs as jsxs41 } from "react/jsx-runtime";
function RiskHeatMap() {
  const risks = [{
    category: "Market",
    probability: "High",
    impact: "High",
    color: "bg-error"
  }, {
    category: "Credit",
    probability: "Medium",
    impact: "High",
    color: "bg-warning"
  }, {
    category: "Operational",
    probability: "Medium",
    impact: "Medium",
    color: "bg-warning"
  }, {
    category: "Liquidity",
    probability: "Low",
    impact: "High",
    color: "bg-warning"
  }, {
    category: "Compliance",
    probability: "Low",
    impact: "Medium",
    color: "bg-growth"
  }];
  const impactLevels = ["Low", "Medium", "High"];
  const probabilityLevels = ["Low", "Medium", "High"];
  return /* @__PURE__ */ jsxs41("div", { className: "bg-surface border border-recessed p-8 rounded-sm shadow-sm", children: [
    /* @__PURE__ */ jsxs41("div", { className: "mb-8", children: [
      /* @__PURE__ */ jsx51("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-2 leading-normal", children: "Risk Assessment" }),
      /* @__PURE__ */ jsx51("h3", { className: "text-heading-section font-sans font-semibold text-primary leading-normal", children: "Risk heat map" })
    ] }),
    /* @__PURE__ */ jsxs41("div", { className: "mb-6", children: [
      /* @__PURE__ */ jsxs41("div", { className: "grid grid-cols-4 gap-2", children: [
        /* @__PURE__ */ jsx51("div", {}),
        impactLevels.map((level) => /* @__PURE__ */ jsx51("div", { className: "text-xs font-sans font-semibold text-tertiary text-center", children: level }, level)),
        probabilityLevels.reverse().map((probLevel) => /* @__PURE__ */ jsxs41(Fragment11, { children: [
          /* @__PURE__ */ jsx51("div", { className: "text-xs font-sans font-semibold text-tertiary flex items-center", children: probLevel }),
          impactLevels.map((impLevel) => {
            const risk = risks.find((r) => r.probability === probLevel && r.impact === impLevel);
            return /* @__PURE__ */ jsx51("div", { className: `aspect-square flex items-center justify-center text-xs font-sans font-bold text-white ${risk ? risk.color : "bg-elevated"}`, children: risk && risk.category.slice(0, 3).toUpperCase() }, `${probLevel}-${impLevel}`);
          })
        ] }, probLevel))
      ] }),
      /* @__PURE__ */ jsx51("div", { className: "text-center text-xs font-sans text-tertiary mt-2", children: "Impact \u2192" })
    ] }),
    /* @__PURE__ */ jsx51("div", { className: "space-y-2", children: risks.map((risk, index) => /* @__PURE__ */ jsxs41("div", { className: "flex items-center gap-3 text-sm font-sans", children: [
      /* @__PURE__ */ jsx51("div", { className: `w-3 h-3 ${risk.color}` }),
      /* @__PURE__ */ jsx51("span", { className: "font-medium text-primary", children: risk.category }),
      /* @__PURE__ */ jsxs41("span", { className: "text-tertiary", children: [
        risk.probability,
        " probability, ",
        risk.impact,
        " impact"
      ] })
    ] }, index)) })
  ] });
}

// src/charts/SmallMultiplesGrid.tsx
import { jsx as jsx52, jsxs as jsxs42 } from "react/jsx-runtime";
function SmallMultiplesGrid({
  multiples,
  columns = 3,
  valueFont = "serif"
}) {
  const allValues = multiples.flatMap((m) => m.data.map((d) => d.value));
  const globalMin = Math.min(...allValues);
  const globalMax = Math.max(...allValues);
  const globalRange = globalMax - globalMin || 1;
  const valueFontClass = {
    mono: "font-mono tabular-nums",
    sans: "font-sans tabular-nums",
    serif: "font-serif oldstyle-nums"
  }[valueFont];
  const formatValue = (value) => {
    if (Number.isInteger(value)) {
      return value.toString();
    }
    return value.toFixed(1);
  };
  const getStatusColor = (status) => {
    switch (status) {
      case "positive":
        return "rgb(var(--color-success))";
      case "negative":
        return "rgb(var(--color-error-bright))";
      case "warning":
        return "rgb(var(--color-warning-bright))";
      default:
        return "rgb(var(--color-neutral-data))";
    }
  };
  return /* @__PURE__ */ jsx52("div", { className: "grid gap-6", style: {
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`
  }, children: multiples.map((multiple, index) => {
    const color = multiple.color || getStatusColor(multiple.status);
    const lastY = 100 - (multiple.data[multiple.data.length - 1].value - globalMin) / globalRange * 100;
    return /* @__PURE__ */ jsxs42("div", { className: "bg-surface p-4 rounded-sm border border-recessed hover:border-secondary/30 transition-colors", children: [
      /* @__PURE__ */ jsxs42("div", { className: "flex items-baseline justify-between mb-3", children: [
        /* @__PURE__ */ jsx52("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary", children: multiple.title }),
        multiple.currentValue !== void 0 && /* @__PURE__ */ jsxs42("div", { className: "text-right", children: [
          /* @__PURE__ */ jsx52("span", { className: `text-xl font-semibold text-primary tracking-[-0.01em] ${valueFontClass}`, children: formatValue(multiple.currentValue) }),
          multiple.unit && /* @__PURE__ */ jsx52("span", { className: "text-xs font-sans font-medium text-tertiary tracking-normal ml-0.5 align-baseline", children: multiple.unit })
        ] })
      ] }),
      /* @__PURE__ */ jsxs42("div", { className: "relative h-24", children: [
        [0, 1, 2, 3].map((i) => /* @__PURE__ */ jsx52("div", { className: "absolute w-full border-t border-recessed/40", style: {
          top: `${i / 3 * 100}%`
        } }, i)),
        /* @__PURE__ */ jsxs42("svg", { width: "100%", height: "100%", viewBox: "0 0 100 100", preserveAspectRatio: "none", className: "overflow-visible", children: [
          /* @__PURE__ */ jsx52("defs", { children: /* @__PURE__ */ jsxs42("linearGradient", { id: `gradient-${index}`, x1: "0%", y1: "0%", x2: "0%", y2: "100%", children: [
            /* @__PURE__ */ jsx52("stop", { offset: "0%", stopColor: color, stopOpacity: "0.2" }),
            /* @__PURE__ */ jsx52("stop", { offset: "100%", stopColor: color, stopOpacity: "0.05" })
          ] }) }),
          /* @__PURE__ */ jsx52("path", { d: (() => {
            const points = multiple.data.map((d, i) => {
              const x = i / (multiple.data.length - 1) * 100;
              const y = 100 - (d.value - globalMin) / globalRange * 100;
              return `${x},${y}`;
            }).join(" ");
            return `M 0,100 L ${points} L 100,100 Z`;
          })(), fill: `url(#gradient-${index})` }),
          /* @__PURE__ */ jsx52("polyline", { points: multiple.data.map((d, i) => {
            const x = i / (multiple.data.length - 1) * 100;
            const y = 100 - (d.value - globalMin) / globalRange * 100;
            return `${x},${y}`;
          }).join(" "), fill: "none", stroke: color, strokeWidth: "0.5", vectorEffect: "non-scaling-stroke" })
        ] }),
        /* @__PURE__ */ jsx52(
          "div",
          {
            className: "absolute w-2 h-2 rounded-full pointer-events-none",
            style: {
              backgroundColor: color,
              right: 0,
              top: `${lastY}%`,
              transform: "translate(50%, -50%)"
            }
          }
        )
      ] }),
      multiple.data.length > 0 && multiple.data[0].label && multiple.data[multiple.data.length - 1].label && /* @__PURE__ */ jsxs42("div", { className: "flex justify-between text-caption-xs font-sans text-tertiary/70 mt-2", children: [
        /* @__PURE__ */ jsx52("span", { children: multiple.data[0].label }),
        /* @__PURE__ */ jsx52("span", { children: multiple.data[multiple.data.length - 1].label })
      ] })
    ] }, index);
  }) });
}

// src/metrics/MetricLedger.tsx
import { jsx as jsx53, jsxs as jsxs43 } from "react/jsx-runtime";
function MetricLedger({
  items,
  title,
  className = "",
  compact = false,
  showLeaders = true,
  valueFont = "mono"
}) {
  const getChangeColor = (type) => {
    switch (type) {
      case "positive":
        return "text-growth";
      case "negative":
        return "text-error";
      default:
        return "text-tertiary";
    }
  };
  const valueFontClass = {
    mono: "font-mono tabular-nums",
    sans: "font-sans",
    serif: "font-serif"
  }[valueFont];
  return /* @__PURE__ */ jsxs43("div", { className: `${className}`, children: [
    title && /* @__PURE__ */ jsx53("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-3 pb-2 border-b border-recessed", children: title }),
    /* @__PURE__ */ jsx53("div", { className: compact ? "space-y-1" : "space-y-2", children: items.map((item, index) => /* @__PURE__ */ jsxs43(
      "div",
      {
        className: `flex items-baseline gap-2 ${compact ? "py-0.5" : "py-1"}`,
        children: [
          /* @__PURE__ */ jsx53("span", { className: "text-sm font-sans text-secondary flex-shrink-0", children: item.label }),
          showLeaders && /* @__PURE__ */ jsx53(
            "span",
            {
              className: "flex-1 border-b border-dotted border-recessed",
              style: { marginBottom: "0.3em" }
            }
          ),
          !showLeaders && /* @__PURE__ */ jsx53("span", { className: "flex-1" }),
          /* @__PURE__ */ jsx53("span", { className: `text-sm font-medium text-primary flex-shrink-0 ${valueFontClass}`, children: item.value }),
          item.change && /* @__PURE__ */ jsxs43(
            "span",
            {
              className: `text-xs flex-shrink-0 ${valueFontClass} ${getChangeColor(item.changeType)}`,
              children: [
                "(",
                item.change,
                ")"
              ]
            }
          )
        ]
      },
      index
    )) })
  ] });
}
function MetricLedgerBlock({
  items,
  title,
  className = "",
  valueFont = "mono"
}) {
  const getChangeColor = (type) => {
    switch (type) {
      case "positive":
        return "text-growth";
      case "negative":
        return "text-error";
      default:
        return "text-tertiary";
    }
  };
  const valueFontClass = {
    mono: "font-mono tabular-nums",
    sans: "font-sans",
    serif: "font-serif"
  }[valueFont];
  return /* @__PURE__ */ jsxs43("div", { className: `${className}`, children: [
    title && /* @__PURE__ */ jsx53("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-3", children: title }),
    /* @__PURE__ */ jsx53("div", { className: "border-t-2 border-primary", children: items.map((item, index) => {
      const isTotal = item.isTotal || item.isSubtotal;
      const indent = item.indent || 0;
      return /* @__PURE__ */ jsxs43(
        "div",
        {
          className: `flex items-baseline gap-2 py-1.5 ${item.isTotal ? "border-t-2 border-b-2 border-primary font-semibold" : item.isSubtotal ? "border-t border-recessed font-medium" : "border-b border-dotted border-recessed"}`,
          style: { paddingLeft: `${indent * 16}px` },
          children: [
            /* @__PURE__ */ jsx53(
              "span",
              {
                className: `text-sm font-sans flex-shrink-0 ${isTotal ? "text-primary" : "text-secondary"}`,
                children: item.label
              }
            ),
            /* @__PURE__ */ jsx53("span", { className: "flex-1" }),
            /* @__PURE__ */ jsx53(
              "span",
              {
                className: `text-sm flex-shrink-0 ${valueFontClass} ${isTotal ? "text-primary font-semibold" : "text-primary"}`,
                children: item.value
              }
            ),
            item.change && /* @__PURE__ */ jsx53(
              "span",
              {
                className: `text-xs flex-shrink-0 ${valueFontClass} ${getChangeColor(item.changeType)}`,
                children: item.change
              }
            )
          ]
        },
        index
      );
    }) })
  ] });
}

// src/metrics/MetricStrip.tsx
import React15 from "react";
import { jsx as jsx54, jsxs as jsxs44 } from "react/jsx-runtime";
function MetricStrip({
  items,
  className = "",
  variant = "default",
  separator = "pipe",
  wrap = true,
  valueFont = "mono"
}) {
  const getChangeColor = (type) => {
    switch (type) {
      case "positive":
        return "text-growth";
      case "negative":
        return "text-error";
      default:
        return "text-tertiary";
    }
  };
  const getSeparator = () => {
    switch (separator) {
      case "pipe":
        return /* @__PURE__ */ jsx54("span", { className: "text-recessed mx-3", children: "|" });
      case "dot":
        return /* @__PURE__ */ jsx54("span", { className: "text-recessed mx-3", children: "\xB7" });
      case "slash":
        return /* @__PURE__ */ jsx54("span", { className: "text-recessed mx-3", children: "/" });
      default:
        return /* @__PURE__ */ jsx54("span", { className: "mx-3" });
    }
  };
  const valueFontClass = {
    mono: "font-mono tabular-nums",
    sans: "font-sans",
    serif: "font-serif"
  }[valueFont];
  const getVariantStyles = () => {
    switch (variant) {
      case "terminal":
        return {
          container: "bg-primary text-white px-4 py-2",
          label: "text-white/60 uppercase text-xs tracking-wider",
          value: `text-white ${valueFontClass} font-medium`,
          change: (type) => type === "positive" ? "text-growth" : type === "negative" ? "text-error" : "text-white/50"
        };
      case "minimal":
        return {
          container: "py-2",
          label: "text-tertiary text-xs",
          value: `text-primary ${valueFontClass} font-medium`,
          change: getChangeColor
        };
      default:
        return {
          container: "bg-surface border border-recessed px-4 py-2 rounded-sm",
          label: "text-tertiary uppercase text-xs tracking-wider",
          value: `text-primary ${valueFontClass} font-semibold`,
          change: getChangeColor
        };
    }
  };
  const styles = getVariantStyles();
  return /* @__PURE__ */ jsx54(
    "div",
    {
      className: `${styles.container} ${wrap ? "flex flex-wrap" : "flex overflow-x-auto"} items-center ${className}`,
      children: items.map((item, index) => /* @__PURE__ */ jsxs44(React15.Fragment, { children: [
        index > 0 && getSeparator(),
        /* @__PURE__ */ jsxs44("div", { className: "flex items-baseline gap-2 flex-shrink-0", children: [
          /* @__PURE__ */ jsx54("span", { className: styles.label, children: item.label }),
          /* @__PURE__ */ jsx54("span", { className: styles.value, children: item.value }),
          item.change && /* @__PURE__ */ jsx54(
            "span",
            {
              className: `text-xs ${valueFontClass} ${typeof styles.change === "function" ? styles.change(item.changeType) : styles.change}`,
              children: item.change
            }
          )
        ] })
      ] }, index))
    }
  );
}
function MetricQuote({
  label,
  value,
  change,
  changeType = "neutral",
  sparkline,
  className = "",
  valueFont = "mono"
}) {
  const getChangeColor = () => {
    switch (changeType) {
      case "positive":
        return "text-growth";
      case "negative":
        return "text-error";
      default:
        return "text-tertiary";
    }
  };
  const valueFontClass = {
    mono: "font-mono tabular-nums",
    sans: "font-sans",
    serif: "font-serif"
  }[valueFont];
  const sparklinePath = sparkline && sparkline.length > 1 ? sparkline.map((v, i) => {
    const x = i / (sparkline.length - 1) * 60;
    const min = Math.min(...sparkline);
    const max = Math.max(...sparkline);
    const range = max - min || 1;
    const y = 20 - (v - min) / range * 20;
    return `${i === 0 ? "M" : "L"}${x},${y}`;
  }).join(" ") : null;
  return /* @__PURE__ */ jsxs44("div", { className: `flex items-center gap-4 ${className}`, children: [
    sparklinePath && /* @__PURE__ */ jsx54("svg", { width: "60", height: "24", className: "flex-shrink-0", children: /* @__PURE__ */ jsx54(
      "path",
      {
        d: sparklinePath,
        fill: "none",
        stroke: "currentColor",
        strokeWidth: "1.5",
        className: getChangeColor()
      }
    ) }),
    /* @__PURE__ */ jsxs44("div", { className: "flex items-baseline gap-3", children: [
      /* @__PURE__ */ jsx54("span", { className: "text-xs font-sans uppercase tracking-wider text-tertiary", children: label }),
      /* @__PURE__ */ jsx54("span", { className: `text-xl font-semibold text-primary ${valueFontClass}`, children: value }),
      change && /* @__PURE__ */ jsx54("span", { className: `text-sm ${valueFontClass} ${getChangeColor()}`, children: change })
    ] })
  ] });
}

// src/metrics/MetricGlance.tsx
import { jsx as jsx55, jsxs as jsxs45 } from "react/jsx-runtime";
function MetricGlance({
  items,
  title,
  className = "",
  columns = 2,
  showDividers = true,
  valueFont = "mono"
}) {
  const getChangeColor = (type) => {
    switch (type) {
      case "positive":
        return "text-growth";
      case "negative":
        return "text-error";
      default:
        return "text-tertiary";
    }
  };
  const valueFontClass = {
    mono: "font-mono tabular-nums",
    sans: "font-sans",
    serif: "font-serif"
  }[valueFont];
  const columnClasses = {
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-2 sm:grid-cols-4"
  };
  return /* @__PURE__ */ jsxs45("div", { className, children: [
    title && /* @__PURE__ */ jsx55("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-4 pb-2 border-b-2 border-primary", children: title }),
    /* @__PURE__ */ jsx55("div", { className: `grid ${columnClasses[columns]} gap-x-6 gap-y-0`, children: items.map((item, index) => /* @__PURE__ */ jsxs45(
      "div",
      {
        className: `py-3 ${showDividers ? "border-b border-recessed" : ""}`,
        children: [
          /* @__PURE__ */ jsxs45("div", { className: "flex items-baseline gap-2 mb-0.5", children: [
            /* @__PURE__ */ jsx55("span", { className: `text-lg font-semibold text-primary ${valueFontClass}`, children: item.value }),
            item.change && /* @__PURE__ */ jsx55(
              "span",
              {
                className: `text-xs ${valueFontClass} ${getChangeColor(item.changeType)}`,
                children: item.change
              }
            )
          ] }),
          /* @__PURE__ */ jsxs45("div", { className: "text-xs font-sans text-tertiary leading-tight", children: [
            item.label,
            item.period && /* @__PURE__ */ jsxs45("span", { className: "text-tertiary/60 ml-1", children: [
              "(",
              item.period,
              ")"
            ] })
          ] })
        ]
      },
      index
    )) })
  ] });
}
function MetricIndex({
  items,
  title,
  className = "",
  valueFont = "mono"
}) {
  const getStatusDot = (type) => {
    switch (type) {
      case "positive":
        return "bg-growth";
      case "negative":
        return "bg-error";
      default:
        return "bg-tertiary";
    }
  };
  const getChangeColor = (type) => {
    switch (type) {
      case "positive":
        return "text-growth";
      case "negative":
        return "text-error";
      default:
        return "text-tertiary";
    }
  };
  const valueFontClass = {
    mono: "font-mono tabular-nums",
    sans: "font-sans",
    serif: "font-serif"
  }[valueFont];
  return /* @__PURE__ */ jsxs45("div", { className, children: [
    title && /* @__PURE__ */ jsx55("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-3 pb-2 border-b border-recessed", children: title }),
    /* @__PURE__ */ jsx55("div", { className: "space-y-0", children: items.map((item, index) => /* @__PURE__ */ jsxs45(
      "div",
      {
        className: "flex items-center gap-3 py-2 border-b border-dotted border-recessed last:border-b-0",
        children: [
          /* @__PURE__ */ jsx55("div", { className: `w-1.5 h-1.5 rounded-full flex-shrink-0 ${getStatusDot(item.changeType)}` }),
          /* @__PURE__ */ jsx55("span", { className: "text-sm font-sans text-secondary flex-1 min-w-0 truncate", children: item.name }),
          /* @__PURE__ */ jsx55("span", { className: `text-sm font-medium text-primary flex-shrink-0 ${valueFontClass}`, children: item.value }),
          /* @__PURE__ */ jsx55(
            "span",
            {
              className: `text-xs w-16 text-right flex-shrink-0 ${valueFontClass} ${getChangeColor(item.changeType)}`,
              children: item.change
            }
          )
        ]
      },
      index
    )) }),
    items[0]?.timestamp && /* @__PURE__ */ jsxs45("div", { className: "text-xs font-sans text-tertiary/60 mt-2 pt-2 border-t border-recessed", children: [
      "As of ",
      items[0].timestamp
    ] })
  ] });
}
function MetricDelta({
  items,
  title,
  className = "",
  columns = 3,
  valueFont = "mono"
}) {
  const getChangeColor = (type) => {
    switch (type) {
      case "positive":
        return "text-growth";
      case "negative":
        return "text-error";
      default:
        return "text-secondary";
    }
  };
  const getArrow = (type) => {
    switch (type) {
      case "positive":
        return "\u2191";
      // ↑
      case "negative":
        return "\u2193";
      // ↓
      default:
        return "\u2192";
    }
  };
  const valueFontClass = {
    mono: "font-mono tabular-nums",
    sans: "font-sans",
    serif: "font-serif"
  }[valueFont];
  const columnClasses = {
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-2 sm:grid-cols-4"
  };
  return /* @__PURE__ */ jsxs45("div", { className, children: [
    title && /* @__PURE__ */ jsx55("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-4", children: title }),
    /* @__PURE__ */ jsx55("div", { className: `grid ${columnClasses[columns]} gap-6`, children: items.map((item, index) => /* @__PURE__ */ jsxs45("div", { children: [
      /* @__PURE__ */ jsxs45("div", { className: `text-xl font-semibold ${valueFontClass} ${getChangeColor(item.changeType)}`, children: [
        /* @__PURE__ */ jsx55("span", { className: "mr-1", children: getArrow(item.changeType) }),
        item.change
      ] }),
      /* @__PURE__ */ jsx55("div", { className: "text-xs font-sans text-tertiary mt-1", children: item.label }),
      (item.value || item.baseline) && /* @__PURE__ */ jsxs45("div", { "data-slot": "metric-delta-context", className: `text-xs text-tertiary mt-0.5 ${valueFontClass}`, children: [
        item.value && /* @__PURE__ */ jsx55("span", { children: item.value }),
        item.value && item.baseline && /* @__PURE__ */ jsx55("span", { children: " vs " }),
        item.baseline && /* @__PURE__ */ jsx55("span", { children: item.baseline })
      ] })
    ] }, index)) })
  ] });
}

// src/metrics/MetricRow.tsx
import { jsx as jsx56, jsxs as jsxs46 } from "react/jsx-runtime";
function MetricRow({
  label,
  value,
  unit = "",
  change,
  changeLabel,
  target,
  targetLabel,
  trend,
  status = "neutral",
  context,
  showBullet = false,
  bulletMax = 100
}) {
  const getStatusColor = () => {
    switch (status) {
      case "positive":
        return "text-growth";
      case "negative":
        return "text-error";
      case "warning":
        return "text-warning";
      case "critical":
        return "text-error";
      default:
        return "text-secondary";
    }
  };
  const getChangeColor = () => {
    if (!change) return "text-tertiary";
    return change > 0 ? "text-growth" : "text-error";
  };
  return /* @__PURE__ */ jsxs46("div", { className: "flex items-center gap-6 py-4 border-b border-recessed last:border-b-0 hover:bg-elevated/30 transition-colors", children: [
    /* @__PURE__ */ jsxs46("div", { className: "w-48 flex-shrink-0", children: [
      /* @__PURE__ */ jsx56("div", { className: "text-sm font-sans text-secondary", children: label }),
      context && /* @__PURE__ */ jsx56("div", { className: "text-xs font-sans text-tertiary mt-0.5", children: context })
    ] }),
    /* @__PURE__ */ jsx56("div", { className: "w-32 flex-shrink-0", children: /* @__PURE__ */ jsxs46("div", { className: `text-2xl font-serif font-semibold tracking-headline-serif tabular-nums ${getStatusColor()}`, children: [
      value,
      unit && /* @__PURE__ */ jsx56("span", { className: "text-base text-tertiary ml-1", children: unit })
    ] }) }),
    trend && trend.length > 0 && /* @__PURE__ */ jsx56("div", { className: "w-32 flex-shrink-0", children: /* @__PURE__ */ jsx56("svg", { width: "100%", height: "32", className: "overflow-visible", children: /* @__PURE__ */ jsx56("polyline", { points: trend.map((v, i) => {
      const x = i / (trend.length - 1) * 100;
      const minVal = Math.min(...trend);
      const maxVal = Math.max(...trend);
      const range = maxVal - minVal || 1;
      const y = 32 - (v - minVal) / range * 32;
      return `${x}%,${y}`;
    }).join(" "), fill: "none", stroke: "currentColor", strokeWidth: "1.5", className: getStatusColor(), vectorEffect: "non-scaling-stroke" }) }) }),
    showBullet && target !== void 0 && typeof value === "number" && /* @__PURE__ */ jsx56("div", { className: "flex-1 min-w-0", children: /* @__PURE__ */ jsxs46("div", { className: "relative h-4", children: [
      /* @__PURE__ */ jsxs46("div", { className: "absolute inset-0 flex rounded-sm overflow-hidden bg-recessed", children: [
        /* @__PURE__ */ jsx56("div", { className: "bg-error/10", style: {
          width: "33%"
        } }),
        /* @__PURE__ */ jsx56("div", { className: "bg-warning/10", style: {
          width: "34%"
        } }),
        /* @__PURE__ */ jsx56("div", { className: "bg-growth/10", style: {
          width: "33%"
        } })
      ] }),
      /* @__PURE__ */ jsx56("div", { className: "absolute top-1/2 left-0 h-2 bg-primary -translate-y-1/2 rounded-sm", style: {
        width: `${Math.min(value / bulletMax * 100, 100)}%`
      } }),
      /* @__PURE__ */ jsx56("div", { className: "absolute top-0 bottom-0 w-0.5 bg-accent", style: {
        left: `${Math.min(target / bulletMax * 100, 100)}%`
      } })
    ] }) }),
    change !== void 0 && /* @__PURE__ */ jsxs46("div", { className: "w-24 flex-shrink-0 text-right", children: [
      /* @__PURE__ */ jsxs46("div", { className: `text-sm font-mono font-medium tabular-nums ${getChangeColor()}`, children: [
        change > 0 ? "+" : "",
        change.toFixed(1),
        "%"
      ] }),
      changeLabel && /* @__PURE__ */ jsx56("div", { className: "text-xs font-sans text-tertiary", children: changeLabel })
    ] }),
    target !== void 0 && targetLabel && /* @__PURE__ */ jsxs46("div", { className: "w-28 flex-shrink-0 text-right", children: [
      /* @__PURE__ */ jsx56("div", { className: "text-xs font-sans text-tertiary", children: targetLabel }),
      /* @__PURE__ */ jsxs46("div", { className: "text-sm font-mono tabular-nums text-secondary", children: [
        target,
        unit
      ] })
    ] })
  ] });
}

// src/metrics/MetricCard.tsx
import { jsx as jsx57, jsxs as jsxs47 } from "react/jsx-runtime";
var METRIC_READ_CAPTIONS = {
  failed: "Could not be read",
  unread: "Not yet read",
  notReported: "Not reported"
};
function MetricCard({
  label,
  value,
  caption,
  change,
  changeType = "neutral",
  description,
  source,
  valueColor,
  onClick,
  className = ""
}) {
  const changeColorStyles = {
    positive: "text-growth",
    negative: "text-error",
    neutral: "text-secondary"
  };
  const valueColorClass = valueColor ? { accent: "text-accent", primary: "text-primary", growth: "text-growth" }[valueColor] : "text-primary";
  return (
    /*
      Card's own p-6 is the tile padding. This passed `p-4`, which never
      rendered: joined unmerged, p-6 won on stylesheet order. Card now lets a
      caller's padding replace its own, so keeping `p-4` here would have
      shrunk every metric tile on every portal. The tile keeps the padding it
      has always shown; a caller's padding class still applies.
    */
    /* @__PURE__ */ jsxs47(
      Card,
      {
        className,
        onClick,
        children: [
          /* @__PURE__ */ jsx57("div", { className: `min-h-8 sm:min-h-0 ${METRIC_LABEL_CLASSES} mb-1`, children: label }),
          /* @__PURE__ */ jsx57("div", { className: `text-2xl font-serif font-semibold ${valueColorClass} tracking-headline-serif tabular-nums`, children: value }),
          caption && /* @__PURE__ */ jsx57("p", { className: "text-sm font-sans text-secondary mt-1", children: caption }),
          description && /* @__PURE__ */ jsx57("p", { className: "text-sm font-sans text-secondary mt-1", children: description }),
          change && /* @__PURE__ */ jsx57("div", { className: `text-sm font-mono mt-1 ${changeColorStyles[changeType]}`, children: change }),
          source && /* @__PURE__ */ jsx57("p", { className: "text-xs font-sans text-tertiary mt-2", children: source })
        ]
      }
    )
  );
}

// src/metrics/MetricCardGrid.tsx
import { jsx as jsx58 } from "react/jsx-runtime";
var SM_COLUMNS = {
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-3",
  4: "sm:grid-cols-4"
};
function MetricCardGrid({ children, columns = 4, className = "" }) {
  return /* @__PURE__ */ jsx58("div", { className: `grid grid-cols-2 ${SM_COLUMNS[columns]} gap-4 ${className}`, children });
}

// src/metrics/MetricDisplay.tsx
import { TrendingUpIcon as TrendingUpIcon3, TrendingDownIcon as TrendingDownIcon2 } from "lucide-react";
import { jsx as jsx59, jsxs as jsxs48 } from "react/jsx-runtime";
function MetricDisplay({
  value,
  label,
  change,
  changeType = "neutral",
  size = "medium",
  context,
  showChangeIcon = false,
  valueColor = "primary",
  className = ""
}) {
  const valueSizeStyles = {
    small: "text-2xl sm:text-3xl",
    medium: "text-3xl sm:text-4xl",
    large: "text-4xl sm:text-5xl"
  };
  const valueColorStyles = {
    primary: "text-primary",
    accent: "text-accent",
    growth: "text-growth",
    error: "text-error"
  };
  const changeColorStyles = {
    positive: "text-growth",
    negative: "text-error",
    neutral: "text-secondary"
  };
  return /* @__PURE__ */ jsxs48("div", { className, children: [
    /* @__PURE__ */ jsx59("div", { className: `${valueSizeStyles[size]} font-serif font-semibold ${valueColorStyles[valueColor]} tabular-nums leading-none tracking-headline-serif mb-2`, children: value }),
    label && /* @__PURE__ */ jsx59("div", { className: "text-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-2", children: label }),
    change && /* @__PURE__ */ jsxs48("div", { className: `text-sm sm:text-base font-mono font-medium tabular-nums ${changeColorStyles[changeType]} flex items-center gap-1`, children: [
      showChangeIcon && changeType === "positive" && /* @__PURE__ */ jsx59(TrendingUpIcon3, { className: "w-4 h-4", strokeWidth: 2 }),
      showChangeIcon && changeType === "negative" && /* @__PURE__ */ jsx59(TrendingDownIcon2, { className: "w-4 h-4", strokeWidth: 2 }),
      change
    ] }),
    context && /* @__PURE__ */ jsx59("div", { className: "text-xs font-sans text-tertiary mt-1", children: context })
  ] });
}

// src/shell/AppShell.tsx
import { jsx as jsx60, jsxs as jsxs49 } from "react/jsx-runtime";
function AppShell({
  sidebar,
  header,
  children,
  sidebarWidth = 256,
  className = ""
}) {
  return /* @__PURE__ */ jsxs49("div", { className: `min-h-screen bg-canvas flex ${className}`, children: [
    /* @__PURE__ */ jsx60(
      "aside",
      {
        className: "bg-surface border-r border-recessed flex-shrink-0 h-screen sticky top-0",
        style: { width: sidebarWidth, minWidth: sidebarWidth },
        children: sidebar
      }
    ),
    /* @__PURE__ */ jsxs49("div", { className: "flex-1 flex flex-col min-w-0", children: [
      header,
      /* @__PURE__ */ jsx60("main", { className: "flex-1 overflow-auto", children })
    ] })
  ] });
}

// src/shell/PortalFrame.tsx
import { useEffect as useEffect7, useRef as useRef6 } from "react";
import { jsx as jsx61, jsxs as jsxs50 } from "react/jsx-runtime";
var PORTAL_SIDEBAR_WIDTH = 260;
function PortalFrame({
  sidebar,
  mobileSidebar,
  mobileOpen = false,
  onMobileClose,
  header,
  beforeMain,
  children,
  footer,
  overlays,
  className = ""
}) {
  const drawerContent = mobileSidebar ?? sidebar;
  const drawerRef = useRef6(null);
  useEffect7(() => {
    if (!mobileOpen) return;
    const current = drawerRef.current?.querySelector('[aria-current="page"]');
    current?.scrollIntoView?.({ block: "nearest" });
  }, [mobileOpen]);
  return /* @__PURE__ */ jsxs50("div", { className: `min-h-screen flex bg-canvas ${className}`, children: [
    overlays,
    /* @__PURE__ */ jsx61(
      "aside",
      {
        className: "hidden md:flex flex-col fixed left-0 top-0 h-full bg-surface border-r border-recessed shadow-sm z-50",
        style: { width: PORTAL_SIDEBAR_WIDTH },
        children: sidebar
      }
    ),
    /* @__PURE__ */ jsxs50("div", { className: "md:hidden fixed inset-0 z-50", hidden: !mobileOpen, children: [
      /* @__PURE__ */ jsx61("div", { className: "absolute inset-0 bg-black/50", onClick: onMobileClose }),
      /* @__PURE__ */ jsx61(
        "aside",
        {
          ref: drawerRef,
          className: "absolute left-0 top-0 h-full bg-surface border-r border-recessed shadow-lg flex flex-col",
          style: { width: PORTAL_SIDEBAR_WIDTH },
          children: drawerContent
        }
      )
    ] }),
    /* @__PURE__ */ jsxs50("div", { className: "flex-1 flex flex-col min-w-0 md:ml-[260px]", children: [
      header,
      beforeMain,
      /* @__PURE__ */ jsx61("main", { className: "flex-1 min-w-0 px-4 py-4 md:px-6", children }),
      footer
    ] })
  ] });
}

// src/shell/TreeNav.tsx
import {
  createContext,
  useContext,
  useState as useState9,
  useCallback as useCallback5,
  useMemo as useMemo5,
  useRef as useRef7
} from "react";
import { SearchIcon, XIcon } from "lucide-react";
import { jsx as jsx62, jsxs as jsxs51 } from "react/jsx-runtime";
function densityToSize(density) {
  return density === "compact" ? "sm" : "md";
}
var TreeNavContext = createContext(null);
function useTreeNav() {
  const context = useContext(TreeNavContext);
  if (!context) {
    throw new Error("useTreeNav must be used within a TreeNav component");
  }
  return context;
}
function TreeNav({
  children,
  defaultExpanded = [],
  activeItemId: controlledActiveId,
  onActiveItemChange,
  onItemSelect,
  onExpandedChange,
  density,
  size: sizeProp,
  searchable = false,
  searchPlaceholder = "Search...",
  className = "",
  "aria-label": ariaLabel = "Navigation",
  treeAriaLabel
}) {
  const resolvedTreeLabel = treeAriaLabel && treeAriaLabel !== ariaLabel ? treeAriaLabel : void 0;
  const resolvedDensity = density ?? (sizeProp === "sm" ? "compact" : "default");
  const resolvedSize = densityToSize(resolvedDensity);
  const [expandedItems, setExpandedItems] = useState9(
    () => new Set(defaultExpanded)
  );
  const [internalActiveId, setInternalActiveId] = useState9(null);
  const [searchQuery, setSearchQuery] = useState9("");
  const treeRef = useRef7(null);
  const searchRef = useRef7(null);
  const isControlled = controlledActiveId !== void 0;
  const activeItemId = isControlled ? controlledActiveId : internalActiveId;
  const toggleItem = useCallback5((id) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      onExpandedChange?.(Array.from(next));
      return next;
    });
  }, [onExpandedChange]);
  const expandItem = useCallback5((id) => {
    setExpandedItems((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      onExpandedChange?.(Array.from(next));
      return next;
    });
  }, [onExpandedChange]);
  const collapseItem = useCallback5((id) => {
    setExpandedItems((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      onExpandedChange?.(Array.from(next));
      return next;
    });
  }, [onExpandedChange]);
  const setActiveItem = useCallback5((id) => {
    if (!isControlled) {
      setInternalActiveId(id);
    }
    onActiveItemChange?.(id);
    if (id) {
      onItemSelect?.(id);
    }
  }, [isControlled, onActiveItemChange, onItemSelect]);
  const isExpanded = useCallback5((id) => {
    return expandedItems.has(id);
  }, [expandedItems]);
  const isActive = useCallback5((id) => {
    return activeItemId === id;
  }, [activeItemId]);
  const matchesSearch = useCallback5((label, keywords) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const searchableText = [label, ...keywords || []].join(" ").toLowerCase();
    return searchableText.includes(query);
  }, [searchQuery]);
  const getVisibleItems = useCallback5(() => {
    const tree = treeRef.current;
    if (!tree) return [];
    return Array.from(
      tree.querySelectorAll(
        '[role="treeitem"]:not([aria-disabled="true"])'
      )
    );
  }, []);
  const handleTreeKeyDown = useCallback5((e) => {
    const items = getVisibleItems();
    if (items.length === 0) return;
    const currentIndex = items.indexOf(document.activeElement);
    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        items[nextIndex]?.focus();
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        items[prevIndex]?.focus();
        break;
      }
      case "Home": {
        e.preventDefault();
        items[0]?.focus();
        break;
      }
      case "End": {
        e.preventDefault();
        items[items.length - 1]?.focus();
        break;
      }
      default:
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          const char = e.key.toLowerCase();
          const startIndex = currentIndex + 1;
          for (let i = 0; i < items.length; i++) {
            const idx = (startIndex + i) % items.length;
            const text = items[idx]?.textContent?.trim().toLowerCase() ?? "";
            if (text.startsWith(char)) {
              items[idx]?.focus();
              break;
            }
          }
        }
        break;
    }
  }, [getVisibleItems]);
  const contextValue = useMemo5(() => ({
    expandedItems,
    activeItemId,
    searchQuery,
    toggleItem,
    expandItem,
    collapseItem,
    setActiveItem,
    isExpanded,
    isActive,
    size: resolvedSize,
    density: resolvedDensity,
    level: 0,
    matchesSearch
  }), [
    expandedItems,
    activeItemId,
    searchQuery,
    toggleItem,
    expandItem,
    collapseItem,
    setActiveItem,
    isExpanded,
    isActive,
    resolvedSize,
    resolvedDensity,
    matchesSearch
  ]);
  return /* @__PURE__ */ jsx62(TreeNavContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsxs51(
    "nav",
    {
      className: `flex flex-col h-full ${className}`,
      role: "navigation",
      "aria-label": ariaLabel,
      children: [
        searchable && /* @__PURE__ */ jsx62("div", { className: "px-3 pt-2 pb-1.5", children: /* @__PURE__ */ jsxs51("div", { className: "relative", children: [
          /* @__PURE__ */ jsx62(
            SearchIcon,
            {
              className: "absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-tertiary pointer-events-none",
              strokeWidth: 2
            }
          ),
          /* @__PURE__ */ jsx62(
            "input",
            {
              ref: searchRef,
              type: "text",
              value: searchQuery,
              onChange: (e) => setSearchQuery(e.target.value),
              placeholder: searchPlaceholder,
              className: "w-full pl-7 pr-7 py-1 text-xs font-sans bg-canvas border border-recessed rounded-sm text-primary placeholder:text-quaternary focus:outline-none focus:ring-1 focus:ring-tertiary/40 focus:border-tertiary transition-colors",
              "aria-label": "Search navigation"
            }
          ),
          searchQuery && /* @__PURE__ */ jsx62(
            "button",
            {
              onClick: () => setSearchQuery(""),
              className: "absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded-sm hover:bg-elevated transition-colors",
              "aria-label": "Clear search",
              children: /* @__PURE__ */ jsx62(XIcon, { className: "w-3 h-3 text-tertiary", strokeWidth: 2 })
            }
          )
        ] }) }),
        /* @__PURE__ */ jsx62(
          "div",
          {
            ref: treeRef,
            className: "flex-1 overflow-y-auto py-2",
            role: "tree",
            "aria-label": resolvedTreeLabel,
            onKeyDown: handleTreeKeyDown,
            children
          }
        )
      ]
    }
  ) });
}
var TreeNavLevelContext = createContext(0);
function useTreeNavLevel() {
  return useContext(TreeNavLevelContext);
}

// src/shell/AppSidebar.tsx
import { jsx as jsx63, jsxs as jsxs52 } from "react/jsx-runtime";
function AppSidebar({
  logoSrc = "https://cdn.aaim.app/logos/aaim-master-grey.svg",
  logoAlt = "Aaim",
  subtitle,
  header,
  toolbar,
  children,
  nav,
  activeItemId,
  onItemSelect,
  onActiveItemChange,
  defaultExpanded,
  onExpandedChange,
  density = "default",
  searchable = false,
  searchPlaceholder,
  version,
  footer,
  "aria-label": ariaLabel = "Sidebar navigation",
  className = ""
}) {
  return /* @__PURE__ */ jsxs52("div", { className: `flex flex-col h-full ${className}`, children: [
    header ?? /* @__PURE__ */ jsx63("div", { className: "border-b border-recessed flex-shrink-0", children: /* @__PURE__ */ jsx63("div", { className: "flex items-center justify-center px-6 h-16", children: /* @__PURE__ */ jsxs52("div", { className: "text-center", children: [
      /* @__PURE__ */ jsx63("img", { src: logoSrc, alt: logoAlt, className: "h-6 mx-auto" }),
      subtitle && /* @__PURE__ */ jsx63("div", { className: "text-label-xs font-sans font-semibold uppercase tracking-[0.1em] text-tertiary mt-1", children: subtitle })
    ] }) }) }),
    toolbar && /* @__PURE__ */ jsx63("div", { className: "flex-shrink-0 border-b border-recessed px-3 py-2 relative z-30", children: toolbar }),
    /* @__PURE__ */ jsx63("div", { className: "flex-1 min-h-0", children: nav ?? /* @__PURE__ */ jsx63(
      TreeNav,
      {
        activeItemId,
        onActiveItemChange,
        onItemSelect,
        defaultExpanded,
        onExpandedChange,
        density,
        searchable,
        searchPlaceholder,
        "aria-label": ariaLabel,
        children
      }
    ) }),
    (footer || version) && /* @__PURE__ */ jsx63("div", { className: "flex-shrink-0 border-t border-recessed px-4 py-3", children: footer ?? /* @__PURE__ */ jsx63("div", { className: "text-code-xs font-mono text-tertiary", children: version }) })
  ] });
}

// src/shell/AppHeader.tsx
import React17 from "react";
import { Fragment as Fragment12, jsx as jsx64, jsxs as jsxs53 } from "react/jsx-runtime";
function AppHeader({
  title,
  meta,
  breadcrumbs,
  leading,
  center,
  actions,
  sticky = true,
  backdrop = true,
  className = ""
}) {
  return /* @__PURE__ */ jsx64(
    "header",
    {
      className: `
        ${sticky ? "sticky top-0" : ""}
        ${backdrop ? "bg-surface/95 backdrop-blur-[10px]" : "bg-surface"}
        z-40 flex-shrink-0
        ${className}
      `,
      children: /* @__PURE__ */ jsxs53("div", { className: "flex items-center justify-between gap-2 h-[65px] px-4 md:px-6 border-b border-recessed", children: [
        /* @__PURE__ */ jsxs53("div", { className: "flex-1 min-w-0 flex items-center gap-3", children: [
          leading,
          center ?? /* @__PURE__ */ jsx64(Fragment12, { children: breadcrumbs && breadcrumbs.length > 0 ? /* @__PURE__ */ jsxs53("div", { className: "min-w-0 flex items-baseline gap-3", children: [
            /* @__PURE__ */ jsx64(
              "nav",
              {
                className: "flex min-w-0 items-center gap-1.5 text-sm font-sans",
                "aria-label": "Breadcrumb",
                children: breadcrumbs.map((crumb, index) => {
                  const isTerminal = index === breadcrumbs.length - 1;
                  const ancestorHidden = isTerminal ? "" : "hidden md:inline-flex";
                  return /* @__PURE__ */ jsxs53(React17.Fragment, { children: [
                    index > 0 && /* @__PURE__ */ jsx64(
                      "span",
                      {
                        className: "text-tertiary select-none hidden md:inline",
                        "aria-hidden": "true",
                        children: "/"
                      }
                    ),
                    crumb.href || crumb.onClick ? /* @__PURE__ */ jsx64(
                      "a",
                      {
                        href: crumb.href,
                        onClick: crumb.onClick,
                        className: `text-tertiary hover:text-primary transition-colors cursor-pointer whitespace-nowrap ${ancestorHidden}`,
                        children: crumb.label
                      }
                    ) : /* @__PURE__ */ jsx64(
                      "span",
                      {
                        className: `${isTerminal ? "text-primary font-semibold truncate" : `text-tertiary ${ancestorHidden}`}`,
                        "aria-current": isTerminal ? "page" : void 0,
                        children: crumb.label
                      }
                    )
                  ] }, index);
                })
              }
            ),
            meta && /* @__PURE__ */ jsx64("span", { className: "text-xs font-sans text-tertiary whitespace-nowrap", children: meta })
          ] }) : title ? (
            /* Title only -- no breadcrumbs.
               Below sm the title may wrap to two lines at a smaller size.
               On a 390px phone the action cluster leaves the title about
               130px, and several portals render no body heading, so a
               single truncated line left the page unnamed ('Pledge Inb...',
               'Jurisdictio...'). From sm up it stays one truncated line. */
            /* @__PURE__ */ jsxs53("div", { className: "flex items-center gap-3 min-w-0", children: [
              /* @__PURE__ */ jsx64("h1", { className: "text-sm leading-tight line-clamp-2 break-words sm:text-base sm:leading-normal sm:line-clamp-none sm:truncate font-serif font-semibold text-primary tracking-headline-serif", children: title }),
              meta && /* @__PURE__ */ jsx64("span", { className: "text-xs font-sans text-tertiary whitespace-nowrap", children: meta })
            ] })
          ) : null })
        ] }),
        actions && /* @__PURE__ */ jsx64("div", { className: "flex items-center gap-1 flex-shrink-0 ml-2 sm:ml-4", children: actions })
      ] })
    }
  );
}

// src/typography/BodyText.tsx
import { forwardRef as forwardRef7 } from "react";
import { jsx as jsx65 } from "react/jsx-runtime";
var VARIANT_CLASSES2 = {
  content: "font-serif leading-relaxed",
  ui: "font-sans"
};
var SIZE_CLASSES3 = {
  xs: "text-xs",
  sm: "text-sm",
  base: "text-base",
  lg: "text-lg"
};
var COLOR_CLASSES3 = {
  primary: "text-primary",
  secondary: "text-secondary",
  tertiary: "text-tertiary",
  accent: "text-accent",
  inherit: ""
};
var BodyText = forwardRef7(
  function BodyText2({
    variant = "content",
    size = "base",
    color = "secondary",
    inline = false,
    className = "",
    children,
    ...props
  }, ref) {
    const classes = mergeUtilityClasses(
      [VARIANT_CLASSES2[variant], SIZE_CLASSES3[size], COLOR_CLASSES3[color]].filter(Boolean).join(" "),
      className
    );
    if (inline) {
      return /* @__PURE__ */ jsx65("span", { className: classes, ...props, children });
    }
    return /* @__PURE__ */ jsx65("p", { ref, className: classes, ...props, children });
  }
);

// src/shell/PageHeader.tsx
import { jsx as jsx66, jsxs as jsxs54 } from "react/jsx-runtime";
function PageHeader({
  title,
  headingLevel = 1,
  description,
  actions,
  className = ""
}) {
  return /* @__PURE__ */ jsxs54("div", { className: `flex flex-col gap-4 pb-6 sm:flex-row sm:items-start sm:justify-between ${className}`, children: [
    /* @__PURE__ */ jsxs54("div", { className: "min-w-0", children: [
      title && /* @__PURE__ */ jsx66(Heading, { level: headingLevel, size: "section", color: "primary", children: title }),
      description && /* @__PURE__ */ jsx66(
        BodyText,
        {
          variant: "ui",
          size: "base",
          color: "secondary",
          className: `${title ? "mt-1 " : ""}max-w-content-narrow`,
          children: description
        }
      )
    ] }),
    actions && // flex-wrap: below sm the slot is the full column width, and two or
    // three buttons in one unwrapped row clipped the last one at 375px.
    /* @__PURE__ */ jsx66("div", { className: "flex flex-shrink-0 flex-wrap items-center gap-2", children: actions })
  ] });
}

// src/shell/CommandPalette.tsx
import React18, {
  useState as useState10,
  useEffect as useEffect8,
  useCallback as useCallback6,
  useMemo as useMemo6,
  useRef as useRef8
} from "react";
import {
  SearchIcon as SearchIcon2,
  XIcon as XIcon2,
  ChevronRightIcon as ChevronRightIcon2,
  ClockIcon,
  LayoutDashboardIcon,
  FileTextIcon,
  CircleIcon
} from "lucide-react";
import { Fragment as Fragment13, jsx as jsx67, jsxs as jsxs55 } from "react/jsx-runtime";
var DEBOUNCE_MS = 200;
var ALL_ID = "all";
var MAX_RECENT = 5;
var STATUS_DOT = {
  online: "bg-success",
  offline: "bg-tertiary",
  away: "bg-warning-amber",
  busy: "bg-error-bright"
};
function highlightRanges(text, query) {
  if (!query.trim() || !text) return [];
  const ranges = [];
  const lower = text.toLowerCase();
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  for (const w of words) {
    let from = 0;
    while (from < lower.length) {
      const idx = lower.indexOf(w, from);
      if (idx === -1) break;
      ranges.push([idx, idx + w.length]);
      from = idx + 1;
    }
  }
  if (ranges.length === 0) return [];
  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [[ranges[0][0], ranges[0][1]]];
  for (let i = 1; i < ranges.length; i++) {
    const prev = merged[merged.length - 1];
    const curr = ranges[i];
    if (curr[0] <= prev[1]) {
      prev[1] = Math.max(prev[1], curr[1]);
    } else {
      merged.push([curr[0], curr[1]]);
    }
  }
  return merged;
}
function Hl({ text, query }) {
  const ranges = highlightRanges(text, query);
  if (ranges.length === 0) return /* @__PURE__ */ jsx67(Fragment13, { children: text });
  const parts = [];
  let last = 0;
  ranges.forEach(([s, e], i) => {
    if (s > last) parts.push(/* @__PURE__ */ jsx67("span", { children: text.slice(last, s) }, `t${i}`));
    parts.push(
      /* @__PURE__ */ jsx67("mark", { className: "bg-warning/20 text-primary rounded-sm", children: text.slice(s, e) }, `m${i}`)
    );
    last = e;
  });
  if (last < text.length) parts.push(/* @__PURE__ */ jsx67("span", { children: text.slice(last) }, "tail"));
  return /* @__PURE__ */ jsx67(Fragment13, { children: parts });
}
function ContentDefault({ r, q }) {
  return /* @__PURE__ */ jsxs55("div", { className: "flex-1 min-w-0", children: [
    /* @__PURE__ */ jsx67("span", { className: "font-sans text-sm text-primary block truncate", children: /* @__PURE__ */ jsx67(Hl, { text: r.title, query: q }) }),
    r.description && /* @__PURE__ */ jsx67("span", { className: "font-sans text-xs text-tertiary block truncate", children: /* @__PURE__ */ jsx67(Hl, { text: r.description, query: q }) })
  ] });
}
function ContentNavigation({ r, q }) {
  return /* @__PURE__ */ jsxs55("div", { className: "flex-1 min-w-0", children: [
    /* @__PURE__ */ jsx67("span", { className: "font-sans text-sm text-primary block truncate", children: /* @__PURE__ */ jsx67(Hl, { text: r.title, query: q }) }),
    r.breadcrumb.length > 0 && /* @__PURE__ */ jsx67("span", { className: "font-sans text-xs text-tertiary block truncate", children: r.breadcrumb.map((seg, i) => /* @__PURE__ */ jsxs55(React18.Fragment, { children: [
      i > 0 && /* @__PURE__ */ jsx67("span", { className: "mx-1 text-quaternary", children: "/" }),
      /* @__PURE__ */ jsx67(Hl, { text: seg, query: q })
    ] }, i)) })
  ] });
}
function ContentRecord({ r, q }) {
  return /* @__PURE__ */ jsxs55(Fragment13, { children: [
    /* @__PURE__ */ jsxs55("div", { className: "relative flex-shrink-0", children: [
      r.avatarUrl ? /* @__PURE__ */ jsx67("img", { src: r.avatarUrl, alt: "", className: "w-7 h-7 rounded-full object-cover" }) : r.initials ? /* @__PURE__ */ jsx67("div", { className: "w-7 h-7 rounded-full bg-elevated flex items-center justify-center text-xs font-sans font-semibold text-secondary", children: r.initials }) : /* @__PURE__ */ jsx67("div", { className: "w-7 h-7 rounded-full bg-elevated flex items-center justify-center", children: /* @__PURE__ */ jsx67(CircleIcon, { className: "w-3.5 h-3.5 text-tertiary", strokeWidth: 2 }) }),
      r.status && /* @__PURE__ */ jsx67(
        "span",
        {
          className: `absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-surface ${STATUS_DOT[r.status]}`,
          "aria-label": r.status
        }
      )
    ] }),
    /* @__PURE__ */ jsxs55("div", { className: "flex-1 min-w-0", children: [
      /* @__PURE__ */ jsx67("span", { className: "font-sans text-sm text-primary block truncate", children: /* @__PURE__ */ jsx67(Hl, { text: r.title, query: q }) }),
      r.subtitle && /* @__PURE__ */ jsx67("span", { className: "font-sans text-xs text-tertiary block truncate", children: /* @__PURE__ */ jsx67(Hl, { text: r.subtitle, query: q }) })
    ] })
  ] });
}
function ContentConfig({ r, q }) {
  return /* @__PURE__ */ jsxs55("div", { className: "flex-1 min-w-0 flex items-center gap-2", children: [
    /* @__PURE__ */ jsxs55("div", { className: "min-w-0", children: [
      /* @__PURE__ */ jsx67("span", { className: "font-sans text-sm text-primary block truncate", children: /* @__PURE__ */ jsx67(Hl, { text: r.title, query: q }) }),
      r.description && /* @__PURE__ */ jsx67("span", { className: "font-sans text-xs text-tertiary block truncate", children: /* @__PURE__ */ jsx67(Hl, { text: r.description, query: q }) })
    ] }),
    /* @__PURE__ */ jsxs55("span", { className: "flex-shrink-0 ml-auto flex items-center gap-1", children: [
      /* @__PURE__ */ jsx67("code", { className: "px-1.5 py-0.5 text-xs font-mono bg-elevated border border-recessed rounded-sm text-secondary truncate max-w-[120px]", children: r.currentValue }),
      r.editable && /* @__PURE__ */ jsx67("span", { className: "text-caption-xs text-tertiary font-sans", children: "edit" })
    ] })
  ] });
}
function ContentCommand({ r, q }) {
  return /* @__PURE__ */ jsxs55("div", { className: "flex-1 min-w-0 flex items-center gap-2", children: [
    /* @__PURE__ */ jsxs55("div", { className: "min-w-0", children: [
      /* @__PURE__ */ jsx67("span", { className: "font-sans text-sm text-primary block truncate", children: /* @__PURE__ */ jsx67(Hl, { text: r.title, query: q }) }),
      r.description && /* @__PURE__ */ jsx67("span", { className: "font-sans text-xs text-tertiary block truncate", children: r.description })
    ] }),
    r.shortcut && r.shortcut.length > 0 && /* @__PURE__ */ jsx67("span", { className: "flex-shrink-0 ml-auto flex items-center gap-0.5", children: r.shortcut.map((key, i) => /* @__PURE__ */ jsx67(
      "kbd",
      {
        className: "px-1.5 py-0.5 text-xs font-mono bg-elevated border border-recessed rounded text-tertiary min-w-[1.25rem] text-center",
        children: key
      },
      i
    )) })
  ] });
}
function ContentDocument({ r, q }) {
  return /* @__PURE__ */ jsxs55("div", { className: "flex-1 min-w-0", children: [
    /* @__PURE__ */ jsxs55("span", { className: "font-sans text-sm text-primary flex items-center gap-2", children: [
      /* @__PURE__ */ jsx67("span", { className: "truncate", children: /* @__PURE__ */ jsx67(Hl, { text: r.title, query: q }) }),
      r.fileType && /* @__PURE__ */ jsx67("span", { className: "flex-shrink-0 px-1.5 py-0.5 text-label-xs font-mono font-semibold uppercase bg-elevated border border-recessed rounded-sm text-tertiary", children: r.fileType })
    ] }),
    /* @__PURE__ */ jsxs55("span", { className: "font-sans text-xs text-tertiary block truncate", children: [
      [r.lastModified, r.owner].filter(Boolean).join(" \xB7 "),
      r.description && /* @__PURE__ */ jsxs55(Fragment13, { children: [
        (r.lastModified || r.owner) && " \u2014 ",
        /* @__PURE__ */ jsx67(Hl, { text: r.description, query: q })
      ] })
    ] })
  ] });
}
function ResultRow({
  result,
  query,
  isSelected,
  index,
  category,
  renderItem,
  onSelect,
  onHover
}) {
  const custom = renderItem?.(result, { query, isSelected, index, category });
  if (custom) {
    return /* @__PURE__ */ jsx67(
      "button",
      {
        id: `palette-result-${result.id}`,
        "data-index": index,
        onClick: () => onSelect(result),
        onMouseEnter: () => onHover(index),
        role: "option",
        "aria-selected": isSelected,
        className: `w-full text-left transition-colors ${isSelected ? "bg-elevated text-primary" : "hover:bg-elevated/50"}`,
        children: custom
      }
    );
  }
  const Icon = result.icon || category?.icon || FileTextIcon;
  const variant = result.variant ?? "default";
  const showIcon = variant !== "record";
  const showChevron = isSelected && variant !== "command" && variant !== "config";
  return /* @__PURE__ */ jsxs55(
    "button",
    {
      id: `palette-result-${result.id}`,
      "data-index": index,
      onClick: () => onSelect(result),
      onMouseEnter: () => onHover(index),
      role: "option",
      "aria-selected": isSelected,
      className: `w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${isSelected ? "bg-elevated text-primary" : "hover:bg-elevated/50"}`,
      children: [
        showIcon && /* @__PURE__ */ jsx67(Icon, { className: "w-4 h-4 flex-shrink-0 text-tertiary", strokeWidth: 2 }),
        variant === "navigation" && /* @__PURE__ */ jsx67(ContentNavigation, { r: result, q: query }),
        variant === "record" && /* @__PURE__ */ jsx67(ContentRecord, { r: result, q: query }),
        variant === "config" && /* @__PURE__ */ jsx67(ContentConfig, { r: result, q: query }),
        variant === "command" && /* @__PURE__ */ jsx67(ContentCommand, { r: result, q: query }),
        variant === "document" && /* @__PURE__ */ jsx67(ContentDocument, { r: result, q: query }),
        (variant === "default" || !variant) && /* @__PURE__ */ jsx67(ContentDefault, { r: result, q: query }),
        result.meta && variant !== "config" && variant !== "command" && /* @__PURE__ */ jsx67("span", { className: "font-mono text-xs text-quaternary flex-shrink-0", children: result.meta }),
        showChevron && /* @__PURE__ */ jsx67(ChevronRightIcon2, { className: "w-4 h-4 text-secondary flex-shrink-0", strokeWidth: 2 })
      ]
    }
  );
}
function CommandPalette({
  categories,
  onSearch,
  onSelect,
  renderItem,
  recentSearches: recentProp,
  onRecentSearchesChange,
  placeholder = "Search pages, users, settings...",
  maxResults = 10,
  shortcutKey = "k",
  shortcutModifier = true,
  className = ""
}) {
  const [isOpen, setIsOpen] = useState10(false);
  const [query, setQuery] = useState10("");
  const [activeCat, setActiveCat] = useState10(ALL_ID);
  const [results, setResults] = useState10([]);
  const [loading, setLoading] = useState10(false);
  const [selIdx, setSelIdx] = useState10(0);
  const [isMac, setIsMac] = useState10(false);
  const [internalRecent, setInternalRecent] = useState10([]);
  const recent = recentProp ?? internalRecent;
  const inputRef = useRef8(null);
  const listRef = useRef8(null);
  const debounceRef = useRef8();
  const counterRef = useRef8(0);
  useEffect8(() => {
    if (typeof navigator !== "undefined") {
      setIsMac(navigator.platform?.includes("Mac") || navigator.userAgent?.includes("Mac"));
    }
  }, []);
  const allCats = useMemo6(
    () => [{ id: ALL_ID, label: "All" }, ...categories],
    [categories]
  );
  const displayed = useMemo6(() => results.slice(0, maxResults), [results, maxResults]);
  const grouped = useMemo6(() => {
    if (activeCat !== ALL_ID) return null;
    const g = {};
    displayed.forEach((r) => {
      if (!g[r.category]) g[r.category] = [];
      g[r.category].push(r);
    });
    return g;
  }, [displayed, activeCat]);
  const exec = useCallback6(
    async (q, cat) => {
      if (!q.trim()) {
        setResults([]);
        setLoading(false);
        return;
      }
      const c = ++counterRef.current;
      setLoading(true);
      try {
        const res = await onSearch(q, cat === ALL_ID ? void 0 : cat);
        if (c === counterRef.current) {
          setResults(res);
          setSelIdx(0);
        }
      } catch {
        if (c === counterRef.current) setResults([]);
      } finally {
        if (c === counterRef.current) setLoading(false);
      }
    },
    [onSearch]
  );
  const addRecent = useCallback6(
    (q) => {
      const t = q.trim();
      if (!t) return;
      const next = [t, ...recent.filter((s) => s !== t)].slice(0, MAX_RECENT);
      onRecentSearchesChange ? onRecentSearchesChange(next) : setInternalRecent(next);
    },
    [recent, onRecentSearchesChange]
  );
  const open = useCallback6(() => {
    setIsOpen(true);
    setQuery("");
    setResults([]);
    setSelIdx(0);
    setActiveCat(ALL_ID);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);
  const close = useCallback6(() => {
    setIsOpen(false);
    setQuery("");
    setResults([]);
    setSelIdx(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);
  const select = useCallback6(
    (r) => {
      if (query.trim()) addRecent(query);
      onSelect(r);
      close();
    },
    [query, addRecent, onSelect, close]
  );
  const clickRecent = useCallback6(
    (q) => {
      setQuery(q);
      exec(q, activeCat);
    },
    [exec, activeCat]
  );
  const onInput = useCallback6(
    (e) => {
      const v = e.target.value;
      setQuery(v);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => exec(v, activeCat), DEBOUNCE_MS);
    },
    [exec, activeCat]
  );
  const onCatChange = useCallback6(
    (id) => {
      setActiveCat(id);
      setSelIdx(0);
      if (query.trim()) exec(query, id);
    },
    [query, exec]
  );
  useEffect8(() => {
    const handler = (e) => {
      const mod = shortcutModifier ? e.metaKey || e.ctrlKey : true;
      if (mod && e.key.toLowerCase() === shortcutKey) {
        e.preventDefault();
        isOpen ? close() : open();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [shortcutKey, shortcutModifier, isOpen, open, close]);
  const onKeyDown = useCallback6(
    (e) => {
      const count = query.trim() ? displayed.length : recent.length;
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelIdx((p) => p < count - 1 ? p + 1 : 0);
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelIdx((p) => p > 0 ? p - 1 : count - 1);
          break;
        case "Enter":
          e.preventDefault();
          if (query.trim() && displayed[selIdx]) select(displayed[selIdx]);
          else if (!query.trim() && recent[selIdx]) clickRecent(recent[selIdx]);
          break;
        case "Escape":
          e.preventDefault();
          close();
          break;
      }
    },
    [query, displayed, recent, selIdx, select, clickRecent, close]
  );
  useEffect8(() => {
    listRef.current?.querySelector(`[data-index="${selIdx}"]`)?.scrollIntoView({ block: "nearest" });
  }, [selIdx]);
  useEffect8(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);
  if (!isOpen) return null;
  const hasQuery = query.trim().length > 0;
  const hasResults = displayed.length > 0;
  const hasRecent = recent.length > 0;
  const showResults = hasQuery && hasResults && !loading;
  const showEmpty = hasQuery && !hasResults && !loading;
  const showRecent = !hasQuery && hasRecent;
  const showInitial = !hasQuery && !hasRecent;
  const announce = loading ? "Searching..." : hasQuery ? `${displayed.length} result${displayed.length !== 1 ? "s" : ""} found` : "";
  return /* @__PURE__ */ jsxs55(Fragment13, { children: [
    /* @__PURE__ */ jsx67("div", { "aria-live": "polite", "aria-atomic": "true", className: "sr-only", children: announce }),
    /* @__PURE__ */ jsx67(
      "div",
      {
        className: "fixed inset-0 bg-primary/60 backdrop-blur-sm z-50 animate-fade-in",
        onClick: close,
        "aria-hidden": "true"
      }
    ),
    /* @__PURE__ */ jsxs55(
      "div",
      {
        className: `fixed top-[15%] left-1/2 -translate-x-1/2 w-full max-w-[640px] bg-surface border border-recessed rounded-sm shadow-xl z-50 overflow-hidden animate-scale-in ${className}`,
        role: "dialog",
        "aria-modal": "true",
        "aria-label": "Command palette",
        children: [
          /* @__PURE__ */ jsxs55("div", { className: "flex items-center gap-3 px-4 py-3 border-b border-recessed", children: [
            /* @__PURE__ */ jsx67(SearchIcon2, { className: "w-5 h-5 text-tertiary flex-shrink-0", strokeWidth: 2 }),
            /* @__PURE__ */ jsx67(
              "input",
              {
                ref: inputRef,
                type: "text",
                value: query,
                onChange: onInput,
                onKeyDown,
                placeholder,
                className: "flex-1 bg-transparent text-base font-sans text-primary placeholder:text-tertiary outline-none",
                role: "combobox",
                "aria-expanded": hasResults,
                "aria-controls": "palette-listbox",
                "aria-activedescendant": displayed[selIdx] ? `palette-result-${displayed[selIdx].id}` : void 0,
                autoComplete: "off",
                autoCorrect: "off",
                autoCapitalize: "off",
                spellCheck: false
              }
            ),
            query && /* @__PURE__ */ jsx67(
              "button",
              {
                onClick: () => {
                  setQuery("");
                  setResults([]);
                  inputRef.current?.focus();
                },
                className: "p-1 rounded-sm hover:bg-elevated transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tertiary/30",
                "aria-label": "Clear search",
                children: /* @__PURE__ */ jsx67(XIcon2, { className: "w-4 h-4 text-tertiary", strokeWidth: 2 })
              }
            ),
            /* @__PURE__ */ jsx67("kbd", { className: "px-1.5 py-0.5 text-xs font-mono bg-elevated rounded border border-recessed text-tertiary", children: "esc" })
          ] }),
          /* @__PURE__ */ jsx67(
            "div",
            {
              className: "flex items-center gap-1 px-4 py-2 border-b border-recessed overflow-x-auto",
              role: "tablist",
              "aria-label": "Search categories",
              children: allCats.map((cat) => {
                const active = cat.id === activeCat;
                const I = cat.icon;
                return /* @__PURE__ */ jsxs55(
                  "button",
                  {
                    role: "tab",
                    "aria-selected": active,
                    "aria-controls": "palette-listbox",
                    onClick: () => onCatChange(cat.id),
                    className: `flex items-center gap-1.5 px-3 py-1.5 text-sm font-sans rounded-sm whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tertiary/30 ${active ? "bg-elevated text-primary font-medium" : "text-tertiary hover:text-secondary hover:bg-elevated/50"}`,
                    children: [
                      I && /* @__PURE__ */ jsx67(I, { className: "w-3.5 h-3.5", strokeWidth: 2 }),
                      cat.label
                    ]
                  },
                  cat.id
                );
              })
            }
          ),
          /* @__PURE__ */ jsxs55(
            "div",
            {
              ref: listRef,
              id: "palette-listbox",
              className: "max-h-[400px] overflow-y-auto",
              role: "listbox",
              "aria-label": "Search results",
              children: [
                loading && /* @__PURE__ */ jsxs55("div", { className: "px-4 py-8 text-center", children: [
                  /* @__PURE__ */ jsx67("div", { className: "inline-block w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin" }),
                  /* @__PURE__ */ jsx67("p", { className: "mt-2 text-sm font-sans text-tertiary", children: "Searching..." })
                ] }),
                showResults && grouped && Object.entries(grouped).map(([catId, catResults]) => {
                  const cat = categories.find((c) => c.id === catId);
                  const CatIcon = cat?.icon || FileTextIcon;
                  return /* @__PURE__ */ jsxs55("div", { children: [
                    /* @__PURE__ */ jsxs55("div", { className: "px-4 py-2 text-label-xs font-sans font-semibold uppercase text-tertiary flex items-center gap-2", children: [
                      /* @__PURE__ */ jsx67(CatIcon, { className: "w-3.5 h-3.5", strokeWidth: 2 }),
                      cat?.label || catId
                    ] }),
                    catResults.map((r) => {
                      const gi = displayed.findIndex((d) => d.id === r.id);
                      return /* @__PURE__ */ jsx67(
                        ResultRow,
                        {
                          result: r,
                          query,
                          isSelected: gi === selIdx,
                          index: gi,
                          category: cat,
                          renderItem,
                          onSelect: select,
                          onHover: setSelIdx
                        },
                        r.id
                      );
                    })
                  ] }, catId);
                }),
                showResults && !grouped && /* @__PURE__ */ jsx67("div", { className: "py-2", children: displayed.map((r, i) => {
                  const cat = categories.find((c) => c.id === r.category);
                  return /* @__PURE__ */ jsx67(
                    ResultRow,
                    {
                      result: r,
                      query,
                      isSelected: i === selIdx,
                      index: i,
                      category: cat,
                      renderItem,
                      onSelect: select,
                      onHover: setSelIdx
                    },
                    r.id
                  );
                }) }),
                showRecent && /* @__PURE__ */ jsxs55("div", { className: "py-2", children: [
                  /* @__PURE__ */ jsxs55("div", { className: "px-4 py-2 text-label-xs font-sans font-semibold uppercase text-tertiary flex items-center gap-2", children: [
                    /* @__PURE__ */ jsx67(ClockIcon, { className: "w-3.5 h-3.5", strokeWidth: 2 }),
                    "Recent Searches"
                  ] }),
                  recent.map((s, i) => /* @__PURE__ */ jsxs55(
                    "button",
                    {
                      "data-index": i,
                      onClick: () => clickRecent(s),
                      onMouseEnter: () => setSelIdx(i),
                      role: "option",
                      "aria-selected": i === selIdx,
                      className: `w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${i === selIdx ? "bg-elevated text-primary" : "hover:bg-elevated/50"}`,
                      children: [
                        /* @__PURE__ */ jsx67(ClockIcon, { className: "w-4 h-4 flex-shrink-0 text-tertiary", strokeWidth: 2 }),
                        /* @__PURE__ */ jsx67("span", { className: "flex-1 font-sans text-sm text-secondary truncate", children: s }),
                        i === selIdx && /* @__PURE__ */ jsx67(ChevronRightIcon2, { className: "w-4 h-4 text-secondary", strokeWidth: 2 })
                      ]
                    },
                    s
                  ))
                ] }),
                showEmpty && /* @__PURE__ */ jsxs55("div", { className: "px-4 py-8 text-center", children: [
                  /* @__PURE__ */ jsx67(SearchIcon2, { className: "w-8 h-8 text-tertiary mx-auto mb-2", strokeWidth: 1.5 }),
                  /* @__PURE__ */ jsx67("p", { className: "text-sm font-sans text-secondary", children: "No results found" }),
                  /* @__PURE__ */ jsx67("p", { className: "text-xs font-sans text-tertiary mt-1", children: "Try adjusting your search terms or selecting a different category" })
                ] }),
                showInitial && /* @__PURE__ */ jsxs55("div", { className: "px-4 py-8 text-center", children: [
                  /* @__PURE__ */ jsx67(LayoutDashboardIcon, { className: "w-8 h-8 text-tertiary mx-auto mb-2", strokeWidth: 1.5 }),
                  /* @__PURE__ */ jsx67("p", { className: "text-sm font-sans text-secondary", children: "Start typing to search" }),
                  /* @__PURE__ */ jsx67("p", { className: "text-xs font-sans text-tertiary mt-1", children: "Search across pages, users, settings, and more" })
                ] })
              ]
            }
          ),
          /* @__PURE__ */ jsxs55("div", { className: "px-4 py-2 border-t border-recessed bg-elevated/50 flex items-center justify-between text-xs font-sans text-tertiary", children: [
            /* @__PURE__ */ jsxs55("div", { className: "flex items-center gap-4", children: [
              /* @__PURE__ */ jsxs55("span", { className: "flex items-center gap-1", children: [
                /* @__PURE__ */ jsx67("kbd", { className: "px-1 py-0.5 font-mono bg-surface rounded border border-recessed", children: "\u2191" }),
                /* @__PURE__ */ jsx67("kbd", { className: "px-1 py-0.5 font-mono bg-surface rounded border border-recessed", children: "\u2193" }),
                /* @__PURE__ */ jsx67("span", { children: "navigate" })
              ] }),
              /* @__PURE__ */ jsxs55("span", { className: "flex items-center gap-1", children: [
                /* @__PURE__ */ jsx67("kbd", { className: "px-1.5 py-0.5 font-mono bg-surface rounded border border-recessed", children: "\u21B5" }),
                /* @__PURE__ */ jsx67("span", { children: "select" })
              ] })
            ] }),
            /* @__PURE__ */ jsxs55("span", { className: "flex items-center gap-1", children: [
              /* @__PURE__ */ jsx67("kbd", { className: "px-1.5 py-0.5 font-mono bg-surface rounded border border-recessed", children: isMac ? "\u2318" : "Ctrl" }),
              /* @__PURE__ */ jsx67("kbd", { className: "px-1 py-0.5 font-mono bg-surface rounded border border-recessed", children: "K" }),
              /* @__PURE__ */ jsx67("span", { children: "to toggle" })
            ] })
          ] })
        ]
      }
    )
  ] });
}
function CommandPaletteTrigger({ onClick, className = "" }) {
  const [isMac, setIsMac] = useState10(false);
  useEffect8(() => {
    if (typeof navigator !== "undefined") {
      setIsMac(navigator.platform?.includes("Mac") || navigator.userAgent?.includes("Mac"));
    }
  }, []);
  return /* @__PURE__ */ jsxs55(
    "button",
    {
      onClick,
      className: `flex items-center gap-2 px-2 sm:px-3 py-1.5 bg-elevated hover:bg-surface border border-recessed rounded-sm transition-colors ${className}`,
      "aria-label": "Open command palette",
      children: [
        /* @__PURE__ */ jsx67(SearchIcon2, { className: "w-4 h-4 text-tertiary", strokeWidth: 2 }),
        /* @__PURE__ */ jsx67("span", { className: "text-sm font-sans text-tertiary hidden sm:inline", children: "Search..." }),
        /* @__PURE__ */ jsxs55("kbd", { className: "hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-mono text-tertiary bg-surface border border-recessed rounded", children: [
          isMac ? "\u2318" : "Ctrl",
          /* @__PURE__ */ jsx67("span", { children: "K" })
        ] })
      ]
    }
  );
}

// src/shell/SplitPane.tsx
import {
  useCallback as useCallback7,
  useEffect as useEffect9,
  useLayoutEffect,
  useRef as useRef9,
  useState as useState11
} from "react";
import clsx4 from "clsx";
import { ChevronLeftIcon, ChevronRightIcon as ChevronRightIcon3, ChevronUpIcon, ChevronDownIcon as ChevronDownIcon2 } from "lucide-react";
import { jsx as jsx68, jsxs as jsxs56 } from "react/jsx-runtime";
var DEFAULT_SIZES = [50, 50];
var DEFAULT_SNAP_THRESHOLD = 30;
var DEFAULT_MOBILE_BREAKPOINT = 640;
var KEYBOARD_STEP = 2;
var COLLAPSED_SIZE_PX = 0;
function clampSizes(rawFirst, containerPx, minSize, maxSize) {
  const minFirstPx = minSize?.[0] ?? 0;
  const minSecondPx = minSize?.[1] ?? 0;
  const maxFirstPx = maxSize?.[0] ?? containerPx;
  const maxSecondPx = maxSize?.[1] ?? containerPx;
  let firstPx = rawFirst / 100 * containerPx;
  let secondPx = containerPx - firstPx;
  if (firstPx < minFirstPx) {
    firstPx = minFirstPx;
    secondPx = containerPx - firstPx;
  }
  if (secondPx < minSecondPx) {
    secondPx = minSecondPx;
    firstPx = containerPx - secondPx;
  }
  if (firstPx > maxFirstPx) {
    firstPx = maxFirstPx;
    secondPx = containerPx - firstPx;
  }
  if (secondPx > maxSecondPx) {
    secondPx = maxSecondPx;
    firstPx = containerPx - secondPx;
  }
  const firstPct = containerPx > 0 ? firstPx / containerPx * 100 : 50;
  const secondPct = 100 - firstPct;
  return [firstPct, secondPct];
}
function readStorage(key) {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length === 2 && typeof parsed[0] === "number" && typeof parsed[1] === "number") {
      return parsed;
    }
  } catch {
  }
  return null;
}
function writeStorage(key, sizes) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify(sizes));
  } catch {
  }
}
function resolveCollapsible(collapsible) {
  if (collapsible === void 0 || collapsible === false) return [false, false];
  if (collapsible === true) return [true, true];
  return collapsible;
}
function SplitPane({
  direction = "horizontal",
  defaultSizes,
  sizes: controlledSizes,
  onSizesChange,
  minSize,
  maxSize,
  collapsible,
  collapsed: controlledCollapsed,
  onCollapse,
  storageKey,
  snapThreshold = DEFAULT_SNAP_THRESHOLD,
  collapseOnMobile = false,
  mobileBreakpoint = DEFAULT_MOBILE_BREAKPOINT,
  className,
  handleClassName,
  children
}) {
  const containerRef = useRef9(null);
  const isDragging = useRef9(false);
  const rafId = useRef9(0);
  const startPos = useRef9(0);
  const startSizes = useRef9([50, 50]);
  const isControlled = controlledSizes !== void 0;
  const storedSizes = storageKey ? readStorage(storageKey) : null;
  const initialSizes = controlledSizes ?? storedSizes ?? defaultSizes ?? DEFAULT_SIZES;
  const [internalSizes, setInternalSizes] = useState11(initialSizes);
  const sizes = isControlled ? controlledSizes : internalSizes;
  const isCollapsedControlled = controlledCollapsed !== void 0;
  const [internalCollapsed, setInternalCollapsed] = useState11(null);
  const collapsed = isCollapsedControlled ? controlledCollapsed : internalCollapsed;
  const [collapsibleFirst, collapsibleSecond] = resolveCollapsible(collapsible);
  const setCollapsed = useCallback7(
    (pane) => {
      if (isCollapsedControlled) {
        onCollapse?.(pane);
      } else {
        setInternalCollapsed(pane);
        onCollapse?.(pane);
      }
    },
    [isCollapsedControlled, onCollapse]
  );
  const [isMobile, setIsMobile] = useState11(false);
  useEffect9(() => {
    if (!collapseOnMobile) return;
    const query = window.matchMedia(`(max-width: ${mobileBreakpoint - 1}px)`);
    const handler = (e) => {
      setIsMobile(e.matches);
    };
    handler(query);
    query.addEventListener("change", handler);
    return () => query.removeEventListener("change", handler);
  }, [collapseOnMobile, mobileBreakpoint]);
  const isStacked = collapseOnMobile && isMobile;
  const updateSizes = useCallback7(
    (newSizes) => {
      if (isControlled) {
        onSizesChange?.(newSizes);
      } else {
        setInternalSizes(newSizes);
        onSizesChange?.(newSizes);
      }
      if (storageKey) {
        writeStorage(storageKey, newSizes);
      }
    },
    [isControlled, onSizesChange, storageKey]
  );
  const getContainerSize = useCallback7(() => {
    if (!containerRef.current) return 0;
    return direction === "horizontal" ? containerRef.current.offsetWidth : containerRef.current.offsetHeight;
  }, [direction]);
  const onPointerMove = useCallback7(
    (clientPos) => {
      if (!isDragging.current) return;
      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        const containerPx = getContainerSize();
        if (containerPx === 0) return;
        const delta = clientPos - startPos.current;
        const deltaPct = delta / containerPx * 100;
        const rawFirst = startSizes.current[0] + deltaPct;
        const clamped = clampSizes(rawFirst, containerPx, minSize, maxSize);
        const firstPx = clamped[0] / 100 * containerPx;
        const secondPx = clamped[1] / 100 * containerPx;
        if (collapsibleFirst && firstPx < snapThreshold) {
          setCollapsed("first");
          return;
        }
        if (collapsibleSecond && secondPx < snapThreshold) {
          setCollapsed("second");
          return;
        }
        if (collapsed !== null) {
          setCollapsed(null);
        }
        updateSizes(clamped);
      });
    },
    [
      getContainerSize,
      minSize,
      maxSize,
      snapThreshold,
      collapsibleFirst,
      collapsibleSecond,
      collapsed,
      setCollapsed,
      updateSizes
    ]
  );
  const onPointerUp = useCallback7(() => {
    isDragging.current = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);
  const handleMouseMove = useCallback7(
    (e) => {
      onPointerMove(direction === "horizontal" ? e.clientX : e.clientY);
    },
    [direction, onPointerMove]
  );
  const handleTouchMove = useCallback7(
    (e) => {
      if (e.touches.length === 1) {
        onPointerMove(
          direction === "horizontal" ? e.touches[0].clientX : e.touches[0].clientY
        );
      }
    },
    [direction, onPointerMove]
  );
  const handleMouseUp = useCallback7(() => {
    onPointerUp();
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  }, [onPointerUp, handleMouseMove]);
  const handleTouchEnd = useCallback7(() => {
    onPointerUp();
    document.removeEventListener("touchmove", handleTouchMove);
    document.removeEventListener("touchend", handleTouchEnd);
  }, [onPointerUp, handleTouchMove]);
  const startDrag = useCallback7(
    (clientPos, isTouch) => {
      isDragging.current = true;
      startPos.current = clientPos;
      startSizes.current = [...sizes];
      document.body.style.cursor = direction === "horizontal" ? "col-resize" : "row-resize";
      document.body.style.userSelect = "none";
      if (isTouch) {
        document.addEventListener("touchmove", handleTouchMove, { passive: false });
        document.addEventListener("touchend", handleTouchEnd);
      } else {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
      }
    },
    [sizes, direction, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]
  );
  const onHandleMouseDown = useCallback7(
    (e) => {
      e.preventDefault();
      startDrag(direction === "horizontal" ? e.clientX : e.clientY, false);
    },
    [direction, startDrag]
  );
  const onHandleTouchStart = useCallback7(
    (e) => {
      if (e.touches.length === 1) {
        startDrag(
          direction === "horizontal" ? e.touches[0].clientX : e.touches[0].clientY,
          true
        );
      }
    },
    [direction, startDrag]
  );
  const onHandleDoubleClick = useCallback7(() => {
    if (!collapsibleFirst && !collapsibleSecond) return;
    if (collapsed !== null) {
      setCollapsed(null);
      return;
    }
    if (collapsibleFirst) {
      setCollapsed("first");
    } else if (collapsibleSecond) {
      setCollapsed("second");
    }
  }, [collapsed, collapsibleFirst, collapsibleSecond, setCollapsed]);
  const onHandleKeyDown = useCallback7(
    (e) => {
      const containerPx = getContainerSize();
      if (containerPx === 0) return;
      let newFirst = null;
      const increaseKey = direction === "horizontal" ? "ArrowRight" : "ArrowDown";
      const decreaseKey = direction === "horizontal" ? "ArrowLeft" : "ArrowUp";
      switch (e.key) {
        case increaseKey:
          e.preventDefault();
          if (collapsed === "first") {
            setCollapsed(null);
            return;
          }
          newFirst = sizes[0] + KEYBOARD_STEP;
          break;
        case decreaseKey:
          e.preventDefault();
          if (collapsed === "second") {
            setCollapsed(null);
            return;
          }
          newFirst = sizes[0] - KEYBOARD_STEP;
          break;
        case "Home":
          e.preventDefault();
          if (collapsibleFirst) {
            setCollapsed("first");
            return;
          }
          newFirst = minSize ? minSize[0] / containerPx * 100 : 0;
          break;
        case "End":
          e.preventDefault();
          if (collapsibleSecond) {
            setCollapsed("second");
            return;
          }
          newFirst = maxSize ? maxSize[0] / containerPx * 100 : 100;
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          onHandleDoubleClick();
          return;
        default:
          return;
      }
      if (newFirst !== null) {
        if (collapsed !== null) setCollapsed(null);
        const clamped = clampSizes(newFirst, containerPx, minSize, maxSize);
        updateSizes(clamped);
      }
    },
    [
      direction,
      sizes,
      collapsed,
      minSize,
      maxSize,
      collapsibleFirst,
      collapsibleSecond,
      getContainerSize,
      setCollapsed,
      updateSizes,
      onHandleDoubleClick
    ]
  );
  const toggleCollapse = useCallback7(
    (pane) => {
      setCollapsed(collapsed === pane ? null : pane);
    },
    [collapsed, setCollapsed]
  );
  useEffect9(() => {
    return () => {
      cancelAnimationFrame(rafId.current);
    };
  }, []);
  useLayoutEffect(() => {
    if (!isControlled && storageKey) {
      const stored = readStorage(storageKey);
      if (stored) {
        setInternalSizes(stored);
      }
    }
  }, []);
  const isHorizontal = direction === "horizontal";
  const computePaneStyles = () => {
    if (isStacked) {
      return [{}, {}];
    }
    if (collapsed === "first") {
      return [
        { [isHorizontal ? "width" : "height"]: COLLAPSED_SIZE_PX, overflow: "hidden" },
        { flex: 1 }
      ];
    }
    if (collapsed === "second") {
      return [
        { flex: 1 },
        { [isHorizontal ? "width" : "height"]: COLLAPSED_SIZE_PX, overflow: "hidden" }
      ];
    }
    return [
      { [isHorizontal ? "width" : "height"]: `${sizes[0]}%` },
      { [isHorizontal ? "width" : "height"]: `${sizes[1]}%` }
    ];
  };
  const [firstStyle, secondStyle] = computePaneStyles();
  const CollapseFirstIcon = isHorizontal ? ChevronLeftIcon : ChevronUpIcon;
  const CollapseSecondIcon = isHorizontal ? ChevronRightIcon3 : ChevronDownIcon2;
  const ExpandFirstIcon = isHorizontal ? ChevronRightIcon3 : ChevronDownIcon2;
  const ExpandSecondIcon = isHorizontal ? ChevronLeftIcon : ChevronUpIcon;
  if (isStacked) {
    return /* @__PURE__ */ jsxs56(
      "div",
      {
        ref: containerRef,
        className: clsx4("flex flex-col w-full h-full", className),
        "data-splitpane": true,
        "data-direction": direction,
        "data-stacked": "true",
        children: [
          /* @__PURE__ */ jsx68("div", { className: "flex-1 overflow-auto", children: children[0] }),
          /* @__PURE__ */ jsx68("div", { className: "flex-1 overflow-auto", children: children[1] })
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs56(
    "div",
    {
      ref: containerRef,
      className: clsx4(
        "flex w-full h-full relative",
        isHorizontal ? "flex-row" : "flex-col",
        className
      ),
      "data-splitpane": true,
      "data-direction": direction,
      children: [
        /* @__PURE__ */ jsx68(
          "div",
          {
            className: clsx4(
              "overflow-auto",
              collapsed === "first" && "sr-only"
            ),
            style: firstStyle,
            "data-splitpane-pane": "first",
            children: children[0]
          }
        ),
        /* @__PURE__ */ jsxs56(
          "div",
          {
            className: clsx4(
              "relative flex items-center justify-center shrink-0 group",
              "transition-colors duration-150",
              isHorizontal ? "w-1 cursor-col-resize hover:bg-accent/30 active:bg-accent/50" : "h-1 cursor-row-resize hover:bg-accent/30 active:bg-accent/50",
              "bg-recessed",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selection/40",
              handleClassName
            ),
            role: "separator",
            "aria-orientation": isHorizontal ? "vertical" : "horizontal",
            "aria-valuenow": Math.round(sizes[0]),
            "aria-valuemin": 0,
            "aria-valuemax": 100,
            "aria-label": isHorizontal ? "Resize panes horizontally. Use left/right arrow keys to adjust." : "Resize panes vertically. Use up/down arrow keys to adjust.",
            tabIndex: 0,
            onMouseDown: onHandleMouseDown,
            onTouchStart: onHandleTouchStart,
            onDoubleClick: onHandleDoubleClick,
            onKeyDown: onHandleKeyDown,
            "data-splitpane-handle": true,
            children: [
              /* @__PURE__ */ jsx68(
                "div",
                {
                  className: clsx4(
                    "absolute",
                    isHorizontal ? "inset-y-0 -left-1.5 -right-1.5" : "inset-x-0 -top-1.5 -bottom-1.5"
                  ),
                  "aria-hidden": "true"
                }
              ),
              (collapsibleFirst || collapsibleSecond) && /* @__PURE__ */ jsxs56(
                "div",
                {
                  className: clsx4(
                    "absolute z-10 flex gap-0.5",
                    isHorizontal ? "flex-col -left-3 -right-3 top-1/2 -translate-y-1/2" : "flex-row -top-3 -bottom-3 left-1/2 -translate-x-1/2"
                  ),
                  children: [
                    collapsibleFirst && /* @__PURE__ */ jsx68(
                      "button",
                      {
                        type: "button",
                        onClick: (e) => {
                          e.stopPropagation();
                          toggleCollapse("first");
                        },
                        className: clsx4(
                          "flex items-center justify-center",
                          "w-5 h-5 rounded-full",
                          "bg-surface border border-recessed shadow-sm",
                          "hover:bg-elevated transition-colors duration-150",
                          "opacity-0 group-hover:opacity-100 focus:opacity-100",
                          "focus:outline-none focus:ring-2 focus:ring-accent/40"
                        ),
                        "aria-label": collapsed === "first" ? "Expand first pane" : "Collapse first pane",
                        tabIndex: -1,
                        children: collapsed === "first" ? /* @__PURE__ */ jsx68(ExpandFirstIcon, { className: "w-3 h-3 text-tertiary", strokeWidth: 2 }) : /* @__PURE__ */ jsx68(CollapseFirstIcon, { className: "w-3 h-3 text-tertiary", strokeWidth: 2 })
                      }
                    ),
                    collapsibleSecond && /* @__PURE__ */ jsx68(
                      "button",
                      {
                        type: "button",
                        onClick: (e) => {
                          e.stopPropagation();
                          toggleCollapse("second");
                        },
                        className: clsx4(
                          "flex items-center justify-center",
                          "w-5 h-5 rounded-full",
                          "bg-surface border border-recessed shadow-sm",
                          "hover:bg-elevated transition-colors duration-150",
                          "opacity-0 group-hover:opacity-100 focus:opacity-100",
                          "focus:outline-none focus:ring-2 focus:ring-accent/40"
                        ),
                        "aria-label": collapsed === "second" ? "Expand second pane" : "Collapse second pane",
                        tabIndex: -1,
                        children: collapsed === "second" ? /* @__PURE__ */ jsx68(ExpandSecondIcon, { className: "w-3 h-3 text-tertiary", strokeWidth: 2 }) : /* @__PURE__ */ jsx68(CollapseSecondIcon, { className: "w-3 h-3 text-tertiary", strokeWidth: 2 })
                      }
                    )
                  ]
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx68(
          "div",
          {
            className: clsx4(
              "overflow-auto",
              collapsed === "second" && "sr-only"
            ),
            style: secondStyle,
            "data-splitpane-pane": "second",
            children: children[1]
          }
        )
      ]
    }
  );
}

// src/shell/TreeNavItem.tsx
import React20, { useCallback as useCallback8, useRef as useRef10, useState as useState12, useEffect as useEffect10 } from "react";
import { createPortal as createPortal2 } from "react-dom";
import { ChevronDownIcon as ChevronDownIcon3, ChevronRightIcon as ChevronRightIcon4 } from "lucide-react";
import { Fragment as Fragment14, jsx as jsx69, jsxs as jsxs57 } from "react/jsx-runtime";
function TreeNavItem({
  id,
  label,
  icon: Icon,
  badge,
  description,
  keywords,
  href,
  onClick,
  active: activeProp,
  disabled = false,
  children,
  actions,
  hoverActions,
  truncateAt,
  className = ""
}) {
  const {
    toggleItem,
    expandItem,
    collapseItem,
    setActiveItem,
    isExpanded,
    isActive,
    size,
    matchesSearch
  } = useTreeNav();
  const level = useTreeNavLevel();
  const hasChildren = React20.Children.count(children) > 0;
  const expanded = isExpanded(id);
  const active = activeProp !== void 0 ? activeProp : isActive(id);
  const visible = matchesSearch(label, keywords);
  const paddingLeft = size === "sm" ? 8 + level * 12 : 12 + level * 16;
  const labelRef = useRef10(null);
  const [isTruncated, setIsTruncated] = useState12(false);
  const [showTooltip, setShowTooltip] = useState12(false);
  const [tooltipPos, setTooltipPos] = useState12({ top: 0, left: 0 });
  useEffect10(() => {
    const el = labelRef.current;
    if (!el || !truncateAt) {
      setIsTruncated(false);
      return;
    }
    setIsTruncated(el.scrollWidth > el.clientWidth);
  }, [label, truncateAt]);
  const handleLabelMouseEnter = useCallback8(() => {
    if (!isTruncated || !labelRef.current) return;
    const rect = labelRef.current.getBoundingClientRect();
    setTooltipPos({
      top: rect.bottom + 4,
      left: rect.left
    });
    setShowTooltip(true);
  }, [isTruncated]);
  const handleLabelMouseLeave = useCallback8(() => {
    setShowTooltip(false);
  }, []);
  const handleClick = useCallback8(() => {
    if (disabled) return;
    if (hasChildren) {
      toggleItem(id);
    } else {
      setActiveItem(id);
      onClick?.();
    }
  }, [disabled, hasChildren, toggleItem, id, setActiveItem, onClick]);
  const handleKeyDown = useCallback8((e) => {
    if (disabled) return;
    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        handleClick();
        break;
      case "ArrowRight":
        if (hasChildren) {
          e.preventDefault();
          e.stopPropagation();
          if (!expanded) {
            expandItem(id);
          }
        }
        break;
      case "ArrowLeft":
        if (hasChildren && expanded) {
          e.preventDefault();
          e.stopPropagation();
          collapseItem(id);
        }
        break;
    }
  }, [disabled, handleClick, hasChildren, expanded, expandItem, collapseItem, id]);
  if (!visible) {
    return null;
  }
  const sizeClasses4 = size === "sm" ? "py-1.5 text-xs" : "py-2 text-sm";
  const stateClasses = active ? "text-primary font-medium bg-elevated border-l-2 border-tertiary" : disabled ? "text-tertiary cursor-not-allowed border-l-2 border-transparent" : "text-secondary hover:text-primary hover:bg-elevated border-l-2 border-transparent";
  const renderActions = () => {
    if (actions && actions.length > 0) {
      return /* @__PURE__ */ jsx69(
        "span",
        {
          className: "flex-shrink-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity",
          onClick: (e) => e.stopPropagation(),
          onKeyDown: (e) => e.stopPropagation(),
          children: actions.map((action) => {
            const ActionIcon = action.icon;
            return /* @__PURE__ */ jsx69(
              "button",
              {
                className: "p-0.5 rounded hover:bg-surface transition-colors",
                onClick: (e) => {
                  e.stopPropagation();
                  action.onClick();
                },
                "aria-label": action.label,
                title: action.label,
                tabIndex: -1,
                children: /* @__PURE__ */ jsx69(
                  ActionIcon,
                  {
                    className: "w-3.5 h-3.5 text-tertiary hover:text-primary",
                    strokeWidth: 2
                  }
                )
              },
              action.id
            );
          })
        }
      );
    }
    if (hoverActions) {
      return /* @__PURE__ */ jsx69(
        "span",
        {
          className: "flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity",
          onClick: (e) => e.stopPropagation(),
          children: hoverActions
        }
      );
    }
    return null;
  };
  const labelStyle = truncateAt ? { maxWidth: `${truncateAt}px` } : {};
  const itemContent = /* @__PURE__ */ jsxs57(
    "div",
    {
      role: "treeitem",
      tabIndex: disabled ? -1 : 0,
      "aria-expanded": hasChildren ? expanded : void 0,
      "aria-selected": active,
      "aria-current": active ? "page" : void 0,
      "aria-disabled": disabled || void 0,
      "data-tree-item-id": id,
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      style: { paddingLeft: `${paddingLeft}px` },
      className: `
        group relative flex items-center gap-2 pr-3 font-sans cursor-pointer
        transition-colors select-none outline-none
        focus-visible:ring-2 focus-visible:ring-tertiary/40 focus-visible:ring-inset
        ${sizeClasses4}
        ${stateClasses}
        ${className}
      `,
      children: [
        hasChildren ? /* @__PURE__ */ jsx69("span", { className: "flex-shrink-0 w-4 h-4 flex items-center justify-center", children: expanded ? /* @__PURE__ */ jsx69(ChevronDownIcon3, { className: "w-3.5 h-3.5", strokeWidth: 2 }) : /* @__PURE__ */ jsx69(ChevronRightIcon4, { className: "w-3.5 h-3.5", strokeWidth: 2 }) }) : /* @__PURE__ */ jsx69("span", { className: "flex-shrink-0 w-4 h-4", "aria-hidden": "true" }),
        Icon && /* @__PURE__ */ jsx69(
          Icon,
          {
            className: `flex-shrink-0 ${size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4"}`,
            strokeWidth: active ? 2 : 1.5
          }
        ),
        /* @__PURE__ */ jsxs57("span", { className: "flex-1 min-w-0", children: [
          /* @__PURE__ */ jsx69(
            "span",
            {
              ref: labelRef,
              className: "block truncate",
              style: labelStyle,
              onMouseEnter: handleLabelMouseEnter,
              onMouseLeave: handleLabelMouseLeave,
              children: label
            }
          ),
          description && /* @__PURE__ */ jsx69("span", { className: "block truncate text-tertiary text-caption-xs leading-tight mt-0.5", children: description })
        ] }),
        badge && /* @__PURE__ */ jsx69("span", { className: "flex-shrink-0", children: badge }),
        renderActions(),
        showTooltip && isTruncated && typeof document !== "undefined" && createPortal2(
          /* @__PURE__ */ jsx69(
            "div",
            {
              className: "fixed z-[9999] px-2.5 py-1.5 text-xs font-sans text-primary bg-surface border border-recessed rounded-sm shadow-md pointer-events-none animate-fade-in",
              style: {
                top: tooltipPos.top,
                left: tooltipPos.left,
                maxWidth: 320
              },
              role: "tooltip",
              children: label
            }
          ),
          document.body
        )
      ]
    }
  );
  const item = href && !disabled && !hasChildren ? /* @__PURE__ */ jsx69("a", { href, className: "block no-underline", children: itemContent }) : itemContent;
  return /* @__PURE__ */ jsxs57(Fragment14, { children: [
    item,
    hasChildren && expanded && /* @__PURE__ */ jsx69(TreeNavLevelContext.Provider, { value: level + 1, children: /* @__PURE__ */ jsx69("div", { role: "group", "aria-label": `${label} items`, children }) })
  ] });
}

// src/shell/TreeNavSection.tsx
import { useState as useState13, useCallback as useCallback9 } from "react";
import { ChevronDownIcon as ChevronDownIcon4, ChevronRightIcon as ChevronRightIcon5 } from "lucide-react";
import { jsx as jsx70, jsxs as jsxs58 } from "react/jsx-runtime";
function TreeNavSection({
  id,
  label,
  icon: Icon,
  children,
  collapsible = true,
  defaultExpanded,
  defaultCollapsed = false,
  badge,
  className = ""
}) {
  const { size } = useTreeNav();
  const initialCollapsed = defaultExpanded !== void 0 ? !defaultExpanded : defaultCollapsed;
  const [collapsed, setCollapsed] = useState13(initialCollapsed);
  const toggleCollapsed = useCallback9(() => {
    if (collapsible) {
      setCollapsed((prev) => !prev);
    }
  }, [collapsible]);
  const handleKeyDown = useCallback9((e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleCollapsed();
    }
  }, [toggleCollapsed]);
  const headerPadding = size === "sm" ? "px-2 py-1.5" : "px-3 py-2";
  const textSize = size === "sm" ? "text-caption-xs" : "text-caption-xs";
  return /* @__PURE__ */ jsxs58(
    "div",
    {
      className,
      role: "group",
      "aria-label": label,
      "data-tree-section-id": id,
      children: [
        /* @__PURE__ */ jsxs58(
          "div",
          {
            className: `
          flex items-center gap-2 font-sans font-semibold uppercase tracking-[0.1em] text-tertiary
          ${headerPadding} ${textSize}
          ${collapsible ? "cursor-pointer hover:text-secondary select-none" : ""}
        `,
            onClick: toggleCollapsed,
            onKeyDown: handleKeyDown,
            tabIndex: collapsible ? 0 : void 0,
            role: collapsible ? "button" : void 0,
            "aria-expanded": collapsible ? !collapsed : void 0,
            children: [
              collapsible && /* @__PURE__ */ jsx70("span", { className: "flex-shrink-0 w-3 h-3 flex items-center justify-center", children: collapsed ? /* @__PURE__ */ jsx70(ChevronRightIcon5, { className: "w-3 h-3", strokeWidth: 2 }) : /* @__PURE__ */ jsx70(ChevronDownIcon4, { className: "w-3 h-3", strokeWidth: 2 }) }),
              Icon && /* @__PURE__ */ jsx70(Icon, { className: "w-3.5 h-3.5 flex-shrink-0", strokeWidth: 2 }),
              /* @__PURE__ */ jsx70("span", { className: "flex-1", children: label }),
              badge && /* @__PURE__ */ jsx70("span", { className: "flex-shrink-0", children: badge })
            ]
          }
        ),
        !collapsed && /* @__PURE__ */ jsx70("div", { className: "mt-0.5", children })
      ]
    }
  );
}

// src/shell/TreeNavDivider.tsx
import { jsx as jsx71, jsxs as jsxs59 } from "react/jsx-runtime";
function TreeNavDivider({
  label,
  className = ""
}) {
  if (label) {
    return /* @__PURE__ */ jsxs59(
      "div",
      {
        className: `flex items-center gap-2 px-3 py-2 ${className}`,
        role: "separator",
        "aria-label": label,
        children: [
          /* @__PURE__ */ jsx71("div", { className: "flex-1 h-px bg-recessed" }),
          /* @__PURE__ */ jsx71("span", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary", children: label }),
          /* @__PURE__ */ jsx71("div", { className: "flex-1 h-px bg-recessed" })
        ]
      }
    );
  }
  return /* @__PURE__ */ jsx71(
    "div",
    {
      className: `px-3 py-1.5 ${className}`,
      role: "separator",
      children: /* @__PURE__ */ jsx71("div", { className: "h-px bg-recessed" })
    }
  );
}

// src/shell/OrgSwitcher.tsx
import { useState as useState14, useCallback as useCallback10, useRef as useRef11, useEffect as useEffect11 } from "react";
import { BuildingIcon, ChevronDownIcon as ChevronDownIcon5, CheckIcon as CheckIcon3, PlusIcon, SearchIcon as SearchIcon3 } from "lucide-react";
import { jsx as jsx72, jsxs as jsxs60 } from "react/jsx-runtime";
function OrgSwitcher({
  currentOrg,
  organizations,
  onOrgChange,
  onCreateNew,
  showCreateNew = false,
  searchable = true,
  searchThreshold = 5,
  searchPlaceholder = "Search organizations...",
  className = "",
  impersonating = false
}) {
  const [isOpen, setIsOpen] = useState14(false);
  const [searchQuery, setSearchQuery] = useState14("");
  const containerRef = useRef11(null);
  const triggerRef = useRef11(null);
  const listboxRef = useRef11(null);
  const searchInputRef = useRef11(null);
  const filteredOrgs = searchQuery ? organizations.filter(
    (org) => org.name.toLowerCase().includes(searchQuery.toLowerCase()) || org.type?.toLowerCase().includes(searchQuery.toLowerCase())
  ) : organizations;
  const showSearch = searchable && organizations.length > searchThreshold;
  useEffect11(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);
  useEffect11(() => {
    if (isOpen && showSearch) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    } else if (isOpen && listboxRef.current) {
      const firstOption = listboxRef.current.querySelector('[role="option"]');
      firstOption?.focus();
    }
  }, [isOpen, showSearch]);
  const handleListboxKeyDown = (e) => {
    if (!listboxRef.current) return;
    const options = Array.from(
      listboxRef.current.querySelectorAll('[role="option"]')
    );
    if (!options.length) return;
    const currentIndex = options.indexOf(document.activeElement);
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        options[(currentIndex + 1) % options.length]?.focus();
        break;
      case "ArrowUp":
        e.preventDefault();
        options[(currentIndex - 1 + options.length) % options.length]?.focus();
        break;
      case "Home":
        e.preventDefault();
        options[0]?.focus();
        break;
      case "End":
        e.preventDefault();
        options[options.length - 1]?.focus();
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSearchQuery("");
        triggerRef.current?.focus();
        break;
      case "Tab":
        setIsOpen(false);
        setSearchQuery("");
        break;
    }
  };
  const handleSelect = useCallback10((org) => {
    onOrgChange?.(org);
    setIsOpen(false);
    setSearchQuery("");
  }, [onOrgChange]);
  const toggle = useCallback10(() => {
    setIsOpen((prev) => !prev);
    if (isOpen) {
      setSearchQuery("");
    }
  }, [isOpen]);
  return /* @__PURE__ */ jsxs60("div", { ref: containerRef, className: `relative ${className}`, children: [
    /* @__PURE__ */ jsxs60(
      "button",
      {
        ref: triggerRef,
        onClick: toggle,
        onKeyDown: (e) => {
          if (e.key === "ArrowDown" && !isOpen) {
            e.preventDefault();
            setIsOpen(true);
          }
        },
        className: "flex items-center gap-2 px-3 py-2 rounded-sm hover:bg-elevated transition-colors",
        "aria-expanded": isOpen,
        "aria-haspopup": "listbox",
        children: [
          currentOrg.logo ? /* @__PURE__ */ jsx72("img", { src: currentOrg.logo, alt: "", className: "w-5 h-5 rounded object-cover" }) : /* @__PURE__ */ jsx72(BuildingIcon, { className: "w-4 h-4 text-tertiary", strokeWidth: 2 }),
          /* @__PURE__ */ jsx72(
            "span",
            {
              className: `text-sm font-sans font-medium max-w-[200px] truncate ${impersonating ? "text-accent" : "text-primary"}`,
              children: currentOrg.name
            }
          ),
          /* @__PURE__ */ jsx72(
            ChevronDownIcon5,
            {
              className: `w-4 h-4 text-tertiary transition-transform ${isOpen ? "rotate-180" : ""}`,
              strokeWidth: 2
            }
          )
        ]
      }
    ),
    isOpen && /* @__PURE__ */ jsxs60(
      "div",
      {
        ref: listboxRef,
        className: "absolute top-full left-0 mt-1 w-[280px] bg-surface border border-recessed rounded-sm shadow-lg z-50 animate-scale-in",
        role: "listbox",
        "aria-label": "Select organization",
        onKeyDown: handleListboxKeyDown,
        children: [
          showSearch && /* @__PURE__ */ jsx72("div", { className: "p-2 border-b border-recessed", children: /* @__PURE__ */ jsxs60("div", { className: "relative", children: [
            /* @__PURE__ */ jsx72(
              SearchIcon3,
              {
                className: "absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary pointer-events-none",
                strokeWidth: 2
              }
            ),
            /* @__PURE__ */ jsx72(
              "input",
              {
                ref: searchInputRef,
                type: "text",
                value: searchQuery,
                onChange: (e) => setSearchQuery(e.target.value),
                placeholder: searchPlaceholder,
                className: "w-full pl-8 pr-3 py-1.5 text-sm font-sans bg-elevated border border-recessed rounded-sm text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-tertiary/30 focus:border-secondary",
                "aria-label": "Search organizations"
              }
            )
          ] }) }),
          /* @__PURE__ */ jsx72("div", { className: "px-3 py-2 border-b border-recessed", children: /* @__PURE__ */ jsx72("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary", children: "Switch Organization" }) }),
          /* @__PURE__ */ jsx72("div", { className: "max-h-[300px] overflow-y-auto p-2", children: filteredOrgs.length === 0 ? /* @__PURE__ */ jsx72("div", { className: "px-3 py-4 text-center", children: /* @__PURE__ */ jsx72("p", { className: "text-sm font-sans text-tertiary", children: "No organizations found" }) }) : filteredOrgs.map((org) => {
            const isSelected = org.id === currentOrg.id;
            return /* @__PURE__ */ jsxs60(
              "button",
              {
                onClick: () => handleSelect(org),
                className: `
                      w-full text-left px-3 py-2 rounded-sm transition-colors
                      flex items-center gap-3 focus:outline-none
                      ${isSelected ? "bg-elevated" : "hover:bg-elevated focus:bg-elevated"}
                    `,
                role: "option",
                "aria-selected": isSelected,
                tabIndex: -1,
                children: [
                  org.logo ? /* @__PURE__ */ jsx72("img", { src: org.logo, alt: "", className: "w-6 h-6 rounded object-cover flex-shrink-0" }) : /* @__PURE__ */ jsx72("div", { className: "w-6 h-6 bg-elevated rounded flex items-center justify-center flex-shrink-0", children: /* @__PURE__ */ jsx72(BuildingIcon, { className: "w-3.5 h-3.5 text-tertiary", strokeWidth: 2 }) }),
                  /* @__PURE__ */ jsxs60("div", { className: "flex-1 min-w-0", children: [
                    /* @__PURE__ */ jsx72("div", { className: "text-sm font-sans font-medium truncate text-primary", children: org.name }),
                    org.type && /* @__PURE__ */ jsx72("div", { className: "text-xs font-sans text-tertiary capitalize", children: org.type.replace(/_/g, " ") })
                  ] }),
                  isSelected && /* @__PURE__ */ jsx72(CheckIcon3, { className: "w-4 h-4 text-secondary flex-shrink-0", strokeWidth: 2 })
                ]
              },
              org.id
            );
          }) }),
          showCreateNew && onCreateNew && /* @__PURE__ */ jsx72("div", { className: "p-2 border-t border-recessed", children: /* @__PURE__ */ jsxs60(
            "button",
            {
              onClick: () => {
                onCreateNew();
                setIsOpen(false);
              },
              className: "w-full flex items-center gap-3 px-3 py-2 rounded-sm hover:bg-elevated transition-colors text-left",
              children: [
                /* @__PURE__ */ jsx72("div", { className: "w-6 h-6 bg-accent/10 rounded flex items-center justify-center flex-shrink-0", children: /* @__PURE__ */ jsx72(PlusIcon, { className: "w-3.5 h-3.5 text-accent", strokeWidth: 2 }) }),
                /* @__PURE__ */ jsx72("span", { className: "text-sm font-sans font-medium text-accent", children: "Create New Organization" })
              ]
            }
          ) })
        ]
      }
    )
  ] });
}

// src/shell/OrgSwitcherDialog.tsx
import { useMemo as useMemo7, useState as useState15 } from "react";
import { BuildingIcon as BuildingIcon2, CheckIcon as CheckIcon4, SearchIcon as SearchIcon4 } from "lucide-react";

// src/overlays/Dialog.tsx
import {
  Modal,
  ModalOverlay,
  Dialog as AriaDialog,
  Heading as Heading3,
  Button as Button2
} from "react-aria-components";
import { X } from "lucide-react";
import { clsx as clsx5 } from "clsx";
import { Fragment as Fragment15, jsx as jsx73, jsxs as jsxs61 } from "react/jsx-runtime";
var sizeClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  full: "max-w-4xl"
};
function Dialog({
  open,
  onOpenChange,
  title,
  children,
  size = "md",
  showCloseButton = true,
  isDismissable = true,
  isKeyboardDismissDisabled = false,
  footer,
  className
}) {
  return /* @__PURE__ */ jsx73(
    ModalOverlay,
    {
      isOpen: open,
      onOpenChange,
      isDismissable,
      isKeyboardDismissDisabled,
      className: clsx5(
        "fixed inset-0 z-50 flex items-center justify-center",
        "bg-black/40 backdrop-blur-sm",
        "data-[entering]:animate-in data-[entering]:fade-in",
        "data-[exiting]:animate-out data-[exiting]:fade-out"
      ),
      children: /* @__PURE__ */ jsx73(
        Modal,
        {
          className: clsx5(
            "w-full mx-4",
            sizeClasses[size],
            "data-[entering]:animate-in data-[entering]:zoom-in-95 data-[entering]:fade-in",
            "data-[exiting]:animate-out data-[exiting]:zoom-out-95 data-[exiting]:fade-out"
          ),
          children: /* @__PURE__ */ jsx73(
            AriaDialog,
            {
              className: clsx5(
                "bg-surface border border-recessed rounded-sm shadow-lg",
                "outline-none",
                className
              ),
              children: ({ close }) => /* @__PURE__ */ jsxs61(Fragment15, { children: [
                (title || showCloseButton) && /* @__PURE__ */ jsxs61("div", { className: "flex items-center justify-between px-6 py-4 border-b border-recessed", children: [
                  title && /* @__PURE__ */ jsx73(
                    Heading3,
                    {
                      slot: "title",
                      className: "text-xl font-sans font-bold text-primary leading-tight",
                      children: title
                    }
                  ),
                  showCloseButton && /* @__PURE__ */ jsx73(
                    Button2,
                    {
                      onPress: close,
                      className: clsx5(
                        "p-1.5 rounded-sm",
                        "text-tertiary hover:text-primary",
                        "hover:bg-canvas",
                        "transition-colors duration-150",
                        "outline-none focus-visible:ring-2 focus-visible:ring-accent"
                      ),
                      "aria-label": "Close dialog",
                      children: /* @__PURE__ */ jsx73(X, { className: "w-5 h-5" })
                    }
                  )
                ] }),
                /* @__PURE__ */ jsx73("div", { className: "px-6 py-4 text-secondary", children }),
                footer && /* @__PURE__ */ jsx73("div", { className: "flex items-center justify-end gap-3 px-6 py-4 border-t border-recessed bg-canvas/50", children: footer })
              ] })
            }
          )
        }
      )
    }
  );
}

// src/shell/OrgSwitcherDialog.tsx
import { jsx as jsx74, jsxs as jsxs62 } from "react/jsx-runtime";
function OrgSwitcherDialog({
  open,
  onOpenChange,
  currentOrg,
  organizations,
  onOrgChange,
  searchPlaceholder = "Search organizations...",
  maxResults = 50
}) {
  const [query, setQuery] = useState15("");
  const matches = useMemo7(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return organizations;
    return organizations.filter(
      (org) => org.name.toLowerCase().includes(needle) || (org.type ?? "").toLowerCase().includes(needle)
    );
  }, [organizations, query]);
  const shown = matches.slice(0, maxResults);
  const handleOpenChange = (next) => {
    if (!next) setQuery("");
    onOpenChange(next);
  };
  const choose = (org) => {
    if (org.id !== currentOrg.id) onOrgChange(org);
    handleOpenChange(false);
  };
  return /* @__PURE__ */ jsx74(Dialog, { open, onOpenChange: handleOpenChange, title: "Switch organization", size: "md", children: /* @__PURE__ */ jsxs62("div", { className: "flex flex-col gap-3", children: [
    /* @__PURE__ */ jsxs62("div", { className: "relative", children: [
      /* @__PURE__ */ jsx74(
        SearchIcon4,
        {
          className: "absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary pointer-events-none",
          strokeWidth: 2
        }
      ),
      /* @__PURE__ */ jsx74(
        "input",
        {
          type: "text",
          value: query,
          onChange: (e) => setQuery(e.target.value),
          onKeyDown: (e) => {
            if (e.key === "Enter" && shown.length > 0) {
              e.preventDefault();
              choose(shown[0]);
            }
          },
          placeholder: searchPlaceholder,
          "aria-label": "Search organizations",
          autoFocus: true,
          className: "w-full pl-8 pr-3 py-2 text-sm font-sans bg-elevated border border-recessed rounded-sm text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-tertiary/30 focus:border-secondary"
        }
      )
    ] }),
    matches.length > shown.length && /* @__PURE__ */ jsxs62("p", { className: "text-xs font-sans text-tertiary", children: [
      "Showing ",
      shown.length,
      " of ",
      matches.length,
      " organizations. Type to narrow the list."
    ] }),
    shown.length === 0 ? /* @__PURE__ */ jsxs62("p", { className: "px-3 py-4 text-center text-sm font-sans text-tertiary", children: [
      'No organizations match "',
      query.trim(),
      '".'
    ] }) : /* @__PURE__ */ jsx74("ul", { className: "max-h-[60vh] overflow-y-auto -mx-1", "aria-label": "Organizations", children: shown.map((org) => {
      const isCurrent = org.id === currentOrg.id;
      return /* @__PURE__ */ jsx74("li", { children: /* @__PURE__ */ jsxs62(
        "button",
        {
          type: "button",
          onClick: () => choose(org),
          "aria-current": isCurrent ? "true" : void 0,
          className: `
                      w-full text-left px-3 py-2 rounded-sm transition-colors
                      flex items-center gap-3 focus:outline-none focus:bg-elevated
                      ${isCurrent ? "bg-elevated" : "hover:bg-elevated"}
                    `,
          children: [
            org.logo ? /* @__PURE__ */ jsx74("img", { src: org.logo, alt: "", className: "w-6 h-6 rounded object-cover flex-shrink-0" }) : /* @__PURE__ */ jsx74("div", { className: "w-6 h-6 bg-elevated rounded flex items-center justify-center flex-shrink-0", children: /* @__PURE__ */ jsx74(BuildingIcon2, { className: "w-3.5 h-3.5 text-tertiary", strokeWidth: 2 }) }),
            /* @__PURE__ */ jsxs62("div", { className: "flex-1 min-w-0", children: [
              /* @__PURE__ */ jsx74("div", { className: "text-sm font-sans font-medium truncate text-primary", children: org.name }),
              org.type && /* @__PURE__ */ jsx74("div", { className: "text-xs font-sans text-tertiary capitalize", children: org.type.replace(/_/g, " ") })
            ] }),
            isCurrent && /* @__PURE__ */ jsx74(CheckIcon4, { className: "w-4 h-4 text-secondary flex-shrink-0", strokeWidth: 2 })
          ]
        }
      ) }, org.id);
    }) })
  ] }) });
}

// src/shell/UserProfileMenu.tsx
import { useState as useState16, useRef as useRef12, useEffect as useEffect12 } from "react";
import { ArrowLeftRightIcon, BuildingIcon as BuildingIcon3, ChevronDownIcon as ChevronDownIcon6, LogOutIcon } from "lucide-react";
import { jsx as jsx75, jsxs as jsxs63 } from "react/jsx-runtime";
function UserProfileMenu({
  user,
  menuItems = [],
  onSignOut,
  showEmail = true,
  showRole = true,
  footer,
  className = "",
  impersonating = false,
  orgSection
}) {
  const [isOpen, setIsOpen] = useState16(false);
  const containerRef = useRef12(null);
  const triggerRef = useRef12(null);
  const menuRef = useRef12(null);
  const getInitials2 = (name) => {
    return name.split(" ").map((part) => part[0]).join("").toUpperCase().slice(0, 2);
  };
  const initials = user.initials || getInitials2(user.name);
  const actionableItems = menuItems.filter(
    (item) => Boolean(item.href) || typeof item.onClick === "function"
  );
  useEffect12(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);
  useEffect12(() => {
    if (isOpen && menuRef.current) {
      const firstItem = menuRef.current.querySelector('[role="menuitem"]');
      firstItem?.focus();
    }
  }, [isOpen]);
  const handleMenuKeyDown = (e) => {
    if (!menuRef.current) return;
    const items = Array.from(
      menuRef.current.querySelectorAll('[role="menuitem"]')
    );
    if (!items.length) return;
    const currentIndex = items.indexOf(document.activeElement);
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        items[(currentIndex + 1) % items.length]?.focus();
        break;
      case "ArrowUp":
        e.preventDefault();
        items[(currentIndex - 1 + items.length) % items.length]?.focus();
        break;
      case "Home":
        e.preventDefault();
        items[0]?.focus();
        break;
      case "End":
        e.preventDefault();
        items[items.length - 1]?.focus();
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        triggerRef.current?.focus();
        break;
      case "Tab":
        setIsOpen(false);
        break;
    }
  };
  const handleItemClick = (item) => {
    item.onClick?.();
    setIsOpen(false);
  };
  return /* @__PURE__ */ jsxs63("div", { ref: containerRef, className: `relative ${className}`, children: [
    /* @__PURE__ */ jsxs63(
      "button",
      {
        ref: triggerRef,
        onClick: () => setIsOpen((prev) => !prev),
        onKeyDown: (e) => {
          if (e.key === "ArrowDown" && !isOpen) {
            e.preventDefault();
            setIsOpen(true);
          }
        },
        className: "flex items-center gap-2 px-2 sm:px-3 py-2 rounded-sm hover:bg-elevated transition-colors",
        "aria-expanded": isOpen,
        "aria-haspopup": "menu",
        children: [
          user.avatar ? /* @__PURE__ */ jsx75(
            "img",
            {
              src: user.avatar,
              alt: user.name,
              className: "w-8 h-8 rounded-full object-cover"
            }
          ) : /* @__PURE__ */ jsx75("div", { className: "w-8 h-8 bg-accent rounded-full flex items-center justify-center", children: /* @__PURE__ */ jsx75("span", { className: "text-xs font-sans font-semibold text-white", children: initials }) }),
          /* @__PURE__ */ jsx75("div", { className: "hidden md:block text-left", children: /* @__PURE__ */ jsx75(
            "div",
            {
              className: `text-sm font-sans font-medium text-primary transition-opacity ${impersonating ? "opacity-60" : "opacity-100"}`,
              children: user.name
            }
          ) }),
          /* @__PURE__ */ jsx75(
            ChevronDownIcon6,
            {
              "aria-hidden": "true",
              className: `hidden sm:block w-4 h-4 text-tertiary transition-transform ${isOpen ? "rotate-180" : ""}`,
              strokeWidth: 2
            }
          )
        ]
      }
    ),
    isOpen && /* @__PURE__ */ jsxs63(
      "div",
      {
        ref: menuRef,
        className: "absolute top-full right-0 mt-1 w-56 bg-surface border border-recessed rounded-sm shadow-lg z-50 animate-scale-in",
        role: "menu",
        "aria-label": "User menu",
        onKeyDown: handleMenuKeyDown,
        children: [
          /* @__PURE__ */ jsxs63("div", { className: "p-4 border-b border-recessed", children: [
            /* @__PURE__ */ jsx75("div", { className: "text-sm font-sans font-medium text-primary", children: user.name }),
            showEmail && user.email && /* @__PURE__ */ jsx75("div", { className: "text-xs font-sans text-tertiary mt-0.5", children: user.email }),
            showRole && user.role && /* @__PURE__ */ jsx75("div", { className: "text-xs font-sans text-secondary mt-1", children: user.role })
          ] }),
          orgSection && /* @__PURE__ */ jsxs63(
            "div",
            {
              className: `p-2 border-b border-recessed ${orgSection.className ?? ""}`,
              role: "group",
              "aria-label": "Organization",
              children: [
                /* @__PURE__ */ jsxs63("div", { className: "flex items-center gap-3 px-3 pt-1 pb-2", children: [
                  /* @__PURE__ */ jsx75(BuildingIcon3, { className: "w-4 h-4 text-tertiary flex-shrink-0", strokeWidth: 2 }),
                  /* @__PURE__ */ jsxs63("div", { className: "min-w-0", children: [
                    /* @__PURE__ */ jsx75("div", { className: "text-xs font-sans text-tertiary", children: "Organization" }),
                    /* @__PURE__ */ jsx75("div", { className: "text-sm font-sans text-primary truncate", children: orgSection.currentOrg.name })
                  ] })
                ] }),
                /* @__PURE__ */ jsxs63(
                  "button",
                  {
                    type: "button",
                    onClick: () => {
                      setIsOpen(false);
                      orgSection.onSwitchRequest();
                    },
                    className: "w-full flex items-center gap-3 px-3 py-2 rounded-sm hover:bg-elevated focus:bg-elevated focus:outline-none transition-colors text-left",
                    role: "menuitem",
                    tabIndex: -1,
                    children: [
                      /* @__PURE__ */ jsx75(ArrowLeftRightIcon, { className: "w-4 h-4 text-tertiary flex-shrink-0", strokeWidth: 2 }),
                      /* @__PURE__ */ jsx75("span", { className: "text-sm font-sans text-primary", children: "Switch organization" })
                    ]
                  }
                )
              ]
            }
          ),
          actionableItems.length > 0 && /* @__PURE__ */ jsx75("div", { className: "p-2", children: actionableItems.map((item) => {
            const Icon = item.icon;
            const Element = item.href ? "a" : "button";
            return /* @__PURE__ */ jsxs63(
              Element,
              {
                href: item.href,
                onClick: () => handleItemClick(item),
                className: `
                      w-full flex items-center gap-3 px-3 py-2 rounded-sm
                      hover:bg-elevated focus:bg-elevated focus:outline-none transition-colors text-left
                      ${item.destructive ? "text-accent" : ""}
                    `,
                role: "menuitem",
                tabIndex: -1,
                children: [
                  Icon && /* @__PURE__ */ jsx75(
                    Icon,
                    {
                      className: `w-4 h-4 flex-shrink-0 ${item.destructive ? "text-accent" : "text-tertiary"}`,
                      strokeWidth: 2
                    }
                  ),
                  /* @__PURE__ */ jsx75("span", { className: `text-sm font-sans ${item.destructive ? "text-accent" : "text-primary"}`, children: item.label })
                ]
              },
              item.id
            );
          }) }),
          onSignOut && /* @__PURE__ */ jsx75("div", { className: "p-2 border-t border-recessed", children: /* @__PURE__ */ jsxs63(
            "button",
            {
              onClick: () => {
                onSignOut();
                setIsOpen(false);
              },
              className: "w-full flex items-center gap-3 px-3 py-2 rounded-sm hover:bg-elevated focus:bg-elevated focus:outline-none transition-colors text-left",
              role: "menuitem",
              tabIndex: -1,
              children: [
                /* @__PURE__ */ jsx75(LogOutIcon, { className: "w-4 h-4 text-tertiary flex-shrink-0", strokeWidth: 2 }),
                /* @__PURE__ */ jsx75("span", { className: "text-sm font-sans text-primary", children: "Sign out" })
              ]
            }
          ) }),
          footer && /* @__PURE__ */ jsx75("div", { className: "p-2 border-t border-recessed", children: footer })
        ]
      }
    )
  ] });
}

// src/shell/NotificationPanel.tsx
import { useState as useState17, useRef as useRef13, useEffect as useEffect13 } from "react";
import { BellIcon, CheckIcon as CheckIcon5, SettingsIcon as SettingsIcon2, TrashIcon } from "lucide-react";

// src/data/date-format.ts
var DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
function formatDateTime(value, { seconds = true, timeZone, locale = "en-US" } = {}) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...seconds ? { second: "2-digit" } : {},
    timeZoneName: "short",
    ...timeZone ? { timeZone } : {}
  });
}
function formatCalendarDate(value, timeZone) {
  const match = DATE_ONLY.exec(value);
  if (match) {
    const [, year, month, day] = match;
    const date2 = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    return date2.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC"
    });
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    ...timeZone ? { timeZone } : {}
  });
}
var MINUTE_MS = 6e4;
var HOUR_MS = 36e5;
var DAY_MS = 864e5;
function viewerLocale() {
  if (typeof navigator === "undefined") return void 0;
  return navigator.languages?.[0] || navigator.language || void 0;
}
function formatEventTime(value, { now = /* @__PURE__ */ new Date(), timeZone, locale = viewerLocale() } = {}) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { label: value };
  const options = { timeZone, ...locale ? { locale } : {} };
  const title = formatDateTime(date, { ...options, seconds: true });
  const dateTime = date.toISOString();
  const diffMs = now.getTime() - date.getTime();
  if (diffMs > -MINUTE_MS && diffMs < MINUTE_MS) return { label: "Just now", title, dateTime };
  if (diffMs > 0 && diffMs < HOUR_MS) {
    return { label: `${Math.floor(diffMs / MINUTE_MS)}m ago`, title, dateTime };
  }
  if (diffMs > 0 && diffMs < DAY_MS) {
    return { label: `${Math.floor(diffMs / HOUR_MS)}h ago`, title, dateTime };
  }
  if (diffMs > 0 && diffMs < 7 * DAY_MS) {
    return { label: `${Math.floor(diffMs / DAY_MS)}d ago`, title, dateTime };
  }
  return { label: formatDateTime(date, { ...options, seconds: false }), title, dateTime };
}

// src/shell/NotificationPanel.tsx
import { Fragment as Fragment16, jsx as jsx76, jsxs as jsxs64 } from "react/jsx-runtime";
function NotificationPanel({
  notifications,
  onNotificationClick,
  onMarkAllRead,
  onDismiss,
  onViewAll,
  onSettings,
  maxVisible = 5,
  emptyState,
  unreadCount: unreadCountProp,
  timeZone,
  locale,
  className = ""
}) {
  const [isOpen, setIsOpen] = useState17(false);
  const containerRef = useRef13(null);
  const triggerRef = useRef13(null);
  const panelRef = useRef13(null);
  const unreadCount = unreadCountProp ?? notifications.filter((n) => n.unread).length;
  const hasUnread = unreadCount > 0;
  const visibleNotifications = notifications.slice(0, maxVisible);
  const hasMore = notifications.length > maxVisible;
  useEffect13(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);
  useEffect13(() => {
    if (isOpen && panelRef.current) {
      const firstFocusable = panelRef.current.querySelector(
        'button, a, [tabindex]:not([tabindex="-1"])'
      );
      firstFocusable?.focus();
    }
  }, [isOpen]);
  const handlePanelKeyDown = (e) => {
    if (!panelRef.current) return;
    const focusableItems = Array.from(
      panelRef.current.querySelectorAll('button, a, [tabindex]:not([tabindex="-1"])')
    );
    if (!focusableItems.length) return;
    const currentIndex = focusableItems.indexOf(document.activeElement);
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        focusableItems[(currentIndex + 1) % focusableItems.length]?.focus();
        break;
      case "ArrowUp":
        e.preventDefault();
        focusableItems[(currentIndex - 1 + focusableItems.length) % focusableItems.length]?.focus();
        break;
      case "Home":
        e.preventDefault();
        focusableItems[0]?.focus();
        break;
      case "End":
        e.preventDefault();
        focusableItems[focusableItems.length - 1]?.focus();
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        triggerRef.current?.focus();
        break;
      case "Tab":
        setIsOpen(false);
        break;
    }
  };
  const handleClick = (notification) => {
    notification.onClick?.();
    onNotificationClick?.(notification);
    setIsOpen(false);
  };
  const getTypeColor = (type) => {
    switch (type) {
      case "success":
        return "bg-success";
      case "warning":
        return "bg-warning";
      case "error":
        return "bg-error";
      default:
        return "bg-secondary";
    }
  };
  return /* @__PURE__ */ jsxs64("div", { ref: containerRef, className: `relative ${className}`, children: [
    /* @__PURE__ */ jsxs64(
      "button",
      {
        ref: triggerRef,
        onClick: () => setIsOpen((prev) => !prev),
        onKeyDown: (e) => {
          if (e.key === "ArrowDown" && !isOpen) {
            e.preventDefault();
            setIsOpen(true);
          }
        },
        className: "relative p-2 rounded-sm hover:bg-elevated transition-colors",
        "aria-expanded": isOpen,
        "aria-haspopup": "true",
        "aria-label": `Notifications${hasUnread ? ` (${unreadCount} unread)` : ""}`,
        children: [
          /* @__PURE__ */ jsx76(BellIcon, { className: "w-5 h-5 text-tertiary", strokeWidth: 2 }),
          hasUnread && /* @__PURE__ */ jsx76(
            "span",
            {
              className: "absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-caption-xs font-sans font-semibold bg-accent text-white rounded-full",
              "aria-hidden": "true",
              children: unreadCount > 99 ? "99+" : unreadCount
            }
          )
        ]
      }
    ),
    isOpen && /* @__PURE__ */ jsxs64(
      "div",
      {
        ref: panelRef,
        className: "absolute top-full right-0 mt-1 w-[360px] bg-surface border border-recessed rounded-sm shadow-lg z-50 animate-scale-in",
        role: "dialog",
        "aria-label": "Notifications",
        onKeyDown: handlePanelKeyDown,
        children: [
          /* @__PURE__ */ jsxs64("div", { className: "flex items-center justify-between p-4 border-b border-recessed", children: [
            /* @__PURE__ */ jsxs64("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx76("span", { className: "text-sm font-sans font-semibold text-primary", children: "Notifications" }),
              hasUnread && /* @__PURE__ */ jsx76("span", { className: "px-1.5 py-0.5 text-caption-xs font-sans font-semibold bg-accent/10 text-accent rounded-full", children: unreadCount })
            ] }),
            /* @__PURE__ */ jsxs64("div", { className: "flex items-center gap-1", children: [
              hasUnread && onMarkAllRead && /* @__PURE__ */ jsx76(
                "button",
                {
                  onClick: () => {
                    onMarkAllRead();
                  },
                  className: "p-1.5 rounded-sm hover:bg-elevated transition-colors",
                  "aria-label": "Mark all as read",
                  title: "Mark all as read",
                  children: /* @__PURE__ */ jsx76(CheckIcon5, { className: "w-4 h-4 text-tertiary", strokeWidth: 2 })
                }
              ),
              onSettings && /* @__PURE__ */ jsx76(
                "button",
                {
                  onClick: () => {
                    onSettings();
                    setIsOpen(false);
                  },
                  className: "p-1.5 rounded-sm hover:bg-elevated transition-colors",
                  "aria-label": "Notification settings",
                  title: "Settings",
                  children: /* @__PURE__ */ jsx76(SettingsIcon2, { className: "w-4 h-4 text-tertiary", strokeWidth: 2 })
                }
              )
            ] })
          ] }),
          /* @__PURE__ */ jsx76("div", { className: "max-h-[400px] overflow-y-auto", children: notifications.length === 0 ? /* @__PURE__ */ jsx76("div", { className: "p-8 text-center", children: emptyState || /* @__PURE__ */ jsxs64(Fragment16, { children: [
            /* @__PURE__ */ jsx76(BellIcon, { className: "w-8 h-8 text-tertiary mx-auto mb-2", strokeWidth: 1.5 }),
            /* @__PURE__ */ jsx76("p", { className: "text-sm font-sans text-secondary", children: "No notifications" }),
            /* @__PURE__ */ jsx76("p", { className: "text-xs font-sans text-tertiary mt-1", children: "You are all caught up" })
          ] }) }) : visibleNotifications.map((notification) => {
            const Icon = notification.icon;
            const Element = notification.href ? "a" : "button";
            const time = notification.timestamp ? formatEventTime(notification.timestamp, {
              timeZone,
              ...locale ? { locale } : {}
            }) : null;
            return /* @__PURE__ */ jsxs64(
              "div",
              {
                className: `
                      group flex items-start border-b border-recessed
                      hover:bg-elevated transition-colors
                      ${notification.unread ? "bg-elevated/50" : ""}
                    `,
                children: [
                  /* @__PURE__ */ jsxs64(
                    Element,
                    {
                      ...notification.href ? { href: notification.href } : { type: "button" },
                      onClick: () => handleClick(notification),
                      className: "flex-1 min-w-0 flex items-start gap-3 p-4 focus:bg-elevated focus:outline-none cursor-pointer text-left",
                      children: [
                        Icon ? /* @__PURE__ */ jsx76(Icon, { className: "w-5 h-5 text-secondary flex-shrink-0 mt-0.5", strokeWidth: 2 }) : /* @__PURE__ */ jsx76("div", { className: `w-2 h-2 rounded-full mt-2 flex-shrink-0 ${getTypeColor(notification.type)}` }),
                        /* @__PURE__ */ jsxs64("div", { className: "flex-1 min-w-0", children: [
                          /* @__PURE__ */ jsx76("div", { className: `text-sm font-sans ${notification.unread ? "font-medium text-primary" : "text-secondary"}`, children: notification.title }),
                          notification.message && /* @__PURE__ */ jsx76("div", { className: "text-xs font-sans text-tertiary mt-0.5 line-clamp-2", children: notification.message }),
                          time ? /* @__PURE__ */ jsx76(
                            "time",
                            {
                              dateTime: time.dateTime,
                              title: time.title,
                              className: "block text-xs font-sans text-tertiary mt-1",
                              children: time.label
                            }
                          ) : notification.time && /* @__PURE__ */ jsx76("div", { className: "text-xs font-sans text-tertiary mt-1", children: notification.time })
                        ] })
                      ]
                    }
                  ),
                  onDismiss && /* @__PURE__ */ jsx76(
                    "button",
                    {
                      type: "button",
                      onClick: () => onDismiss(notification.id),
                      className: "flex-shrink-0 mt-3 mr-3 p-1 rounded-sm hover:bg-surface opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity",
                      "aria-label": "Dismiss notification",
                      children: /* @__PURE__ */ jsx76(TrashIcon, { className: "w-3.5 h-3.5 text-tertiary", strokeWidth: 2 })
                    }
                  )
                ]
              },
              notification.id
            );
          }) }),
          (hasMore || onViewAll) && notifications.length > 0 && /* @__PURE__ */ jsx76("div", { className: "p-3 text-center border-t border-recessed", children: /* @__PURE__ */ jsxs64(
            "button",
            {
              onClick: () => {
                onViewAll?.();
                setIsOpen(false);
              },
              className: "text-xs font-sans font-semibold text-accent hover:text-accent-hover transition-colors",
              children: [
                "View all notifications",
                hasMore && ` (${notifications.length})`
              ]
            }
          ) })
        ]
      }
    )
  ] });
}

// src/shell/FilterBar.tsx
import { useState as useState18, useCallback as useCallback11, useRef as useRef14, useEffect as useEffect14 } from "react";
import {
  SearchIcon as SearchIcon5,
  XIcon as XIcon3,
  SlidersHorizontalIcon,
  ChevronDownIcon as ChevronDownIcon7,
  ChevronLeftIcon as ChevronLeftIcon2,
  BookmarkIcon,
  Trash2Icon,
  CalendarIcon,
  CheckIcon as CheckIcon6
} from "lucide-react";
import { Fragment as Fragment17, jsx as jsx77, jsxs as jsxs65 } from "react/jsx-runtime";
var DATE_RANGE_PRESETS = [
  { label: "Today", value: "today" },
  { label: "Last 7 days", value: "last_7_days" },
  { label: "Last 30 days", value: "last_30_days" },
  { label: "This month", value: "this_month" },
  { label: "Last month", value: "last_month" },
  { label: "This quarter", value: "this_quarter" },
  { label: "This year", value: "this_year" }
];
function FilterBar({
  filters,
  activeFilters,
  onFiltersChange,
  searchValue = "",
  onSearchChange,
  searchPlaceholder = "Search...",
  debounceMs = 300,
  savedViews = [],
  onSaveView,
  onLoadView,
  onDeleteView,
  className = ""
}) {
  const [localSearch, setLocalSearch] = useState18(searchValue);
  const [showFilterMenu, setShowFilterMenu] = useState18(false);
  const [selectedFilterDef, setSelectedFilterDef] = useState18(null);
  const [filterInputValue, setFilterInputValue] = useState18("");
  const [showViewsMenu, setShowViewsMenu] = useState18(false);
  const [newViewName, setNewViewName] = useState18("");
  const [showCustomDateRange, setShowCustomDateRange] = useState18(false);
  const [customDateFrom, setCustomDateFrom] = useState18("");
  const [customDateTo, setCustomDateTo] = useState18("");
  const debounceRef = useRef14();
  const filterMenuRef = useRef14(null);
  const viewsMenuRef = useRef14(null);
  const searchInputRef = useRef14(null);
  const filterInputRef = useRef14(null);
  useEffect14(() => {
    setLocalSearch(searchValue);
  }, [searchValue]);
  useEffect14(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);
  useEffect14(() => {
    const handleClickOutside = (event) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) {
        setShowFilterMenu(false);
        setSelectedFilterDef(null);
        setFilterInputValue("");
        setShowCustomDateRange(false);
        setCustomDateFrom("");
        setCustomDateTo("");
      }
      if (viewsMenuRef.current && !viewsMenuRef.current.contains(event.target)) {
        setShowViewsMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect14(() => {
    if (selectedFilterDef && filterInputRef.current) {
      filterInputRef.current.focus();
    }
  }, [selectedFilterDef]);
  useEffect14(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (showFilterMenu) {
          if (selectedFilterDef) {
            setSelectedFilterDef(null);
            setFilterInputValue("");
            setShowCustomDateRange(false);
          } else {
            setShowFilterMenu(false);
          }
        }
        if (showViewsMenu) {
          setShowViewsMenu(false);
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showFilterMenu, showViewsMenu, selectedFilterDef]);
  const handleSearchChange = useCallback11(
    (value) => {
      setLocalSearch(value);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        onSearchChange?.(value);
      }, debounceMs);
    },
    [onSearchChange, debounceMs]
  );
  const handleClearSearch = useCallback11(() => {
    setLocalSearch("");
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    onSearchChange?.("");
    searchInputRef.current?.focus();
  }, [onSearchChange]);
  const handleRemoveFilter = useCallback11(
    (filterId) => {
      onFiltersChange(activeFilters.filter((f) => f.filterId !== filterId));
    },
    [activeFilters, onFiltersChange]
  );
  const handleClearAll = useCallback11(() => {
    onFiltersChange([]);
    if (onSearchChange) {
      setLocalSearch("");
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      onSearchChange("");
    }
  }, [onFiltersChange, onSearchChange]);
  const addFilter = useCallback11(
    (filterId, value, displayValue) => {
      const definition = filters.find((f) => f.id === filterId);
      if (!definition) return;
      const newFilter = {
        filterId,
        value,
        label: definition.label,
        displayValue: displayValue || value
      };
      const existingIndex = activeFilters.findIndex(
        (f) => f.filterId === filterId
      );
      if (existingIndex >= 0) {
        const updated = [...activeFilters];
        updated[existingIndex] = newFilter;
        onFiltersChange(updated);
      } else {
        onFiltersChange([...activeFilters, newFilter]);
      }
      setShowFilterMenu(false);
      setSelectedFilterDef(null);
      setFilterInputValue("");
      setShowCustomDateRange(false);
      setCustomDateFrom("");
      setCustomDateTo("");
    },
    [filters, activeFilters, onFiltersChange]
  );
  const handleFilterOptionSelect = useCallback11(
    (definition) => {
      if (definition.type === "select" && definition.options) {
        setSelectedFilterDef(definition);
        setFilterInputValue("");
      } else if (definition.type === "daterange") {
        setSelectedFilterDef(definition);
        setShowCustomDateRange(false);
      } else {
        setSelectedFilterDef(definition);
        setFilterInputValue("");
      }
    },
    []
  );
  const handleFilterValueSubmit = useCallback11(() => {
    if (!selectedFilterDef || !filterInputValue.trim()) return;
    const value = filterInputValue.trim();
    addFilter(
      selectedFilterDef.id,
      value,
      selectedFilterDef.type === "date" ? formatDate(value) : void 0
    );
  }, [selectedFilterDef, filterInputValue, addFilter]);
  const handleDateRangePreset = useCallback11(
    (preset) => {
      if (!selectedFilterDef) return;
      addFilter(selectedFilterDef.id, preset.value, preset.label);
    },
    [selectedFilterDef, addFilter]
  );
  const handleCustomDateRangeSubmit = useCallback11(() => {
    if (!selectedFilterDef || !customDateFrom || !customDateTo) return;
    const value = `${customDateFrom} to ${customDateTo}`;
    const displayValue = `${formatDate(customDateFrom)} -- ${formatDate(customDateTo)}`;
    addFilter(selectedFilterDef.id, value, displayValue);
  }, [selectedFilterDef, customDateFrom, customDateTo, addFilter]);
  const handleSaveView = useCallback11(() => {
    if (!newViewName.trim() || !onSaveView) return;
    onSaveView(newViewName.trim(), [...activeFilters]);
    setNewViewName("");
    setShowViewsMenu(false);
  }, [newViewName, onSaveView, activeFilters]);
  const handleLoadView = useCallback11(
    (view) => {
      onLoadView?.(view);
      setShowViewsMenu(false);
    },
    [onLoadView]
  );
  const handleDeleteView = useCallback11(
    (event, viewId) => {
      event.stopPropagation();
      onDeleteView?.(viewId);
    },
    [onDeleteView]
  );
  const hasActiveState = activeFilters.length > 0 || localSearch.trim().length > 0;
  const hasSavedViewsFeature = onSaveView !== void 0 || onLoadView !== void 0;
  const availableFilters = filters.filter(
    (def) => !activeFilters.some((af) => af.filterId === def.id)
  );
  const renderValuePanel = () => {
    if (!selectedFilterDef) return null;
    const { type } = selectedFilterDef;
    if (type === "select" && selectedFilterDef.options) {
      return /* @__PURE__ */ jsx77("div", { className: "p-1", children: selectedFilterDef.options.map((option) => {
        const isActive = activeFilters.some(
          (af) => af.filterId === selectedFilterDef.id && af.value === option.value
        );
        return /* @__PURE__ */ jsxs65(
          "button",
          {
            onClick: () => addFilter(
              selectedFilterDef.id,
              option.value,
              option.label
            ),
            className: `
                  w-full text-left px-3 py-2 text-body-sm font-sans rounded-sm transition-colors
                  flex items-center justify-between
                  ${isActive ? "bg-selection/10 text-primary" : "text-primary hover:bg-elevated"}
                `,
            children: [
              /* @__PURE__ */ jsx77("span", { children: option.label }),
              isActive && /* @__PURE__ */ jsx77(
                CheckIcon6,
                {
                  className: "w-3.5 h-3.5 text-selection",
                  strokeWidth: 2
                }
              )
            ]
          },
          option.value
        );
      }) });
    }
    if (type === "daterange") {
      return /* @__PURE__ */ jsx77("div", { className: "p-1", children: !showCustomDateRange ? /* @__PURE__ */ jsxs65(Fragment17, { children: [
        DATE_RANGE_PRESETS.map((preset) => /* @__PURE__ */ jsxs65(
          "button",
          {
            onClick: () => handleDateRangePreset(preset),
            className: "w-full text-left px-3 py-2 text-body-sm font-sans text-primary hover:bg-elevated rounded-sm transition-colors flex items-center gap-2",
            children: [
              /* @__PURE__ */ jsx77(
                CalendarIcon,
                {
                  className: "w-3.5 h-3.5 text-tertiary",
                  strokeWidth: 2
                }
              ),
              preset.label
            ]
          },
          preset.value
        )),
        /* @__PURE__ */ jsx77("div", { className: "border-t border-recessed my-1" }),
        /* @__PURE__ */ jsx77(
          "button",
          {
            onClick: () => setShowCustomDateRange(true),
            className: "w-full text-left px-3 py-2 text-body-sm font-sans text-secondary hover:text-primary hover:bg-elevated rounded-sm transition-colors",
            children: "Custom range..."
          }
        )
      ] }) : /* @__PURE__ */ jsxs65("div", { className: "px-3 py-2 space-y-2", children: [
        /* @__PURE__ */ jsx77("div", { className: "text-caption-md font-sans font-medium text-tertiary uppercase tracking-wider", children: "Custom Range" }),
        /* @__PURE__ */ jsxs65("div", { className: "space-y-1.5", children: [
          /* @__PURE__ */ jsxs65("label", { className: "block", children: [
            /* @__PURE__ */ jsx77("span", { className: "text-caption-md font-sans text-tertiary", children: "From" }),
            /* @__PURE__ */ jsx77(
              "input",
              {
                type: "date",
                value: customDateFrom,
                onChange: (e) => setCustomDateFrom(e.target.value),
                className: "mt-0.5 w-full px-2 py-1.5 text-body-sm font-sans bg-elevated border border-recessed rounded-sm text-primary focus:outline-none focus:ring-2 focus:ring-tertiary/30 focus:border-secondary"
              }
            )
          ] }),
          /* @__PURE__ */ jsxs65("label", { className: "block", children: [
            /* @__PURE__ */ jsx77("span", { className: "text-caption-md font-sans text-tertiary", children: "To" }),
            /* @__PURE__ */ jsx77(
              "input",
              {
                type: "date",
                value: customDateTo,
                onChange: (e) => setCustomDateTo(e.target.value),
                className: "mt-0.5 w-full px-2 py-1.5 text-body-sm font-sans bg-elevated border border-recessed rounded-sm text-primary focus:outline-none focus:ring-2 focus:ring-tertiary/30 focus:border-secondary"
              }
            )
          ] })
        ] }),
        /* @__PURE__ */ jsxs65("div", { className: "flex gap-2 pt-1", children: [
          /* @__PURE__ */ jsx77(
            "button",
            {
              onClick: () => setShowCustomDateRange(false),
              className: "flex-1 px-2 py-1.5 text-caption-md font-sans text-tertiary hover:text-primary rounded-sm transition-colors",
              children: "Back"
            }
          ),
          /* @__PURE__ */ jsx77(
            "button",
            {
              onClick: handleCustomDateRangeSubmit,
              disabled: !customDateFrom || !customDateTo,
              className: "flex-1 px-2 py-1.5 text-caption-md font-sans font-medium bg-accent text-white rounded-sm hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors",
              children: "Apply"
            }
          )
        ] })
      ] }) });
    }
    if (type === "date") {
      return /* @__PURE__ */ jsxs65("div", { className: "px-3 py-2", children: [
        /* @__PURE__ */ jsx77(
          "input",
          {
            ref: filterInputRef,
            type: "date",
            "aria-label": selectedFilterDef.label,
            value: filterInputValue,
            onChange: (e) => setFilterInputValue(e.target.value),
            onKeyDown: (e) => {
              if (e.key === "Enter") handleFilterValueSubmit();
            },
            className: "w-full px-2 py-1.5 text-body-sm font-sans bg-elevated border border-recessed rounded-sm text-primary focus:outline-none focus:ring-2 focus:ring-tertiary/30 focus:border-secondary"
          }
        ),
        /* @__PURE__ */ jsx77(
          "button",
          {
            onClick: handleFilterValueSubmit,
            disabled: !filterInputValue,
            className: "mt-2 w-full px-2 py-1.5 text-caption-md font-sans font-medium bg-accent text-white rounded-sm hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors",
            children: "Apply"
          }
        )
      ] });
    }
    return /* @__PURE__ */ jsxs65("div", { className: "px-3 py-2", children: [
      /* @__PURE__ */ jsx77(
        "input",
        {
          ref: filterInputRef,
          type: type === "number" ? "number" : "text",
          value: filterInputValue,
          onChange: (e) => setFilterInputValue(e.target.value),
          onKeyDown: (e) => {
            if (e.key === "Enter") handleFilterValueSubmit();
          },
          placeholder: selectedFilterDef.placeholder || `Enter ${selectedFilterDef.label.toLowerCase()}...`,
          className: "w-full px-2 py-1.5 text-body-sm font-sans bg-elevated border border-recessed rounded-sm text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-tertiary/30 focus:border-secondary"
        }
      ),
      /* @__PURE__ */ jsx77(
        "button",
        {
          onClick: handleFilterValueSubmit,
          disabled: !filterInputValue.trim(),
          className: "mt-2 w-full px-2 py-1.5 text-caption-md font-sans font-medium bg-accent text-white rounded-sm hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors",
          children: "Apply"
        }
      )
    ] });
  };
  return /* @__PURE__ */ jsxs65(
    "div",
    {
      className: `flex flex-wrap items-center gap-2 p-3 bg-elevated/30 border-b border-recessed ${className}`,
      role: "toolbar",
      "aria-label": "Filter controls",
      children: [
        onSearchChange && /* Below sm the search takes the whole first row and the filter
           controls wrap under it; from sm up it sits inline, up to 480px.
           At the previous 320px cap a placeholder of about 40 characters was
           cut mid-word even at 1920px ('Search by Package ID, member, email,
           or i'), and at 390px the 200px minimum cut it near 20. A
           placeholder longer than the field now ends in an ellipsis instead
           of a clipped word, and the full text stays in the accessible name. */
        /* @__PURE__ */ jsxs65("div", { className: "relative flex-1 basis-full sm:basis-auto min-w-[200px] max-w-full sm:max-w-[480px]", children: [
          /* @__PURE__ */ jsx77(
            SearchIcon5,
            {
              className: "absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary pointer-events-none",
              strokeWidth: 2
            }
          ),
          /* @__PURE__ */ jsx77(
            "input",
            {
              ref: searchInputRef,
              type: "text",
              value: localSearch,
              onChange: (e) => handleSearchChange(e.target.value),
              placeholder: searchPlaceholder,
              className: "w-full pl-9 pr-8 py-2 text-body-sm font-sans text-ellipsis bg-surface border border-recessed rounded-sm text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-tertiary/30 focus:border-secondary",
              "aria-label": searchPlaceholder
            }
          ),
          localSearch && /* @__PURE__ */ jsx77(
            "button",
            {
              onClick: handleClearSearch,
              className: "absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-sm hover:bg-elevated transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tertiary/30",
              "aria-label": "Clear search",
              children: /* @__PURE__ */ jsx77(
                XIcon3,
                {
                  className: "w-3.5 h-3.5 text-tertiary",
                  strokeWidth: 2
                }
              )
            }
          )
        ] }),
        activeFilters.map((filter) => /* @__PURE__ */ jsxs65(
          "div",
          {
            className: "flex items-center gap-1.5 px-2.5 py-1 bg-elevated border border-recessed rounded-sm animate-scale-in",
            children: [
              /* @__PURE__ */ jsxs65("span", { className: "text-body-xs font-sans text-tertiary whitespace-nowrap", children: [
                filter.label,
                ":"
              ] }),
              /* @__PURE__ */ jsx77("span", { className: "text-body-xs font-sans font-medium text-primary whitespace-nowrap", children: filter.displayValue || filter.value }),
              /* @__PURE__ */ jsx77(
                "button",
                {
                  onClick: () => handleRemoveFilter(filter.filterId),
                  className: "p-0.5 rounded-sm hover:bg-recessed/50 transition-colors ml-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tertiary/30",
                  "aria-label": `Remove ${filter.label} filter`,
                  children: /* @__PURE__ */ jsx77(
                    XIcon3,
                    {
                      className: "w-3 h-3 text-tertiary hover:text-primary",
                      strokeWidth: 2
                    }
                  )
                }
              )
            ]
          },
          filter.filterId
        )),
        activeFilters.length > 0 && /* @__PURE__ */ jsx77(
          "span",
          {
            className: "inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-caption-sm font-sans font-medium bg-accent/10 text-accent rounded-full",
            "aria-label": `${activeFilters.length} active ${activeFilters.length === 1 ? "filter" : "filters"}`,
            children: activeFilters.length
          }
        ),
        filters.length > 0 && /* @__PURE__ */ jsxs65("div", { className: "relative", ref: filterMenuRef, children: [
          /* @__PURE__ */ jsxs65(
            "button",
            {
              onClick: () => {
                setShowFilterMenu(!showFilterMenu);
                setSelectedFilterDef(null);
                setFilterInputValue("");
                setShowCustomDateRange(false);
              },
              className: "flex items-center gap-1.5 px-2.5 py-1.5 text-body-xs font-sans text-secondary hover:text-primary hover:bg-elevated rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tertiary/30",
              "aria-expanded": showFilterMenu,
              "aria-haspopup": "true",
              children: [
                /* @__PURE__ */ jsx77(SlidersHorizontalIcon, { className: "w-3.5 h-3.5", strokeWidth: 2 }),
                "Add Filter",
                /* @__PURE__ */ jsx77(
                  ChevronDownIcon7,
                  {
                    className: `w-3.5 h-3.5 transition-transform ${showFilterMenu ? "rotate-180" : ""}`,
                    strokeWidth: 2
                  }
                )
              ]
            }
          ),
          showFilterMenu && /* @__PURE__ */ jsxs65(
            "div",
            {
              className: "absolute top-full left-0 mt-1 w-56 bg-surface border border-recessed rounded-sm shadow-lg z-20 animate-slide-in",
              role: "menu",
              "aria-label": "Filter options",
              children: [
                selectedFilterDef && /* @__PURE__ */ jsxs65("div", { className: "flex items-center gap-2 px-3 py-2 border-b border-recessed", children: [
                  /* @__PURE__ */ jsx77(
                    "button",
                    {
                      onClick: () => {
                        setSelectedFilterDef(null);
                        setFilterInputValue("");
                        setShowCustomDateRange(false);
                      },
                      className: "p-0.5 rounded hover:bg-elevated transition-colors",
                      "aria-label": "Back to filter list",
                      children: /* @__PURE__ */ jsx77(
                        ChevronLeftIcon2,
                        {
                          className: "w-3.5 h-3.5 text-tertiary",
                          strokeWidth: 2
                        }
                      )
                    }
                  ),
                  /* @__PURE__ */ jsx77("span", { className: "text-body-xs font-sans font-medium text-primary", children: selectedFilterDef.label })
                ] }),
                !selectedFilterDef ? /* @__PURE__ */ jsx77("div", { className: "p-1", children: availableFilters.length > 0 ? availableFilters.map((definition) => /* @__PURE__ */ jsxs65(
                  "button",
                  {
                    onClick: () => handleFilterOptionSelect(definition),
                    className: "w-full text-left px-3 py-2 text-body-sm font-sans text-primary hover:bg-elevated rounded-sm transition-colors flex items-center justify-between",
                    role: "menuitem",
                    children: [
                      /* @__PURE__ */ jsx77("span", { children: definition.label }),
                      /* @__PURE__ */ jsx77("span", { className: "text-caption-md font-sans text-tertiary capitalize", children: definition.type === "daterange" ? "date range" : definition.type })
                    ]
                  },
                  definition.id
                )) : /* @__PURE__ */ jsx77("div", { className: "px-3 py-2 text-body-sm font-sans text-tertiary", children: "All filters applied" }) }) : renderValuePanel()
              ]
            }
          )
        ] }),
        /* @__PURE__ */ jsx77("div", { className: "flex-1" }),
        hasActiveState && /* @__PURE__ */ jsxs65(
          "button",
          {
            onClick: handleClearAll,
            className: "flex items-center gap-1.5 px-2.5 py-1.5 text-body-xs font-sans text-tertiary hover:text-primary hover:bg-elevated rounded-sm transition-colors",
            children: [
              /* @__PURE__ */ jsx77(XIcon3, { className: "w-3.5 h-3.5", strokeWidth: 2 }),
              "Clear All"
            ]
          }
        ),
        hasSavedViewsFeature && /* @__PURE__ */ jsxs65("div", { className: "relative", ref: viewsMenuRef, children: [
          /* @__PURE__ */ jsxs65(
            "button",
            {
              onClick: () => setShowViewsMenu(!showViewsMenu),
              className: "flex items-center gap-1.5 px-2.5 py-1.5 text-body-xs font-sans text-secondary hover:text-primary hover:bg-elevated rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tertiary/30",
              "aria-expanded": showViewsMenu,
              "aria-haspopup": "true",
              children: [
                /* @__PURE__ */ jsx77(BookmarkIcon, { className: "w-3.5 h-3.5", strokeWidth: 2 }),
                "Views",
                /* @__PURE__ */ jsx77(
                  ChevronDownIcon7,
                  {
                    className: `w-3.5 h-3.5 transition-transform ${showViewsMenu ? "rotate-180" : ""}`,
                    strokeWidth: 2
                  }
                )
              ]
            }
          ),
          showViewsMenu && /* @__PURE__ */ jsxs65(
            "div",
            {
              className: "absolute top-full right-0 mt-1 w-64 bg-surface border border-recessed rounded-sm shadow-lg z-20 animate-slide-in",
              role: "menu",
              "aria-label": "Saved views",
              children: [
                savedViews.length > 0 && /* @__PURE__ */ jsxs65("div", { className: "p-2 border-b border-recessed", children: [
                  /* @__PURE__ */ jsx77("div", { className: "text-caption-sm font-sans font-semibold uppercase tracking-widest text-tertiary px-2 py-1", children: "Saved Views" }),
                  savedViews.map((view) => /* @__PURE__ */ jsxs65(
                    "div",
                    {
                      className: "flex items-center justify-between px-2 py-1.5 rounded-sm hover:bg-elevated transition-colors group",
                      children: [
                        /* @__PURE__ */ jsxs65(
                          "button",
                          {
                            onClick: () => handleLoadView(view),
                            className: "flex-1 text-left text-body-sm font-sans text-primary",
                            role: "menuitem",
                            children: [
                              /* @__PURE__ */ jsx77("span", { children: view.name }),
                              view.isDefault && /* @__PURE__ */ jsx77("span", { className: "ml-2 text-caption-sm font-sans text-tertiary", children: "Default" })
                            ]
                          }
                        ),
                        /* @__PURE__ */ jsxs65("div", { className: "flex items-center gap-1", children: [
                          /* @__PURE__ */ jsx77("span", { className: "text-caption-sm font-sans text-tertiary", children: view.filters.length }),
                          onDeleteView && /* @__PURE__ */ jsx77(
                            "button",
                            {
                              onClick: (e) => handleDeleteView(e, view.id),
                              className: "p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-recessed/50 transition-all",
                              "aria-label": `Delete ${view.name} view`,
                              children: /* @__PURE__ */ jsx77(
                                Trash2Icon,
                                {
                                  className: "w-3 h-3 text-tertiary hover:text-error",
                                  strokeWidth: 2
                                }
                              )
                            }
                          )
                        ] })
                      ]
                    },
                    view.id
                  ))
                ] }),
                onSaveView && hasActiveState && /* @__PURE__ */ jsxs65("div", { className: "p-2", children: [
                  /* @__PURE__ */ jsx77("div", { className: "text-caption-sm font-sans font-semibold uppercase tracking-widest text-tertiary px-2 py-1", children: "Save Current View" }),
                  /* @__PURE__ */ jsxs65("div", { className: "flex gap-2 px-2 mt-1", children: [
                    /* @__PURE__ */ jsx77(
                      "input",
                      {
                        type: "text",
                        value: newViewName,
                        onChange: (e) => setNewViewName(e.target.value),
                        placeholder: "View name...",
                        className: "flex-1 px-2 py-1.5 text-body-sm font-sans bg-elevated border border-recessed rounded-sm text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-tertiary/30 focus:border-secondary",
                        onKeyDown: (e) => {
                          if (e.key === "Enter") handleSaveView();
                        }
                      }
                    ),
                    /* @__PURE__ */ jsx77(
                      "button",
                      {
                        onClick: handleSaveView,
                        disabled: !newViewName.trim(),
                        className: "px-3 py-1.5 text-caption-md font-sans font-medium bg-accent text-white rounded-sm hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors",
                        children: "Save"
                      }
                    )
                  ] })
                ] }),
                savedViews.length === 0 && (!onSaveView || !hasActiveState) && /* @__PURE__ */ jsx77("div", { className: "px-4 py-3 text-body-sm font-sans text-tertiary", children: "No saved views. Apply filters and save a view to get started." })
              ]
            }
          )
        ] })
      ]
    }
  );
}
function formatDate(isoDate) {
  if (!isoDate) return "";
  const date = /* @__PURE__ */ new Date(isoDate + "T00:00:00");
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}

// src/shell/ActionBar.tsx
import { useState as useState19, useRef as useRef15, useEffect as useEffect15 } from "react";
import {
  XIcon as XIcon4,
  CheckIcon as CheckIcon7,
  AlertTriangleIcon,
  MoreHorizontalIcon as MoreHorizontalIcon2
} from "lucide-react";
import { Fragment as Fragment18, jsx as jsx78, jsxs as jsxs66 } from "react/jsx-runtime";
function ActionBar({
  selectedCount,
  totalCount,
  actions,
  onSelectAll,
  onDeselectAll,
  onAction,
  confirmDangerActions = true,
  sticky = false,
  className = ""
}) {
  const [confirmingActionId, setConfirmingActionId] = useState19(
    null
  );
  const [overflowOpen, setOverflowOpen] = useState19(false);
  const overflowRef = useRef15(null);
  useEffect15(() => {
    function handleClickOutside(event) {
      if (overflowRef.current && !overflowRef.current.contains(event.target)) {
        setOverflowOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect15(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setOverflowOpen(false);
        setConfirmingActionId(null);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);
  useEffect15(() => {
    setConfirmingActionId(null);
    setOverflowOpen(false);
  }, [selectedCount]);
  if (selectedCount === 0) {
    return null;
  }
  const inlineActions = actions.filter(
    (a) => a.variant === "primary" || a.variant === "danger"
  );
  const overflowActions = actions.filter(
    (a) => !a.variant || a.variant === "secondary"
  );
  const allSelected = selectedCount >= totalCount;
  function executeAction(action) {
    if (action.disabled) return;
    if (action.confirmMessage || action.variant === "danger" && confirmDangerActions) {
      setConfirmingActionId(action.id);
      return;
    }
    action.onClick();
    onAction?.(action.id);
  }
  function handleConfirm(action) {
    action.onClick();
    onAction?.(action.id);
    setConfirmingActionId(null);
  }
  function handleCancelConfirm() {
    setConfirmingActionId(null);
  }
  function getButtonClasses(variant, disabled) {
    const base = "flex items-center gap-1.5 px-3 py-1.5 text-body-sm font-sans font-medium rounded-sm transition-colors whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selection/40";
    const disabledStyle = disabled ? "opacity-50 cursor-not-allowed" : "";
    switch (variant) {
      case "primary":
        return `${base} bg-accent text-white hover:bg-accent-hover ${disabledStyle}`;
      case "danger":
        return `${base} text-accent hover:bg-accent/10 ${disabledStyle}`;
      default:
        return `${base} text-secondary hover:text-primary hover:bg-elevated ${disabledStyle}`;
    }
  }
  function renderInlineButton(action) {
    const Icon = action.icon;
    return /* @__PURE__ */ jsxs66(
      "button",
      {
        onClick: () => executeAction(action),
        disabled: action.disabled,
        className: getButtonClasses(action.variant, action.disabled),
        title: action.tooltip,
        "aria-label": action.tooltip || action.label,
        children: [
          Icon && /* @__PURE__ */ jsx78(Icon, { className: "w-4 h-4", strokeWidth: 2 }),
          action.label
        ]
      },
      action.id
    );
  }
  function renderOverflowItem(action) {
    const Icon = action.icon;
    const isDanger = action.variant === "danger";
    return /* @__PURE__ */ jsxs66(
      "button",
      {
        onClick: () => {
          setOverflowOpen(false);
          executeAction(action);
        },
        disabled: action.disabled,
        className: `
          w-full text-left flex items-center gap-2 px-3 py-2 text-body-sm font-sans
          rounded-sm transition-colors
          ${isDanger ? "text-accent hover:bg-accent/10" : "text-primary hover:bg-elevated"}
          ${action.disabled ? "opacity-50 cursor-not-allowed" : ""}
        `,
        title: action.tooltip,
        role: "menuitem",
        children: [
          Icon && /* @__PURE__ */ jsx78(Icon, { className: "w-4 h-4", strokeWidth: 2 }),
          action.label
        ]
      },
      action.id
    );
  }
  const confirmingAction = confirmingActionId ? actions.find((a) => a.id === confirmingActionId) || null : null;
  const confirmingIsDanger = confirmingAction?.variant === "danger";
  const confirmQuestion = confirmingAction ? confirmingAction.confirmMessage?.(selectedCount) ?? `${confirmingAction.label} ${selectedCount} ${selectedCount === 1 ? "item" : "items"}?` : "";
  const stickyClasses = sticky ? "sticky top-0 z-40 shadow-sm" : "";
  const overflowDesktopHidden = overflowActions.length === 0 ? "md:hidden" : "";
  return /* @__PURE__ */ jsx78(
    "div",
    {
      className: `
        flex items-center gap-3 px-4 py-2.5
        bg-surface border-t border-recessed
        animate-slide-in
        ${stickyClasses}
        ${className}
      `,
      role: "toolbar",
      "aria-label": `Bulk actions for ${selectedCount} selected ${selectedCount === 1 ? "item" : "items"}`,
      children: confirmingAction ? (
        /* ------------------------------------------------------------------ */
        /* Confirmation State                                                 */
        /* ------------------------------------------------------------------ */
        /* The alert styling (red tint, warning icon) marks a danger action   */
        /* only; any other confirmed action asks in the neutral surface.     */
        /* @__PURE__ */ jsx78("div", { className: "flex items-center gap-3 flex-1 animate-fade-in", children: /* @__PURE__ */ jsxs66(
          "div",
          {
            className: `flex flex-wrap items-center gap-2 px-3 py-1.5 border rounded-sm ${confirmingIsDanger ? "bg-accent/5 border-accent/20" : "bg-elevated border-recessed"}`,
            children: [
              confirmingIsDanger && /* @__PURE__ */ jsx78(
                AlertTriangleIcon,
                {
                  className: "w-4 h-4 text-accent flex-shrink-0",
                  strokeWidth: 2,
                  "aria-hidden": "true"
                }
              ),
              /* @__PURE__ */ jsx78("span", { className: "text-body-sm font-sans text-primary", children: confirmQuestion }),
              /* @__PURE__ */ jsx78(
                "button",
                {
                  onClick: () => handleConfirm(confirmingAction),
                  className: "px-3 py-1 text-body-sm font-sans font-medium bg-accent text-white rounded-sm hover:bg-accent-hover transition-colors",
                  children: "Confirm"
                }
              ),
              /* @__PURE__ */ jsx78(
                "button",
                {
                  onClick: handleCancelConfirm,
                  className: "px-3 py-1 text-body-sm font-sans text-tertiary hover:text-primary transition-colors",
                  children: "Cancel"
                }
              )
            ]
          }
        ) })
      ) : (
        /* ------------------------------------------------------------------ */
        /* Normal State                                                       */
        /* ------------------------------------------------------------------ */
        /* @__PURE__ */ jsxs66(Fragment18, { children: [
          /* @__PURE__ */ jsxs66("div", { className: "flex items-center gap-2 flex-shrink-0", children: [
            /* @__PURE__ */ jsx78("div", { className: "flex items-center justify-center w-5 h-5 bg-selection/10 rounded-sm", children: /* @__PURE__ */ jsx78(
              CheckIcon7,
              {
                className: "w-3 h-3 text-selection",
                strokeWidth: allSelected ? 3 : 2
              }
            ) }),
            /* @__PURE__ */ jsxs66("span", { className: "text-body-sm font-sans text-primary", children: [
              /* @__PURE__ */ jsx78("span", { className: "font-medium", children: selectedCount }),
              /* @__PURE__ */ jsxs66("span", { className: "hidden sm:inline", children: [
                " of ",
                totalCount
              ] }),
              " ",
              "selected"
            ] })
          ] }),
          /* @__PURE__ */ jsxs66("div", { className: "flex items-center gap-1 flex-shrink-0", children: [
            !allSelected && /* @__PURE__ */ jsx78(
              "button",
              {
                onClick: onSelectAll,
                className: "text-body-xs font-sans text-accent hover:text-accent-hover transition-colors",
                "aria-label": `Select all ${totalCount} items`,
                children: "Select all"
              }
            ),
            /* @__PURE__ */ jsx78(
              "button",
              {
                onClick: onDeselectAll,
                className: "p-1 rounded-sm hover:bg-elevated transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selection/40",
                "aria-label": "Clear selection",
                children: /* @__PURE__ */ jsx78(XIcon4, { className: "w-3.5 h-3.5 text-tertiary", strokeWidth: 2 })
              }
            )
          ] }),
          actions.length > 0 && /* @__PURE__ */ jsx78(
            "div",
            {
              className: "w-px h-6 bg-recessed flex-shrink-0",
              "aria-hidden": "true"
            }
          ),
          inlineActions.length > 0 && /* @__PURE__ */ jsx78("div", { className: "hidden md:flex items-center gap-1.5 flex-shrink-0", children: inlineActions.map(renderInlineButton) }),
          actions.length > 0 && /* @__PURE__ */ jsxs66(
            "div",
            {
              className: `relative flex-shrink-0 ${overflowDesktopHidden}`,
              ref: overflowRef,
              children: [
                /* @__PURE__ */ jsxs66(
                  "button",
                  {
                    onClick: () => setOverflowOpen(!overflowOpen),
                    className: "flex items-center gap-1.5 px-2 py-1.5 text-body-sm font-sans text-secondary hover:text-primary hover:bg-elevated rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selection/40",
                    "aria-expanded": overflowOpen,
                    "aria-haspopup": "true",
                    "aria-label": "More actions",
                    children: [
                      /* @__PURE__ */ jsx78(MoreHorizontalIcon2, { className: "w-4 h-4", strokeWidth: 2 }),
                      /* @__PURE__ */ jsx78("span", { className: "md:hidden text-body-xs", children: "Actions" })
                    ]
                  }
                ),
                overflowOpen && /* @__PURE__ */ jsxs66(
                  "div",
                  {
                    className: "absolute top-full right-0 mt-1 w-48 bg-surface border border-recessed rounded-sm shadow-lg z-30 animate-scale-in",
                    role: "menu",
                    "aria-label": "Additional actions",
                    children: [
                      /* @__PURE__ */ jsx78("div", { className: "md:hidden p-1", children: actions.map(renderOverflowItem) }),
                      overflowActions.length > 0 && /* @__PURE__ */ jsx78("div", { className: "hidden md:block p-1", children: overflowActions.map(renderOverflowItem) })
                    ]
                  }
                )
              ]
            }
          )
        ] })
      )
    }
  );
}
function DataToolbar({
  filterBar,
  actionBar,
  className = ""
}) {
  return /* @__PURE__ */ jsxs66("div", { className: `flex flex-col ${className}`, children: [
    filterBar,
    actionBar
  ] });
}

// src/shell/NotificationCenter.tsx
import { useState as useState20, useMemo as useMemo8, useCallback as useCallback12 } from "react";
import {
  BellIcon as BellIcon2,
  CheckIcon as CheckIcon8,
  CheckCircleIcon as CheckCircleIcon2,
  Trash2Icon as Trash2Icon2,
  ArchiveIcon,
  SettingsIcon as SettingsIcon3,
  AlertTriangleIcon as AlertTriangleIcon2,
  InfoIcon,
  XCircleIcon as XCircleIcon2,
  SearchIcon as SearchIcon6
} from "lucide-react";
import { Fragment as Fragment19, jsx as jsx79, jsxs as jsxs67 } from "react/jsx-runtime";
var CATEGORY_CONFIG = {
  all: { label: "All", icon: BellIcon2 },
  approvals: { label: "Approvals", icon: CheckCircleIcon2 },
  // The neutral info icon, never the alert triangle or the error circle.
  activity: { label: "Activity", icon: InfoIcon },
  alerts: { label: "Alerts", icon: AlertTriangleIcon2 },
  compliance: { label: "Compliance", icon: InfoIcon },
  system: { label: "System", icon: SettingsIcon3 },
  mentions: { label: "Mentions", icon: BellIcon2 }
};
var ALL_CATEGORIES = Object.keys(CATEGORY_CONFIG);
var PRIORITY_CONFIG = {
  critical: { color: "text-error", bg: "bg-error/10", label: "Critical", sortWeight: 0 },
  high: { color: "text-warning", bg: "bg-warning/10", label: "High", sortWeight: 1 },
  normal: { color: "text-secondary", bg: "bg-secondary/10", label: "Normal", sortWeight: 2 },
  low: { color: "text-tertiary", bg: "bg-tertiary/10", label: "Low", sortWeight: 3 }
};
var TYPE_ICON_MAP = {
  info: InfoIcon,
  success: CheckCircleIcon2,
  warning: AlertTriangleIcon2,
  error: XCircleIcon2
};
var TYPE_COLOR_MAP = {
  info: { icon: "text-secondary", bg: "bg-secondary/10" },
  success: { icon: "text-success", bg: "bg-success/10" },
  warning: { icon: "text-warning", bg: "bg-warning/10" },
  error: { icon: "text-error", bg: "bg-error/10" }
};
function NotificationItem({
  notification,
  isSelected,
  onSelect,
  onClick,
  onAction,
  timeZone,
  locale
}) {
  const type = notification.type || "info";
  const Icon = notification.icon || TYPE_ICON_MAP[type] || InfoIcon;
  const colorConfig = TYPE_COLOR_MAP[type] || TYPE_COLOR_MAP.info;
  const isUnread = notification.state === "unread";
  const priorityConfig = PRIORITY_CONFIG[notification.priority];
  const time = formatEventTime(notification.timestamp, {
    timeZone,
    ...locale ? { locale } : {}
  });
  const timestampLabel = time.label;
  const badgeText = notification.badgeLabel?.trim() || null;
  const showBadge = badgeText !== null || notification.priority !== "normal";
  return /* @__PURE__ */ jsxs67(
    "div",
    {
      role: "listitem",
      className: `
        flex items-start gap-3 px-4 py-3 border-b border-recessed transition-colors
        ${isUnread ? "bg-elevated/30" : ""}
        ${isSelected ? "bg-selection/10" : ""}
      `,
      children: [
        /* @__PURE__ */ jsx79(
          "button",
          {
            type: "button",
            onClick: (e) => {
              e.stopPropagation();
              onSelect();
            },
            className: "group flex-shrink-0 -mx-1 p-1 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selection/40",
            "aria-label": `Select ${notification.title}, ${timestampLabel}`,
            "aria-checked": isSelected,
            role: "checkbox",
            children: /* @__PURE__ */ jsx79(
              "span",
              {
                "data-selection-box": "",
                className: `
            flex w-4 h-4 rounded-sm border border-solid transition-colors
            ${isSelected ? "bg-selection border-selection" : "border-[rgb(var(--color-control-boundary))] group-hover:border-secondary"}
          `,
                children: isSelected && /* @__PURE__ */ jsx79("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", className: "p-0.5", "aria-hidden": "true", children: /* @__PURE__ */ jsx79(
                  "path",
                  {
                    d: "M3 8L6.5 11.5L13 4.5",
                    stroke: "white",
                    strokeWidth: "2",
                    strokeLinecap: "round",
                    strokeLinejoin: "round"
                  }
                ) })
              }
            )
          }
        ),
        /* @__PURE__ */ jsx79(
          "div",
          {
            className: `flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${colorConfig.bg}`,
            children: /* @__PURE__ */ jsx79(Icon, { className: `w-4 h-4 ${colorConfig.icon}`, strokeWidth: 2 })
          }
        ),
        /* @__PURE__ */ jsxs67("div", { className: "relative flex-1 min-w-0", children: [
          /* @__PURE__ */ jsx79("div", { className: "flex items-start justify-between gap-2", children: /* @__PURE__ */ jsxs67("div", { className: "flex-1 min-w-0", children: [
            /* @__PURE__ */ jsxs67("div", { className: "flex items-center gap-2", children: [
              onClick ? /* @__PURE__ */ jsx79(
                "button",
                {
                  type: "button",
                  onClick,
                  className: `text-left text-sm font-sans rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selection/40 after:absolute after:inset-0 ${isUnread ? "font-medium text-primary" : "text-secondary"}`,
                  children: notification.title
                }
              ) : /* @__PURE__ */ jsx79(
                "span",
                {
                  className: `text-sm font-sans ${isUnread ? "font-medium text-primary" : "text-secondary"}`,
                  children: notification.title
                }
              ),
              showBadge && /* @__PURE__ */ jsx79(
                "span",
                {
                  className: `${badgeText === null ? "text-label-xs uppercase" : "text-caption-xs"} font-sans font-medium px-1.5 py-0.5 rounded-sm whitespace-nowrap ${priorityConfig.bg} ${priorityConfig.color}`,
                  children: badgeText ?? priorityConfig.label
                }
              )
            ] }),
            notification.message && /* @__PURE__ */ jsx79("p", { className: "text-xs font-sans text-tertiary mt-0.5 line-clamp-2", children: notification.message }),
            /* @__PURE__ */ jsxs67("div", { className: "flex items-center gap-3 mt-1.5 text-caption-xs font-sans text-tertiary", children: [
              notification.source && /* @__PURE__ */ jsx79("span", { children: notification.source }),
              /* @__PURE__ */ jsx79("time", { dateTime: time.dateTime, title: time.title, children: timestampLabel }),
              /* @__PURE__ */ jsx79("span", { className: "capitalize", children: CATEGORY_CONFIG[notification.category]?.label })
            ] })
          ] }) }),
          notification.actionLabel && onAction && /* @__PURE__ */ jsx79(
            "button",
            {
              type: "button",
              onClick: (e) => {
                e.stopPropagation();
                onAction();
              },
              className: "relative z-10 mt-2 px-3 py-1 text-xs font-sans font-medium text-accent hover:text-accent-hover border border-accent/30 rounded-sm hover:bg-accent/5 transition-colors",
              children: notification.actionLabel
            }
          )
        ] }),
        isUnread && /* @__PURE__ */ jsxs67(Fragment19, { children: [
          /* @__PURE__ */ jsx79("span", { className: "flex-shrink-0 w-2 h-2 rounded-full bg-accent mt-2", "aria-hidden": "true" }),
          /* @__PURE__ */ jsx79("span", { className: "sr-only", children: "Unread" })
        ] })
      ]
    }
  );
}
function NotificationCenter({
  notifications,
  onMarkRead,
  onMarkAllRead,
  onArchive,
  onDelete,
  onNotificationClick,
  onNotificationAction,
  onOpenPreferences,
  unreadCount: unreadCountProp,
  categories,
  showArchiveToggle,
  hideTitle = false,
  timeZone,
  locale,
  className = ""
}) {
  const visibleCategories = useMemo8(() => {
    const known = (categories ?? []).filter((cat) => cat in CATEGORY_CONFIG);
    return known.length > 0 ? known : ALL_CATEGORIES;
  }, [categories]);
  const [selectedCategory, setActiveCategory] = useState20("all");
  const activeCategory = visibleCategories.includes(selectedCategory) ? selectedCategory : visibleCategories[0];
  const [selectedIds, setSelectedIds] = useState20(/* @__PURE__ */ new Set());
  const [searchQuery, setSearchQuery] = useState20("");
  const [showArchived, setShowArchived] = useState20(false);
  const archiveToggleVisible = showArchiveToggle ?? (Boolean(onArchive) || notifications.some((n) => n.state === "archived"));
  const filteredNotifications = useMemo8(() => {
    let filtered = notifications;
    if (activeCategory !== "all") {
      filtered = filtered.filter((n) => n.category === activeCategory);
    }
    if (!showArchived) {
      filtered = filtered.filter((n) => n.state !== "archived");
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (n) => n.title.toLowerCase().includes(query) || n.message?.toLowerCase().includes(query) || n.source?.toLowerCase().includes(query)
      );
    }
    return [...filtered].sort((a, b) => {
      if (a.state === "unread" && b.state !== "unread") return -1;
      if (a.state !== "unread" && b.state === "unread") return 1;
      const priorityDiff = PRIORITY_CONFIG[a.priority].sortWeight - PRIORITY_CONFIG[b.priority].sortWeight;
      if (priorityDiff !== 0) return priorityDiff;
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });
  }, [notifications, activeCategory, showArchived, searchQuery]);
  const categoryCounts = useMemo8(() => {
    const counts = { all: 0 };
    for (const n of notifications) {
      if (n.state !== "archived") {
        counts.all = (counts.all || 0) + 1;
        counts[n.category] = (counts[n.category] || 0) + 1;
      }
    }
    return counts;
  }, [notifications]);
  const unreadCount = unreadCountProp ?? notifications.filter((n) => n.state === "unread").length;
  const toggleSelect = useCallback12((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);
  const selectAll = useCallback12(() => {
    setSelectedIds(new Set(filteredNotifications.map((n) => n.id)));
  }, [filteredNotifications]);
  const deselectAll = useCallback12(() => {
    setSelectedIds(/* @__PURE__ */ new Set());
  }, []);
  const handleBulkMarkRead = useCallback12(() => {
    if (selectedIds.size > 0 && onMarkRead) {
      onMarkRead(Array.from(selectedIds));
      setSelectedIds(/* @__PURE__ */ new Set());
    }
  }, [selectedIds, onMarkRead]);
  const handleBulkArchive = useCallback12(() => {
    if (selectedIds.size > 0 && onArchive) {
      onArchive(Array.from(selectedIds));
      setSelectedIds(/* @__PURE__ */ new Set());
    }
  }, [selectedIds, onArchive]);
  const handleBulkDelete = useCallback12(() => {
    if (selectedIds.size > 0 && onDelete) {
      onDelete(Array.from(selectedIds));
      setSelectedIds(/* @__PURE__ */ new Set());
    }
  }, [selectedIds, onDelete]);
  const hasSelection = selectedIds.size > 0;
  return /* @__PURE__ */ jsxs67("div", { className: `flex flex-col h-full ${className}`, children: [
    /* @__PURE__ */ jsxs67("div", { className: "px-4 py-4 border-b border-recessed", children: [
      /* @__PURE__ */ jsxs67("div", { className: "flex flex-wrap items-center justify-between gap-2 mb-4", children: [
        /* @__PURE__ */ jsxs67("div", { className: "flex items-center gap-3", children: [
          !hideTitle && /* @__PURE__ */ jsx79("h2", { className: "text-xl font-serif font-semibold text-primary tracking-headline-serif", children: "Notifications" }),
          unreadCount > 0 && /* @__PURE__ */ jsxs67("span", { className: "whitespace-nowrap px-2 py-0.5 text-xs font-sans font-semibold bg-accent/10 text-accent rounded-full tabular-nums", children: [
            unreadCount,
            " unread"
          ] })
        ] }),
        /* @__PURE__ */ jsxs67("div", { className: "flex items-center gap-2", children: [
          unreadCount > 0 && onMarkAllRead && /* @__PURE__ */ jsxs67(
            "button",
            {
              type: "button",
              onClick: onMarkAllRead,
              className: "flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 text-xs font-sans text-tertiary hover:text-primary hover:bg-elevated rounded-sm transition-colors",
              children: [
                /* @__PURE__ */ jsx79(CheckIcon8, { className: "w-3.5 h-3.5", strokeWidth: 2 }),
                "Mark all read"
              ]
            }
          ),
          onOpenPreferences && /* @__PURE__ */ jsx79(
            "button",
            {
              onClick: onOpenPreferences,
              className: "p-2 rounded-sm hover:bg-elevated transition-colors",
              "aria-label": "Notification preferences",
              children: /* @__PURE__ */ jsx79(SettingsIcon3, { className: "w-4 h-4 text-tertiary", strokeWidth: 2 })
            }
          )
        ] })
      ] }),
      /* @__PURE__ */ jsx79("div", { className: "flex flex-wrap items-center gap-1", role: "tablist", "aria-label": "Notification categories", children: visibleCategories.map((cat) => {
        const config = CATEGORY_CONFIG[cat];
        const isActive = activeCategory === cat;
        const count = categoryCounts[cat] || 0;
        const CatIcon = config.icon;
        return /* @__PURE__ */ jsxs67(
          "button",
          {
            role: "tab",
            "aria-selected": isActive,
            onClick: () => {
              setActiveCategory(cat);
              setSelectedIds(/* @__PURE__ */ new Set());
            },
            className: `
                  flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans rounded-sm whitespace-nowrap transition-colors
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selection/40
                  ${isActive ? "bg-elevated text-primary font-medium" : "text-tertiary hover:text-secondary hover:bg-elevated/50"}
                `,
            children: [
              /* @__PURE__ */ jsx79(CatIcon, { className: "w-3.5 h-3.5", strokeWidth: 2 }),
              config.label,
              count > 0 && /* @__PURE__ */ jsx79("span", { className: "tabular-nums ml-0.5", children: count })
            ]
          },
          cat
        );
      }) })
    ] }),
    /* @__PURE__ */ jsxs67("div", { className: "flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2 bg-elevated/30 border-b border-recessed", children: [
      /* @__PURE__ */ jsxs67("div", { className: "relative w-full sm:w-auto sm:flex-1 sm:max-w-[280px]", children: [
        /* @__PURE__ */ jsx79(
          SearchIcon6,
          {
            className: "absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary pointer-events-none",
            strokeWidth: 2
          }
        ),
        /* @__PURE__ */ jsx79(
          "input",
          {
            type: "text",
            value: searchQuery,
            onChange: (e) => setSearchQuery(e.target.value),
            placeholder: "Search notifications...",
            className: "w-full pl-9 pr-3 py-1.5 text-sm font-sans bg-surface border border-recessed rounded-sm text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-tertiary/30 focus:border-secondary",
            "aria-label": "Search notifications"
          }
        )
      ] }),
      hasSelection ? /* @__PURE__ */ jsxs67("div", { className: "flex items-center gap-2 animate-fade-in", children: [
        /* @__PURE__ */ jsxs67("span", { className: "text-xs font-sans text-primary", children: [
          /* @__PURE__ */ jsx79("span", { className: "font-medium tabular-nums", children: selectedIds.size }),
          " selected"
        ] }),
        onMarkRead && /* @__PURE__ */ jsxs67(
          "button",
          {
            onClick: handleBulkMarkRead,
            className: "flex items-center gap-1 px-2 py-1 text-xs font-sans text-secondary hover:text-primary hover:bg-elevated rounded-sm transition-colors",
            "aria-label": "Mark selected as read",
            children: [
              /* @__PURE__ */ jsx79(CheckCircleIcon2, { className: "w-3.5 h-3.5", strokeWidth: 2 }),
              "Read"
            ]
          }
        ),
        onArchive && /* @__PURE__ */ jsxs67(
          "button",
          {
            onClick: handleBulkArchive,
            className: "flex items-center gap-1 px-2 py-1 text-xs font-sans text-secondary hover:text-primary hover:bg-elevated rounded-sm transition-colors",
            "aria-label": "Archive selected",
            children: [
              /* @__PURE__ */ jsx79(ArchiveIcon, { className: "w-3.5 h-3.5", strokeWidth: 2 }),
              "Archive"
            ]
          }
        ),
        onDelete && /* @__PURE__ */ jsxs67(
          "button",
          {
            onClick: handleBulkDelete,
            className: "flex items-center gap-1 px-2 py-1 text-xs font-sans text-error hover:bg-error/10 rounded-sm transition-colors",
            "aria-label": "Delete selected",
            children: [
              /* @__PURE__ */ jsx79(Trash2Icon2, { className: "w-3.5 h-3.5", strokeWidth: 2 }),
              "Delete"
            ]
          }
        ),
        /* @__PURE__ */ jsx79(
          "button",
          {
            onClick: deselectAll,
            className: "text-xs font-sans text-tertiary hover:text-primary transition-colors ml-1",
            children: "Clear"
          }
        )
      ] }) : /* @__PURE__ */ jsxs67("div", { className: "flex items-center gap-3", children: [
        /* @__PURE__ */ jsx79(
          "button",
          {
            type: "button",
            onClick: selectAll,
            className: "whitespace-nowrap text-xs font-sans text-tertiary hover:text-secondary transition-colors",
            children: "Select all"
          }
        ),
        archiveToggleVisible && /* @__PURE__ */ jsx79(
          "button",
          {
            type: "button",
            onClick: () => setShowArchived((p) => !p),
            className: `whitespace-nowrap text-xs font-sans transition-colors ${showArchived ? "text-primary font-medium" : "text-tertiary hover:text-secondary"}`,
            "aria-pressed": showArchived,
            children: showArchived ? "Hide archived" : "Show archived"
          }
        )
      ] }),
      /* @__PURE__ */ jsx79("div", { className: "flex-1" }),
      /* @__PURE__ */ jsxs67("span", { className: "whitespace-nowrap text-xs font-sans text-tertiary tabular-nums", children: [
        filteredNotifications.length,
        " notification",
        filteredNotifications.length !== 1 ? "s" : ""
      ] })
    ] }),
    /* @__PURE__ */ jsx79("div", { className: "flex-1 overflow-y-auto", role: "list", "aria-label": "Notifications", children: filteredNotifications.length === 0 ? /* @__PURE__ */ jsxs67("div", { className: "px-6 py-12 text-center", children: [
      /* @__PURE__ */ jsx79(BellIcon2, { className: "w-8 h-8 text-tertiary mx-auto mb-2", strokeWidth: 1.5 }),
      /* @__PURE__ */ jsx79("p", { className: "text-sm font-sans text-secondary", children: "No notifications" }),
      /* @__PURE__ */ jsx79("p", { className: "text-xs font-sans text-tertiary mt-1", children: searchQuery ? "Try adjusting your search terms" : activeCategory !== "all" ? `No ${CATEGORY_CONFIG[activeCategory].label.toLowerCase()} notifications` : "You are all caught up" })
    ] }) : filteredNotifications.map((notification) => /* @__PURE__ */ jsx79(
      NotificationItem,
      {
        notification,
        isSelected: selectedIds.has(notification.id),
        onSelect: () => toggleSelect(notification.id),
        onClick: onNotificationClick ? () => onNotificationClick(notification) : void 0,
        onAction: notification.actionLabel ? () => onNotificationAction?.(notification) : void 0,
        timeZone,
        locale
      },
      notification.id
    )) })
  ] });
}

// src/data/DataGrid.tsx
import React28, {
  useState as useState21,
  useMemo as useMemo9,
  useCallback as useCallback13,
  useRef as useRef16,
  useEffect as useEffect16
} from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  flexRender
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  ChevronUpIcon as ChevronUpIcon2,
  ChevronDownIcon as ChevronDownIcon8,
  ChevronsUpDownIcon,
  ChevronLeftIcon as ChevronLeftIcon3,
  ChevronRightIcon as ChevronRightIcon6,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  DownloadIcon,
  ColumnsIcon,
  SearchIcon as SearchIcon7,
  XIcon as XIcon5,
  FilterIcon,
  AlertTriangleIcon as AlertTriangleIcon3,
  CheckIcon as CheckIcon9,
  MinusIcon
} from "lucide-react";
import { Fragment as Fragment20, jsx as jsx80, jsxs as jsxs68 } from "react/jsx-runtime";
function IndeterminateCheckbox({
  indeterminate,
  className = "",
  ...rest
}) {
  const ref = useRef16(null);
  useEffect16(() => {
    if (ref.current && typeof indeterminate === "boolean") {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate, rest.checked]);
  const isIndeterminate = !rest.checked && !!indeterminate;
  return (
    // Design-system checkbox (mirrors forms/Checkbox visuals at grid
    // density) -- the native input stays for a11y/peer state; the box is
    // the visible control. Focus ring is focus-visible-only so a mouse
    // click never leaves a lingering ring.
    /* @__PURE__ */ jsxs68("label", { className: `relative inline-flex cursor-pointer items-center ${className}`, children: [
      /* @__PURE__ */ jsx80("input", { type: "checkbox", ref, className: "peer sr-only", ...rest }),
      /* @__PURE__ */ jsx80(
        "span",
        {
          "aria-hidden": true,
          className: `
          flex h-4 w-4 items-center justify-center rounded border-2
          transition-all duration-150
          ${rest.checked || isIndeterminate ? "border-selection bg-selection" : "border-recessed bg-surface hover:border-secondary"}
          ${rest.disabled ? "cursor-not-allowed opacity-50" : ""}
          peer-focus-visible:ring-2 peer-focus-visible:ring-selection/40
        `,
          children: rest.checked ? /* @__PURE__ */ jsx80(CheckIcon9, { className: "h-3 w-3 text-white", strokeWidth: 3 }) : isIndeterminate ? /* @__PURE__ */ jsx80(MinusIcon, { className: "h-3 w-3 text-white", strokeWidth: 3 }) : null
        }
      )
    ] })
  );
}
function rowsToCsv(rows, columns) {
  const headers = columns.filter((col) => col.id !== "select").map((col) => {
    const header = typeof col.header === "string" ? col.header : col.id ?? "";
    return `"${header.replace(/"/g, '""')}"`;
  });
  const body = rows.map(
    (row) => columns.filter((col) => col.id !== "select").map((col) => {
      const key = "accessorKey" in col ? col.accessorKey : col.id;
      const value = key ? row[key] : "";
      const str = value == null ? "" : String(value);
      return `"${str.replace(/"/g, '""')}"`;
    }).join(",")
  );
  return [headers.join(","), ...body].join("\n");
}
function downloadFile(content, filename, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
function SkeletonRow({
  columnCount,
  dense
}) {
  const cellPadding = dense ? "px-3 py-1.5" : "px-4 py-3";
  return /* @__PURE__ */ jsx80("tr", { className: "animate-pulse", children: Array.from({ length: columnCount }).map((_, i) => /* @__PURE__ */ jsx80("td", { className: `${cellPadding} border-b border-recessed`, children: /* @__PURE__ */ jsx80("div", { className: "h-4 bg-recessed/40 rounded w-3/4" }) }, i)) });
}
function ResizeHandle({
  header,
  isResizing
}) {
  return /* @__PURE__ */ jsx80(
    "div",
    {
      onMouseDown: header.getResizeHandler(),
      onTouchStart: header.getResizeHandler(),
      role: "separator",
      "aria-orientation": "vertical",
      tabIndex: 0,
      onKeyDown: (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          header.getResizeHandler()(e);
        }
      },
      className: `
        absolute right-0 top-0 h-full w-1 cursor-col-resize
        select-none touch-none
        hover:bg-accent/40 active:bg-accent/60
        ${isResizing ? "bg-accent/60" : "bg-transparent"}
      `,
      style: { userSelect: "none" }
    }
  );
}
function columnAlign(meta) {
  if (meta !== null && typeof meta === "object" && "align" in meta) {
    const align = meta.align;
    if (align === "right" || align === "center") return align;
  }
  return "left";
}
var HEADER_TEXT_ALIGN = {
  left: "text-left",
  center: "text-center",
  right: "text-right"
};
var HEADER_JUSTIFY = {
  left: "",
  center: "justify-center",
  right: "justify-end"
};
var CELL_TEXT_ALIGN = {
  left: "",
  center: "text-center",
  right: "text-right"
};
var ROW_CONTROL_SELECTOR = 'button, a, input, select, textarea, label, [role="checkbox"], [role="switch"], [role="menuitem"]';
var GRID_OWNED_COLUMNS = /* @__PURE__ */ new Set(["select", "expand"]);
var ACTIVATABLE_CELL_FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-selection/40";
var PAGE_BUTTON_CLASS = "inline-flex items-center justify-center min-w-11 min-h-11 sm:min-w-0 sm:min-h-0 p-1.5 rounded-sm hover:bg-elevated disabled:opacity-30 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selection/40";
function PaginationControls({
  currentPage,
  pageCount,
  pageSize,
  totalRows,
  canPreviousPage,
  canNextPage,
  pageSizeOptions,
  onFirstPage,
  onPreviousPage,
  onNextPage,
  onLastPage,
  onPageSizeChange
}) {
  const rangeStart = totalRows === 0 ? 0 : currentPage * pageSize + 1;
  const rangeEnd = Math.min((currentPage + 1) * pageSize, totalRows);
  const displayPageCount = Math.max(1, pageCount);
  return /* @__PURE__ */ jsxs68("div", { className: "flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-2.5 border-t border-recessed bg-elevated/30", children: [
    /* @__PURE__ */ jsxs68("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx80("span", { className: "text-body-xs font-sans text-tertiary whitespace-nowrap", children: "Rows per page:" }),
      /* @__PURE__ */ jsx80(
        "select",
        {
          value: pageSize,
          onChange: (e) => onPageSizeChange(Number(e.target.value)),
          className: "px-2 py-1 text-body-xs font-sans bg-surface border border-recessed rounded-sm\n                     focus:outline-none focus:ring-1 focus:ring-selection/40",
          "aria-label": "Rows per page",
          children: pageSizeOptions.map((size) => /* @__PURE__ */ jsx80("option", { value: size, children: size }, size))
        }
      )
    ] }),
    /* @__PURE__ */ jsxs68("span", { className: "text-body-xs font-sans text-tertiary tabular-nums whitespace-nowrap", children: [
      rangeStart,
      "\u2013",
      rangeEnd,
      " of ",
      totalRows.toLocaleString("en-US")
    ] }),
    /* @__PURE__ */ jsxs68("div", { className: "flex items-center gap-0.5", children: [
      /* @__PURE__ */ jsx80(
        "button",
        {
          type: "button",
          onClick: onFirstPage,
          disabled: !canPreviousPage,
          className: PAGE_BUTTON_CLASS,
          "aria-label": "First page",
          children: /* @__PURE__ */ jsx80(ChevronsLeftIcon, { className: "w-4 h-4 text-tertiary", strokeWidth: 2 })
        }
      ),
      /* @__PURE__ */ jsx80(
        "button",
        {
          type: "button",
          onClick: onPreviousPage,
          disabled: !canPreviousPage,
          className: PAGE_BUTTON_CLASS,
          "aria-label": "Previous page",
          children: /* @__PURE__ */ jsx80(ChevronLeftIcon3, { className: "w-4 h-4 text-tertiary", strokeWidth: 2 })
        }
      ),
      /* @__PURE__ */ jsxs68("span", { className: "px-2 text-body-xs font-sans text-tertiary tabular-nums whitespace-nowrap", children: [
        "Page ",
        currentPage + 1,
        " of ",
        displayPageCount
      ] }),
      /* @__PURE__ */ jsx80(
        "button",
        {
          type: "button",
          onClick: onNextPage,
          disabled: !canNextPage,
          className: PAGE_BUTTON_CLASS,
          "aria-label": "Next page",
          children: /* @__PURE__ */ jsx80(ChevronRightIcon6, { className: "w-4 h-4 text-tertiary", strokeWidth: 2 })
        }
      ),
      /* @__PURE__ */ jsx80(
        "button",
        {
          type: "button",
          onClick: onLastPage,
          disabled: !canNextPage,
          className: PAGE_BUTTON_CLASS,
          "aria-label": "Last page",
          children: /* @__PURE__ */ jsx80(ChevronsRightIcon, { className: "w-4 h-4 text-tertiary", strokeWidth: 2 })
        }
      )
    ] })
  ] });
}
function DataGrid({
  data,
  columns,
  // Selection
  enableRowSelection = false,
  controlledRowSelection,
  onControlledRowSelectionChange,
  hideSelectAll = false,
  rowSelectionLabel,
  getRowLabel,
  rowExpandLabel,
  fixedLayout = false,
  minTableWidth,
  enableMultiRowSelection = true,
  onSelectionChange,
  onRowSelect,
  // Column visibility
  enableColumnVisibility = false,
  // Sorting
  onSort,
  // Filtering
  enableColumnFilters = false,
  enableGlobalFilter = false,
  onFilter,
  // Client-side pagination
  enablePagination = true,
  pageSizeOptions = [10, 25, 50, 100],
  defaultPageSize = 25,
  // Server-side pagination
  pagination,
  // Virtualization
  enableVirtualization = false,
  estimateRowHeight,
  rowHeight = 40,
  virtualizedHeight = 600,
  // Column resize
  enableColumnResize = false,
  // Export
  enableExport = false,
  onExport,
  // Row expansion
  enableExpanding = false,
  renderSubComponent,
  getSubRows,
  // Visual
  isLoading: isLoadingProp,
  loading: loadingProp,
  emptyMessage = "No data available",
  emptyState,
  errorMessage,
  errorState,
  density,
  dense = false,
  striped = true,
  hoverable = true,
  bordered = false,
  className = "",
  skeletonRowCount: skeletonRowCountProp,
  // Interaction
  onRowClick,
  getRowId
}) {
  const isLoading = isLoadingProp ?? loadingProp ?? false;
  const hasError = errorMessage != null || errorState != null;
  const effectiveRowHeight = estimateRowHeight ?? rowHeight;
  const handleSelectionChange = onSelectionChange ?? onRowSelect;
  const isServerPagination = pagination != null;
  const effectiveDensity = density ?? (dense ? "compact" : "default");
  const isCompact = effectiveDensity === "compact";
  const isComfortable = effectiveDensity === "comfortable";
  const [sorting, setSorting] = useState21([]);
  const [columnFilters, setColumnFilters] = useState21([]);
  const [globalFilter, setGlobalFilter] = useState21("");
  const [columnVisibility, setColumnVisibility] = useState21({});
  const [internalRowSelection, setInternalRowSelection] = useState21({});
  const selectionIsControlled = controlledRowSelection !== void 0 && onControlledRowSelectionChange !== void 0;
  const rowSelection = selectionIsControlled ? controlledRowSelection : internalRowSelection;
  const [expanded, setExpanded] = useState21({});
  const [showColumnMenu, setShowColumnMenu] = useState21(false);
  const [showExportMenu, setShowExportMenu] = useState21(false);
  const [showFilterRow, setShowFilterRow] = useState21(enableColumnFilters);
  const tableContainerRef = useRef16(null);
  const gridRef = useRef16(null);
  const columnMenuRef = useRef16(null);
  const exportMenuRef = useRef16(null);
  useEffect16(() => {
    function handleClickOutside(e) {
      if (columnMenuRef.current && !columnMenuRef.current.contains(e.target)) {
        setShowColumnMenu(false);
      }
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const rowNamingRef = useRef16({ getRowLabel, rowSelectionLabel, rowExpandLabel });
  rowNamingRef.current = { getRowLabel, rowSelectionLabel, rowExpandLabel };
  const selectionColumn = useMemo9(
    () => ({
      id: "select",
      header: ({ table: table2 }) => enableMultiRowSelection && !hideSelectAll ? /* @__PURE__ */ jsx80(
        IndeterminateCheckbox,
        {
          checked: table2.getIsAllPageRowsSelected(),
          indeterminate: table2.getIsSomePageRowsSelected(),
          onChange: table2.getToggleAllPageRowsSelectedHandler(),
          "aria-label": "Select all rows"
        }
      ) : null,
      cell: ({ row }) => {
        const naming = rowNamingRef.current;
        const label = naming.rowSelectionLabel ? naming.rowSelectionLabel(row.original) : naming.getRowLabel ? `Select ${naming.getRowLabel(row.original)}` : `Select row ${row.id}`;
        return /* @__PURE__ */ jsx80(
          IndeterminateCheckbox,
          {
            checked: row.getIsSelected(),
            disabled: !row.getCanSelect(),
            onChange: row.getToggleSelectedHandler(),
            "aria-label": label
          }
        );
      },
      size: 40,
      minSize: 40,
      maxSize: 40,
      enableSorting: false,
      enableResizing: false,
      enableHiding: false
    }),
    [enableMultiRowSelection, hideSelectAll]
  );
  const expandColumn = useMemo9(
    () => ({
      id: "expand",
      header: () => null,
      cell: ({ row }) => {
        const canExpand = renderSubComponent ? true : row.getCanExpand();
        if (!canExpand) return null;
        const naming = rowNamingRef.current;
        const expanded2 = row.getIsExpanded();
        const label = naming.rowExpandLabel ? naming.rowExpandLabel(row.original) : naming.getRowLabel ? `Show details for ${naming.getRowLabel(row.original)}` : expanded2 ? "Collapse row" : "Expand row";
        return /* @__PURE__ */ jsx80(
          "button",
          {
            type: "button",
            onClick: (e) => {
              e.stopPropagation();
              row.toggleExpanded();
            },
            className: "inline-flex items-center justify-center min-w-11 min-h-11 sm:min-w-0 sm:min-h-0 p-0.5 rounded-sm hover:bg-elevated transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selection/40",
            "aria-label": label,
            "aria-expanded": expanded2,
            children: /* @__PURE__ */ jsx80(
              ChevronRightIcon6,
              {
                className: `w-4 h-4 text-tertiary transition-transform ${row.getIsExpanded() ? "rotate-90" : ""}`,
                strokeWidth: 2
              }
            )
          }
        );
      },
      size: 36,
      minSize: 36,
      maxSize: 36,
      enableSorting: false,
      enableResizing: false,
      enableHiding: false
    }),
    [renderSubComponent]
  );
  const allColumns = useMemo9(() => {
    const cols = [];
    if (enableExpanding && renderSubComponent) cols.push(expandColumn);
    if (enableRowSelection) cols.push(selectionColumn);
    cols.push(...columns);
    return cols;
  }, [columns, enableRowSelection, selectionColumn, enableExpanding, renderSubComponent, expandColumn]);
  const columnResizeMode = "onChange";
  const table = useReactTable({
    data,
    columns: allColumns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      columnVisibility,
      rowSelection,
      expanded
    },
    enableRowSelection,
    enableMultiRowSelection,
    enableColumnResizing: enableColumnResize,
    enableExpanding,
    columnResizeMode,
    getSubRows,
    onSortingChange: (updater) => {
      setSorting(updater);
      const next = typeof updater === "function" ? updater(sorting) : updater;
      onSort?.(next);
    },
    onColumnFiltersChange: (updater) => {
      setColumnFilters(updater);
      const next = typeof updater === "function" ? updater(columnFilters) : updater;
      onFilter?.(
        next.map((f) => ({
          id: f.id,
          value: String(f.value ?? "")
        }))
      );
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: (updater) => {
      const next = typeof updater === "function" ? updater(rowSelection) : updater;
      if (selectionIsControlled) {
        onControlledRowSelectionChange(next);
      } else {
        setInternalRowSelection(next);
      }
    },
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: enableExpanding ? getExpandedRowModel() : void 0,
    getPaginationRowModel: enablePagination && !isServerPagination && !enableVirtualization ? getPaginationRowModel() : void 0,
    getRowId,
    initialState: {
      pagination: {
        pageSize: isServerPagination ? pagination.pageSize : defaultPageSize
      }
    }
  });
  const fixedLayoutTotalSize = useMemo9(() => {
    if (!fixedLayout) return 0;
    return table.getVisibleLeafColumns().reduce((sum, c) => sum + (c.columnDef.size ?? 0), 0);
  }, [fixedLayout, table, columnVisibility, allColumns]);
  const selectedRows = useMemo9(
    () => table.getSelectedRowModel().rows.map((r) => r.original),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowSelection, table]
  );
  useEffect16(() => {
    handleSelectionChange?.(selectedRows);
  }, [selectedRows, handleSelectionChange]);
  const { rows } = table.getRowModel();
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => effectiveRowHeight,
    overscan: 15,
    enabled: enableVirtualization
  });
  const virtualRows = enableVirtualization ? virtualizer.getVirtualItems() : [];
  const totalVirtualSize = enableVirtualization ? virtualizer.getTotalSize() : 0;
  const showsMessageRow = !isLoading && (hasError || rows.length === 0);
  const [messageWidth, setMessageWidth] = useState21(null);
  useEffect16(() => {
    if (!showsMessageRow) return void 0;
    const container = tableContainerRef.current;
    if (!container) return void 0;
    const measure = () => {
      setMessageWidth(container.clientWidth > 0 ? container.clientWidth : null);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return void 0;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [showsMessageRow]);
  const handleRowClick = useCallback13(
    (row, e) => {
      const target = e.target;
      if (target?.closest(ROW_CONTROL_SELECTOR)) {
        return;
      }
      onRowClick?.(row.original);
    },
    [onRowClick]
  );
  const handleRowKeyDown = useCallback13(
    (row, e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      const target = e.target;
      if (target?.closest(ROW_CONTROL_SELECTOR)) return;
      e.preventDefault();
      onRowClick?.(row.original);
    },
    [onRowClick]
  );
  const activationTabIndex = (row, cellIndex, isFirstRenderedRow) => {
    if (!onRowClick) return void 0;
    const stop = row.getVisibleCells().findIndex((cell) => !GRID_OWNED_COLUMNS.has(cell.column.id));
    if (cellIndex !== stop) return void 0;
    return isFirstRenderedRow ? 0 : -1;
  };
  const handleExport = useCallback13(
    (format, scope) => {
      setShowExportMenu(false);
      if (onExport) {
        onExport(format);
        return;
      }
      let exportRows;
      if (scope === "selected") {
        exportRows = selectedRows;
      } else if (scope === "visible") {
        exportRows = rows.map((r) => r.original);
      } else {
        exportRows = data;
      }
      if (format === "csv") {
        const csv = rowsToCsv(exportRows, columns);
        downloadFile(csv, "export.csv", "text/csv;charset=utf-8;");
      } else {
        const json = JSON.stringify(exportRows, null, 2);
        downloadFile(json, "export.json", "application/json");
      }
    },
    [onExport, selectedRows, rows, data, columns]
  );
  const handleGridKeyDown = useCallback13(
    (e) => {
      const target = e.target;
      const cell = target.closest("td, th");
      if (!cell) return;
      const row = cell.parentElement;
      if (!row) return;
      const cellIndex = Array.from(row.cells).indexOf(
        cell
      );
      let nextRow = null;
      let nextCellIndex = cellIndex;
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          nextRow = row.nextElementSibling;
          break;
        case "ArrowUp":
          e.preventDefault();
          nextRow = row.previousElementSibling;
          break;
        case "ArrowRight":
          e.preventDefault();
          nextCellIndex = Math.min(cellIndex + 1, row.cells.length - 1);
          nextRow = row;
          break;
        case "ArrowLeft":
          e.preventDefault();
          nextCellIndex = Math.max(cellIndex - 1, 0);
          nextRow = row;
          break;
        case "Home":
          e.preventDefault();
          nextCellIndex = 0;
          nextRow = row;
          break;
        case "End":
          e.preventDefault();
          nextCellIndex = row.cells.length - 1;
          nextRow = row;
          break;
        default:
          return;
      }
      if (nextRow && nextRow.cells[nextCellIndex]) {
        const nextCell = nextRow.cells[nextCellIndex];
        const focusable = nextCell.querySelector(
          "a, button, input, [tabindex]"
        );
        if (focusable) {
          focusable.focus();
        } else {
          nextCell.setAttribute("tabindex", "0");
          nextCell.focus();
        }
      }
    },
    []
  );
  const cellPadding = isCompact ? "px-3 py-1.5" : isComfortable ? "px-5 py-3.5" : "px-4 py-2.5";
  const headerPadding = isCompact ? "px-3 py-2" : isComfortable ? "px-5 py-3" : "px-4 py-2.5";
  const hasToolbar = enableColumnVisibility || enableExport || enableGlobalFilter || enableColumnFilters;
  const visibleColumnCount = table.getVisibleFlatColumns().length;
  const skeletonRowCount = skeletonRowCountProp ?? (isCompact ? 10 : 6);
  const renderMessageRow = (content, padded = true) => /* @__PURE__ */ jsx80("tr", { children: /* @__PURE__ */ jsx80("td", { colSpan: visibleColumnCount, className: "p-0", children: /* @__PURE__ */ jsx80(
    "div",
    {
      "data-grid-message": "",
      className: `sticky left-0 ${cellPadding} text-center`,
      style: messageWidth !== null ? { width: messageWidth } : void 0,
      children: /* @__PURE__ */ jsx80("div", { className: `${padded ? "py-12 " : ""}flex flex-col items-center gap-2`, children: content })
    }
  ) }) });
  const serverPageCount = isServerPagination ? Math.max(1, Math.ceil(pagination.total / pagination.pageSize)) : 0;
  const configuredPageSize = pagination ? pagination.pageSize : defaultPageSize;
  const effectivePageSizeOptions = useMemo9(() => {
    const merged = new Set(pageSizeOptions);
    merged.add(configuredPageSize);
    return Array.from(merged).sort((a, b) => a - b);
  }, [pageSizeOptions, configuredPageSize]);
  return /* @__PURE__ */ jsxs68(
    "div",
    {
      className: `flex flex-col bg-surface rounded-sm ${className}`,
      role: "region",
      "aria-label": "Data grid",
      children: [
        hasToolbar && /* @__PURE__ */ jsxs68("div", { className: "flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-b border-recessed bg-elevated/30", children: [
          /* @__PURE__ */ jsxs68("div", { className: "flex items-center gap-3", children: [
            enableRowSelection && selectedRows.length > 0 && /* @__PURE__ */ jsxs68("span", { className: "text-body-xs font-sans text-secondary tabular-nums", children: [
              selectedRows.length,
              " selected"
            ] }),
            enableGlobalFilter && /* @__PURE__ */ jsxs68("div", { className: "relative", children: [
              /* @__PURE__ */ jsx80(SearchIcon7, { className: "absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-tertiary", strokeWidth: 2 }),
              /* @__PURE__ */ jsx80(
                "input",
                {
                  type: "text",
                  value: globalFilter,
                  onChange: (e) => setGlobalFilter(e.target.value),
                  placeholder: "Search all columns...",
                  "aria-label": "Search all columns",
                  className: "\n                    pl-8 pr-7 py-1.5 w-56 text-body-xs font-sans\n                    bg-surface border border-recessed rounded-sm\n                    placeholder:text-quaternary text-primary\n                    focus:outline-none focus:ring-1 focus:ring-selection/40\n                    transition-colors\n                  "
                }
              ),
              globalFilter && /* @__PURE__ */ jsx80(
                "button",
                {
                  onClick: () => setGlobalFilter(""),
                  className: "absolute right-2 top-1/2 -translate-y-1/2 text-tertiary hover:text-primary",
                  "aria-label": "Clear search",
                  children: /* @__PURE__ */ jsx80(XIcon5, { className: "w-3.5 h-3.5", strokeWidth: 2 })
                }
              )
            ] })
          ] }),
          /* @__PURE__ */ jsxs68("div", { className: "flex items-center gap-1.5", children: [
            enableColumnFilters && /* @__PURE__ */ jsxs68(
              "button",
              {
                onClick: () => setShowFilterRow((p) => !p),
                className: `
                  flex items-center gap-1.5 px-2 py-1 text-caption-md font-sans
                  rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selection/40
                  ${showFilterRow ? "bg-selection/10 text-primary" : "text-secondary hover:text-primary hover:bg-elevated"}
                `,
                "aria-label": "Toggle column filters",
                "aria-pressed": showFilterRow,
                children: [
                  /* @__PURE__ */ jsx80(FilterIcon, { className: "w-3.5 h-3.5", strokeWidth: 2 }),
                  "Filters"
                ]
              }
            ),
            enableExport && /* @__PURE__ */ jsxs68("div", { className: "relative", ref: exportMenuRef, children: [
              /* @__PURE__ */ jsxs68(
                "button",
                {
                  onClick: () => setShowExportMenu((p) => !p),
                  className: "flex items-center gap-1.5 px-2 py-1 text-caption-md font-sans text-secondary hover:text-primary hover:bg-elevated rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selection/40",
                  "aria-haspopup": "menu",
                  "aria-expanded": showExportMenu,
                  children: [
                    /* @__PURE__ */ jsx80(DownloadIcon, { className: "w-3.5 h-3.5", strokeWidth: 2 }),
                    "Export"
                  ]
                }
              ),
              showExportMenu && /* @__PURE__ */ jsxs68(
                "div",
                {
                  className: "absolute right-0 top-full mt-1 w-44 bg-surface border border-recessed rounded-sm shadow-lg z-20",
                  role: "menu",
                  children: [
                    /* @__PURE__ */ jsx80(
                      "button",
                      {
                        role: "menuitem",
                        onClick: () => handleExport("csv", "visible"),
                        className: "w-full px-3 py-2 text-body-xs font-sans text-left hover:bg-elevated transition-colors",
                        children: "Export visible (CSV)"
                      }
                    ),
                    selectedRows.length > 0 && /* @__PURE__ */ jsx80(
                      "button",
                      {
                        role: "menuitem",
                        onClick: () => handleExport("csv", "selected"),
                        className: "w-full px-3 py-2 text-body-xs font-sans text-left hover:bg-elevated transition-colors",
                        children: "Export selected (CSV)"
                      }
                    ),
                    /* @__PURE__ */ jsx80(
                      "button",
                      {
                        role: "menuitem",
                        onClick: () => handleExport("csv", "all"),
                        className: "w-full px-3 py-2 text-body-xs font-sans text-left hover:bg-elevated transition-colors",
                        children: "Export all (CSV)"
                      }
                    ),
                    /* @__PURE__ */ jsx80("div", { className: "border-t border-recessed" }),
                    /* @__PURE__ */ jsx80(
                      "button",
                      {
                        role: "menuitem",
                        onClick: () => handleExport("json", "all"),
                        className: "w-full px-3 py-2 text-body-xs font-sans text-left hover:bg-elevated transition-colors",
                        children: "Export all (JSON)"
                      }
                    )
                  ]
                }
              )
            ] }),
            enableColumnVisibility && /* @__PURE__ */ jsxs68("div", { className: "relative", ref: columnMenuRef, children: [
              /* @__PURE__ */ jsxs68(
                "button",
                {
                  onClick: () => setShowColumnMenu((p) => !p),
                  className: "flex items-center gap-1.5 px-2 py-1 text-caption-md font-sans text-secondary hover:text-primary hover:bg-elevated rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selection/40",
                  "aria-haspopup": "menu",
                  "aria-expanded": showColumnMenu,
                  children: [
                    /* @__PURE__ */ jsx80(ColumnsIcon, { className: "w-3.5 h-3.5", strokeWidth: 2 }),
                    "Columns"
                  ]
                }
              ),
              showColumnMenu && /* @__PURE__ */ jsx80(
                "div",
                {
                  className: "absolute right-0 top-full mt-1 w-48 bg-surface border border-recessed rounded-sm shadow-lg z-20 py-1 max-h-64 overflow-y-auto",
                  role: "menu",
                  children: table.getAllLeafColumns().map((column) => {
                    if (column.id === "select") return null;
                    if (!column.getCanHide()) return null;
                    return /* @__PURE__ */ jsxs68(
                      "label",
                      {
                        className: "flex items-center gap-2 px-3 py-1.5 text-body-xs font-sans hover:bg-elevated cursor-pointer",
                        children: [
                          /* @__PURE__ */ jsx80(
                            "input",
                            {
                              type: "checkbox",
                              checked: column.getIsVisible(),
                              onChange: column.getToggleVisibilityHandler(),
                              className: "h-3.5 w-3.5 rounded border-recessed text-selection"
                            }
                          ),
                          /* @__PURE__ */ jsx80("span", { className: "text-primary truncate", children: typeof column.columnDef.header === "string" ? column.columnDef.header : column.id })
                        ]
                      },
                      column.id
                    );
                  })
                }
              )
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsx80(
          "div",
          {
            ref: tableContainerRef,
            className: "overflow-auto",
            style: enableVirtualization ? { height: virtualizedHeight } : void 0,
            children: /* @__PURE__ */ jsxs68(
              "table",
              {
                ref: gridRef,
                role: "grid",
                "aria-rowcount": data.length,
                "aria-colcount": visibleColumnCount,
                className: `w-full border-collapse ${fixedLayout ? "table-fixed" : ""}`,
                style: enableColumnResize && !fixedLayout ? { minWidth: table.getCenterTotalSize(), width: "100%" } : fixedLayout && minTableWidth !== void 0 ? { minWidth: minTableWidth, width: "100%" } : void 0,
                onKeyDown: handleGridKeyDown,
                children: [
                  /* @__PURE__ */ jsxs68("thead", { className: "bg-elevated/50 sticky top-0 z-10", children: [
                    table.getHeaderGroups().map((headerGroup) => /* @__PURE__ */ jsx80("tr", { role: "row", children: headerGroup.headers.map((header) => {
                      const canSort = header.column.getCanSort();
                      const sorted = header.column.getIsSorted();
                      const align = columnAlign(header.column.columnDef.meta);
                      const ariaSortValue = sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : canSort ? "none" : void 0;
                      return /* @__PURE__ */ jsxs68(
                        "th",
                        {
                          role: "columnheader",
                          "aria-sort": ariaSortValue,
                          scope: "col",
                          className: `
                        ${headerPadding} ${HEADER_TEXT_ALIGN[align]} text-label-xs font-sans font-semibold
                        uppercase tracking-widest text-tertiary relative
                        ${bordered ? "border-r border-recessed last:border-r-0" : ""}
                        ${canSort ? "cursor-pointer select-none hover:bg-elevated transition-colors" : ""}
                      `,
                          style: {
                            width: fixedLayout ? header.column.columnDef.size && fixedLayoutTotalSize > 0 ? `${(header.column.columnDef.size / fixedLayoutTotalSize * 100).toFixed(2)}%` : "auto" : enableColumnResize ? header.getSize() : header.column.columnDef.size ?? "auto",
                            minWidth: enableColumnResize && !fixedLayout ? header.column.columnDef.minSize : void 0,
                            maxWidth: enableColumnResize && !fixedLayout ? header.column.columnDef.maxSize : void 0
                          },
                          onClick: canSort ? header.column.getToggleSortingHandler() : void 0,
                          children: [
                            /* @__PURE__ */ jsxs68("div", { className: `flex items-center gap-1 ${HEADER_JUSTIFY[align]}`, children: [
                              header.isPlaceholder ? null : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              ),
                              canSort && /* @__PURE__ */ jsx80("span", { className: "flex-shrink-0 ml-0.5", children: sorted === "asc" ? /* @__PURE__ */ jsx80(
                                ChevronUpIcon2,
                                {
                                  className: "w-3.5 h-3.5 text-primary",
                                  strokeWidth: 2
                                }
                              ) : sorted === "desc" ? /* @__PURE__ */ jsx80(
                                ChevronDownIcon8,
                                {
                                  className: "w-3.5 h-3.5 text-primary",
                                  strokeWidth: 2
                                }
                              ) : /* @__PURE__ */ jsx80(
                                ChevronsUpDownIcon,
                                {
                                  className: "w-3.5 h-3.5 opacity-30",
                                  strokeWidth: 2
                                }
                              ) })
                            ] }),
                            enableColumnResize && header.column.getCanResize() && /* @__PURE__ */ jsx80(
                              ResizeHandle,
                              {
                                header,
                                isResizing: header.column.getIsResizing()
                              }
                            )
                          ]
                        },
                        header.id
                      );
                    }) }, headerGroup.id)),
                    showFilterRow && /* @__PURE__ */ jsx80("tr", { role: "row", className: "bg-elevated/30", children: table.getVisibleFlatColumns().map((column) => /* @__PURE__ */ jsx80("th", { className: `${headerPadding} p-1`, children: column.getCanFilter() && column.id !== "select" ? /* @__PURE__ */ jsx80(
                      "input",
                      {
                        type: "text",
                        value: column.getFilterValue() ?? "",
                        onChange: (e) => column.setFilterValue(e.target.value || void 0),
                        placeholder: `Filter...`,
                        "aria-label": `Filter ${typeof column.columnDef.header === "string" ? column.columnDef.header : column.id}`,
                        className: "\n                          w-full px-2 py-1 text-caption-md font-sans\n                          bg-surface border border-recessed rounded-sm\n                          placeholder:text-quaternary text-primary\n                          focus:outline-none focus:ring-1 focus:ring-selection/40\n                        "
                      }
                    ) : null }, column.id)) })
                  ] }),
                  /* @__PURE__ */ jsx80("tbody", { children: isLoading ? (
                    // Skeleton loading rows
                    Array.from({ length: skeletonRowCount }).map((_, i) => /* @__PURE__ */ jsx80(
                      SkeletonRow,
                      {
                        columnCount: visibleColumnCount,
                        dense: isCompact
                      },
                      `skeleton-${i}`
                    ))
                  ) : hasError ? (
                    // Error state
                    renderMessageRow(
                      errorState ?? /* @__PURE__ */ jsxs68(Fragment20, { children: [
                        /* @__PURE__ */ jsx80(AlertTriangleIcon3, { className: "w-8 h-8 text-error", strokeWidth: 1.5 }),
                        /* @__PURE__ */ jsx80("span", { className: "text-body-sm font-sans text-secondary mt-1", children: errorMessage })
                      ] })
                    )
                  ) : rows.length === 0 ? (
                    // Empty state
                    emptyState != null ? renderMessageRow(emptyState, false) : renderMessageRow(
                      /* @__PURE__ */ jsx80("span", { className: "text-body-sm font-sans text-tertiary", children: emptyMessage })
                    )
                  ) : enableVirtualization ? (
                    // Virtualized rows
                    /* @__PURE__ */ jsxs68(Fragment20, { children: [
                      virtualRows.length > 0 && /* @__PURE__ */ jsx80("tr", { "aria-hidden": "true", children: /* @__PURE__ */ jsx80(
                        "td",
                        {
                          colSpan: visibleColumnCount,
                          style: { height: virtualRows[0].start, padding: 0 }
                        }
                      ) }),
                      virtualRows.map((virtualRow, renderedIndex) => {
                        const row = rows[virtualRow.index];
                        return /* @__PURE__ */ jsx80(
                          "tr",
                          {
                            role: "row",
                            "aria-rowindex": virtualRow.index + 2,
                            "aria-selected": row.getIsSelected() || void 0,
                            "data-index": virtualRow.index,
                            ref: virtualizer.measureElement,
                            className: `
                        ${striped && virtualRow.index % 2 === 1 ? "bg-elevated/30" : ""}
                        ${hoverable ? "hover:bg-elevated/50" : ""}
                        ${row.getIsSelected() ? "bg-selection/10" : ""}
                        ${onRowClick ? "cursor-pointer" : ""}
                        transition-colors
                      `,
                            onClick: (e) => handleRowClick(row, e),
                            onKeyDown: onRowClick ? (e) => handleRowKeyDown(row, e) : void 0,
                            children: row.getVisibleCells().map((cell, cellIndex) => /* @__PURE__ */ jsx80(
                              "td",
                              {
                                role: "gridcell",
                                tabIndex: activationTabIndex(row, cellIndex, renderedIndex === 0),
                                className: `
                            ${cellPadding} text-body-sm font-sans text-primary
                            ${CELL_TEXT_ALIGN[columnAlign(cell.column.columnDef.meta)]}
                            ${bordered ? "border-r border-recessed last:border-r-0" : ""}
                            ${onRowClick ? ACTIVATABLE_CELL_FOCUS : ""}
                            border-b border-recessed
                          `,
                                style: enableColumnResize ? { width: cell.column.getSize() } : void 0,
                                children: flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )
                              },
                              cell.id
                            ))
                          },
                          row.id
                        );
                      }),
                      virtualRows.length > 0 && /* @__PURE__ */ jsx80("tr", { "aria-hidden": "true", children: /* @__PURE__ */ jsx80(
                        "td",
                        {
                          colSpan: visibleColumnCount,
                          style: {
                            height: totalVirtualSize - virtualRows[virtualRows.length - 1].end,
                            padding: 0
                          }
                        }
                      ) })
                    ] })
                  ) : (
                    // Standard (non-virtualized) rows
                    rows.map((row, index) => /* @__PURE__ */ jsxs68(React28.Fragment, { children: [
                      /* @__PURE__ */ jsx80(
                        "tr",
                        {
                          role: "row",
                          "aria-rowindex": index + 2,
                          "aria-selected": row.getIsSelected() || void 0,
                          className: `
                      ${striped && index % 2 === 1 ? "bg-elevated/30" : ""}
                      ${hoverable ? "hover:bg-elevated/50" : ""}
                      ${row.getIsSelected() ? "bg-selection/10" : ""}
                      ${onRowClick ? "cursor-pointer" : ""}
                      transition-colors
                    `,
                          onClick: (e) => handleRowClick(row, e),
                          onKeyDown: onRowClick ? (e) => handleRowKeyDown(row, e) : void 0,
                          children: row.getVisibleCells().map((cell, cellIndex) => /* @__PURE__ */ jsx80(
                            "td",
                            {
                              role: "gridcell",
                              tabIndex: activationTabIndex(row, cellIndex, index === 0),
                              className: `
                          ${cellPadding} text-body-sm font-sans text-primary
                          ${CELL_TEXT_ALIGN[columnAlign(cell.column.columnDef.meta)]}
                          ${bordered ? "border-r border-recessed last:border-r-0" : ""}
                          ${onRowClick ? ACTIVATABLE_CELL_FOCUS : ""}
                          border-b border-recessed
                        `,
                              style: enableColumnResize ? { width: cell.column.getSize() } : void 0,
                              children: flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )
                            },
                            cell.id
                          ))
                        }
                      ),
                      enableExpanding && row.getIsExpanded() && renderSubComponent && /* @__PURE__ */ jsx80("tr", { className: "bg-elevated/20", children: /* @__PURE__ */ jsx80("td", { colSpan: visibleColumnCount, className: "px-4 py-3 border-b border-recessed", children: renderSubComponent({ row }) }) })
                    ] }, row.id))
                  ) })
                ]
              }
            )
          }
        ),
        isServerPagination && !(pagination.total === 0 && rows.length === 0) && /* @__PURE__ */ jsx80(
          PaginationControls,
          {
            currentPage: pagination.page,
            pageCount: serverPageCount,
            pageSize: pagination.pageSize,
            totalRows: pagination.total,
            canPreviousPage: pagination.page > 0,
            canNextPage: pagination.page < serverPageCount - 1,
            pageSizeOptions: effectivePageSizeOptions,
            onFirstPage: () => pagination.onPageChange(0),
            onPreviousPage: () => pagination.onPageChange(pagination.page - 1),
            onNextPage: () => pagination.onPageChange(pagination.page + 1),
            onLastPage: () => pagination.onPageChange(serverPageCount - 1),
            onPageSizeChange: (size) => pagination.onPageSizeChange?.(size)
          }
        ),
        enablePagination && !isServerPagination && !enableVirtualization && table.getFilteredRowModel().rows.length > 0 && /* @__PURE__ */ jsx80(
          PaginationControls,
          {
            currentPage: table.getState().pagination.pageIndex,
            pageCount: table.getPageCount(),
            pageSize: table.getState().pagination.pageSize,
            totalRows: table.getFilteredRowModel().rows.length,
            canPreviousPage: table.getCanPreviousPage(),
            canNextPage: table.getCanNextPage(),
            pageSizeOptions: effectivePageSizeOptions,
            onFirstPage: () => table.setPageIndex(0),
            onPreviousPage: () => table.previousPage(),
            onNextPage: () => table.nextPage(),
            onLastPage: () => table.setPageIndex(table.getPageCount() - 1),
            onPageSizeChange: (size) => table.setPageSize(size)
          }
        ),
        enableVirtualization && /* @__PURE__ */ jsxs68("div", { className: "flex items-center justify-between px-4 py-2 border-t border-recessed bg-elevated/30", children: [
          /* @__PURE__ */ jsxs68("span", { className: "text-caption-md font-sans text-tertiary tabular-nums", children: [
            rows.length.toLocaleString(),
            " rows (virtualized)"
          ] }),
          enableRowSelection && selectedRows.length > 0 && /* @__PURE__ */ jsxs68("span", { className: "text-caption-md font-sans text-secondary tabular-nums", children: [
            selectedRows.length,
            " selected"
          ] })
        ] })
      ]
    }
  );
}

// src/data/DataTable.tsx
import { jsx as jsx81, jsxs as jsxs69 } from "react/jsx-runtime";
var HEADER_CLASS = "py-3 px-4 text-label-xs font-sans font-semibold uppercase tracking-widest text-tertiary";
function DataTable({
  columns,
  data,
  striped = false,
  hoverable = false,
  className = "",
  rowKey,
  emptyMessage = "No data available"
}) {
  const getRowKey = (row, index) => {
    if (!rowKey) return String(index);
    if (typeof rowKey === "function") return rowKey(row);
    return String(row[rowKey]);
  };
  const getCellValue = (row, column) => {
    if (typeof column.accessor === "function") {
      return column.accessor(row);
    }
    return row[column.accessor];
  };
  const getAlignClass = (align) => {
    switch (align) {
      case "center":
        return "text-center";
      case "right":
        return "text-right";
      default:
        return "text-left";
    }
  };
  const monospaceColumns = columns.map(
    (column) => column.monospace ?? data.some((row) => typeof getCellValue(row, column) === "number")
  );
  return /* @__PURE__ */ jsx81(Card, { className, children: /* @__PURE__ */ jsx81("div", { className: "overflow-x-auto", children: /* @__PURE__ */ jsxs69("table", { className: "w-full", children: [
    /* @__PURE__ */ jsx81("thead", { children: /* @__PURE__ */ jsx81("tr", { className: "border-b border-recessed bg-elevated", children: columns.map((column, index) => /* @__PURE__ */ jsx81(
      "th",
      {
        className: `${HEADER_CLASS} ${getAlignClass(column.align)}`,
        style: column.width ? { width: column.width } : void 0,
        children: column.header
      },
      index
    )) }) }),
    /* @__PURE__ */ jsx81("tbody", { children: data.length === 0 ? /* @__PURE__ */ jsx81("tr", { children: /* @__PURE__ */ jsx81(
      "td",
      {
        colSpan: columns.length,
        className: "py-8 px-4 text-center font-sans text-tertiary",
        children: emptyMessage
      }
    ) }) : data.map((row, rowIndex) => /* @__PURE__ */ jsx81(
      "tr",
      {
        className: `border-b border-recessed ${striped && rowIndex % 2 === 1 ? "bg-elevated/50" : ""} ${hoverable ? "hover:bg-elevated transition-colors" : ""}`,
        children: columns.map((column, colIndex) => {
          const value = getCellValue(row, column);
          const displayValue = column.cell ? column.cell(value, row) : String(value ?? "");
          return /* @__PURE__ */ jsx81(
            "td",
            {
              className: `py-3 px-4 text-sm ${getAlignClass(column.align)} ${monospaceColumns[colIndex] ? "font-mono tabular-nums" : "font-sans"}`,
              children: displayValue
            },
            colIndex
          );
        })
      },
      getRowKey(row, rowIndex)
    )) })
  ] }) }) });
}

// src/data/Table.tsx
import { ArrowUpIcon, ArrowDownIcon } from "lucide-react";

// src/data/TableScroll.tsx
import { jsx as jsx82 } from "react/jsx-runtime";
function TableScroll({ label, className = "", children, ...rest }) {
  return /* @__PURE__ */ jsx82(
    "div",
    {
      ...rest,
      role: "region",
      "aria-label": label,
      tabIndex: 0,
      className: `overflow-x-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-selection ${className}`.trim(),
      children
    }
  );
}

// src/data/Table.tsx
import { Fragment as Fragment21, jsx as jsx83, jsxs as jsxs70 } from "react/jsx-runtime";
function Table({
  columns,
  data,
  onRowClick,
  sortConfig,
  onSort,
  hoverable = true,
  stickyHeader = false,
  label,
  className = ""
}) {
  const handleSort = (key, sortable) => {
    if (sortable && onSort) {
      onSort(key);
    }
  };
  const getAlignmentClass = (align) => {
    switch (align) {
      case "center":
        return "text-center";
      case "right":
        return "text-right";
      default:
        return "text-left";
    }
  };
  const body = /* @__PURE__ */ jsxs70(Fragment21, { children: [
    /* @__PURE__ */ jsxs70("table", { className: "w-full", children: [
      /* @__PURE__ */ jsx83("thead", { className: `border-b border-recessed ${stickyHeader ? "sticky top-0 bg-surface z-10" : ""}`, children: /* @__PURE__ */ jsx83("tr", { children: columns.map((column) => /* @__PURE__ */ jsx83("th", { className: `px-4 sm:px-6 py-3 text-xs font-sans font-bold text-tertiary uppercase tracking-wide ${getAlignmentClass(column.align)} ${column.sortable ? "cursor-pointer hover:text-primary select-none focus:outline-none focus:ring-2 focus:ring-accent focus:ring-inset" : ""}`, style: {
        width: column.width
      }, onClick: () => handleSort(column.key, column.sortable), onKeyDown: column.sortable ? (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleSort(column.key, column.sortable);
        }
      } : void 0, tabIndex: column.sortable ? 0 : void 0, role: column.sortable ? "button" : void 0, "aria-sort": sortConfig?.key === column.key ? sortConfig.direction === "asc" ? "ascending" : "descending" : void 0, children: /* @__PURE__ */ jsxs70("div", { className: `flex items-center gap-2 ${column.align === "right" ? "justify-end" : column.align === "center" ? "justify-center" : "justify-start"}`, children: [
        /* @__PURE__ */ jsx83("span", { children: column.label }),
        column.sortable && sortConfig?.key === column.key && /* @__PURE__ */ jsx83("span", { className: "text-accent", children: sortConfig.direction === "asc" ? /* @__PURE__ */ jsx83(ArrowUpIcon, { className: "w-3 h-3" }) : /* @__PURE__ */ jsx83(ArrowDownIcon, { className: "w-3 h-3" }) })
      ] }) }, column.key)) }) }),
      /* @__PURE__ */ jsx83("tbody", { children: data.map((row, rowIndex) => /* @__PURE__ */ jsx83("tr", { className: `border-b border-recessed transition-colors ${hoverable ? "hover:bg-elevated" : ""} ${onRowClick ? "cursor-pointer focus:outline-none focus:ring-2 focus:ring-accent focus:ring-inset" : ""}`, onClick: () => onRowClick && onRowClick(row), onKeyDown: onRowClick ? (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onRowClick(row);
        }
      } : void 0, tabIndex: onRowClick ? 0 : void 0, role: onRowClick ? "button" : void 0, children: columns.map((column) => {
        const value = row[column.key];
        const content = column.render ? column.render(value, row) : value;
        return /* @__PURE__ */ jsx83("td", { className: `px-4 sm:px-6 py-4 text-sm font-sans text-primary ${getAlignmentClass(column.align)}`, children: content }, column.key);
      }) }, rowIndex)) })
    ] }),
    data.length === 0 && /* @__PURE__ */ jsx83("div", { className: "text-center py-12", children: /* @__PURE__ */ jsx83("p", { className: "text-sm font-sans text-tertiary", children: "No data available" }) })
  ] });
  return label ? /* @__PURE__ */ jsx83(TableScroll, { label, className, children: body }) : /* @__PURE__ */ jsx83("div", { className: `overflow-x-auto ${className}`, children: body });
}

// src/data/RegulatoryHeatMap.tsx
import React29, { useMemo as useMemo10 } from "react";
import { jsx as jsx84, jsxs as jsxs71 } from "react/jsx-runtime";
var SIGNAL_ORDER = ["GREEN", "YELLOW", "RED"];
var SIGNAL_FILL = {
  GREEN: "bg-success/15",
  YELLOW: "bg-warning/25",
  RED: "bg-error/15"
};
var SIGNAL_DOT = {
  GREEN: "bg-success",
  YELLOW: "bg-warning",
  RED: "bg-error"
};
var SIGNAL_INDICATOR = {
  GREEN: "\u2713",
  // checkmark
  YELLOW: "\u2013",
  // en dash
  RED: "\u2717"
  // ballot X
};
var SIGNAL_NAME = {
  GREEN: "Green",
  YELLOW: "Yellow",
  RED: "Red"
};
var SIGNAL_MEANING = {
  GREEN: "compliant",
  YELLOW: "attention needed",
  RED: "non-compliant"
};
var SUMMARY_COUNT = {
  GREEN: "greenCount",
  YELLOW: "yellowCount",
  RED: "redCount"
};
var GRID_BORDER = "border border-recessed";
function RegulatoryHeatMap({
  entityConfigName,
  generatedAt,
  assetClassGroups,
  rows,
  summary,
  onCellClick
}) {
  const sortedRows = useMemo10(
    () => [...rows].sort((a, b) => a.jurisdictionName.localeCompare(b.jurisdictionName, "en-US")),
    [rows]
  );
  const presentSignals = /* @__PURE__ */ new Set();
  for (const row of rows) {
    for (const cell of row.cells) {
      presentSignals.add(cell.signal);
    }
  }
  const visibleSignals = SIGNAL_ORDER.filter((s) => presentSignals.has(s));
  return /* @__PURE__ */ jsxs71("div", { className: "font-sans", children: [
    /* @__PURE__ */ jsxs71("div", { className: "mb-5", children: [
      /* @__PURE__ */ jsx84("h3", { className: "font-serif font-semibold text-heading-sm text-primary tracking-headline-serif mb-1", children: entityConfigName }),
      /* @__PURE__ */ jsxs71("p", { className: "text-body-xs text-tertiary", children: [
        "Generated ",
        formatDateTime(generatedAt, { seconds: false })
      ] })
    ] }),
    /* @__PURE__ */ jsx84("div", { className: "overflow-x-auto mb-5", "data-testid": "regulatory-heat-map-scroll", children: /* @__PURE__ */ jsxs71("table", { className: "w-full border-collapse", children: [
      /* @__PURE__ */ jsx84("thead", { children: /* @__PURE__ */ jsxs71("tr", { children: [
        /* @__PURE__ */ jsx84("th", { className: `${GRID_BORDER} bg-elevated/50 px-3 py-2.5`, children: /* @__PURE__ */ jsx84("span", { className: "sr-only", children: "Jurisdiction" }) }),
        assetClassGroups.map((group) => /* @__PURE__ */ jsx84(
          "th",
          {
            scope: "col",
            className: `${GRID_BORDER} bg-elevated/50 px-3 py-2.5 text-center text-label-xs font-sans font-semibold uppercase tracking-widest text-tertiary whitespace-nowrap`,
            children: group.groupName
          },
          group.groupCode
        ))
      ] }) }),
      /* @__PURE__ */ jsx84("tbody", { children: sortedRows.map((row) => /* @__PURE__ */ jsxs71("tr", { children: [
        /* @__PURE__ */ jsx84(
          "th",
          {
            scope: "row",
            className: `${GRID_BORDER} bg-elevated/50 px-3 py-1.5 text-left text-body-sm font-sans font-semibold normal-case tracking-normal text-primary whitespace-nowrap`,
            children: row.jurisdictionName
          }
        ),
        assetClassGroups.map((group) => {
          const cell = row.cells.find(
            (c) => c.assetClassGroupCode === group.groupCode
          );
          if (!cell) {
            return /* @__PURE__ */ jsx84(
              "td",
              {
                className: `${GRID_BORDER} px-2 py-1.5 text-center align-middle text-body-xs text-tertiary whitespace-nowrap`,
                children: "Not assessed"
              },
              group.groupCode
            );
          }
          const cellClassName = [
            GRID_BORDER,
            "px-2 py-1.5 text-center align-middle text-primary",
            SIGNAL_FILL[cell.signal],
            onCellClick ? "cursor-pointer hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-selection/60" : ""
          ].filter(Boolean).join(" ");
          const ariaLabel = `${SIGNAL_NAME[cell.signal]}, ${SIGNAL_MEANING[cell.signal]}. ${row.jurisdictionName}, ${group.groupName}: ${cell.signalRationale}`;
          return /* @__PURE__ */ jsx84(
            "td",
            {
              className: cellClassName,
              title: cell.signalRationale,
              "aria-label": ariaLabel,
              "data-signal": cell.signal,
              role: onCellClick ? "button" : void 0,
              tabIndex: onCellClick ? 0 : void 0,
              onClick: onCellClick ? () => onCellClick(row.jurisdictionCode, group.groupCode) : void 0,
              onKeyDown: onCellClick ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onCellClick(row.jurisdictionCode, group.groupCode);
                }
              } : void 0,
              children: /* @__PURE__ */ jsx84("span", { className: "text-body-sm font-bold leading-none", "aria-hidden": "true", children: SIGNAL_INDICATOR[cell.signal] })
            },
            group.groupCode
          );
        })
      ] }, row.jurisdictionCode)) })
    ] }) }),
    visibleSignals.length > 0 && /* @__PURE__ */ jsx84("div", { className: "flex flex-wrap items-center gap-x-5 gap-y-2 mb-3", children: visibleSignals.map((signal) => /* @__PURE__ */ jsxs71(
      "span",
      {
        className: "flex items-center gap-1.5 text-body-sm text-secondary",
        children: [
          /* @__PURE__ */ jsx84(
            "span",
            {
              className: `inline-flex items-center justify-center w-4 h-4 rounded-sm text-label-xs font-bold text-primary ${SIGNAL_FILL[signal]}`,
              "aria-hidden": "true",
              children: SIGNAL_INDICATOR[signal]
            }
          ),
          SIGNAL_NAME[signal],
          ": ",
          SIGNAL_MEANING[signal]
        ]
      },
      signal
    )) }),
    /* @__PURE__ */ jsxs71("div", { className: "flex flex-wrap items-center gap-1.5 text-body-sm text-secondary", children: [
      /* @__PURE__ */ jsx84("span", { className: "font-semibold text-primary", children: "Summary:" }),
      SIGNAL_ORDER.map((signal, index) => /* @__PURE__ */ jsxs71(React29.Fragment, { children: [
        index > 0 && /* @__PURE__ */ jsx84("span", { className: "text-tertiary mx-0.5", "aria-hidden": "true", children: "/" }),
        /* @__PURE__ */ jsxs71("span", { className: "inline-flex items-center gap-1 tabular-nums", children: [
          /* @__PURE__ */ jsx84(
            "span",
            {
              className: `inline-block w-2 h-2 rounded-full ${SIGNAL_DOT[signal]}`,
              "aria-hidden": "true"
            }
          ),
          summary[SUMMARY_COUNT[signal]],
          " ",
          SIGNAL_NAME[signal]
        ] })
      ] }, signal))
    ] })
  ] });
}

// src/data/money-format.ts
var MONEY_UNAVAILABLE = "Unavailable";
var USD_COMPACT = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  // USD's default minimum of two fraction digits would clamp the maximum and
  // print '$125.0K'; zero lets whole figures print whole.
  minimumFractionDigits: 0,
  maximumFractionDigits: 1
});
var USD_FULL = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});
function formatUsdCompact(value) {
  return Number.isFinite(value) ? USD_COMPACT.format(value) : MONEY_UNAVAILABLE;
}
function formatUsd(value) {
  return Number.isFinite(value) ? USD_FULL.format(value) : MONEY_UNAVAILABLE;
}

// src/sections/Footer.tsx
import { jsx as jsx85, jsxs as jsxs72 } from "react/jsx-runtime";
var defaultSections = [
  {
    title: "Platform",
    links: [
      { label: "Valuation Engine" },
      { label: "Risk Assessment" },
      { label: "API Documentation" },
      { label: "Security" }
    ]
  },
  {
    title: "Solutions",
    links: [
      { label: "Credit Unions" },
      { label: "Community Banks" },
      { label: "Direct Lenders" },
      { label: "Mortgage Lenders" }
    ]
  },
  {
    title: "Resources",
    links: [
      { label: "Documentation" },
      { label: "Case Studies" },
      { label: "Whitepapers" },
      { label: "Blog" }
    ]
  },
  {
    title: "Company",
    links: [
      { label: "About" },
      { label: "Careers" },
      { label: "Contact" },
      { label: "Press" }
    ]
  }
];
var defaultLegalLinks = [
  { label: "Privacy Policy" },
  { label: "Terms of Service" },
  { label: "Cookie Policy" }
];
function Footer({
  sections = defaultSections,
  copyrightYear,
  legalLinks = defaultLegalLinks,
  logoUrl = "https://cdn.aaim.app/logos/aaim-master-grey.svg",
  className = ""
}) {
  const year = copyrightYear || (/* @__PURE__ */ new Date()).getFullYear();
  return /* @__PURE__ */ jsx85("footer", { className: `bg-canvas py-12 sm:py-16 ${className}`, children: /* @__PURE__ */ jsxs72("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: [
    /* @__PURE__ */ jsx85("div", { className: "grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12 mb-8 sm:mb-12", children: sections.map((section, index) => /* @__PURE__ */ jsxs72("div", { children: [
      /* @__PURE__ */ jsx85("h4", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-3 sm:mb-4", children: section.title }),
      /* @__PURE__ */ jsx85("ul", { className: "space-y-2", children: section.links.map((link, linkIndex) => /* @__PURE__ */ jsx85("li", { children: /* @__PURE__ */ jsx85(
        "a",
        {
          href: link.href,
          onClick: link.onClick,
          className: "text-sm font-serif text-secondary hover:text-primary transition-colors cursor-pointer",
          children: link.label
        }
      ) }, linkIndex)) })
    ] }, index)) }),
    /* @__PURE__ */ jsxs72("div", { className: "pt-6 sm:pt-8 border-t border-recessed flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0", children: [
      /* @__PURE__ */ jsxs72("div", { className: "flex items-center gap-4", children: [
        /* @__PURE__ */ jsx85(
          "img",
          {
            src: logoUrl,
            alt: "Aaim",
            className: "h-6"
          }
        ),
        /* @__PURE__ */ jsxs72("span", { className: "text-sm font-serif text-tertiary", children: [
          "\xA9 ",
          year,
          " Aaim, Inc. All rights reserved."
        ] })
      ] }),
      /* @__PURE__ */ jsx85("div", { className: "flex flex-wrap justify-center gap-4 sm:gap-6", children: legalLinks.map((link, index) => /* @__PURE__ */ jsx85(
        "a",
        {
          href: link.href,
          onClick: link.onClick,
          className: "text-xs font-sans text-tertiary hover:text-primary transition-colors cursor-pointer whitespace-nowrap",
          children: link.label
        },
        index
      )) })
    ] })
  ] }) });
}

// src/sections/EnhancedHeroSection.tsx
import { ArrowRightIcon as ArrowRightIcon2 } from "lucide-react";
import { jsx as jsx86, jsxs as jsxs73 } from "react/jsx-runtime";
function EnhancedHeroSection() {
  const chartData = [{
    year: "2019",
    alternative: 8.2,
    traditional: 4.1
  }, {
    year: "2020",
    alternative: 11.5,
    traditional: 3.8
  }, {
    year: "2021",
    alternative: 13.8,
    traditional: 5.2
  }, {
    year: "2022",
    alternative: 16.4,
    traditional: 2.9
  }, {
    year: "2023",
    alternative: 15.2,
    traditional: 4.5
  }, {
    year: "2024",
    alternative: 18.5,
    traditional: 5.1
  }];
  const maxValue = 20;
  const yAxisSteps = [0, 5, 10, 15, 20];
  return /* @__PURE__ */ jsx86("section", { className: "bg-canvas py-16 sm:py-20 lg:py-24", children: /* @__PURE__ */ jsx86("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: /* @__PURE__ */ jsxs73("div", { className: "grid lg:grid-cols-2 gap-8 lg:gap-16 items-center", children: [
    /* @__PURE__ */ jsxs73("div", { children: [
      /* @__PURE__ */ jsxs73(Heading, { level: 1, size: "page", className: "mb-4 sm:mb-6", children: [
        "Making modern",
        /* @__PURE__ */ jsx86("br", {}),
        "wealth ",
        /* @__PURE__ */ jsx86("span", { className: "text-accent", children: "lendable" })
      ] }),
      /* @__PURE__ */ jsx86("p", { className: "text-base sm:text-lg lg:text-xl font-serif text-secondary mb-6 sm:mb-8 max-w-2xl", children: "Alternative assets represent $20 trillion in invisible wealth. Traditional banking infrastructure cannot see, value, or underwrite loans against cryptocurrency, private equity, or tokenized securities. Aaim solves this through patent-pending valuation methodology." }),
      /* @__PURE__ */ jsxs73("div", { className: "flex flex-col sm:flex-row gap-3 sm:gap-4", children: [
        /* @__PURE__ */ jsxs73("button", { className: "bg-accent text-white py-3 px-6 rounded-sm font-sans font-semibold text-sm hover:bg-accent-hover hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2", children: [
          "Explore platform",
          /* @__PURE__ */ jsx86(ArrowRightIcon2, { className: "w-4 h-4" })
        ] }),
        /* @__PURE__ */ jsx86("button", { className: "bg-surface text-accent py-3 px-6 rounded-sm font-sans font-semibold text-sm border-2 border-accent hover:bg-elevated transition-all duration-200", children: "View case studies" })
      ] })
    ] }),
    /* @__PURE__ */ jsxs73("div", { className: "bg-surface border border-recessed p-6 sm:p-8 rounded-sm shadow-sm", children: [
      /* @__PURE__ */ jsx86("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-2", children: "Portfolio Performance Comparison" }),
      /* @__PURE__ */ jsx86(Heading, { level: 3, size: "card", className: "mb-4 sm:mb-6", children: "Alternative vs. traditional asset returns" }),
      /* @__PURE__ */ jsxs73("div", { className: "relative", children: [
        /* @__PURE__ */ jsx86("div", { className: "absolute left-0 top-[-6px] h-[252px] flex flex-col justify-between z-10", children: [...yAxisSteps].reverse().map((step) => /* @__PURE__ */ jsxs73("div", { className: `text-xs font-sans tabular-nums leading-none ${step === 0 ? "opacity-0" : "text-tertiary"}`, children: [
          step,
          "%"
        ] }, step)) }),
        /* @__PURE__ */ jsxs73("div", { className: "ml-10 sm:ml-12 h-[240px] bg-gradient-to-t from-[rgb(var(--color-alternative)/0.08)] to-[rgb(var(--color-alternative)/0.15)] rounded-sm relative overflow-hidden", children: [
          /* @__PURE__ */ jsx86("div", { className: "absolute inset-0 flex flex-col justify-between pointer-events-none z-0", children: yAxisSteps.map((step, index) => /* @__PURE__ */ jsx86("div", { className: `w-full border-t border-neutral-data ${index === yAxisSteps.length - 1 ? "opacity-0" : "opacity-40"}` }, step)) }),
          /* @__PURE__ */ jsx86("div", { className: "relative z-10 h-full flex items-end gap-3 sm:gap-4 lg:gap-5 px-4 sm:px-6", children: chartData.map((item, index) => /* @__PURE__ */ jsxs73("div", { className: "flex-1 flex gap-1 items-end", children: [
            /* @__PURE__ */ jsx86("div", { className: "flex-1 bg-alternative rounded-t-sm transition-all duration-200 hover:opacity-80 cursor-pointer group relative min-h-[2px]", style: {
              height: `${item.alternative / maxValue * 100}%`
            }, children: /* @__PURE__ */ jsxs73("div", { className: "absolute -top-7 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-primary text-white text-caption-xs px-2 py-1 rounded whitespace-nowrap font-mono tabular-nums shadow-lg z-20", children: [
              item.alternative,
              "%"
            ] }) }),
            /* @__PURE__ */ jsx86("div", { className: "flex-1 bg-traditional rounded-t-sm transition-all duration-200 hover:opacity-80 cursor-pointer group relative min-h-[2px]", style: {
              height: `${item.traditional / maxValue * 100}%`
            }, children: /* @__PURE__ */ jsxs73("div", { className: "absolute -top-7 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-primary text-white text-caption-xs px-2 py-1 rounded whitespace-nowrap font-mono tabular-nums shadow-lg z-20", children: [
              item.traditional,
              "%"
            ] }) })
          ] }, index)) })
        ] }),
        /* @__PURE__ */ jsx86("div", { className: "ml-10 sm:ml-12 flex gap-3 sm:gap-4 lg:gap-5 mt-2 px-4 sm:px-6", children: chartData.map((item, index) => /* @__PURE__ */ jsx86("div", { className: "flex-1 text-center", children: /* @__PURE__ */ jsx86("span", { className: "text-caption-xs sm:text-xs font-sans text-tertiary tabular-nums", children: item.year }) }, index)) })
      ] }),
      /* @__PURE__ */ jsxs73("div", { className: "flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0 pt-4 mt-6 border-t border-recessed", children: [
        /* @__PURE__ */ jsxs73("div", { className: "flex items-center gap-2 sm:gap-3", children: [
          /* @__PURE__ */ jsx86("div", { className: "w-3 h-3 rounded-full bg-alternative flex-shrink-0" }),
          /* @__PURE__ */ jsx86("span", { className: "text-xs sm:text-sm font-sans text-tertiary", children: "Alternative Assets" }),
          /* @__PURE__ */ jsx86("span", { className: "text-base sm:text-lg font-mono font-semibold text-growth tabular-nums", children: "+18.5%" })
        ] }),
        /* @__PURE__ */ jsxs73("div", { className: "flex items-center gap-2 sm:gap-3", children: [
          /* @__PURE__ */ jsx86("div", { className: "w-3 h-3 rounded-full bg-traditional flex-shrink-0" }),
          /* @__PURE__ */ jsx86("span", { className: "text-xs sm:text-sm font-sans text-tertiary", children: "Traditional" }),
          /* @__PURE__ */ jsx86("span", { className: "text-base sm:text-lg font-mono text-secondary tabular-nums", children: "+5.1%" })
        ] })
      ] })
    ] })
  ] }) }) });
}

// src/sections/HeroStatsSidebar.tsx
import clsx6 from "clsx";
import { jsx as jsx87, jsxs as jsxs74 } from "react/jsx-runtime";
function HeroStatsSidebar({
  sectionLabel = "Market Opportunity",
  stats,
  className = ""
}) {
  if (!stats || stats.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxs74("div", { className: clsx6("bg-surface rounded-sm shadow-sm p-6 sm:p-8", className), children: [
    sectionLabel && /* @__PURE__ */ jsx87("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-6", children: sectionLabel }),
    /* @__PURE__ */ jsx87("div", { className: "divide-y divide-recessed", children: stats.map((stat, idx) => /* @__PURE__ */ jsxs74("div", { className: "py-4 first:pt-0 last:pb-0", children: [
      /* @__PURE__ */ jsx87("div", { className: "text-2xl sm:text-3xl font-serif font-semibold text-accent tracking-headline-serif leading-none mb-2 tabular-nums", children: stat.value }),
      /* @__PURE__ */ jsx87("p", { className: "text-sm font-sans font-semibold text-primary mb-1", children: stat.description }),
      stat.source && /* @__PURE__ */ jsx87("p", { className: "text-xs font-sans text-tertiary", children: stat.source })
    ] }, idx)) })
  ] });
}

// src/sections/FeaturesSection.tsx
import {
  ShieldCheckIcon as ShieldCheckIcon2,
  TrendingUpIcon as TrendingUpIcon4,
  ZapIcon as ZapIcon2,
  LockIcon as LockIcon2,
  DatabaseIcon as DatabaseIcon2,
  GlobeIcon as GlobeIcon2,
  CodeIcon as CodeIcon2,
  UsersIcon as UsersIcon2
} from "lucide-react";
import { jsx as jsx88, jsxs as jsxs75 } from "react/jsx-runtime";
var iconMap = {
  ShieldCheckIcon: ShieldCheckIcon2,
  TrendingUpIcon: TrendingUpIcon4,
  ZapIcon: ZapIcon2,
  LockIcon: LockIcon2,
  DatabaseIcon: DatabaseIcon2,
  GlobeIcon: GlobeIcon2,
  CodeIcon: CodeIcon2,
  UsersIcon: UsersIcon2
};
var defaultFeatures = [
  {
    icon: "ShieldCheckIcon",
    title: "Institutional-grade security",
    description: "Encryption in transit and at rest, role-based access, and logged access to member data. A SOC 2 Type 1 examination of the Security category is in preparation."
  },
  {
    icon: "TrendingUpIcon",
    title: "Real-time valuation",
    description: "Patent-pending methodology values alternative assets with institutional precision. Updates every 15 minutes."
  },
  {
    icon: "ZapIcon",
    title: "Instant underwriting",
    description: "Automated risk assessment reduces loan approval from weeks to minutes while maintaining rigorous standards."
  },
  {
    icon: "LockIcon",
    title: "Custody integration",
    description: "Direct integration with leading custodians ensures asset verification and automated collateral monitoring."
  }
];
function FeaturesSection({
  sectionLabel = "Platform Capabilities",
  headline = "Infrastructure built for scale",
  subheadline = "Enterprise-grade platform processing $2.4B in alternative assets with 99.97% uptime and institutional security.",
  coreFeatures
}) {
  const features = coreFeatures || defaultFeatures;
  return /* @__PURE__ */ jsx88("section", { className: "bg-canvas py-16 sm:py-20 lg:py-24", children: /* @__PURE__ */ jsxs75("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: [
    /* @__PURE__ */ jsxs75("div", { className: "text-center mb-12 sm:mb-16", children: [
      /* @__PURE__ */ jsx88("div", { className: "text-label-xs uppercase font-sans font-semibold text-tertiary mb-4", children: sectionLabel }),
      /* @__PURE__ */ jsx88(Heading, { level: 2, size: "section", weight: "normal", className: "mb-4 sm:mb-6", children: headline }),
      /* @__PURE__ */ jsx88("p", { className: "text-base sm:text-lg lg:text-xl font-serif text-secondary max-w-content-narrow mx-auto", children: subheadline })
    ] }),
    /* @__PURE__ */ jsx88("div", { className: "grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8", children: features.map((feature, index) => {
      const IconComponent = iconMap[feature.icon] || ZapIcon2;
      return /* @__PURE__ */ jsxs75(
        "div",
        {
          className: "bg-surface border border-recessed p-6 sm:p-8 rounded-sm shadow-sm hover:shadow-md transition-shadow duration-200",
          children: [
            /* @__PURE__ */ jsx88("div", { className: "w-9 h-9 sm:w-10 sm:h-10 border border-recessed rounded-sm flex items-center justify-center mb-4 sm:mb-5", children: /* @__PURE__ */ jsx88(
              IconComponent,
              {
                className: "w-4 h-4 sm:w-5 sm:h-5 text-tertiary",
                strokeWidth: 1.5
              }
            ) }),
            /* @__PURE__ */ jsx88("h3", { className: "text-base sm:text-lg font-sans font-semibold text-primary mb-3 sm:mb-4 leading-normal", children: feature.title }),
            /* @__PURE__ */ jsx88("p", { className: "text-sm sm:text-base font-serif text-secondary", children: feature.description })
          ]
        },
        index
      );
    }) })
  ] }) });
}

// src/utils/HeadlineWithEmphasis.tsx
import React30, { useState as useState22, useEffect as useEffect17 } from "react";
import { jsx as jsx89, jsxs as jsxs76 } from "react/jsx-runtime";
function HeadlineWithEmphasis({
  text,
  className = "",
  emphasisClassName = "text-accent",
  as: Component = "span",
  desktopSize = "auto"
}) {
  const [isDesktop, setIsDesktop] = useState22(false);
  useEffect17(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 1280);
    };
    checkDesktop();
    window.addEventListener("resize", checkDesktop);
    return () => window.removeEventListener("resize", checkDesktop);
  }, []);
  const parts = parseEmphasis(text);
  const sizeMap = {
    "auto": null,
    "sm": 36,
    "md": 40,
    "lg": 48,
    "xl": 56,
    "2xl": 64,
    "3xl": 72,
    "4xl": 80
  };
  const hasDesktopSizeOverride = isDesktop && desktopSize !== "auto";
  const desktopFontSize = sizeMap[desktopSize];
  const combinedClassName = `font-serif tracking-headline-serif [text-wrap:balance] ${className}`.trim();
  const style = hasDesktopSizeOverride && desktopFontSize ? { fontSize: `${desktopFontSize}px` } : void 0;
  return /* @__PURE__ */ jsx89(Component, { className: combinedClassName, style, children: parts.map((part, index) => {
    if (part.type === "emphasis") {
      return /* @__PURE__ */ jsx89("span", { className: emphasisClassName, children: part.content }, index);
    }
    if (part.type === "linebreak") {
      return /* @__PURE__ */ jsx89("br", {}, index);
    }
    if (part.type === "linebreak-mobile") {
      return /* @__PURE__ */ jsx89("br", { className: "md:hidden" }, index);
    }
    if (part.type === "linebreak-desktop") {
      return /* @__PURE__ */ jsx89("br", { className: "hidden xl:block" }, index);
    }
    if (part.type === "linebreak-sm") {
      return /* @__PURE__ */ jsx89("br", { className: "sm:hidden" }, index);
    }
    if (part.type === "linebreak-md") {
      return /* @__PURE__ */ jsx89("br", { className: "md:hidden" }, index);
    }
    if (part.type === "linebreak-lg") {
      return /* @__PURE__ */ jsx89("br", { className: "hidden lg:block" }, index);
    }
    const textParts = part.content.split(/\|\||\n|<br\s*\/?>/i);
    return textParts.map((textPart, textIndex) => /* @__PURE__ */ jsxs76(React30.Fragment, { children: [
      textIndex > 0 && /* @__PURE__ */ jsx89("br", {}),
      textPart
    ] }, `${index}-${textIndex}`));
  }) });
}
function parseEmphasis(text) {
  const parts = [];
  const processedText = text.replace(/~/g, "\xA0").replace(/\|\|mobile/gi, "MOBILE").replace(/\|\|desktop/gi, "DESKTOP").replace(/\|\|sm/gi, "SM").replace(/\|\|md/gi, "MD").replace(/\|\|lg/gi, "LG");
  const pattern = /\*\*([^*]+)\*\*|\u0001(MOBILE|DESKTOP|SM|MD|LG)\u0001/g;
  const matches = Array.from(processedText.matchAll(pattern));
  let lastIndex = 0;
  for (const match of matches) {
    if (match.index !== void 0 && match.index > lastIndex) {
      parts.push({
        type: "text",
        content: processedText.slice(lastIndex, match.index)
      });
    }
    if (match[1]) {
      parts.push({
        type: "emphasis",
        content: match[1]
      });
    } else if (match[2]) {
      const breakpointMap = {
        "MOBILE": "linebreak-mobile",
        "DESKTOP": "linebreak-desktop",
        "SM": "linebreak-sm",
        "MD": "linebreak-md",
        "LG": "linebreak-lg"
      };
      parts.push({
        type: breakpointMap[match[2]] || "linebreak",
        content: ""
      });
    }
    if (match.index !== void 0) {
      lastIndex = match.index + match[0].length;
    }
  }
  if (lastIndex < processedText.length) {
    parts.push({
      type: "text",
      content: processedText.slice(lastIndex)
    });
  }
  return parts;
}
function FormattedText({
  text,
  className = "",
  emphasisClassName = "text-accent font-semibold",
  as: Component = "span"
}) {
  if (!text) return null;
  const processedText = text.replace(/~/g, "\xA0");
  const lines = processedText.split(/\|\||\n|<br\s*\/?>/i);
  return /* @__PURE__ */ jsx89(Component, { className, children: lines.map((line, lineIndex) => /* @__PURE__ */ jsxs76(React30.Fragment, { children: [
    lineIndex > 0 && /* @__PURE__ */ jsx89("br", {}),
    renderLineWithEmphasis(line.trim(), emphasisClassName)
  ] }, lineIndex)) });
}
function renderLineWithEmphasis(line, emphasisClassName) {
  const pattern = /\*\*([^*]+)\*\*/g;
  const matches = Array.from(line.matchAll(pattern));
  if (matches.length === 0) {
    return line;
  }
  const result = [];
  let lastIndex = 0;
  matches.forEach((match, i) => {
    if (match.index !== void 0 && match.index > lastIndex) {
      result.push(line.slice(lastIndex, match.index));
    }
    result.push(
      /* @__PURE__ */ jsx89("span", { className: emphasisClassName, children: match[1] }, i)
    );
    if (match.index !== void 0) {
      lastIndex = match.index + match[0].length;
    }
  });
  if (lastIndex < line.length) {
    result.push(line.slice(lastIndex));
  }
  return result;
}

// src/sections/IntegrationSection.tsx
import { jsx as jsx90, jsxs as jsxs77 } from "react/jsx-runtime";
var defaultPartners = ["[Partner]"];
function IntegrationSection({
  sectionLabel = "Integrations",
  headline = "[Integration Headline]",
  description = "[Integration description]",
  partners = defaultPartners
}) {
  return /* @__PURE__ */ jsx90("section", { className: "bg-canvas py-16 sm:py-20 lg:py-24", children: /* @__PURE__ */ jsxs77("div", { className: "max-w-content-wide mx-auto px-6 sm:px-8", children: [
    /* @__PURE__ */ jsxs77("div", { className: "text-center mb-10 sm:mb-12", children: [
      /* @__PURE__ */ jsx90("div", { className: "text-label-xs uppercase font-sans font-semibold text-tertiary mb-4", children: sectionLabel }),
      /* @__PURE__ */ jsx90(
        HeadlineWithEmphasis,
        {
          text: headline,
          as: "h2",
          className: "text-heading-section sm:text-heading-section-md lg:text-heading-section-lg mb-4"
        }
      ),
      /* @__PURE__ */ jsx90(
        FormattedText,
        {
          text: description,
          as: "p",
          className: "text-lg sm:text-xl font-serif text-secondary max-w-content-narrow mx-auto"
        }
      )
    ] }),
    /* @__PURE__ */ jsx90("div", { className: "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-6", children: partners.map((partner, index) => /* @__PURE__ */ jsx90(
      "div",
      {
        className: "bg-surface border border-recessed shadow-sm p-4 sm:p-6 lg:p-8 rounded-sm text-center hover:shadow-md transition-all duration-200",
        children: /* @__PURE__ */ jsx90("span", { className: "text-xs sm:text-sm font-sans font-medium text-tertiary", children: partner })
      },
      index
    )) })
  ] }) });
}

// src/sections/RegulatoryFramework.tsx
import {
  CircleIcon as CircleIcon2,
  CircleDotIcon,
  CircleDotDashedIcon,
  DatabaseIcon as DatabaseIcon3,
  FingerprintIcon,
  NetworkIcon,
  ShieldIcon
} from "lucide-react";
import { jsx as jsx91, jsxs as jsxs78 } from "react/jsx-runtime";
var defaultFrameworks = [
  {
    title: "UCC Article 8 Perfection",
    description: "Tri-party control agreements establish super-priority security interests in securities entitlements. Smart contract orchestration ensures atomic execution across all counterparties."
  },
  {
    title: "UCC Article 9 Filings",
    description: "Automated UCC-1 filings with jurisdiction determination and collateral description generation. Five-year continuation tracking with advance reminders."
  },
  {
    title: "UCC Article 12 Digital Assets",
    description: "Controllable electronic record provisions enable cryptocurrency collateralization with cryptographic proof of control transfer."
  }
];
var defaultInfrastructureStack = [
  {
    name: "Aaim Horizon",
    tagline: "Asset Visibility",
    description: "Real-time aggregation across custodians. Borrowers share; lenders monitor."
  },
  {
    name: "Aaim Frontier",
    tagline: "Pledged-Asset Lending",
    description: "Legal perfection across the broadest asset taxonomy. UCC filings, control agreements."
  },
  {
    name: "Aaim Transport",
    tagline: "Dynamic Orchestration",
    description: "Tri-party coordination for complex, high-value arrangements. Real-time margin monitoring."
  },
  {
    name: "Reference Model",
    tagline: "Mapping the Financial Genome",
    description: "Unified asset taxonomy with regulatory guidance encoded. 8 major classes, 50+ subcategories."
  },
  {
    name: "QuantumVerify Smart Contracts",
    tagline: "Atomic Execution",
    description: "Multi-party transparency with cryptographic provenance. Every state change verified."
  },
  {
    name: "QVToken Identity",
    tagline: "Continuous Trust",
    description: "Identity as living document. Succession planning, entity resolution, evolving KYC."
  },
  {
    name: "SocketCloud",
    tagline: "Computational Privacy",
    description: "Tenant-isolated multi-cloud with homomorphic encryption. Byzantine fault-tolerant."
  }
];
var iconMap2 = {
  // Platform tiers
  "Aaim Horizon": CircleIcon2,
  "Aaim Frontier": CircleDotIcon,
  "Aaim Transport": CircleDotDashedIcon,
  // Core infrastructure
  "Reference Model": DatabaseIcon3,
  "QuantumVerify Smart Contracts": NetworkIcon,
  "QVToken Identity": FingerprintIcon,
  "SocketCloud": ShieldIcon
};
function RegulatoryFramework({
  sectionLabel = "Legal Framework",
  headline = "Institutional-grade **legal infrastructure**",
  description,
  frameworks = defaultFrameworks,
  infrastructureHeadline = "Tech infrastructure that ||just **gets it all done**",
  infrastructureStackLabel = "Technology Infrastructure",
  infrastructureStack = defaultInfrastructureStack
}) {
  return /* @__PURE__ */ jsx91("section", { className: "bg-canvas py-20", children: /* @__PURE__ */ jsxs78("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: [
    /* @__PURE__ */ jsx91("div", { className: "text-label-xs uppercase font-sans font-semibold text-tertiary mb-8", children: sectionLabel }),
    /* @__PURE__ */ jsxs78("div", { className: "grid lg:grid-cols-2 gap-12 lg:gap-16", children: [
      /* @__PURE__ */ jsxs78("div", { children: [
        /* @__PURE__ */ jsx91(
          HeadlineWithEmphasis,
          {
            text: headline,
            as: "h2",
            className: "text-heading-section sm:text-heading-section-md lg:text-heading-section-lg mb-6"
          }
        ),
        description && /* @__PURE__ */ jsx91("p", { className: "text-body-md font-serif text-secondary mb-10 leading-relaxed", children: description }),
        /* @__PURE__ */ jsx91("div", { className: "space-y-8", children: frameworks.map((framework, index) => /* @__PURE__ */ jsxs78("div", { className: "border-l-2 border-accent/30 pl-6", children: [
          /* @__PURE__ */ jsx91("h3", { className: "text-heading-card sm:text-heading-card-md mb-3", children: framework.title }),
          /* @__PURE__ */ jsx91("p", { className: "text-body-md font-serif text-secondary", children: framework.description })
        ] }, index)) })
      ] }),
      /* @__PURE__ */ jsxs78("div", { className: "lg:pl-4", children: [
        /* @__PURE__ */ jsx91(
          HeadlineWithEmphasis,
          {
            text: infrastructureHeadline,
            as: "h2",
            className: "text-heading-section sm:text-heading-section-md lg:text-heading-section-lg mb-10"
          }
        ),
        /* @__PURE__ */ jsxs78("div", { className: "bg-surface border border-recessed p-8 sm:p-10 rounded-sm shadow-sm", children: [
          /* @__PURE__ */ jsx91("div", { className: "text-label-xs uppercase font-sans font-semibold text-tertiary mb-8", children: infrastructureStackLabel }),
          /* @__PURE__ */ jsx91("div", { className: "space-y-8", children: infrastructureStack.map((item, index) => {
            const IconComponent = iconMap2[item.name] || DatabaseIcon3;
            return /* @__PURE__ */ jsx91(
              "div",
              {
                className: "group",
                children: /* @__PURE__ */ jsxs78("div", { className: "flex items-start gap-3", children: [
                  /* @__PURE__ */ jsx91("div", { className: "flex-shrink-0 w-8 h-8 mt-0.5 rounded-sm border border-recessed flex items-center justify-center", children: /* @__PURE__ */ jsx91(IconComponent, { className: "w-4 h-4 text-tertiary", strokeWidth: 1.5 }) }),
                  /* @__PURE__ */ jsxs78("div", { className: "flex-1 min-w-0", children: [
                    /* @__PURE__ */ jsxs78("div", { className: "flex items-baseline gap-2 mb-0.5", children: [
                      /* @__PURE__ */ jsx91("span", { className: "text-base font-sans font-semibold text-primary", children: item.name }),
                      /* @__PURE__ */ jsx91("span", { className: "text-caption-sm font-sans text-tertiary uppercase tracking-wider", children: item.tagline })
                    ] }),
                    /* @__PURE__ */ jsx91("p", { className: "text-body-sm font-serif text-secondary", children: item.description })
                  ] })
                ] })
              },
              index
            );
          }) })
        ] })
      ] })
    ] })
  ] }) });
}

// src/sections/UseCaseSection.tsx
import {
  GaugeIcon,
  PieChartIcon as PieChartIcon2,
  TrendingUpIcon as TrendingUpIcon5,
  ShieldCheckIcon as ShieldCheckIcon3,
  LockIcon as LockIcon3,
  ServerIcon,
  KeyIcon,
  DatabaseIcon as DatabaseIcon4,
  LayersIcon as LayersIcon2
} from "lucide-react";
import { jsx as jsx92, jsxs as jsxs79 } from "react/jsx-runtime";
var ICON_MAP = {
  "shield-check": ShieldCheckIcon3,
  database: DatabaseIcon4,
  key: KeyIcon,
  lock: LockIcon3,
  server: ServerIcon,
  layers: LayersIcon2,
  gauge: GaugeIcon,
  "pie-chart": PieChartIcon2,
  "trending-up": TrendingUpIcon5
};
var defaultUseCases = [
  {
    title: "[Use Case Title]",
    content: "[Use case description]"
  }
];
var defaultCollateralTypes = [
  { name: "[Asset Class]", minLtv: 0, maxLtv: 0 }
];
function UseCaseSection({
  sectionLabel = "[Section Label]",
  headline = "[Headline]",
  description = "[Section description]",
  useCasesLeadIn,
  useCases = defaultUseCases,
  useCasesOutro,
  useCasesOutroDescription,
  rightSlot,
  collateralTypesVisible = true,
  collateralTypesHeadline = "Typical LTV by Asset Type",
  collateralTypesDescription,
  collateralTypes = defaultCollateralTypes,
  collateralTypesCitation
}) {
  const maxLtvValue = Math.min(
    100,
    Math.max(...collateralTypes.map((c) => c.maxLtv), 75)
  );
  return /* @__PURE__ */ jsx92("section", { className: "bg-canvas py-16 sm:py-20 lg:py-24", children: /* @__PURE__ */ jsxs79("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: [
    /* @__PURE__ */ jsx92("div", { className: "text-label-xs uppercase font-sans font-semibold text-tertiary mb-6 sm:mb-8", children: sectionLabel }),
    /* @__PURE__ */ jsxs79("div", { className: "grid lg:grid-cols-2 gap-8 lg:gap-16 items-start", children: [
      /* @__PURE__ */ jsxs79("div", { children: [
        /* @__PURE__ */ jsx92(
          HeadlineWithEmphasis,
          {
            text: headline,
            as: "h2",
            className: "text-heading-section sm:text-heading-section-md lg:text-heading-section-lg mb-6 sm:mb-8"
          }
        ),
        /* @__PURE__ */ jsx92(
          FormattedText,
          {
            text: description,
            as: "p",
            className: "text-body-md sm:text-body-lg font-serif text-secondary mb-6"
          }
        ),
        /* @__PURE__ */ jsxs79("div", { className: "space-y-4 sm:space-y-5", children: [
          useCasesLeadIn && /* @__PURE__ */ jsx92(
            HeadlineWithEmphasis,
            {
              text: useCasesLeadIn,
              as: "h3",
              className: "text-heading-card sm:text-heading-card-md font-serif font-normal tracking-headline-serif"
            }
          ),
          /* @__PURE__ */ jsx92("div", { className: "space-y-4 sm:space-y-5", children: useCases.map((useCase, index) => {
            const IconComponent = useCase.icon ? ICON_MAP[useCase.icon] : void 0;
            return /* @__PURE__ */ jsxs79("div", { className: IconComponent ? "flex gap-4 items-start" : "border-l-2 border-accent/30 pl-4 sm:pl-5", children: [
              IconComponent && /* @__PURE__ */ jsx92("div", { className: "flex-shrink-0 w-10 h-10 rounded-sm bg-canvas flex items-center justify-center mt-0.5", children: /* @__PURE__ */ jsx92(IconComponent, { className: "w-5 h-5 text-accent", strokeWidth: 1.5 }) }),
              /* @__PURE__ */ jsxs79("div", { className: IconComponent ? "flex-1 min-w-0" : void 0, children: [
                /* @__PURE__ */ jsx92("h4", { className: "text-base sm:text-lg font-sans font-semibold text-primary leading-tight mb-1.5 sm:mb-2", children: useCase.title }),
                /* @__PURE__ */ jsx92("p", { className: "text-sm sm:text-body-md font-serif text-secondary leading-relaxed", children: useCase.content }),
                useCase.badge && /* @__PURE__ */ jsx92("p", { className: "mt-2 text-sm font-sans font-medium text-accent", children: useCase.badge })
              ] })
            ] }, index);
          }) }),
          (useCasesOutro || useCasesOutroDescription) && /* @__PURE__ */ jsxs79("div", { className: "mt-6 sm:mt-8", children: [
            useCasesOutro && /* @__PURE__ */ jsx92(
              HeadlineWithEmphasis,
              {
                text: useCasesOutro,
                as: "p",
                className: "text-heading-card sm:text-heading-card-md font-serif font-normal tracking-headline-serif mb-3"
              }
            ),
            useCasesOutroDescription && /* @__PURE__ */ jsx92(
              FormattedText,
              {
                text: useCasesOutroDescription,
                as: "p",
                className: "text-sm sm:text-body-md font-serif text-secondary leading-relaxed"
              }
            )
          ] })
        ] })
      ] }),
      rightSlot ? /* @__PURE__ */ jsx92("div", { children: rightSlot }) : collateralTypesVisible ? /* @__PURE__ */ jsx92("div", { children: /* @__PURE__ */ jsx92("div", { className: "w-full", children: /* @__PURE__ */ jsxs79("div", { className: "bg-surface border border-recessed p-5 sm:p-6 lg:p-8 rounded-sm shadow-sm", children: [
        /* @__PURE__ */ jsxs79("div", { className: "mb-6 sm:mb-8", children: [
          /* @__PURE__ */ jsx92("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-3", children: collateralTypesHeadline }),
          collateralTypesDescription && /* @__PURE__ */ jsx92(
            FormattedText,
            {
              text: collateralTypesDescription,
              as: "p",
              className: "text-sm sm:text-base font-serif text-secondary leading-tight"
            }
          )
        ] }),
        /* @__PURE__ */ jsx92("div", { className: "space-y-2.5 sm:space-y-3", children: collateralTypes.map((item, index) => {
          const minPercent = item.minLtv / maxLtvValue * 100;
          const rangePercent = (item.maxLtv - item.minLtv) / maxLtvValue * 100;
          return /* @__PURE__ */ jsxs79("div", { className: "group", children: [
            /* @__PURE__ */ jsxs79("div", { className: "flex justify-between items-baseline mb-1", children: [
              /* @__PURE__ */ jsx92("span", { className: "text-xs sm:text-sm font-sans font-medium text-primary", children: item.name }),
              /* @__PURE__ */ jsxs79("span", { className: "text-xs sm:text-sm font-sans font-semibold text-accent tabular-nums", children: [
                item.minLtv,
                "\u2013",
                item.maxLtv,
                "%"
              ] })
            ] }),
            /* @__PURE__ */ jsxs79("div", { className: "relative h-2 bg-recessed rounded-[1px] overflow-hidden", children: [
              /* @__PURE__ */ jsx92(
                "div",
                {
                  className: "absolute h-full bg-accent rounded-[1px] transition-all duration-300",
                  style: {
                    left: `${minPercent}%`,
                    width: `${rangePercent}%`
                  }
                }
              ),
              /* @__PURE__ */ jsx92("div", { className: "absolute inset-0 flex", children: [25, 50, 75].map((mark) => /* @__PURE__ */ jsx92(
                "div",
                {
                  className: "absolute h-full w-px bg-white/40",
                  style: { left: `${mark / maxLtvValue * 100}%` }
                },
                mark
              )) })
            ] }),
            item.rationale && /* @__PURE__ */ jsx92("p", { className: "mt-1 text-label-xs font-sans uppercase text-tertiary sm:opacity-0 sm:group-hover:opacity-100 sm:transition-opacity sm:duration-200", children: item.rationale })
          ] }, index);
        }) }),
        /* @__PURE__ */ jsx92("div", { className: "mt-3 sm:mt-4 pt-2.5 sm:pt-3 border-t border-recessed", children: /* @__PURE__ */ jsxs79("div", { className: "flex justify-between text-code-xs sm:text-caption-xs font-mono text-tertiary", children: [
          /* @__PURE__ */ jsx92("span", { children: "0%" }),
          /* @__PURE__ */ jsx92("span", { children: "25%" }),
          /* @__PURE__ */ jsx92("span", { children: "50%" }),
          /* @__PURE__ */ jsx92("span", { children: "75%" })
        ] }) }),
        collateralTypesCitation && /* @__PURE__ */ jsx92("div", { className: "mt-3 sm:mt-4", children: /* @__PURE__ */ jsx92("p", { className: "text-caption-xs sm:text-caption-sm font-sans text-tertiary", children: collateralTypesCitation }) })
      ] }) }) }) : null
    ] })
  ] }) });
}

// src/sections/MetricsBar.tsx
import { jsx as jsx93, jsxs as jsxs80 } from "react/jsx-runtime";
var defaultMetrics = [
  { value: "$2.4B", label: "Assets under management", change: "+18.3%" },
  { value: "47", label: "Active institutions", change: "+12" },
  { value: "$186M", label: "Loan originations", change: "+34.2%" },
  { value: "14.3%", label: "Risk-adjusted return", change: "+2.1pp" }
];
function MetricsBar({ stats, variant = "light" }) {
  const metrics = (stats || defaultMetrics).map((stat) => ({
    ...stat,
    change: stat.change || "",
    positive: !stat.change || !stat.change.startsWith("-")
  }));
  const isDark = variant === "dark";
  const isWhite = variant === "white";
  const containerStyles = isDark ? "bg-primary py-10 sm:py-12" : isWhite ? "bg-surface py-8 sm:py-10" : "bg-canvas py-8 sm:py-10";
  const valueStyles = isDark ? "text-heading-section sm:text-heading-section-md lg:text-heading-section-lg font-serif font-semibold tracking-headline-serif text-white mb-1 tabular-nums" : "text-heading-section sm:text-heading-section-md lg:text-heading-section-lg font-serif font-semibold tracking-headline-serif text-accent mb-1 tabular-nums";
  const labelStyles = isDark ? "text-label-xs sm:text-label-sm font-sans font-semibold uppercase text-white/70 mb-1 sm:mb-2 min-h-[2.5em] flex items-center justify-center" : "text-label-xs sm:text-label-sm font-sans font-semibold uppercase text-tertiary mb-1 sm:mb-2 min-h-[2.5em] flex items-center justify-center";
  const changeStyles = isDark ? "text-caption-sm font-mono font-medium tabular-nums" : "text-caption-sm font-mono font-medium tabular-nums";
  return /* @__PURE__ */ jsx93("section", { className: containerStyles, children: /* @__PURE__ */ jsx93("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: /* @__PURE__ */ jsx93("div", { className: "grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8", children: metrics.map((metric, index) => /* @__PURE__ */ jsxs80("div", { className: "text-center", children: [
    /* @__PURE__ */ jsx93("div", { className: valueStyles, children: metric.value }),
    /* @__PURE__ */ jsx93("div", { className: labelStyles, children: /* @__PURE__ */ jsx93("span", { className: "text-center", children: metric.label }) }),
    metric.change && /* @__PURE__ */ jsx93(
      "div",
      {
        className: `${changeStyles} ${metric.positive ? "text-growth" : "text-error"}`,
        children: metric.change
      }
    )
  ] }, index)) }) }) });
}

// src/sections/CompetitivePositioning.tsx
import { jsx as jsx94, jsxs as jsxs81 } from "react/jsx-runtime";
var defaultCompetitors = [
  {
    name: "Aaim",
    position: 95,
    label: "Infrastructure",
    highlight: true
  },
  {
    name: "Figure",
    position: 60,
    label: "Platform",
    highlight: false
  },
  {
    name: "Blend",
    position: 35,
    label: "Mortgage",
    highlight: false
  }
];
function CompetitivePositioning({
  sectionLabel = "Competitive Positioning",
  competitors = defaultCompetitors
}) {
  return /* @__PURE__ */ jsxs81("div", { className: "bg-surface border border-recessed p-10 shadow-sm rounded-sm", children: [
    /* @__PURE__ */ jsx94("div", { className: "text-label-xs uppercase font-sans font-semibold text-tertiary mb-6", children: sectionLabel }),
    /* @__PURE__ */ jsx94("div", { className: "space-y-8", children: competitors.map((competitor, index) => /* @__PURE__ */ jsxs81("div", { children: [
      /* @__PURE__ */ jsxs81("div", { className: "flex justify-between items-baseline mb-3", children: [
        /* @__PURE__ */ jsx94("span", { className: "text-heading-card font-serif text-primary tracking-headline-serif", children: competitor.name }),
        /* @__PURE__ */ jsx94("span", { className: "text-base font-sans text-accent", children: competitor.label })
      ] }),
      /* @__PURE__ */ jsx94("div", { className: "relative h-3 bg-recessed rounded-sm", children: /* @__PURE__ */ jsx94(
        "div",
        {
          className: `absolute left-0 top-0 h-full rounded-sm ${competitor.highlight ? "bg-accent" : "bg-neutral-data"}`,
          style: { width: `${competitor.position}%` }
        }
      ) })
    ] }, index)) })
  ] });
}

// src/sections/WealthTransferVisualization.tsx
import { jsx as jsx95, jsxs as jsxs82 } from "react/jsx-runtime";
var defaultStats = [
  {
    value: "$78.6T",
    label: "Boomer Wealth",
    sublabel: "Declining 2024-2045",
    color: "accent"
  },
  {
    value: "31%",
    label: "Alternative Allocation",
    sublabel: "Millennial Preference",
    color: "primary"
  },
  {
    value: "4,619",
    label: "Credit Unions",
    sublabel: "Immediate Market",
    color: "primary"
  }
];
function WealthTransferVisualization({
  sectionLabel = "Use Case: Data Visualization",
  headline = "Wealth Transfer Visualization",
  stats = defaultStats,
  flowTopologyLabel = "Flow Topology",
  flowTopologyDescription = "Generational wealth flows through four cascading stages: demographic cohort, wealth tier, asset category, and collateralization status. Each transition represents friction points traditional infrastructure cannot address. Aaim's patent-pending methodology captures value at every transition.",
  className = ""
}) {
  return /* @__PURE__ */ jsx95("section", { className: `bg-canvas py-16 sm:py-20 lg:py-24 ${className}`, children: /* @__PURE__ */ jsxs82("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: [
    /* @__PURE__ */ jsx95("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-6 sm:mb-8", children: sectionLabel }),
    /* @__PURE__ */ jsx95(Heading, { level: 2, size: "section", className: "mb-10 sm:mb-16", children: headline }),
    /* @__PURE__ */ jsx95("div", { className: "grid sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12 lg:gap-16 mb-10 sm:mb-16", children: stats.map((stat, index) => /* @__PURE__ */ jsxs82("div", { className: "text-center", children: [
      /* @__PURE__ */ jsx95("div", { className: `text-stat sm:text-stat-md lg:text-stat-lg font-serif font-normal tracking-headline-serif ${stat.color === "accent" ? "text-accent" : "text-primary"} mb-2 sm:mb-3 tabular-nums leading-none`, children: stat.value }),
      /* @__PURE__ */ jsx95("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-2", children: stat.label }),
      /* @__PURE__ */ jsx95("div", { className: "text-sm sm:text-base font-serif text-secondary", children: stat.sublabel })
    ] }, index)) }),
    /* @__PURE__ */ jsxs82("div", { className: "bg-surface border border-recessed p-6 sm:p-8 lg:p-10 rounded-sm shadow-sm", children: [
      /* @__PURE__ */ jsx95("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-4 sm:mb-6", children: flowTopologyLabel }),
      /* @__PURE__ */ jsx95("p", { className: "text-base sm:text-lg font-serif text-secondary leading-body", children: flowTopologyDescription })
    ] })
  ] }) });
}

// src/sections/AssetClassTaxonomy.tsx
import { jsx as jsx96, jsxs as jsxs83 } from "react/jsx-runtime";
var defaultCategories = [
  {
    name: "Digital Assets",
    description: "Cryptocurrency and tokenized holdings",
    assets: [
      { name: "Bitcoin", examples: ["BTC", "Wrapped BTC"] },
      { name: "Ethereum", examples: ["ETH", "Staked ETH"] },
      { name: "Stablecoins", examples: ["USDC", "USDT"] },
      { name: "DeFi Tokens", examples: ["Aave", "Compound"] }
    ]
  },
  {
    name: "Investment Accounts",
    description: "Brokerage and retirement holdings",
    assets: [
      { name: "Brokerage", examples: ["Stocks", "Bonds", "ETFs"] },
      { name: "IRA/401k", examples: ["Traditional", "Roth"] },
      { name: "HSA/529", examples: ["Health", "Education"] }
    ]
  },
  {
    name: "Alternative Investments",
    description: "Non-traditional asset classes",
    assets: [
      { name: "Private Equity", examples: ["VC funds", "PE funds"] },
      { name: "Real Estate", examples: ["REITs", "Syndications"] },
      { name: "Collectibles", examples: ["Art", "Wine", "Watches"] },
      { name: "Commodities", examples: ["Gold", "Silver"] }
    ]
  },
  {
    name: "Income Streams",
    description: "Receivables and future payments",
    assets: [
      { name: "Royalties", examples: ["Music", "Patents"] },
      { name: "Receivables", examples: ["Invoices", "Notes"] },
      { name: "Annuities", examples: ["Fixed", "Variable"] }
    ]
  }
];
function AssetClassTaxonomy({
  sectionLabel = "Asset Coverage",
  headline = "Every asset class your members own",
  description = "Comprehensive valuation and monitoring across 50+ asset types, from traditional investments to emerging digital assets.",
  categories = defaultCategories,
  sourceAttribution = "Asset classification based on UCC Article 9 and SEC regulatory frameworks",
  variant = "grid",
  className
}) {
  if (variant === "list") {
    return /* @__PURE__ */ jsx96("section", { className: `py-16 sm:py-20 lg:py-24 ${className || ""}`, children: /* @__PURE__ */ jsxs83("div", { className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8", children: [
      /* @__PURE__ */ jsxs83("div", { className: "mb-12", children: [
        sectionLabel && /* @__PURE__ */ jsx96("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-4", children: sectionLabel }),
        /* @__PURE__ */ jsx96("h2", { className: "text-heading-section sm:text-heading-section-md font-serif font-semibold text-primary mb-4 tracking-headline-serif", children: headline }),
        description && /* @__PURE__ */ jsx96("p", { className: "text-lead font-serif text-secondary leading-relaxed max-w-2xl", children: description })
      ] }),
      /* @__PURE__ */ jsx96("div", { className: "space-y-10", children: categories.map((category, idx) => /* @__PURE__ */ jsxs83("div", { children: [
        /* @__PURE__ */ jsx96("h3", { className: "text-heading-card font-sans font-semibold text-primary mb-2", children: category.name }),
        /* @__PURE__ */ jsx96("p", { className: "text-body-sm font-serif text-tertiary mb-4", children: category.description }),
        /* @__PURE__ */ jsx96("div", { className: "grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-2", children: category.assets.map((asset, assetIdx) => /* @__PURE__ */ jsxs83("div", { className: "py-1", children: [
          /* @__PURE__ */ jsx96("span", { className: "text-body-sm font-sans text-primary", children: asset.name }),
          asset.examples && /* @__PURE__ */ jsx96("span", { className: "text-caption-sm font-sans text-tertiary ml-2", children: asset.examples.join(", ") })
        ] }, assetIdx)) })
      ] }, idx)) }),
      sourceAttribution && /* @__PURE__ */ jsx96("div", { className: "mt-12 pt-6 border-t border-recessed/30", children: /* @__PURE__ */ jsx96("p", { className: "text-caption-sm font-sans text-tertiary", children: sourceAttribution }) })
    ] }) });
  }
  if (variant === "compact") {
    return /* @__PURE__ */ jsx96("section", { className: `py-12 sm:py-16 lg:py-20 ${className || ""}`, children: /* @__PURE__ */ jsxs83("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: [
      (sectionLabel || headline) && /* @__PURE__ */ jsxs83("div", { className: "mb-8", children: [
        sectionLabel && /* @__PURE__ */ jsx96("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-2", children: sectionLabel }),
        headline && /* @__PURE__ */ jsx96("h2", { className: "text-heading-subsection font-serif font-semibold text-primary tracking-headline-serif", children: headline })
      ] }),
      /* @__PURE__ */ jsx96("div", { className: "grid grid-cols-2 lg:grid-cols-4 gap-8", children: categories.map((category, idx) => /* @__PURE__ */ jsxs83("div", { children: [
        /* @__PURE__ */ jsx96("h3", { className: "text-body-sm font-sans font-semibold text-primary mb-3", children: category.name }),
        /* @__PURE__ */ jsx96("ul", { className: "space-y-1", children: category.assets.map((asset, assetIdx) => /* @__PURE__ */ jsx96(
          "li",
          {
            className: "text-caption-md font-sans text-secondary",
            children: asset.name
          },
          assetIdx
        )) })
      ] }, idx)) })
    ] }) });
  }
  return /* @__PURE__ */ jsx96("section", { className: `py-16 sm:py-20 lg:py-24 ${className || ""}`, children: /* @__PURE__ */ jsxs83("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: [
    /* @__PURE__ */ jsxs83("div", { className: "mb-12 sm:mb-16", children: [
      sectionLabel && /* @__PURE__ */ jsx96("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-4", children: sectionLabel }),
      /* @__PURE__ */ jsx96("h2", { className: "text-heading-section sm:text-heading-section-md lg:text-heading-section-lg font-serif font-semibold text-primary mb-4 tracking-headline-serif", children: headline }),
      description && /* @__PURE__ */ jsx96("p", { className: "text-lead sm:text-lead-md font-serif text-secondary leading-relaxed max-w-content-narrow", children: description })
    ] }),
    /* @__PURE__ */ jsx96("div", { className: "grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10", children: categories.map((category, idx) => /* @__PURE__ */ jsxs83("div", { children: [
      /* @__PURE__ */ jsx96("h3", { className: "text-heading-card font-sans font-semibold text-primary mb-2", children: category.name }),
      /* @__PURE__ */ jsx96("p", { className: "text-body-sm font-serif text-tertiary mb-6 leading-relaxed", children: category.description }),
      /* @__PURE__ */ jsx96("ul", { className: "space-y-3", children: category.assets.map((asset, assetIdx) => /* @__PURE__ */ jsxs83("li", { children: [
        /* @__PURE__ */ jsx96("div", { className: "text-body-sm font-sans font-medium text-primary", children: asset.name }),
        asset.examples && /* @__PURE__ */ jsx96("div", { className: "text-caption-sm font-sans text-tertiary mt-0.5", children: asset.examples.join(", ") })
      ] }, assetIdx)) })
    ] }, idx)) }),
    sourceAttribution && /* @__PURE__ */ jsx96("div", { className: "mt-12 sm:mt-16 pt-6 border-t border-recessed/30", children: /* @__PURE__ */ jsx96("p", { className: "text-caption-sm font-sans text-tertiary", children: sourceAttribution }) })
  ] }) });
}

// src/sections/FAQSection.tsx
import { useState as useState23 } from "react";
import { ChevronDownIcon as ChevronDownIcon9 } from "lucide-react";
import { jsx as jsx97, jsxs as jsxs84 } from "react/jsx-runtime";
function FAQSection({
  faqs,
  title = "Frequently asked questions",
  subtitle,
  onFAQOpen
}) {
  const [openIndex, setOpenIndex] = useState23(null);
  const toggleFAQ = (index, question, category) => {
    const isOpening = openIndex !== index;
    setOpenIndex(openIndex === index ? null : index);
    if (isOpening && onFAQOpen) {
      onFAQOpen(question, category);
    }
  };
  if (!faqs || faqs.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsx97("section", { className: "py-16 sm:py-20 lg:py-24 bg-canvas", children: /* @__PURE__ */ jsxs84("div", { className: "max-w-content-narrow mx-auto px-4 sm:px-6 lg:px-8", children: [
    /* @__PURE__ */ jsxs84("div", { className: "text-center mb-12", children: [
      /* @__PURE__ */ jsx97(Heading, { level: 2, size: "section", weight: "normal", className: "mb-4", children: title }),
      subtitle && /* @__PURE__ */ jsx97("p", { className: "text-base sm:text-lg font-serif text-secondary", children: subtitle })
    ] }),
    /* @__PURE__ */ jsx97("div", { className: "space-y-4", children: faqs.map((faq, index) => /* @__PURE__ */ jsxs84(
      "div",
      {
        className: "bg-surface border border-recessed rounded-sm shadow-sm overflow-hidden",
        children: [
          /* @__PURE__ */ jsxs84(
            "button",
            {
              onClick: () => toggleFAQ(index, faq.question, faq.category),
              className: "w-full px-6 py-5 text-left flex items-center justify-between gap-4 hover:bg-canvas/50 transition-colors",
              "aria-expanded": openIndex === index,
              children: [
                /* @__PURE__ */ jsx97("span", { className: "text-base sm:text-lg font-sans font-medium text-primary leading-relaxed", children: faq.question }),
                /* @__PURE__ */ jsx97(
                  ChevronDownIcon9,
                  {
                    className: `w-5 h-5 text-tertiary flex-shrink-0 transition-transform duration-200 ${openIndex === index ? "rotate-180" : ""}`
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsx97(
            "div",
            {
              className: `grid transition-all duration-200 ease-in-out ${openIndex === index ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`,
              children: /* @__PURE__ */ jsx97("div", { className: "overflow-hidden", children: /* @__PURE__ */ jsx97("div", { className: "px-6 pb-6 pt-0 space-y-4", children: faq.answer.split(/\n\n+/).map((paragraph, pIndex) => /* @__PURE__ */ jsx97(
                "p",
                {
                  className: "text-base font-serif text-secondary leading-body",
                  children: paragraph.trim()
                },
                pIndex
              )) }) })
            }
          )
        ]
      },
      index
    )) })
  ] }) });
}

// src/sections/HeroManifestoTransition.tsx
import { useEffect as useEffect18, useRef as useRef17, useState as useState24, useCallback as useCallback14, useMemo as useMemo11 } from "react";
import { jsx as jsx98, jsxs as jsxs85 } from "react/jsx-runtime";
function parseSentence(sentence) {
  const pattern = /\^\^([^^]+)\^\^/g;
  const revealWords = [];
  const matches = sentence.text.matchAll(pattern);
  for (const match of matches) {
    revealWords.push(match[1]);
  }
  const baseText = sentence.text.replace(pattern, "\u200B$1\u200B");
  return {
    baseText,
    revealWords,
    color: sentence.color
  };
}
function HeroManifestoTransition({
  heroContent,
  metricsContent,
  sectionLabel,
  sentences
}) {
  const containerRef = useRef17(null);
  const mobileManifestoRef = useRef17(null);
  const manifestoContentRef = useRef17(null);
  const [crossfadeProgress, setCrossfadeProgress] = useState24(0);
  const [revealProgress, setRevealProgress] = useState24(0);
  const [mobileRevealProgress, setMobileRevealProgress] = useState24(0);
  const [isPinned, setIsPinned] = useState24(true);
  const [isDesktop, setIsDesktop] = useState24(false);
  const [contentScale, setContentScale] = useState24(1);
  const [showManifestoPeek, setShowManifestoPeek] = useState24(false);
  const parsedSentences = useMemo11(() => sentences.map(parseSentence), [sentences]);
  const totalRevealWords = useMemo11(
    () => parsedSentences.reduce((sum, s) => sum + s.revealWords.length, 0),
    [parsedSentences]
  );
  useEffect18(() => {
    const checkViewport = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setIsDesktop(width >= 1280);
      setShowManifestoPeek(height >= 900);
    };
    checkViewport();
    window.addEventListener("resize", checkViewport);
    return () => window.removeEventListener("resize", checkViewport);
  }, []);
  const calculateScale = useCallback14(() => {
    if (!manifestoContentRef.current || !isDesktop) return;
    const content = manifestoContentRef.current;
    const naturalHeight = content.scrollHeight;
    const viewportHeight = window.innerHeight;
    const topMargin = 96;
    const bottomMargin = 80;
    const availableHeight = viewportHeight - topMargin - bottomMargin;
    const scale = Math.min(1.6, Math.max(1, availableHeight / naturalHeight));
    setContentScale(scale);
  }, [isDesktop]);
  useEffect18(() => {
    if (!isDesktop) {
      setContentScale(1);
      return;
    }
    const timer = setTimeout(calculateScale, 50);
    window.addEventListener("resize", calculateScale);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", calculateScale);
    };
  }, [isDesktop, calculateScale]);
  useEffect18(() => {
    if (!isDesktop) return;
    const handleScroll = () => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const crossfadeScrolled = Math.max(0, -rect.top);
      const crossfade = Math.min(1, crossfadeScrolled / windowHeight);
      setCrossfadeProgress(crossfade);
      const revealStart = windowHeight * 0.5;
      const revealScrolled = Math.max(0, -rect.top - revealStart);
      const revealTotal = windowHeight * 2.5;
      const reveal = Math.min(1, revealScrolled / revealTotal);
      setRevealProgress(reveal);
      const totalPinnedScroll = windowHeight * 3.5;
      const shouldPin = -rect.top < totalPinnedScroll;
      setIsPinned(shouldPin);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isDesktop]);
  useEffect18(() => {
    if (isDesktop) return;
    const handleMobileScroll = () => {
      const manifesto = mobileManifestoRef.current;
      if (!manifesto) return;
      const rect = manifesto.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const enterPoint = windowHeight * 0.8;
      const completePoint = windowHeight * 0.2;
      if (rect.top > enterPoint) {
        setMobileRevealProgress(0);
      } else if (rect.top < completePoint) {
        setMobileRevealProgress(1);
      } else {
        const progress = (enterPoint - rect.top) / (enterPoint - completePoint);
        setMobileRevealProgress(Math.min(1, Math.max(0, progress)));
      }
    };
    window.addEventListener("scroll", handleMobileScroll, { passive: true });
    handleMobileScroll();
    return () => window.removeEventListener("scroll", handleMobileScroll);
  }, [isDesktop]);
  const heroOpacity = 1 - crossfadeProgress;
  const manifestoTranslateY = (1 - crossfadeProgress) * 100;
  const manifestoOpacity = crossfadeProgress;
  const sentenceProgress = totalRevealWords > 0 ? Math.min(1, revealProgress / 0.8) : revealProgress;
  const wordProgress = totalRevealWords > 0 ? Math.max(0, (revealProgress - 0.8) / 0.2) : 0;
  const desktopVisibleCount = Math.ceil(sentenceProgress * sentences.length);
  const mobileVisibleCount = Math.ceil(mobileRevealProgress * sentences.length);
  const visibleRevealWords = Math.ceil(wordProgress * totalRevealWords);
  const renderSentence = (parsed, sentenceIndex, isSentenceVisible, revealWordOffset) => {
    const isAccent = parsed.color === "accent";
    const hasRevealWords = parsed.revealWords.length > 0;
    if (!hasRevealWords) {
      return /* @__PURE__ */ jsxs85(
        "span",
        {
          className: `inline transition-all duration-500 ${isAccent ? "text-accent" : "text-primary"}`,
          style: {
            opacity: isSentenceVisible ? 1 : 0.06,
            filter: isSentenceVisible ? "none" : "blur(2px)",
            transitionDelay: `${sentenceIndex % 3 * 40}ms`
          },
          children: [
            parsed.baseText,
            sentenceIndex < sentences.length - 1 && " "
          ]
        },
        sentenceIndex
      );
    }
    const parts = parsed.baseText.split("\u200B");
    let wordIndex = 0;
    return /* @__PURE__ */ jsxs85(
      "span",
      {
        className: `inline transition-all duration-500 ${isAccent ? "text-accent" : "text-primary"}`,
        style: {
          opacity: isSentenceVisible ? 1 : 0.06,
          filter: isSentenceVisible ? "none" : "blur(2px)",
          transitionDelay: `${sentenceIndex % 3 * 40}ms`
        },
        children: [
          parts.map((part, partIndex) => {
            const isRevealWord = partIndex % 2 === 1;
            if (isRevealWord) {
              const globalWordIndex = revealWordOffset + wordIndex;
              const isWordVisible = isSentenceVisible && globalWordIndex < visibleRevealWords;
              wordIndex++;
              return /* @__PURE__ */ jsx98(
                "span",
                {
                  className: "inline-block transition-all duration-700",
                  style: {
                    opacity: isWordVisible ? 1 : 0,
                    transform: isWordVisible ? "translateY(0)" : "translateY(8px)",
                    filter: isWordVisible ? "none" : "blur(4px)",
                    transitionDelay: `${globalWordIndex % totalRevealWords * 150}ms`
                  },
                  children: part
                },
                partIndex
              );
            }
            return /* @__PURE__ */ jsx98("span", { children: part }, partIndex);
          }),
          sentenceIndex < sentences.length - 1 && " "
        ]
      },
      sentenceIndex
    );
  };
  const getRevealWordOffset = (sentenceIndex) => {
    let offset = 0;
    for (let i = 0; i < sentenceIndex; i++) {
      offset += parsedSentences[i].revealWords.length;
    }
    return offset;
  };
  if (!isDesktop) {
    return /* @__PURE__ */ jsxs85("div", { className: "relative", children: [
      /* @__PURE__ */ jsx98("div", { className: "min-h-[75vh] sm:min-h-[80vh] flex flex-col", children: heroContent }),
      /* @__PURE__ */ jsx98("div", { className: "relative z-10 -mt-8 sm:-mt-12 mb-8 sm:mb-12", children: metricsContent }),
      /* @__PURE__ */ jsx98("div", { ref: mobileManifestoRef, className: "bg-canvas py-12 sm:py-16 lg:py-20", children: /* @__PURE__ */ jsxs85("div", { className: "max-w-content-medium mx-auto px-6 sm:px-8", children: [
        sectionLabel && /* @__PURE__ */ jsx98("div", { className: "text-center mb-6", children: /* @__PURE__ */ jsx98("span", { className: "text-label-xs uppercase font-sans font-semibold text-tertiary", children: sectionLabel }) }),
        /* @__PURE__ */ jsx98("p", { className: "text-lead sm:text-lead lg:text-lead-md font-serif font-normal leading-body tracking-headline-sans", children: parsedSentences.map((parsed, index) => {
          const isVisible = index < mobileVisibleCount;
          const isAccent = parsed.color === "accent";
          const displayText = parsed.baseText.replace(/\u200B/g, "");
          return /* @__PURE__ */ jsxs85(
            "span",
            {
              className: `inline transition-all duration-500 ${isAccent ? "text-accent" : "text-primary"}`,
              style: {
                opacity: isVisible ? 1 : 0.08,
                filter: isVisible ? "none" : "blur(1px)",
                transitionDelay: `${index % 3 * 30}ms`
              },
              children: [
                displayText,
                index < sentences.length - 1 && " "
              ]
            },
            index
          );
        }) })
      ] }) })
    ] });
  }
  return /* @__PURE__ */ jsxs85(
    "div",
    {
      ref: containerRef,
      className: "relative",
      style: { height: "450vh" },
      children: [
        /* @__PURE__ */ jsxs85(
          "div",
          {
            className: "fixed top-0 left-0 right-0 z-10",
            style: {
              opacity: heroOpacity,
              pointerEvents: crossfadeProgress >= 1 ? "none" : "auto",
              visibility: crossfadeProgress >= 1 ? "hidden" : "visible"
            },
            children: [
              /* @__PURE__ */ jsx98("div", { className: "pt-16", children: heroContent }),
              /* @__PURE__ */ jsx98(
                "div",
                {
                  className: "pb-4",
                  style: {
                    opacity: Math.max(0, 1 - crossfadeProgress * 1.5)
                  },
                  children: metricsContent
                }
              )
            ]
          }
        ),
        showManifestoPeek && /* @__PURE__ */ jsx98(
          "div",
          {
            className: "fixed bottom-0 left-0 right-0 z-10 pointer-events-none",
            style: {
              opacity: Math.max(0, 1 - crossfadeProgress * 2)
            },
            children: /* @__PURE__ */ jsxs85("div", { className: "max-w-content-medium mx-auto px-8 sm:px-12 lg:px-16 text-center", children: [
              sectionLabel && /* @__PURE__ */ jsx98("span", { className: "text-label-xs uppercase font-sans font-semibold text-tertiary block mb-3", children: sectionLabel }),
              /* @__PURE__ */ jsxs85("div", { className: "relative overflow-hidden", style: { maxHeight: "2.8em" }, children: [
                /* @__PURE__ */ jsx98("p", { className: "text-lead-md font-serif font-normal leading-relaxed tracking-headline-sans", children: parsedSentences.slice(0, 2).map((parsed, index) => {
                  const isAccent = parsed.color === "accent";
                  const displayText = parsed.baseText.replace(/\u200B/g, "");
                  return /* @__PURE__ */ jsxs85(
                    "span",
                    {
                      className: isAccent ? "text-accent" : "text-primary",
                      children: [
                        displayText,
                        index < 1 && " "
                      ]
                    },
                    index
                  );
                }) }),
                /* @__PURE__ */ jsx98("div", { className: "absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-canvas to-transparent" })
              ] })
            ] })
          }
        ),
        /* @__PURE__ */ jsx98(
          "div",
          {
            className: `${isPinned ? "fixed" : "absolute"} left-0 right-0 h-screen z-20 bg-canvas border-t border-warm-200`,
            style: {
              top: isPinned ? 0 : "350vh",
              transform: isPinned ? `translateY(${manifestoTranslateY}%)` : "none",
              pointerEvents: crossfadeProgress > 0.5 ? "auto" : "none"
            },
            children: /* @__PURE__ */ jsxs85("div", { className: "h-full flex flex-col items-center justify-center px-4", children: [
              /* @__PURE__ */ jsx98(
                "div",
                {
                  className: "absolute left-0 top-1/2 w-1 bg-gradient-to-b from-transparent via-accent to-transparent transition-all duration-700",
                  style: {
                    opacity: revealProgress > 0.05 ? 0.7 : 0,
                    height: `${Math.min(revealProgress * 400, 200)}px`,
                    transform: "translateY(-50%)"
                  }
                }
              ),
              /* @__PURE__ */ jsx98(
                "div",
                {
                  className: "absolute right-0 top-1/2 w-1 bg-gradient-to-b from-transparent via-accent to-transparent transition-all duration-700",
                  style: {
                    opacity: revealProgress > 0.05 ? 0.7 : 0,
                    height: `${Math.min(revealProgress * 400, 200)}px`,
                    transform: "translateY(-50%)"
                  }
                }
              ),
              /* @__PURE__ */ jsxs85(
                "div",
                {
                  ref: manifestoContentRef,
                  className: "relative max-w-content-medium mx-auto px-8 sm:px-12 lg:px-16 origin-center",
                  style: { transform: `scale(${contentScale})` },
                  children: [
                    /* @__PURE__ */ jsxs85("div", { style: { opacity: isPinned ? manifestoOpacity : 1 }, children: [
                      sectionLabel && /* @__PURE__ */ jsx98("div", { className: "text-center mb-6", children: /* @__PURE__ */ jsx98("span", { className: "text-label-xs uppercase font-sans font-semibold text-tertiary", children: sectionLabel }) }),
                      /* @__PURE__ */ jsx98("p", { className: "text-lead-md xl:text-lead-lg font-serif font-normal leading-relaxed tracking-headline-sans text-justify", children: parsedSentences.map((parsed, index) => {
                        const isVisible = index < desktopVisibleCount;
                        const revealWordOffset = getRevealWordOffset(index);
                        return renderSentence(parsed, index, isVisible, revealWordOffset);
                      }) })
                    ] }),
                    /* @__PURE__ */ jsxs85("div", { className: "mt-10 flex justify-center h-12 relative", children: [
                      /* @__PURE__ */ jsx98(
                        "div",
                        {
                          className: "absolute inset-0 flex justify-center items-center transition-opacity duration-500",
                          style: { opacity: crossfadeProgress >= 1 && revealProgress < 0.95 ? 1 : 0 },
                          children: /* @__PURE__ */ jsxs85("div", { className: "flex flex-col items-center gap-2", children: [
                            /* @__PURE__ */ jsx98("div", { className: "w-16 h-[2px] bg-warm-200 rounded-full overflow-hidden", children: /* @__PURE__ */ jsx98(
                              "div",
                              {
                                className: "h-full bg-accent rounded-full transition-all duration-100",
                                style: { width: `${revealProgress * 100}%` }
                              }
                            ) }),
                            /* @__PURE__ */ jsx98("span", { className: "text-code-xs font-sans text-tertiary/50 uppercase tracking-widest", children: "Scroll" })
                          ] })
                        }
                      ),
                      /* @__PURE__ */ jsx98(
                        "div",
                        {
                          className: "absolute inset-0 flex justify-center items-center transition-opacity duration-500",
                          style: { opacity: revealProgress >= 0.95 ? 1 : 0 },
                          children: /* @__PURE__ */ jsxs85(
                            "svg",
                            {
                              width: "24",
                              height: "24",
                              viewBox: "0 0 24 24",
                              fill: "none",
                              stroke: "currentColor",
                              strokeWidth: "2",
                              strokeLinecap: "round",
                              strokeLinejoin: "round",
                              className: "text-accent animate-bounce",
                              children: [
                                /* @__PURE__ */ jsx98("path", { d: "M12 5v14" }),
                                /* @__PURE__ */ jsx98("path", { d: "m19 12-7 7-7-7" })
                              ]
                            }
                          )
                        }
                      )
                    ] })
                  ]
                }
              )
            ] })
          }
        )
      ]
    }
  );
}

// src/sections/HowWeWorkInfographic.tsx
import React31 from "react";
import { jsx as jsx99, jsxs as jsxs86 } from "react/jsx-runtime";
var defaults = {
  sectionLabel: "Partnership Model",
  headline: "You remain the lender.\nWe provide the infrastructure.",
  description: "Aaim sits between your core systems and modern lending products\u2014enabling new capabilities without replacing existing infrastructure.",
  productsLayerLabel: "Member/Customer Layer",
  productsLayerTitle: "Truly Modern Lending Products",
  productsOwnership: "Your Institution",
  products: ["Securities-backed lines", "Crypto-collateralized loans", "Private equity facilities"],
  infrastructureLayerLabel: "SaaS Infrastructure Layer",
  infrastructureLayerTitle: "Aaim Institutional Platform",
  infrastructureOwnership: "Technology Provider",
  infrastructureCapabilities: [
    { title: "Asset verification", description: "Real-time valuation across 50+ asset types" },
    { title: "Contract automation", description: "Generate compliant loan documents instantly" },
    { title: "UCC perfection", description: "Automated filing and tracking workflows" },
    { title: "Compliance tools", description: "Examiner-ready audit trails and reporting" }
  ],
  integrations: ["Custody", "Pricing APIs", "eSignature", "Core Banking"],
  coreLayerLabel: "Core/Tech Layer",
  coreLayerTitle: "Your Existing Systems",
  coreOwnership: "Your Platform",
  coreSystems: ["Core banking platform", "Loan origination system", "Member management"],
  responsibilitiesHeadline: "Clear division of responsibilities",
  responsibilitiesDescription: "You maintain full control over credit decisions and member relationships. We handle the complex technology.",
  yourInstitutionTitle: "Your Institution",
  yourInstitutionItems: [
    "Credit decisions and underwriting",
    "Credit risk ownership",
    "Member relationships",
    "Loan ownership and servicing",
    "Regulatory responsibility",
    "Pricing and terms"
  ],
  aaimProvidesTitle: "Aaim Provides",
  aaimProvidesItems: [
    "Real-time asset valuation",
    "50+ alternative asset types",
    "Automated contract generation",
    "UCC filing and perfection",
    "Custody integrations",
    "Examiner-ready audit trails"
  ],
  bottomQuote: "Infrastructure that scales with your ambitions, not your engineering team."
};
function HowWeWorkInfographic({
  sectionLabel = defaults.sectionLabel,
  headline = defaults.headline,
  description = defaults.description,
  productsLayerLabel = defaults.productsLayerLabel,
  productsLayerTitle = defaults.productsLayerTitle,
  productsOwnership = defaults.productsOwnership,
  products = defaults.products,
  infrastructureLayerLabel = defaults.infrastructureLayerLabel,
  infrastructureLayerTitle = defaults.infrastructureLayerTitle,
  infrastructureOwnership = defaults.infrastructureOwnership,
  infrastructureCapabilities = defaults.infrastructureCapabilities,
  integrations = defaults.integrations,
  coreLayerLabel = defaults.coreLayerLabel,
  coreLayerTitle = defaults.coreLayerTitle,
  coreOwnership = defaults.coreOwnership,
  coreSystems = defaults.coreSystems,
  responsibilitiesHeadline = defaults.responsibilitiesHeadline,
  responsibilitiesDescription = defaults.responsibilitiesDescription,
  yourInstitutionTitle = defaults.yourInstitutionTitle,
  yourInstitutionItems = defaults.yourInstitutionItems,
  aaimProvidesTitle = defaults.aaimProvidesTitle,
  aaimProvidesItems = defaults.aaimProvidesItems,
  bottomQuote = defaults.bottomQuote
}) {
  const headlineParts = headline.split("\n");
  return /* @__PURE__ */ jsx99("section", { className: "py-16 sm:py-20 lg:py-24", children: /* @__PURE__ */ jsxs86("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: [
    /* @__PURE__ */ jsxs86("div", { className: "text-center mb-12 lg:mb-16", children: [
      /* @__PURE__ */ jsx99("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-4", children: sectionLabel }),
      /* @__PURE__ */ jsx99("h2", { className: "text-heading-section sm:text-heading-section-md lg:text-heading-section-lg font-serif font-semibold text-primary mb-6 leading-tight tracking-headline-serif", children: headlineParts.map((part, i) => /* @__PURE__ */ jsxs86(React31.Fragment, { children: [
        part,
        i < headlineParts.length - 1 && /* @__PURE__ */ jsx99("br", {})
      ] }, i)) }),
      /* @__PURE__ */ jsx99("p", { className: "text-base sm:text-lg font-serif text-secondary max-w-content-narrow mx-auto leading-body", children: description })
    ] }),
    /* @__PURE__ */ jsx99("div", { className: "max-w-5xl mx-auto mb-16 lg:mb-20", children: /* @__PURE__ */ jsxs86("div", { className: "relative", children: [
      /* @__PURE__ */ jsxs86("div", { className: "bg-gradient-to-br from-canvas to-canvas border border-tan-warm rounded-sm p-4 sm:p-8 lg:p-10 relative z-10 overflow-hidden", children: [
        /* @__PURE__ */ jsx99("div", { className: "absolute top-2 right-2 sm:top-3 sm:right-3 flex items-center h-5 sm:h-6 px-2 sm:px-2.5 bg-surface border border-tan-warm rounded-sm", children: /* @__PURE__ */ jsx99("span", { className: "text-caption-xs sm:text-label-xs font-sans font-semibold text-primary uppercase tracking-[0.05em]", children: productsOwnership }) }),
        /* @__PURE__ */ jsxs86("div", { className: "mb-3 sm:mb-6 pr-20 sm:pr-28", children: [
          /* @__PURE__ */ jsx99("div", { className: "text-label-xs sm:text-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-1 sm:mb-2", children: productsLayerLabel }),
          /* @__PURE__ */ jsx99("h3", { className: "text-lg sm:text-2xl lg:text-3xl font-sans font-semibold text-primary", children: productsLayerTitle })
        ] }),
        /* @__PURE__ */ jsx99("div", { className: "grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3", children: products.map((product, i) => /* @__PURE__ */ jsx99(
          "div",
          {
            className: "bg-surface px-3 py-2 sm:px-4 sm:py-3 rounded-sm border border-recessed shadow-sm",
            children: /* @__PURE__ */ jsx99("div", { className: "text-xs sm:text-sm font-sans font-medium text-primary", children: product })
          },
          i
        )) })
      ] }),
      /* @__PURE__ */ jsx99("div", { className: "flex justify-center -my-3 relative z-20", children: /* @__PURE__ */ jsxs86("div", { className: "relative w-16 h-12 flex items-center justify-center", children: [
        /* @__PURE__ */ jsx99("div", { className: "absolute left-1/2 top-0 bottom-0 w-0.5 bg-accent -translate-x-1/2" }),
        /* @__PURE__ */ jsx99("div", { className: "absolute left-1/2 top-0 -translate-x-1/2 text-accent", children: /* @__PURE__ */ jsx99("svg", { width: "12", height: "8", viewBox: "0 0 12 8", fill: "none", children: /* @__PURE__ */ jsx99("path", { d: "M6 0L11 8H1L6 0Z", fill: "currentColor" }) }) }),
        /* @__PURE__ */ jsx99("div", { className: "absolute left-1/2 bottom-0 -translate-x-1/2 text-accent", children: /* @__PURE__ */ jsx99("svg", { width: "12", height: "8", viewBox: "0 0 12 8", fill: "none", children: /* @__PURE__ */ jsx99("path", { d: "M6 8L1 0H11L6 8Z", fill: "currentColor" }) }) }),
        /* @__PURE__ */ jsx99("div", { className: "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-surface border-2 border-accent rounded-full" })
      ] }) }),
      /* @__PURE__ */ jsxs86("div", { className: "bg-gradient-to-br from-canvas-warm to-canvas-dark border border-accent rounded-sm p-4 sm:p-8 lg:p-10 relative z-10 shadow-lg overflow-hidden", children: [
        /* @__PURE__ */ jsx99("div", { className: "absolute top-2 right-2 sm:top-3 sm:right-3 flex items-center h-5 sm:h-6 px-2 sm:px-2.5 bg-accent text-white rounded-sm", children: /* @__PURE__ */ jsx99("span", { className: "text-caption-xs sm:text-label-xs font-sans font-semibold uppercase tracking-[0.05em]", children: infrastructureOwnership }) }),
        /* @__PURE__ */ jsxs86("div", { className: "mb-3 sm:mb-6 pr-24 sm:pr-32", children: [
          /* @__PURE__ */ jsx99("div", { className: "text-label-xs sm:text-xs font-sans font-semibold uppercase tracking-[0.15em] text-accent mb-1 sm:mb-2", children: infrastructureLayerLabel }),
          /* @__PURE__ */ jsx99("h3", { className: "text-lg sm:text-2xl lg:text-3xl font-sans font-semibold text-primary", children: infrastructureLayerTitle })
        ] }),
        /* @__PURE__ */ jsx99("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-4 sm:mb-6", children: infrastructureCapabilities.map((item, i) => /* @__PURE__ */ jsxs86(
          "div",
          {
            className: "bg-surface px-3 py-2.5 sm:px-4 sm:py-4 rounded-sm border border-tan-dark shadow-sm",
            children: [
              /* @__PURE__ */ jsx99("div", { className: "text-xs sm:text-sm font-sans font-semibold text-primary mb-0.5 sm:mb-1", children: item.title }),
              /* @__PURE__ */ jsx99("div", { className: "text-caption-sm sm:text-xs font-serif text-secondary leading-relaxed", children: item.description })
            ]
          },
          i
        )) }),
        /* @__PURE__ */ jsxs86("div", { className: "flex flex-wrap items-center justify-center gap-2 sm:gap-4 pt-3 sm:pt-4 border-t border-tan-dark/40", children: [
          /* @__PURE__ */ jsx99("div", { className: "text-caption-xs sm:text-xs font-sans text-tertiary", children: "Integrates with:" }),
          /* @__PURE__ */ jsx99("div", { className: "flex flex-wrap gap-1.5 sm:gap-2", children: integrations.map((int, i) => /* @__PURE__ */ jsx99(
            "div",
            {
              className: "px-2 py-0.5 sm:px-3 sm:py-1 bg-surface/60 rounded-sm text-caption-xs sm:text-xs font-sans text-secondary border border-tan-warm",
              children: int
            },
            i
          )) })
        ] })
      ] }),
      /* @__PURE__ */ jsx99("div", { className: "flex justify-center -my-3 relative z-20", children: /* @__PURE__ */ jsxs86("div", { className: "relative w-16 h-12 flex items-center justify-center", children: [
        /* @__PURE__ */ jsx99("div", { className: "absolute left-1/2 top-0 bottom-0 w-0.5 bg-accent -translate-x-1/2" }),
        /* @__PURE__ */ jsx99("div", { className: "absolute left-1/2 top-0 -translate-x-1/2 text-accent", children: /* @__PURE__ */ jsx99("svg", { width: "12", height: "8", viewBox: "0 0 12 8", fill: "none", children: /* @__PURE__ */ jsx99("path", { d: "M6 0L11 8H1L6 0Z", fill: "currentColor" }) }) }),
        /* @__PURE__ */ jsx99("div", { className: "absolute left-1/2 bottom-0 -translate-x-1/2 text-accent", children: /* @__PURE__ */ jsx99("svg", { width: "12", height: "8", viewBox: "0 0 12 8", fill: "none", children: /* @__PURE__ */ jsx99("path", { d: "M6 8L1 0H11L6 8Z", fill: "currentColor" }) }) }),
        /* @__PURE__ */ jsx99("div", { className: "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-surface border-2 border-accent rounded-full" })
      ] }) }),
      /* @__PURE__ */ jsxs86("div", { className: "bg-gradient-to-br from-canvas to-canvas border border-tan-warm rounded-sm p-4 sm:p-8 lg:p-10 relative z-10 overflow-hidden", children: [
        /* @__PURE__ */ jsx99("div", { className: "absolute top-2 right-2 sm:top-3 sm:right-3 flex items-center h-5 sm:h-6 px-2 sm:px-2.5 bg-surface border border-tan-warm rounded-sm", children: /* @__PURE__ */ jsx99("span", { className: "text-caption-xs sm:text-label-xs font-sans font-semibold text-primary uppercase tracking-[0.05em]", children: coreOwnership }) }),
        /* @__PURE__ */ jsxs86("div", { className: "mb-3 sm:mb-6 pr-20 sm:pr-28", children: [
          /* @__PURE__ */ jsx99("div", { className: "text-label-xs sm:text-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-1 sm:mb-2", children: coreLayerLabel }),
          /* @__PURE__ */ jsx99("h3", { className: "text-lg sm:text-2xl lg:text-3xl font-sans font-semibold text-primary", children: coreLayerTitle })
        ] }),
        /* @__PURE__ */ jsx99("div", { className: "grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3", children: coreSystems.map((system, i) => /* @__PURE__ */ jsx99(
          "div",
          {
            className: "bg-surface px-3 py-2 sm:px-4 sm:py-3 rounded-sm border border-recessed shadow-sm",
            children: /* @__PURE__ */ jsx99("div", { className: "text-xs sm:text-sm font-sans font-medium text-primary", children: system })
          },
          i
        )) })
      ] })
    ] }) }),
    /* @__PURE__ */ jsxs86("div", { className: "border-t-2 border-recessed pt-10 sm:pt-12 lg:pt-16", children: [
      /* @__PURE__ */ jsxs86("div", { className: "text-center mb-8 sm:mb-10", children: [
        /* @__PURE__ */ jsx99("h3", { className: "text-xl sm:text-2xl lg:text-3xl font-serif font-semibold text-primary mb-3 tracking-headline-serif", children: responsibilitiesHeadline }),
        /* @__PURE__ */ jsx99("p", { className: "text-sm sm:text-base font-serif text-secondary max-w-2xl mx-auto leading-relaxed", children: responsibilitiesDescription })
      ] }),
      /* @__PURE__ */ jsxs86("div", { className: "grid lg:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto", children: [
        /* @__PURE__ */ jsxs86("div", { className: "bg-elevated-warm border border-tan-warm rounded-sm p-5 sm:p-8 lg:p-10 shadow-sm hover:shadow-md transition-shadow", children: [
          /* @__PURE__ */ jsxs86("div", { className: "flex items-center gap-3 sm:gap-4 mb-5 sm:mb-8", children: [
            /* @__PURE__ */ jsx99("div", { className: "w-10 h-10 sm:w-14 sm:h-14 bg-canvas-warm rounded-full flex items-center justify-center border border-tan-warm flex-shrink-0", children: /* @__PURE__ */ jsx99(
              "svg",
              {
                className: "w-5 h-5 sm:w-7 sm:h-7 text-primary",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24",
                children: /* @__PURE__ */ jsx99(
                  "path",
                  {
                    strokeLinecap: "round",
                    strokeLinejoin: "round",
                    strokeWidth: 1.5,
                    d: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  }
                )
              }
            ) }),
            /* @__PURE__ */ jsxs86("div", { children: [
              /* @__PURE__ */ jsx99("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-0.5 sm:mb-1", children: "You Own" }),
              /* @__PURE__ */ jsx99("h4", { className: "text-lg sm:text-2xl font-sans font-semibold text-primary", children: yourInstitutionTitle })
            ] })
          ] }),
          /* @__PURE__ */ jsx99("ul", { className: "space-y-2.5 sm:space-y-4", children: yourInstitutionItems.map((item, i) => /* @__PURE__ */ jsxs86("li", { className: "flex items-start gap-2.5 sm:gap-3", children: [
            /* @__PURE__ */ jsx99("div", { className: "w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" }),
            /* @__PURE__ */ jsx99("span", { className: "text-sm sm:text-base font-serif text-secondary leading-relaxed", children: item })
          ] }, i)) })
        ] }),
        /* @__PURE__ */ jsxs86("div", { className: "bg-elevated-warm border border-tan-warm rounded-sm p-5 sm:p-8 lg:p-10 shadow-sm hover:shadow-md transition-shadow", children: [
          /* @__PURE__ */ jsxs86("div", { className: "flex items-center gap-3 sm:gap-4 mb-5 sm:mb-8", children: [
            /* @__PURE__ */ jsx99("div", { className: "w-10 h-10 sm:w-14 sm:h-14 bg-canvas-warm rounded-full flex items-center justify-center border border-tan-warm flex-shrink-0", children: /* @__PURE__ */ jsx99(
              "svg",
              {
                className: "w-5 h-5 sm:w-7 sm:h-7 text-primary",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24",
                children: /* @__PURE__ */ jsx99(
                  "path",
                  {
                    strokeLinecap: "round",
                    strokeLinejoin: "round",
                    strokeWidth: 1.5,
                    d: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  }
                )
              }
            ) }),
            /* @__PURE__ */ jsxs86("div", { children: [
              /* @__PURE__ */ jsx99("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-0.5 sm:mb-1", children: "We Handle" }),
              /* @__PURE__ */ jsx99("h4", { className: "text-lg sm:text-2xl font-sans font-semibold text-primary", children: aaimProvidesTitle })
            ] })
          ] }),
          /* @__PURE__ */ jsx99("ul", { className: "space-y-2.5 sm:space-y-4", children: aaimProvidesItems.map((item, i) => /* @__PURE__ */ jsxs86("li", { className: "flex items-start gap-2.5 sm:gap-3", children: [
            /* @__PURE__ */ jsx99("div", { className: "w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" }),
            /* @__PURE__ */ jsx99("span", { className: "text-sm sm:text-base font-serif text-secondary leading-relaxed", children: item })
          ] }, i)) })
        ] })
      ] })
    ] }),
    bottomQuote && /* @__PURE__ */ jsx99("div", { className: "mt-12 lg:mt-16 flex justify-center", children: /* @__PURE__ */ jsx99("blockquote", { className: "max-w-2xl bg-surface border-l-4 border-tan-dark px-8 py-6 rounded-r-sm shadow-sm", children: /* @__PURE__ */ jsxs86("p", { className: "text-lg font-serif text-secondary italic leading-body", children: [
      '"',
      bottomQuote,
      '"'
    ] }) }) })
  ] }) });
}

// src/sections/IntegrationArchitecture.tsx
import { jsx as jsx100, jsxs as jsxs87 } from "react/jsx-runtime";
function IntegrationArchitecture({
  sectionLabel = "Integration",
  headline = "Seamless integration architecture",
  description = "Connect to your existing systems with minimal friction. Our API-first platform integrates with core banking, loan origination, and custody providers.",
  showLabels = true
}) {
  return /* @__PURE__ */ jsx100("section", { className: "py-16 sm:py-20 lg:py-24", children: /* @__PURE__ */ jsxs87("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: [
    /* @__PURE__ */ jsxs87("div", { className: "text-center mb-12 sm:mb-16", children: [
      /* @__PURE__ */ jsx100("div", { className: "text-label-xs uppercase font-sans font-semibold text-tertiary mb-4 leading-normal", children: sectionLabel }),
      /* @__PURE__ */ jsx100("h2", { className: "text-heading-section sm:text-heading-section-md lg:text-heading-section-lg font-serif font-normal leading-tight tracking-headline-serif mb-4 max-w-content-narrow mx-auto", children: headline }),
      /* @__PURE__ */ jsx100("p", { className: "text-base sm:text-lg font-serif text-secondary max-w-2xl mx-auto", children: description })
    ] }),
    /* @__PURE__ */ jsxs87("div", { className: "bg-canvas rounded-sm p-6 sm:p-10", children: [
      /* @__PURE__ */ jsxs87("div", { className: "grid grid-cols-3 gap-4 sm:gap-6 mb-8", children: [
        /* @__PURE__ */ jsx100(
          SystemBlock,
          {
            name: "Core Banking",
            type: "external",
            description: "FIS, Fiserv, Jack Henry",
            showLabel: showLabels
          }
        ),
        /* @__PURE__ */ jsx100(
          SystemBlock,
          {
            name: "LOS/LMS",
            type: "external",
            description: "Loan origination systems",
            showLabel: showLabels
          }
        ),
        /* @__PURE__ */ jsx100(
          SystemBlock,
          {
            name: "Member Portal",
            type: "external",
            description: "Digital banking",
            showLabel: showLabels
          }
        )
      ] }),
      /* @__PURE__ */ jsx100("div", { className: "flex justify-center mb-8", children: /* @__PURE__ */ jsxs87("div", { className: "flex items-center gap-4", children: [
        /* @__PURE__ */ jsx100(ArrowDown, {}),
        /* @__PURE__ */ jsx100("span", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary", children: "REST API / Webhooks" }),
        /* @__PURE__ */ jsx100(ArrowUp, {})
      ] }) }),
      /* @__PURE__ */ jsxs87("div", { className: "bg-surface border-2 border-accent rounded-sm p-6 sm:p-8 mb-8", children: [
        /* @__PURE__ */ jsxs87("div", { className: "text-center mb-6", children: [
          /* @__PURE__ */ jsx100("div", { className: "text-label-xs font-sans font-semibold uppercase text-accent mb-2", children: "Aaim Platform" }),
          /* @__PURE__ */ jsx100("div", { className: "text-xl sm:text-2xl font-serif font-semibold text-primary tracking-headline-serif", children: "Asset Intelligence Layer" })
        ] }),
        /* @__PURE__ */ jsxs87("div", { className: "grid grid-cols-2 sm:grid-cols-4 gap-4", children: [
          /* @__PURE__ */ jsx100(ServiceBlock, { name: "Valuation Engine" }),
          /* @__PURE__ */ jsx100(ServiceBlock, { name: "Risk Assessment" }),
          /* @__PURE__ */ jsx100(ServiceBlock, { name: "Collateral Mgmt" }),
          /* @__PURE__ */ jsx100(ServiceBlock, { name: "Compliance" })
        ] })
      ] }),
      /* @__PURE__ */ jsx100("div", { className: "flex justify-center mb-8", children: /* @__PURE__ */ jsxs87("div", { className: "flex items-center gap-4", children: [
        /* @__PURE__ */ jsx100(ArrowDown, {}),
        /* @__PURE__ */ jsx100("span", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary", children: "Secure Connections" }),
        /* @__PURE__ */ jsx100(ArrowUp, {})
      ] }) }),
      /* @__PURE__ */ jsxs87("div", { className: "grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6", children: [
        /* @__PURE__ */ jsx100(PartnerBlock, { name: "Custodians", examples: "Coinbase, Anchorage" }),
        /* @__PURE__ */ jsx100(PartnerBlock, { name: "Data Providers", examples: "Plaid, Quiltt" }),
        /* @__PURE__ */ jsx100(PartnerBlock, { name: "Registries", examples: "UCC, SEC" }),
        /* @__PURE__ */ jsx100(PartnerBlock, { name: "Valuations", examples: "Pricing feeds" })
      ] })
    ] }),
    /* @__PURE__ */ jsxs87("div", { className: "mt-6 flex flex-wrap justify-center gap-6 sm:gap-8", children: [
      /* @__PURE__ */ jsxs87("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx100("div", { className: "w-4 h-4 bg-surface border border-tertiary rounded-sm" }),
        /* @__PURE__ */ jsx100("span", { className: "text-xs font-sans text-tertiary", children: "Your Systems" })
      ] }),
      /* @__PURE__ */ jsxs87("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx100("div", { className: "w-4 h-4 bg-surface border-2 border-accent rounded-sm" }),
        /* @__PURE__ */ jsx100("span", { className: "text-xs font-sans text-tertiary", children: "Aaim Platform" })
      ] }),
      /* @__PURE__ */ jsxs87("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx100("div", { className: "w-4 h-4 bg-canvas border border-tertiary rounded-sm" }),
        /* @__PURE__ */ jsx100("span", { className: "text-xs font-sans text-tertiary", children: "Partners" })
      ] })
    ] })
  ] }) });
}
function SystemBlock({
  name,
  description,
  showLabel
}) {
  return /* @__PURE__ */ jsxs87("div", { className: "bg-surface border border-tertiary rounded-sm p-4 text-center", children: [
    /* @__PURE__ */ jsx100("div", { className: "text-sm sm:text-base font-sans font-semibold text-primary mb-1", children: name }),
    description && showLabel && /* @__PURE__ */ jsx100("div", { className: "text-caption-xs font-serif text-tertiary", children: description })
  ] });
}
function ServiceBlock({ name }) {
  return /* @__PURE__ */ jsx100("div", { className: "bg-accent/5 rounded-sm p-3 text-center", children: /* @__PURE__ */ jsx100("div", { className: "text-xs sm:text-sm font-sans font-medium text-primary", children: name }) });
}
function PartnerBlock({ name, examples }) {
  return /* @__PURE__ */ jsxs87("div", { className: "bg-canvas border border-tertiary/50 rounded-sm p-3 text-center", children: [
    /* @__PURE__ */ jsx100("div", { className: "text-xs sm:text-sm font-sans font-medium text-secondary", children: name }),
    examples && /* @__PURE__ */ jsx100("div", { className: "text-caption-xs font-serif text-tertiary mt-1", children: examples })
  ] });
}
function ArrowDown() {
  return /* @__PURE__ */ jsx100(
    "svg",
    {
      width: "24",
      height: "24",
      viewBox: "0 0 24 24",
      fill: "none",
      className: "text-tertiary",
      children: /* @__PURE__ */ jsx100(
        "path",
        {
          d: "M12 4v16m0 0l-4-4m4 4l4-4",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
}
function ArrowUp() {
  return /* @__PURE__ */ jsx100(
    "svg",
    {
      width: "24",
      height: "24",
      viewBox: "0 0 24 24",
      fill: "none",
      className: "text-tertiary",
      children: /* @__PURE__ */ jsx100(
        "path",
        {
          d: "M12 20V4m0 0l-4 4m4-4l4 4",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
}

// src/sections/ManifestoSection.tsx
import { jsx as jsx101, jsxs as jsxs88 } from "react/jsx-runtime";
function ManifestoSection({ sectionLabel, sentences, revealProgress }) {
  const visibleCount = Math.ceil(revealProgress * sentences.length);
  return /* @__PURE__ */ jsxs88("div", { className: "h-screen flex items-center justify-center bg-canvas", children: [
    /* @__PURE__ */ jsx101(
      "div",
      {
        className: "absolute left-0 top-1/2 w-1 bg-gradient-to-b from-transparent via-accent to-transparent transition-all duration-700",
        style: {
          opacity: revealProgress > 0.05 ? 0.7 : 0,
          height: `${Math.min(revealProgress * 400, 200)}px`,
          transform: "translateY(-50%)"
        }
      }
    ),
    /* @__PURE__ */ jsx101(
      "div",
      {
        className: "absolute right-0 top-1/2 w-1 bg-gradient-to-b from-transparent via-accent to-transparent transition-all duration-700",
        style: {
          opacity: revealProgress > 0.05 ? 0.7 : 0,
          height: `${Math.min(revealProgress * 400, 200)}px`,
          transform: "translateY(-50%)"
        }
      }
    ),
    /* @__PURE__ */ jsxs88("div", { className: "relative max-w-content-medium mx-auto px-8 sm:px-12 lg:px-16", children: [
      sectionLabel && /* @__PURE__ */ jsx101(
        "div",
        {
          className: "text-center mb-6 transition-all duration-700",
          style: {
            opacity: revealProgress > 0 ? 1 : 0.3,
            transform: `translateY(${revealProgress > 0 ? 0 : -20}px)`
          },
          children: /* @__PURE__ */ jsx101("span", { className: "text-label-xs uppercase font-sans font-semibold text-tertiary", children: sectionLabel })
        }
      ),
      /* @__PURE__ */ jsx101("p", { className: "text-body-lg sm:text-heading-card lg:text-heading-card-lg font-serif font-normal tracking-headline-sans text-justify", children: sentences.map((sentence, index) => {
        const isVisible = index < visibleCount;
        const isAccent = sentence.color === "accent";
        return /* @__PURE__ */ jsxs88(
          "span",
          {
            className: `inline transition-all duration-500 ${isAccent ? "text-accent" : "text-primary"}`,
            style: {
              opacity: isVisible ? 1 : 0.06,
              filter: isVisible ? "none" : "blur(2px)",
              transitionDelay: `${index % 3 * 40}ms`
            },
            children: [
              sentence.text,
              index < sentences.length - 1 && " "
            ]
          },
          index
        );
      }) }),
      /* @__PURE__ */ jsxs88("div", { className: "mt-10 flex justify-center h-12 relative", children: [
        /* @__PURE__ */ jsx101(
          "div",
          {
            className: "absolute inset-0 flex justify-center items-center transition-opacity duration-500",
            style: { opacity: revealProgress < 0.95 ? 1 : 0 },
            children: /* @__PURE__ */ jsxs88("div", { className: "flex flex-col items-center gap-2", children: [
              /* @__PURE__ */ jsx101("div", { className: "w-16 h-[2px] bg-warm-200 rounded-full overflow-hidden", children: /* @__PURE__ */ jsx101(
                "div",
                {
                  className: "h-full bg-accent rounded-full transition-all duration-100",
                  style: { width: `${revealProgress * 100}%` }
                }
              ) }),
              /* @__PURE__ */ jsx101("span", { className: "text-code-sm font-sans text-tertiary/50 uppercase tracking-widest", children: "Scroll" })
            ] })
          }
        ),
        /* @__PURE__ */ jsx101(
          "div",
          {
            className: "absolute inset-0 flex justify-center items-center transition-opacity duration-500",
            style: { opacity: revealProgress >= 0.95 ? 1 : 0 },
            children: /* @__PURE__ */ jsxs88(
              "svg",
              {
                width: "24",
                height: "24",
                viewBox: "0 0 24 24",
                fill: "none",
                stroke: "currentColor",
                strokeWidth: "2",
                strokeLinecap: "round",
                strokeLinejoin: "round",
                className: "text-accent animate-bounce",
                children: [
                  /* @__PURE__ */ jsx101("path", { d: "M12 5v14" }),
                  /* @__PURE__ */ jsx101("path", { d: "m19 12-7 7-7-7" })
                ]
              }
            )
          }
        )
      ] })
    ] })
  ] });
}

// src/sections/CTASection.tsx
import { ArrowRightIcon as ArrowRightIcon3 } from "lucide-react";

// src/utils/MarkdocContent.tsx
import React32 from "react";
import { Fragment as Fragment22, jsx as jsx102 } from "react/jsx-runtime";
function renderNode(node, key) {
  if (node === null) {
    return null;
  }
  if (typeof node === "string") {
    return node;
  }
  if (Array.isArray(node)) {
    return node.map((child, i) => renderNode(child, i));
  }
  if (node.$$mdtype === "Tag") {
    const { name, attributes, children } = node;
    const renderedChildren = children.map((child, i) => renderNode(child, i));
    return React32.createElement(
      name,
      { ...attributes, key },
      renderedChildren.length > 0 ? renderedChildren : void 0
    );
  }
  return null;
}
function MarkdocContent({ content }) {
  return /* @__PURE__ */ jsx102(Fragment22, { children: renderNode(content) });
}

// src/utils/PullQuote.tsx
import { Fragment as Fragment23, jsx as jsx103, jsxs as jsxs89 } from "react/jsx-runtime";
function PullQuote({
  quote,
  attribution,
  variant = "editorial",
  accentStyle = "top-corner"
}) {
  const variantBgStyles = {
    editorial: "bg-elevated-warm",
    informational: "bg-surface border border-recessed",
    emphasis: "bg-warning/10"
  };
  const accentColors = {
    editorial: "bg-tan-dark dark:bg-secondary",
    informational: "bg-tertiary dark:bg-tertiary",
    emphasis: "bg-warning dark:bg-warning-bright"
  };
  const leftBorderStyles = {
    editorial: "border-l-4 border-tan-dark dark:border-secondary",
    informational: "border-l-4 border-tertiary",
    emphasis: "border-l-4 border-warning dark:border-warning-bright"
  };
  const needsPositioning = ["top-corner", "top", "top-warm"].includes(accentStyle);
  const baseStyles = `p-6 my-8 rounded-sm ${needsPositioning ? "overflow-hidden relative" : ""}`;
  let bgStyles = variantBgStyles[variant];
  if (accentStyle === "tint") {
    bgStyles = variant === "emphasis" ? "bg-warning/10 border border-warning/20" : "bg-elevated-warm border border-tan-warm";
  }
  const borderStyles = accentStyle === "left" ? leftBorderStyles[variant] : "";
  const combinedStyles = `${baseStyles} ${bgStyles} ${borderStyles}`;
  const renderAccentElements = () => {
    const color = accentColors[variant];
    switch (accentStyle) {
      case "top":
        return /* @__PURE__ */ jsx103("div", { className: `absolute top-0 left-0 right-0 h-1 ${color}` });
      case "top-warm":
        return /* @__PURE__ */ jsx103("div", { className: `absolute top-0 left-0 right-0 h-[1px] ${color}` });
      case "top-corner":
        return /* @__PURE__ */ jsxs89(Fragment23, { children: [
          /* @__PURE__ */ jsx103("div", { className: `absolute top-0 left-0 right-0 h-[2px] ${color}` }),
          /* @__PURE__ */ jsx103("div", { className: `absolute top-[2px] left-0 w-[2px] h-4 ${color}` })
        ] });
      default:
        return null;
    }
  };
  return /* @__PURE__ */ jsxs89("blockquote", { className: combinedStyles, children: [
    renderAccentElements(),
    /* @__PURE__ */ jsxs89("p", { className: "text-base font-serif text-secondary leading-body italic mb-3", children: [
      "\u201C",
      quote,
      "\u201D"
    ] }),
    attribution && /* @__PURE__ */ jsxs89("footer", { className: "text-sm font-sans text-tertiary", children: [
      /* @__PURE__ */ jsx103("span", { className: "inline-block w-4 h-px bg-recessed mr-2 align-middle" }),
      attribution
    ] })
  ] });
}

// src/sections/CTASection.tsx
import { jsx as jsx104, jsxs as jsxs90 } from "react/jsx-runtime";
function CTASection({
  headline,
  subheadline,
  description,
  primaryText,
  primaryCta,
  onPrimaryClick,
  primaryHref,
  primaryCtaHref,
  secondaryText,
  secondaryCta,
  onSecondaryClick,
  secondaryHref,
  secondaryCtaHref,
  showArrow = true,
  background = "canvas",
  className = ""
}) {
  const headlineText = headline;
  const descriptionText = subheadline || description;
  const primaryButtonText = primaryCta || primaryText;
  const primaryButtonHref = primaryCtaHref || primaryHref;
  const secondaryButtonText = secondaryCta || secondaryText;
  const secondaryButtonHref = secondaryCtaHref || secondaryHref;
  const bgClass = background === "canvas" ? "bg-canvas" : "bg-surface";
  const PrimaryCTA = primaryButtonHref ? /* @__PURE__ */ jsxs90(
    "a",
    {
      href: primaryButtonHref,
      onClick: onPrimaryClick,
      className: "bg-accent text-white py-3 sm:py-4 px-6 sm:px-8 rounded-sm font-sans font-semibold text-sm sm:text-base hover:bg-accent-hover hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 leading-normal",
      children: [
        primaryButtonText,
        showArrow && /* @__PURE__ */ jsx104(ArrowRightIcon3, { className: "w-4 sm:w-5 h-4 sm:h-5" })
      ]
    }
  ) : /* @__PURE__ */ jsxs90(
    "button",
    {
      onClick: onPrimaryClick,
      className: "bg-accent text-white py-3 sm:py-4 px-6 sm:px-8 rounded-sm font-sans font-semibold text-sm sm:text-base hover:bg-accent-hover hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 leading-normal",
      children: [
        primaryButtonText,
        showArrow && /* @__PURE__ */ jsx104(ArrowRightIcon3, { className: "w-4 sm:w-5 h-4 sm:h-5" })
      ]
    }
  );
  const SecondaryCTA = secondaryButtonHref ? /* @__PURE__ */ jsx104(
    "a",
    {
      href: secondaryButtonHref,
      onClick: onSecondaryClick,
      className: "bg-surface text-accent py-3 sm:py-4 px-6 sm:px-8 rounded-sm font-sans font-semibold text-sm sm:text-base border-2 border-accent hover:bg-elevated transition-all duration-200 leading-normal text-center",
      children: secondaryButtonText
    }
  ) : /* @__PURE__ */ jsx104(
    "button",
    {
      onClick: onSecondaryClick,
      className: "bg-surface text-accent py-3 sm:py-4 px-6 sm:px-8 rounded-sm font-sans font-semibold text-sm sm:text-base border-2 border-accent hover:bg-elevated transition-all duration-200 leading-normal",
      children: secondaryButtonText
    }
  );
  return /* @__PURE__ */ jsx104("section", { className: `${bgClass} py-16 sm:py-20 lg:py-24 ${className}`, children: /* @__PURE__ */ jsxs90("div", { className: "max-w-content-narrow mx-auto px-4 sm:px-6 lg:px-8 text-center", children: [
    headlineText && /* @__PURE__ */ jsx104(
      HeadlineWithEmphasis,
      {
        text: headlineText,
        as: "h2",
        className: "text-heading-section sm:text-heading-section-md lg:text-heading-section-lg font-serif font-normal tracking-headline-serif mb-4 sm:mb-6 leading-snug"
      }
    ),
    descriptionText && /* @__PURE__ */ jsx104(
      FormattedText,
      {
        text: descriptionText,
        as: "p",
        className: "text-base sm:text-lg lg:text-xl font-serif text-secondary mb-6 sm:mb-8 leading-body"
      }
    ),
    /* @__PURE__ */ jsxs90("div", { className: "flex flex-col sm:flex-row justify-center gap-3 sm:gap-4", children: [
      primaryButtonText && PrimaryCTA,
      secondaryButtonText && SecondaryCTA
    ] })
  ] }) });
}

// src/sections/DeveloperDocumentation.tsx
import { jsx as jsx105, jsxs as jsxs91 } from "react/jsx-runtime";
function DeveloperDocumentation() {
  return /* @__PURE__ */ jsx105("section", { className: "bg-canvas py-16 sm:py-20 lg:py-24", children: /* @__PURE__ */ jsxs91("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: [
    /* @__PURE__ */ jsx105("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-8", children: "Use Case: API Reference" }),
    /* @__PURE__ */ jsxs91("div", { className: "grid md:grid-cols-2 gap-12", children: [
      /* @__PURE__ */ jsxs91("div", { children: [
        /* @__PURE__ */ jsx105(Heading, { level: 2, size: "section", className: "mb-10 sm:mb-12", children: "Developer Documentation" }),
        /* @__PURE__ */ jsxs91("div", { className: "bg-surface border border-recessed p-6 sm:p-8 rounded-sm space-y-8", children: [
          /* @__PURE__ */ jsx105("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary mb-6", children: "Endpoint Reference" }),
          /* @__PURE__ */ jsxs91("div", { className: "space-y-6", children: [
            /* @__PURE__ */ jsxs91("div", { children: [
              /* @__PURE__ */ jsx105("div", { className: "font-mono text-sm text-primary mb-3 bg-recessed px-3 py-2", children: "POST /v3/asset/discovery" }),
              /* @__PURE__ */ jsx105("p", { className: "text-sm font-sans text-secondary leading-relaxed", children: "Initiates comprehensive asset discovery across connected accounts. Returns classified holdings with real-time valuations." })
            ] }),
            /* @__PURE__ */ jsxs91("div", { children: [
              /* @__PURE__ */ jsxs91("div", { className: "font-mono text-sm text-primary mb-3 bg-recessed px-3 py-2", children: [
                "GET /v3/valuation/",
                /* @__PURE__ */ jsxs91("span", { className: "text-accent", children: [
                  "{",
                  "asset_id",
                  "}"
                ] })
              ] }),
              /* @__PURE__ */ jsx105("p", { className: "text-sm font-sans text-secondary leading-relaxed", children: "Retrieves current valuation with confidence intervals. Includes liquidity scoring and volatility metrics." })
            ] }),
            /* @__PURE__ */ jsxs91("div", { children: [
              /* @__PURE__ */ jsx105("div", { className: "font-mono text-sm text-primary mb-3 bg-recessed px-3 py-2", children: "PUT /v3/control/agreement" }),
              /* @__PURE__ */ jsx105("p", { className: "text-sm font-sans text-secondary leading-relaxed", children: "Establishes tri-party control agreement for collateral perfection. Returns signed agreement with blockchain attestation." })
            ] })
          ] })
        ] })
      ] }),
      /* @__PURE__ */ jsxs91("div", { children: [
        /* @__PURE__ */ jsx105(Heading, { level: 3, size: "card", className: "mb-6", children: "Response Schema" }),
        /* @__PURE__ */ jsx105("div", { className: "bg-code-dark p-6 rounded-sm overflow-x-auto", children: /* @__PURE__ */ jsx105("pre", { className: "font-mono text-sm text-growth leading-relaxed", children: `{
  "valuation": {
    "asset_id": "btc_1a2b3c",
    "current_value": 525000,
    "confidence": 0.97,
    "liquidity_score": 9.2,
    "volatility_30d": 0.18,
    "methodology": "reference_model_v4",
    "attestation": {
      "blockchain": "ethereum",
      "tx_hash": "0x7d9a...",
      "timestamp": 1702934400
    }
  },
  "control": {
    "perfected": true,
    "priority": 1,
    "agreement_uri": "ipfs://..."
  }
}` }) })
      ] })
    ] })
  ] }) });
}

// src/sections/EmailTemplate.tsx
import { Fragment as Fragment24, jsx as jsx106, jsxs as jsxs92 } from "react/jsx-runtime";
function EmailTemplate({
  documentType,
  subject,
  recipientName,
  bodyText,
  highlightedMessage,
  ctaText,
  ctaUrl,
  signature = "The Aaim Team",
  isTransactional = true,
  unsubscribeUrl
}) {
  return /* @__PURE__ */ jsx106("div", { style: {
    backgroundColor: "#F5F7F9",
    padding: "40px 20px",
    fontFamily: "'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
  }, children: /* @__PURE__ */ jsxs92("div", { style: {
    maxWidth: "600px",
    margin: "0 auto",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)"
  }, children: [
    /* @__PURE__ */ jsxs92("div", { style: {
      padding: "40px 40px 32px",
      borderBottom: "1px solid #E5E7EB"
    }, children: [
      /* @__PURE__ */ jsx106("img", { src: "https://cdn.aaim.app/logos/aaim-master-grey.svg", alt: "Aaim", style: {
        height: "40px",
        display: "block",
        marginBottom: "32px"
      } }),
      documentType && /* @__PURE__ */ jsx106("div", { style: {
        fontSize: "11px",
        fontFamily: "'Lato', -apple-system, sans-serif",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.15em",
        color: "#D2001F",
        marginBottom: "16px"
      }, children: documentType }),
      /* @__PURE__ */ jsx106("h1", { style: {
        fontSize: "28px",
        fontFamily: "'Lato', -apple-system, sans-serif",
        fontWeight: 700,
        color: "#1A1D21",
        lineHeight: "1.3",
        marginBottom: 0,
        marginTop: 0
      }, children: subject })
    ] }),
    /* @__PURE__ */ jsxs92("div", { style: {
      padding: "40px 40px"
    }, children: [
      /* @__PURE__ */ jsxs92("p", { style: {
        fontSize: "16px",
        fontFamily: "'Lato', -apple-system, sans-serif",
        color: "#1A1D21",
        lineHeight: "1.6",
        marginBottom: "20px",
        marginTop: 0
      }, children: [
        "Hi ",
        recipientName,
        ","
      ] }),
      /* @__PURE__ */ jsx106("p", { style: {
        fontSize: "16px",
        fontFamily: "'Lato', -apple-system, sans-serif",
        color: "#4A5568",
        lineHeight: "1.7",
        marginBottom: "28px",
        marginTop: 0
      }, children: bodyText }),
      highlightedMessage && /* @__PURE__ */ jsx106("div", { style: {
        backgroundColor: "#F5F0E8",
        borderLeft: "4px solid #C4B299",
        padding: "20px 24px",
        marginBottom: "32px"
      }, children: /* @__PURE__ */ jsx106("p", { style: {
        fontSize: "15px",
        fontFamily: "'Lato', -apple-system, sans-serif",
        color: "#1A1D21",
        lineHeight: "1.7",
        margin: 0,
        fontWeight: 500
      }, children: highlightedMessage }) }),
      ctaText && ctaUrl && /* @__PURE__ */ jsx106("div", { style: {
        textAlign: "center",
        marginTop: "36px",
        marginBottom: "36px"
      }, children: /* @__PURE__ */ jsx106("a", { href: ctaUrl, style: {
        display: "inline-block",
        backgroundColor: "#D2001F",
        color: "#FFFFFF",
        fontSize: "16px",
        fontFamily: "'Lato', -apple-system, sans-serif",
        fontWeight: 600,
        textDecoration: "none",
        padding: "14px 32px",
        borderRadius: "2px"
      }, children: ctaText }) }),
      /* @__PURE__ */ jsx106("div", { style: {
        paddingTop: "28px",
        borderTop: "1px solid #E5E7EB",
        marginTop: "36px"
      }, children: /* @__PURE__ */ jsx106("p", { style: {
        fontSize: "16px",
        fontFamily: "'Lato', -apple-system, sans-serif",
        fontWeight: 600,
        color: "#1A1D21",
        lineHeight: "1.6",
        margin: 0
      }, children: signature }) })
    ] }),
    /* @__PURE__ */ jsx106("div", { style: {
      backgroundColor: "#FAF8F5",
      borderTop: "1px solid #E5E7EB"
    }, children: isTransactional ? (
      // Transactional email footer (no unsubscribe required)
      /* @__PURE__ */ jsxs92(Fragment24, { children: [
        /* @__PURE__ */ jsx106("div", { style: {
          padding: "24px 40px 20px",
          borderBottom: "1px solid #D4C4B0"
        }, children: /* @__PURE__ */ jsx106("p", { style: {
          fontSize: "13px",
          fontFamily: "'Lato', -apple-system, sans-serif",
          color: "#6B7280",
          lineHeight: "1.6",
          textAlign: "center",
          margin: 0
        }, children: "This is a transactional email related to your account activity." }) }),
        /* @__PURE__ */ jsx106("div", { style: {
          padding: "16px 40px"
        }, children: /* @__PURE__ */ jsx106("p", { style: {
          fontSize: "11px",
          fontFamily: "'Lato', -apple-system, sans-serif",
          color: "#9CA3AF",
          lineHeight: "1.5",
          textAlign: "center",
          margin: 0,
          opacity: 0.75
        }, children: "\xA9 2025 Aaim, Inc. \u2022 99 Wall Street, Suite 1610, New York, NY 10005" }) })
      ] })
    ) : (
      // Marketing email footer (unsubscribe required)
      /* @__PURE__ */ jsxs92(Fragment24, { children: [
        /* @__PURE__ */ jsx106("div", { style: {
          padding: "24px 40px 20px",
          borderBottom: "1px solid #D4C4B0"
        }, children: /* @__PURE__ */ jsx106("p", { style: {
          fontSize: "13px",
          fontFamily: "'Lato', -apple-system, sans-serif",
          color: "#6B7280",
          lineHeight: "1.6",
          textAlign: "center",
          margin: 0
        }, children: "You're receiving this email because you subscribed to Aaim updates." }) }),
        unsubscribeUrl && /* @__PURE__ */ jsx106("div", { style: {
          padding: "16px 40px",
          borderBottom: "1px solid #D4C4B0"
        }, children: /* @__PURE__ */ jsxs92("p", { style: {
          fontSize: "13px",
          fontFamily: "'Lato', -apple-system, sans-serif",
          color: "#6B7280",
          lineHeight: "1.6",
          textAlign: "center",
          margin: 0
        }, children: [
          /* @__PURE__ */ jsx106("a", { href: unsubscribeUrl, style: {
            color: "#D2001F",
            textDecoration: "none",
            fontWeight: 500
          }, children: "Unsubscribe" }),
          /* @__PURE__ */ jsx106("span", { style: {
            margin: "0 8px",
            color: "#D1D5DB"
          }, children: "|" }),
          /* @__PURE__ */ jsx106("a", { href: "https://app.aaim.com/preferences", style: {
            color: "#D2001F",
            textDecoration: "none",
            fontWeight: 500
          }, children: "Email Preferences" })
        ] }) }),
        /* @__PURE__ */ jsx106("div", { style: {
          padding: "16px 40px"
        }, children: /* @__PURE__ */ jsx106("p", { style: {
          fontSize: "11px",
          fontFamily: "'Lato', -apple-system, sans-serif",
          color: "#9CA3AF",
          lineHeight: "1.5",
          textAlign: "center",
          margin: 0,
          opacity: 0.75
        }, children: "\xA9 2025 Aaim, Inc. \u2022 99 Wall Street, Suite 1610, New York, NY 10005" }) })
      ] })
    ) })
  ] }) });
}
function EmailTemplateCompact({
  documentType,
  subject,
  recipientName,
  bodyText,
  ctaText,
  ctaUrl,
  signature = "The Aaim Team",
  isTransactional = true,
  unsubscribeUrl
}) {
  return /* @__PURE__ */ jsx106("div", { style: {
    backgroundColor: "#F5F7F9",
    padding: "40px 20px",
    fontFamily: "'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
  }, children: /* @__PURE__ */ jsxs92("div", { style: {
    maxWidth: "600px",
    margin: "0 auto",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)"
  }, children: [
    /* @__PURE__ */ jsxs92("div", { style: {
      padding: "32px 32px 24px",
      borderBottom: "1px solid #E5E7EB"
    }, children: [
      /* @__PURE__ */ jsx106("img", { src: "https://cdn.aaim.app/logos/aaim-master-grey.svg", alt: "Aaim", style: {
        height: "36px",
        display: "block",
        marginBottom: "24px"
      } }),
      documentType && /* @__PURE__ */ jsx106("div", { style: {
        fontSize: "10px",
        fontFamily: "'Lato', -apple-system, sans-serif",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.15em",
        color: "#D2001F",
        marginBottom: "12px"
      }, children: documentType }),
      /* @__PURE__ */ jsx106("h2", { style: {
        fontSize: "22px",
        fontFamily: "'Lato', -apple-system, sans-serif",
        fontWeight: 700,
        color: "#1A1D21",
        lineHeight: "1.3",
        marginBottom: 0,
        marginTop: 0
      }, children: subject })
    ] }),
    /* @__PURE__ */ jsxs92("div", { style: {
      padding: "32px"
    }, children: [
      /* @__PURE__ */ jsxs92("p", { style: {
        fontSize: "15px",
        fontFamily: "'Lato', -apple-system, sans-serif",
        color: "#1A1D21",
        lineHeight: "1.6",
        marginBottom: "16px",
        marginTop: 0
      }, children: [
        "Hi ",
        recipientName,
        ","
      ] }),
      /* @__PURE__ */ jsx106("p", { style: {
        fontSize: "15px",
        fontFamily: "'Lato', -apple-system, sans-serif",
        color: "#4A5568",
        lineHeight: "1.7",
        marginBottom: "24px",
        marginTop: 0
      }, children: bodyText }),
      ctaText && ctaUrl && /* @__PURE__ */ jsx106("div", { style: {
        marginTop: "28px",
        marginBottom: "28px"
      }, children: /* @__PURE__ */ jsx106("a", { href: ctaUrl, style: {
        display: "inline-block",
        backgroundColor: "#D2001F",
        color: "#FFFFFF",
        fontSize: "15px",
        fontFamily: "'Lato', -apple-system, sans-serif",
        fontWeight: 600,
        textDecoration: "none",
        padding: "12px 28px",
        borderRadius: "2px"
      }, children: ctaText }) }),
      /* @__PURE__ */ jsx106("p", { style: {
        fontSize: "15px",
        fontFamily: "'Lato', -apple-system, sans-serif",
        fontWeight: 600,
        color: "#1A1D21",
        lineHeight: "1.6",
        marginTop: "24px",
        marginBottom: 0,
        paddingTop: "24px",
        borderTop: "1px solid #E5E7EB"
      }, children: signature })
    ] }),
    /* @__PURE__ */ jsx106("div", { style: {
      backgroundColor: "#FAF8F5",
      borderTop: "1px solid #E5E7EB"
    }, children: isTransactional ? /* @__PURE__ */ jsxs92(Fragment24, { children: [
      /* @__PURE__ */ jsx106("div", { style: {
        padding: "20px 32px 16px",
        borderBottom: "1px solid #D4C4B0"
      }, children: /* @__PURE__ */ jsx106("p", { style: {
        fontSize: "12px",
        fontFamily: "'Lato', -apple-system, sans-serif",
        color: "#6B7280",
        lineHeight: "1.5",
        textAlign: "center",
        margin: 0
      }, children: "Transactional email related to your account." }) }),
      /* @__PURE__ */ jsx106("div", { style: {
        padding: "12px 32px"
      }, children: /* @__PURE__ */ jsx106("p", { style: {
        fontSize: "11px",
        fontFamily: "'Lato', -apple-system, sans-serif",
        color: "#9CA3AF",
        lineHeight: "1.4",
        textAlign: "center",
        margin: 0,
        opacity: 0.75
      }, children: "\xA9 2025 Aaim, Inc. All rights reserved." }) })
    ] }) : /* @__PURE__ */ jsxs92(Fragment24, { children: [
      unsubscribeUrl && /* @__PURE__ */ jsx106("div", { style: {
        padding: "16px 32px",
        borderBottom: "1px solid #D4C4B0"
      }, children: /* @__PURE__ */ jsx106("p", { style: {
        fontSize: "12px",
        fontFamily: "'Lato', -apple-system, sans-serif",
        color: "#6B7280",
        lineHeight: "1.5",
        textAlign: "center",
        margin: 0
      }, children: /* @__PURE__ */ jsx106("a", { href: unsubscribeUrl, style: {
        color: "#D2001F",
        textDecoration: "none",
        fontWeight: 500
      }, children: "Unsubscribe" }) }) }),
      /* @__PURE__ */ jsx106("div", { style: {
        padding: "12px 32px"
      }, children: /* @__PURE__ */ jsx106("p", { style: {
        fontSize: "11px",
        fontFamily: "'Lato', -apple-system, sans-serif",
        color: "#9CA3AF",
        lineHeight: "1.4",
        textAlign: "center",
        margin: 0,
        opacity: 0.75
      }, children: "\xA9 2025 Aaim, Inc. All rights reserved." }) })
    ] }) })
  ] }) });
}

// src/sections/Testimonials.tsx
import { LockIcon as LockIcon4 } from "lucide-react";
import { jsx as jsx107, jsxs as jsxs93 } from "react/jsx-runtime";
var defaultCertifications = [
  { name: "FFIEC Aligned", label: "FFIEC ALIGNED" },
  { name: "GLBA Compliant", label: "GLBA COMPLIANT" },
  { name: "BSA/AML Ready", label: "BSA/AML READY" }
];
function getInitials(name) {
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}
function Testimonials({
  headline = "Trusted by leading financial institutions",
  certifications = defaultCertifications,
  testimonials,
  ImageComponent,
  className = ""
}) {
  const hasTestimonials = testimonials && testimonials.length > 0;
  return /* @__PURE__ */ jsx107("section", { className: `bg-canvas py-16 sm:py-20 lg:py-24 ${className}`, children: /* @__PURE__ */ jsxs93("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: [
    /* @__PURE__ */ jsx107("div", { className: "text-center mb-8 sm:mb-12", children: /* @__PURE__ */ jsx107(
      HeadlineWithEmphasis,
      {
        text: headline,
        as: "h2",
        className: "text-heading-section sm:text-heading-section-md lg:text-heading-section-lg leading-snug"
      }
    ) }),
    hasTestimonials && /* @__PURE__ */ jsx107("div", { className: "grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12", children: testimonials.map((testimonial, index) => /* @__PURE__ */ jsxs93(
      "div",
      {
        className: "bg-surface border border-recessed p-6 sm:p-8 rounded-sm shadow-sm hover:shadow-md transition-shadow",
        children: [
          /* @__PURE__ */ jsxs93("p", { className: "text-base sm:text-lg font-serif italic text-secondary mb-4 sm:mb-6 leading-body", children: [
            "\u201C",
            testimonial.quote,
            "\u201D"
          ] }),
          /* @__PURE__ */ jsxs93("div", { className: "flex items-center gap-3", children: [
            testimonial.photo ? ImageComponent ? /* @__PURE__ */ jsx107(
              ImageComponent,
              {
                src: testimonial.photo,
                alt: testimonial.author,
                width: 48,
                height: 48,
                className: "w-12 h-12 rounded-full object-cover flex-shrink-0"
              }
            ) : /* @__PURE__ */ jsx107(
              "img",
              {
                src: testimonial.photo,
                alt: testimonial.author,
                width: 48,
                height: 48,
                className: "w-12 h-12 rounded-full object-cover flex-shrink-0"
              }
            ) : /* @__PURE__ */ jsx107("div", { className: "w-12 h-12 bg-recessed rounded-full flex-shrink-0 flex items-center justify-center", children: /* @__PURE__ */ jsx107("span", { className: "text-sm font-sans font-semibold text-tertiary", children: getInitials(testimonial.author) }) }),
            /* @__PURE__ */ jsxs93("div", { children: [
              /* @__PURE__ */ jsx107("div", { className: "text-sm font-sans font-semibold text-primary", children: testimonial.author }),
              (testimonial.role || testimonial.company) && /* @__PURE__ */ jsxs93("div", { className: "text-xs font-sans text-tertiary", children: [
                testimonial.role,
                testimonial.role && testimonial.company && ", ",
                testimonial.company
              ] })
            ] })
          ] })
        ]
      },
      index
    )) }),
    /* @__PURE__ */ jsx107("div", { className: "flex flex-wrap justify-center items-center gap-6 sm:gap-8 lg:gap-12", children: certifications.map((cert, index) => /* @__PURE__ */ jsxs93("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx107(LockIcon4, { className: "w-4 h-4 text-tertiary", strokeWidth: 2 }),
      /* @__PURE__ */ jsx107("span", { className: "text-label-xs sm:text-label-sm font-sans font-normal uppercase text-tertiary", children: cert.label })
    ] }, index)) })
  ] }) });
}

// src/sections/ProcessTimeline.tsx
import { clsx as clsx7 } from "clsx";
import { jsx as jsx108, jsxs as jsxs94 } from "react/jsx-runtime";
function ProcessTimeline({
  categoryTag,
  headline,
  lead,
  steps,
  variant = "vertical",
  showConnector = true,
  headless = false,
  className
}) {
  if (variant === "horizontal") {
    return /* @__PURE__ */ jsx108("div", { className: clsx7(headless ? "" : "py-16 sm:py-20 lg:py-24", className), children: /* @__PURE__ */ jsxs94("div", { className: headless ? "" : "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: [
      !headless && /* @__PURE__ */ jsxs94("div", { className: "max-w-content-narrow mb-12 lg:mb-16", children: [
        categoryTag && /* @__PURE__ */ jsx108("div", { className: "text-label-xs font-sans font-semibold uppercase text-accent mb-4", children: categoryTag }),
        headline && /* @__PURE__ */ jsx108("h2", { className: "text-heading-section sm:text-heading-section-md lg:text-heading-section-lg font-serif font-semibold text-primary mb-4 tracking-headline-serif", children: headline }),
        lead && /* @__PURE__ */ jsx108("p", { className: "text-lead sm:text-lead-md font-serif text-secondary leading-relaxed", children: lead })
      ] }),
      /* @__PURE__ */ jsx108("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12", children: steps.map((step, index) => {
        const stepNum = step.stepNumber ?? index + 1;
        return /* @__PURE__ */ jsxs94(
          "div",
          {
            className: "relative",
            children: [
              /* @__PURE__ */ jsx108("div", { className: "text-label-md font-sans font-semibold uppercase text-tertiary mb-4", children: String(stepNum) }),
              /* @__PURE__ */ jsx108("h3", { className: "text-heading-card sm:text-heading-card-md font-sans font-semibold text-primary mb-3", children: step.title }),
              /* @__PURE__ */ jsx108("p", { className: "text-body-md font-serif text-secondary leading-body", children: step.description }),
              step.detail && /* @__PURE__ */ jsx108("p", { className: "text-caption-md font-sans text-tertiary mt-3", children: step.detail }),
              step.highlightLabel && /* @__PURE__ */ jsx108("div", { className: "mt-6 pt-4 border-t border-recessed/40", children: /* @__PURE__ */ jsx108("span", { className: "text-label-xs font-sans font-semibold text-accent uppercase", children: step.highlightLabel }) })
            ]
          },
          index
        );
      }) })
    ] }) });
  }
  if (variant === "staggered") {
    return /* @__PURE__ */ jsx108("section", { className: clsx7("py-16 sm:py-20 lg:py-24", className), children: /* @__PURE__ */ jsxs94("div", { className: "max-w-5xl mx-auto px-4 sm:px-6 lg:px-8", children: [
      /* @__PURE__ */ jsxs94("div", { className: "max-w-content-narrow mb-16 lg:mb-20", children: [
        categoryTag && /* @__PURE__ */ jsx108("div", { className: "text-label-xs font-sans font-semibold uppercase text-accent mb-4", children: categoryTag }),
        /* @__PURE__ */ jsx108("h2", { className: "text-heading-section sm:text-heading-section-md lg:text-heading-section-lg font-serif font-semibold text-primary mb-4 tracking-headline-serif", children: headline }),
        lead && /* @__PURE__ */ jsx108("p", { className: "text-lead sm:text-lead-md font-serif text-secondary leading-relaxed", children: lead })
      ] }),
      /* @__PURE__ */ jsx108("div", { className: "space-y-12 lg:space-y-16", children: steps.map((step, index) => /* @__PURE__ */ jsxs94(
        "div",
        {
          className: "grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8",
          children: [
            /* @__PURE__ */ jsx108("div", { className: "lg:col-span-2", children: /* @__PURE__ */ jsx108("span", { className: "text-stat font-serif font-normal text-recessed leading-none", children: String(step.stepNumber ?? index + 1) }) }),
            /* @__PURE__ */ jsxs94("div", { className: "lg:col-span-10", children: [
              /* @__PURE__ */ jsx108("h3", { className: "text-heading-card sm:text-heading-card-md font-sans font-semibold text-primary mb-2", children: step.title }),
              /* @__PURE__ */ jsx108("p", { className: "text-body-md font-serif text-secondary leading-body max-w-2xl", children: step.description }),
              step.detail && /* @__PURE__ */ jsx108("p", { className: "text-caption-sm font-sans text-tertiary mt-3", children: step.detail })
            ] })
          ]
        },
        index
      )) })
    ] }) });
  }
  return /* @__PURE__ */ jsx108("section", { className: clsx7("py-16 sm:py-20 lg:py-24", className), children: /* @__PURE__ */ jsxs94("div", { className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8", children: [
    /* @__PURE__ */ jsxs94("div", { className: "mb-12 lg:mb-16", children: [
      categoryTag && /* @__PURE__ */ jsx108("div", { className: "text-label-xs font-sans font-semibold uppercase text-accent mb-4", children: categoryTag }),
      /* @__PURE__ */ jsx108("h2", { className: "text-heading-section sm:text-heading-section-md lg:text-heading-section-lg font-serif font-semibold text-primary mb-4 tracking-headline-serif", children: headline }),
      lead && /* @__PURE__ */ jsx108("p", { className: "text-lead sm:text-lead-md font-serif text-secondary leading-relaxed max-w-2xl", children: lead })
    ] }),
    /* @__PURE__ */ jsxs94("div", { className: "relative", children: [
      showConnector && /* @__PURE__ */ jsx108("div", { className: "absolute left-6 sm:left-8 top-0 bottom-0 w-px bg-gradient-to-b from-recessed/60 via-recessed/40 to-transparent" }),
      /* @__PURE__ */ jsx108("div", { className: "space-y-10 lg:space-y-12", children: steps.map((step, index) => /* @__PURE__ */ jsxs94("div", { className: "relative flex gap-6 sm:gap-8", children: [
        /* @__PURE__ */ jsx108("div", { className: "relative z-10 flex-shrink-0", children: /* @__PURE__ */ jsx108("div", { className: "w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-canvas border border-recessed flex items-center justify-center", children: /* @__PURE__ */ jsx108("span", { className: "text-heading-card sm:text-heading-card-md font-serif font-normal text-primary tracking-headline-serif", children: String(step.stepNumber ?? index + 1) }) }) }),
        /* @__PURE__ */ jsxs94("div", { className: "flex-1 pt-2 sm:pt-3", children: [
          /* @__PURE__ */ jsx108("h3", { className: "text-heading-card sm:text-heading-card-md font-sans font-semibold text-primary mb-2", children: step.title }),
          /* @__PURE__ */ jsx108("p", { className: "text-body-md font-serif text-secondary leading-body", children: step.description }),
          step.detail && /* @__PURE__ */ jsx108("p", { className: "text-caption-md font-sans text-tertiary mt-3 border-l-2 border-recessed/40 pl-4", children: step.detail })
        ] })
      ] }, index)) })
    ] })
  ] }) });
}

// src/sections/StatementBlock.tsx
import { clsx as clsx8 } from "clsx";
import { jsx as jsx109, jsxs as jsxs95 } from "react/jsx-runtime";
function StatementBlock({
  statement,
  attribution,
  context,
  variant = "default",
  align = "left",
  size = "default",
  className
}) {
  const sizeClasses4 = {
    compact: "py-12 sm:py-16 lg:py-20",
    default: "py-16 sm:py-20 lg:py-24",
    large: "py-20 sm:py-24 lg:py-32"
  };
  const statementSizeClasses = {
    compact: "text-heading-subsection sm:text-heading-subsection-md lg:text-heading-subsection-lg",
    default: "text-heading-section sm:text-heading-section-md lg:text-heading-section-lg",
    large: "text-heading-feature sm:text-heading-feature-md lg:text-heading-feature-lg"
  };
  const containerClasses = {
    default: "",
    accent: "border-l-4 border-accent pl-6 sm:pl-8",
    bordered: "border-t border-b border-recessed/40 py-12 sm:py-16 lg:py-20",
    minimal: ""
  };
  return /* @__PURE__ */ jsx109("section", { className: clsx8(sizeClasses4[size], className), children: /* @__PURE__ */ jsx109(
    "div",
    {
      className: clsx8(
        "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8",
        align === "center" && "text-center"
      ),
      children: /* @__PURE__ */ jsxs95("div", { className: containerClasses[variant], children: [
        context && /* @__PURE__ */ jsx109(
          "div",
          {
            className: clsx8(
              "text-label-sm font-sans font-semibold uppercase mb-6",
              variant === "accent" ? "text-accent" : "text-tertiary"
            ),
            children: context
          }
        ),
        /* @__PURE__ */ jsx109(
          "p",
          {
            className: clsx8(
              "font-serif font-semibold text-primary leading-snug tracking-headline-serif",
              statementSizeClasses[size]
            ),
            children: statement
          }
        ),
        attribution && /* @__PURE__ */ jsxs95(
          "div",
          {
            className: clsx8(
              "mt-6 sm:mt-8 text-body-sm font-sans text-tertiary",
              align === "center" && "flex items-center justify-center gap-2"
            ),
            children: [
              /* @__PURE__ */ jsx109("span", { className: "inline-block w-8 h-px bg-recessed/60 mr-3" }),
              attribution
            ]
          }
        )
      ] })
    }
  ) });
}
function DualStatement({
  first,
  second,
  showDivider = true,
  className
}) {
  return /* @__PURE__ */ jsx109("section", { className: clsx8("py-16 sm:py-20 lg:py-24", className), children: /* @__PURE__ */ jsx109("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: /* @__PURE__ */ jsxs95("div", { className: "grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16", children: [
    /* @__PURE__ */ jsxs95("div", { className: "relative", children: [
      first.context && /* @__PURE__ */ jsx109("div", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-4", children: first.context }),
      /* @__PURE__ */ jsx109("p", { className: "text-heading-subsection sm:text-heading-subsection-md font-serif font-normal text-secondary leading-snug tracking-headline-serif", children: first.statement })
    ] }),
    showDivider && /* @__PURE__ */ jsx109("div", { className: "hidden lg:block absolute left-1/2 top-1/2 -translate-y-1/2 h-32 w-px bg-recessed/40" }),
    /* @__PURE__ */ jsxs95("div", { className: "lg:border-l lg:border-recessed/40 lg:pl-12", children: [
      second.context && /* @__PURE__ */ jsx109("div", { className: "text-label-sm font-sans font-semibold uppercase text-accent mb-4", children: second.context }),
      /* @__PURE__ */ jsx109("p", { className: "text-heading-subsection sm:text-heading-subsection-md font-serif font-semibold text-primary leading-snug tracking-headline-serif", children: second.statement })
    ] })
  ] }) }) });
}

// src/sections/SplitFeature.tsx
import { clsx as clsx9 } from "clsx";
import { ArrowRight } from "lucide-react";
import { jsx as jsx110, jsxs as jsxs96 } from "react/jsx-runtime";
function SplitFeature({
  categoryTag,
  headline,
  description,
  features,
  primaryAction,
  secondaryAction,
  media,
  imageUrl,
  imageAlt = "",
  mediaPosition = "right",
  ImageComponent,
  variant = "default",
  className
}) {
  const renderMedia = () => {
    if (media) {
      return media;
    }
    if (imageUrl) {
      const Image = ImageComponent || "img";
      return /* @__PURE__ */ jsx110(
        "div",
        {
          className: clsx9(
            "relative overflow-hidden",
            variant === "elevated" ? "rounded-sm shadow-lg" : variant === "default" ? "rounded-sm border border-recessed" : ""
          ),
          children: /* @__PURE__ */ jsx110(
            Image,
            {
              src: imageUrl,
              alt: imageAlt,
              width: 800,
              height: 600,
              className: "w-full h-auto"
            }
          )
        }
      );
    }
    return /* @__PURE__ */ jsx110(
      "div",
      {
        className: clsx9(
          "aspect-[4/3] bg-recessed/20 flex items-center justify-center",
          variant === "elevated" ? "rounded-sm shadow-lg" : variant === "default" ? "rounded-sm border border-recessed" : ""
        ),
        children: /* @__PURE__ */ jsx110("span", { className: "text-caption-md font-sans text-tertiary", children: "Media placeholder" })
      }
    );
  };
  const renderAction = (action, isPrimary) => {
    const baseClasses = isPrimary ? "inline-flex items-center gap-2 px-6 py-3 bg-accent text-white font-sans font-semibold text-body-sm rounded-sm hover:bg-accent-hover transition-colors" : "inline-flex items-center gap-2 px-6 py-3 text-accent font-sans font-semibold text-body-sm hover:text-accent-hover transition-colors";
    if (action.href) {
      return /* @__PURE__ */ jsxs96("a", { href: action.href, onClick: action.onClick, className: baseClasses, children: [
        action.label,
        isPrimary && /* @__PURE__ */ jsx110(ArrowRight, { className: "w-4 h-4" })
      ] });
    }
    return /* @__PURE__ */ jsxs96("button", { onClick: action.onClick, className: baseClasses, children: [
      action.label,
      isPrimary && /* @__PURE__ */ jsx110(ArrowRight, { className: "w-4 h-4" })
    ] });
  };
  return /* @__PURE__ */ jsx110("section", { className: clsx9("py-16 sm:py-20 lg:py-24", className), children: /* @__PURE__ */ jsx110("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: /* @__PURE__ */ jsxs96(
    "div",
    {
      className: clsx9(
        "grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center",
        mediaPosition === "left" && "lg:grid-flow-dense"
      ),
      children: [
        /* @__PURE__ */ jsxs96("div", { className: mediaPosition === "left" ? "lg:col-start-2" : "", children: [
          categoryTag && /* @__PURE__ */ jsx110("div", { className: "text-label-xs font-sans font-semibold uppercase text-accent mb-4", children: categoryTag }),
          /* @__PURE__ */ jsx110("h2", { className: "text-heading-section sm:text-heading-section-md lg:text-heading-section-lg font-serif font-semibold text-primary mb-6 tracking-headline-serif", children: headline }),
          /* @__PURE__ */ jsx110("p", { className: "text-lead sm:text-lead-md font-serif text-secondary leading-relaxed mb-8", children: description }),
          features && features.length > 0 && /* @__PURE__ */ jsx110("ul", { className: "space-y-3 mb-8", children: features.map((feature, index) => /* @__PURE__ */ jsxs96("li", { className: "flex items-start gap-3", children: [
            /* @__PURE__ */ jsx110("span", { className: "flex-shrink-0 w-1.5 h-1.5 rounded-full bg-accent mt-2" }),
            /* @__PURE__ */ jsx110("span", { className: "text-body-md font-serif text-secondary", children: feature })
          ] }, index)) }),
          (primaryAction || secondaryAction) && /* @__PURE__ */ jsxs96("div", { className: "flex flex-wrap items-center gap-4", children: [
            primaryAction && renderAction(primaryAction, true),
            secondaryAction && renderAction(secondaryAction, false)
          ] })
        ] }),
        /* @__PURE__ */ jsx110("div", { className: mediaPosition === "left" ? "lg:col-start-1" : "", children: renderMedia() })
      ]
    }
  ) }) });
}
function SplitFeatureStack({
  features,
  className
}) {
  return /* @__PURE__ */ jsx110("div", { className, children: features.map((feature, index) => /* @__PURE__ */ jsx110(
    SplitFeature,
    {
      ...feature,
      mediaPosition: index % 2 === 0 ? "right" : "left"
    },
    index
  )) });
}

// src/sections/LogoCloud.tsx
import { clsx as clsx10 } from "clsx";
import { jsx as jsx111, jsxs as jsxs97 } from "react/jsx-runtime";
function LogoCloud({
  headline,
  lead,
  logos,
  variant = "grid",
  logoStyle = "grayscale",
  columns = 5,
  ImageComponent,
  className
}) {
  const columnClasses = {
    3: "grid-cols-2 sm:grid-cols-3",
    4: "grid-cols-2 sm:grid-cols-4",
    5: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5",
    6: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6"
  };
  const styleClasses = {
    grayscale: "grayscale opacity-60 hover:grayscale-0 hover:opacity-100",
    color: "",
    monochrome: "brightness-0 opacity-40 hover:opacity-60"
  };
  const renderLogo = (logo, index) => {
    const Image = ImageComponent || "img";
    const content = logo.logo ?? (logo.imageUrl ? /* @__PURE__ */ jsx111(
      Image,
      {
        src: logo.imageUrl,
        alt: logo.name,
        width: 160,
        height: 48,
        className: clsx10(
          "h-8 sm:h-10 w-auto object-contain transition-all duration-300",
          styleClasses[logoStyle]
        )
      }
    ) : (
      // Text fallback
      /* @__PURE__ */ jsx111("span", { className: "text-body-sm font-sans font-semibold text-tertiary", children: logo.name })
    ));
    if (logo.href) {
      return /* @__PURE__ */ jsx111(
        "a",
        {
          href: logo.href,
          className: "flex items-center justify-center p-4",
          title: logo.name,
          children: content
        },
        index
      );
    }
    return /* @__PURE__ */ jsx111(
      "div",
      {
        className: "flex items-center justify-center p-4",
        title: logo.name,
        children: content
      },
      index
    );
  };
  if (variant === "inline") {
    return /* @__PURE__ */ jsx111("section", { className: clsx10("py-12 sm:py-16 lg:py-20", className), children: /* @__PURE__ */ jsxs97("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: [
      headline && /* @__PURE__ */ jsx111("div", { className: "text-center mb-8", children: /* @__PURE__ */ jsx111("p", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary", children: headline }) }),
      /* @__PURE__ */ jsx111("div", { className: "flex flex-wrap items-center justify-center gap-8 sm:gap-12", children: logos.map(renderLogo) }),
      lead && /* @__PURE__ */ jsx111("p", { className: "text-center text-caption-md font-sans text-tertiary mt-8", children: lead })
    ] }) });
  }
  if (variant === "stacked") {
    return /* @__PURE__ */ jsx111("section", { className: clsx10("py-12 sm:py-16 lg:py-20", className), children: /* @__PURE__ */ jsxs97("div", { className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8", children: [
      headline && /* @__PURE__ */ jsx111("p", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-8", children: headline }),
      /* @__PURE__ */ jsx111("div", { className: "divide-y divide-recessed/30", children: logos.map((logo, index) => /* @__PURE__ */ jsxs97(
        "div",
        {
          className: "flex items-center justify-between py-4 sm:py-6",
          children: [
            logo.logo ?? (logo.imageUrl ? /* @__PURE__ */ jsx111("div", { className: "flex items-center", children: ImageComponent ? /* @__PURE__ */ jsx111(
              ImageComponent,
              {
                src: logo.imageUrl,
                alt: logo.name,
                width: 120,
                height: 40,
                className: clsx10(
                  "h-6 sm:h-8 w-auto object-contain",
                  styleClasses[logoStyle]
                )
              }
            ) : /* @__PURE__ */ jsx111(
              "img",
              {
                src: logo.imageUrl,
                alt: logo.name,
                className: clsx10(
                  "h-6 sm:h-8 w-auto object-contain",
                  styleClasses[logoStyle]
                )
              }
            ) }) : /* @__PURE__ */ jsx111("span", { className: "text-body-md font-sans font-medium text-primary", children: logo.name })),
            logo.href && /* @__PURE__ */ jsx111(
              "a",
              {
                href: logo.href,
                className: "text-caption-md font-sans text-tertiary hover:text-accent transition-colors",
                children: "View case study"
              }
            )
          ]
        },
        index
      )) })
    ] }) });
  }
  return /* @__PURE__ */ jsx111("section", { className: clsx10("py-16 sm:py-20 lg:py-24", className), children: /* @__PURE__ */ jsxs97("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: [
    (headline || lead) && /* @__PURE__ */ jsxs97("div", { className: "text-center mb-10 sm:mb-12", children: [
      headline && /* @__PURE__ */ jsx111("p", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-3", children: headline }),
      lead && /* @__PURE__ */ jsx111("p", { className: "text-body-md font-serif text-secondary max-w-2xl mx-auto", children: lead })
    ] }),
    /* @__PURE__ */ jsx111(
      "div",
      {
        className: clsx10(
          "grid gap-4 sm:gap-6",
          columnClasses[columns]
        ),
        children: logos.map((logo, index) => /* @__PURE__ */ jsx111(
          "div",
          {
            className: "flex items-center justify-center py-6 sm:py-8 px-4 bg-surface/50 border border-recessed/20 rounded-sm",
            children: logo.logo ?? (logo.imageUrl ? ImageComponent ? /* @__PURE__ */ jsx111(
              ImageComponent,
              {
                src: logo.imageUrl,
                alt: logo.name,
                width: 140,
                height: 48,
                className: clsx10(
                  "h-8 sm:h-10 w-auto object-contain transition-all duration-300",
                  styleClasses[logoStyle]
                )
              }
            ) : /* @__PURE__ */ jsx111(
              "img",
              {
                src: logo.imageUrl,
                alt: logo.name,
                className: clsx10(
                  "h-8 sm:h-10 w-auto object-contain transition-all duration-300",
                  styleClasses[logoStyle]
                )
              }
            ) : /* @__PURE__ */ jsx111("span", { className: "text-body-md font-sans font-semibold text-tertiary", children: logo.name }))
          },
          index
        ))
      }
    )
  ] }) });
}

// src/sections/MetricShowcase.tsx
import { clsx as clsx11 } from "clsx";
import { jsx as jsx112, jsxs as jsxs98 } from "react/jsx-runtime";
function MetricShowcase({
  headline,
  lead,
  metrics,
  variant = "row",
  density = "default",
  showDividers = true,
  className
}) {
  const densityClasses = {
    compact: "py-12 sm:py-16 lg:py-20",
    default: "py-16 sm:py-20 lg:py-24",
    spacious: "py-20 sm:py-24 lg:py-32"
  };
  const renderTrend = (trend) => {
    if (!trend) return null;
    const colors = {
      up: "text-growth",
      down: "text-error",
      neutral: "text-tertiary"
    };
    const arrows = {
      up: "\u2191",
      down: "\u2193",
      neutral: "\u2192"
    };
    return /* @__PURE__ */ jsxs98("span", { className: clsx11("text-caption-md font-sans ml-2", colors[trend.direction]), children: [
      arrows[trend.direction],
      " ",
      trend.value
    ] });
  };
  if (variant === "featured" && metrics.length > 0) {
    const [featured, ...supporting] = metrics;
    return /* @__PURE__ */ jsx112("section", { className: clsx11(densityClasses[density], className), children: /* @__PURE__ */ jsxs98("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: [
      (headline || lead) && /* @__PURE__ */ jsxs98("div", { className: "text-center mb-12 sm:mb-16", children: [
        headline && /* @__PURE__ */ jsx112("p", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-3", children: headline }),
        lead && /* @__PURE__ */ jsx112("p", { className: "text-body-md font-serif text-secondary max-w-2xl mx-auto", children: lead })
      ] }),
      /* @__PURE__ */ jsxs98("div", { className: "text-center mb-12 sm:mb-16", children: [
        /* @__PURE__ */ jsxs98("div", { className: "text-heading-feature sm:text-heading-feature-md lg:text-heading-feature-lg font-serif font-semibold text-primary tracking-headline-serif mb-4", children: [
          featured.value,
          renderTrend(featured.trend)
        ] }),
        /* @__PURE__ */ jsx112("p", { className: "text-lead sm:text-lead-md font-serif text-secondary mb-2", children: featured.label }),
        featured.detail && /* @__PURE__ */ jsx112("p", { className: "text-caption-md font-sans text-tertiary", children: featured.detail })
      ] }),
      supporting.length > 0 && /* @__PURE__ */ jsx112(
        "div",
        {
          className: clsx11(
            "grid gap-8 sm:gap-12 pt-8 sm:pt-12 border-t border-recessed/40",
            supporting.length === 2 && "grid-cols-2",
            supporting.length === 3 && "grid-cols-3",
            supporting.length >= 4 && "grid-cols-2 sm:grid-cols-4"
          ),
          children: supporting.map((metric, index) => /* @__PURE__ */ jsxs98("div", { className: "text-center", children: [
            /* @__PURE__ */ jsxs98("div", { className: "text-heading-section sm:text-heading-section-md font-serif font-semibold text-primary tracking-headline-serif mb-2", children: [
              metric.value,
              renderTrend(metric.trend)
            ] }),
            /* @__PURE__ */ jsx112("p", { className: "text-body-sm font-sans text-secondary", children: metric.label }),
            metric.detail && /* @__PURE__ */ jsx112("p", { className: "text-caption-sm font-sans text-tertiary mt-1", children: metric.detail })
          ] }, index))
        }
      )
    ] }) });
  }
  if (variant === "editorial") {
    return /* @__PURE__ */ jsx112("section", { className: clsx11(densityClasses[density], className), children: /* @__PURE__ */ jsxs98("div", { className: "max-w-5xl mx-auto px-4 sm:px-6 lg:px-8", children: [
      (headline || lead) && /* @__PURE__ */ jsxs98("div", { className: "mb-12 sm:mb-16", children: [
        headline && /* @__PURE__ */ jsx112("p", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-4", children: headline }),
        lead && /* @__PURE__ */ jsx112("p", { className: "text-lead sm:text-lead-md font-serif text-secondary max-w-content-narrow", children: lead })
      ] }),
      /* @__PURE__ */ jsx112("div", { className: "space-y-8 sm:space-y-12", children: metrics.map((metric, index) => /* @__PURE__ */ jsxs98(
        "div",
        {
          className: clsx11(
            "grid grid-cols-1 sm:grid-cols-12 gap-4 sm:gap-8 items-baseline",
            showDividers && index > 0 && "pt-8 sm:pt-12 border-t border-recessed/30"
          ),
          children: [
            /* @__PURE__ */ jsxs98(
              "div",
              {
                className: clsx11(
                  "sm:col-span-4",
                  index % 2 === 1 && "sm:col-start-2"
                ),
                children: [
                  /* @__PURE__ */ jsx112("span", { className: "text-heading-section sm:text-heading-section-md lg:text-heading-section-lg font-serif font-semibold text-primary tracking-headline-serif", children: metric.value }),
                  renderTrend(metric.trend)
                ]
              }
            ),
            /* @__PURE__ */ jsxs98(
              "div",
              {
                className: clsx11(
                  "sm:col-span-6",
                  index % 2 === 1 && "sm:col-start-6"
                ),
                children: [
                  /* @__PURE__ */ jsx112("p", { className: "text-body-md font-serif text-primary mb-1", children: metric.label }),
                  metric.detail && /* @__PURE__ */ jsx112("p", { className: "text-body-sm font-sans text-tertiary", children: metric.detail })
                ]
              }
            )
          ]
        },
        index
      )) })
    ] }) });
  }
  if (variant === "grid") {
    return /* @__PURE__ */ jsx112("section", { className: clsx11(densityClasses[density], className), children: /* @__PURE__ */ jsxs98("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: [
      (headline || lead) && /* @__PURE__ */ jsxs98("div", { className: "text-center mb-12 sm:mb-16", children: [
        headline && /* @__PURE__ */ jsx112("p", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-3", children: headline }),
        lead && /* @__PURE__ */ jsx112("p", { className: "text-body-md font-serif text-secondary max-w-2xl mx-auto", children: lead })
      ] }),
      /* @__PURE__ */ jsx112(
        "div",
        {
          className: clsx11(
            "grid gap-8 sm:gap-10",
            metrics.length <= 4 && "grid-cols-2 lg:grid-cols-4",
            metrics.length > 4 && "grid-cols-2 sm:grid-cols-3"
          ),
          children: metrics.map((metric, index) => /* @__PURE__ */ jsxs98(
            "div",
            {
              className: clsx11(
                "text-center py-6 sm:py-8",
                showDividers && "border border-recessed/20 rounded-sm"
              ),
              children: [
                /* @__PURE__ */ jsxs98("div", { className: "text-heading-section sm:text-heading-section-md font-serif font-semibold text-primary tracking-headline-serif mb-3", children: [
                  metric.value,
                  renderTrend(metric.trend)
                ] }),
                /* @__PURE__ */ jsx112("p", { className: "text-body-sm font-sans font-medium text-secondary mb-1", children: metric.label }),
                metric.detail && /* @__PURE__ */ jsx112("p", { className: "text-caption-md font-sans text-tertiary", children: metric.detail })
              ]
            },
            index
          ))
        }
      )
    ] }) });
  }
  return /* @__PURE__ */ jsx112("section", { className: clsx11(densityClasses[density], className), children: /* @__PURE__ */ jsxs98("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: [
    (headline || lead) && /* @__PURE__ */ jsxs98("div", { className: "text-center mb-10 sm:mb-12", children: [
      headline && /* @__PURE__ */ jsx112("p", { className: "text-label-sm font-sans font-semibold uppercase text-tertiary mb-3", children: headline }),
      lead && /* @__PURE__ */ jsx112("p", { className: "text-body-md font-serif text-secondary max-w-2xl mx-auto", children: lead })
    ] }),
    /* @__PURE__ */ jsx112(
      "div",
      {
        className: clsx11(
          "grid gap-8 sm:gap-4",
          metrics.length === 2 && "grid-cols-2",
          metrics.length === 3 && "grid-cols-1 sm:grid-cols-3",
          metrics.length === 4 && "grid-cols-2 lg:grid-cols-4",
          metrics.length > 4 && "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"
        ),
        children: metrics.map((metric, index) => /* @__PURE__ */ jsxs98(
          "div",
          {
            className: clsx11(
              "text-center sm:text-left",
              showDividers && index > 0 && "sm:border-l sm:border-recessed/40 sm:pl-6 lg:pl-8"
            ),
            children: [
              /* @__PURE__ */ jsxs98("div", { className: "text-heading-section sm:text-heading-section-md font-serif font-semibold text-primary tracking-headline-serif mb-2", children: [
                metric.value,
                renderTrend(metric.trend)
              ] }),
              /* @__PURE__ */ jsx112("p", { className: "text-body-sm font-sans text-secondary", children: metric.label }),
              metric.detail && /* @__PURE__ */ jsx112("p", { className: "text-caption-md font-sans text-tertiary mt-1", children: metric.detail })
            ]
          },
          index
        ))
      }
    )
  ] }) });
}
function MetricHighlight({
  value,
  label,
  size = "md",
  className
}) {
  const sizeClasses4 = {
    sm: {
      value: "text-heading-card sm:text-heading-card-md",
      label: "text-caption-md"
    },
    md: {
      value: "text-heading-subsection sm:text-heading-subsection-md",
      label: "text-body-sm"
    },
    lg: {
      value: "text-heading-section sm:text-heading-section-md",
      label: "text-body-md"
    }
  };
  return /* @__PURE__ */ jsxs98("div", { className: clsx11("text-center", className), children: [
    /* @__PURE__ */ jsx112(
      "div",
      {
        className: clsx11(
          "font-serif font-semibold text-primary mb-1",
          sizeClasses4[size].value
        ),
        children: value
      }
    ),
    /* @__PURE__ */ jsx112("p", { className: clsx11("font-sans text-secondary", sizeClasses4[size].label), children: label })
  ] });
}

// src/sections/EditorialQuote.tsx
import React33 from "react";
import { clsx as clsx12 } from "clsx";
import { jsx as jsx113, jsxs as jsxs99 } from "react/jsx-runtime";
function EditorialQuote({
  quote,
  attribution,
  role,
  organization,
  avatar,
  avatarUrl,
  variant = "default",
  align = "left",
  showQuoteMarks = true,
  ImageComponent,
  className
}) {
  const renderAvatar = () => {
    if (avatar) return avatar;
    if (avatarUrl) {
      const Image = ImageComponent || "img";
      return /* @__PURE__ */ jsx113(
        Image,
        {
          src: avatarUrl,
          alt: attribution,
          width: 80,
          height: 80,
          className: "w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover"
        }
      );
    }
    const initials = attribution.split(" ").map((n) => n[0]).join("").slice(0, 2);
    return /* @__PURE__ */ jsx113("div", { className: "w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-recessed/30 flex items-center justify-center", children: /* @__PURE__ */ jsx113("span", { className: "text-body-md font-sans font-semibold text-tertiary", children: initials }) });
  };
  if (variant === "featured") {
    return /* @__PURE__ */ jsx113("section", { className: clsx12("py-20 sm:py-24 lg:py-32", className), children: /* @__PURE__ */ jsx113("div", { className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8", children: /* @__PURE__ */ jsxs99(
      "figure",
      {
        className: clsx12(
          align === "center" && "text-center"
        ),
        children: [
          showQuoteMarks && /* @__PURE__ */ jsx113(
            "div",
            {
              className: clsx12(
                "text-heading-feature font-serif text-accent/20 mb-4",
                align === "center" && "mx-auto"
              ),
              "aria-hidden": "true",
              children: '"'
            }
          ),
          /* @__PURE__ */ jsx113("blockquote", { children: /* @__PURE__ */ jsx113("p", { className: "text-heading-section sm:text-heading-section-md lg:text-heading-section-lg font-serif font-normal text-primary leading-snug tracking-headline-serif", children: quote }) }),
          /* @__PURE__ */ jsxs99(
            "figcaption",
            {
              className: clsx12(
                "mt-8 sm:mt-10",
                align === "center" ? "flex flex-col items-center" : "flex items-center gap-4"
              ),
              children: [
                (avatar || avatarUrl) && /* @__PURE__ */ jsx113("div", { className: align === "center" ? "mb-4" : "", children: renderAvatar() }),
                /* @__PURE__ */ jsxs99("div", { className: align === "center" ? "text-center" : "", children: [
                  /* @__PURE__ */ jsx113("cite", { className: "not-italic text-body-md font-sans font-semibold text-primary", children: attribution }),
                  (role || organization) && /* @__PURE__ */ jsxs99("p", { className: "text-body-sm font-sans text-tertiary mt-1", children: [
                    role,
                    role && organization && ", ",
                    organization
                  ] })
                ] })
              ]
            }
          )
        ]
      }
    ) }) });
  }
  if (variant === "minimal") {
    return /* @__PURE__ */ jsx113("section", { className: clsx12("py-12 sm:py-16 lg:py-20", className), children: /* @__PURE__ */ jsx113("div", { className: "max-w-3xl mx-auto px-4 sm:px-6 lg:px-8", children: /* @__PURE__ */ jsxs99("figure", { className: align === "center" ? "text-center" : "", children: [
      /* @__PURE__ */ jsx113("blockquote", { children: /* @__PURE__ */ jsxs99("p", { className: "text-lead sm:text-lead-md font-serif italic text-secondary leading-relaxed", children: [
        '"',
        quote,
        '"'
      ] }) }),
      /* @__PURE__ */ jsx113("figcaption", { className: "mt-4", children: /* @__PURE__ */ jsxs99("cite", { className: "not-italic text-caption-md font-sans text-tertiary", children: [
        attribution,
        role && `, ${role}`,
        organization && ` at ${organization}`
      ] }) })
    ] }) }) });
  }
  if (variant === "accent") {
    return /* @__PURE__ */ jsx113("section", { className: clsx12("py-16 sm:py-20 lg:py-24", className), children: /* @__PURE__ */ jsx113("div", { className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8", children: /* @__PURE__ */ jsxs99("figure", { className: "border-l-4 border-accent pl-6 sm:pl-8", children: [
      /* @__PURE__ */ jsx113("blockquote", { children: /* @__PURE__ */ jsxs99("p", { className: "text-heading-subsection sm:text-heading-subsection-md font-serif font-normal text-primary leading-snug tracking-headline-serif", children: [
        showQuoteMarks && '"',
        quote,
        showQuoteMarks && '"'
      ] }) }),
      /* @__PURE__ */ jsxs99("figcaption", { className: "mt-6 flex items-center gap-4", children: [
        (avatar || avatarUrl) && renderAvatar(),
        /* @__PURE__ */ jsxs99("div", { children: [
          /* @__PURE__ */ jsx113("cite", { className: "not-italic text-body-sm font-sans font-semibold text-primary", children: attribution }),
          (role || organization) && /* @__PURE__ */ jsxs99("p", { className: "text-caption-md font-sans text-tertiary", children: [
            role,
            role && organization && ", ",
            organization
          ] })
        ] })
      ] })
    ] }) }) });
  }
  return /* @__PURE__ */ jsx113("section", { className: clsx12("py-16 sm:py-20 lg:py-24", className), children: /* @__PURE__ */ jsx113("div", { className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8", children: /* @__PURE__ */ jsxs99("figure", { className: align === "center" ? "text-center" : "", children: [
    showQuoteMarks && /* @__PURE__ */ jsx113(
      "div",
      {
        className: clsx12(
          "text-heading-section font-serif tracking-headline-serif text-recessed/60 mb-2",
          align === "center" && "mx-auto"
        ),
        "aria-hidden": "true",
        children: '"'
      }
    ),
    /* @__PURE__ */ jsx113("blockquote", { children: /* @__PURE__ */ jsx113("p", { className: "text-heading-subsection sm:text-heading-subsection-md lg:text-heading-subsection-lg font-serif font-normal text-primary leading-snug tracking-headline-serif", children: quote }) }),
    /* @__PURE__ */ jsxs99(
      "figcaption",
      {
        className: clsx12(
          "mt-6 sm:mt-8",
          align === "center" ? "flex flex-col items-center" : "flex items-center gap-4"
        ),
        children: [
          (avatar || avatarUrl) && /* @__PURE__ */ jsx113("div", { className: align === "center" ? "mb-3" : "", children: renderAvatar() }),
          /* @__PURE__ */ jsxs99("div", { className: align === "center" ? "text-center" : "", children: [
            /* @__PURE__ */ jsx113("span", { className: "inline-block w-8 h-px bg-recessed/60 mr-3 align-middle" }),
            /* @__PURE__ */ jsxs99("cite", { className: "not-italic text-body-sm font-sans text-tertiary", children: [
              attribution,
              role && `, ${role}`,
              organization && ` at ${organization}`
            ] })
          ] })
        ]
      }
    )
  ] }) }) });
}
function QuoteCarousel({
  quotes,
  autoPlay = false,
  interval = 5e3,
  className
}) {
  const [activeIndex, setActiveIndex] = React33.useState(0);
  React33.useEffect(() => {
    if (!autoPlay) return;
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % quotes.length);
    }, interval);
    return () => clearInterval(timer);
  }, [autoPlay, interval, quotes.length]);
  const currentQuote = quotes[activeIndex];
  return /* @__PURE__ */ jsx113("section", { className: clsx12("py-16 sm:py-20 lg:py-24", className), children: /* @__PURE__ */ jsxs99("div", { className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8", children: [
    /* @__PURE__ */ jsxs99("figure", { className: "text-center", children: [
      /* @__PURE__ */ jsx113("div", { className: "text-heading-section font-serif tracking-headline-serif text-recessed/40 mb-2", children: '"' }),
      /* @__PURE__ */ jsx113("blockquote", { children: /* @__PURE__ */ jsx113("p", { className: "text-heading-subsection sm:text-heading-subsection-md font-serif font-normal text-primary leading-snug tracking-headline-serif", children: currentQuote.quote }) }),
      /* @__PURE__ */ jsxs99("figcaption", { className: "mt-8 flex flex-col items-center", children: [
        currentQuote.avatarUrl && /* @__PURE__ */ jsx113(
          "img",
          {
            src: currentQuote.avatarUrl,
            alt: currentQuote.attribution,
            className: "w-16 h-16 rounded-full object-cover mb-4"
          }
        ),
        /* @__PURE__ */ jsx113("cite", { className: "not-italic text-body-sm font-sans font-semibold text-primary", children: currentQuote.attribution }),
        (currentQuote.role || currentQuote.organization) && /* @__PURE__ */ jsxs99("p", { className: "text-caption-md font-sans text-tertiary mt-1", children: [
          currentQuote.role,
          currentQuote.role && currentQuote.organization && ", ",
          currentQuote.organization
        ] })
      ] })
    ] }),
    quotes.length > 1 && /* @__PURE__ */ jsx113("div", { className: "flex justify-center gap-2 mt-8", children: quotes.map((_, index) => /* @__PURE__ */ jsx113(
      "button",
      {
        onClick: () => setActiveIndex(index),
        className: clsx12(
          "w-2 h-2 rounded-full transition-colors",
          index === activeIndex ? "bg-accent" : "bg-recessed/40 hover:bg-recessed/60"
        ),
        "aria-label": `Go to quote ${index + 1}`
      },
      index
    )) })
  ] }) });
}

// src/sections/ComparisonTable.tsx
import { clsx as clsx13 } from "clsx";
import { Check, X as X2, Minus } from "lucide-react";
import { jsx as jsx114, jsxs as jsxs100 } from "react/jsx-runtime";
function ComparisonTable({
  headline,
  lead,
  columns,
  rows,
  variant = "default",
  showDescriptions = true,
  className
}) {
  const renderValue = (value, highlighted) => {
    if (value === true) {
      return /* @__PURE__ */ jsx114("span", { className: "inline-flex items-center justify-center w-5 h-5", children: /* @__PURE__ */ jsx114(Check, { className: "w-4 h-4 text-growth", strokeWidth: 2 }) });
    }
    if (value === false) {
      return /* @__PURE__ */ jsx114("span", { className: "inline-flex items-center justify-center w-5 h-5", children: /* @__PURE__ */ jsx114(X2, { className: "w-4 h-4 text-tertiary", strokeWidth: 1.5 }) });
    }
    if (value === null) {
      return /* @__PURE__ */ jsx114("span", { className: "inline-flex items-center justify-center w-5 h-5", children: /* @__PURE__ */ jsx114(Minus, { className: "w-4 h-4 text-recessed", strokeWidth: 1.5 }) });
    }
    return /* @__PURE__ */ jsx114("span", { className: clsx13(
      "text-body-sm font-serif",
      highlighted ? "text-primary font-medium" : "text-secondary"
    ), children: value });
  };
  if (variant === "cards") {
    return /* @__PURE__ */ jsx114("section", { className: clsx13("py-16 sm:py-20 lg:py-24", className), children: /* @__PURE__ */ jsxs100("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: [
      (headline || lead) && /* @__PURE__ */ jsxs100("div", { className: "text-center mb-12 sm:mb-16", children: [
        headline && /* @__PURE__ */ jsx114("h2", { className: "text-heading-section sm:text-heading-section-md font-serif font-semibold text-primary tracking-headline-serif mb-4", children: headline }),
        lead && /* @__PURE__ */ jsx114("p", { className: "text-lead font-serif text-secondary max-w-content-narrow mx-auto", children: lead })
      ] }),
      /* @__PURE__ */ jsx114(
        "div",
        {
          className: clsx13(
            "grid gap-6 sm:gap-8",
            columns.length === 2 && "grid-cols-1 md:grid-cols-2",
            columns.length === 3 && "grid-cols-1 md:grid-cols-3",
            columns.length >= 4 && "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
          ),
          children: columns.map((column, colIndex) => /* @__PURE__ */ jsxs100(
            "div",
            {
              className: clsx13(
                "p-6 sm:p-8 rounded-sm",
                column.highlighted ? "bg-surface border border-recessed shadow-md" : "bg-surface border border-recessed/30"
              ),
              children: [
                /* @__PURE__ */ jsxs100("div", { className: "mb-6 pb-5 border-b border-recessed/30", children: [
                  /* @__PURE__ */ jsx114(
                    "h3",
                    {
                      className: clsx13(
                        "text-label-sm font-sans font-semibold uppercase mb-2",
                        column.highlighted ? "text-accent" : "text-primary"
                      ),
                      children: column.label
                    }
                  ),
                  column.description && /* @__PURE__ */ jsx114("p", { className: "text-caption-sm font-serif text-tertiary normal-case tracking-normal", children: column.description })
                ] }),
                /* @__PURE__ */ jsx114("ul", { className: "space-y-3", children: rows.filter((row) => !row.isCategory).map((row, rowIndex) => /* @__PURE__ */ jsxs100("li", { className: "flex items-start gap-3", children: [
                  /* @__PURE__ */ jsx114("div", { className: "flex-shrink-0 mt-0.5", children: renderValue(row.values[colIndex], column.highlighted) }),
                  /* @__PURE__ */ jsxs100("div", { children: [
                    /* @__PURE__ */ jsx114("span", { className: "text-body-sm font-sans text-primary", children: row.feature }),
                    showDescriptions && row.description && /* @__PURE__ */ jsx114("p", { className: "text-caption-sm font-serif text-tertiary mt-0.5", children: row.description })
                  ] })
                ] }, rowIndex)) })
              ]
            },
            colIndex
          ))
        }
      )
    ] }) });
  }
  if (variant === "minimal") {
    return /* @__PURE__ */ jsx114("section", { className: clsx13("py-16 sm:py-20 lg:py-24", className), children: /* @__PURE__ */ jsxs100("div", { className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8", children: [
      (headline || lead) && /* @__PURE__ */ jsxs100("div", { className: "mb-10 sm:mb-12", children: [
        headline && /* @__PURE__ */ jsx114("h2", { className: "text-heading-section sm:text-heading-section-md font-serif font-semibold text-primary tracking-headline-serif mb-4", children: headline }),
        lead && /* @__PURE__ */ jsx114("p", { className: "text-lead font-serif text-secondary", children: lead })
      ] }),
      /* @__PURE__ */ jsx114("div", { className: "space-y-4", children: rows.map((row, rowIndex) => {
        if (row.isCategory) {
          return /* @__PURE__ */ jsx114(
            "div",
            {
              className: clsx13(
                "text-caption-sm font-sans font-semibold uppercase tracking-wider text-tertiary",
                rowIndex > 0 && "pt-6 border-t border-recessed/30"
              ),
              children: row.feature
            },
            rowIndex
          );
        }
        return /* @__PURE__ */ jsxs100(
          "div",
          {
            className: "grid grid-cols-3 gap-4 sm:gap-8 items-start py-2.5 border-b border-recessed/20",
            children: [
              /* @__PURE__ */ jsx114("div", { className: "col-span-1", children: /* @__PURE__ */ jsx114("span", { className: "text-body-sm font-sans text-primary", children: row.feature }) }),
              row.values.map((value, valIndex) => /* @__PURE__ */ jsx114("div", { className: "text-center", children: renderValue(value, columns[valIndex]?.highlighted) }, valIndex))
            ]
          },
          rowIndex
        );
      }) })
    ] }) });
  }
  return /* @__PURE__ */ jsx114("section", { className: clsx13("py-16 sm:py-20 lg:py-24", className), children: /* @__PURE__ */ jsxs100("div", { className: "max-w-content-wide mx-auto px-4 sm:px-6 lg:px-8", children: [
    (headline || lead) && /* @__PURE__ */ jsxs100("div", { className: "text-center mb-12 sm:mb-16", children: [
      headline && /* @__PURE__ */ jsx114("h2", { className: "text-heading-section sm:text-heading-section-md font-serif font-semibold text-primary tracking-headline-serif mb-4", children: headline }),
      lead && /* @__PURE__ */ jsx114("p", { className: "text-lead font-serif text-secondary max-w-content-narrow mx-auto", children: lead })
    ] }),
    /* @__PURE__ */ jsx114("div", { className: "overflow-x-auto", children: /* @__PURE__ */ jsxs100("table", { className: "w-full", children: [
      /* @__PURE__ */ jsx114("thead", { children: /* @__PURE__ */ jsxs100("tr", { className: "border-b-2 border-recessed/40", children: [
        /* @__PURE__ */ jsx114("th", { className: "text-left py-4 pr-4 w-1/3", children: /* @__PURE__ */ jsx114("span", { className: "sr-only", children: "Feature" }) }),
        columns.map((column, index) => /* @__PURE__ */ jsxs100(
          "th",
          {
            className: clsx13(
              "text-center py-4 px-4",
              column.highlighted && "border-b-2 border-accent"
            ),
            children: [
              /* @__PURE__ */ jsx114(
                "span",
                {
                  className: clsx13(
                    "text-label-sm font-sans font-semibold uppercase block",
                    column.highlighted ? "text-accent" : "text-primary"
                  ),
                  children: column.label
                }
              ),
              column.description && /* @__PURE__ */ jsx114("span", { className: "text-caption-sm font-serif text-tertiary mt-1 block normal-case tracking-normal", children: column.description })
            ]
          },
          index
        ))
      ] }) }),
      /* @__PURE__ */ jsx114("tbody", { children: rows.map((row, rowIndex) => {
        if (row.isCategory) {
          return /* @__PURE__ */ jsx114("tr", { children: /* @__PURE__ */ jsx114(
            "td",
            {
              colSpan: columns.length + 1,
              className: clsx13(
                "text-caption-sm font-sans font-semibold uppercase tracking-wider text-tertiary py-3",
                rowIndex > 0 && "pt-8"
              ),
              children: row.feature
            }
          ) }, rowIndex);
        }
        return /* @__PURE__ */ jsxs100("tr", { className: "border-b border-recessed/20", children: [
          /* @__PURE__ */ jsxs100("td", { className: "py-3 pr-4", children: [
            /* @__PURE__ */ jsx114("span", { className: "text-body-sm font-sans text-primary block", children: row.feature }),
            showDescriptions && row.description && /* @__PURE__ */ jsx114("span", { className: "text-caption-sm font-serif text-tertiary mt-0.5 block", children: row.description })
          ] }),
          row.values.map((value, valIndex) => /* @__PURE__ */ jsx114(
            "td",
            {
              className: "text-center py-3 px-4",
              children: renderValue(value, columns[valIndex]?.highlighted)
            },
            valIndex
          ))
        ] }, rowIndex);
      }) })
    ] }) })
  ] }) });
}
function BeforeAfter({
  headline,
  lead,
  beforeLabel = "Before",
  afterLabel = "After",
  items,
  className
}) {
  return /* @__PURE__ */ jsx114("section", { className: clsx13("py-16 sm:py-20 lg:py-24", className), children: /* @__PURE__ */ jsxs100("div", { className: "max-w-5xl mx-auto px-4 sm:px-6 lg:px-8", children: [
    (headline || lead) && /* @__PURE__ */ jsxs100("div", { className: "mb-10 sm:mb-12", children: [
      headline && /* @__PURE__ */ jsx114("h2", { className: "text-heading-section sm:text-heading-section-md font-serif font-semibold text-primary tracking-headline-serif mb-4", children: headline }),
      lead && /* @__PURE__ */ jsx114("p", { className: "text-lead font-serif text-secondary", children: lead })
    ] }),
    /* @__PURE__ */ jsxs100("div", { className: "space-y-0", children: [
      /* @__PURE__ */ jsxs100("div", { className: "grid grid-cols-12 gap-4 sm:gap-8 pb-3 border-b-2 border-recessed/40", children: [
        /* @__PURE__ */ jsx114("div", { className: "col-span-4 sm:col-span-3", children: /* @__PURE__ */ jsx114("span", { className: "sr-only", children: "Aspect" }) }),
        /* @__PURE__ */ jsx114("div", { className: "col-span-4 sm:col-span-4", children: /* @__PURE__ */ jsx114("span", { className: "text-caption-sm font-sans font-semibold uppercase tracking-wider text-tertiary", children: beforeLabel }) }),
        /* @__PURE__ */ jsx114("div", { className: "col-span-4 sm:col-span-5", children: /* @__PURE__ */ jsx114("span", { className: "text-caption-sm font-sans font-semibold uppercase tracking-wider text-accent", children: afterLabel }) })
      ] }),
      items.map((item, index) => /* @__PURE__ */ jsxs100(
        "div",
        {
          className: "grid grid-cols-12 gap-4 sm:gap-8 py-3 sm:py-4 border-b border-recessed/20 items-start",
          children: [
            /* @__PURE__ */ jsx114("div", { className: "col-span-4 sm:col-span-3", children: /* @__PURE__ */ jsx114("span", { className: "text-body-sm font-sans text-primary", children: item.aspect }) }),
            /* @__PURE__ */ jsx114("div", { className: "col-span-4 sm:col-span-4", children: /* @__PURE__ */ jsx114("span", { className: "text-body-sm font-serif text-tertiary", children: item.before }) }),
            /* @__PURE__ */ jsx114("div", { className: "col-span-4 sm:col-span-5", children: /* @__PURE__ */ jsx114("span", { className: "text-body-sm font-serif text-primary", children: item.after }) })
          ]
        },
        index
      ))
    ] })
  ] }) });
}

// src/sections/BlogArticleHeader.tsx
import { Fragment as Fragment25, jsx as jsx115, jsxs as jsxs101 } from "react/jsx-runtime";
function BlogArticleHeader({
  category,
  title,
  excerpt,
  author,
  authorRole,
  date,
  readingTime,
  authorAvatar
}) {
  return /* @__PURE__ */ jsx115("header", { className: "border-b border-recessed bg-surface", children: /* @__PURE__ */ jsxs101("div", { className: "max-w-content-narrow mx-auto px-8 py-12 sm:py-16", children: [
    /* @__PURE__ */ jsx115("div", { className: "text-xs font-sans font-semibold uppercase tracking-[0.15em] text-accent mb-6", children: category }),
    /* @__PURE__ */ jsx115("h1", { className: "text-4xl sm:text-5xl lg:text-heading-page-lg font-serif font-semibold text-primary mb-6 leading-headline tracking-headline-serif", children: title }),
    excerpt && /* @__PURE__ */ jsx115("p", { className: "text-lg sm:text-xl font-serif text-secondary mb-8", children: excerpt }),
    /* @__PURE__ */ jsxs101("div", { className: "flex items-center gap-4 pt-6 border-t border-recessed", children: [
      authorAvatar ? /* @__PURE__ */ jsx115("img", { src: authorAvatar, alt: author, className: "w-12 h-12 rounded-full object-cover" }) : /* @__PURE__ */ jsx115("div", { className: "w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0", children: /* @__PURE__ */ jsx115("span", { className: "text-base font-sans font-semibold text-accent", children: author.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) }) }),
      /* @__PURE__ */ jsxs101("div", { className: "flex-1 min-w-0", children: [
        /* @__PURE__ */ jsxs101("div", { className: "flex flex-wrap items-center gap-2 text-sm font-sans", children: [
          /* @__PURE__ */ jsx115("span", { className: "font-semibold text-primary", children: author }),
          authorRole && /* @__PURE__ */ jsxs101(Fragment25, { children: [
            /* @__PURE__ */ jsx115("span", { className: "text-tertiary", children: "\u2022" }),
            /* @__PURE__ */ jsx115("span", { className: "text-secondary", children: authorRole })
          ] })
        ] }),
        /* @__PURE__ */ jsxs101("div", { className: "flex flex-wrap items-center gap-2 text-sm font-sans text-tertiary mt-1", children: [
          /* @__PURE__ */ jsx115("time", { dateTime: date, children: date }),
          readingTime && /* @__PURE__ */ jsxs101(Fragment25, { children: [
            /* @__PURE__ */ jsx115("span", { children: "\u2022" }),
            /* @__PURE__ */ jsx115("span", { children: readingTime })
          ] })
        ] })
      ] })
    ] })
  ] }) });
}
function BlogArticleHeaderCompact({
  category,
  title,
  author,
  date,
  readingTime
}) {
  return /* @__PURE__ */ jsx115("header", { className: "border-b border-recessed bg-surface", children: /* @__PURE__ */ jsxs101("div", { className: "max-w-content-narrow mx-auto px-8 py-8 sm:py-10", children: [
    /* @__PURE__ */ jsxs101("div", { className: "flex items-center gap-3 mb-4", children: [
      /* @__PURE__ */ jsx115("div", { className: "text-label-xs font-sans font-semibold uppercase text-accent", children: category }),
      /* @__PURE__ */ jsx115("span", { className: "text-tertiary text-xs", children: "\u2022" }),
      /* @__PURE__ */ jsxs101("div", { className: "text-xs font-sans text-tertiary", children: [
        date,
        readingTime && ` \u2022 ${readingTime}`
      ] })
    ] }),
    /* @__PURE__ */ jsx115("h1", { className: "text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold text-primary mb-3 leading-snug tracking-headline-serif", children: title }),
    /* @__PURE__ */ jsxs101("div", { className: "text-sm font-sans text-secondary", children: [
      "By ",
      author
    ] })
  ] }) });
}

// src/sections/WhitepaperHeader.tsx
import { jsx as jsx116, jsxs as jsxs102 } from "react/jsx-runtime";
function WhitepaperHeader({
  documentType,
  title,
  subtitle,
  date,
  author,
  readingTime
}) {
  return /* @__PURE__ */ jsx116("header", { className: "border-b border-recessed", children: /* @__PURE__ */ jsxs102("div", { className: "max-w-content-narrow mx-auto bg-surface", children: [
    /* @__PURE__ */ jsx116("div", { className: "h-1 bg-accent" }),
    /* @__PURE__ */ jsxs102("div", { className: "px-8 py-12 sm:py-16", children: [
      /* @__PURE__ */ jsx116("div", { className: "text-label-sm font-sans font-semibold uppercase text-accent mb-6", children: documentType }),
      /* @__PURE__ */ jsx116("h1", { className: "text-4xl sm:text-5xl lg:text-heading-page-lg font-serif font-semibold text-primary mb-6 leading-headline tracking-headline-serif", children: title }),
      subtitle && /* @__PURE__ */ jsx116("p", { className: "text-lg sm:text-xl font-serif text-secondary mb-8", children: subtitle }),
      (date || author || readingTime) && /* @__PURE__ */ jsxs102("div", { className: "flex flex-wrap items-center gap-4 text-sm font-sans text-tertiary pt-6 border-t border-recessed", children: [
        date && /* @__PURE__ */ jsxs102("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx116("span", { className: "font-medium", children: "Published" }),
          /* @__PURE__ */ jsx116("span", { children: date })
        ] }),
        author && /* @__PURE__ */ jsxs102("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx116("span", { className: "w-1 h-1 rounded-full bg-tertiary opacity-50" }),
          /* @__PURE__ */ jsx116("span", { className: "font-medium", children: "By" }),
          /* @__PURE__ */ jsx116("span", { children: author })
        ] }),
        readingTime && /* @__PURE__ */ jsxs102("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx116("span", { className: "w-1 h-1 rounded-full bg-tertiary opacity-50" }),
          /* @__PURE__ */ jsx116("span", { children: readingTime })
        ] })
      ] })
    ] })
  ] }) });
}
function WhitepaperHeaderCompact({
  documentType,
  title,
  date
}) {
  return /* @__PURE__ */ jsx116("header", { className: "border-b border-recessed", children: /* @__PURE__ */ jsxs102("div", { className: "max-w-content-narrow mx-auto bg-surface", children: [
    /* @__PURE__ */ jsx116("div", { className: "h-0.5 bg-accent" }),
    /* @__PURE__ */ jsxs102("div", { className: "px-8 py-8 sm:py-10", children: [
      /* @__PURE__ */ jsxs102("div", { className: "flex items-start justify-between gap-6 mb-4", children: [
        /* @__PURE__ */ jsx116("div", { className: "text-label-xs font-sans font-semibold uppercase text-accent", children: documentType }),
        date && /* @__PURE__ */ jsx116("div", { className: "text-xs font-sans text-tertiary whitespace-nowrap", children: date })
      ] }),
      /* @__PURE__ */ jsx116("h1", { className: "text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold text-primary leading-tight tracking-headline-serif", children: title })
    ] })
  ] }) });
}

// src/sections/WebsiteNav.tsx
import React34, { useState as useState25, useRef as useRef18, useEffect as useEffect19, useCallback as useCallback15 } from "react";
import { MenuIcon, XIcon as XIcon6, ChevronDownIcon as ChevronDownIcon10, ArrowUpRightIcon } from "lucide-react";
import { Fragment as Fragment26, jsx as jsx117, jsxs as jsxs103 } from "react/jsx-runtime";
function DefaultLink2({
  href,
  children,
  className,
  onClick,
  ...rest
}) {
  return /* @__PURE__ */ jsx117("a", { href, className, onClick, ...rest, children });
}
function defaultIsActive(item, currentPath) {
  if (!currentPath) return false;
  if (item.href === "/") return currentPath === "/";
  return currentPath.startsWith(item.href);
}
function isGroupActive(item, currentPath, isItemActive) {
  if (isItemActive(item, currentPath)) return true;
  if (item.children) {
    return item.children.some((child) => isItemActive(child, currentPath));
  }
  return false;
}
function NavBadge({ text }) {
  return /* @__PURE__ */ jsx117("span", { className: "ml-1.5 inline-flex items-center px-1.5 py-0.5 text-label-xs font-sans font-semibold uppercase leading-none rounded-sm bg-accent/10 text-accent", children: text });
}
function externalLinkProps(item) {
  return item.external ? { target: "_blank", rel: "noopener noreferrer" } : {};
}
function ExternalMark() {
  return /* @__PURE__ */ jsxs103(Fragment26, { children: [
    /* @__PURE__ */ jsx117(ArrowUpRightIcon, { className: "w-3 h-3 shrink-0", "aria-hidden": "true" }),
    /* @__PURE__ */ jsx117("span", { className: "sr-only", children: "(opens in a new tab)" })
  ] });
}
function DesktopDropdown({
  item,
  isActive,
  isItemActive,
  currentPath,
  LinkComponent,
  onNavigate
}) {
  const [isOpen, setIsOpen] = useState25(false);
  const containerRef = useRef18(null);
  const timeoutRef = useRef18(null);
  const handleMouseEnter = useCallback15(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsOpen(true);
  }, []);
  const handleMouseLeave = useCallback15(() => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  }, []);
  const handleKeyDown = useCallback15((e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      const trigger = containerRef.current?.querySelector("a, button");
      trigger?.focus();
    }
  }, []);
  useEffect19(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  const handleItemClick = (child) => {
    if (onNavigate) {
      onNavigate(child);
    }
    setIsOpen(false);
  };
  return /* @__PURE__ */ jsxs103(
    "div",
    {
      ref: containerRef,
      className: "relative",
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onKeyDown: handleKeyDown,
      children: [
        /* @__PURE__ */ jsxs103(
          LinkComponent,
          {
            href: item.href,
            onClick: () => onNavigate?.(item),
            className: `text-xs font-sans uppercase tracking-[0.05em] whitespace-nowrap pb-0.5 border-b-2 flex items-center gap-1 transition-all duration-200 ${isActive ? "text-primary border-accent" : "text-tertiary hover:text-primary border-transparent"}`,
            "aria-expanded": isOpen,
            "aria-haspopup": "true",
            children: [
              item.label,
              item.badge && /* @__PURE__ */ jsx117(NavBadge, { text: item.badge }),
              /* @__PURE__ */ jsx117(
                ChevronDownIcon10,
                {
                  className: `w-3 h-3 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`,
                  "aria-hidden": "true"
                }
              )
            ]
          }
        ),
        isOpen && item.children && /* @__PURE__ */ jsx117(
          "div",
          {
            className: "absolute top-full left-0 pt-2 w-52",
            role: "menu",
            "aria-label": `${item.label} submenu`,
            children: /* @__PURE__ */ jsx117("div", { className: "bg-surface border border-recessed rounded-sm shadow-lg py-2", children: item.children.map((child) => {
              const childActive = isItemActive(child, currentPath);
              return /* @__PURE__ */ jsxs103(
                LinkComponent,
                {
                  href: child.href,
                  role: "menuitem",
                  onClick: () => handleItemClick(child),
                  ...externalLinkProps(child),
                  className: `${child.external ? "flex items-center gap-1" : "block"} px-4 py-2 text-sm font-sans transition-colors ${childActive ? "text-accent bg-canvas" : "text-secondary hover:text-primary hover:bg-canvas"}`,
                  children: [
                    child.label,
                    child.badge && /* @__PURE__ */ jsx117(NavBadge, { text: child.badge }),
                    child.external && /* @__PURE__ */ jsx117(ExternalMark, {})
                  ]
                },
                child.href
              );
            }) })
          }
        )
      ]
    }
  );
}
function MobileMenu({
  items,
  actions,
  currentPath,
  isItemActive,
  LinkComponent,
  onNavigate,
  onClose
}) {
  const handleItemClick = (item) => {
    if (onNavigate) {
      onNavigate(item);
    }
    onClose();
  };
  return /* @__PURE__ */ jsx117(
    "div",
    {
      className: "lg:hidden mt-6 pb-4 border-t border-recessed pt-6",
      role: "navigation",
      "aria-label": "Mobile navigation",
      children: /* @__PURE__ */ jsxs103("div", { className: "flex flex-col gap-2", children: [
        items.map((item) => {
          const hasChildren = item.children && item.children.length > 0;
          if (!hasChildren) {
            const active = isItemActive(item, currentPath);
            return /* @__PURE__ */ jsxs103(
              LinkComponent,
              {
                href: item.href,
                onClick: () => handleItemClick(item),
                ...externalLinkProps(item),
                className: `${item.external ? "flex items-center gap-1" : ""} text-sm font-sans font-semibold py-2 pl-4 cursor-pointer transition-all duration-200 ${active ? "text-accent border-l-2 border-accent" : "text-primary hover:text-accent border-l-2 border-transparent"}`,
                children: [
                  item.label,
                  item.badge && /* @__PURE__ */ jsx117(NavBadge, { text: item.badge }),
                  item.external && /* @__PURE__ */ jsx117(ExternalMark, {})
                ]
              },
              item.href
            );
          }
          return /* @__PURE__ */ jsxs103("div", { children: [
            /* @__PURE__ */ jsx117("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary py-2 pl-4 mt-2", children: item.label }),
            item.children.map((child) => {
              const childActive = isItemActive(child, currentPath);
              return /* @__PURE__ */ jsxs103(
                LinkComponent,
                {
                  href: child.href,
                  onClick: () => handleItemClick(child),
                  ...externalLinkProps(child),
                  className: `${child.external ? "flex items-center gap-1" : "block"} text-sm font-sans py-2 pl-8 cursor-pointer transition-all duration-200 ${childActive ? "text-accent border-l-2 border-accent" : "text-secondary hover:text-primary border-l-2 border-transparent"}`,
                  children: [
                    child.label,
                    child.badge && /* @__PURE__ */ jsx117(NavBadge, { text: child.badge }),
                    child.external && /* @__PURE__ */ jsx117(ExternalMark, {})
                  ]
                },
                child.href
              );
            })
          ] }, item.href);
        }),
        actions && /* @__PURE__ */ jsx117("div", { className: "mt-2", children: actions })
      ] })
    }
  );
}
function WebsiteNav({
  logo,
  items,
  actions,
  linkComponent: LinkComponent = DefaultLink2,
  sticky = true,
  transparent = false,
  currentPath = "",
  isItemActive: customIsActive,
  onNavigate,
  onMobileMenuToggle,
  className = ""
}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState25(false);
  const activeCheck = customIsActive || defaultIsActive;
  useEffect19(() => {
    if (!mobileMenuOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setMobileMenuOpen(false);
        onMobileMenuToggle?.(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [mobileMenuOpen, onMobileMenuToggle]);
  useEffect19(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && mobileMenuOpen) {
        setMobileMenuOpen(false);
        onMobileMenuToggle?.(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [mobileMenuOpen, onMobileMenuToggle]);
  const toggleMobileMenu = () => {
    const nextState = !mobileMenuOpen;
    setMobileMenuOpen(nextState);
    onMobileMenuToggle?.(nextState);
  };
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    onMobileMenuToggle?.(false);
  };
  const headerBackground = transparent ? "bg-transparent" : "bg-canvas/95 backdrop-blur-[10px]";
  return /* @__PURE__ */ jsx117(
    "header",
    {
      className: `
        ${sticky ? "sticky top-0" : ""}
        ${headerBackground}
        z-50 py-6
        ${className}
      `.trim(),
      children: /* @__PURE__ */ jsxs103("nav", { className: "max-w-content-wide mx-auto px-8", "aria-label": "Main navigation", children: [
        /* @__PURE__ */ jsxs103("div", { className: "flex justify-between items-center", children: [
          /* @__PURE__ */ jsx117("div", { className: "flex items-center", children: logo }),
          /* @__PURE__ */ jsxs103("div", { className: "hidden lg:flex items-baseline gap-6", children: [
            items.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              const active = isGroupActive(item, currentPath, activeCheck);
              if (hasChildren) {
                return /* @__PURE__ */ jsx117(
                  DesktopDropdown,
                  {
                    item,
                    isActive: active,
                    isItemActive: activeCheck,
                    currentPath,
                    LinkComponent,
                    onNavigate
                  },
                  item.href
                );
              }
              const standaloneLink = /* @__PURE__ */ jsxs103(
                LinkComponent,
                {
                  href: item.href,
                  onClick: () => onNavigate?.(item),
                  ...externalLinkProps(item),
                  className: `${item.external ? "inline-flex items-center gap-1" : ""} text-xs font-sans uppercase tracking-[0.05em] whitespace-nowrap pb-0.5 border-b-2 transition-all duration-200 ${active ? "text-primary border-accent" : "text-tertiary hover:text-primary border-transparent"}`,
                  children: [
                    item.label,
                    item.badge && /* @__PURE__ */ jsx117(NavBadge, { text: item.badge }),
                    item.external && /* @__PURE__ */ jsx117(ExternalMark, {})
                  ]
                }
              );
              if (item.external) {
                return /* @__PURE__ */ jsx117(
                  "span",
                  {
                    className: "flex items-baseline border-l border-recessed pl-6",
                    children: standaloneLink
                  },
                  item.href
                );
              }
              return /* @__PURE__ */ jsx117(React34.Fragment, { children: standaloneLink }, item.href);
            }),
            actions && /* @__PURE__ */ jsx117("div", { className: "ml-4", children: actions })
          ] }),
          /* @__PURE__ */ jsx117(
            "button",
            {
              className: "lg:hidden p-2 text-primary",
              onClick: toggleMobileMenu,
              "aria-expanded": mobileMenuOpen,
              "aria-controls": "website-nav-mobile-menu",
              "aria-label": mobileMenuOpen ? "Close navigation menu" : "Open navigation menu",
              children: mobileMenuOpen ? /* @__PURE__ */ jsx117(XIcon6, { className: "w-6 h-6" }) : /* @__PURE__ */ jsx117(MenuIcon, { className: "w-6 h-6" })
            }
          )
        ] }),
        mobileMenuOpen && /* @__PURE__ */ jsx117("div", { id: "website-nav-mobile-menu", children: /* @__PURE__ */ jsx117(
          MobileMenu,
          {
            items,
            actions,
            currentPath,
            isItemActive: activeCheck,
            LinkComponent,
            onNavigate,
            onClose: closeMobileMenu
          }
        ) })
      ] })
    }
  );
}

// src/typography/CTA.tsx
import { forwardRef as forwardRef8, useCallback as useCallback16 } from "react";
import { jsx as jsx118, jsxs as jsxs104 } from "react/jsx-runtime";
var VARIANT_CLASSES3 = {
  primary: {
    container: "bg-surface p-8 sm:p-12 rounded-sm shadow-sm text-center",
    button: "bg-accent text-white hover:bg-accent-hover",
    secondaryButton: "bg-transparent text-accent border border-accent hover:bg-accent/5"
  },
  secondary: {
    container: "bg-canvas p-8 sm:p-10 rounded-sm text-center",
    button: "bg-accent text-white hover:bg-accent-hover",
    secondaryButton: "bg-transparent text-accent border border-accent hover:bg-accent/5"
  },
  minimal: {
    container: "py-8 text-center",
    button: "bg-accent text-white hover:bg-accent-hover",
    secondaryButton: "text-accent hover:underline"
  }
};
var SIZE_CLASSES4 = {
  large: {
    headline: "text-2xl sm:text-3xl lg:text-heading-section-lg",
    description: "text-base sm:text-lg",
    button: "py-3 px-6 text-sm",
    gap: "mb-4 sm:mb-6"
  },
  medium: {
    headline: "text-xl sm:text-2xl lg:text-3xl",
    description: "text-base",
    button: "py-2.5 px-5 text-sm",
    gap: "mb-3 sm:mb-4"
  },
  small: {
    headline: "text-lg sm:text-xl lg:text-2xl",
    description: "text-sm",
    button: "py-2 px-4 text-xs",
    gap: "mb-2 sm:mb-3"
  }
};
var CTA = forwardRef8(
  function CTA2({
    headline,
    description,
    action,
    secondaryAction,
    variant = "primary",
    size = "large",
    trackingId,
    onTrack,
    LinkComponent,
    className = "",
    children,
    ...props
  }, ref) {
    const variantStyles = VARIANT_CLASSES3[variant];
    const sizeStyles = SIZE_CLASSES4[size];
    const handleTrack = useCallback16(
      (actionType, clickedAction) => {
        if (trackingId && onTrack) {
          onTrack({
            trackingId,
            action: actionType,
            href: clickedAction.href,
            label: clickedAction.label,
            timestamp: Date.now()
          });
        }
        if (trackingId && typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("cta:interaction", {
              detail: {
                trackingId,
                action: actionType,
                href: clickedAction.href,
                label: clickedAction.label
              }
            })
          );
        }
      },
      [trackingId, onTrack]
    );
    const renderButton = (buttonAction, isPrimary) => {
      const buttonClasses = [
        "inline-flex items-center gap-2 rounded-sm font-sans font-semibold transition-all duration-200",
        sizeStyles.button,
        isPrimary ? variantStyles.button : variantStyles.secondaryButton
      ].join(" ");
      const handleClick = () => {
        handleTrack(isPrimary ? "primary_click" : "secondary_click", buttonAction);
      };
      if (buttonAction.external) {
        return /* @__PURE__ */ jsx118(
          "a",
          {
            href: buttonAction.href,
            target: "_blank",
            rel: "noopener noreferrer",
            className: buttonClasses,
            onClick: handleClick,
            children: buttonAction.label
          }
        );
      }
      if (LinkComponent) {
        return /* @__PURE__ */ jsx118(LinkComponent, { href: buttonAction.href, className: buttonClasses, onClick: handleClick, children: buttonAction.label });
      }
      return /* @__PURE__ */ jsx118("a", { href: buttonAction.href, className: buttonClasses, onClick: handleClick, children: buttonAction.label });
    };
    return /* @__PURE__ */ jsxs104(
      "section",
      {
        ref,
        className: `${variantStyles.container} ${className}`,
        "data-tracking-id": trackingId,
        ...props,
        children: [
          /* @__PURE__ */ jsx118(
            "h2",
            {
              className: `font-serif font-semibold text-primary leading-snug ${sizeStyles.headline} ${sizeStyles.gap}`,
              children: headline
            }
          ),
          description && /* @__PURE__ */ jsx118(
            "p",
            {
              className: `font-serif text-secondary ${sizeStyles.description} mb-8`,
              children: description
            }
          ),
          children && /* @__PURE__ */ jsx118("div", { className: "mb-8", children }),
          /* @__PURE__ */ jsxs104("div", { className: "flex flex-wrap items-center justify-center gap-4", children: [
            renderButton(action, true),
            secondaryAction && renderButton(secondaryAction, false)
          ] })
        ]
      }
    );
  }
);

// src/typography/LeadText.tsx
import { forwardRef as forwardRef9 } from "react";
import { jsx as jsx119 } from "react/jsx-runtime";
var SIZE_CLASSES5 = {
  sm: "text-sm",
  base: "text-base",
  lg: "text-lg"
};
var COLOR_CLASSES4 = {
  primary: "text-primary",
  secondary: "text-secondary",
  tertiary: "text-tertiary",
  inherit: ""
};
var LeadText = forwardRef9(
  function LeadText2({ size = "lg", color = "secondary", inline = false, className = "", children, ...props }, ref) {
    const classes = [
      "font-serif",
      SIZE_CLASSES5[size],
      COLOR_CLASSES4[color],
      className
    ].filter(Boolean).join(" ");
    if (inline) {
      return /* @__PURE__ */ jsx119("span", { className: classes, ...props, children });
    }
    return /* @__PURE__ */ jsx119("p", { ref, className: classes, ...props, children });
  }
);

// src/typography/Blockquote.tsx
import { Fragment as Fragment27, jsx as jsx120, jsxs as jsxs105 } from "react/jsx-runtime";
function Blockquote({
  quote,
  author,
  role,
  company,
  accentStyle = "left",
  size = "default",
  showAvatar = false,
  avatar,
  className = ""
}) {
  const sizeStyles = {
    default: {
      padding: "p-6",
      quote: "text-base font-serif text-secondary leading-body-relaxed italic mb-3",
      attribution: "text-sm font-sans text-tertiary"
    },
    featured: {
      padding: "p-8 sm:p-10 lg:p-12",
      quote: "text-xl sm:text-2xl lg:text-heading-section font-serif text-primary leading-relaxed italic mb-4",
      attribution: "text-base font-sans text-secondary"
    }
  };
  const currentSize = sizeStyles[size];
  const needsOverflow = ["top", "corner", "top-warm", "top-corner", "top-border"].includes(accentStyle);
  const baseStyles = `rounded-sm transition-all duration-200 ${needsOverflow ? "overflow-hidden relative" : ""}`;
  const accentBgStyles = {
    // Minimal: warm background only, subtle border
    none: "bg-canvas-warm border border-recessed",
    // Classic editorial: surface with left accent
    left: "bg-surface border-l-4 border-tan-dark shadow-sm",
    // Top accent bar: surface background
    top: "bg-surface border border-recessed",
    // Corner brackets: surface with subtle border
    corner: "bg-surface border border-recessed",
    // Warm border + shadow (most original): NO accent line, tan border, refined shadow
    tint: "border-2 border-tan-warm shadow-md",
    // Inset shadow (Bloomberg): subtle inset accent ring + neutral drop shadow.
    // design-token-skip: 0_1px_3px_rgba(0,0,0,0.1) is the canonical boxShadow.sm
    // black-alpha drop shadow; no CSS-variable equivalent exists for neutral shadows.
    inset: "bg-surface shadow-[inset_0_0_0_1px_rgb(var(--color-accent)/0.15),0_1px_3px_rgba(0,0,0,0.1)]",
    // Aaim Variation 1: thin top accent + warm background
    "top-warm": "bg-elevated-warm border border-recessed",
    // Aaim Variation 2: hybrid top + corner - warm neutral background with accent elements
    "top-corner": "bg-elevated-warm",
    // Aaim Variation 3: thin top accent + warm tan border
    "top-border": "border-2 border-tan-warm"
  };
  const combinedStyles = `${baseStyles} ${accentBgStyles[accentStyle]} ${currentSize.padding} ${className}`;
  const renderAccentElements = () => {
    switch (accentStyle) {
      case "top":
        return /* @__PURE__ */ jsx120("div", { className: "absolute top-0 left-0 right-0 h-[2px] bg-accent" });
      case "top-warm":
      case "top-border":
        return /* @__PURE__ */ jsx120("div", { className: "absolute top-0 left-0 right-0 h-[1px] bg-accent" });
      case "corner":
        return /* @__PURE__ */ jsxs105(Fragment27, { children: [
          /* @__PURE__ */ jsx120("div", { className: "absolute top-0 left-0 w-5 h-5 border-t-2 border-l-2 border-accent" }),
          /* @__PURE__ */ jsx120("div", { className: "absolute bottom-0 right-0 w-5 h-5 border-b-2 border-r-2 border-accent" })
        ] });
      case "top-corner":
        return /* @__PURE__ */ jsxs105(Fragment27, { children: [
          /* @__PURE__ */ jsx120("div", { className: "absolute top-0 left-0 right-0 h-[2px] bg-tan-dark" }),
          /* @__PURE__ */ jsx120("div", { className: "absolute top-[2px] left-0 w-[2px] h-4 bg-tan-dark" })
        ] });
      default:
        return null;
    }
  };
  if (showAvatar) {
    return /* @__PURE__ */ jsxs105("div", { className: combinedStyles, children: [
      renderAccentElements(),
      /* @__PURE__ */ jsxs105("div", { className: "flex items-start gap-4", children: [
        /* @__PURE__ */ jsx120("div", { className: "w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden", children: avatar ? /* @__PURE__ */ jsx120("img", { src: avatar, alt: author, className: "w-full h-full object-cover" }) : /* @__PURE__ */ jsx120("span", { className: "text-base font-sans font-semibold text-accent", children: author.split(" ").map((n) => n[0]).join("") }) }),
        /* @__PURE__ */ jsxs105("div", { className: "flex-1", children: [
          /* @__PURE__ */ jsxs105("p", { className: currentSize.quote, children: [
            '"',
            quote,
            '"'
          ] }),
          /* @__PURE__ */ jsxs105("div", { className: "flex items-center gap-2 text-sm font-sans", children: [
            /* @__PURE__ */ jsx120("span", { className: "font-semibold text-primary", children: author }),
            (role || company) && /* @__PURE__ */ jsx120("span", { className: "text-tertiary", children: "\u2022" }),
            role && /* @__PURE__ */ jsx120("span", { className: "text-tertiary", children: role }),
            role && company && /* @__PURE__ */ jsx120("span", { className: "text-tertiary", children: "," }),
            company && /* @__PURE__ */ jsx120("span", { className: "text-tertiary", children: company })
          ] })
        ] })
      ] })
    ] });
  }
  return /* @__PURE__ */ jsxs105("blockquote", { className: combinedStyles, children: [
    renderAccentElements(),
    /* @__PURE__ */ jsxs105("p", { className: currentSize.quote, children: [
      '"',
      quote,
      '"'
    ] }),
    /* @__PURE__ */ jsxs105("footer", { className: currentSize.attribution, children: [
      "\u2014 ",
      author,
      role && `, ${role}`,
      company && `, ${company}`
    ] })
  ] });
}

// src/social-cards/SocialQuoteCard.tsx
import { jsx as jsx121, jsxs as jsxs106 } from "react/jsx-runtime";
function SocialQuoteCard({
  quote,
  author,
  authorTitle,
  format = "linkedin",
  logoUrl = "https://cdn.aaim.app/logos/aaim-master-grey.svg",
  brandUrl = "aaim.com"
}) {
  const height = format === "twitter" ? 675 : 630;
  const quoteFontSize = quote.length > 200 ? 32 : quote.length > 100 ? 40 : 48;
  return /* @__PURE__ */ jsxs106(
    "div",
    {
      style: {
        width: 1200,
        height,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        backgroundColor: "#F5F1E8",
        fontFamily: "'Source Serif 4', Georgia, serif",
        padding: "64px 80px",
        position: "relative"
      },
      children: [
        /* @__PURE__ */ jsx121(
          "div",
          {
            style: {
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              backgroundColor: "#D2001F"
            }
          }
        ),
        /* @__PURE__ */ jsx121(
          "div",
          {
            style: {
              position: "absolute",
              top: 0,
              left: 0,
              width: 2,
              height: 48,
              backgroundColor: "#D2001F"
            }
          }
        ),
        /* @__PURE__ */ jsxs106(
          "div",
          {
            style: {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            },
            children: [
              /* @__PURE__ */ jsx121(
                "img",
                {
                  src: logoUrl,
                  alt: "Aaim",
                  style: {
                    height: 32,
                    width: "auto"
                  }
                }
              ),
              /* @__PURE__ */ jsx121(
                "span",
                {
                  style: {
                    fontSize: 16,
                    fontFamily: "'Lato', sans-serif",
                    fontWeight: 500,
                    color: "#6B5D54",
                    letterSpacing: "0.02em"
                  },
                  children: brandUrl
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsxs106(
          "div",
          {
            style: {
              display: "flex",
              flexDirection: "column",
              flex: 1,
              justifyContent: "center",
              paddingTop: 40,
              paddingBottom: 40
            },
            children: [
              /* @__PURE__ */ jsxs106(
                "div",
                {
                  style: {
                    fontSize: quoteFontSize,
                    fontStyle: "italic",
                    color: "#000000",
                    lineHeight: 1.35,
                    maxWidth: 960
                  },
                  children: [
                    /* @__PURE__ */ jsx121("span", { style: { color: "#C4B299" }, children: "\u201C" }),
                    quote,
                    /* @__PURE__ */ jsx121("span", { style: { color: "#C4B299" }, children: "\u201D" })
                  ]
                }
              ),
              /* @__PURE__ */ jsxs106(
                "div",
                {
                  style: {
                    marginTop: 40,
                    display: "flex",
                    alignItems: "baseline",
                    gap: 16
                  },
                  children: [
                    /* @__PURE__ */ jsx121(
                      "div",
                      {
                        style: {
                          width: 32,
                          height: 1,
                          backgroundColor: "#C4B299",
                          marginTop: 10
                        }
                      }
                    ),
                    /* @__PURE__ */ jsxs106("div", { style: { display: "flex", flexDirection: "column", gap: 2 }, children: [
                      /* @__PURE__ */ jsx121(
                        "span",
                        {
                          style: {
                            fontSize: 20,
                            fontFamily: "'Lato', sans-serif",
                            fontWeight: 600,
                            color: "#000000",
                            letterSpacing: "0.01em"
                          },
                          children: author
                        }
                      ),
                      authorTitle && /* @__PURE__ */ jsx121(
                        "span",
                        {
                          style: {
                            fontSize: 16,
                            fontFamily: "'Lato', sans-serif",
                            fontWeight: 400,
                            color: "#6B5D54"
                          },
                          children: authorTitle
                        }
                      )
                    ] })
                  ]
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx121(
          "div",
          {
            style: {
              width: "100%",
              height: 1,
              backgroundColor: "#D5D1C8"
            }
          }
        )
      ]
    }
  );
}

// src/social-cards/SocialAnnouncementBanner.tsx
import { jsx as jsx122, jsxs as jsxs107 } from "react/jsx-runtime";
var announcementTypeConfig = {
  launch: { label: "PRODUCT LAUNCH", bgColor: "#D2001F" },
  partnership: { label: "PARTNERSHIP", bgColor: "#5A6B5A" },
  milestone: { label: "MILESTONE", bgColor: "#D2001F" },
  funding: { label: "FUNDING ANNOUNCEMENT", bgColor: "#5A6B5A" },
  news: { label: "NEWS", bgColor: "#3A3330" }
};
function SocialAnnouncementBanner({
  headline,
  subheadline,
  type = "news",
  date,
  format = "linkedin",
  logoUrl = "https://cdn.aaim.app/logos/aaim-master-grey.svg",
  tagline = "Making modern wealth lendable",
  brandUrl = "aaim.com"
}) {
  const height = format === "twitter" ? 675 : 630;
  const typeConfig = announcementTypeConfig[type];
  const headlineFontSize = headline.length > 60 ? 48 : headline.length > 40 ? 56 : 64;
  return /* @__PURE__ */ jsxs107(
    "div",
    {
      style: {
        width: 1200,
        height,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        backgroundColor: "#F5F1E8",
        fontFamily: "'Source Serif 4', Georgia, serif",
        padding: "64px 80px",
        position: "relative"
      },
      children: [
        /* @__PURE__ */ jsx122(
          "div",
          {
            style: {
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              backgroundColor: "#D2001F"
            }
          }
        ),
        /* @__PURE__ */ jsx122(
          "div",
          {
            style: {
              position: "absolute",
              top: 0,
              left: 0,
              width: 2,
              height: 48,
              backgroundColor: "#D2001F"
            }
          }
        ),
        /* @__PURE__ */ jsxs107(
          "div",
          {
            style: {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            },
            children: [
              /* @__PURE__ */ jsx122(
                "img",
                {
                  src: logoUrl,
                  alt: "Aaim",
                  style: {
                    height: 32,
                    width: "auto"
                  }
                }
              ),
              /* @__PURE__ */ jsxs107("div", { style: { display: "flex", alignItems: "center", gap: 20 }, children: [
                date && /* @__PURE__ */ jsx122(
                  "span",
                  {
                    style: {
                      fontSize: 14,
                      fontFamily: "'Lato', sans-serif",
                      fontWeight: 400,
                      color: "#6B5D54"
                    },
                    children: date
                  }
                ),
                /* @__PURE__ */ jsx122(
                  "span",
                  {
                    style: {
                      fontSize: 11,
                      fontFamily: "'Lato', sans-serif",
                      fontWeight: 600,
                      letterSpacing: "0.08em",
                      color: typeConfig.bgColor,
                      textTransform: "uppercase"
                    },
                    children: typeConfig.label
                  }
                )
              ] })
            ]
          }
        ),
        /* @__PURE__ */ jsxs107(
          "div",
          {
            style: {
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              flex: 1,
              justifyContent: "center",
              paddingTop: 24,
              paddingBottom: 24
            },
            children: [
              /* @__PURE__ */ jsx122(
                "div",
                {
                  style: {
                    fontSize: headlineFontSize,
                    fontWeight: 600,
                    color: "#000000",
                    lineHeight: 1.15,
                    maxWidth: 1e3,
                    letterSpacing: "-0.01em"
                  },
                  children: headline
                }
              ),
              subheadline && /* @__PURE__ */ jsx122(
                "div",
                {
                  style: {
                    fontSize: 26,
                    fontWeight: 400,
                    color: "#3A3330",
                    lineHeight: 1.4,
                    maxWidth: 900,
                    marginTop: 20
                  },
                  children: subheadline
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsxs107("div", { style: { display: "flex", flexDirection: "column", gap: 16 }, children: [
          /* @__PURE__ */ jsx122(
            "div",
            {
              style: {
                width: "100%",
                height: 1,
                backgroundColor: "#D5D1C8"
              }
            }
          ),
          /* @__PURE__ */ jsxs107(
            "div",
            {
              style: {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline"
              },
              children: [
                /* @__PURE__ */ jsx122(
                  "span",
                  {
                    style: {
                      fontSize: 14,
                      fontFamily: "'Lato', sans-serif",
                      fontWeight: 400,
                      color: "#6B5D54",
                      fontStyle: "italic"
                    },
                    children: tagline
                  }
                ),
                /* @__PURE__ */ jsx122(
                  "span",
                  {
                    style: {
                      fontSize: 16,
                      fontFamily: "'Lato', sans-serif",
                      fontWeight: 500,
                      color: "#6B5D54",
                      letterSpacing: "0.02em"
                    },
                    children: brandUrl
                  }
                )
              ]
            }
          )
        ] })
      ]
    }
  );
}

// src/social-cards/SocialMetricCard.tsx
import { jsx as jsx123, jsxs as jsxs108 } from "react/jsx-runtime";
function SocialMetricCard({
  value,
  label,
  context,
  source,
  format = "linkedin",
  logoUrl = "https://cdn.aaim.app/logos/aaim-master-grey.svg",
  brandUrl = "aaim.com"
}) {
  const height = format === "twitter" ? 675 : 630;
  return /* @__PURE__ */ jsxs108(
    "div",
    {
      style: {
        width: 1200,
        height,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        backgroundColor: "#F5F1E8",
        fontFamily: "'Source Serif 4', Georgia, serif",
        padding: "64px 80px",
        position: "relative"
      },
      children: [
        /* @__PURE__ */ jsx123(
          "div",
          {
            style: {
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              backgroundColor: "#D2001F"
            }
          }
        ),
        /* @__PURE__ */ jsx123(
          "div",
          {
            style: {
              position: "absolute",
              top: 0,
              left: 0,
              width: 2,
              height: 48,
              backgroundColor: "#D2001F"
            }
          }
        ),
        /* @__PURE__ */ jsxs108(
          "div",
          {
            style: {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            },
            children: [
              /* @__PURE__ */ jsx123(
                "img",
                {
                  src: logoUrl,
                  alt: "Aaim",
                  style: {
                    height: 32,
                    width: "auto"
                  }
                }
              ),
              /* @__PURE__ */ jsx123(
                "span",
                {
                  style: {
                    fontSize: 16,
                    fontFamily: "'Lato', sans-serif",
                    fontWeight: 500,
                    color: "#6B5D54",
                    letterSpacing: "0.02em"
                  },
                  children: brandUrl
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsxs108(
          "div",
          {
            style: {
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              flex: 1,
              justifyContent: "center",
              paddingTop: 24,
              paddingBottom: 24
            },
            children: [
              /* @__PURE__ */ jsx123(
                "div",
                {
                  style: {
                    fontSize: 140,
                    fontWeight: 600,
                    color: "#000000",
                    lineHeight: 1,
                    letterSpacing: "-0.02em"
                  },
                  children: value
                }
              ),
              /* @__PURE__ */ jsx123(
                "div",
                {
                  style: {
                    fontSize: 32,
                    fontWeight: 400,
                    color: "#3A3330",
                    lineHeight: 1.3,
                    marginTop: 20,
                    maxWidth: 900
                  },
                  children: label
                }
              ),
              context && /* @__PURE__ */ jsx123(
                "div",
                {
                  style: {
                    fontSize: 22,
                    fontWeight: 400,
                    color: "#6B5D54",
                    lineHeight: 1.45,
                    maxWidth: 750,
                    marginTop: 16
                  },
                  children: context
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsxs108("div", { style: { display: "flex", flexDirection: "column", gap: 16 }, children: [
          /* @__PURE__ */ jsx123(
            "div",
            {
              style: {
                width: "100%",
                height: 1,
                backgroundColor: "#D5D1C8"
              }
            }
          ),
          source && /* @__PURE__ */ jsxs108(
            "span",
            {
              style: {
                fontSize: 13,
                fontFamily: "'Lato', sans-serif",
                fontWeight: 400,
                color: "#6B5D54"
              },
              children: [
                "Source: ",
                source
              ]
            }
          )
        ] })
      ]
    }
  );
}

// src/social-cards/SocialArticleCard.tsx
import { jsx as jsx124, jsxs as jsxs109 } from "react/jsx-runtime";
var contentTypeConfig = {
  blog: { label: "BLOG", color: "#5A6B5A" },
  whitepaper: { label: "WHITEPAPER", color: "#3A3330" },
  "case-study": { label: "CASE STUDY", color: "#5A6B5A" },
  webinar: { label: "WEBINAR", color: "#D2001F" },
  "press-release": { label: "PRESS RELEASE", color: "#3A3330" },
  news: { label: "NEWS", color: "#3A3330" }
};
function SocialArticleCard({
  headline,
  contentType = "blog",
  categoryLabel,
  subtitle,
  date,
  author,
  format = "linkedin",
  logoUrl = "https://cdn.aaim.app/logos/aaim-master-grey.svg",
  brandUrl = "aaim.com"
}) {
  const height = format === "twitter" ? 675 : 630;
  const typeConfig = contentTypeConfig[contentType];
  const displayLabel = categoryLabel || typeConfig.label;
  const headlineFontSize = headline.length > 100 ? 40 : headline.length > 70 ? 48 : headline.length > 40 ? 56 : 64;
  return /* @__PURE__ */ jsxs109(
    "div",
    {
      style: {
        width: 1200,
        height,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        backgroundColor: "#F5F1E8",
        fontFamily: "'Source Serif 4', Georgia, serif",
        padding: "64px 80px",
        position: "relative"
      },
      children: [
        /* @__PURE__ */ jsx124(
          "div",
          {
            style: {
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              backgroundColor: "#D2001F"
            }
          }
        ),
        /* @__PURE__ */ jsx124(
          "div",
          {
            style: {
              position: "absolute",
              top: 0,
              left: 0,
              width: 2,
              height: 48,
              backgroundColor: "#D2001F"
            }
          }
        ),
        /* @__PURE__ */ jsxs109(
          "div",
          {
            style: {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            },
            children: [
              /* @__PURE__ */ jsx124(
                "img",
                {
                  src: logoUrl,
                  alt: "Aaim",
                  style: {
                    height: 32,
                    width: "auto"
                  }
                }
              ),
              /* @__PURE__ */ jsxs109("div", { style: { display: "flex", alignItems: "center", gap: 20 }, children: [
                date && /* @__PURE__ */ jsx124(
                  "span",
                  {
                    style: {
                      fontSize: 14,
                      fontFamily: "'Lato', sans-serif",
                      fontWeight: 400,
                      color: "#6B5D54"
                    },
                    children: date
                  }
                ),
                /* @__PURE__ */ jsx124(
                  "span",
                  {
                    style: {
                      fontSize: 11,
                      fontFamily: "'Lato', sans-serif",
                      fontWeight: 600,
                      letterSpacing: "0.08em",
                      color: typeConfig.color,
                      textTransform: "uppercase"
                    },
                    children: displayLabel
                  }
                )
              ] })
            ]
          }
        ),
        /* @__PURE__ */ jsxs109(
          "div",
          {
            style: {
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              flex: 1,
              justifyContent: "center",
              paddingTop: 24,
              paddingBottom: 24
            },
            children: [
              /* @__PURE__ */ jsx124(
                "div",
                {
                  style: {
                    fontSize: headlineFontSize,
                    fontWeight: 600,
                    color: "#000000",
                    lineHeight: 1.15,
                    maxWidth: 1e3,
                    letterSpacing: "-0.01em"
                  },
                  children: headline
                }
              ),
              subtitle && /* @__PURE__ */ jsx124(
                "div",
                {
                  style: {
                    fontSize: 24,
                    fontWeight: 400,
                    color: "#3A3330",
                    lineHeight: 1.4,
                    maxWidth: 900,
                    marginTop: 20
                  },
                  children: subtitle
                }
              ),
              author && /* @__PURE__ */ jsxs109(
                "div",
                {
                  style: {
                    fontSize: 18,
                    fontFamily: "'Lato', sans-serif",
                    fontWeight: 500,
                    color: "#6B5D54",
                    marginTop: 24
                  },
                  children: [
                    "By ",
                    author
                  ]
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsxs109("div", { style: { display: "flex", flexDirection: "column", gap: 16 }, children: [
          /* @__PURE__ */ jsx124(
            "div",
            {
              style: {
                width: "100%",
                height: 1,
                backgroundColor: "#D5D1C8"
              }
            }
          ),
          /* @__PURE__ */ jsx124(
            "div",
            {
              style: {
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "baseline"
              },
              children: /* @__PURE__ */ jsx124(
                "span",
                {
                  style: {
                    fontSize: 16,
                    fontFamily: "'Lato', sans-serif",
                    fontWeight: 500,
                    color: "#6B5D54",
                    letterSpacing: "0.02em"
                  },
                  children: brandUrl
                }
              )
            }
          )
        ] })
      ]
    }
  );
}

// src/social-cards/SocialArticleHeroCard.tsx
import { jsx as jsx125, jsxs as jsxs110 } from "react/jsx-runtime";
var contentTypeConfig2 = {
  blog: { label: "BLOG", color: "#5A6B5A" },
  whitepaper: { label: "WHITEPAPER", color: "#3A3330" },
  "case-study": { label: "CASE STUDY", color: "#5A6B5A" },
  webinar: { label: "WEBINAR", color: "#D2001F" },
  "press-release": { label: "PRESS RELEASE", color: "#3A3330" },
  news: { label: "NEWS", color: "#3A3330" }
};
function SocialArticleHeroCard({
  headline,
  contentType = "blog",
  heroImageUrl,
  excerpt,
  categoryLabel,
  ctaText = "Read on aaim.com",
  format = "linkedin",
  logoUrl = "https://cdn.aaim.app/logos/aaim-master-grey.svg",
  brandUrl = "aaim.com"
}) {
  const height = format === "twitter" ? 675 : 630;
  const typeConfig = contentTypeConfig2[contentType];
  const displayLabel = categoryLabel || typeConfig.label;
  const hasHeroImage = Boolean(heroImageUrl);
  const getHeadlineFontSize = () => {
    if (!hasHeroImage) {
      if (headline.length > 100) return 40;
      if (headline.length > 70) return 48;
      if (headline.length > 40) return 56;
      return 64;
    }
    if (headline.length > 100) return 32;
    if (headline.length > 70) return 36;
    if (headline.length > 40) return 42;
    return 48;
  };
  const headlineFontSize = getHeadlineFontSize();
  if (!hasHeroImage) {
    return /* @__PURE__ */ jsxs110(
      "div",
      {
        style: {
          width: 1200,
          height,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          backgroundColor: "#F5F1E8",
          fontFamily: "'Source Serif 4', Georgia, serif",
          padding: "64px 80px",
          position: "relative"
        },
        children: [
          /* @__PURE__ */ jsx125(
            "div",
            {
              style: {
                display: "flex",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 2,
                backgroundColor: "#D2001F"
              }
            }
          ),
          /* @__PURE__ */ jsx125(
            "div",
            {
              style: {
                display: "flex",
                position: "absolute",
                top: 0,
                left: 0,
                width: 2,
                height: 48,
                backgroundColor: "#D2001F"
              }
            }
          ),
          /* @__PURE__ */ jsxs110(
            "div",
            {
              style: {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              },
              children: [
                /* @__PURE__ */ jsx125(
                  "img",
                  {
                    src: logoUrl,
                    alt: "Aaim",
                    style: {
                      height: 32,
                      width: "auto"
                    }
                  }
                ),
                /* @__PURE__ */ jsx125(
                  "div",
                  {
                    style: {
                      display: "flex",
                      fontSize: 11,
                      fontFamily: "'Lato', sans-serif",
                      fontWeight: 700,
                      letterSpacing: "0.15em",
                      color: typeConfig.color,
                      textTransform: "uppercase"
                    },
                    children: displayLabel
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsxs110(
            "div",
            {
              style: {
                display: "flex",
                flexDirection: "column",
                flex: 1,
                justifyContent: "center",
                paddingTop: 24,
                paddingBottom: 24
              },
              children: [
                /* @__PURE__ */ jsx125(
                  "div",
                  {
                    style: {
                      display: "flex",
                      fontSize: headlineFontSize,
                      fontWeight: 600,
                      color: "#000000",
                      lineHeight: 1.15,
                      maxWidth: 1e3,
                      letterSpacing: "-0.045em"
                    },
                    children: headline
                  }
                ),
                excerpt && /* @__PURE__ */ jsx125(
                  "div",
                  {
                    style: {
                      display: "flex",
                      fontSize: 18,
                      fontWeight: 400,
                      color: "#3A3330",
                      lineHeight: 1.3,
                      maxWidth: 900,
                      marginTop: 20
                    },
                    children: excerpt
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsxs110("div", { style: { display: "flex", flexDirection: "column", gap: 16 }, children: [
            /* @__PURE__ */ jsx125(
              "div",
              {
                style: {
                  display: "flex",
                  width: "100%",
                  height: 1,
                  backgroundColor: "#D5D1C8"
                }
              }
            ),
            /* @__PURE__ */ jsxs110(
              "div",
              {
                style: {
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                },
                children: [
                  /* @__PURE__ */ jsxs110(
                    "div",
                    {
                      style: {
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 14,
                        fontFamily: "'Lato', sans-serif",
                        fontWeight: 700,
                        color: "#D2001F"
                      },
                      children: [
                        ctaText,
                        /* @__PURE__ */ jsx125("span", { style: { display: "flex", fontSize: 18 }, children: "\u2192" })
                      ]
                    }
                  ),
                  /* @__PURE__ */ jsx125(
                    "div",
                    {
                      style: {
                        display: "flex",
                        fontSize: 14,
                        fontFamily: "'Lato', sans-serif",
                        fontWeight: 400,
                        color: "#6B5D54",
                        letterSpacing: "0.02em"
                      },
                      children: brandUrl
                    }
                  )
                ]
              }
            )
          ] })
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs110(
    "div",
    {
      style: {
        width: 1200,
        height,
        display: "flex",
        backgroundColor: "#F5F1E8",
        fontFamily: "'Source Serif 4', Georgia, serif",
        position: "relative"
      },
      children: [
        /* @__PURE__ */ jsx125(
          "div",
          {
            style: {
              display: "flex",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              backgroundColor: "#D2001F",
              zIndex: 10
            }
          }
        ),
        /* @__PURE__ */ jsx125(
          "div",
          {
            style: {
              display: "flex",
              position: "absolute",
              top: 0,
              left: 0,
              width: 2,
              height: 48,
              backgroundColor: "#D2001F",
              zIndex: 10
            }
          }
        ),
        /* @__PURE__ */ jsxs110(
          "div",
          {
            style: {
              display: "flex",
              width: 720,
              height: "100%",
              position: "relative",
              overflow: "hidden"
            },
            children: [
              /* @__PURE__ */ jsx125(
                "img",
                {
                  src: heroImageUrl,
                  alt: "",
                  style: {
                    width: "100%",
                    height: "100%",
                    objectFit: "cover"
                  }
                }
              ),
              /* @__PURE__ */ jsx125(
                "div",
                {
                  style: {
                    display: "flex",
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: 80,
                    background: "linear-gradient(to right, transparent, #F5F1E8)"
                  }
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsxs110(
          "div",
          {
            style: {
              display: "flex",
              flexDirection: "column",
              width: 480,
              height: "100%",
              padding: 48,
              justifyContent: "space-between"
            },
            children: [
              /* @__PURE__ */ jsx125(
                "div",
                {
                  style: {
                    display: "flex",
                    fontSize: 10,
                    fontFamily: "'Lato', sans-serif",
                    fontWeight: 700,
                    letterSpacing: "0.15em",
                    color: typeConfig.color,
                    textTransform: "uppercase"
                  },
                  children: displayLabel
                }
              ),
              /* @__PURE__ */ jsxs110(
                "div",
                {
                  style: {
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    justifyContent: "center",
                    gap: 16
                  },
                  children: [
                    /* @__PURE__ */ jsx125(
                      "div",
                      {
                        style: {
                          display: "flex",
                          fontSize: headlineFontSize,
                          fontWeight: 600,
                          color: "#000000",
                          lineHeight: 1.15,
                          letterSpacing: "-0.045em"
                        },
                        children: headline
                      }
                    ),
                    excerpt && /* @__PURE__ */ jsx125(
                      "div",
                      {
                        style: {
                          display: "flex",
                          fontSize: 18,
                          fontWeight: 400,
                          color: "#3A3330",
                          lineHeight: 1.3,
                          overflow: "hidden"
                        },
                        children: excerpt
                      }
                    )
                  ]
                }
              ),
              /* @__PURE__ */ jsxs110("div", { style: { display: "flex", flexDirection: "column", gap: 16 }, children: [
                /* @__PURE__ */ jsx125(
                  "div",
                  {
                    style: {
                      display: "flex",
                      width: "100%",
                      height: 1,
                      backgroundColor: "#D5D1C8"
                    }
                  }
                ),
                /* @__PURE__ */ jsxs110(
                  "div",
                  {
                    style: {
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    },
                    children: [
                      /* @__PURE__ */ jsxs110(
                        "div",
                        {
                          style: {
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: 14,
                            fontFamily: "'Lato', sans-serif",
                            fontWeight: 700,
                            color: "#D2001F"
                          },
                          children: [
                            ctaText,
                            /* @__PURE__ */ jsx125("span", { style: { display: "flex", fontSize: 18 }, children: "\u2192" })
                          ]
                        }
                      ),
                      /* @__PURE__ */ jsx125(
                        "img",
                        {
                          src: logoUrl,
                          alt: "Aaim",
                          style: {
                            height: 24,
                            width: "auto"
                          }
                        }
                      )
                    ]
                  }
                )
              ] })
            ]
          }
        )
      ]
    }
  );
}

// src/social-cards/ShareButton.tsx
import { useState as useState26, useEffect as useEffect20, useRef as useRef19 } from "react";
import { createPortal as createPortal3 } from "react-dom";
import { Share2Icon, CheckIcon as CheckIcon10, CopyIcon, XIcon as XIcon7 } from "lucide-react";
import { Fragment as Fragment28, jsx as jsx126, jsxs as jsxs111 } from "react/jsx-runtime";
function getTwitterShareUrl(text, url) {
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
}
function getLinkedInShareUrl(url) {
  return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
}
function getEmailShareUrl(subject, body, url) {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`${body}

${url}`)}`;
}
function isTrueMobileDevice() {
  if (typeof window === "undefined") return false;
  const mobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
  const smallScreen = window.innerWidth < 768;
  return mobileUA && smallScreen;
}
function computeTooltipPosition(buttonRect) {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const tooltipWidth = Math.min(256, viewportWidth - 32);
  const tooltipHeight = 220;
  const padding = 16;
  const isMobile = viewportWidth < 640;
  if (isMobile) {
    const spaceBelow = viewportHeight - buttonRect.bottom - 8;
    const spaceAbove = buttonRect.top - 8;
    let topPosition;
    if (spaceBelow >= tooltipHeight) {
      topPosition = buttonRect.bottom + 8;
    } else if (spaceAbove >= tooltipHeight) {
      topPosition = buttonRect.top - tooltipHeight - 8;
    } else {
      topPosition = Math.max(padding, (viewportHeight - tooltipHeight) / 2);
    }
    return {
      isMobile: true,
      style: {
        position: "fixed",
        left: padding,
        right: padding,
        top: topPosition,
        maxHeight: viewportHeight - padding * 2,
        overflowY: "auto",
        width: "auto",
        zIndex: 9999
      },
      desktopPosition: "left"
    };
  }
  const spaceOnRight = viewportWidth - buttonRect.left;
  const spaceOnLeft = buttonRect.right;
  let desktopPosition = "right";
  if (spaceOnRight >= tooltipWidth + padding) {
    desktopPosition = "left";
  } else if (spaceOnLeft >= tooltipWidth + padding) {
    desktopPosition = "right";
  } else {
    desktopPosition = spaceOnRight > spaceOnLeft ? "left" : "right";
  }
  return {
    isMobile: false,
    style: {},
    desktopPosition
  };
}
function ShareButton({
  title,
  text,
  url,
  variant = "inline",
  className = ""
}) {
  const [isOpen, setIsOpen] = useState26(false);
  const [copied, setCopied] = useState26(false);
  const [shareUrl, setShareUrl] = useState26("");
  const [supportsNativeShare, setSupportsNativeShare] = useState26(false);
  const [mounted, setMounted] = useState26(false);
  const dropdownRef = useRef19(null);
  const buttonRef = useRef19(null);
  const tooltipRef = useRef19(null);
  useEffect20(() => {
    setMounted(true);
  }, []);
  useEffect20(() => {
    setShareUrl(url || window.location.href);
    setSupportsNativeShare(
      typeof navigator !== "undefined" && !!navigator.share && isTrueMobileDevice()
    );
  }, [url]);
  useEffect20(() => {
    function handleClickOutside(event) {
      const target = event.target;
      const clickedInsideDropdown = dropdownRef.current?.contains(target);
      const clickedInsideTooltip = tooltipRef.current?.contains(target);
      if (!clickedInsideDropdown && !clickedInsideTooltip) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title,
        text: text || title,
        url: shareUrl
      });
    } catch {
      setIsOpen(true);
    }
  };
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2e3);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2e3);
    }
  };
  const handleShare = () => {
    if (supportsNativeShare) {
      handleNativeShare();
    } else {
      setIsOpen(!isOpen);
    }
  };
  const shareText = text || title;
  const tooltipContent = /* @__PURE__ */ jsxs111(Fragment28, { children: [
    /* @__PURE__ */ jsxs111("div", { className: "p-3 border-b border-recessed flex items-center justify-between", children: [
      /* @__PURE__ */ jsx126("span", { className: "text-sm font-sans font-semibold text-primary", children: "Share this article" }),
      /* @__PURE__ */ jsx126(
        "button",
        {
          onClick: () => setIsOpen(false),
          className: "text-tertiary hover:text-primary transition-colors",
          children: /* @__PURE__ */ jsx126(XIcon7, { className: "w-4 h-4" })
        }
      )
    ] }),
    /* @__PURE__ */ jsxs111("div", { className: "p-2", children: [
      /* @__PURE__ */ jsxs111(
        "a",
        {
          href: getLinkedInShareUrl(shareUrl),
          target: "_blank",
          rel: "noopener noreferrer",
          className: "flex items-center gap-3 px-3 py-2 text-sm font-sans text-secondary hover:bg-recessed rounded-sm transition-colors",
          children: [
            /* @__PURE__ */ jsx126(LinkedInIcon, { className: "w-5 h-5 text-[#0A66C2]" }),
            "LinkedIn"
          ]
        }
      ),
      /* @__PURE__ */ jsxs111(
        "a",
        {
          href: getTwitterShareUrl(shareText, shareUrl),
          target: "_blank",
          rel: "noopener noreferrer",
          className: "flex items-center gap-3 px-3 py-2 text-sm font-sans text-secondary hover:bg-recessed rounded-sm transition-colors",
          children: [
            /* @__PURE__ */ jsx126(XTwitterIcon, { className: "w-5 h-5" }),
            "X (Twitter)"
          ]
        }
      ),
      /* @__PURE__ */ jsxs111(
        "a",
        {
          href: getEmailShareUrl(title, shareText, shareUrl),
          className: "flex items-center gap-3 px-3 py-2 text-sm font-sans text-secondary hover:bg-recessed rounded-sm transition-colors",
          children: [
            /* @__PURE__ */ jsx126(EmailIcon, { className: "w-5 h-5 text-tertiary" }),
            "Email"
          ]
        }
      ),
      /* @__PURE__ */ jsx126(
        "button",
        {
          onClick: handleCopyLink,
          className: "flex items-center gap-3 px-3 py-2 text-sm font-sans text-secondary hover:bg-recessed rounded-sm transition-colors w-full text-left",
          children: copied ? /* @__PURE__ */ jsxs111(Fragment28, { children: [
            /* @__PURE__ */ jsx126(CheckIcon10, { className: "w-5 h-5 text-success" }),
            /* @__PURE__ */ jsx126("span", { className: "text-success", children: "Copied" })
          ] }) : /* @__PURE__ */ jsxs111(Fragment28, { children: [
            /* @__PURE__ */ jsx126(CopyIcon, { className: "w-5 h-5 text-tertiary" }),
            "Copy link"
          ] })
        }
      )
    ] })
  ] });
  const renderTooltip = () => {
    if (!isOpen || !mounted) return null;
    if (!buttonRef.current) return null;
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const { isMobile, style, desktopPosition } = computeTooltipPosition(buttonRect);
    if (isMobile) {
      return createPortal3(
        /* @__PURE__ */ jsx126(
          "div",
          {
            ref: tooltipRef,
            className: "bg-surface border border-recessed rounded-sm shadow-lg",
            style,
            children: tooltipContent
          }
        ),
        document.body
      );
    }
    const positionClass = desktopPosition === "left" ? "left-0" : "right-0";
    const desktopTooltipClasses = `absolute ${positionClass} top-full mt-2 w-64 max-w-[calc(100vw-32px)] bg-surface border border-recessed rounded-sm shadow-lg z-50`;
    return /* @__PURE__ */ jsx126("div", { ref: tooltipRef, className: desktopTooltipClasses, children: tooltipContent });
  };
  if (variant === "button") {
    return /* @__PURE__ */ jsxs111("div", { className: `relative ${className}`, ref: dropdownRef, children: [
      /* @__PURE__ */ jsxs111(
        "button",
        {
          ref: buttonRef,
          onClick: handleShare,
          className: "inline-flex items-center gap-2 border border-accent text-accent py-3 px-6 rounded-sm font-sans font-semibold text-sm hover:bg-accent hover:text-white transition-all duration-200",
          children: [
            /* @__PURE__ */ jsx126(Share2Icon, { className: "w-4 h-4" }),
            "Share"
          ]
        }
      ),
      renderTooltip()
    ] });
  }
  return /* @__PURE__ */ jsxs111("div", { className: `relative inline-block ${className}`, ref: dropdownRef, children: [
    /* @__PURE__ */ jsxs111(
      "button",
      {
        ref: buttonRef,
        onClick: handleShare,
        className: "text-accent font-sans font-semibold text-sm hover:underline flex items-center gap-1 transition-all",
        children: [
          "Share",
          /* @__PURE__ */ jsx126(Share2Icon, { className: "w-4 h-4" })
        ]
      }
    ),
    renderTooltip()
  ] });
}
function LinkedInIcon({ className }) {
  return /* @__PURE__ */ jsx126("svg", { className, viewBox: "0 0 24 24", fill: "currentColor", children: /* @__PURE__ */ jsx126("path", { d: "M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" }) });
}
function XTwitterIcon({ className }) {
  return /* @__PURE__ */ jsx126("svg", { className, viewBox: "0 0 24 24", fill: "currentColor", children: /* @__PURE__ */ jsx126("path", { d: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" }) });
}
function EmailIcon({ className }) {
  return /* @__PURE__ */ jsxs111("svg", { className, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [
    /* @__PURE__ */ jsx126("rect", { x: "2", y: "4", width: "20", height: "16", rx: "2" }),
    /* @__PURE__ */ jsx126("path", { d: "M22 6l-10 7L2 6" })
  ] });
}

// src/forms/NewsletterForm.tsx
import { useId as useId3, useState as useState27 } from "react";
import clsx14 from "clsx";
import { Fragment as Fragment29, jsx as jsx127, jsxs as jsxs112 } from "react/jsx-runtime";
var NEWSLETTER_HONEYPOT_FIELD = "website";
function NewsletterForm({
  onSubmit,
  source = "website",
  className = "",
  fieldsClassName,
  inputClassName,
  buttonClassName,
  messageClassName,
  placeholder = "Enter your email",
  emailLabel = "Email address",
  showFirstName = false,
  firstNamePlaceholder = "First name (optional)",
  firstNameLabel = "First name",
  buttonText = "Subscribe",
  submittingText = "Subscribing...",
  layout = "horizontal"
}) {
  const fieldId = useId3();
  const emailId = `${fieldId}-email`;
  const firstNameId = `${fieldId}-first-name`;
  const honeypotId = `${fieldId}-${NEWSLETTER_HONEYPOT_FIELD}`;
  const [email, setEmail] = useState27("");
  const [firstName, setFirstName] = useState27("");
  const [honeypot, setHoneypot] = useState27("");
  const [message, setMessage] = useState27("");
  const [isSuccess, setIsSuccess] = useState27(false);
  const [isPending, setIsPending] = useState27(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setIsSuccess(false);
    setIsPending(true);
    try {
      const result = await onSubmit({
        email,
        source,
        honeypot,
        ...showFirstName && firstName ? { firstName } : {}
      });
      setMessage(result.message);
      setIsSuccess(result.success);
      if (result.success && !result.alreadySubscribed) {
        setEmail("");
        setFirstName("");
      }
    } catch {
      setMessage("An error occurred. Please try again.");
      setIsSuccess(false);
    } finally {
      setIsPending(false);
    }
  };
  const defaultInputClassName = clsx14(
    "flex-1 px-4 py-3 border border-recessed rounded-sm text-sm font-sans",
    "focus:outline-none focus:border-accent disabled:opacity-50",
    "bg-surface text-primary placeholder:text-tertiary"
  );
  const defaultButtonClassName = clsx14(
    "bg-accent text-white py-3 px-6 rounded-sm font-sans font-semibold text-sm",
    "hover:bg-accent-hover transition-all duration-200",
    "disabled:opacity-50 disabled:cursor-not-allowed"
  );
  return /* @__PURE__ */ jsxs112("form", { onSubmit: handleSubmit, className, children: [
    /* @__PURE__ */ jsxs112("div", { className: "hidden", "aria-hidden": "true", children: [
      /* @__PURE__ */ jsx127("label", { htmlFor: honeypotId, children: "Website" }),
      /* @__PURE__ */ jsx127(
        "input",
        {
          type: "text",
          id: honeypotId,
          name: NEWSLETTER_HONEYPOT_FIELD,
          value: honeypot,
          onChange: (e) => setHoneypot(e.target.value),
          tabIndex: -1,
          autoComplete: "off"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs112(
      "div",
      {
        className: fieldsClassName || clsx14(
          "flex gap-3",
          layout === "horizontal" ? "flex-col sm:flex-row" : "flex-col"
        ),
        children: [
          showFirstName && /* @__PURE__ */ jsxs112(Fragment29, { children: [
            /* @__PURE__ */ jsx127("label", { htmlFor: firstNameId, className: "sr-only", children: firstNameLabel }),
            /* @__PURE__ */ jsx127(
              "input",
              {
                type: "text",
                id: firstNameId,
                name: "firstName",
                maxLength: 100,
                value: firstName,
                onChange: (e) => setFirstName(e.target.value),
                placeholder: firstNamePlaceholder,
                disabled: isPending,
                className: inputClassName || defaultInputClassName
              }
            )
          ] }),
          /* @__PURE__ */ jsx127("label", { htmlFor: emailId, className: "sr-only", children: emailLabel }),
          /* @__PURE__ */ jsx127(
            "input",
            {
              type: "email",
              id: emailId,
              name: "email",
              value: email,
              onChange: (e) => setEmail(e.target.value),
              placeholder,
              required: true,
              disabled: isPending,
              className: inputClassName || defaultInputClassName
            }
          ),
          /* @__PURE__ */ jsx127(
            "button",
            {
              type: "submit",
              disabled: isPending,
              className: buttonClassName || defaultButtonClassName,
              children: isPending ? submittingText : buttonText
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx127(
      "p",
      {
        role: "status",
        "aria-live": "polite",
        className: clsx14(
          messageClassName || "text-sm font-sans",
          message && "mt-3",
          message && (isSuccess ? "text-success" : "text-error")
        ),
        children: message
      }
    )
  ] });
}

// src/forms/Switch.tsx
import {
  Switch as AriaSwitch
} from "react-aria-components";
import { clsx as clsx15 } from "clsx";
import { jsx as jsx128, jsxs as jsxs113 } from "react/jsx-runtime";
var sizeClasses2 = {
  sm: {
    track: "w-8 h-4",
    thumb: "w-3 h-3",
    translate: "translate-x-4"
  },
  md: {
    track: "w-10 h-5",
    thumb: "w-4 h-4",
    translate: "translate-x-5"
  },
  lg: {
    track: "w-12 h-6",
    thumb: "w-5 h-5",
    translate: "translate-x-6"
  }
};
function Switch({
  label,
  description,
  size = "md",
  className,
  ...props
}) {
  const sizes = sizeClasses2[size];
  return /* @__PURE__ */ jsxs113(
    AriaSwitch,
    {
      ...props,
      className: clsx15(
        "group flex items-start gap-3 cursor-pointer",
        props.isDisabled && "opacity-50 cursor-not-allowed",
        className
      ),
      children: [
        /* @__PURE__ */ jsx128(
          "div",
          {
            className: clsx15(
              "relative shrink-0 rounded-full transition-colors duration-200",
              sizes.track,
              // Default state
              "bg-recessed",
              // Selected state
              "group-data-[selected]:bg-selection",
              // Focus state
              "group-focus-visible:ring-2 group-focus-visible:ring-selection group-focus-visible:ring-offset-2"
            ),
            children: /* @__PURE__ */ jsx128(
              "span",
              {
                className: clsx15(
                  "absolute top-0.5 left-0.5 rounded-full bg-white shadow-sm",
                  "transition-transform duration-200",
                  sizes.thumb,
                  size === "sm" && "group-data-[selected]:translate-x-4",
                  size === "md" && "group-data-[selected]:translate-x-5",
                  size === "lg" && "group-data-[selected]:translate-x-6"
                )
              }
            )
          }
        ),
        (label || description) && /* @__PURE__ */ jsxs113("div", { className: "flex flex-col", children: [
          label && /* @__PURE__ */ jsx128("span", { className: "text-sm font-medium text-primary", children: label }),
          description && /* @__PURE__ */ jsx128("span", { className: "text-xs text-tertiary mt-0.5", children: description })
        ] })
      ]
    }
  );
}

// src/forms/RadioGroup.tsx
import {
  RadioGroup as AriaRadioGroup,
  Radio as AriaRadio,
  Label,
  Text
} from "react-aria-components";
import { clsx as clsx16 } from "clsx";
import { Fragment as Fragment30, jsx as jsx129, jsxs as jsxs114 } from "react/jsx-runtime";
function RadioGroup({
  label,
  description,
  errorMessage,
  options,
  orientation = "vertical",
  variant = "default",
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs114(
    AriaRadioGroup,
    {
      ...props,
      orientation,
      className: clsx16("flex flex-col gap-2", className),
      children: [
        label && /* @__PURE__ */ jsx129(Label, { className: "text-sm font-medium text-primary", children: label }),
        description && /* @__PURE__ */ jsx129(Text, { slot: "description", className: "text-xs text-tertiary", children: description }),
        /* @__PURE__ */ jsx129(
          "div",
          {
            className: clsx16(
              "flex gap-3",
              orientation === "vertical" ? "flex-col" : "flex-row flex-wrap"
            ),
            children: options.map((option) => /* @__PURE__ */ jsx129(
              AriaRadio,
              {
                value: option.value,
                isDisabled: option.isDisabled,
                className: ({ isSelected, isDisabled, isFocusVisible }) => clsx16(
                  "group flex items-start gap-3 cursor-pointer",
                  variant === "card" && [
                    "p-4 border rounded-sm transition-colors",
                    isSelected ? "border-selection bg-selection/10" : "border-[rgb(var(--color-control-boundary))] bg-surface hover:bg-canvas"
                  ],
                  isDisabled && "opacity-50 cursor-not-allowed",
                  isFocusVisible && "outline-none ring-2 ring-selection ring-offset-2"
                ),
                children: ({ isSelected }) => /* @__PURE__ */ jsxs114(Fragment30, { children: [
                  /* @__PURE__ */ jsx129(
                    "div",
                    {
                      "data-slot": "radio-indicator",
                      className: clsx16(
                        "w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors",
                        isSelected ? "border-selection bg-selection" : "border-[rgb(var(--color-control-boundary))] bg-surface"
                      ),
                      children: isSelected && /* @__PURE__ */ jsx129("div", { className: "w-1.5 h-1.5 rounded-full bg-white" })
                    }
                  ),
                  /* @__PURE__ */ jsxs114("div", { className: "flex flex-col", children: [
                    /* @__PURE__ */ jsx129("span", { className: "text-sm font-medium text-primary", children: option.label }),
                    option.description && /* @__PURE__ */ jsx129("span", { className: "text-xs text-tertiary mt-0.5", children: option.description })
                  ] })
                ] })
              },
              option.value
            ))
          }
        ),
        errorMessage && /* @__PURE__ */ jsx129(Text, { slot: "errorMessage", className: "text-xs text-error", children: errorMessage })
      ]
    }
  );
}

// src/forms/DatePicker.tsx
import { useState as useState28, useRef as useRef20, useEffect as useEffect21, useCallback as useCallback17, useMemo as useMemo12 } from "react";
import { CalendarIcon as CalendarIcon2, ChevronLeftIcon as ChevronLeftIcon4, ChevronRightIcon as ChevronRightIcon7, ChevronDownIcon as ChevronDownIcon11, XIcon as XIcon8 } from "lucide-react";

// src/forms/field-chrome.ts
var FIELD_CONTROL_SIZE = "px-4 py-3";
var FIELD_CONTROL_MIN_H = "min-h-[50px]";
var FIELD_CONTROL_FRAME = `w-full ${FIELD_CONTROL_SIZE} bg-surface border rounded font-sans placeholder:text-tertiary transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed`;
var FIELD_CONTROL_TEXT = "text-secondary";
var FIELD_CONTROL_BASE = `${FIELD_CONTROL_FRAME} ${FIELD_CONTROL_TEXT}`;
var FIELD_LABEL_BASE = "block text-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary";
var FIELD_LABEL_CHROME = `${FIELD_LABEL_BASE} mb-2`;

// src/forms/DatePicker.tsx
import {
  format as formatDate2,
  parse,
  isValid,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
  addDays,
  subDays,
  addWeeks,
  subWeeks,
  isSameDay,
  isSameMonth,
  isToday,
  isBefore,
  isAfter,
  startOfDay,
  getYear,
  getMonth,
  setMonth as setDateMonth,
  setYear as setDateYear
} from "date-fns";
import { jsx as jsx130, jsxs as jsxs115 } from "react/jsx-runtime";
var WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
var MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];
var MONTH_ABBREVS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];
var INPUT_PARSE_FORMAT = "MM/dd/yyyy";
function DatePicker({
  value,
  onChange,
  placeholder = "MM/DD/YYYY",
  format: formatProp,
  displayFormat,
  min,
  minDate: minDateProp,
  max,
  maxDate: maxDateProp,
  disabled = false,
  isDateDisabled,
  error = false,
  errorMessage,
  label,
  helperText,
  size = "md",
  className = ""
}) {
  const dateFormat = formatProp || displayFormat || "MMM d, yyyy";
  const minConstraint = min || minDateProp;
  const maxConstraint = max || maxDateProp;
  const [isOpen, setIsOpen] = useState28(false);
  const [viewDate, setViewDate] = useState28(value || /* @__PURE__ */ new Date());
  const [focusedDate, setFocusedDate] = useState28(value || /* @__PURE__ */ new Date());
  const [isEditing, setIsEditing] = useState28(false);
  const [inputText, setInputText] = useState28("");
  const [monthSelectorOpen, setMonthSelectorOpen] = useState28(false);
  const [yearSelectorOpen, setYearSelectorOpen] = useState28(false);
  const containerRef = useRef20(null);
  const inputRef = useRef20(null);
  const gridRef = useRef20(null);
  const yearListRef = useRef20(null);
  const shouldFocusGrid = useRef20(false);
  const calendarDays = useMemo12(() => {
    const monthStart = startOfMonth(viewDate);
    const monthEnd = endOfMonth(viewDate);
    const calStart = startOfWeek(monthStart);
    const calEnd = endOfWeek(monthEnd);
    const days = [];
    let current = calStart;
    while (current <= calEnd) {
      days.push(current);
      current = addDays(current, 1);
    }
    return days;
  }, [viewDate]);
  const yearRange = useMemo12(() => {
    const currentYear = getYear(/* @__PURE__ */ new Date());
    const startYear = minConstraint ? Math.min(getYear(minConstraint), currentYear - 10) : currentYear - 100;
    const endYear = maxConstraint ? Math.max(getYear(maxConstraint), currentYear + 5) : currentYear + 20;
    return Array.from(
      { length: endYear - startYear + 1 },
      (_, i) => startYear + i
    );
  }, [minConstraint, maxConstraint]);
  const isDateAllowed = useCallback17((date) => {
    const day = startOfDay(date);
    if (minConstraint && isBefore(day, startOfDay(minConstraint))) return false;
    if (maxConstraint && isAfter(day, startOfDay(maxConstraint))) return false;
    if (isDateDisabled?.(date)) return false;
    return true;
  }, [minConstraint, maxConstraint, isDateDisabled]);
  const handleSelectDate = useCallback17((date) => {
    if (!isDateAllowed(date)) return;
    onChange?.(startOfDay(date));
    setIsOpen(false);
    setIsEditing(false);
    setMonthSelectorOpen(false);
    setYearSelectorOpen(false);
  }, [isDateAllowed, onChange]);
  const goToPreviousMonth = useCallback17(() => {
    setViewDate((prev) => subMonths(prev, 1));
  }, []);
  const goToNextMonth = useCallback17(() => {
    setViewDate((prev) => addMonths(prev, 1));
  }, []);
  const handleMonthSelect = useCallback17((monthIndex) => {
    setViewDate((prev) => setDateMonth(prev, monthIndex));
    setMonthSelectorOpen(false);
  }, []);
  const handleYearSelect = useCallback17((year) => {
    setViewDate((prev) => setDateYear(prev, year));
    setYearSelectorOpen(false);
  }, []);
  const handleInputFocus = useCallback17(() => {
    if (disabled) return;
    setIsEditing(true);
    setInputText(value ? formatDate2(value, INPUT_PARSE_FORMAT) : "");
  }, [disabled, value]);
  const handleInputChange = useCallback17((e) => {
    setInputText(e.target.value);
  }, []);
  const commitInputText = useCallback17(() => {
    setIsEditing(false);
    const trimmed = inputText.trim();
    if (!trimmed) {
      onChange?.(null);
      return;
    }
    const parsed = parse(trimmed, INPUT_PARSE_FORMAT, /* @__PURE__ */ new Date());
    if (isValid(parsed) && isDateAllowed(parsed)) {
      onChange?.(startOfDay(parsed));
      setViewDate(parsed);
    }
  }, [inputText, isDateAllowed, onChange]);
  const handleInputBlur = useCallback17((e) => {
    if (containerRef.current?.contains(e.relatedTarget)) return;
    commitInputText();
  }, [commitInputText]);
  const handleInputKeyDown = useCallback17((e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitInputText();
    }
    if (e.key === "Escape") {
      setIsEditing(false);
      setIsOpen(false);
    }
    if (e.key === "ArrowDown" && !isOpen) {
      e.preventDefault();
      setIsOpen(true);
      shouldFocusGrid.current = true;
      setFocusedDate(value || /* @__PURE__ */ new Date());
    }
  }, [commitInputText, isOpen, value]);
  const toggleCalendar = useCallback17(() => {
    if (disabled) return;
    if (isOpen) {
      setIsOpen(false);
      setMonthSelectorOpen(false);
      setYearSelectorOpen(false);
    } else {
      setIsOpen(true);
      shouldFocusGrid.current = true;
      setFocusedDate(value || /* @__PURE__ */ new Date());
      setViewDate(value || /* @__PURE__ */ new Date());
    }
  }, [disabled, isOpen, value]);
  const handleCalendarKeyDown = useCallback17((e) => {
    let newDate = focusedDate;
    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        newDate = addDays(focusedDate, 1);
        break;
      case "ArrowLeft":
        e.preventDefault();
        newDate = subDays(focusedDate, 1);
        break;
      case "ArrowDown":
        e.preventDefault();
        newDate = addWeeks(focusedDate, 1);
        break;
      case "ArrowUp":
        e.preventDefault();
        newDate = subWeeks(focusedDate, 1);
        break;
      case "Home":
        e.preventDefault();
        newDate = startOfWeek(focusedDate);
        break;
      case "End":
        e.preventDefault();
        newDate = endOfWeek(focusedDate);
        break;
      case "PageUp":
        e.preventDefault();
        newDate = e.shiftKey ? setDateYear(focusedDate, getYear(focusedDate) - 1) : subMonths(focusedDate, 1);
        break;
      case "PageDown":
        e.preventDefault();
        newDate = e.shiftKey ? setDateYear(focusedDate, getYear(focusedDate) + 1) : addMonths(focusedDate, 1);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (isDateAllowed(focusedDate)) {
          handleSelectDate(focusedDate);
        }
        return;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setMonthSelectorOpen(false);
        setYearSelectorOpen(false);
        inputRef.current?.focus();
        return;
      default:
        return;
    }
    if (minConstraint && isBefore(startOfDay(newDate), startOfDay(minConstraint))) return;
    if (maxConstraint && isAfter(startOfDay(newDate), startOfDay(maxConstraint))) return;
    setFocusedDate(newDate);
    if (!isSameMonth(newDate, viewDate)) {
      setViewDate(startOfMonth(newDate));
    }
  }, [focusedDate, viewDate, minConstraint, maxConstraint, isDateAllowed, handleSelectDate]);
  const handleClear = useCallback17((e) => {
    e.stopPropagation();
    onChange?.(null);
    setIsEditing(false);
  }, [onChange]);
  useEffect21(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setMonthSelectorOpen(false);
        setYearSelectorOpen(false);
        if (isEditing) {
          commitInputText();
        }
      }
    };
    if (isOpen || isEditing) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, isEditing, commitInputText]);
  useEffect21(() => {
    if (value) {
      setViewDate(value);
    }
  }, [value]);
  useEffect21(() => {
    if (!isOpen || !gridRef.current) return;
    if (shouldFocusGrid.current) {
      shouldFocusGrid.current = false;
      requestAnimationFrame(() => {
        const btn = gridRef.current?.querySelector(
          '[tabindex="0"]'
        );
        btn?.focus();
      });
    }
  }, [isOpen, focusedDate]);
  useEffect21(() => {
    if (!isOpen || !gridRef.current) return;
    const activeInGrid = gridRef.current.contains(document.activeElement);
    if (activeInGrid) {
      requestAnimationFrame(() => {
        const btn = gridRef.current?.querySelector(
          '[tabindex="0"]'
        );
        btn?.focus();
      });
    }
  }, [focusedDate, isOpen]);
  useEffect21(() => {
    if (yearSelectorOpen && yearListRef.current) {
      const selected = yearListRef.current.querySelector('[data-selected="true"]');
      selected?.scrollIntoView({ block: "center" });
    }
  }, [yearSelectorOpen]);
  const displayValue = useMemo12(() => {
    if (isEditing) return inputText;
    if (value) return formatDate2(value, dateFormat);
    return "";
  }, [isEditing, inputText, value, dateFormat]);
  const sizeClasses4 = {
    sm: "text-xs py-1.5 px-2",
    md: FIELD_CONTROL_SIZE,
    lg: "text-base py-2.5 px-4"
  };
  return /* @__PURE__ */ jsxs115("div", { ref: containerRef, className: `relative ${className}`, children: [
    label && /* @__PURE__ */ jsx130("label", { className: FIELD_LABEL_CHROME, children: label }),
    /* @__PURE__ */ jsxs115("div", { className: "relative", children: [
      /* @__PURE__ */ jsx130(
        "input",
        {
          ref: inputRef,
          type: "text",
          value: displayValue,
          placeholder,
          disabled,
          onFocus: handleInputFocus,
          onBlur: handleInputBlur,
          onChange: handleInputChange,
          onKeyDown: handleInputKeyDown,
          "aria-label": label || "Date input",
          "aria-haspopup": "dialog",
          "aria-expanded": isOpen,
          "aria-invalid": error || void 0,
          "aria-errormessage": error && errorMessage ? "datepicker-error" : void 0,
          className: `
            w-full font-sans bg-surface border rounded-sm
            ${sizeClasses4[size]}
            ${error ? "border-error focus:ring-error/20" : "border-recessed focus:ring-tertiary/30"}
            ${disabled ? "opacity-50 cursor-not-allowed bg-elevated" : "hover:border-secondary"}
            focus:outline-none focus:ring-2 focus:border-secondary
            pr-16 text-primary placeholder:text-tertiary
          `
        }
      ),
      /* @__PURE__ */ jsxs115("div", { className: "absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1", children: [
        value && !disabled && /* @__PURE__ */ jsx130(
          "button",
          {
            type: "button",
            onClick: handleClear,
            className: "p-0.5 rounded hover:bg-elevated transition-colors",
            "aria-label": "Clear date",
            tabIndex: -1,
            children: /* @__PURE__ */ jsx130(XIcon8, { className: "w-3.5 h-3.5 text-tertiary", strokeWidth: 2 })
          }
        ),
        /* @__PURE__ */ jsx130(
          "button",
          {
            type: "button",
            onClick: toggleCalendar,
            disabled,
            className: "p-0.5 rounded hover:bg-elevated transition-colors",
            "aria-label": isOpen ? "Close calendar" : "Open calendar",
            tabIndex: -1,
            children: /* @__PURE__ */ jsx130(CalendarIcon2, { className: "w-4 h-4 text-tertiary", strokeWidth: 2 })
          }
        )
      ] })
    ] }),
    (helperText || errorMessage) && /* @__PURE__ */ jsx130(
      "p",
      {
        id: error && errorMessage ? "datepicker-error" : void 0,
        role: error ? "alert" : void 0,
        className: `mt-1 text-xs font-sans ${error ? "text-accent" : "text-tertiary"}`,
        children: error ? errorMessage : helperText
      }
    ),
    isOpen && /* @__PURE__ */ jsxs115(
      "div",
      {
        role: "dialog",
        "aria-label": "Choose date",
        "aria-modal": "false",
        className: "absolute top-full left-0 mt-1 w-[280px] bg-surface border border-recessed rounded-sm shadow-lg z-50 animate-fade-in",
        children: [
          /* @__PURE__ */ jsxs115("div", { className: "flex items-center justify-between px-2 py-2 border-b border-recessed", children: [
            /* @__PURE__ */ jsx130(
              "button",
              {
                type: "button",
                onClick: goToPreviousMonth,
                className: "p-1 rounded hover:bg-elevated transition-colors",
                "aria-label": "Previous month",
                children: /* @__PURE__ */ jsx130(ChevronLeftIcon4, { className: "w-4 h-4 text-tertiary", strokeWidth: 2 })
              }
            ),
            /* @__PURE__ */ jsxs115("div", { className: "flex items-center gap-1", children: [
              /* @__PURE__ */ jsxs115("div", { className: "relative", children: [
                /* @__PURE__ */ jsxs115(
                  "button",
                  {
                    type: "button",
                    onClick: () => {
                      setMonthSelectorOpen(!monthSelectorOpen);
                      setYearSelectorOpen(false);
                    },
                    className: "flex items-center gap-0.5 px-1.5 py-0.5 text-sm font-sans font-medium text-primary hover:bg-elevated rounded-sm transition-colors",
                    "aria-label": "Select month",
                    children: [
                      MONTH_NAMES[getMonth(viewDate)],
                      /* @__PURE__ */ jsx130(ChevronDownIcon11, { className: "w-3 h-3 text-tertiary", strokeWidth: 2 })
                    ]
                  }
                ),
                monthSelectorOpen && /* @__PURE__ */ jsx130("div", { className: "absolute left-1/2 -translate-x-1/2 top-full mt-1 bg-surface border border-recessed rounded-sm shadow-lg z-10 grid grid-cols-3 gap-0.5 p-1.5 w-[200px]", children: MONTH_ABBREVS.map((name, index) => /* @__PURE__ */ jsx130(
                  "button",
                  {
                    type: "button",
                    onClick: () => handleMonthSelect(index),
                    className: `
                          py-1.5 text-xs font-sans rounded-sm transition-colors text-center
                          ${index === getMonth(viewDate) ? "bg-selection text-white font-medium" : "text-primary hover:bg-elevated"}
                        `,
                    children: name
                  },
                  name
                )) })
              ] }),
              /* @__PURE__ */ jsxs115("div", { className: "relative", children: [
                /* @__PURE__ */ jsxs115(
                  "button",
                  {
                    type: "button",
                    onClick: () => {
                      setYearSelectorOpen(!yearSelectorOpen);
                      setMonthSelectorOpen(false);
                    },
                    className: "flex items-center gap-0.5 px-1.5 py-0.5 text-sm font-sans font-medium text-primary hover:bg-elevated rounded-sm transition-colors",
                    "aria-label": "Select year",
                    children: [
                      getYear(viewDate),
                      /* @__PURE__ */ jsx130(ChevronDownIcon11, { className: "w-3 h-3 text-tertiary", strokeWidth: 2 })
                    ]
                  }
                ),
                yearSelectorOpen && /* @__PURE__ */ jsx130(
                  "div",
                  {
                    ref: yearListRef,
                    className: "absolute left-1/2 -translate-x-1/2 top-full mt-1 bg-surface border border-recessed rounded-sm shadow-lg z-10 w-[80px] max-h-[200px] overflow-y-auto p-1",
                    children: yearRange.map((year) => /* @__PURE__ */ jsx130(
                      "button",
                      {
                        type: "button",
                        "data-selected": year === getYear(viewDate),
                        onClick: () => handleYearSelect(year),
                        className: `
                          w-full py-1 text-xs font-sans rounded-sm transition-colors text-center
                          ${year === getYear(viewDate) ? "bg-selection text-white font-medium" : "text-primary hover:bg-elevated"}
                        `,
                        children: year
                      },
                      year
                    ))
                  }
                )
              ] })
            ] }),
            /* @__PURE__ */ jsx130(
              "button",
              {
                type: "button",
                onClick: goToNextMonth,
                className: "p-1 rounded hover:bg-elevated transition-colors",
                "aria-label": "Next month",
                children: /* @__PURE__ */ jsx130(ChevronRightIcon7, { className: "w-4 h-4 text-tertiary", strokeWidth: 2 })
              }
            )
          ] }),
          /* @__PURE__ */ jsxs115(
            "div",
            {
              ref: gridRef,
              role: "grid",
              "aria-label": `${MONTH_NAMES[getMonth(viewDate)]} ${getYear(viewDate)}`,
              onKeyDown: handleCalendarKeyDown,
              className: "p-2",
              children: [
                /* @__PURE__ */ jsx130("div", { role: "row", className: "grid grid-cols-7 mb-1", children: WEEKDAY_LABELS.map((day) => /* @__PURE__ */ jsx130(
                  "div",
                  {
                    role: "columnheader",
                    "aria-label": day,
                    className: "text-center text-label-xs font-sans font-semibold uppercase text-tertiary py-1",
                    children: day
                  },
                  day
                )) }),
                /* @__PURE__ */ jsx130("div", { className: "grid grid-cols-7", children: calendarDays.map((day) => {
                  const dateKey = formatDate2(day, "yyyy-MM-dd");
                  const isSelected = value ? isSameDay(day, value) : false;
                  const isCurrentMonth = isSameMonth(day, viewDate);
                  const isTodayDate = isToday(day);
                  const isFocused = isSameDay(day, focusedDate);
                  const allowed = isDateAllowed(day);
                  return /* @__PURE__ */ jsx130(
                    "button",
                    {
                      type: "button",
                      role: "gridcell",
                      tabIndex: isFocused ? 0 : -1,
                      "data-date": dateKey,
                      "aria-label": formatDate2(day, "EEEE, MMMM d, yyyy"),
                      "aria-selected": isSelected,
                      "aria-disabled": !allowed,
                      disabled: !allowed || !isCurrentMonth,
                      onClick: () => handleSelectDate(day),
                      className: `
                      w-9 h-9 text-sm font-sans rounded-sm transition-colors relative
                      ${isSelected ? "bg-selection text-white font-medium" : isCurrentMonth ? allowed ? "text-primary hover:bg-elevated" : "text-disabled cursor-not-allowed" : "text-disabled/40 cursor-default"}
                      ${isTodayDate && !isSelected ? "ring-1 ring-inset ring-accent" : ""}
                      ${isFocused && !isSelected ? "ring-2 ring-inset ring-tertiary/50" : ""}
                    `,
                      children: formatDate2(day, "d")
                    },
                    dateKey
                  );
                }) })
              ]
            }
          ),
          /* @__PURE__ */ jsxs115("div", { className: "px-3 py-2 border-t border-recessed flex items-center justify-between", children: [
            /* @__PURE__ */ jsx130(
              "button",
              {
                type: "button",
                onClick: () => {
                  const today = /* @__PURE__ */ new Date();
                  if (isDateAllowed(today)) {
                    handleSelectDate(today);
                  }
                },
                disabled: !isDateAllowed(/* @__PURE__ */ new Date()),
                className: "text-xs font-sans font-medium text-accent hover:text-accent-hover transition-colors disabled:text-disabled disabled:cursor-not-allowed",
                children: "Today"
              }
            ),
            /* @__PURE__ */ jsx130("span", { className: "text-caption-xs font-sans text-quaternary", children: "MM/DD/YYYY" })
          ] })
        ]
      }
    )
  ] });
}

// src/forms/DateRangePicker.tsx
import { useState as useState29, useRef as useRef21, useEffect as useEffect22, useCallback as useCallback18, useMemo as useMemo13 } from "react";
import {
  CalendarIcon as CalendarIcon3,
  ChevronLeftIcon as ChevronLeftIcon5,
  ChevronRightIcon as ChevronRightIcon8,
  ChevronDownIcon as ChevronDownIcon12,
  XIcon as XIcon9
} from "lucide-react";
import {
  format as formatDate3,
  parse as parse2,
  isValid as isValid2,
  startOfMonth as startOfMonth2,
  endOfMonth as endOfMonth2,
  startOfWeek as startOfWeek2,
  endOfWeek as endOfWeek2,
  addMonths as addMonths2,
  subMonths as subMonths2,
  addDays as addDays2,
  isSameDay as isSameDay2,
  isSameMonth as isSameMonth2,
  isToday as isToday2,
  isBefore as isBefore2,
  isAfter as isAfter2,
  isWithinInterval,
  startOfDay as startOfDay2,
  subDays as subDays2,
  startOfYear,
  endOfYear,
  startOfQuarter,
  endOfQuarter,
  getYear as getYear2,
  getMonth as getMonth2,
  setMonth as setDateMonth2,
  setYear as setDateYear2
} from "date-fns";
import { jsx as jsx131, jsxs as jsxs116 } from "react/jsx-runtime";
var WEEKDAY_LABELS2 = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
var MONTH_NAMES2 = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];
var MONTH_ABBREVS2 = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];
var INPUT_PARSE_FORMAT2 = "MM/dd/yyyy";
function getDefaultPresets() {
  return [
    {
      id: "today",
      label: "Today",
      getValue: () => ({ start: startOfDay2(/* @__PURE__ */ new Date()), end: startOfDay2(/* @__PURE__ */ new Date()) })
    },
    {
      id: "yesterday",
      label: "Yesterday",
      getValue: () => {
        const d = subDays2(/* @__PURE__ */ new Date(), 1);
        return { start: startOfDay2(d), end: startOfDay2(d) };
      }
    },
    {
      id: "last7",
      label: "Last 7 days",
      getValue: () => ({ start: startOfDay2(subDays2(/* @__PURE__ */ new Date(), 6)), end: startOfDay2(/* @__PURE__ */ new Date()) })
    },
    {
      id: "last30",
      label: "Last 30 days",
      getValue: () => ({ start: startOfDay2(subDays2(/* @__PURE__ */ new Date(), 29)), end: startOfDay2(/* @__PURE__ */ new Date()) })
    },
    {
      id: "thisMonth",
      label: "This month",
      getValue: () => ({
        start: startOfMonth2(/* @__PURE__ */ new Date()),
        end: startOfDay2(/* @__PURE__ */ new Date())
      })
    },
    {
      id: "lastMonth",
      label: "Last month",
      getValue: () => ({
        start: startOfMonth2(subMonths2(/* @__PURE__ */ new Date(), 1)),
        end: endOfMonth2(subMonths2(/* @__PURE__ */ new Date(), 1))
      })
    },
    {
      id: "thisQuarter",
      label: "This quarter",
      getValue: () => ({
        start: startOfQuarter(/* @__PURE__ */ new Date()),
        end: startOfDay2(/* @__PURE__ */ new Date())
      })
    },
    {
      id: "lastQuarter",
      label: "Last quarter",
      getValue: () => {
        const prevQuarterDate = subMonths2(startOfQuarter(/* @__PURE__ */ new Date()), 1);
        return {
          start: startOfQuarter(prevQuarterDate),
          end: endOfQuarter(prevQuarterDate)
        };
      }
    },
    {
      id: "thisYear",
      label: "This year",
      getValue: () => ({
        start: startOfYear(/* @__PURE__ */ new Date()),
        end: startOfDay2(/* @__PURE__ */ new Date())
      })
    },
    {
      id: "lastYear",
      label: "Last year",
      getValue: () => {
        const lastYearDate = new Date(getYear2(/* @__PURE__ */ new Date()) - 1, 0, 1);
        return {
          start: startOfYear(lastYearDate),
          end: endOfYear(lastYearDate)
        };
      }
    },
    {
      id: "custom",
      label: "Custom",
      getValue: () => ({ start: null, end: null })
    }
  ];
}
function CalendarPanel({
  viewDate,
  selectedStart,
  selectedEnd,
  selectionStart,
  hoverDate,
  isDateAllowed,
  onDateClick,
  onDateHover,
  showNavigation = true,
  onPrevMonth,
  onNextMonth,
  onMonthSelect,
  onYearSelect,
  yearRange
}) {
  const [monthSelectorOpen, setMonthSelectorOpen] = useState29(false);
  const [yearSelectorOpen, setYearSelectorOpen] = useState29(false);
  const yearListRef = useRef21(null);
  const calendarDays = useMemo13(() => {
    const monthStart = startOfMonth2(viewDate);
    const monthEnd = endOfMonth2(viewDate);
    const calStart = startOfWeek2(monthStart);
    const calEnd = endOfWeek2(monthEnd);
    const days = [];
    let current = calStart;
    while (current <= calEnd) {
      days.push(current);
      current = addDays2(current, 1);
    }
    return days;
  }, [viewDate]);
  const isInRange = useCallback18((date) => {
    if (selectionStart && hoverDate) {
      const rangeStart = isBefore2(selectionStart, hoverDate) ? selectionStart : hoverDate;
      const rangeEnd = isBefore2(selectionStart, hoverDate) ? hoverDate : selectionStart;
      return isWithinInterval(date, { start: rangeStart, end: rangeEnd });
    }
    if (selectedStart && selectedEnd) {
      return isWithinInterval(date, { start: selectedStart, end: selectedEnd });
    }
    return false;
  }, [selectionStart, hoverDate, selectedStart, selectedEnd]);
  const isRangeStart = useCallback18((date) => {
    if (selectionStart && isSameDay2(date, selectionStart)) return true;
    if (selectedStart && isSameDay2(date, selectedStart)) return true;
    return false;
  }, [selectionStart, selectedStart]);
  const isRangeEnd = useCallback18((date) => {
    if (selectedEnd && isSameDay2(date, selectedEnd)) return true;
    return false;
  }, [selectedEnd]);
  useEffect22(() => {
    if (yearSelectorOpen && yearListRef.current) {
      const selected = yearListRef.current.querySelector('[data-selected="true"]');
      selected?.scrollIntoView({ block: "center" });
    }
  }, [yearSelectorOpen]);
  return /* @__PURE__ */ jsxs116("div", { className: "w-[280px]", children: [
    /* @__PURE__ */ jsxs116("div", { className: "flex items-center justify-between px-2 py-2 border-b border-recessed", children: [
      showNavigation && onPrevMonth ? /* @__PURE__ */ jsx131(
        "button",
        {
          type: "button",
          onClick: onPrevMonth,
          className: "p-1 rounded hover:bg-elevated transition-colors",
          "aria-label": "Previous month",
          children: /* @__PURE__ */ jsx131(ChevronLeftIcon5, { className: "w-4 h-4 text-tertiary", strokeWidth: 2 })
        }
      ) : /* @__PURE__ */ jsx131("div", { className: "w-6" }),
      /* @__PURE__ */ jsxs116("div", { className: "flex items-center gap-1", children: [
        /* @__PURE__ */ jsxs116("div", { className: "relative", children: [
          /* @__PURE__ */ jsxs116(
            "button",
            {
              type: "button",
              onClick: () => {
                setMonthSelectorOpen(!monthSelectorOpen);
                setYearSelectorOpen(false);
              },
              className: "flex items-center gap-0.5 px-1 py-0.5 text-sm font-sans font-medium text-primary hover:bg-elevated rounded-sm transition-colors",
              "aria-label": "Select month",
              children: [
                MONTH_NAMES2[getMonth2(viewDate)],
                /* @__PURE__ */ jsx131(ChevronDownIcon12, { className: "w-3 h-3 text-tertiary", strokeWidth: 2 })
              ]
            }
          ),
          monthSelectorOpen && /* @__PURE__ */ jsx131("div", { className: "absolute left-1/2 -translate-x-1/2 top-full mt-1 bg-surface border border-recessed rounded-sm shadow-lg z-10 grid grid-cols-3 gap-0.5 p-1.5 w-[200px]", children: MONTH_ABBREVS2.map((name, index) => /* @__PURE__ */ jsx131(
            "button",
            {
              type: "button",
              onClick: () => {
                onMonthSelect?.(index);
                setMonthSelectorOpen(false);
              },
              className: `
                      py-1.5 text-xs font-sans rounded-sm transition-colors text-center
                      ${index === getMonth2(viewDate) ? "bg-selection text-white font-medium" : "text-primary hover:bg-elevated"}
                    `,
              children: name
            },
            name
          )) })
        ] }),
        /* @__PURE__ */ jsxs116("div", { className: "relative", children: [
          /* @__PURE__ */ jsxs116(
            "button",
            {
              type: "button",
              onClick: () => {
                setYearSelectorOpen(!yearSelectorOpen);
                setMonthSelectorOpen(false);
              },
              className: "flex items-center gap-0.5 px-1 py-0.5 text-sm font-sans font-medium text-primary hover:bg-elevated rounded-sm transition-colors",
              "aria-label": "Select year",
              children: [
                getYear2(viewDate),
                /* @__PURE__ */ jsx131(ChevronDownIcon12, { className: "w-3 h-3 text-tertiary", strokeWidth: 2 })
              ]
            }
          ),
          yearSelectorOpen && /* @__PURE__ */ jsx131(
            "div",
            {
              ref: yearListRef,
              className: "absolute left-1/2 -translate-x-1/2 top-full mt-1 bg-surface border border-recessed rounded-sm shadow-lg z-10 w-[80px] max-h-[200px] overflow-y-auto p-1",
              children: yearRange.map((year) => /* @__PURE__ */ jsx131(
                "button",
                {
                  type: "button",
                  "data-selected": year === getYear2(viewDate),
                  onClick: () => {
                    onYearSelect?.(year);
                    setYearSelectorOpen(false);
                  },
                  className: `
                      w-full py-1 text-xs font-sans rounded-sm transition-colors text-center
                      ${year === getYear2(viewDate) ? "bg-selection text-white font-medium" : "text-primary hover:bg-elevated"}
                    `,
                  children: year
                },
                year
              ))
            }
          )
        ] })
      ] }),
      showNavigation && onNextMonth ? /* @__PURE__ */ jsx131(
        "button",
        {
          type: "button",
          onClick: onNextMonth,
          className: "p-1 rounded hover:bg-elevated transition-colors",
          "aria-label": "Next month",
          children: /* @__PURE__ */ jsx131(ChevronRightIcon8, { className: "w-4 h-4 text-tertiary", strokeWidth: 2 })
        }
      ) : /* @__PURE__ */ jsx131("div", { className: "w-6" })
    ] }),
    /* @__PURE__ */ jsxs116("div", { className: "p-2", children: [
      /* @__PURE__ */ jsx131("div", { className: "grid grid-cols-7 mb-1", children: WEEKDAY_LABELS2.map((day) => /* @__PURE__ */ jsx131(
        "div",
        {
          className: "text-center text-label-xs font-sans font-semibold uppercase text-tertiary py-1",
          children: day
        },
        day
      )) }),
      /* @__PURE__ */ jsx131("div", { className: "grid grid-cols-7", children: calendarDays.map((day) => {
        const dateKey = formatDate3(day, "yyyy-MM-dd");
        const rangeStart = isRangeStart(day);
        const rangeEnd = isRangeEnd(day);
        const inRange = isInRange(day);
        const isCurrentMonth = isSameMonth2(day, viewDate);
        const isTodayDate = isToday2(day);
        const allowed = isDateAllowed(day);
        return /* @__PURE__ */ jsx131(
          "button",
          {
            type: "button",
            disabled: !allowed || !isCurrentMonth,
            onClick: () => onDateClick(day),
            onMouseEnter: () => onDateHover(day),
            onMouseLeave: () => onDateHover(null),
            className: `
                  w-9 h-9 text-sm font-sans transition-colors
                  ${rangeStart || rangeEnd ? "bg-selection text-white font-medium rounded-sm" : inRange ? "bg-selection/15 text-primary" : isCurrentMonth ? allowed ? "text-primary hover:bg-elevated rounded-sm" : "text-disabled cursor-not-allowed" : "text-disabled/40 cursor-default"}
                  ${isTodayDate && !(rangeStart || rangeEnd) ? "ring-1 ring-inset ring-accent rounded-sm" : ""}
                `,
            children: formatDate3(day, "d")
          },
          dateKey
        );
      }) })
    ] })
  ] });
}
function DateRangePicker({
  value = { start: null, end: null },
  onChange,
  placeholder = "Select date range...",
  format: formatProp,
  displayFormat,
  min,
  minDate: minDateProp,
  max,
  maxDate: maxDateProp,
  disabled = false,
  isDateDisabled,
  error = false,
  errorMessage,
  label,
  helperText,
  showPresets = true,
  presets: presets2,
  size = "md",
  className = ""
}) {
  const dateFormat = formatProp || displayFormat || "MMM d, yyyy";
  const minConstraint = min || minDateProp;
  const maxConstraint = max || maxDateProp;
  const activePresets = presets2 || getDefaultPresets();
  const [isOpen, setIsOpen] = useState29(false);
  const [leftViewDate, setLeftViewDate] = useState29(
    value.start || /* @__PURE__ */ new Date()
  );
  const [hoverDate, setHoverDate] = useState29(null);
  const [selectionStart, setSelectionStart] = useState29(null);
  const [activePresetId, setActivePresetId] = useState29(null);
  const [isEditingStart, setIsEditingStart] = useState29(false);
  const [isEditingEnd, setIsEditingEnd] = useState29(false);
  const [startInputText, setStartInputText] = useState29("");
  const [endInputText, setEndInputText] = useState29("");
  const containerRef = useRef21(null);
  const triggerRef = useRef21(null);
  const startInputRef = useRef21(null);
  const endInputRef = useRef21(null);
  const rightViewDate = useMemo13(() => addMonths2(leftViewDate, 1), [leftViewDate]);
  const yearRange = useMemo13(() => {
    const currentYear = getYear2(/* @__PURE__ */ new Date());
    const startYear = minConstraint ? Math.min(getYear2(minConstraint), currentYear - 10) : currentYear - 100;
    const endYear = maxConstraint ? Math.max(getYear2(maxConstraint), currentYear + 5) : currentYear + 20;
    return Array.from(
      { length: endYear - startYear + 1 },
      (_, i) => startYear + i
    );
  }, [minConstraint, maxConstraint]);
  const isDateAllowed = useCallback18((date) => {
    const day = startOfDay2(date);
    if (minConstraint && isBefore2(day, startOfDay2(minConstraint))) return false;
    if (maxConstraint && isAfter2(day, startOfDay2(maxConstraint))) return false;
    if (isDateDisabled?.(date)) return false;
    return true;
  }, [minConstraint, maxConstraint, isDateDisabled]);
  const goToPreviousMonth = useCallback18(() => {
    setLeftViewDate((prev) => subMonths2(prev, 1));
  }, []);
  const goToNextMonth = useCallback18(() => {
    setLeftViewDate((prev) => addMonths2(prev, 1));
  }, []);
  const handleLeftMonthSelect = useCallback18((monthIndex) => {
    setLeftViewDate((prev) => setDateMonth2(prev, monthIndex));
  }, []);
  const handleLeftYearSelect = useCallback18((year) => {
    setLeftViewDate((prev) => setDateYear2(prev, year));
  }, []);
  const handleRightMonthSelect = useCallback18((monthIndex) => {
    const newRight = setDateMonth2(rightViewDate, monthIndex);
    setLeftViewDate(subMonths2(newRight, 1));
  }, [rightViewDate]);
  const handleRightYearSelect = useCallback18((year) => {
    const newRight = setDateYear2(rightViewDate, year);
    setLeftViewDate(subMonths2(newRight, 1));
  }, [rightViewDate]);
  const handleDateClick = useCallback18((date) => {
    if (!isDateAllowed(date)) return;
    if (!selectionStart) {
      setSelectionStart(date);
      setActivePresetId(null);
    } else {
      const start = isBefore2(selectionStart, date) ? selectionStart : date;
      const end = isBefore2(selectionStart, date) ? date : selectionStart;
      onChange?.({ start: startOfDay2(start), end: startOfDay2(end) });
      setSelectionStart(null);
      setActivePresetId("custom");
      setIsOpen(false);
    }
  }, [selectionStart, isDateAllowed, onChange]);
  const handlePresetSelect = useCallback18((preset) => {
    if (preset.id === "custom") {
      setSelectionStart(null);
      setActivePresetId("custom");
      return;
    }
    const range = preset.getValue();
    onChange?.(range);
    setActivePresetId(preset.id);
    setSelectionStart(null);
    if (range.start) {
      setLeftViewDate(range.start);
    }
    setIsOpen(false);
  }, [onChange]);
  const handleClear = useCallback18((e) => {
    e.stopPropagation();
    onChange?.({ start: null, end: null });
    setSelectionStart(null);
    setActivePresetId(null);
  }, [onChange]);
  const handleStartInputFocus = useCallback18(() => {
    setIsEditingStart(true);
    setStartInputText(value.start ? formatDate3(value.start, INPUT_PARSE_FORMAT2) : "");
  }, [value.start]);
  const handleEndInputFocus = useCallback18(() => {
    setIsEditingEnd(true);
    setEndInputText(value.end ? formatDate3(value.end, INPUT_PARSE_FORMAT2) : "");
  }, [value.end]);
  const commitStartInput = useCallback18(() => {
    setIsEditingStart(false);
    const trimmed = startInputText.trim();
    if (!trimmed) return;
    const parsed = parse2(trimmed, INPUT_PARSE_FORMAT2, /* @__PURE__ */ new Date());
    if (isValid2(parsed) && isDateAllowed(parsed)) {
      const newStart = startOfDay2(parsed);
      if (value.end && isAfter2(newStart, value.end)) {
        onChange?.({ start: value.end, end: newStart });
      } else {
        onChange?.({ start: newStart, end: value.end });
      }
      setActivePresetId("custom");
    }
  }, [startInputText, isDateAllowed, value.end, onChange]);
  const commitEndInput = useCallback18(() => {
    setIsEditingEnd(false);
    const trimmed = endInputText.trim();
    if (!trimmed) return;
    const parsed = parse2(trimmed, INPUT_PARSE_FORMAT2, /* @__PURE__ */ new Date());
    if (isValid2(parsed) && isDateAllowed(parsed)) {
      const newEnd = startOfDay2(parsed);
      if (value.start && isBefore2(newEnd, value.start)) {
        onChange?.({ start: newEnd, end: value.start });
      } else {
        onChange?.({ start: value.start, end: newEnd });
      }
      setActivePresetId("custom");
    }
  }, [endInputText, isDateAllowed, value.start, onChange]);
  const handleStartInputKeyDown = useCallback18((e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitStartInput();
    }
    if (e.key === "Escape") {
      setIsEditingStart(false);
    }
    if (e.key === "Tab" && !e.shiftKey) {
      e.preventDefault();
      commitStartInput();
      endInputRef.current?.focus();
    }
  }, [commitStartInput]);
  const handleEndInputKeyDown = useCallback18((e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitEndInput();
    }
    if (e.key === "Escape") {
      setIsEditingEnd(false);
    }
  }, [commitEndInput]);
  const toggleCalendar = useCallback18(() => {
    if (disabled) return;
    setIsOpen((prev) => {
      if (!prev && value.start) {
        setLeftViewDate(value.start);
      }
      return !prev;
    });
    setSelectionStart(null);
  }, [disabled, value.start]);
  useEffect22(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSelectionStart(null);
        if (isEditingStart) commitStartInput();
        if (isEditingEnd) commitEndInput();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, isEditingStart, isEditingEnd, commitStartInput, commitEndInput]);
  useEffect22(() => {
    if (value.start && !isOpen) {
      setLeftViewDate(value.start);
    }
  }, [value.start, isOpen]);
  const triggerDisplayValue = useMemo13(() => {
    if (value.start && value.end) {
      return `${formatDate3(value.start, dateFormat)} - ${formatDate3(value.end, dateFormat)}`;
    }
    return "";
  }, [value, dateFormat]);
  const startDisplayValue = useMemo13(() => {
    if (isEditingStart) return startInputText;
    if (value.start) return formatDate3(value.start, INPUT_PARSE_FORMAT2);
    return "";
  }, [isEditingStart, startInputText, value.start]);
  const endDisplayValue = useMemo13(() => {
    if (isEditingEnd) return endInputText;
    if (value.end) return formatDate3(value.end, INPUT_PARSE_FORMAT2);
    return "";
  }, [isEditingEnd, endInputText, value.end]);
  const sizeClasses4 = {
    sm: "text-xs py-1.5 px-2",
    md: FIELD_CONTROL_SIZE,
    lg: "text-base py-2.5 px-4"
  };
  return /* @__PURE__ */ jsxs116("div", { ref: containerRef, className: `relative ${className}`, children: [
    label && /* @__PURE__ */ jsx131("label", { className: FIELD_LABEL_CHROME, children: label }),
    /* @__PURE__ */ jsxs116("div", { ref: triggerRef, className: "relative", children: [
      /* @__PURE__ */ jsx131(
        "input",
        {
          type: "text",
          readOnly: true,
          value: triggerDisplayValue,
          placeholder,
          disabled,
          onClick: toggleCalendar,
          "aria-label": label || "Date range input",
          "aria-haspopup": "dialog",
          "aria-expanded": isOpen,
          "aria-invalid": error || void 0,
          "aria-errormessage": error && errorMessage ? "daterangepicker-error" : void 0,
          className: `
            w-full font-sans bg-surface border rounded-sm cursor-pointer
            ${sizeClasses4[size]}
            ${error ? "border-error focus:ring-error/20" : "border-recessed focus:ring-tertiary/30"}
            ${disabled ? "opacity-50 cursor-not-allowed bg-elevated" : "hover:border-secondary"}
            focus:outline-none focus:ring-2 focus:border-secondary
            pr-16 text-primary placeholder:text-tertiary
          `
        }
      ),
      /* @__PURE__ */ jsxs116("div", { className: "absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1", children: [
        triggerDisplayValue && !disabled && /* @__PURE__ */ jsx131(
          "button",
          {
            type: "button",
            onClick: handleClear,
            className: "p-0.5 rounded hover:bg-elevated transition-colors",
            "aria-label": "Clear date range",
            children: /* @__PURE__ */ jsx131(XIcon9, { className: "w-3.5 h-3.5 text-tertiary", strokeWidth: 2 })
          }
        ),
        /* @__PURE__ */ jsx131(CalendarIcon3, { className: "w-4 h-4 text-tertiary pointer-events-none", strokeWidth: 2 })
      ] })
    ] }),
    (helperText || errorMessage) && /* @__PURE__ */ jsx131(
      "p",
      {
        id: error && errorMessage ? "daterangepicker-error" : void 0,
        role: error ? "alert" : void 0,
        className: `mt-1 text-xs font-sans ${error ? "text-accent" : "text-tertiary"}`,
        children: error ? errorMessage : helperText
      }
    ),
    isOpen && /* @__PURE__ */ jsxs116(
      "div",
      {
        role: "dialog",
        "aria-label": "Choose date range",
        "aria-modal": "false",
        className: "absolute top-full left-0 mt-1 bg-surface border border-recessed rounded-sm shadow-lg z-50 flex animate-fade-in",
        children: [
          showPresets && /* @__PURE__ */ jsxs116("div", { className: "w-[140px] border-r border-recessed p-2 flex flex-col", children: [
            /* @__PURE__ */ jsx131("div", { className: "text-label-xs font-sans font-semibold uppercase text-tertiary px-2 py-1 mb-0.5", children: "Presets" }),
            /* @__PURE__ */ jsx131("div", { className: "flex-1 overflow-y-auto", children: activePresets.map((preset) => /* @__PURE__ */ jsx131(
              "button",
              {
                type: "button",
                onClick: () => handlePresetSelect(preset),
                className: `
                      w-full text-left px-2 py-1.5 text-xs font-sans rounded-sm transition-colors
                      ${activePresetId === preset.id ? "bg-selection/15 text-primary font-medium" : "text-secondary hover:text-primary hover:bg-elevated"}
                    `,
                children: preset.label
              },
              preset.id
            )) })
          ] }),
          /* @__PURE__ */ jsxs116("div", { children: [
            /* @__PURE__ */ jsxs116("div", { className: "flex", children: [
              /* @__PURE__ */ jsx131(
                CalendarPanel,
                {
                  viewDate: leftViewDate,
                  selectedStart: value.start,
                  selectedEnd: value.end,
                  selectionStart,
                  hoverDate,
                  isDateAllowed,
                  onDateClick: handleDateClick,
                  onDateHover: setHoverDate,
                  showNavigation: true,
                  onPrevMonth: goToPreviousMonth,
                  onNextMonth: void 0,
                  onMonthSelect: handleLeftMonthSelect,
                  onYearSelect: handleLeftYearSelect,
                  yearRange
                }
              ),
              /* @__PURE__ */ jsx131("div", { className: "border-l border-recessed", children: /* @__PURE__ */ jsx131(
                CalendarPanel,
                {
                  viewDate: rightViewDate,
                  selectedStart: value.start,
                  selectedEnd: value.end,
                  selectionStart,
                  hoverDate,
                  isDateAllowed,
                  onDateClick: handleDateClick,
                  onDateHover: setHoverDate,
                  showNavigation: true,
                  onPrevMonth: void 0,
                  onNextMonth: goToNextMonth,
                  onMonthSelect: handleRightMonthSelect,
                  onYearSelect: handleRightYearSelect,
                  yearRange
                }
              ) })
            ] }),
            /* @__PURE__ */ jsxs116("div", { className: "px-3 py-2 border-t border-recessed", children: [
              /* @__PURE__ */ jsxs116("div", { className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsxs116("div", { className: "flex-1", children: [
                  /* @__PURE__ */ jsx131("label", { className: "block text-label-xs font-sans font-semibold uppercase text-tertiary mb-0.5", children: "Start" }),
                  /* @__PURE__ */ jsx131(
                    "input",
                    {
                      ref: startInputRef,
                      type: "text",
                      value: startDisplayValue,
                      placeholder: "MM/DD/YYYY",
                      onFocus: handleStartInputFocus,
                      onBlur: () => commitStartInput(),
                      onChange: (e) => setStartInputText(e.target.value),
                      onKeyDown: handleStartInputKeyDown,
                      className: "w-full px-2 py-1 text-xs font-sans bg-elevated border border-recessed rounded-sm\n                      focus:outline-none focus:ring-1 focus:ring-tertiary/30 focus:border-secondary\n                      text-primary placeholder:text-tertiary"
                    }
                  )
                ] }),
                /* @__PURE__ */ jsx131("span", { className: "text-tertiary text-xs font-sans mt-4", children: "--" }),
                /* @__PURE__ */ jsxs116("div", { className: "flex-1", children: [
                  /* @__PURE__ */ jsx131("label", { className: "block text-label-xs font-sans font-semibold uppercase text-tertiary mb-0.5", children: "End" }),
                  /* @__PURE__ */ jsx131(
                    "input",
                    {
                      ref: endInputRef,
                      type: "text",
                      value: endDisplayValue,
                      placeholder: "MM/DD/YYYY",
                      onFocus: handleEndInputFocus,
                      onBlur: () => commitEndInput(),
                      onChange: (e) => setEndInputText(e.target.value),
                      onKeyDown: handleEndInputKeyDown,
                      className: "w-full px-2 py-1 text-xs font-sans bg-elevated border border-recessed rounded-sm\n                      focus:outline-none focus:ring-1 focus:ring-tertiary/30 focus:border-secondary\n                      text-primary placeholder:text-tertiary"
                    }
                  )
                ] })
              ] }),
              /* @__PURE__ */ jsx131("div", { className: "mt-1.5 text-caption-xs font-sans text-quaternary", children: selectionStart ? `Select end date (from ${formatDate3(selectionStart, dateFormat)})` : value.start && value.end ? `${formatDate3(value.start, dateFormat)} - ${formatDate3(value.end, dateFormat)}` : "Click a date to start selection" })
            ] })
          ] })
        ]
      }
    )
  ] });
}

// src/forms/Combobox.tsx
import { useState as useState30, useRef as useRef22, useCallback as useCallback19, useMemo as useMemo14, useEffect as useEffect23 } from "react";
import {
  ComboBox as AriaComboBox,
  Input as AriaInput,
  Button as AriaButton,
  Popover as AriaPopover,
  ListBox as AriaListBox,
  ListBoxItem as AriaListBoxItem,
  Label as AriaLabel,
  Text as AriaText,
  Group as AriaGroup
} from "react-aria-components";
import { clsx as clsx17 } from "clsx";
import {
  ChevronDownIcon as ChevronDownIcon13,
  XIcon as XIcon10,
  CheckIcon as CheckIcon11,
  PlusIcon as PlusIcon2,
  SearchIcon as SearchIcon8,
  LoaderIcon
} from "lucide-react";
import { Fragment as Fragment31, jsx as jsx132, jsxs as jsxs117 } from "react/jsx-runtime";
var DEBOUNCE_MS2 = 300;
function sameOptionIds(a, b) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((option, index) => option.id === b[index].id);
}
var CREATE_ID_PREFIX = "__create__";
var sizeConfig = {
  sm: {
    group: "min-h-[32px] text-xs px-2",
    input: "text-xs py-0.5",
    tag: "text-caption-xs leading-tight px-1.5 py-0.5 gap-0.5",
    tagClose: "w-2.5 h-2.5",
    icon: "w-3.5 h-3.5",
    option: "text-xs py-1 px-2",
    optionDesc: "text-caption-xs"
  },
  md: {
    // Canonical field height + padding (field-chrome); text size inherits
    // like Input/Select/Textarea so paired controls render equal.
    group: `${FIELD_CONTROL_MIN_H} px-4`,
    input: "py-1",
    tag: "text-xs leading-tight px-2 py-0.5 gap-1",
    tagClose: "w-3 h-3",
    icon: "w-4 h-4",
    option: "text-sm py-1.5 px-2",
    optionDesc: "text-xs"
  },
  lg: {
    group: "min-h-[48px] text-base px-2",
    input: "text-base py-1.5",
    tag: "text-sm leading-tight px-2.5 py-1 gap-1",
    tagClose: "w-3.5 h-3.5",
    icon: "w-4 h-4",
    option: "text-base py-2 px-3",
    optionDesc: "text-sm"
  }
};
function HighlightedText({ text, query }) {
  if (!query.trim()) return /* @__PURE__ */ jsx132(Fragment31, { children: text });
  const parts = [];
  const lower = text.toLowerCase();
  const term = query.toLowerCase().trim();
  let last = 0;
  let idx = lower.indexOf(term);
  let key = 0;
  while (idx !== -1) {
    if (idx > last) {
      parts.push(/* @__PURE__ */ jsx132("span", { children: text.slice(last, idx) }, `t${key++}`));
    }
    parts.push(
      /* @__PURE__ */ jsx132(
        "mark",
        {
          className: "bg-warning/20 text-inherit rounded-sm",
          children: text.slice(idx, idx + term.length)
        },
        `m${key++}`
      )
    );
    last = idx + term.length;
    idx = lower.indexOf(term, last);
  }
  if (last < text.length) {
    parts.push(/* @__PURE__ */ jsx132("span", { children: text.slice(last) }, `e${key}`));
  }
  return /* @__PURE__ */ jsx132(Fragment31, { children: parts });
}
function Combobox({
  options,
  value,
  defaultValue,
  onChange,
  onSearch,
  placeholder = "Select...",
  label,
  description,
  errorMessage,
  isMulti = false,
  isCreatable = false,
  isLoading = false,
  isDisabled = false,
  isClearable = true,
  size = "md",
  emptyMessage = "No options found",
  createLabel,
  groupBy,
  renderOption,
  className
}) {
  const [inputValue, setInputValue] = useState30("");
  const [multiOpen, setMultiOpen] = useState30(false);
  const [asyncResults, setAsyncResults] = useState30(null);
  const optionsRef = useRef22(options);
  optionsRef.current = options;
  const [asyncLoading, setAsyncLoading] = useState30(false);
  const [internalValue, setInternalValue] = useState30(defaultValue);
  const debounceRef = useRef22();
  const searchCounter = useRef22(0);
  const justSelected = useRef22(false);
  const inputRef = useRef22(null);
  const sizes = sizeConfig[size];
  const isControlled = value !== void 0;
  const effective = isControlled ? value : internalValue;
  const showLoading = isLoading || asyncLoading;
  const hasError = !!errorMessage;
  const selectedIds = useMemo14(() => {
    if (isMulti) {
      return Array.isArray(effective) ? effective : [];
    }
    return typeof effective === "string" && effective !== "" ? [effective] : [];
  }, [isMulti, effective]);
  const selectedOptions = useMemo14(
    () => selectedIds.map((id) => options.find((o) => o.id === id)).filter((o) => o !== void 0),
    [selectedIds, options]
  );
  const hasValue = selectedIds.length > 0;
  const filteredOptions = useMemo14(() => {
    if (onSearch && asyncResults !== null) {
      return sameOptionIds(asyncResults.against, options) ? asyncResults.results : options;
    }
    if (!inputValue.trim()) return options;
    const q = inputValue.toLowerCase();
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.description?.toLowerCase().includes(q) ?? false)
    );
  }, [options, asyncResults, inputValue, onSearch]);
  const displayItems = useMemo14(() => {
    const items = [...filteredOptions];
    if (isCreatable && inputValue.trim()) {
      const exists = options.some(
        (o) => o.label.toLowerCase() === inputValue.trim().toLowerCase()
      );
      if (!exists) {
        const factory = createLabel || ((v) => `Create "${v}"`);
        items.push({
          id: `${CREATE_ID_PREFIX}${inputValue.trim()}`,
          label: factory(inputValue.trim())
        });
      }
    }
    return items;
  }, [filteredOptions, isCreatable, inputValue, createLabel, options]);
  const grouped = useMemo14(() => {
    const groups = {};
    const ungrouped = [];
    let hasGroups = false;
    for (const item of displayItems) {
      const g = groupBy ? groupBy(item) : item.group;
      if (g) {
        hasGroups = true;
        if (!groups[g]) groups[g] = [];
        groups[g].push(item);
      } else {
        ungrouped.push(item);
      }
    }
    return { groups, ungrouped, hasGroups };
  }, [displayItems, groupBy]);
  const updateValue = useCallback19(
    (next) => {
      if (!isControlled) {
        setInternalValue(next);
      }
      onChange?.(next);
    },
    [isControlled, onChange]
  );
  const handleInputChange = useCallback19(
    (next) => {
      if (justSelected.current) {
        justSelected.current = false;
        setInputValue(next);
        return;
      }
      setInputValue(next);
      if (onSearch) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const id = ++searchCounter.current;
        debounceRef.current = setTimeout(async () => {
          setAsyncLoading(true);
          try {
            const result = onSearch(next);
            const resolved = result instanceof Promise ? await result : result;
            if (id === searchCounter.current) {
              setAsyncResults({ results: resolved, against: optionsRef.current });
            }
          } catch {
            if (id === searchCounter.current) {
              setAsyncResults({ results: [], against: optionsRef.current });
            }
          } finally {
            if (id === searchCounter.current) setAsyncLoading(false);
          }
        }, DEBOUNCE_MS2);
      }
    },
    [onSearch]
  );
  const handleSelectionChange = useCallback19(
    (key) => {
      if (key === null) return;
      const id = String(key);
      const isCreate = id.startsWith(CREATE_ID_PREFIX);
      const resolvedId = isCreate ? id.slice(CREATE_ID_PREFIX.length) : id;
      if (isMulti) {
        justSelected.current = true;
        const current = selectedIds;
        const next = current.includes(resolvedId) ? current.filter((i) => i !== resolvedId) : [...current, resolvedId];
        updateValue(next);
        setInputValue("");
        requestAnimationFrame(() => inputRef.current?.focus());
      } else {
        justSelected.current = true;
        updateValue(resolvedId);
      }
    },
    [isMulti, selectedIds, updateValue]
  );
  const handleMultiOpenChange = useCallback19((open) => {
    if (!open && justSelected.current) {
      justSelected.current = false;
      requestAnimationFrame(() => setMultiOpen(true));
      return;
    }
    setMultiOpen(open);
  }, []);
  const handleRemoveTag = useCallback19(
    (id, e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isMulti) {
        updateValue(selectedIds.filter((i) => i !== id));
      }
    },
    [isMulti, selectedIds, updateValue]
  );
  const handleClear = useCallback19(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      updateValue(isMulti ? [] : "");
      setInputValue("");
      setAsyncResults(null);
    },
    [isMulti, updateValue]
  );
  const handleGroupKeyDown = useCallback19(
    (e) => {
      if (e.key === "Backspace" && isMulti && !inputValue && selectedIds.length > 0) {
        updateValue(selectedIds.slice(0, -1));
      }
    },
    [isMulti, inputValue, selectedIds, updateValue]
  );
  useEffect23(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);
  const selectionProps = useMemo14(() => {
    if (isMulti) return { selectedKey: null };
    if (isControlled) {
      const v = typeof value === "string" && value ? value : null;
      return { selectedKey: v };
    }
    if (typeof defaultValue === "string" && defaultValue) {
      return { defaultSelectedKey: defaultValue };
    }
    return {};
  }, [isMulti, isControlled, value, defaultValue]);
  const openProps = useMemo14(() => {
    if (isMulti) {
      return { isOpen: multiOpen, onOpenChange: handleMultiOpenChange };
    }
    return {};
  }, [isMulti, multiOpen, handleMultiOpenChange]);
  function renderItem(item) {
    const selected = selectedIds.includes(item.id);
    const isCreate = item.id.startsWith(CREATE_ID_PREFIX);
    return /* @__PURE__ */ jsx132(
      AriaListBoxItem,
      {
        id: item.id,
        textValue: item.label,
        isDisabled: item.disabled,
        className: ({ isFocused, isHovered }) => clsx17(
          "w-full rounded-sm flex items-center gap-2 outline-none transition-colors cursor-pointer",
          sizes.option,
          item.disabled && "opacity-50 cursor-not-allowed",
          (isFocused || isHovered) && !item.disabled && "bg-elevated"
        ),
        children: ({ isFocused }) => /* @__PURE__ */ jsxs117(Fragment31, { children: [
          isMulti && !isCreate && /* @__PURE__ */ jsx132(
            "div",
            {
              className: clsx17(
                "w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors",
                selected ? "bg-selection border-selection" : "border-recessed"
              ),
              children: selected && /* @__PURE__ */ jsx132(
                CheckIcon11,
                {
                  className: "w-3 h-3 text-white",
                  strokeWidth: 2.5
                }
              )
            }
          ),
          isCreate && /* @__PURE__ */ jsx132(
            PlusIcon2,
            {
              className: clsx17(sizes.icon, "text-accent flex-shrink-0"),
              strokeWidth: 2
            }
          ),
          renderOption ? renderOption(item, {
            isSelected: selected,
            isHighlighted: isFocused
          }) : /* @__PURE__ */ jsxs117("div", { className: "flex-1 min-w-0", children: [
            /* @__PURE__ */ jsxs117("div", { className: "flex items-center gap-2", children: [
              item.icon && /* @__PURE__ */ jsx132("span", { className: "flex-shrink-0", children: item.icon }),
              /* @__PURE__ */ jsx132(
                "span",
                {
                  className: clsx17(
                    "font-sans truncate",
                    selected && !isMulti ? "text-selection font-medium" : "text-primary",
                    isCreate && "text-accent font-medium"
                  ),
                  children: /* @__PURE__ */ jsx132(HighlightedText, { text: item.label, query: inputValue })
                }
              )
            ] }),
            item.description && /* @__PURE__ */ jsx132(
              "p",
              {
                className: clsx17(
                  "font-sans text-tertiary truncate mt-0.5",
                  sizes.optionDesc
                ),
                children: /* @__PURE__ */ jsx132(
                  HighlightedText,
                  {
                    text: item.description,
                    query: inputValue
                  }
                )
              }
            )
          ] }),
          !isMulti && selected && !isCreate && /* @__PURE__ */ jsx132(
            CheckIcon11,
            {
              className: clsx17(
                sizes.icon,
                "text-selection flex-shrink-0 ml-auto"
              ),
              strokeWidth: 2
            }
          )
        ] })
      },
      item.id
    );
  }
  return /* @__PURE__ */ jsxs117("div", { className: clsx17("relative", className), children: [
    /* @__PURE__ */ jsxs117(
      AriaComboBox,
      {
        ...selectionProps,
        ...openProps,
        inputValue,
        onInputChange: handleInputChange,
        onSelectionChange: handleSelectionChange,
        isDisabled,
        menuTrigger: "input",
        allowsCustomValue: isCreatable || isMulti,
        defaultFilter: () => true,
        className: "relative",
        "aria-label": label || "Combobox",
        children: [
          label && /* @__PURE__ */ jsx132(AriaLabel, { className: FIELD_LABEL_CHROME, children: label }),
          description && /* @__PURE__ */ jsx132(
            AriaText,
            {
              slot: "description",
              className: "block text-xs font-sans text-tertiary mb-1",
              children: description
            }
          ),
          /* @__PURE__ */ jsxs117(
            AriaGroup,
            {
              className: clsx17(
                "w-full font-sans bg-surface border rounded-sm flex items-center flex-wrap gap-1",
                sizes.group,
                hasError ? "border-error focus-within:ring-1 focus-within:ring-error/50" : "border-recessed focus-within:ring-1 focus-within:ring-accent/30 focus-within:border-secondary",
                isDisabled ? "opacity-50 cursor-not-allowed bg-elevated" : "hover:border-secondary"
              ),
              onKeyDown: handleGroupKeyDown,
              children: [
                isMulti && selectedOptions.map((opt) => /* @__PURE__ */ jsxs117(
                  "span",
                  {
                    className: clsx17(
                      "inline-flex items-center bg-elevated rounded font-sans text-primary",
                      sizes.tag
                    ),
                    children: [
                      opt.icon && /* @__PURE__ */ jsx132("span", { className: "flex-shrink-0", children: opt.icon }),
                      /* @__PURE__ */ jsx132("span", { className: "truncate max-w-[120px]", children: opt.label }),
                      /* @__PURE__ */ jsx132(
                        "button",
                        {
                          type: "button",
                          onClick: (e) => handleRemoveTag(opt.id, e),
                          className: "flex-shrink-0 rounded-sm hover:bg-recessed transition-colors",
                          "aria-label": `Remove ${opt.label}`,
                          children: /* @__PURE__ */ jsx132(XIcon10, { className: sizes.tagClose, strokeWidth: 2 })
                        }
                      )
                    ]
                  },
                  opt.id
                )),
                /* @__PURE__ */ jsx132(
                  AriaInput,
                  {
                    ref: inputRef,
                    className: clsx17(
                      "flex-1 min-w-[60px] bg-transparent outline-none font-sans text-primary placeholder:text-tertiary",
                      sizes.input
                    ),
                    placeholder: isMulti && selectedOptions.length > 0 ? "" : !isMulti && hasValue && !inputValue ? selectedOptions[0]?.label ?? placeholder : placeholder
                  }
                ),
                /* @__PURE__ */ jsxs117("div", { className: "flex items-center gap-0.5 ml-auto flex-shrink-0", children: [
                  showLoading && /* @__PURE__ */ jsx132(
                    LoaderIcon,
                    {
                      className: clsx17(sizes.icon, "text-tertiary animate-spin"),
                      strokeWidth: 2
                    }
                  ),
                  isClearable && hasValue && !isDisabled && /* @__PURE__ */ jsx132(
                    "button",
                    {
                      type: "button",
                      onClick: handleClear,
                      className: "p-0.5 rounded-sm hover:bg-elevated transition-colors",
                      "aria-label": "Clear selection",
                      children: /* @__PURE__ */ jsx132(
                        XIcon10,
                        {
                          className: clsx17(sizes.icon, "text-tertiary"),
                          strokeWidth: 2
                        }
                      )
                    }
                  ),
                  /* @__PURE__ */ jsx132(AriaButton, { className: "p-0.5 rounded-sm hover:bg-elevated transition-colors outline-none", children: /* @__PURE__ */ jsx132(
                    ChevronDownIcon13,
                    {
                      className: clsx17(sizes.icon, "text-tertiary"),
                      strokeWidth: 2
                    }
                  ) })
                ] })
              ]
            }
          ),
          /* @__PURE__ */ jsx132(
            AriaPopover,
            {
              className: "w-[var(--trigger-width)] bg-surface border border-recessed rounded-sm shadow-lg z-50 overflow-hidden",
              offset: 4,
              children: showLoading ? (
                /* Loading skeleton */
                /* @__PURE__ */ jsx132("div", { className: "p-2 space-y-1", role: "status", "aria-label": "Loading", children: [1, 2, 3].map((i) => /* @__PURE__ */ jsxs117(
                  "div",
                  {
                    className: "flex items-center gap-2 px-2 py-1.5 animate-pulse",
                    children: [
                      /* @__PURE__ */ jsx132("div", { className: "w-4 h-4 rounded bg-recessed flex-shrink-0" }),
                      /* @__PURE__ */ jsxs117("div", { className: "flex-1 space-y-1", children: [
                        /* @__PURE__ */ jsx132(
                          "div",
                          {
                            className: "h-3 rounded bg-recessed",
                            style: { width: `${60 + i * 10}%` }
                          }
                        ),
                        /* @__PURE__ */ jsx132(
                          "div",
                          {
                            className: "h-2.5 rounded bg-recessed",
                            style: { width: `${40 + i * 5}%` }
                          }
                        )
                      ] })
                    ]
                  },
                  i
                )) })
              ) : displayItems.length === 0 ? (
                /* Empty state */
                /* @__PURE__ */ jsxs117("div", { className: "px-3 py-6 text-center", children: [
                  /* @__PURE__ */ jsx132(
                    SearchIcon8,
                    {
                      className: "w-5 h-5 text-tertiary mx-auto mb-2",
                      strokeWidth: 1.5
                    }
                  ),
                  /* @__PURE__ */ jsx132("p", { className: "text-sm font-sans text-tertiary", children: emptyMessage })
                ] })
              ) : (
                /* Options list */
                /* @__PURE__ */ jsx132(
                  AriaListBox,
                  {
                    className: "max-h-60 overflow-y-auto p-1 outline-none",
                    selectionMode: "single",
                    children: grouped.hasGroups ? /* @__PURE__ */ jsxs117(Fragment31, { children: [
                      Object.entries(grouped.groups).map(
                        ([groupName, groupItems]) => /* @__PURE__ */ jsxs117("div", { role: "group", "aria-label": groupName, children: [
                          /* @__PURE__ */ jsx132("div", { className: "px-2 py-1 text-label-xs font-sans font-semibold uppercase text-tertiary", children: groupName }),
                          groupItems.map((item) => renderItem(item))
                        ] }, groupName)
                      ),
                      grouped.ungrouped.map((item) => renderItem(item))
                    ] }) : displayItems.map((item) => renderItem(item))
                  }
                )
              )
            }
          )
        ]
      }
    ),
    hasError && /* @__PURE__ */ jsx132("p", { className: "mt-1 text-xs font-sans text-error", children: errorMessage })
  ] });
}

// src/forms/Slider.tsx
import {
  Slider as AriaSlider,
  Label as Label2,
  SliderOutput,
  SliderThumb,
  SliderTrack
} from "react-aria-components";
import { clsx as clsx18 } from "clsx";
import { Fragment as Fragment32, jsx as jsx133, jsxs as jsxs118 } from "react/jsx-runtime";
function Slider({
  label,
  showValue = true,
  formatValue,
  onChange,
  className,
  value,
  defaultValue,
  step = 0.01,
  ...props
}) {
  const format = formatValue || ((v) => v.toString());
  return /* @__PURE__ */ jsxs118(
    AriaSlider,
    {
      ...props,
      step,
      "aria-label": label || "Slider",
      value,
      defaultValue,
      onChange: (v) => {
        onChange?.(v);
      },
      className: clsx18(
        "grid gap-1",
        props.isDisabled && "opacity-50",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs118("div", { className: clsx18(
          "flex justify-between items-baseline",
          !label && !showValue && "sr-only"
        ), children: [
          label ? /* @__PURE__ */ jsx133(Label2, { className: "text-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary", children: label }) : /* @__PURE__ */ jsx133(Label2, { className: "sr-only", children: "Slider" }),
          /* @__PURE__ */ jsx133(SliderOutput, { className: clsx18(
            "text-2xl font-serif font-semibold text-primary tabular-nums",
            !showValue && "sr-only"
          ), children: ({ state }) => format(state.values[0]) })
        ] }),
        /* @__PURE__ */ jsx133(SliderTrack, { className: "relative h-6 w-full", children: ({ state }) => {
          const percentage = state.getThumbPercent(0) * 100;
          return /* @__PURE__ */ jsxs118(Fragment32, { children: [
            /* @__PURE__ */ jsx133(
              "div",
              {
                "data-slot": "slider-track",
                className: "absolute inset-x-0 top-1/2 -translate-y-1/2 h-1 bg-[rgb(var(--color-control-boundary))] rounded-full"
              }
            ),
            /* @__PURE__ */ jsx133(
              "div",
              {
                className: "absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-primary rounded-full",
                style: { width: `${percentage}%` }
              }
            ),
            /* @__PURE__ */ jsx133(
              SliderThumb,
              {
                index: 0,
                className: clsx18(
                  "w-4 h-4 rounded-full bg-surface border-2 border-primary shadow-sm",
                  "transition-shadow",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-selection focus-visible:ring-offset-2",
                  "data-[dragging]:shadow-md",
                  props.isDisabled ? "cursor-not-allowed border-tertiary" : "cursor-grab active:cursor-grabbing"
                ),
                style: { top: "50%" }
              }
            )
          ] });
        } })
      ]
    }
  );
}

// src/forms/Checkbox.tsx
import { CheckIcon as CheckIcon12 } from "lucide-react";
import { jsx as jsx134, jsxs as jsxs119 } from "react/jsx-runtime";
function Checkbox({
  label,
  checked = false,
  onChange,
  disabled = false,
  required = false,
  helperText,
  error,
  name,
  id,
  className = ""
}) {
  const checkboxId = id || name || label.toLowerCase().replace(/\s+/g, "-");
  const handleChange = (e) => {
    if (onChange) {
      onChange(e.target.checked);
    }
  };
  return /* @__PURE__ */ jsx134("div", { className, children: /* @__PURE__ */ jsxs119("div", { className: "flex items-start gap-3", children: [
    /* @__PURE__ */ jsx134("input", { type: "checkbox", id: checkboxId, name, checked, onChange: handleChange, disabled, required, className: "sr-only peer", "aria-invalid": error ? "true" : "false", "aria-describedby": error ? `${checkboxId}-error` : helperText ? `${checkboxId}-helper` : void 0 }),
    /* @__PURE__ */ jsx134("label", { htmlFor: checkboxId, className: `
            relative flex-shrink-0 w-5 h-5 border-2 rounded transition-all duration-200
            flex items-center justify-center
            ${checked ? "bg-selection border-selection" : "bg-surface border-recessed"}
            ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-secondary"}
            ${error ? "border-error" : ""}
            peer-focus-visible:ring-2 peer-focus-visible:ring-selection/40 peer-focus-visible:ring-offset-2
          `, children: checked && /* @__PURE__ */ jsx134(CheckIcon12, { className: "w-3 h-3 text-white", strokeWidth: 3 }) }),
    /* @__PURE__ */ jsxs119("div", { className: "flex-1 pt-0.5", children: [
      /* @__PURE__ */ jsxs119("label", { htmlFor: checkboxId, className: `text-sm font-sans text-secondary cursor-pointer ${disabled ? "opacity-50 cursor-not-allowed" : ""}`, children: [
        label,
        required && /* @__PURE__ */ jsx134("span", { className: "text-error ml-1", children: "*" })
      ] }),
      helperText && !error && /* @__PURE__ */ jsx134("p", { id: `${checkboxId}-helper`, className: "mt-1 text-xs font-sans text-tertiary", children: helperText }),
      error && /* @__PURE__ */ jsx134("p", { id: `${checkboxId}-error`, className: "mt-1 text-xs font-sans text-error", role: "alert", children: error })
    ] })
  ] }) });
}

// src/forms/FormHelperText.tsx
import { jsx as jsx135 } from "react/jsx-runtime";
function FormHelperText({ children, className = "" }) {
  return /* @__PURE__ */ jsx135("span", { className: `text-sm text-tertiary ${className}`, children });
}

// src/forms/FormLabel.tsx
import { jsx as jsx136, jsxs as jsxs120 } from "react/jsx-runtime";
function FormLabel({
  children,
  required = false,
  className = "",
  ...props
}) {
  return /* @__PURE__ */ jsxs120(
    "label",
    {
      className: `block text-sm font-sans font-medium text-primary mb-1 ${className}`,
      ...props,
      children: [
        children,
        required && /* @__PURE__ */ jsx136("span", { className: "text-error ml-1", children: "*" })
      ]
    }
  );
}

// src/forms/Input.tsx
import { AlertCircleIcon as AlertCircleIcon2 } from "lucide-react";
import { jsx as jsx137, jsxs as jsxs121 } from "react/jsx-runtime";
function Input({
  label,
  type = "text",
  placeholder,
  helperText,
  error,
  value,
  onChange,
  onBlur,
  disabled = false,
  required = false,
  fullWidth = true,
  name,
  id,
  autoComplete,
  min,
  max,
  step,
  className = "",
  ...restProps
}) {
  const inputId = id || name || (label ? label.toLowerCase().replace(/\s+/g, "-") : void 0);
  const handleChange = (e) => {
    if (onChange) {
      onChange(e);
    }
  };
  const baseInputStyles = FIELD_CONTROL_BASE;
  const borderStyles = error ? "border-2 border-error focus:border-error focus:ring-2 focus:ring-error/20" : "border border-recessed focus:border-secondary focus:ring-2 focus:ring-tertiary/30";
  const widthStyles = fullWidth ? "w-full" : "";
  const combinedInputStyles = `${baseInputStyles} ${borderStyles} ${className}`;
  return /* @__PURE__ */ jsxs121("div", { className: widthStyles, children: [
    label && /* @__PURE__ */ jsxs121("label", { htmlFor: inputId, className: FIELD_LABEL_CHROME, children: [
      label,
      required && /* @__PURE__ */ jsx137("span", { className: "text-error ml-1", children: "*" })
    ] }),
    /* @__PURE__ */ jsx137("input", { id: inputId, name, type, value, onChange: handleChange, onBlur, placeholder, disabled, required, autoComplete, min, max, step, className: combinedInputStyles, "aria-invalid": error ? "true" : "false", "aria-describedby": error ? `${inputId}-error` : helperText ? `${inputId}-helper` : void 0, ...restProps }),
    error && /* @__PURE__ */ jsxs121("div", { id: `${inputId}-error`, className: "flex items-center gap-1 mt-1 text-xs font-sans text-error", role: "alert", children: [
      /* @__PURE__ */ jsx137(AlertCircleIcon2, { className: "w-3 h-3 flex-shrink-0" }),
      /* @__PURE__ */ jsx137("span", { children: error })
    ] }),
    helperText && !error && /* @__PURE__ */ jsx137("p", { id: `${inputId}-helper`, className: "mt-1 text-xs font-sans text-tertiary", children: helperText })
  ] });
}

// src/forms/Select.tsx
import React39 from "react";
import { ChevronDownIcon as ChevronDownIcon14 } from "lucide-react";
import { jsx as jsx138, jsxs as jsxs122 } from "react/jsx-runtime";
function Select({
  label,
  options,
  children,
  value,
  onChange,
  placeholder = "Select an option",
  helperText,
  error,
  disabled = false,
  required = false,
  fullWidth = true,
  name,
  id,
  ariaLabel,
  "aria-label": ariaLabelAttribute,
  className = ""
}) {
  const generatedId = React39.useId();
  const selectId = id || name || (label ? label.toLowerCase().replace(/\s+/g, "-") : generatedId);
  const accessibleName = ariaLabel ?? ariaLabelAttribute;
  const hasEmptyOption = options?.some((option) => option.value === "") ?? false;
  const valueIsAnOption = value !== void 0 && value !== "" && (options?.some((option) => option.value === value) ?? false);
  const showPlaceholder = !children && !hasEmptyOption && !valueIsAnOption;
  const showsPrompt = showPlaceholder && value === "";
  const handleChange = (e) => {
    if (onChange) {
      onChange(e);
    }
  };
  const baseSelectStyles = `${FIELD_CONTROL_FRAME} ${showsPrompt ? "text-tertiary" : FIELD_CONTROL_TEXT} appearance-none pr-10`;
  const borderStyles = error ? "border-2 border-error focus:border-error focus:ring-2 focus:ring-error/20" : "border border-recessed focus:border-secondary focus:ring-2 focus:ring-tertiary/30";
  const widthStyles = fullWidth ? "w-full" : "";
  const combinedSelectStyles = `${baseSelectStyles} ${borderStyles} ${className}`;
  return /* @__PURE__ */ jsxs122("div", { className: widthStyles, children: [
    label && /* @__PURE__ */ jsxs122("label", { htmlFor: selectId, className: FIELD_LABEL_CHROME, children: [
      label,
      required && /* @__PURE__ */ jsx138("span", { className: "text-error ml-1", children: "*" })
    ] }),
    /* @__PURE__ */ jsxs122("div", { className: "relative", children: [
      /* @__PURE__ */ jsxs122("select", { id: selectId, name, value, onChange: handleChange, disabled, required, className: combinedSelectStyles, "aria-label": accessibleName, "aria-invalid": error ? "true" : "false", "aria-describedby": error ? `${selectId}-error` : helperText ? `${selectId}-helper` : void 0, children: [
        showPlaceholder && /* @__PURE__ */ jsx138("option", { value: "", disabled: true, className: "text-tertiary", children: placeholder }),
        children ? children : options?.map((option) => /* @__PURE__ */ jsx138("option", { value: option.value, disabled: option.disabled, className: FIELD_CONTROL_TEXT, children: option.label }, option.value))
      ] }),
      /* @__PURE__ */ jsx138(ChevronDownIcon14, { className: "absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-tertiary pointer-events-none" })
    ] }),
    error && /* @__PURE__ */ jsx138("div", { id: `${selectId}-error`, className: "flex items-center gap-1 mt-1 text-xs font-sans text-error", role: "alert", children: /* @__PURE__ */ jsx138("span", { children: error }) }),
    helperText && !error && /* @__PURE__ */ jsx138("p", { id: `${selectId}-helper`, className: "mt-1 text-xs font-sans text-tertiary", children: helperText })
  ] });
}

// src/forms/Textarea.tsx
import { AlertCircleIcon as AlertCircleIcon3 } from "lucide-react";
import { jsx as jsx139, jsxs as jsxs123 } from "react/jsx-runtime";
function Textarea({
  label,
  placeholder,
  helperText,
  error,
  value,
  onChange,
  onBlur,
  disabled = false,
  required = false,
  rows = 4,
  maxLength,
  showCharCount = false,
  fullWidth = true,
  name,
  id,
  className = ""
}) {
  const textareaId = id || name || label.toLowerCase().replace(/\s+/g, "-");
  const charCount = value?.length || 0;
  const handleChange = (e) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };
  const baseTextareaStyles = `${FIELD_CONTROL_BASE} resize-y`;
  const borderStyles = error ? "border-2 border-error focus:border-error focus:ring-2 focus:ring-error/20" : "border border-recessed focus:border-secondary focus:ring-2 focus:ring-tertiary/30";
  const widthStyles = fullWidth ? "w-full" : "";
  const combinedTextareaStyles = `${baseTextareaStyles} ${borderStyles} ${className}`;
  return /* @__PURE__ */ jsxs123("div", { className: widthStyles, children: [
    /* @__PURE__ */ jsxs123("div", { className: "flex items-center justify-between mb-2", children: [
      /* @__PURE__ */ jsxs123("label", { htmlFor: textareaId, className: FIELD_LABEL_BASE, children: [
        label,
        required && /* @__PURE__ */ jsx139("span", { className: "text-error ml-1", children: "*" })
      ] }),
      showCharCount && maxLength && /* @__PURE__ */ jsxs123("span", { className: `text-xs font-mono tabular-nums ${charCount > maxLength ? "text-error" : "text-tertiary"}`, children: [
        charCount,
        "/",
        maxLength
      ] })
    ] }),
    /* @__PURE__ */ jsx139("textarea", { id: textareaId, name, value, onChange: handleChange, onBlur, placeholder, disabled, required, rows, maxLength, className: combinedTextareaStyles, "aria-invalid": error ? "true" : "false", "aria-describedby": error ? `${textareaId}-error` : helperText ? `${textareaId}-helper` : void 0 }),
    error && /* @__PURE__ */ jsxs123("div", { id: `${textareaId}-error`, className: "flex items-center gap-1 mt-1 text-xs font-sans text-error", role: "alert", children: [
      /* @__PURE__ */ jsx139(AlertCircleIcon3, { className: "w-3 h-3 flex-shrink-0" }),
      /* @__PURE__ */ jsx139("span", { children: error })
    ] }),
    helperText && !error && /* @__PURE__ */ jsx139("p", { id: `${textareaId}-helper`, className: "mt-1 text-xs font-sans text-tertiary", children: helperText })
  ] });
}

// src/print/PrintableDocument.tsx
import { jsx as jsx140, jsxs as jsxs124 } from "react/jsx-runtime";
var confidentialityText = {
  public: "",
  confidential: "CONFIDENTIAL",
  "board-only": "BOARD CONFIDENTIAL"
};
function PrintableDocumentV2({
  title,
  subtitle,
  date,
  confidentiality = "confidential",
  children,
  brandName = "Aaim",
  companyAddress = "Aaim, Inc. | 99 Wall Street, Suite 1610, New York, NY 10005"
}) {
  const confText = confidentialityText[confidentiality];
  return /* @__PURE__ */ jsxs124(
    "article",
    {
      style: {
        maxWidth: "8.5in",
        margin: "0 auto",
        padding: "1in",
        background: "white",
        fontFamily: "'Source Serif 4', Georgia, serif",
        fontSize: "11pt",
        lineHeight: 1.5,
        color: "#000"
      },
      children: [
        /* @__PURE__ */ jsxs124(
          "header",
          {
            style: {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              paddingBottom: "16px",
              marginBottom: "24px",
              borderBottom: "0.5pt solid #C4B299"
              // Single hairline, warm gray
            },
            children: [
              /* @__PURE__ */ jsxs124("div", { children: [
                /* @__PURE__ */ jsx140(
                  "img",
                  {
                    src: "https://cdn.aaim.app/logos/aaim-master-grey.svg",
                    alt: brandName,
                    style: { height: "24px", marginBottom: "12px" }
                  }
                ),
                /* @__PURE__ */ jsx140(
                  "h1",
                  {
                    style: {
                      fontSize: "22pt",
                      fontWeight: 400,
                      // Light weight, let size create hierarchy
                      lineHeight: 1.15,
                      margin: 0,
                      letterSpacing: "-0.02em"
                    },
                    children: title
                  }
                ),
                subtitle && /* @__PURE__ */ jsx140(
                  "p",
                  {
                    style: {
                      fontSize: "11pt",
                      fontStyle: "italic",
                      color: "#3A3330",
                      margin: "8px 0 0 0"
                    },
                    children: subtitle
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs124("div", { style: { textAlign: "right" }, children: [
                confText && /* @__PURE__ */ jsx140(
                  "div",
                  {
                    style: {
                      fontFamily: "'Lato', sans-serif",
                      fontSize: "9pt",
                      letterSpacing: "0.15em",
                      color: "#6B5D54",
                      marginBottom: "4px"
                    },
                    children: confText
                  }
                ),
                date && /* @__PURE__ */ jsx140(
                  "div",
                  {
                    style: {
                      fontFamily: "'Lato', sans-serif",
                      fontSize: "9pt",
                      color: "#6B5D54"
                    },
                    children: date
                  }
                )
              ] })
            ]
          }
        ),
        /* @__PURE__ */ jsx140("main", { children }),
        /* @__PURE__ */ jsxs124(
          "footer",
          {
            style: {
              marginTop: "48px",
              paddingTop: "16px",
              textAlign: "center",
              fontFamily: "'Lato', sans-serif",
              fontSize: "8pt",
              color: "#6B5D54"
            },
            children: [
              confText && `${confText}  \xB7  `,
              companyAddress
            ]
          }
        )
      ]
    }
  );
}
function SectionHeading({ children }) {
  return /* @__PURE__ */ jsx140(
    "h2",
    {
      style: {
        fontSize: "11pt",
        // Same as body per Vignelli
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        marginTop: "32px",
        marginBottom: "12px",
        color: "#000"
      },
      children
    }
  );
}
function MetricsBlock({ metrics }) {
  return /* @__PURE__ */ jsx140(
    "div",
    {
      style: {
        display: "flex",
        flexWrap: "wrap",
        gap: "32px",
        margin: "24px 0"
      },
      children: metrics.map((metric, index) => /* @__PURE__ */ jsxs124("div", { style: { minWidth: "120px" }, children: [
        /* @__PURE__ */ jsx140(
          "div",
          {
            style: {
              fontSize: "22pt",
              // 2x body per Vignelli
              fontWeight: 400,
              lineHeight: 1.1,
              color: "#000",
              letterSpacing: "-0.02em"
            },
            children: metric.value
          }
        ),
        /* @__PURE__ */ jsx140(
          "div",
          {
            style: {
              fontSize: "10pt",
              color: "#3A3330",
              marginTop: "4px"
            },
            children: metric.label
          }
        ),
        metric.context && /* @__PURE__ */ jsx140(
          "div",
          {
            style: {
              fontSize: "9pt",
              color: "#6B5D54",
              fontStyle: "italic"
            },
            children: metric.context
          }
        )
      ] }, index))
    }
  );
}
function Paragraph({ children }) {
  return /* @__PURE__ */ jsx140(
    "p",
    {
      style: {
        fontSize: "11pt",
        lineHeight: 1.5,
        margin: "0 0 16px 0",
        color: "#000"
      },
      children
    }
  );
}
function Source({ children }) {
  return /* @__PURE__ */ jsxs124(
    "div",
    {
      style: {
        fontFamily: "'Lato', sans-serif",
        fontSize: "8pt",
        color: "#6B5D54",
        marginTop: "8px"
      },
      children: [
        "Source: ",
        children
      ]
    }
  );
}
function PageBreak() {
  return /* @__PURE__ */ jsx140("div", { style: { pageBreakBefore: "always" } });
}
var PrintableDocument = PrintableDocumentV2;

// src/overlays/Popover.tsx
import {
  Popover as AriaPopover2,
  Dialog as Dialog2,
  OverlayArrow
} from "react-aria-components";
import { clsx as clsx19 } from "clsx";
import { jsx as jsx141, jsxs as jsxs125 } from "react/jsx-runtime";
function Popover({
  children,
  placement = "bottom",
  offset = 8,
  showArrow = true,
  crossOffset = 0,
  className,
  shouldCloseOnInteractOutside
}) {
  return /* @__PURE__ */ jsxs125(
    AriaPopover2,
    {
      placement,
      offset,
      crossOffset,
      shouldCloseOnInteractOutside,
      className: ({ isEntering, isExiting }) => clsx19(
        "bg-surface border border-recessed rounded-sm shadow-lg",
        "outline-none",
        isEntering && "animate-in fade-in zoom-in-95",
        isExiting && "animate-out fade-out zoom-out-95",
        className
      ),
      children: [
        showArrow && /* @__PURE__ */ jsx141(OverlayArrow, { className: "group", children: /* @__PURE__ */ jsx141(
          "svg",
          {
            width: 12,
            height: 12,
            viewBox: "0 0 12 12",
            className: "block fill-surface stroke-recessed group-data-[placement=bottom]:rotate-180 group-data-[placement=left]:-rotate-90 group-data-[placement=right]:rotate-90",
            children: /* @__PURE__ */ jsx141("path", { d: "M0 0 L6 6 L12 0" })
          }
        ) }),
        /* @__PURE__ */ jsx141(Dialog2, { className: "outline-none p-3", children })
      ]
    }
  );
}

// src/overlays/Sheet.tsx
import {
  Modal as Modal2,
  ModalOverlay as ModalOverlay2,
  Dialog as Dialog3,
  Heading as Heading4,
  Button as Button3
} from "react-aria-components";
import { X as X3 } from "lucide-react";
import { clsx as clsx20 } from "clsx";
import { Fragment as Fragment33, jsx as jsx142, jsxs as jsxs126 } from "react/jsx-runtime";
var sizeClassesHorizontal = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  full: "max-w-full"
};
var sizeClassesVertical = {
  sm: "max-h-[25vh]",
  md: "max-h-[50vh]",
  lg: "max-h-[75vh]",
  xl: "max-h-[90vh]",
  full: "max-h-full"
};
var positionClasses = {
  left: "left-0 top-0 bottom-0 h-full",
  right: "right-0 top-0 bottom-0 h-full",
  top: "top-0 left-0 right-0 w-full",
  bottom: "bottom-0 left-0 right-0 w-full"
};
var animationClasses = {
  left: {
    enter: "animate-in slide-in-from-left",
    exit: "animate-out slide-out-to-left"
  },
  right: {
    enter: "animate-in slide-in-from-right",
    exit: "animate-out slide-out-to-right"
  },
  top: {
    enter: "animate-in slide-in-from-top",
    exit: "animate-out slide-out-to-top"
  },
  bottom: {
    enter: "animate-in slide-in-from-bottom",
    exit: "animate-out slide-out-to-bottom"
  }
};
function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  side = "right",
  size = "md",
  showCloseButton = true,
  isDismissable = true,
  footer,
  className
}) {
  const isHorizontal = side === "left" || side === "right";
  const sizeClasses4 = isHorizontal ? sizeClassesHorizontal : sizeClassesVertical;
  return /* @__PURE__ */ jsx142(
    ModalOverlay2,
    {
      isOpen: open,
      onOpenChange,
      isDismissable,
      className: clsx20(
        "fixed inset-0 z-50",
        "bg-black/40 backdrop-blur-sm",
        "data-[entering]:animate-in data-[entering]:fade-in",
        "data-[exiting]:animate-out data-[exiting]:fade-out"
      ),
      children: /* @__PURE__ */ jsx142(
        Modal2,
        {
          className: clsx20(
            "fixed",
            positionClasses[side],
            isHorizontal ? "w-full" : "h-auto",
            sizeClasses4[size],
            "data-[entering]:" + animationClasses[side].enter,
            "data-[exiting]:" + animationClasses[side].exit
          ),
          children: /* @__PURE__ */ jsx142(
            Dialog3,
            {
              className: clsx20(
                "h-full flex flex-col",
                "bg-surface border-recessed shadow-xl outline-none",
                side === "left" && "border-r",
                side === "right" && "border-l",
                side === "top" && "border-b",
                side === "bottom" && "border-t",
                className
              ),
              children: ({ close }) => /* @__PURE__ */ jsxs126(Fragment33, { children: [
                (title || showCloseButton) && /* @__PURE__ */ jsxs126("div", { className: "flex items-start justify-between px-6 py-4 border-b border-recessed", children: [
                  /* @__PURE__ */ jsxs126("div", { className: "flex-1", children: [
                    title && /* @__PURE__ */ jsx142(
                      Heading4,
                      {
                        slot: "title",
                        className: "text-xl font-sans font-bold text-primary leading-tight",
                        children: title
                      }
                    ),
                    description && /* @__PURE__ */ jsx142("p", { className: "mt-1 text-sm text-tertiary", children: description })
                  ] }),
                  showCloseButton && /* @__PURE__ */ jsx142(
                    Button3,
                    {
                      onPress: close,
                      className: clsx20(
                        "p-1.5 rounded-sm ml-4",
                        "text-tertiary hover:text-primary",
                        "hover:bg-canvas",
                        "transition-colors duration-150",
                        "outline-none focus-visible:ring-2 focus-visible:ring-accent"
                      ),
                      "aria-label": "Close sheet",
                      children: /* @__PURE__ */ jsx142(X3, { className: "w-5 h-5" })
                    }
                  )
                ] }),
                /* @__PURE__ */ jsx142("div", { className: "flex-1 overflow-y-auto px-6 py-4", children }),
                footer && /* @__PURE__ */ jsx142("div", { className: "flex items-center justify-end gap-3 px-6 py-4 border-t border-recessed bg-canvas/50", children: footer })
              ] })
            }
          )
        }
      )
    }
  );
}

// src/overlays/ShortcutHelpOverlay.tsx
import { jsx as jsx143, jsxs as jsxs127 } from "react/jsx-runtime";
function formatKey(spec) {
  if (spec.includes(" ")) {
    return spec.split(" ").join(" then ");
  }
  return spec;
}
function ShortcutHelpOverlay({
  open,
  onOpenChange,
  shortcuts
}) {
  const visible = shortcuts.filter((s) => !s.disabled);
  return /* @__PURE__ */ jsx143(
    Dialog,
    {
      open,
      onOpenChange,
      title: "Keyboard shortcuts",
      size: "md",
      footer: /* @__PURE__ */ jsx143(
        Button,
        {
          variant: "secondary",
          size: "medium",
          onClick: () => onOpenChange(false),
          children: "Close"
        }
      ),
      children: /* @__PURE__ */ jsxs127("div", { className: "space-y-3", children: [
        /* @__PURE__ */ jsx143("p", { className: "text-sm font-sans text-secondary", children: "Shortcuts work when focus is on the page container and no dialog is open. Typing in an input or textarea is not intercepted." }),
        /* @__PURE__ */ jsx143("dl", { className: "divide-y divide-recessed border border-recessed rounded-sm", children: visible.map((s) => /* @__PURE__ */ jsxs127(
          "div",
          {
            className: "flex items-center justify-between px-3 py-2 gap-4",
            children: [
              /* @__PURE__ */ jsx143("dt", { children: /* @__PURE__ */ jsx143(
                "kbd",
                {
                  className: "inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono bg-elevated border border-recessed rounded-sm text-primary",
                  "aria-label": `Shortcut ${formatKey(s.keys)}`,
                  children: formatKey(s.keys)
                }
              ) }),
              /* @__PURE__ */ jsx143("dd", { className: "text-sm font-sans text-secondary text-right flex-1", children: s.description })
            ]
          },
          s.keys
        )) })
      ] })
    }
  );
}

// src/feedback/Skeleton.tsx
import { clsx as clsx21 } from "clsx";
import { jsx as jsx144, jsxs as jsxs128 } from "react/jsx-runtime";
function Skeleton({
  width,
  height,
  circle = false,
  animate = true,
  lines,
  className
}) {
  const baseClasses = clsx21(
    "bg-recessed/50",
    animate && "animate-pulse",
    circle ? "rounded-full" : "rounded-sm",
    className
  );
  const style = {
    width: typeof width === "number" ? `${width}px` : width,
    height: typeof height === "number" ? `${height}px` : height
  };
  if (lines && lines > 1) {
    return /* @__PURE__ */ jsx144("div", { className: "space-y-2", children: Array.from({ length: lines }).map((_, i) => /* @__PURE__ */ jsx144(
      "div",
      {
        className: baseClasses,
        style: {
          ...style,
          // Last line is shorter for more natural appearance
          width: i === lines - 1 ? "75%" : style.width || "100%",
          height: style.height || "1rem"
        }
      },
      i
    )) });
  }
  return /* @__PURE__ */ jsx144("div", { className: baseClasses, style });
}
function SkeletonText({ lines = 3, className }) {
  return /* @__PURE__ */ jsx144(Skeleton, { lines, className });
}
function SkeletonAvatar({ size = 40, className }) {
  return /* @__PURE__ */ jsx144(Skeleton, { width: size, height: size, circle: true, className });
}
function SkeletonCard({ className }) {
  return /* @__PURE__ */ jsxs128(
    "div",
    {
      className: clsx21(
        "p-4 bg-surface border border-recessed rounded-sm space-y-4",
        className
      ),
      children: [
        /* @__PURE__ */ jsx144(Skeleton, { height: 160, className: "w-full" }),
        /* @__PURE__ */ jsx144(Skeleton, { height: 24, width: "75%" }),
        /* @__PURE__ */ jsx144(Skeleton, { lines: 2 })
      ]
    }
  );
}
function SkeletonTable({
  rows = 5,
  columns = 4,
  className
}) {
  return /* @__PURE__ */ jsxs128("div", { className: clsx21("space-y-3", className), children: [
    /* @__PURE__ */ jsx144("div", { className: "flex gap-4 pb-3 border-b border-recessed", children: Array.from({ length: columns }).map((_, i) => /* @__PURE__ */ jsx144(Skeleton, { height: 16, className: "flex-1" }, i)) }),
    Array.from({ length: rows }).map((_, rowIndex) => /* @__PURE__ */ jsx144("div", { className: "flex gap-4 py-2", children: Array.from({ length: columns }).map((_2, colIndex) => /* @__PURE__ */ jsx144(
      Skeleton,
      {
        height: 16,
        className: "flex-1",
        width: colIndex === 0 ? "60%" : void 0
      },
      colIndex
    )) }, rowIndex))
  ] });
}

// src/feedback/Progress.tsx
import {
  ProgressBar as AriaProgressBar,
  Label as Label3
} from "react-aria-components";
import { clsx as clsx22 } from "clsx";
import { Fragment as Fragment34, jsx as jsx145, jsxs as jsxs129 } from "react/jsx-runtime";
var sizeClasses3 = {
  sm: "h-1",
  md: "h-2",
  lg: "h-3"
};
var variantClasses = {
  default: "bg-accent",
  success: "bg-growth",
  warning: "bg-warning",
  error: "bg-error"
};
function Progress({
  value,
  label,
  showValue = false,
  size = "md",
  variant = "default",
  className,
  ...props
}) {
  const isIndeterminate = value === void 0;
  return /* @__PURE__ */ jsx145(
    AriaProgressBar,
    {
      ...props,
      value,
      isIndeterminate,
      className: clsx22("flex flex-col gap-1", className),
      children: ({ percentage, valueText }) => /* @__PURE__ */ jsxs129(Fragment34, { children: [
        (label || showValue) && /* @__PURE__ */ jsxs129("div", { className: "flex justify-between text-sm", children: [
          label && /* @__PURE__ */ jsx145(Label3, { className: "text-primary", children: label }),
          showValue && !isIndeterminate && /* @__PURE__ */ jsx145("span", { className: "text-tertiary", children: valueText })
        ] }),
        /* @__PURE__ */ jsx145(
          "div",
          {
            className: clsx22(
              "w-full bg-recessed/30 rounded-full overflow-hidden",
              sizeClasses3[size]
            ),
            children: /* @__PURE__ */ jsx145(
              "div",
              {
                className: clsx22(
                  "h-full rounded-full transition-all duration-300",
                  variantClasses[variant],
                  isIndeterminate && "animate-indeterminate-progress"
                ),
                style: {
                  width: isIndeterminate ? "50%" : `${percentage}%`
                }
              }
            )
          }
        )
      ] })
    }
  );
}
var circularVariantClasses = {
  default: "stroke-accent",
  success: "stroke-growth",
  warning: "stroke-warning",
  error: "stroke-error"
};
function CircularProgress({
  value,
  size = 48,
  strokeWidth = 4,
  showValue = false,
  variant = "default",
  className
}) {
  const isIndeterminate = value === void 0;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = isIndeterminate ? circumference * 0.75 : circumference - circumference * (value || 0) / 100;
  return /* @__PURE__ */ jsxs129(
    "div",
    {
      className: clsx22("relative inline-flex", className),
      role: "progressbar",
      "aria-valuenow": value,
      "aria-valuemin": 0,
      "aria-valuemax": 100,
      children: [
        /* @__PURE__ */ jsxs129(
          "svg",
          {
            width: size,
            height: size,
            className: clsx22(isIndeterminate && "animate-spin"),
            style: { animationDuration: isIndeterminate ? "1.5s" : void 0 },
            children: [
              /* @__PURE__ */ jsx145(
                "circle",
                {
                  cx: size / 2,
                  cy: size / 2,
                  r: radius,
                  fill: "none",
                  strokeWidth,
                  className: "stroke-recessed/30"
                }
              ),
              /* @__PURE__ */ jsx145(
                "circle",
                {
                  cx: size / 2,
                  cy: size / 2,
                  r: radius,
                  fill: "none",
                  strokeWidth,
                  strokeLinecap: "round",
                  strokeDasharray: circumference,
                  strokeDashoffset: offset,
                  className: clsx22(
                    circularVariantClasses[variant],
                    "transition-all duration-300"
                  ),
                  transform: `rotate(-90 ${size / 2} ${size / 2})`
                }
              )
            ]
          }
        ),
        showValue && !isIndeterminate && /* @__PURE__ */ jsx145("div", { className: "absolute inset-0 flex items-center justify-center", children: /* @__PURE__ */ jsxs129("span", { className: "text-xs font-medium text-primary", children: [
          value,
          "%"
        ] }) })
      ]
    }
  );
}

// src/feedback/AlertBanner.tsx
import { AlertCircleIcon as AlertCircleIcon4, XIcon as XIcon11 } from "lucide-react";
import { jsx as jsx146, jsxs as jsxs130 } from "react/jsx-runtime";
function AlertBanner({
  type,
  message,
  onDismiss
}) {
  const styles = {
    warning: {
      bg: "bg-warning/10",
      border: "border-warning",
      text: "text-warning",
      icon: AlertCircleIcon4
    },
    error: {
      bg: "bg-error-bg",
      border: "border-error-bright",
      text: "text-error-bright",
      icon: XIcon11
    }
  };
  const style = styles[type];
  const Icon = style.icon;
  return /* @__PURE__ */ jsxs130("div", { className: `${style.bg} ${style.border} border-l-4 p-4 rounded-sm flex items-start justify-between gap-4`, children: [
    /* @__PURE__ */ jsxs130("div", { className: "flex items-start gap-3", children: [
      /* @__PURE__ */ jsx146(Icon, { className: `w-5 h-5 ${style.text} flex-shrink-0 mt-0.5` }),
      /* @__PURE__ */ jsx146("p", { className: "text-sm font-sans text-secondary", children: message })
    ] }),
    onDismiss && /* @__PURE__ */ jsx146("button", { onClick: onDismiss, className: "text-tertiary hover:text-primary transition-colors", "aria-label": "Dismiss alert", children: /* @__PURE__ */ jsx146(XIcon11, { className: "w-4 h-4" }) })
  ] });
}

// src/feedback/StatusBadge.tsx
import { jsx as jsx147 } from "react/jsx-runtime";
function StatusBadge({
  status,
  children
}) {
  const styles = {
    active: "bg-growth/10 text-growth",
    pending: "bg-warning/10 text-warning-text",
    inactive: "bg-tertiary/10 text-tertiary",
    warning: "bg-warning/10 text-warning-text",
    error: "bg-error/10 text-error"
  };
  return /* @__PURE__ */ jsx147("span", { className: `px-3 py-1 text-xs font-sans font-semibold uppercase tracking-[0.1em] rounded-sm ${styles[status]}`, children });
}

// src/feedback/EmptyState.tsx
import { jsx as jsx148, jsxs as jsxs131 } from "react/jsx-runtime";
function EmptyState({ title, description, icon, action, className = "" }) {
  return /* @__PURE__ */ jsxs131("div", { className: `flex flex-col items-center justify-center gap-3 px-6 py-16 text-center ${className}`, children: [
    icon && /* @__PURE__ */ jsx148("div", { className: "text-tertiary", "aria-hidden": "true", children: icon }),
    /* @__PURE__ */ jsx148("p", { className: "font-sans text-base font-semibold text-primary", children: title }),
    description && /* @__PURE__ */ jsx148("p", { className: "max-w-content-narrow font-sans text-sm text-secondary", children: description }),
    action && /* @__PURE__ */ jsx148("div", { className: "mt-2", children: action })
  ] });
}

// src/workflow/WorkflowCanvas.tsx
import React41, { useCallback as useCallback23, useState as useState31 } from "react";
import ReactFlow, {
  Background,
  BackgroundVariant,
  MiniMap,
  ReactFlowProvider,
  useReactFlow,
  useNodesState,
  useEdgesState
} from "reactflow";

// src/workflow/hooks/useWorkflowLayout.ts
import { useMemo as useMemo15 } from "react";
import dagre from "dagre";
import { MarkerType } from "reactflow";

// src/workflow/constants.ts
var NODE_DIMENSIONS = {
  action: { width: 240, height: 80 },
  decision: { width: 180, height: 100 },
  group: { width: 320, height: 200 },
  start: { width: 160, height: 56 },
  end: { width: 160, height: 56 }
};
var LAYOUT_SPACING = {
  /** Separation between nodes at the same rank */
  nodeSeparation: 60,
  /** Separation between ranks (layers) */
  rankSeparation: 100,
  /** Padding around the graph edges */
  graphPadding: 40
};
var STATUS_BORDER_CLASSES = {
  pending: "border-recessed",
  active: "border-primary",
  completed: "border-success",
  failed: "border-error",
  skipped: "border-recessed opacity-50"
};
var STATUS_DOT_CLASSES = {
  pending: "bg-tertiary",
  active: "bg-primary",
  completed: "bg-success",
  failed: "bg-error",
  skipped: "bg-disabled"
};
var STATUS_STROKE_COLORS = {
  pending: "rgb(var(--color-text-tertiary))",
  active: "rgb(var(--color-text-primary))",
  completed: "rgb(var(--color-success))",
  failed: "rgb(var(--color-error))",
  skipped: "rgb(var(--color-text-disabled))"
};
var STATUS_LABELS2 = {
  pending: "Pending",
  active: "Active",
  completed: "Completed",
  failed: "Failed",
  skipped: "Skipped"
};

// src/workflow/hooks/useWorkflowLayout.ts
function mapNodeType(stepType) {
  switch (stepType) {
    case "decision":
      return "workflowDecision";
    case "group":
      return "workflowGroup";
    case "start":
    case "end":
    case "action":
    default:
      return "workflowNode";
  }
}
function getRankDir(direction) {
  return direction === "horizontal" ? "LR" : "TB";
}
function useWorkflowLayout({
  steps,
  connections,
  direction = "vertical",
  selectedStepId,
  onStepClick,
  onStepDoubleClick,
  collapsedGroups = /* @__PURE__ */ new Set(),
  onToggleCollapse
}) {
  return useMemo15(() => {
    if (steps.length === 0) {
      return { nodes: [], edges: [] };
    }
    const graph = new dagre.graphlib.Graph();
    graph.setDefaultEdgeLabel(() => ({}));
    graph.setGraph({
      rankdir: getRankDir(direction),
      nodesep: LAYOUT_SPACING.nodeSeparation,
      ranksep: LAYOUT_SPACING.rankSeparation,
      marginx: LAYOUT_SPACING.graphPadding,
      marginy: LAYOUT_SPACING.graphPadding
    });
    const topLevelSteps = steps.filter((s) => {
      return !steps.some(
        (parent) => parent.type === "group" && parent.children?.some((child) => child.id === s.id)
      );
    });
    for (const step of topLevelSteps) {
      const dims = NODE_DIMENSIONS[step.type] || NODE_DIMENSIONS.action;
      graph.setNode(step.id, { width: dims.width, height: dims.height });
    }
    for (const conn of connections) {
      const sourceExists = topLevelSteps.some((s) => s.id === conn.source);
      const targetExists = topLevelSteps.some((s) => s.id === conn.target);
      if (sourceExists && targetExists) {
        graph.setEdge(conn.source, conn.target);
      }
    }
    dagre.layout(graph);
    const stepMap = /* @__PURE__ */ new Map();
    for (const step of steps) {
      stepMap.set(step.id, step);
    }
    const nodes = topLevelSteps.map((step) => {
      const dagreNode = graph.node(step.id);
      const dims = NODE_DIMENSIONS[step.type] || NODE_DIMENSIONS.action;
      const baseData = {
        step,
        isSelected: step.id === selectedStepId,
        direction,
        onStepClick,
        onStepDoubleClick
      };
      let data;
      let nodeType;
      if (step.type === "group") {
        data = {
          ...baseData,
          isCollapsed: collapsedGroups.has(step.id),
          onToggleCollapse: () => onToggleCollapse?.(step.id)
        };
        nodeType = "workflowGroup";
      } else if (step.type === "decision") {
        data = baseData;
        nodeType = "workflowDecision";
      } else {
        data = baseData;
        nodeType = mapNodeType(step.type);
      }
      return {
        id: step.id,
        type: nodeType,
        position: {
          x: dagreNode.x - dims.width / 2,
          y: dagreNode.y - dims.height / 2
        },
        data
      };
    });
    const edges = connections.map((conn) => {
      const sourceStep = stepMap.get(conn.source);
      const sourceStatus = sourceStep?.status || "pending";
      const edgeStatus = sourceStatus === "completed" ? "completed" : sourceStatus === "active" ? "active" : sourceStatus === "failed" ? "failed" : "pending";
      return {
        id: `${conn.source}-${conn.target}`,
        source: conn.source,
        target: conn.target,
        type: "workflowEdge",
        animated: edgeStatus === "active",
        data: {
          label: conn.label || conn.condition,
          condition: conn.condition,
          status: edgeStatus
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: STATUS_STROKE_COLORS[edgeStatus],
          width: 16,
          height: 16
        },
        style: {
          stroke: STATUS_STROKE_COLORS[edgeStatus],
          strokeWidth: edgeStatus === "active" ? 2 : 1.5
        }
      };
    });
    return { nodes, edges };
  }, [
    steps,
    connections,
    direction,
    selectedStepId,
    onStepClick,
    onStepDoubleClick,
    collapsedGroups,
    onToggleCollapse
  ]);
}

// src/workflow/nodes/WorkflowNode.tsx
import { useCallback as useCallback20 } from "react";
import { Handle, Position } from "reactflow";
import { jsx as jsx149, jsxs as jsxs132 } from "react/jsx-runtime";
function getHandlePositions(direction) {
  if (direction === "horizontal") {
    return { source: Position.Right, target: Position.Left };
  }
  return { source: Position.Bottom, target: Position.Top };
}
function WorkflowNode({ data }) {
  const { step, isSelected, direction, onStepClick, onStepDoubleClick } = data;
  const status = step.status || "pending";
  const isStart = step.type === "start";
  const isEnd = step.type === "end";
  const handles = getHandlePositions(direction);
  const handleClick = useCallback20(() => {
    onStepClick?.(step);
  }, [onStepClick, step]);
  const handleDoubleClick = useCallback20(() => {
    onStepDoubleClick?.(step);
  }, [onStepDoubleClick, step]);
  const containerClasses = [
    "bg-surface border rounded-sm shadow-sm transition-all duration-200",
    "font-sans cursor-pointer",
    STATUS_BORDER_CLASSES[status],
    isSelected ? "ring-2 ring-primary ring-offset-1 ring-offset-canvas shadow-md" : "",
    status === "active" ? "shadow-md" : "",
    isStart || isEnd ? "px-4 py-3" : "px-4 py-3"
  ].filter(Boolean).join(" ");
  if (isStart || isEnd) {
    return /* @__PURE__ */ jsxs132(
      "div",
      {
        className: `${containerClasses} flex items-center justify-center min-w-[140px]`,
        onClick: handleClick,
        onDoubleClick: handleDoubleClick,
        role: "button",
        tabIndex: 0,
        "aria-label": `${step.label} (${STATUS_LABELS2[status]})`,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        },
        children: [
          !isStart && /* @__PURE__ */ jsx149(
            Handle,
            {
              type: "target",
              position: handles.target,
              className: "!bg-tertiary !border-surface !w-2 !h-2"
            }
          ),
          /* @__PURE__ */ jsxs132("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx149(
              "span",
              {
                className: `w-2 h-2 rounded-full flex-shrink-0 ${STATUS_DOT_CLASSES[status]}`,
                "aria-hidden": "true"
              }
            ),
            /* @__PURE__ */ jsx149("span", { className: "text-body-sm font-semibold text-primary truncate", children: step.label })
          ] }),
          !isEnd && /* @__PURE__ */ jsx149(
            Handle,
            {
              type: "source",
              position: handles.source,
              className: "!bg-tertiary !border-surface !w-2 !h-2"
            }
          )
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs132(
    "div",
    {
      className: containerClasses,
      style: { minWidth: 200, maxWidth: 280 },
      onClick: handleClick,
      onDoubleClick: handleDoubleClick,
      role: "button",
      tabIndex: 0,
      "aria-label": `${step.label}: ${STATUS_LABELS2[status]}${step.description ? `. ${step.description}` : ""}`,
      onKeyDown: (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      },
      children: [
        /* @__PURE__ */ jsx149(
          Handle,
          {
            type: "target",
            position: handles.target,
            className: "!bg-tertiary !border-surface !w-2 !h-2"
          }
        ),
        /* @__PURE__ */ jsxs132("div", { className: "flex items-start gap-3", children: [
          step.icon && /* @__PURE__ */ jsx149("div", { className: "flex-shrink-0 mt-0.5 text-tertiary", "aria-hidden": "true", children: step.icon }),
          /* @__PURE__ */ jsxs132("div", { className: "flex-1 min-w-0", children: [
            /* @__PURE__ */ jsxs132("div", { className: "flex items-center gap-2 mb-1", children: [
              /* @__PURE__ */ jsx149(
                "span",
                {
                  className: `w-2 h-2 rounded-full flex-shrink-0 ${STATUS_DOT_CLASSES[status]}`,
                  "aria-hidden": "true"
                }
              ),
              /* @__PURE__ */ jsx149("span", { className: "text-body-sm font-semibold text-primary truncate", children: step.label })
            ] }),
            step.description && /* @__PURE__ */ jsx149("p", { className: "text-caption-md text-tertiary leading-relaxed truncate", children: step.description })
          ] })
        ] }),
        /* @__PURE__ */ jsx149(
          Handle,
          {
            type: "source",
            position: handles.source,
            className: "!bg-tertiary !border-surface !w-2 !h-2"
          }
        )
      ]
    }
  );
}

// src/workflow/nodes/DecisionNode.tsx
import { useCallback as useCallback21 } from "react";
import { Handle as Handle2, Position as Position2 } from "reactflow";
import { jsx as jsx150, jsxs as jsxs133 } from "react/jsx-runtime";
function getDecisionHandles(direction) {
  if (direction === "horizontal") {
    return {
      target: Position2.Left,
      sourceTrue: Position2.Right,
      sourceFalse: Position2.Bottom
    };
  }
  return {
    target: Position2.Top,
    sourceTrue: Position2.Bottom,
    sourceFalse: Position2.Right
  };
}
function DecisionNode({ data }) {
  const { step, isSelected, direction, onStepClick, onStepDoubleClick } = data;
  const status = step.status || "pending";
  const handles = getDecisionHandles(direction);
  const handleClick = useCallback21(() => {
    onStepClick?.(step);
  }, [onStepClick, step]);
  const handleDoubleClick = useCallback21(() => {
    onStepDoubleClick?.(step);
  }, [onStepDoubleClick, step]);
  const containerClasses = [
    "relative bg-surface border-2 shadow-sm transition-all duration-200",
    "font-sans cursor-pointer",
    STATUS_BORDER_CLASSES[status],
    isSelected ? "ring-2 ring-primary ring-offset-1 ring-offset-canvas shadow-md" : "",
    status === "active" ? "shadow-md" : ""
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs133(
    "div",
    {
      className: "relative",
      style: { width: 180, height: 100 },
      onClick: handleClick,
      onDoubleClick: handleDoubleClick,
      role: "button",
      tabIndex: 0,
      "aria-label": `Decision: ${step.label} (${STATUS_LABELS2[status]})`,
      onKeyDown: (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      },
      children: [
        /* @__PURE__ */ jsx150(
          "div",
          {
            className: containerClasses,
            style: {
              position: "absolute",
              inset: 0,
              clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)"
            }
          }
        ),
        /* @__PURE__ */ jsxs133("div", { className: "absolute inset-0 flex flex-col items-center justify-center px-6 pointer-events-none", children: [
          /* @__PURE__ */ jsxs133("div", { className: "flex items-center gap-1.5 mb-0.5", children: [
            /* @__PURE__ */ jsx150(
              "span",
              {
                className: `w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_DOT_CLASSES[status]}`,
                "aria-hidden": "true"
              }
            ),
            /* @__PURE__ */ jsx150("span", { className: "text-caption-md font-semibold text-primary text-center leading-tight", children: step.label })
          ] }),
          step.description && /* @__PURE__ */ jsx150("span", { className: "text-caption-sm text-tertiary text-center leading-tight", children: step.description })
        ] }),
        /* @__PURE__ */ jsx150(
          Handle2,
          {
            type: "target",
            position: handles.target,
            className: "!bg-tertiary !border-surface !w-2 !h-2"
          }
        ),
        /* @__PURE__ */ jsx150(
          Handle2,
          {
            type: "source",
            position: handles.sourceTrue,
            id: "true",
            className: "!bg-success !border-surface !w-2 !h-2"
          }
        ),
        /* @__PURE__ */ jsx150(
          Handle2,
          {
            type: "source",
            position: handles.sourceFalse,
            id: "false",
            className: "!bg-error !border-surface !w-2 !h-2"
          }
        )
      ]
    }
  );
}

// src/workflow/nodes/GroupNode.tsx
import { useCallback as useCallback22 } from "react";
import { Handle as Handle3, Position as Position3 } from "reactflow";
import { jsx as jsx151, jsxs as jsxs134 } from "react/jsx-runtime";
function getHandlePositions2(direction) {
  if (direction === "horizontal") {
    return { source: Position3.Right, target: Position3.Left };
  }
  return { source: Position3.Bottom, target: Position3.Top };
}
function GroupNode({ data }) {
  const {
    step,
    isSelected,
    isCollapsed,
    direction,
    onStepClick,
    onStepDoubleClick,
    onToggleCollapse
  } = data;
  const status = step.status || "pending";
  const children = step.children || [];
  const handles = getHandlePositions2(direction);
  const handleClick = useCallback22(() => {
    onStepClick?.(step);
  }, [onStepClick, step]);
  const handleDoubleClick = useCallback22(() => {
    onStepDoubleClick?.(step);
  }, [onStepDoubleClick, step]);
  const handleCollapseKeyDown = useCallback22(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        e.stopPropagation();
        onToggleCollapse();
      }
    },
    [onToggleCollapse]
  );
  const statusCounts = children.reduce((acc, child) => {
    const s = child.status || "pending";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const containerClasses = [
    "bg-surface border-2 rounded-sm shadow-sm transition-all duration-200",
    "font-sans",
    STATUS_BORDER_CLASSES[status],
    isSelected ? "ring-2 ring-primary ring-offset-1 ring-offset-canvas shadow-md" : ""
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs134(
    "div",
    {
      className: containerClasses,
      style: { minWidth: 280, maxWidth: 400 },
      onClick: handleClick,
      onDoubleClick: handleDoubleClick,
      role: "group",
      "aria-label": `Group: ${step.label} (${STATUS_LABELS2[status]}, ${children.length} steps)`,
      children: [
        /* @__PURE__ */ jsx151(
          Handle3,
          {
            type: "target",
            position: handles.target,
            className: "!bg-tertiary !border-surface !w-2 !h-2"
          }
        ),
        /* @__PURE__ */ jsxs134("div", { className: "flex items-center justify-between px-3 py-2 border-b border-recessed bg-recessed/20", children: [
          /* @__PURE__ */ jsxs134("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx151(
              "span",
              {
                className: `w-2 h-2 rounded-full flex-shrink-0 ${STATUS_DOT_CLASSES[status]}`,
                "aria-hidden": "true"
              }
            ),
            /* @__PURE__ */ jsx151("span", { className: "text-body-sm font-semibold text-primary", children: step.label }),
            /* @__PURE__ */ jsxs134("span", { className: "text-caption-sm text-tertiary", children: [
              "(",
              children.length,
              " steps)"
            ] })
          ] }),
          /* @__PURE__ */ jsx151(
            "button",
            {
              type: "button",
              className: "p-1 text-tertiary hover:text-primary transition-colors rounded-sm focus:outline-none focus:ring-1 focus:ring-primary",
              onClick: (e) => {
                e.stopPropagation();
                onToggleCollapse();
              },
              onKeyDown: handleCollapseKeyDown,
              "aria-expanded": !isCollapsed,
              "aria-label": isCollapsed ? "Expand group" : "Collapse group",
              children: /* @__PURE__ */ jsx151(
                "svg",
                {
                  width: "14",
                  height: "14",
                  viewBox: "0 0 14 14",
                  fill: "none",
                  className: `transition-transform duration-200 ${isCollapsed ? "" : "rotate-180"}`,
                  children: /* @__PURE__ */ jsx151(
                    "path",
                    {
                      d: "M3.5 5.25L7 8.75L10.5 5.25",
                      stroke: "currentColor",
                      strokeWidth: "1.5",
                      strokeLinecap: "round",
                      strokeLinejoin: "round"
                    }
                  )
                }
              )
            }
          )
        ] }),
        isCollapsed ? (
          /* Collapsed summary */
          /* @__PURE__ */ jsxs134("div", { className: "px-3 py-2", children: [
            /* @__PURE__ */ jsx151("div", { className: "flex items-center gap-3 flex-wrap", children: Object.entries(statusCounts).map(([s, count]) => /* @__PURE__ */ jsxs134("div", { className: "flex items-center gap-1", children: [
              /* @__PURE__ */ jsx151(
                "span",
                {
                  className: `w-1.5 h-1.5 rounded-full ${STATUS_DOT_CLASSES[s]}`,
                  "aria-hidden": "true"
                }
              ),
              /* @__PURE__ */ jsxs134("span", { className: "text-caption-sm text-tertiary", children: [
                count,
                " ",
                STATUS_LABELS2[s]?.toLowerCase()
              ] })
            ] }, s)) }),
            step.description && /* @__PURE__ */ jsx151("p", { className: "text-caption-sm text-tertiary mt-1.5 leading-relaxed", children: step.description })
          ] })
        ) : (
          /* Expanded child list */
          /* @__PURE__ */ jsxs134("div", { className: "px-3 py-2 space-y-1.5", children: [
            step.description && /* @__PURE__ */ jsx151("p", { className: "text-caption-sm text-tertiary leading-relaxed mb-2", children: step.description }),
            children.map((child) => {
              const childStatus = child.status || "pending";
              return /* @__PURE__ */ jsxs134(
                "div",
                {
                  className: "flex items-center gap-2 py-1 px-2 rounded-sm hover:bg-recessed/30 transition-colors",
                  children: [
                    /* @__PURE__ */ jsx151(
                      "span",
                      {
                        className: `w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_DOT_CLASSES[childStatus]}`,
                        "aria-hidden": "true"
                      }
                    ),
                    /* @__PURE__ */ jsx151("span", { className: "text-caption-md text-secondary truncate", children: child.label }),
                    /* @__PURE__ */ jsx151("span", { className: "text-caption-sm text-tertiary ml-auto flex-shrink-0", children: STATUS_LABELS2[childStatus] })
                  ]
                },
                child.id
              );
            })
          ] })
        ),
        /* @__PURE__ */ jsx151(
          Handle3,
          {
            type: "source",
            position: handles.source,
            className: "!bg-tertiary !border-surface !w-2 !h-2"
          }
        )
      ]
    }
  );
}

// src/workflow/edges/WorkflowEdge.tsx
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath
} from "reactflow";
import { Fragment as Fragment35, jsx as jsx152, jsxs as jsxs135 } from "react/jsx-runtime";
function WorkflowEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
  style = {}
}) {
  const status = data?.status || "pending";
  const label = data?.label;
  const strokeColor = STATUS_STROKE_COLORS[status];
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition
  });
  const isActive = status === "active";
  const edgeStyle = {
    ...style,
    stroke: strokeColor,
    strokeWidth: isActive ? 2 : 1.5,
    ...isActive ? {
      strokeDasharray: "8 4",
      animation: "workflowEdgeDash 0.6s linear infinite"
    } : {}
  };
  return /* @__PURE__ */ jsxs135(Fragment35, { children: [
    /* @__PURE__ */ jsx152(
      BaseEdge,
      {
        id,
        path: edgePath,
        markerEnd,
        style: edgeStyle
      }
    ),
    label && /* @__PURE__ */ jsx152(EdgeLabelRenderer, { children: /* @__PURE__ */ jsx152(
      "div",
      {
        className: "absolute bg-surface border border-recessed rounded-sm px-1.5 py-0.5 shadow-sm pointer-events-all",
        style: {
          transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`
        },
        children: /* @__PURE__ */ jsx152("span", { className: "text-caption-sm font-sans text-tertiary whitespace-nowrap", children: label })
      }
    ) })
  ] });
}

// src/workflow/WorkflowControls.tsx
import { jsx as jsx153, jsxs as jsxs136 } from "react/jsx-runtime";
function ControlButton({
  onClick,
  label,
  children
}) {
  return /* @__PURE__ */ jsx153(
    "button",
    {
      type: "button",
      onClick,
      "aria-label": label,
      className: "p-1.5 text-tertiary hover:text-primary hover:bg-recessed/30 rounded-sm transition-colors focus:outline-none focus:ring-1 focus:ring-primary",
      children
    }
  );
}
function WorkflowControls({
  showMinimap,
  onToggleMinimap,
  onFitView,
  onZoomIn,
  onZoomOut
}) {
  return /* @__PURE__ */ jsxs136(
    "div",
    {
      className: "absolute bottom-4 left-4 z-10 flex flex-col gap-0.5 bg-surface border border-recessed rounded-sm shadow-sm p-1",
      role: "toolbar",
      "aria-label": "Workflow canvas controls",
      children: [
        /* @__PURE__ */ jsx153(ControlButton, { onClick: onZoomIn, label: "Zoom in", children: /* @__PURE__ */ jsx153("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: /* @__PURE__ */ jsx153(
          "path",
          {
            d: "M8 3.5V12.5M3.5 8H12.5",
            stroke: "currentColor",
            strokeWidth: "1.5",
            strokeLinecap: "round"
          }
        ) }) }),
        /* @__PURE__ */ jsx153(ControlButton, { onClick: onZoomOut, label: "Zoom out", children: /* @__PURE__ */ jsx153("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: /* @__PURE__ */ jsx153(
          "path",
          {
            d: "M3.5 8H12.5",
            stroke: "currentColor",
            strokeWidth: "1.5",
            strokeLinecap: "round"
          }
        ) }) }),
        /* @__PURE__ */ jsx153("div", { className: "h-px bg-recessed mx-1 my-0.5", role: "separator" }),
        /* @__PURE__ */ jsx153(ControlButton, { onClick: onFitView, label: "Fit to view", children: /* @__PURE__ */ jsx153("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: /* @__PURE__ */ jsx153(
          "path",
          {
            d: "M2.5 5.5V3.5C2.5 2.95 2.95 2.5 3.5 2.5H5.5M10.5 2.5H12.5C13.05 2.5 13.5 2.95 13.5 3.5V5.5M13.5 10.5V12.5C13.5 13.05 13.05 13.5 12.5 13.5H10.5M5.5 13.5H3.5C2.95 13.5 2.5 13.05 2.5 12.5V10.5",
            stroke: "currentColor",
            strokeWidth: "1.5",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ) }) }),
        /* @__PURE__ */ jsx153("div", { className: "h-px bg-recessed mx-1 my-0.5", role: "separator" }),
        /* @__PURE__ */ jsx153(
          ControlButton,
          {
            onClick: onToggleMinimap,
            label: showMinimap ? "Hide minimap" : "Show minimap",
            children: /* @__PURE__ */ jsxs136("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: [
              /* @__PURE__ */ jsx153(
                "rect",
                {
                  x: "2",
                  y: "2",
                  width: "12",
                  height: "12",
                  rx: "1",
                  stroke: "currentColor",
                  strokeWidth: "1.5"
                }
              ),
              /* @__PURE__ */ jsx153(
                "rect",
                {
                  x: "8.5",
                  y: "8.5",
                  width: "4",
                  height: "4",
                  rx: "0.5",
                  fill: showMinimap ? "currentColor" : "none",
                  stroke: "currentColor",
                  strokeWidth: "1"
                }
              )
            ] })
          }
        )
      ]
    }
  );
}

// src/workflow/WorkflowCanvas.tsx
import { jsx as jsx154, jsxs as jsxs137 } from "react/jsx-runtime";
var NODE_TYPES = {
  workflowNode: WorkflowNode,
  workflowDecision: DecisionNode,
  workflowGroup: GroupNode
};
var EDGE_TYPES = {
  workflowEdge: WorkflowEdge
};
var WORKFLOW_STYLES = `
@keyframes workflowEdgeDash {
  to {
    stroke-dashoffset: -12;
  }
}
`;
var stylesInjected = false;
function injectWorkflowStyles() {
  if (stylesInjected || typeof document === "undefined") return;
  const style = document.createElement("style");
  style.setAttribute("data-workflow-styles", "");
  style.textContent = WORKFLOW_STYLES;
  document.head.appendChild(style);
  stylesInjected = true;
}
function WorkflowCanvasInner({
  steps,
  connections,
  direction = "vertical",
  onStepClick,
  onStepDoubleClick,
  selectedStepId,
  interactive = false,
  showMinimap: initialShowMinimap = true,
  showControls: showControlsPanel = true,
  className = ""
}) {
  const { fitView, zoomIn, zoomOut } = useReactFlow();
  const [showMinimap, setShowMinimap] = useState31(initialShowMinimap);
  const [collapsedGroups, setCollapsedGroups] = useState31(
    /* @__PURE__ */ new Set()
  );
  React41.useEffect(() => {
    injectWorkflowStyles();
  }, []);
  const handleToggleCollapse = useCallback23((groupId) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);
  const { nodes: layoutNodes, edges: layoutEdges } = useWorkflowLayout({
    steps,
    connections,
    direction,
    selectedStepId,
    onStepClick,
    onStepDoubleClick,
    collapsedGroups,
    onToggleCollapse: handleToggleCollapse
  });
  const [nodes, setNodes, onNodesChange] = useNodesState(layoutNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(layoutEdges);
  React41.useEffect(() => {
    setNodes(layoutNodes);
    setEdges(layoutEdges);
  }, [layoutNodes, layoutEdges, setNodes, setEdges]);
  React41.useEffect(() => {
    const timer = setTimeout(() => {
      fitView({ padding: 0.15, duration: 300 });
    }, 50);
    return () => clearTimeout(timer);
  }, [direction, fitView]);
  const handleFitView = useCallback23(() => {
    fitView({ padding: 0.15, duration: 300 });
  }, [fitView]);
  const handleZoomIn = useCallback23(() => {
    zoomIn({ duration: 200 });
  }, [zoomIn]);
  const handleZoomOut = useCallback23(() => {
    zoomOut({ duration: 200 });
  }, [zoomOut]);
  const handleToggleMinimap = useCallback23(() => {
    setShowMinimap((prev) => !prev);
  }, []);
  const minimapNodeColor = useCallback23((node) => {
    const status = node.data?.step?.status;
    switch (status) {
      case "active":
        return "rgb(var(--color-text-primary))";
      case "completed":
        return "rgb(var(--color-success))";
      case "failed":
        return "rgb(var(--color-error))";
      case "skipped":
        return "rgb(var(--color-text-disabled))";
      default:
        return "rgb(var(--color-text-tertiary))";
    }
  }, []);
  return /* @__PURE__ */ jsxs137(
    "div",
    {
      className: `relative bg-canvas border border-recessed rounded-sm overflow-hidden ${className}`,
      style: { width: "100%", height: "100%", minHeight: 400 },
      role: "img",
      "aria-label": "Workflow visualization",
      tabIndex: 0,
      children: [
        /* @__PURE__ */ jsxs137(
          ReactFlow,
          {
            nodes,
            edges,
            onNodesChange,
            onEdgesChange,
            nodeTypes: NODE_TYPES,
            edgeTypes: EDGE_TYPES,
            nodesDraggable: interactive,
            nodesConnectable: interactive,
            elementsSelectable: true,
            panOnDrag: true,
            zoomOnScroll: true,
            zoomOnPinch: true,
            zoomOnDoubleClick: false,
            fitView: true,
            fitViewOptions: { padding: 0.15 },
            minZoom: 0.2,
            maxZoom: 2,
            proOptions: { hideAttribution: true },
            children: [
              /* @__PURE__ */ jsx154(
                Background,
                {
                  variant: BackgroundVariant.Dots,
                  gap: 16,
                  size: 1,
                  color: "rgb(var(--color-recessed))"
                }
              ),
              showMinimap && /* @__PURE__ */ jsx154(
                MiniMap,
                {
                  nodeColor: minimapNodeColor,
                  maskColor: "rgb(var(--color-canvas) / 0.7)",
                  className: "!bg-surface/80 !border !border-recessed !rounded-sm !shadow-sm",
                  pannable: true,
                  zoomable: true
                }
              )
            ]
          }
        ),
        showControlsPanel && /* @__PURE__ */ jsx154(
          WorkflowControls,
          {
            showMinimap,
            onToggleMinimap: handleToggleMinimap,
            onFitView: handleFitView,
            onZoomIn: handleZoomIn,
            onZoomOut: handleZoomOut
          }
        )
      ]
    }
  );
}
function WorkflowCanvas(props) {
  return /* @__PURE__ */ jsx154(ReactFlowProvider, { children: /* @__PURE__ */ jsx154(WorkflowCanvasInner, { ...props }) });
}

// src/workflow/LoanWorkflowDashboard.tsx
import React42, { useState as useState32, useCallback as useCallback24, useMemo as useMemo16 } from "react";
import {
  FileTextIcon as FileTextIcon2,
  SearchIcon as SearchIcon9,
  ClockIcon as ClockIcon2,
  AlertTriangleIcon as AlertTriangleIcon4,
  ChevronRightIcon as ChevronRightIcon9,
  FilterIcon as FilterIcon2
} from "lucide-react";
import { jsx as jsx155, jsxs as jsxs138 } from "react/jsx-runtime";

// src/workflow/ComplianceWorkflow.tsx
import { useState as useState33, useCallback as useCallback25 } from "react";
import {
  ShieldCheckIcon as ShieldCheckIcon4,
  AlertTriangleIcon as AlertTriangleIcon5,
  CheckCircleIcon as CheckCircleIcon3,
  XCircleIcon as XCircleIcon3,
  ClockIcon as ClockIcon3,
  FileTextIcon as FileTextIcon3,
  UserIcon,
  ChevronDownIcon as ChevronDownIcon15,
  ChevronRightIcon as ChevronRightIcon10,
  ExternalLinkIcon
} from "lucide-react";
import { jsx as jsx156, jsxs as jsxs139 } from "react/jsx-runtime";

// src/workflow/ApprovalQueue.tsx
import React44, { useState as useState34, useCallback as useCallback26, useMemo as useMemo17 } from "react";
import {
  CheckIcon as CheckIcon13,
  XIcon as XIcon12,
  ClockIcon as ClockIcon4,
  UserIcon as UserIcon2,
  ChevronRightIcon as ChevronRightIcon11,
  AlertTriangleIcon as AlertTriangleIcon6,
  ArrowRightIcon as ArrowRightIcon4,
  MoreHorizontalIcon as MoreHorizontalIcon3,
  SearchIcon as SearchIcon10
} from "lucide-react";
import { jsx as jsx157, jsxs as jsxs140 } from "react/jsx-runtime";

// src/templates/ReportTemplates.tsx
import {
  CheckIcon as CheckIcon14,
  TrendingUpIcon as TrendingUpIcon6,
  AlertTriangleIcon as AlertTriangleIcon7,
  BarChart3Icon,
  FileTextIcon as FileTextIcon4,
  CalendarIcon as CalendarIcon4,
  ArrowUpIcon as ArrowUpIcon2,
  ArrowDownIcon as ArrowDownIcon2,
  MinusIcon as MinusIcon2
} from "lucide-react";
import { jsx as jsx158, jsxs as jsxs141 } from "react/jsx-runtime";
var REPORT_COLORS = {
  // Brand
  accent: "#D2001F",
  accentLight: "#D2001F15",
  // Text
  primary: "#000000",
  secondary: "#3A3330",
  tertiary: "#6B5D54",
  // Backgrounds
  bgPrimary: "#F5F1E8",
  bgWhite: "#FFFFFF",
  bgWarm: "#FFFBF7",
  bgElevated: "#F5EDE5",
  bgData: "#F8F6F3",
  // Functional
  growth: "#5A6B5A",
  growthLight: "#5A6B5A15",
  warning: "#F59E0B",
  warningLight: "#F59E0B15",
  error: "#C52A1A",
  errorLight: "#C52A1A15",
  // Borders
  borderLight: "#E5DDD5",
  borderMedium: "#D4C4B0"
};
function ReportTemplates() {
  return /* @__PURE__ */ jsx158("div", { className: "min-h-screen bg-canvas py-12", children: /* @__PURE__ */ jsxs141("div", { className: "max-w-[1400px] mx-auto px-8", children: [
    /* @__PURE__ */ jsxs141("div", { className: "mb-12", children: [
      /* @__PURE__ */ jsx158("div", { className: "text-label-sm font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-4", children: "Brand Assets" }),
      /* @__PURE__ */ jsx158("h1", { className: "text-4xl font-serif font-semibold text-primary mb-4 tracking-[-0.04em]", children: "Report Template Theme" }),
      /* @__PURE__ */ jsx158("p", { className: "text-lg font-serif text-secondary leading-[1.6] max-w-3xl", children: 'Letter portrait (8.5" \xD7 11") document templates for analytical reports, market forecasts, research papers, and institutional communications. Designed for Google Docs, Microsoft Word, and PDF export.' })
    ] }),
    /* @__PURE__ */ jsxs141("section", { className: "mb-16", children: [
      /* @__PURE__ */ jsx158("h2", { className: "text-2xl font-sans font-bold text-primary mb-6 tracking-[-0.02em]", children: "Report Color Palette" }),
      /* @__PURE__ */ jsx158("div", { className: "bg-surface p-6 rounded-sm shadow-sm", children: /* @__PURE__ */ jsx158("div", { className: "grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4", children: Object.entries(REPORT_COLORS).slice(0, 12).map(
        ([name, hex]) => /* @__PURE__ */ jsxs141("div", { className: "text-center", children: [
          /* @__PURE__ */ jsx158(
            "div",
            {
              className: "w-full h-16 rounded-sm mb-2 border border-recessed",
              style: {
                backgroundColor: hex
              }
            }
          ),
          /* @__PURE__ */ jsx158("div", { className: "text-xs font-sans font-semibold text-primary capitalize", children: name.replace(/([A-Z])/g, " $1").trim() }),
          /* @__PURE__ */ jsx158("div", { className: "text-xs font-mono text-tertiary", children: hex })
        ] }, name)
      ) }) })
    ] }),
    /* @__PURE__ */ jsxs141("section", { className: "space-y-16", children: [
      /* @__PURE__ */ jsxs141("div", { children: [
        /* @__PURE__ */ jsx158("h3", { className: "text-xl font-sans font-semibold text-primary mb-4 tracking-[-0.02em]", children: "1. Report Cover Page" }),
        /* @__PURE__ */ jsx158("p", { className: "text-sm font-serif text-secondary mb-6", children: "Professional cover page for analytical reports and forecasts. Includes document metadata and classification." }),
        /* @__PURE__ */ jsx158("div", { className: "flex justify-center", children: /* @__PURE__ */ jsx158(
          "div",
          {
            className: "w-full max-w-[612px] bg-surface rounded-sm shadow-lg overflow-hidden border border-recessed",
            style: {
              aspectRatio: "8.5/11"
            },
            children: /* @__PURE__ */ jsxs141(
              "div",
              {
                className: "h-full flex flex-col",
                style: {
                  backgroundColor: REPORT_COLORS.bgWhite
                },
                children: [
                  /* @__PURE__ */ jsx158(
                    "div",
                    {
                      className: "h-1.5",
                      style: {
                        backgroundColor: REPORT_COLORS.accent
                      }
                    }
                  ),
                  /* @__PURE__ */ jsxs141("div", { className: "flex-1 flex flex-col px-8 py-6", children: [
                    /* @__PURE__ */ jsx158(
                      "img",
                      {
                        src: "https://cdn.aaim.app/logos/aaim-master-grey.svg",
                        alt: "Aaim",
                        className: "h-5 mb-auto"
                      }
                    ),
                    /* @__PURE__ */ jsx158("div", { className: "mb-3", children: /* @__PURE__ */ jsx158(
                      "span",
                      {
                        className: "inline-block px-2 py-1 text-[8px] font-sans font-semibold uppercase tracking-[0.15em] rounded-[2px] text-white",
                        style: {
                          backgroundColor: REPORT_COLORS.accent
                        },
                        children: "Market Forecast"
                      }
                    ) }),
                    /* @__PURE__ */ jsxs141("h1", { className: "text-xl font-serif font-semibold text-primary mb-2 tracking-[-0.04em] leading-snug", children: [
                      "Alternative Asset Lending",
                      /* @__PURE__ */ jsx158("br", {}),
                      "Market Outlook 2025"
                    ] }),
                    /* @__PURE__ */ jsx158("p", { className: "text-caption-sm font-serif text-secondary leading-loose mb-6 max-w-sm", children: "Institutional analysis of emerging opportunities in private equity, real estate, and digital asset collateralization." }),
                    /* @__PURE__ */ jsx158(
                      "div",
                      {
                        className: "mt-auto pt-4 border-t",
                        style: {
                          borderColor: REPORT_COLORS.borderLight
                        },
                        children: /* @__PURE__ */ jsxs141("div", { className: "grid grid-cols-2 gap-4 text-caption-xs", children: [
                          /* @__PURE__ */ jsxs141("div", { children: [
                            /* @__PURE__ */ jsx158("div", { className: "text-[8px] font-sans font-semibold uppercase tracking-[0.1em] text-tertiary mb-0.5", children: "Published" }),
                            /* @__PURE__ */ jsx158("div", { className: "font-sans text-primary", children: "January 15, 2025" })
                          ] }),
                          /* @__PURE__ */ jsxs141("div", { children: [
                            /* @__PURE__ */ jsx158("div", { className: "text-[8px] font-sans font-semibold uppercase tracking-[0.1em] text-tertiary mb-0.5", children: "Classification" }),
                            /* @__PURE__ */ jsx158("div", { className: "font-sans text-primary", children: "Confidential" })
                          ] }),
                          /* @__PURE__ */ jsxs141("div", { children: [
                            /* @__PURE__ */ jsx158("div", { className: "text-[8px] font-sans font-semibold uppercase tracking-[0.1em] text-tertiary mb-0.5", children: "Authors" }),
                            /* @__PURE__ */ jsx158("div", { className: "font-sans text-primary", children: "Aaim Research Team" })
                          ] }),
                          /* @__PURE__ */ jsxs141("div", { children: [
                            /* @__PURE__ */ jsx158("div", { className: "text-[8px] font-sans font-semibold uppercase tracking-[0.1em] text-tertiary mb-0.5", children: "Version" }),
                            /* @__PURE__ */ jsx158("div", { className: "font-sans text-primary", children: "1.0" })
                          ] })
                        ] })
                      }
                    )
                  ] })
                ]
              }
            )
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxs141("div", { children: [
        /* @__PURE__ */ jsx158("h3", { className: "text-xl font-sans font-semibold text-primary mb-4 tracking-[-0.02em]", children: "2. Executive Summary" }),
        /* @__PURE__ */ jsx158("p", { className: "text-sm font-serif text-secondary mb-6", children: "High-level overview with key findings, recommendations, and critical metrics." }),
        /* @__PURE__ */ jsx158("div", { className: "flex justify-center", children: /* @__PURE__ */ jsx158(
          "div",
          {
            className: "w-full max-w-[612px] bg-surface rounded-sm shadow-lg overflow-hidden border border-recessed",
            style: {
              aspectRatio: "8.5/11"
            },
            children: /* @__PURE__ */ jsxs141(
              "div",
              {
                className: "h-full flex flex-col",
                style: {
                  backgroundColor: REPORT_COLORS.bgWhite
                },
                children: [
                  /* @__PURE__ */ jsx158(
                    "div",
                    {
                      className: "px-8 pt-5 pb-3 border-b",
                      style: {
                        borderColor: REPORT_COLORS.borderLight
                      },
                      children: /* @__PURE__ */ jsxs141("div", { className: "flex items-center justify-between", children: [
                        /* @__PURE__ */ jsxs141("div", { children: [
                          /* @__PURE__ */ jsx158(
                            "div",
                            {
                              className: "text-[8px] font-sans font-semibold uppercase tracking-[0.15em] mb-1",
                              style: {
                                color: REPORT_COLORS.accent
                              },
                              children: "Executive Summary"
                            }
                          ),
                          /* @__PURE__ */ jsx158("h2", { className: "text-base font-serif font-semibold text-primary tracking-[-0.04em]", children: "Key findings and recommendations" })
                        ] }),
                        /* @__PURE__ */ jsx158(
                          "img",
                          {
                            src: "https://cdn.aaim.app/logos/aaim-master-grey.svg",
                            alt: "Aaim",
                            className: "h-4 opacity-50"
                          }
                        )
                      ] })
                    }
                  ),
                  /* @__PURE__ */ jsxs141("div", { className: "flex-1 px-8 py-5 overflow-hidden", children: [
                    /* @__PURE__ */ jsx158("div", { className: "grid grid-cols-3 gap-3 mb-5", children: [
                      {
                        label: "Market Size",
                        value: "$4.5T",
                        change: "+12%",
                        positive: true
                      },
                      {
                        label: "Projected Growth",
                        value: "18.2%",
                        change: "CAGR",
                        positive: true
                      },
                      {
                        label: "Risk Rating",
                        value: "Moderate",
                        change: "Stable",
                        positive: null
                      }
                    ].map(
                      (metric, i) => /* @__PURE__ */ jsxs141(
                        "div",
                        {
                          className: "p-2.5 rounded-sm",
                          style: {
                            backgroundColor: REPORT_COLORS.bgData
                          },
                          children: [
                            /* @__PURE__ */ jsx158("div", { className: "text-[7px] font-sans font-semibold uppercase tracking-[0.1em] text-tertiary mb-0.5", children: metric.label }),
                            /* @__PURE__ */ jsx158(
                              "div",
                              {
                                className: "text-base font-serif font-semibold tracking-[-0.02em]",
                                style: {
                                  color: REPORT_COLORS.accent
                                },
                                children: metric.value
                              }
                            ),
                            /* @__PURE__ */ jsxs141("div", { className: "flex items-center gap-0.5 mt-0.5", children: [
                              metric.positive === true && /* @__PURE__ */ jsx158(
                                ArrowUpIcon2,
                                {
                                  className: "w-2.5 h-2.5",
                                  style: {
                                    color: REPORT_COLORS.growth
                                  }
                                }
                              ),
                              metric.positive === false && /* @__PURE__ */ jsx158(
                                ArrowDownIcon2,
                                {
                                  className: "w-2.5 h-2.5",
                                  style: {
                                    color: REPORT_COLORS.error
                                  }
                                }
                              ),
                              metric.positive === null && /* @__PURE__ */ jsx158(MinusIcon2, { className: "w-2.5 h-2.5 text-tertiary" }),
                              /* @__PURE__ */ jsx158(
                                "span",
                                {
                                  className: "text-[9px] font-sans",
                                  style: {
                                    color: metric.positive === true ? REPORT_COLORS.growth : metric.positive === false ? REPORT_COLORS.error : REPORT_COLORS.tertiary
                                  },
                                  children: metric.change
                                }
                              )
                            ] })
                          ]
                        },
                        i
                      )
                    ) }),
                    /* @__PURE__ */ jsxs141("div", { className: "mb-4", children: [
                      /* @__PURE__ */ jsx158("h3", { className: "text-caption-xs font-sans font-semibold text-primary mb-2 tracking-[-0.02em]", children: "Key Findings" }),
                      /* @__PURE__ */ jsx158("ul", { className: "space-y-1.5", children: [
                        "Alternative asset lending market projected to reach $6.2T by 2028",
                        "Private equity-backed loans show 40% lower default rates than traditional unsecured lending",
                        "Digital asset collateralization emerging as fastest-growing segment (+156% YoY)",
                        "Regulatory frameworks maturing across 15 key jurisdictions"
                      ].map(
                        (finding, i) => /* @__PURE__ */ jsxs141("li", { className: "flex items-start gap-1.5", children: [
                          /* @__PURE__ */ jsx158(
                            CheckIcon14,
                            {
                              className: "w-3 h-3 mt-0.5 flex-shrink-0",
                              style: {
                                color: REPORT_COLORS.growth
                              }
                            }
                          ),
                          /* @__PURE__ */ jsx158("span", { className: "text-caption-xs font-serif text-secondary leading-body", children: finding })
                        ] }, i)
                      ) })
                    ] }),
                    /* @__PURE__ */ jsxs141(
                      "div",
                      {
                        className: "p-3 rounded-sm border-l-2",
                        style: {
                          backgroundColor: REPORT_COLORS.bgWarm,
                          borderColor: REPORT_COLORS.accent
                        },
                        children: [
                          /* @__PURE__ */ jsx158("h3", { className: "text-caption-xs font-sans font-semibold text-primary mb-1.5 tracking-[-0.02em]", children: "Strategic Recommendations" }),
                          /* @__PURE__ */ jsxs141("ol", { className: "space-y-0.5 text-caption-xs font-serif text-secondary leading-body", children: [
                            /* @__PURE__ */ jsx158("li", { children: "1. Prioritize private equity and real estate collateral programs" }),
                            /* @__PURE__ */ jsx158("li", { children: "2. Develop digital asset capabilities with conservative LTV ratios" }),
                            /* @__PURE__ */ jsx158("li", { children: "3. Invest in real-time valuation infrastructure" })
                          ] })
                        ]
                      }
                    )
                  ] }),
                  /* @__PURE__ */ jsx158(
                    "div",
                    {
                      className: "px-8 py-2.5 border-t",
                      style: {
                        borderColor: REPORT_COLORS.borderLight
                      },
                      children: /* @__PURE__ */ jsxs141("div", { className: "flex justify-between items-center text-[9px] font-sans text-tertiary", children: [
                        /* @__PURE__ */ jsx158("span", { children: "Alternative Asset Lending Market Outlook 2025" }),
                        /* @__PURE__ */ jsx158("span", { children: "Page 1" })
                      ] })
                    }
                  )
                ]
              }
            )
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxs141("div", { children: [
        /* @__PURE__ */ jsx158("h3", { className: "text-xl font-sans font-semibold text-primary mb-4 tracking-[-0.02em]", children: "3. Data Analysis Page" }),
        /* @__PURE__ */ jsx158("p", { className: "text-sm font-serif text-secondary mb-6", children: "Detailed data presentation with charts, tables, and analytical commentary." }),
        /* @__PURE__ */ jsx158("div", { className: "flex justify-center", children: /* @__PURE__ */ jsx158(
          "div",
          {
            className: "w-full max-w-[612px] bg-surface rounded-sm shadow-lg overflow-hidden border border-recessed",
            style: {
              aspectRatio: "8.5/11"
            },
            children: /* @__PURE__ */ jsxs141(
              "div",
              {
                className: "h-full flex flex-col",
                style: {
                  backgroundColor: REPORT_COLORS.bgWhite
                },
                children: [
                  /* @__PURE__ */ jsx158(
                    "div",
                    {
                      className: "px-8 pt-5 pb-3 border-b",
                      style: {
                        borderColor: REPORT_COLORS.borderLight
                      },
                      children: /* @__PURE__ */ jsxs141("div", { className: "flex items-center justify-between", children: [
                        /* @__PURE__ */ jsxs141("div", { children: [
                          /* @__PURE__ */ jsx158(
                            "div",
                            {
                              className: "text-[8px] font-sans font-semibold uppercase tracking-[0.15em] mb-1",
                              style: {
                                color: REPORT_COLORS.accent
                              },
                              children: "Section 2: Market Analysis"
                            }
                          ),
                          /* @__PURE__ */ jsx158("h2", { className: "text-base font-serif font-semibold text-primary tracking-[-0.04em]", children: "Asset class performance comparison" })
                        ] }),
                        /* @__PURE__ */ jsx158(
                          "img",
                          {
                            src: "https://cdn.aaim.app/logos/aaim-master-grey.svg",
                            alt: "Aaim",
                            className: "h-4 opacity-50"
                          }
                        )
                      ] })
                    }
                  ),
                  /* @__PURE__ */ jsxs141("div", { className: "flex-1 px-8 py-5 overflow-hidden", children: [
                    /* @__PURE__ */ jsxs141("div", { className: "mb-4", children: [
                      /* @__PURE__ */ jsx158("div", { className: "text-caption-xs font-sans font-semibold text-primary mb-1", children: "Figure 2.1: Collateral recovery rates by asset class" }),
                      /* @__PURE__ */ jsx158("div", { className: "text-[8px] font-sans text-tertiary mb-3", children: "Percentage of principal recovered, 2020\u20132024 average" }),
                      /* @__PURE__ */ jsxs141("div", { className: "relative", children: [
                        /* @__PURE__ */ jsx158("div", { className: "absolute inset-0 flex flex-col justify-between pointer-events-none", children: [0, 1, 2, 3, 4].map(
                          (_, i) => /* @__PURE__ */ jsx158(
                            "div",
                            {
                              className: "border-b",
                              style: {
                                borderColor: i === 4 ? REPORT_COLORS.borderMedium : "transparent"
                              }
                            },
                            i
                          )
                        ) }),
                        /* @__PURE__ */ jsx158("div", { className: "relative space-y-2.5 py-1", children: [
                          {
                            label: "Private Equity",
                            value: 85
                          },
                          {
                            label: "Real Estate",
                            value: 72
                          },
                          {
                            label: "Private Credit",
                            value: 68
                          },
                          {
                            label: "Digital Assets",
                            value: 45
                          }
                        ].map(
                          (item, i) => /* @__PURE__ */ jsxs141("div", { className: "flex items-center", children: [
                            /* @__PURE__ */ jsx158("div", { className: "w-20 text-[9px] font-sans text-secondary pr-2 text-right", children: item.label }),
                            /* @__PURE__ */ jsxs141("div", { className: "flex-1 flex items-center", children: [
                              /* @__PURE__ */ jsx158(
                                "div",
                                {
                                  className: "h-3.5",
                                  style: {
                                    width: `${item.value}%`,
                                    backgroundColor: REPORT_COLORS.accent
                                  }
                                }
                              ),
                              /* @__PURE__ */ jsxs141(
                                "span",
                                {
                                  className: "ml-1.5 text-[9px] font-mono font-medium",
                                  style: {
                                    color: REPORT_COLORS.primary
                                  },
                                  children: [
                                    item.value,
                                    "%"
                                  ]
                                }
                              )
                            ] })
                          ] }, i)
                        ) }),
                        /* @__PURE__ */ jsx158("div", { className: "flex items-center mt-2 ml-20", children: /* @__PURE__ */ jsxs141("div", { className: "flex-1 relative", children: [
                          /* @__PURE__ */ jsx158(
                            "div",
                            {
                              className: "h-px w-full",
                              style: {
                                backgroundColor: REPORT_COLORS.borderMedium
                              }
                            }
                          ),
                          /* @__PURE__ */ jsxs141("div", { className: "flex justify-between text-[7px] font-sans text-tertiary mt-1", children: [
                            /* @__PURE__ */ jsx158("span", { children: "0" }),
                            /* @__PURE__ */ jsx158("span", { children: "25" }),
                            /* @__PURE__ */ jsx158("span", { children: "50" }),
                            /* @__PURE__ */ jsx158("span", { children: "75" }),
                            /* @__PURE__ */ jsx158("span", { children: "100%" })
                          ] })
                        ] }) })
                      ] }),
                      /* @__PURE__ */ jsx158(
                        "div",
                        {
                          className: "mt-3 text-[7px] font-sans",
                          style: {
                            color: REPORT_COLORS.tertiary,
                            opacity: 0.75
                          },
                          children: "Source: Aaim Platform Analytics, Q4 2024"
                        }
                      )
                    ] }),
                    /* @__PURE__ */ jsxs141("div", { className: "space-y-2.5 text-caption-xs font-serif text-secondary leading-loose", children: [
                      /* @__PURE__ */ jsxs141("p", { children: [
                        /* @__PURE__ */ jsx158("strong", { className: "font-sans font-semibold text-primary", children: "Private equity collateral" }),
                        " ",
                        "continues to demonstrate the strongest risk-adjusted performance, with recovery rates exceeding 85% across all market conditions. This outperformance is attributed to the underlying asset quality and established secondary market liquidity."
                      ] }),
                      /* @__PURE__ */ jsxs141("p", { children: [
                        /* @__PURE__ */ jsx158("strong", { className: "font-sans font-semibold text-primary", children: "Digital assets" }),
                        ", while showing the highest volatility, present significant opportunity for institutions willing to implement conservative loan-to-value ratios (typically 40-50%) and real-time monitoring systems."
                      ] })
                    ] }),
                    /* @__PURE__ */ jsxs141(
                      "div",
                      {
                        className: "mt-4 p-3 rounded-sm flex items-start gap-2",
                        style: {
                          backgroundColor: REPORT_COLORS.warningLight
                        },
                        children: [
                          /* @__PURE__ */ jsx158(
                            AlertTriangleIcon7,
                            {
                              className: "w-4 h-4 flex-shrink-0 mt-0.5",
                              style: {
                                color: REPORT_COLORS.warning
                              }
                            }
                          ),
                          /* @__PURE__ */ jsxs141("div", { children: [
                            /* @__PURE__ */ jsx158("div", { className: "text-caption-xs font-sans font-semibold text-primary mb-0.5", children: "Risk Consideration" }),
                            /* @__PURE__ */ jsx158("p", { className: "text-[9px] font-serif text-secondary leading-body", children: "Digital asset valuations require 15-minute refresh cycles to maintain acceptable risk parameters. Institutions should ensure infrastructure supports real-time monitoring." })
                          ] })
                        ]
                      }
                    )
                  ] }),
                  /* @__PURE__ */ jsx158(
                    "div",
                    {
                      className: "px-8 py-2.5 border-t",
                      style: {
                        borderColor: REPORT_COLORS.borderLight
                      },
                      children: /* @__PURE__ */ jsxs141("div", { className: "flex justify-between items-center text-[9px] font-sans text-tertiary", children: [
                        /* @__PURE__ */ jsx158("span", { children: "Alternative Asset Lending Market Outlook 2025" }),
                        /* @__PURE__ */ jsx158("span", { children: "Page 5" })
                      ] })
                    }
                  )
                ]
              }
            )
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxs141("div", { children: [
        /* @__PURE__ */ jsx158("h3", { className: "text-xl font-sans font-semibold text-primary mb-4 tracking-[-0.02em]", children: "4. Forecast Table Page" }),
        /* @__PURE__ */ jsx158("p", { className: "text-sm font-serif text-secondary mb-6", children: "Structured data tables for projections, comparisons, and detailed metrics." }),
        /* @__PURE__ */ jsx158("div", { className: "flex justify-center", children: /* @__PURE__ */ jsx158(
          "div",
          {
            className: "w-full max-w-[612px] bg-surface rounded-sm shadow-lg overflow-hidden border border-recessed",
            style: {
              aspectRatio: "8.5/11"
            },
            children: /* @__PURE__ */ jsxs141(
              "div",
              {
                className: "h-full flex flex-col",
                style: {
                  backgroundColor: REPORT_COLORS.bgWhite
                },
                children: [
                  /* @__PURE__ */ jsx158(
                    "div",
                    {
                      className: "px-8 pt-5 pb-3 border-b",
                      style: {
                        borderColor: REPORT_COLORS.borderLight
                      },
                      children: /* @__PURE__ */ jsxs141("div", { className: "flex items-center justify-between", children: [
                        /* @__PURE__ */ jsxs141("div", { children: [
                          /* @__PURE__ */ jsx158(
                            "div",
                            {
                              className: "text-[8px] font-sans font-semibold uppercase tracking-[0.15em] mb-1",
                              style: {
                                color: REPORT_COLORS.accent
                              },
                              children: "Section 3: Projections"
                            }
                          ),
                          /* @__PURE__ */ jsx158("h2", { className: "text-base font-serif font-semibold text-primary tracking-[-0.04em]", children: "Five-year market forecast" })
                        ] }),
                        /* @__PURE__ */ jsx158(
                          "img",
                          {
                            src: "https://cdn.aaim.app/logos/aaim-master-grey.svg",
                            alt: "Aaim",
                            className: "h-4 opacity-50"
                          }
                        )
                      ] })
                    }
                  ),
                  /* @__PURE__ */ jsxs141("div", { className: "flex-1 px-8 py-5 overflow-hidden", children: [
                    /* @__PURE__ */ jsxs141("div", { className: "mb-4", children: [
                      /* @__PURE__ */ jsx158("div", { className: "text-caption-xs font-sans font-semibold text-primary mb-2", children: "Table 3.1: Alternative Asset Lending Market Size Projections ($B)" }),
                      /* @__PURE__ */ jsx158(
                        "div",
                        {
                          className: "border rounded-sm overflow-hidden",
                          style: {
                            borderColor: REPORT_COLORS.borderLight
                          },
                          children: /* @__PURE__ */ jsxs141("table", { className: "w-full text-[9px]", children: [
                            /* @__PURE__ */ jsx158("thead", { children: /* @__PURE__ */ jsxs141(
                              "tr",
                              {
                                style: {
                                  backgroundColor: REPORT_COLORS.bgData
                                },
                                children: [
                                  /* @__PURE__ */ jsx158(
                                    "th",
                                    {
                                      className: "px-2.5 py-2 text-left font-sans font-semibold text-primary border-b",
                                      style: {
                                        borderColor: REPORT_COLORS.borderLight
                                      },
                                      children: "Asset Class"
                                    }
                                  ),
                                  /* @__PURE__ */ jsx158(
                                    "th",
                                    {
                                      className: "px-2.5 py-2 text-right font-sans font-semibold text-primary border-b",
                                      style: {
                                        borderColor: REPORT_COLORS.borderLight
                                      },
                                      children: "2024"
                                    }
                                  ),
                                  /* @__PURE__ */ jsx158(
                                    "th",
                                    {
                                      className: "px-2.5 py-2 text-right font-sans font-semibold text-primary border-b",
                                      style: {
                                        borderColor: REPORT_COLORS.borderLight
                                      },
                                      children: "2025"
                                    }
                                  ),
                                  /* @__PURE__ */ jsx158(
                                    "th",
                                    {
                                      className: "px-2.5 py-2 text-right font-sans font-semibold text-primary border-b",
                                      style: {
                                        borderColor: REPORT_COLORS.borderLight
                                      },
                                      children: "2026"
                                    }
                                  ),
                                  /* @__PURE__ */ jsx158(
                                    "th",
                                    {
                                      className: "px-2.5 py-2 text-right font-sans font-semibold text-primary border-b",
                                      style: {
                                        borderColor: REPORT_COLORS.borderLight
                                      },
                                      children: "2028"
                                    }
                                  ),
                                  /* @__PURE__ */ jsx158(
                                    "th",
                                    {
                                      className: "px-2.5 py-2 text-right font-sans font-semibold text-primary border-b",
                                      style: {
                                        borderColor: REPORT_COLORS.borderLight
                                      },
                                      children: "CAGR"
                                    }
                                  )
                                ]
                              }
                            ) }),
                            /* @__PURE__ */ jsxs141("tbody", { className: "font-mono", children: [
                              [
                                {
                                  name: "Private Equity",
                                  y24: 1850,
                                  y25: 2120,
                                  y26: 2430,
                                  y28: 3180,
                                  cagr: "14.5%"
                                },
                                {
                                  name: "Real Estate",
                                  y24: 1420,
                                  y25: 1580,
                                  y26: 1760,
                                  y28: 2180,
                                  cagr: "11.3%"
                                },
                                {
                                  name: "Private Credit",
                                  y24: 890,
                                  y25: 1050,
                                  y26: 1240,
                                  y28: 1720,
                                  cagr: "17.9%"
                                },
                                {
                                  name: "Digital Assets",
                                  y24: 340,
                                  y25: 520,
                                  y26: 780,
                                  y28: 1120,
                                  cagr: "34.7%"
                                }
                              ].map(
                                (row, i) => /* @__PURE__ */ jsxs141(
                                  "tr",
                                  {
                                    className: "border-b last:border-b-0",
                                    style: {
                                      borderColor: REPORT_COLORS.borderLight
                                    },
                                    children: [
                                      /* @__PURE__ */ jsx158("td", { className: "px-2.5 py-1.5 font-sans text-secondary", children: row.name }),
                                      /* @__PURE__ */ jsx158("td", { className: "px-2.5 py-1.5 text-right text-secondary", children: row.y24.toLocaleString() }),
                                      /* @__PURE__ */ jsx158("td", { className: "px-2.5 py-1.5 text-right text-secondary", children: row.y25.toLocaleString() }),
                                      /* @__PURE__ */ jsx158("td", { className: "px-2.5 py-1.5 text-right text-secondary", children: row.y26.toLocaleString() }),
                                      /* @__PURE__ */ jsx158(
                                        "td",
                                        {
                                          className: "px-2.5 py-1.5 text-right font-semibold",
                                          style: {
                                            color: REPORT_COLORS.accent
                                          },
                                          children: row.y28.toLocaleString()
                                        }
                                      ),
                                      /* @__PURE__ */ jsx158(
                                        "td",
                                        {
                                          className: "px-2.5 py-1.5 text-right font-semibold",
                                          style: {
                                            color: REPORT_COLORS.growth
                                          },
                                          children: row.cagr
                                        }
                                      )
                                    ]
                                  },
                                  i
                                )
                              ),
                              /* @__PURE__ */ jsxs141(
                                "tr",
                                {
                                  style: {
                                    backgroundColor: REPORT_COLORS.bgData
                                  },
                                  children: [
                                    /* @__PURE__ */ jsx158("td", { className: "px-2.5 py-1.5 font-sans font-semibold text-primary", children: "Total Market" }),
                                    /* @__PURE__ */ jsx158("td", { className: "px-2.5 py-1.5 text-right font-semibold text-primary", children: "4,500" }),
                                    /* @__PURE__ */ jsx158("td", { className: "px-2.5 py-1.5 text-right font-semibold text-primary", children: "5,270" }),
                                    /* @__PURE__ */ jsx158("td", { className: "px-2.5 py-1.5 text-right font-semibold text-primary", children: "6,210" }),
                                    /* @__PURE__ */ jsx158(
                                      "td",
                                      {
                                        className: "px-2.5 py-1.5 text-right font-semibold",
                                        style: {
                                          color: REPORT_COLORS.accent
                                        },
                                        children: "8,200"
                                      }
                                    ),
                                    /* @__PURE__ */ jsx158(
                                      "td",
                                      {
                                        className: "px-2.5 py-1.5 text-right font-semibold",
                                        style: {
                                          color: REPORT_COLORS.growth
                                        },
                                        children: "16.2%"
                                      }
                                    )
                                  ]
                                }
                              )
                            ] })
                          ] })
                        }
                      ),
                      /* @__PURE__ */ jsx158("div", { className: "mt-1.5 text-[8px] font-sans text-tertiary", children: "Source: Aaim Research estimates based on industry data and proprietary models" })
                    ] }),
                    /* @__PURE__ */ jsxs141(
                      "div",
                      {
                        className: "p-3 rounded-sm",
                        style: {
                          backgroundColor: REPORT_COLORS.bgData
                        },
                        children: [
                          /* @__PURE__ */ jsx158("h4", { className: "text-caption-xs font-sans font-semibold text-primary mb-2", children: "Key Assumptions" }),
                          /* @__PURE__ */ jsxs141("ul", { className: "grid grid-cols-2 gap-1.5 text-[9px] font-serif text-secondary", children: [
                            /* @__PURE__ */ jsxs141("li", { className: "flex items-start gap-1.5", children: [
                              /* @__PURE__ */ jsx158(
                                "span",
                                {
                                  className: "w-1 h-1 rounded-full mt-1 flex-shrink-0",
                                  style: {
                                    backgroundColor: REPORT_COLORS.tertiary
                                  }
                                }
                              ),
                              "Continued regulatory clarity in major markets"
                            ] }),
                            /* @__PURE__ */ jsxs141("li", { className: "flex items-start gap-1.5", children: [
                              /* @__PURE__ */ jsx158(
                                "span",
                                {
                                  className: "w-1 h-1 rounded-full mt-1 flex-shrink-0",
                                  style: {
                                    backgroundColor: REPORT_COLORS.tertiary
                                  }
                                }
                              ),
                              "Institutional adoption rate of 15% annually"
                            ] }),
                            /* @__PURE__ */ jsxs141("li", { className: "flex items-start gap-1.5", children: [
                              /* @__PURE__ */ jsx158(
                                "span",
                                {
                                  className: "w-1 h-1 rounded-full mt-1 flex-shrink-0",
                                  style: {
                                    backgroundColor: REPORT_COLORS.tertiary
                                  }
                                }
                              ),
                              "No major market disruptions"
                            ] }),
                            /* @__PURE__ */ jsxs141("li", { className: "flex items-start gap-1.5", children: [
                              /* @__PURE__ */ jsx158(
                                "span",
                                {
                                  className: "w-1 h-1 rounded-full mt-1 flex-shrink-0",
                                  style: {
                                    backgroundColor: REPORT_COLORS.tertiary
                                  }
                                }
                              ),
                              "Technology infrastructure maturation"
                            ] })
                          ] })
                        ]
                      }
                    )
                  ] }),
                  /* @__PURE__ */ jsx158(
                    "div",
                    {
                      className: "px-8 py-2.5 border-t",
                      style: {
                        borderColor: REPORT_COLORS.borderLight
                      },
                      children: /* @__PURE__ */ jsxs141("div", { className: "flex justify-between items-center text-[9px] font-sans text-tertiary", children: [
                        /* @__PURE__ */ jsx158("span", { children: "Alternative Asset Lending Market Outlook 2025" }),
                        /* @__PURE__ */ jsx158("span", { children: "Page 8" })
                      ] })
                    }
                  )
                ]
              }
            )
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxs141("div", { children: [
        /* @__PURE__ */ jsx158("h3", { className: "text-xl font-sans font-semibold text-primary mb-4 tracking-[-0.02em]", children: "5. Risk Assessment Page" }),
        /* @__PURE__ */ jsx158("p", { className: "text-sm font-serif text-secondary mb-6", children: "Risk matrix and mitigation strategies for analytical reports." }),
        /* @__PURE__ */ jsx158("div", { className: "flex justify-center", children: /* @__PURE__ */ jsx158(
          "div",
          {
            className: "w-full max-w-[612px] bg-surface rounded-sm shadow-lg overflow-hidden border border-recessed",
            style: {
              aspectRatio: "8.5/11"
            },
            children: /* @__PURE__ */ jsxs141(
              "div",
              {
                className: "h-full flex flex-col",
                style: {
                  backgroundColor: REPORT_COLORS.bgWhite
                },
                children: [
                  /* @__PURE__ */ jsx158(
                    "div",
                    {
                      className: "px-8 pt-5 pb-3 border-b",
                      style: {
                        borderColor: REPORT_COLORS.borderLight
                      },
                      children: /* @__PURE__ */ jsxs141("div", { className: "flex items-center justify-between", children: [
                        /* @__PURE__ */ jsxs141("div", { children: [
                          /* @__PURE__ */ jsx158(
                            "div",
                            {
                              className: "text-[8px] font-sans font-semibold uppercase tracking-[0.15em] mb-1",
                              style: {
                                color: REPORT_COLORS.accent
                              },
                              children: "Section 4: Risk Analysis"
                            }
                          ),
                          /* @__PURE__ */ jsx158("h2", { className: "text-base font-serif font-semibold text-primary tracking-[-0.04em]", children: "Key risks and mitigations" })
                        ] }),
                        /* @__PURE__ */ jsx158(
                          "img",
                          {
                            src: "https://cdn.aaim.app/logos/aaim-master-grey.svg",
                            alt: "Aaim",
                            className: "h-4 opacity-50"
                          }
                        )
                      ] })
                    }
                  ),
                  /* @__PURE__ */ jsxs141("div", { className: "flex-1 px-8 py-5 overflow-hidden", children: [
                    /* @__PURE__ */ jsx158("div", { className: "space-y-3", children: [
                      {
                        level: "High",
                        color: REPORT_COLORS.error,
                        bgColor: REPORT_COLORS.errorLight,
                        title: "Valuation Volatility",
                        description: "Rapid price fluctuations in digital assets and private markets can trigger margin calls.",
                        mitigation: "Implement real-time monitoring with 15-minute refresh cycles and conservative LTV ratios (40-50%)."
                      },
                      {
                        level: "Medium",
                        color: REPORT_COLORS.warning,
                        bgColor: REPORT_COLORS.warningLight,
                        title: "Regulatory Uncertainty",
                        description: "Evolving regulatory frameworks may impact collateral eligibility and reporting requirements.",
                        mitigation: "Maintain compliance buffers and engage proactively with regulatory bodies across jurisdictions."
                      },
                      {
                        level: "Low",
                        color: REPORT_COLORS.growth,
                        bgColor: REPORT_COLORS.growthLight,
                        title: "Technology Infrastructure",
                        description: "System failures could impact real-time valuation and collateral monitoring capabilities.",
                        mitigation: "Deploy redundant systems with 99.97% uptime SLA and automated failover protocols."
                      }
                    ].map(
                      (risk, i) => /* @__PURE__ */ jsxs141(
                        "div",
                        {
                          className: "p-3 rounded-sm border-l-2",
                          style: {
                            backgroundColor: risk.bgColor,
                            borderColor: risk.color
                          },
                          children: [
                            /* @__PURE__ */ jsxs141("div", { className: "flex items-center gap-1.5 mb-1", children: [
                              /* @__PURE__ */ jsxs141(
                                "span",
                                {
                                  className: "px-1.5 py-0.5 text-[7px] font-sans font-semibold uppercase tracking-[0.1em] rounded-sm text-white",
                                  style: {
                                    backgroundColor: risk.color
                                  },
                                  children: [
                                    risk.level,
                                    " Risk"
                                  ]
                                }
                              ),
                              /* @__PURE__ */ jsx158("h4", { className: "text-caption-xs font-sans font-semibold text-primary", children: risk.title })
                            ] }),
                            /* @__PURE__ */ jsx158("p", { className: "text-[9px] font-serif text-secondary leading-body mb-1.5", children: risk.description }),
                            /* @__PURE__ */ jsxs141("div", { className: "flex items-start gap-1.5", children: [
                              /* @__PURE__ */ jsx158("span", { className: "text-[7px] font-sans font-semibold uppercase tracking-[0.1em] text-tertiary mt-0.5", children: "Mitigation:" }),
                              /* @__PURE__ */ jsx158("p", { className: "text-[9px] font-serif text-secondary leading-body", children: risk.mitigation })
                            ] })
                          ]
                        },
                        i
                      )
                    ) }),
                    /* @__PURE__ */ jsxs141(
                      "div",
                      {
                        className: "mt-4 p-3 rounded-sm",
                        style: {
                          backgroundColor: REPORT_COLORS.bgData
                        },
                        children: [
                          /* @__PURE__ */ jsx158("h4", { className: "text-caption-xs font-sans font-semibold text-primary mb-2", children: "Overall Risk Assessment" }),
                          /* @__PURE__ */ jsxs141("div", { className: "flex items-center gap-3", children: [
                            /* @__PURE__ */ jsx158("div", { className: "flex-1", children: /* @__PURE__ */ jsx158(
                              "div",
                              {
                                className: "h-2 rounded-full overflow-hidden",
                                style: {
                                  backgroundColor: REPORT_COLORS.borderLight
                                },
                                children: /* @__PURE__ */ jsx158(
                                  "div",
                                  {
                                    className: "h-full rounded-full",
                                    style: {
                                      width: "35%",
                                      backgroundColor: REPORT_COLORS.warning
                                    }
                                  }
                                )
                              }
                            ) }),
                            /* @__PURE__ */ jsx158(
                              "div",
                              {
                                className: "text-sm font-serif font-semibold",
                                style: {
                                  color: REPORT_COLORS.warning
                                },
                                children: "Moderate"
                              }
                            )
                          ] }),
                          /* @__PURE__ */ jsx158("p", { className: "mt-2 text-[9px] font-serif text-secondary leading-body", children: "With appropriate risk controls and infrastructure investments, the alternative asset lending market presents an attractive risk-adjusted opportunity for institutional participants." })
                        ]
                      }
                    )
                  ] }),
                  /* @__PURE__ */ jsx158(
                    "div",
                    {
                      className: "px-8 py-2.5 border-t",
                      style: {
                        borderColor: REPORT_COLORS.borderLight
                      },
                      children: /* @__PURE__ */ jsxs141("div", { className: "flex justify-between items-center text-[9px] font-sans text-tertiary", children: [
                        /* @__PURE__ */ jsx158("span", { children: "Alternative Asset Lending Market Outlook 2025" }),
                        /* @__PURE__ */ jsx158("span", { children: "Page 12" })
                      ] })
                    }
                  )
                ]
              }
            )
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxs141("div", { children: [
        /* @__PURE__ */ jsx158("h3", { className: "text-xl font-sans font-semibold text-primary mb-4 tracking-[-0.02em]", children: "6. Appendix / Methodology Page" }),
        /* @__PURE__ */ jsx158("p", { className: "text-sm font-serif text-secondary mb-6", children: "Supporting documentation, methodology notes, and data sources." }),
        /* @__PURE__ */ jsx158("div", { className: "flex justify-center", children: /* @__PURE__ */ jsx158(
          "div",
          {
            className: "w-full max-w-[612px] bg-surface rounded-sm shadow-lg overflow-hidden border border-recessed",
            style: {
              aspectRatio: "8.5/11"
            },
            children: /* @__PURE__ */ jsxs141(
              "div",
              {
                className: "h-full flex flex-col",
                style: {
                  backgroundColor: REPORT_COLORS.bgWhite
                },
                children: [
                  /* @__PURE__ */ jsx158(
                    "div",
                    {
                      className: "px-8 pt-5 pb-3 border-b",
                      style: {
                        borderColor: REPORT_COLORS.borderLight
                      },
                      children: /* @__PURE__ */ jsxs141("div", { className: "flex items-center justify-between", children: [
                        /* @__PURE__ */ jsxs141("div", { children: [
                          /* @__PURE__ */ jsx158(
                            "div",
                            {
                              className: "text-[8px] font-sans font-semibold uppercase tracking-[0.15em] mb-1",
                              style: {
                                color: REPORT_COLORS.accent
                              },
                              children: "Appendix A"
                            }
                          ),
                          /* @__PURE__ */ jsx158("h2", { className: "text-base font-serif font-semibold text-primary tracking-[-0.04em]", children: "Methodology and data sources" })
                        ] }),
                        /* @__PURE__ */ jsx158(
                          "img",
                          {
                            src: "https://cdn.aaim.app/logos/aaim-master-grey.svg",
                            alt: "Aaim",
                            className: "h-4 opacity-50"
                          }
                        )
                      ] })
                    }
                  ),
                  /* @__PURE__ */ jsxs141("div", { className: "flex-1 px-8 py-5 overflow-hidden", children: [
                    /* @__PURE__ */ jsxs141("div", { className: "mb-4", children: [
                      /* @__PURE__ */ jsx158("h3", { className: "text-caption-xs font-sans font-semibold text-primary mb-2 tracking-[-0.02em]", children: "Research Methodology" }),
                      /* @__PURE__ */ jsx158("p", { className: "text-caption-xs font-serif text-secondary leading-loose mb-3", children: "This report combines quantitative analysis of Aaim platform data with qualitative insights from industry interviews and secondary research. Market size estimates are derived from a bottom-up analysis of addressable collateral pools across asset classes." }),
                      /* @__PURE__ */ jsx158("div", { className: "grid grid-cols-2 gap-3", children: [
                        {
                          label: "Platform transactions analyzed",
                          value: "12,000+"
                        },
                        {
                          label: "Industry interviews conducted",
                          value: "45"
                        },
                        {
                          label: "Data points processed",
                          value: "2.4M"
                        },
                        {
                          label: "Time period covered",
                          value: "2020-2024"
                        }
                      ].map(
                        (item, i) => /* @__PURE__ */ jsxs141(
                          "div",
                          {
                            className: "p-2 rounded-sm",
                            style: {
                              backgroundColor: REPORT_COLORS.bgData
                            },
                            children: [
                              /* @__PURE__ */ jsx158(
                                "div",
                                {
                                  className: "text-sm font-serif font-semibold",
                                  style: {
                                    color: REPORT_COLORS.accent
                                  },
                                  children: item.value
                                }
                              ),
                              /* @__PURE__ */ jsx158("div", { className: "text-[8px] font-sans text-tertiary", children: item.label })
                            ]
                          },
                          i
                        )
                      ) })
                    ] }),
                    /* @__PURE__ */ jsxs141("div", { className: "mb-4", children: [
                      /* @__PURE__ */ jsx158("h3", { className: "text-caption-xs font-sans font-semibold text-primary mb-2 tracking-[-0.02em]", children: "Primary Data Sources" }),
                      /* @__PURE__ */ jsx158("ul", { className: "space-y-1 text-caption-xs font-serif text-secondary", children: [
                        "Aaim Platform Analytics (proprietary)",
                        "Federal Reserve Economic Data (FRED)",
                        "SEC EDGAR filings and disclosures",
                        "Preqin Private Capital Database",
                        "CoinGecko Digital Asset Data",
                        "NCUA Credit Union Call Reports"
                      ].map(
                        (source, i) => /* @__PURE__ */ jsxs141("li", { className: "flex items-start gap-1.5", children: [
                          /* @__PURE__ */ jsx158(FileTextIcon4, { className: "w-3 h-3 mt-0.5 flex-shrink-0 text-tertiary" }),
                          /* @__PURE__ */ jsx158("span", { children: source })
                        ] }, i)
                      ) })
                    ] }),
                    /* @__PURE__ */ jsxs141(
                      "div",
                      {
                        className: "p-3 rounded-sm border",
                        style: {
                          borderColor: REPORT_COLORS.borderLight
                        },
                        children: [
                          /* @__PURE__ */ jsx158("h4", { className: "text-[9px] font-sans font-semibold text-primary mb-1", children: "Disclaimer" }),
                          /* @__PURE__ */ jsx158("p", { className: "text-[8px] font-serif text-tertiary leading-loose", children: "This report is provided for informational purposes only and does not constitute investment advice. Past performance is not indicative of future results. Projections are based on current market conditions and assumptions that may change. Aaim makes no representations regarding the accuracy or completeness of information from third-party sources." })
                        ]
                      }
                    )
                  ] }),
                  /* @__PURE__ */ jsx158(
                    "div",
                    {
                      className: "px-8 py-2.5 border-t",
                      style: {
                        borderColor: REPORT_COLORS.borderLight
                      },
                      children: /* @__PURE__ */ jsxs141("div", { className: "flex justify-between items-center text-[9px] font-sans text-tertiary", children: [
                        /* @__PURE__ */ jsx158("span", { children: "Alternative Asset Lending Market Outlook 2025" }),
                        /* @__PURE__ */ jsx158("span", { children: "Page 18" })
                      ] })
                    }
                  )
                ]
              }
            )
          }
        ) })
      ] })
    ] }),
    /* @__PURE__ */ jsxs141("section", { className: "mt-16 bg-surface p-8 rounded-sm shadow-sm", children: [
      /* @__PURE__ */ jsx158("h2", { className: "text-2xl font-sans font-bold text-primary mb-6 tracking-[-0.02em]", children: "Google Docs / Word Implementation Guide" }),
      /* @__PURE__ */ jsxs141("div", { className: "grid md:grid-cols-2 gap-8", children: [
        /* @__PURE__ */ jsxs141("div", { children: [
          /* @__PURE__ */ jsx158("h3", { className: "text-lg font-sans font-semibold text-primary mb-4 tracking-[-0.02em]", children: "Document Setup" }),
          /* @__PURE__ */ jsx158("ul", { className: "space-y-2", children: [
            'Page size: Letter (8.5" \xD7 11")',
            'Margins: 0.75" top/bottom, 1" left/right',
            'Header: 0.4" from top edge',
            'Footer: 0.4" from bottom edge',
            "Line spacing: 1.15-1.25 for body text"
          ].map(
            (item, i) => /* @__PURE__ */ jsxs141("li", { className: "flex items-baseline gap-2", children: [
              /* @__PURE__ */ jsx158(CheckIcon14, { className: "w-4 h-4 text-growth flex-shrink-0 mt-1" }),
              /* @__PURE__ */ jsx158("span", { className: "text-sm font-serif text-secondary leading-[1.6]", children: item })
            ] }, i)
          ) })
        ] }),
        /* @__PURE__ */ jsxs141("div", { children: [
          /* @__PURE__ */ jsx158("h3", { className: "text-lg font-sans font-semibold text-primary mb-4 tracking-[-0.02em]", children: "Typography Setup" }),
          /* @__PURE__ */ jsx158("ul", { className: "space-y-2", children: [
            "Headlines: Crimson Text, Semibold, 14-18pt",
            "Section headers: Lato, Bold, 10-12pt",
            "Body text: Crimson Text, Regular, 9-10pt",
            "Captions: Lato, Regular, 7-8pt",
            "Data/tables: Lato, Medium, 8-9pt, tabular figures"
          ].map(
            (item, i) => /* @__PURE__ */ jsxs141("li", { className: "flex items-baseline gap-2", children: [
              /* @__PURE__ */ jsx158(CheckIcon14, { className: "w-4 h-4 text-growth flex-shrink-0 mt-1" }),
              /* @__PURE__ */ jsx158("span", { className: "text-sm font-serif text-secondary leading-[1.6]", children: item })
            ] }, i)
          ) })
        ] })
      ] }),
      /* @__PURE__ */ jsxs141(
        "div",
        {
          className: "mt-8 p-6 rounded-sm",
          style: {
            backgroundColor: REPORT_COLORS.bgPrimary
          },
          children: [
            /* @__PURE__ */ jsx158("h4", { className: "text-base font-sans font-semibold text-primary mb-3 tracking-[-0.02em]", children: "Report Types This Template Supports" }),
            /* @__PURE__ */ jsx158("div", { className: "grid grid-cols-2 md:grid-cols-4 gap-4", children: [
              {
                icon: BarChart3Icon,
                label: "Market Forecasts"
              },
              {
                icon: FileTextIcon4,
                label: "Research Papers"
              },
              {
                icon: TrendingUpIcon6,
                label: "Performance Reports"
              },
              {
                icon: CalendarIcon4,
                label: "Quarterly Reviews"
              }
            ].map(
              (type, i) => /* @__PURE__ */ jsxs141(
                "div",
                {
                  className: "flex items-center gap-2 text-sm font-sans text-secondary",
                  children: [
                    /* @__PURE__ */ jsx158(
                      type.icon,
                      {
                        className: "w-4 h-4",
                        style: {
                          color: REPORT_COLORS.accent
                        }
                      }
                    ),
                    /* @__PURE__ */ jsx158("span", { children: type.label })
                  ]
                },
                i
              )
            ) })
          ]
        }
      )
    ] })
  ] }) });
}

// src/templates/PresentationTemplates.tsx
import {
  CheckIcon as CheckIcon15,
  ArrowRightIcon as ArrowRightIcon5,
  TrendingUpIcon as TrendingUpIcon7,
  ShieldCheckIcon as ShieldCheckIcon5,
  BarChart3Icon as BarChart3Icon2
} from "lucide-react";
import { jsx as jsx159, jsxs as jsxs142 } from "react/jsx-runtime";
var SLIDE_COLORS = {
  // Brand
  accent: "#D2001F",
  accentHover: "#B8001A",
  // Text
  primary: "#000000",
  secondary: "#3A3330",
  tertiary: "#6B5D54",
  // Backgrounds
  bgPrimary: "#F5F1E8",
  bgWhite: "#FFFFFF",
  bgWarm: "#FFFBF7",
  bgElevated: "#F5EDE5",
  // Functional
  growth: "#5A6B5A",
  warning: "#F59E0B",
  error: "#C52A1A",
  // Borders
  borderLight: "#E5DDD5",
  borderMedium: "#D4C4B0"
};
function PresentationTemplates() {
  return /* @__PURE__ */ jsx159("div", { className: "min-h-screen bg-canvas py-12", children: /* @__PURE__ */ jsxs142("div", { className: "max-w-[1400px] mx-auto px-8", children: [
    /* @__PURE__ */ jsxs142("div", { className: "mb-12", children: [
      /* @__PURE__ */ jsx159("div", { className: "text-label-sm font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-4", children: "Brand Assets" }),
      /* @__PURE__ */ jsx159("h1", { className: "text-4xl font-serif font-semibold text-primary mb-4 tracking-[-0.04em]", children: "Presentation Template Theme" }),
      /* @__PURE__ */ jsx159("p", { className: "text-lg font-serif text-secondary leading-[1.6] max-w-3xl", children: "Slide templates designed for Google Slides following Aaim's institutional design language. Each template demonstrates proper typography, color usage, and layout principles." })
    ] }),
    /* @__PURE__ */ jsxs142("section", { className: "mb-16", children: [
      /* @__PURE__ */ jsx159("h2", { className: "text-2xl font-sans font-bold text-primary mb-6 tracking-[-0.02em]", children: "Theme Color Palette" }),
      /* @__PURE__ */ jsx159("div", { className: "bg-surface p-6 rounded-sm shadow-sm", children: /* @__PURE__ */ jsx159("div", { className: "grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4", children: Object.entries(SLIDE_COLORS).map(
        ([name, hex]) => /* @__PURE__ */ jsxs142("div", { className: "text-center", children: [
          /* @__PURE__ */ jsx159(
            "div",
            {
              className: "w-full h-16 rounded-sm mb-2 border border-recessed",
              style: {
                backgroundColor: hex
              }
            }
          ),
          /* @__PURE__ */ jsx159("div", { className: "text-xs font-sans font-semibold text-primary capitalize", children: name.replace(/([A-Z])/g, " $1").trim() }),
          /* @__PURE__ */ jsx159("div", { className: "text-xs font-mono text-tertiary", children: hex })
        ] }, name)
      ) }) })
    ] }),
    /* @__PURE__ */ jsxs142("section", { className: "space-y-16", children: [
      /* @__PURE__ */ jsxs142("div", { children: [
        /* @__PURE__ */ jsx159("h3", { className: "text-xl font-sans font-semibold text-primary mb-4 tracking-[-0.02em]", children: "1. Title Slide" }),
        /* @__PURE__ */ jsx159("p", { className: "text-sm font-serif text-secondary mb-6", children: "Opening slide for presentations. Clean, authoritative, brand-forward." }),
        /* @__PURE__ */ jsx159("div", { className: "aspect-video bg-surface rounded-sm shadow-lg overflow-hidden border border-recessed", children: /* @__PURE__ */ jsxs142(
          "div",
          {
            className: "h-full flex flex-col",
            style: {
              backgroundColor: SLIDE_COLORS.bgPrimary
            },
            children: [
              /* @__PURE__ */ jsx159(
                "div",
                {
                  className: "h-2",
                  style: {
                    backgroundColor: SLIDE_COLORS.accent
                  }
                }
              ),
              /* @__PURE__ */ jsxs142("div", { className: "flex-1 flex flex-col justify-center items-center px-16 py-12", children: [
                /* @__PURE__ */ jsx159(
                  "img",
                  {
                    src: "https://cdn.aaim.app/logos/aaim-master-grey.svg",
                    alt: "Aaim",
                    className: "h-12 mb-12"
                  }
                ),
                /* @__PURE__ */ jsxs142("h1", { className: "text-4xl md:text-5xl font-serif font-semibold text-primary text-center mb-4 tracking-[-0.04em]", children: [
                  "Institutional Infrastructure for",
                  /* @__PURE__ */ jsx159("br", {}),
                  "Alternative Asset Lending"
                ] }),
                /* @__PURE__ */ jsx159("p", { className: "text-lg font-sans text-secondary text-center max-w-2xl", children: "Platform Overview & Partnership Opportunity" }),
                /* @__PURE__ */ jsx159("div", { className: "mt-12 text-sm font-sans text-tertiary", children: "Q1 2025 \u2022 Confidential" })
              ] })
            ]
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxs142("div", { children: [
        /* @__PURE__ */ jsx159("h3", { className: "text-xl font-sans font-semibold text-primary mb-4 tracking-[-0.02em]", children: "2. Section Divider" }),
        /* @__PURE__ */ jsx159("p", { className: "text-sm font-serif text-secondary mb-6", children: "Transition slide between major sections. Uses accent color for emphasis." }),
        /* @__PURE__ */ jsx159("div", { className: "aspect-video bg-surface rounded-sm shadow-lg overflow-hidden border border-recessed", children: /* @__PURE__ */ jsxs142(
          "div",
          {
            className: "h-full flex",
            style: {
              backgroundColor: SLIDE_COLORS.accent
            },
            children: [
              /* @__PURE__ */ jsxs142("div", { className: "flex-1 flex flex-col justify-center px-16 py-12", children: [
                /* @__PURE__ */ jsx159("div", { className: "text-xs font-sans font-semibold uppercase tracking-[0.15em] text-white/70 mb-4", children: "Section 01" }),
                /* @__PURE__ */ jsxs142("h2", { className: "text-4xl md:text-5xl font-serif font-semibold text-white tracking-[-0.04em] mb-4", children: [
                  "The Market",
                  /* @__PURE__ */ jsx159("br", {}),
                  "Opportunity"
                ] }),
                /* @__PURE__ */ jsx159("p", { className: "text-lg font-sans text-white/80 max-w-md", children: "Understanding the $186M lending gap in alternative assets" })
              ] }),
              /* @__PURE__ */ jsx159("div", { className: "w-1/3 flex items-center justify-center", children: /* @__PURE__ */ jsx159("div", { className: "text-[200px] font-serif font-semibold text-white/10", children: "01" }) })
            ]
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxs142("div", { children: [
        /* @__PURE__ */ jsx159("h3", { className: "text-xl font-sans font-semibold text-primary mb-4 tracking-[-0.02em]", children: "3. Content with Headline" }),
        /* @__PURE__ */ jsx159("p", { className: "text-sm font-serif text-secondary mb-6", children: "Standard content slide with headline and body text. Most commonly used template." }),
        /* @__PURE__ */ jsx159("div", { className: "aspect-video bg-surface rounded-sm shadow-lg overflow-hidden border border-recessed", children: /* @__PURE__ */ jsxs142(
          "div",
          {
            className: "h-full flex flex-col",
            style: {
              backgroundColor: SLIDE_COLORS.bgWhite
            },
            children: [
              /* @__PURE__ */ jsx159(
                "div",
                {
                  className: "h-1",
                  style: {
                    backgroundColor: SLIDE_COLORS.accent
                  }
                }
              ),
              /* @__PURE__ */ jsxs142("div", { className: "flex-1 px-16 py-10", children: [
                /* @__PURE__ */ jsx159(
                  "div",
                  {
                    className: "text-label-xs font-sans font-semibold uppercase tracking-[0.15em] mb-3",
                    style: {
                      color: SLIDE_COLORS.accent
                    },
                    children: "Market Analysis"
                  }
                ),
                /* @__PURE__ */ jsx159("h2", { className: "text-3xl font-serif font-semibold text-primary mb-6 tracking-[-0.04em]", children: "Alternative assets represent a $4.5T untapped lending opportunity" }),
                /* @__PURE__ */ jsxs142("div", { className: "grid grid-cols-2 gap-8", children: [
                  /* @__PURE__ */ jsxs142("div", { children: [
                    /* @__PURE__ */ jsx159("p", { className: "text-base font-serif text-secondary leading-[1.7] mb-4", children: "Financial institutions are leaving significant revenue on the table by not offering lending against alternative assets. Members and clients increasingly hold wealth in:" }),
                    /* @__PURE__ */ jsx159("ul", { className: "space-y-2", children: [
                      "Private equity stakes",
                      "Real estate holdings",
                      "Cryptocurrency portfolios",
                      "Pre-IPO equity"
                    ].map(
                      (item, i) => /* @__PURE__ */ jsxs142("li", { className: "flex items-start gap-2", children: [
                        /* @__PURE__ */ jsx159(
                          CheckIcon15,
                          {
                            className: "w-4 h-4 mt-1 flex-shrink-0",
                            style: {
                              color: SLIDE_COLORS.growth
                            }
                          }
                        ),
                        /* @__PURE__ */ jsx159("span", { className: "text-sm font-sans text-secondary", children: item })
                      ] }, i)
                    ) })
                  ] }),
                  /* @__PURE__ */ jsx159("div", { className: "flex items-center justify-center", children: /* @__PURE__ */ jsxs142("div", { className: "text-center", children: [
                    /* @__PURE__ */ jsx159(
                      "div",
                      {
                        className: "text-6xl font-serif font-semibold mb-2",
                        style: {
                          color: SLIDE_COLORS.accent
                        },
                        children: "$4.5T"
                      }
                    ),
                    /* @__PURE__ */ jsxs142("div", { className: "text-sm font-sans text-tertiary", children: [
                      "Alternative assets held by",
                      /* @__PURE__ */ jsx159("br", {}),
                      "retail investors"
                    ] })
                  ] }) })
                ] })
              ] }),
              /* @__PURE__ */ jsx159(
                "div",
                {
                  className: "px-16 py-4 border-t",
                  style: {
                    borderColor: SLIDE_COLORS.borderLight
                  },
                  children: /* @__PURE__ */ jsxs142("div", { className: "flex justify-between items-center", children: [
                    /* @__PURE__ */ jsx159(
                      "img",
                      {
                        src: "https://cdn.aaim.app/logos/aaim-master-grey.svg",
                        alt: "Aaim",
                        className: "h-5 opacity-50"
                      }
                    ),
                    /* @__PURE__ */ jsx159("div", { className: "text-xs font-sans text-tertiary", children: "3" })
                  ] })
                }
              )
            ]
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxs142("div", { children: [
        /* @__PURE__ */ jsx159("h3", { className: "text-xl font-sans font-semibold text-primary mb-4 tracking-[-0.02em]", children: "4. Three Column Features" }),
        /* @__PURE__ */ jsx159("p", { className: "text-sm font-serif text-secondary mb-6", children: "Feature or benefit showcase with icons. Good for capabilities, differentiators, or process steps." }),
        /* @__PURE__ */ jsx159("div", { className: "aspect-video bg-surface rounded-sm shadow-lg overflow-hidden border border-recessed", children: /* @__PURE__ */ jsxs142(
          "div",
          {
            className: "h-full flex flex-col",
            style: {
              backgroundColor: SLIDE_COLORS.bgPrimary
            },
            children: [
              /* @__PURE__ */ jsx159(
                "div",
                {
                  className: "h-1",
                  style: {
                    backgroundColor: SLIDE_COLORS.accent
                  }
                }
              ),
              /* @__PURE__ */ jsxs142("div", { className: "flex-1 px-16 py-10", children: [
                /* @__PURE__ */ jsxs142("div", { className: "text-center mb-10", children: [
                  /* @__PURE__ */ jsx159("div", { className: "text-label-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-3", children: "Platform Capabilities" }),
                  /* @__PURE__ */ jsx159("h2", { className: "text-3xl font-serif font-semibold text-primary tracking-[-0.04em]", children: "Infrastructure built for scale" })
                ] }),
                /* @__PURE__ */ jsx159("div", { className: "grid grid-cols-3 gap-8", children: [
                  {
                    icon: ShieldCheckIcon5,
                    title: "Institutional Security",
                    desc: "Encryption in transit and at rest, with a SOC 2 Type 1 examination of the Security category in preparation"
                  },
                  {
                    icon: TrendingUpIcon7,
                    title: "Real-time Valuation",
                    desc: "Patent-pending methodology values 50+ alternative asset types every 15 minutes"
                  },
                  {
                    icon: BarChart3Icon2,
                    title: "Automated Compliance",
                    desc: "Examiner-ready audit trails and regulatory reporting built-in"
                  }
                ].map(
                  (feature, i) => /* @__PURE__ */ jsxs142(
                    "div",
                    {
                      className: "bg-surface p-6 rounded-sm shadow-sm",
                      children: [
                        /* @__PURE__ */ jsx159(
                          feature.icon,
                          {
                            className: "w-10 h-10 mb-4",
                            style: {
                              color: SLIDE_COLORS.accent
                            },
                            strokeWidth: 1.5
                          }
                        ),
                        /* @__PURE__ */ jsx159("h3", { className: "text-lg font-sans font-semibold text-primary mb-2 tracking-[-0.02em]", children: feature.title }),
                        /* @__PURE__ */ jsx159("p", { className: "text-sm font-serif text-secondary leading-[1.6]", children: feature.desc })
                      ]
                    },
                    i
                  )
                ) })
              ] }),
              /* @__PURE__ */ jsx159(
                "div",
                {
                  className: "px-16 py-4 border-t",
                  style: {
                    borderColor: SLIDE_COLORS.borderLight
                  },
                  children: /* @__PURE__ */ jsxs142("div", { className: "flex justify-between items-center", children: [
                    /* @__PURE__ */ jsx159(
                      "img",
                      {
                        src: "https://cdn.aaim.app/logos/aaim-master-grey.svg",
                        alt: "Aaim",
                        className: "h-5 opacity-50"
                      }
                    ),
                    /* @__PURE__ */ jsx159("div", { className: "text-xs font-sans text-tertiary", children: "4" })
                  ] })
                }
              )
            ]
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxs142("div", { children: [
        /* @__PURE__ */ jsx159("h3", { className: "text-xl font-sans font-semibold text-primary mb-4 tracking-[-0.02em]", children: "5. Big Number / Metric Slide" }),
        /* @__PURE__ */ jsx159("p", { className: "text-sm font-serif text-secondary mb-6", children: "Highlight a key statistic or metric. High impact for important data points." }),
        /* @__PURE__ */ jsx159("div", { className: "aspect-video bg-surface rounded-sm shadow-lg overflow-hidden border border-recessed", children: /* @__PURE__ */ jsxs142(
          "div",
          {
            className: "h-full flex flex-col",
            style: {
              backgroundColor: SLIDE_COLORS.bgWhite
            },
            children: [
              /* @__PURE__ */ jsx159(
                "div",
                {
                  className: "h-1",
                  style: {
                    backgroundColor: SLIDE_COLORS.accent
                  }
                }
              ),
              /* @__PURE__ */ jsx159("div", { className: "flex-1 flex items-center justify-center px-16 py-10", children: /* @__PURE__ */ jsxs142("div", { className: "text-center", children: [
                /* @__PURE__ */ jsx159("div", { className: "text-label-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-6", children: "Platform Performance" }),
                /* @__PURE__ */ jsx159(
                  "div",
                  {
                    className: "text-8xl md:text-9xl font-serif font-semibold mb-4 tracking-[-0.02em]",
                    style: {
                      color: SLIDE_COLORS.accent
                    },
                    children: "$8.2B"
                  }
                ),
                /* @__PURE__ */ jsx159("h2", { className: "text-2xl font-sans font-semibold text-primary mb-4 tracking-[-0.02em]", children: "Alternative assets under management" }),
                /* @__PURE__ */ jsx159("p", { className: "text-base font-serif text-secondary max-w-lg mx-auto leading-[1.6]", children: "Trusted by 45+ financial institutions representing the fastest-growing alternative asset lending platform in the market." })
              ] }) }),
              /* @__PURE__ */ jsx159(
                "div",
                {
                  className: "px-16 py-4 border-t",
                  style: {
                    borderColor: SLIDE_COLORS.borderLight
                  },
                  children: /* @__PURE__ */ jsxs142("div", { className: "flex justify-between items-center", children: [
                    /* @__PURE__ */ jsx159(
                      "img",
                      {
                        src: "https://cdn.aaim.app/logos/aaim-master-grey.svg",
                        alt: "Aaim",
                        className: "h-5 opacity-50"
                      }
                    ),
                    /* @__PURE__ */ jsx159("div", { className: "text-xs font-sans text-tertiary", children: "5" })
                  ] })
                }
              )
            ]
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxs142("div", { children: [
        /* @__PURE__ */ jsx159("h3", { className: "text-xl font-sans font-semibold text-primary mb-4 tracking-[-0.02em]", children: "6. Two Column Comparison" }),
        /* @__PURE__ */ jsx159("p", { className: "text-sm font-serif text-secondary mb-6", children: "Side-by-side comparison for before/after, build vs. buy, or competitive analysis." }),
        /* @__PURE__ */ jsx159("div", { className: "aspect-video bg-surface rounded-sm shadow-lg overflow-hidden border border-recessed", children: /* @__PURE__ */ jsxs142(
          "div",
          {
            className: "h-full flex flex-col",
            style: {
              backgroundColor: SLIDE_COLORS.bgWhite
            },
            children: [
              /* @__PURE__ */ jsx159(
                "div",
                {
                  className: "h-1",
                  style: {
                    backgroundColor: SLIDE_COLORS.accent
                  }
                }
              ),
              /* @__PURE__ */ jsxs142("div", { className: "flex-1 px-16 py-10", children: [
                /* @__PURE__ */ jsx159("div", { className: "text-center mb-8", children: /* @__PURE__ */ jsx159("h2", { className: "text-3xl font-serif font-semibold text-primary tracking-[-0.04em]", children: "Build vs. partner: The infrastructure decision" }) }),
                /* @__PURE__ */ jsxs142("div", { className: "grid grid-cols-2 gap-8 h-[calc(100%-80px)]", children: [
                  /* @__PURE__ */ jsxs142(
                    "div",
                    {
                      className: "p-6 rounded-sm border-l-4",
                      style: {
                        backgroundColor: SLIDE_COLORS.bgElevated,
                        borderColor: SLIDE_COLORS.tertiary
                      },
                      children: [
                        /* @__PURE__ */ jsxs142("div", { className: "flex items-center justify-between mb-4", children: [
                          /* @__PURE__ */ jsx159("h3", { className: "text-lg font-sans font-semibold text-primary tracking-[-0.02em]", children: "Build in-house" }),
                          /* @__PURE__ */ jsx159(
                            "span",
                            {
                              className: "px-2 py-1 text-xs font-sans font-semibold rounded-sm",
                              style: {
                                backgroundColor: SLIDE_COLORS.borderLight,
                                color: SLIDE_COLORS.tertiary
                              },
                              children: "18-24 months"
                            }
                          )
                        ] }),
                        /* @__PURE__ */ jsx159("ul", { className: "space-y-2 mb-4", children: [
                          "8-12 FTE engineering team",
                          "Compliance from scratch",
                          "Third-party integrations",
                          "Ongoing maintenance"
                        ].map(
                          (item, i) => /* @__PURE__ */ jsxs142("li", { className: "flex items-start gap-2", children: [
                            /* @__PURE__ */ jsx159(
                              "span",
                              {
                                className: "w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0",
                                style: {
                                  backgroundColor: SLIDE_COLORS.tertiary
                                }
                              }
                            ),
                            /* @__PURE__ */ jsx159("span", { className: "text-sm font-sans text-secondary", children: item })
                          ] }, i)
                        ) }),
                        /* @__PURE__ */ jsxs142(
                          "div",
                          {
                            className: "pt-4 border-t",
                            style: {
                              borderColor: SLIDE_COLORS.borderMedium
                            },
                            children: [
                              /* @__PURE__ */ jsx159("div", { className: "text-2xl font-serif font-semibold text-primary", children: "$2.5M - $4M" }),
                              /* @__PURE__ */ jsx159("div", { className: "text-xs font-sans text-tertiary", children: "First year investment" })
                            ]
                          }
                        )
                      ]
                    }
                  ),
                  /* @__PURE__ */ jsxs142(
                    "div",
                    {
                      className: "p-6 rounded-sm border-l-4",
                      style: {
                        backgroundColor: "#FFF9F5",
                        borderColor: SLIDE_COLORS.accent
                      },
                      children: [
                        /* @__PURE__ */ jsxs142("div", { className: "flex items-center justify-between mb-4", children: [
                          /* @__PURE__ */ jsx159("h3", { className: "text-lg font-sans font-semibold text-primary tracking-[-0.02em]", children: "Partner with Aaim" }),
                          /* @__PURE__ */ jsx159(
                            "span",
                            {
                              className: "px-2 py-1 text-xs font-sans font-semibold text-white rounded-sm",
                              style: {
                                backgroundColor: SLIDE_COLORS.growth
                              },
                              children: "2-4 weeks"
                            }
                          )
                        ] }),
                        /* @__PURE__ */ jsx159("ul", { className: "space-y-2 mb-4", children: [
                          "White-label platform",
                          "Built-in compliance",
                          "Pre-configured integrations",
                          "Continuous updates"
                        ].map(
                          (item, i) => /* @__PURE__ */ jsxs142("li", { className: "flex items-start gap-2", children: [
                            /* @__PURE__ */ jsx159(
                              CheckIcon15,
                              {
                                className: "w-4 h-4 mt-0.5 flex-shrink-0",
                                style: {
                                  color: SLIDE_COLORS.growth
                                }
                              }
                            ),
                            /* @__PURE__ */ jsx159("span", { className: "text-sm font-sans text-secondary", children: item })
                          ] }, i)
                        ) }),
                        /* @__PURE__ */ jsxs142(
                          "div",
                          {
                            className: "pt-4 border-t",
                            style: {
                              borderColor: SLIDE_COLORS.borderMedium
                            },
                            children: [
                              /* @__PURE__ */ jsx159(
                                "div",
                                {
                                  className: "text-2xl font-serif font-semibold",
                                  style: {
                                    color: SLIDE_COLORS.accent
                                  },
                                  children: "Revenue share"
                                }
                              ),
                              /* @__PURE__ */ jsx159("div", { className: "text-xs font-sans text-tertiary", children: "No upfront investment" })
                            ]
                          }
                        )
                      ]
                    }
                  )
                ] })
              ] }),
              /* @__PURE__ */ jsx159(
                "div",
                {
                  className: "px-16 py-4 border-t",
                  style: {
                    borderColor: SLIDE_COLORS.borderLight
                  },
                  children: /* @__PURE__ */ jsxs142("div", { className: "flex justify-between items-center", children: [
                    /* @__PURE__ */ jsx159(
                      "img",
                      {
                        src: "https://cdn.aaim.app/logos/aaim-master-grey.svg",
                        alt: "Aaim",
                        className: "h-5 opacity-50"
                      }
                    ),
                    /* @__PURE__ */ jsx159("div", { className: "text-xs font-sans text-tertiary", children: "6" })
                  ] })
                }
              )
            ]
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxs142("div", { children: [
        /* @__PURE__ */ jsx159("h3", { className: "text-xl font-sans font-semibold text-primary mb-4 tracking-[-0.02em]", children: "7. Quote / Testimonial" }),
        /* @__PURE__ */ jsx159("p", { className: "text-sm font-serif text-secondary mb-6", children: "Customer quote or testimonial slide. Builds credibility and social proof." }),
        /* @__PURE__ */ jsx159("div", { className: "aspect-video bg-surface rounded-sm shadow-lg overflow-hidden border border-recessed", children: /* @__PURE__ */ jsxs142(
          "div",
          {
            className: "h-full flex flex-col",
            style: {
              backgroundColor: SLIDE_COLORS.bgPrimary
            },
            children: [
              /* @__PURE__ */ jsx159(
                "div",
                {
                  className: "h-1",
                  style: {
                    backgroundColor: SLIDE_COLORS.accent
                  }
                }
              ),
              /* @__PURE__ */ jsx159("div", { className: "flex-1 flex items-center justify-center px-16 py-10", children: /* @__PURE__ */ jsxs142("div", { className: "max-w-3xl text-center", children: [
                /* @__PURE__ */ jsx159(
                  "div",
                  {
                    className: "text-8xl font-serif leading-none mb-4",
                    style: {
                      color: SLIDE_COLORS.accent
                    },
                    children: '"'
                  }
                ),
                /* @__PURE__ */ jsx159("blockquote", { className: "text-2xl md:text-3xl font-serif text-primary leading-loose mb-8 tracking-[-0.02em]", children: "Aaim's infrastructure enabled us to launch alternative asset lending in weeks, not years. Our members now have access to products we couldn't have built ourselves." }),
                /* @__PURE__ */ jsxs142("div", { className: "flex items-center justify-center gap-4", children: [
                  /* @__PURE__ */ jsx159(
                    "div",
                    {
                      className: "w-12 h-12 rounded-full flex items-center justify-center",
                      style: {
                        backgroundColor: SLIDE_COLORS.accent + "15"
                      },
                      children: /* @__PURE__ */ jsx159(
                        "span",
                        {
                          className: "text-sm font-sans font-semibold",
                          style: {
                            color: SLIDE_COLORS.accent
                          },
                          children: "SC"
                        }
                      )
                    }
                  ),
                  /* @__PURE__ */ jsxs142("div", { className: "text-left", children: [
                    /* @__PURE__ */ jsx159("div", { className: "text-base font-sans font-semibold text-primary", children: "Sarah Chen" }),
                    /* @__PURE__ */ jsx159("div", { className: "text-sm font-sans text-tertiary", children: "VP of Innovation, First National Credit Union" })
                  ] })
                ] })
              ] }) }),
              /* @__PURE__ */ jsx159(
                "div",
                {
                  className: "px-16 py-4 border-t",
                  style: {
                    borderColor: SLIDE_COLORS.borderLight
                  },
                  children: /* @__PURE__ */ jsxs142("div", { className: "flex justify-between items-center", children: [
                    /* @__PURE__ */ jsx159(
                      "img",
                      {
                        src: "https://cdn.aaim.app/logos/aaim-master-grey.svg",
                        alt: "Aaim",
                        className: "h-5 opacity-50"
                      }
                    ),
                    /* @__PURE__ */ jsx159("div", { className: "text-xs font-sans text-tertiary", children: "7" })
                  ] })
                }
              )
            ]
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxs142("div", { children: [
        /* @__PURE__ */ jsx159("h3", { className: "text-xl font-sans font-semibold text-primary mb-4 tracking-[-0.02em]", children: "8. Four Metrics Grid" }),
        /* @__PURE__ */ jsx159("p", { className: "text-sm font-serif text-secondary mb-6", children: "Display multiple KPIs or metrics in a balanced grid layout." }),
        /* @__PURE__ */ jsx159("div", { className: "aspect-video bg-surface rounded-sm shadow-lg overflow-hidden border border-recessed", children: /* @__PURE__ */ jsxs142(
          "div",
          {
            className: "h-full flex flex-col",
            style: {
              backgroundColor: SLIDE_COLORS.bgWhite
            },
            children: [
              /* @__PURE__ */ jsx159(
                "div",
                {
                  className: "h-1",
                  style: {
                    backgroundColor: SLIDE_COLORS.accent
                  }
                }
              ),
              /* @__PURE__ */ jsxs142("div", { className: "flex-1 px-16 py-10", children: [
                /* @__PURE__ */ jsxs142("div", { className: "text-center mb-8", children: [
                  /* @__PURE__ */ jsx159("div", { className: "text-label-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-3", children: "By The Numbers" }),
                  /* @__PURE__ */ jsx159("h2", { className: "text-3xl font-serif font-semibold text-primary tracking-[-0.04em]", children: "Platform performance at scale" })
                ] }),
                /* @__PURE__ */ jsx159("div", { className: "grid grid-cols-2 gap-6", children: [
                  {
                    value: "$8.2B",
                    label: "Assets under management",
                    change: "+34% YoY"
                  },
                  {
                    value: "45+",
                    label: "Financial institutions",
                    change: "+12 this year"
                  },
                  {
                    value: "99.97%",
                    label: "Platform uptime",
                    change: "SLA guaranteed"
                  },
                  {
                    value: "12,000+",
                    label: "Transactions processed",
                    change: "Annually"
                  }
                ].map(
                  (metric, i) => /* @__PURE__ */ jsxs142(
                    "div",
                    {
                      className: "p-6 rounded-sm border",
                      style: {
                        borderColor: SLIDE_COLORS.borderLight
                      },
                      children: [
                        /* @__PURE__ */ jsxs142("div", { className: "flex items-baseline justify-between mb-2", children: [
                          /* @__PURE__ */ jsx159(
                            "div",
                            {
                              className: "text-4xl font-serif font-semibold tracking-[-0.02em]",
                              style: {
                                color: SLIDE_COLORS.accent
                              },
                              children: metric.value
                            }
                          ),
                          /* @__PURE__ */ jsx159(
                            "div",
                            {
                              className: "text-sm font-mono font-medium",
                              style: {
                                color: SLIDE_COLORS.growth
                              },
                              children: metric.change
                            }
                          )
                        ] }),
                        /* @__PURE__ */ jsx159("div", { className: "text-sm font-sans text-secondary", children: metric.label })
                      ]
                    },
                    i
                  )
                ) })
              ] }),
              /* @__PURE__ */ jsx159(
                "div",
                {
                  className: "px-16 py-4 border-t",
                  style: {
                    borderColor: SLIDE_COLORS.borderLight
                  },
                  children: /* @__PURE__ */ jsxs142("div", { className: "flex justify-between items-center", children: [
                    /* @__PURE__ */ jsx159(
                      "img",
                      {
                        src: "https://cdn.aaim.app/logos/aaim-master-grey.svg",
                        alt: "Aaim",
                        className: "h-5 opacity-50"
                      }
                    ),
                    /* @__PURE__ */ jsx159("div", { className: "text-xs font-sans text-tertiary", children: "8" })
                  ] })
                }
              )
            ]
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxs142("div", { children: [
        /* @__PURE__ */ jsx159("h3", { className: "text-xl font-sans font-semibold text-primary mb-4 tracking-[-0.02em]", children: "9. Process / Timeline" }),
        /* @__PURE__ */ jsx159("p", { className: "text-sm font-serif text-secondary mb-6", children: "Show a process flow, implementation timeline, or step-by-step journey." }),
        /* @__PURE__ */ jsx159("div", { className: "aspect-video bg-surface rounded-sm shadow-lg overflow-hidden border border-recessed", children: /* @__PURE__ */ jsxs142(
          "div",
          {
            className: "h-full flex flex-col",
            style: {
              backgroundColor: SLIDE_COLORS.bgWhite
            },
            children: [
              /* @__PURE__ */ jsx159(
                "div",
                {
                  className: "h-1",
                  style: {
                    backgroundColor: SLIDE_COLORS.accent
                  }
                }
              ),
              /* @__PURE__ */ jsxs142("div", { className: "flex-1 px-16 py-10", children: [
                /* @__PURE__ */ jsxs142("div", { className: "text-center mb-10", children: [
                  /* @__PURE__ */ jsx159("div", { className: "text-label-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-3", children: "Implementation" }),
                  /* @__PURE__ */ jsx159("h2", { className: "text-3xl font-serif font-semibold text-primary tracking-[-0.04em]", children: "Go live in 4 weeks" })
                ] }),
                /* @__PURE__ */ jsxs142("div", { className: "relative", children: [
                  /* @__PURE__ */ jsx159(
                    "div",
                    {
                      className: "absolute top-8 left-0 right-0 h-0.5",
                      style: {
                        backgroundColor: SLIDE_COLORS.borderMedium
                      }
                    }
                  ),
                  /* @__PURE__ */ jsx159("div", { className: "grid grid-cols-4 gap-4 relative", children: [
                    {
                      week: "Week 1",
                      title: "Discovery",
                      desc: "Requirements gathering and system audit"
                    },
                    {
                      week: "Week 2",
                      title: "Configuration",
                      desc: "Platform setup and branding"
                    },
                    {
                      week: "Week 3",
                      title: "Integration",
                      desc: "Core banking and custody connections"
                    },
                    {
                      week: "Week 4",
                      title: "Launch",
                      desc: "Training, testing, and go-live"
                    }
                  ].map(
                    (step, i) => /* @__PURE__ */ jsxs142("div", { className: "text-center", children: [
                      /* @__PURE__ */ jsx159(
                        "div",
                        {
                          className: "w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-white font-sans font-bold text-lg",
                          style: {
                            backgroundColor: SLIDE_COLORS.accent
                          },
                          children: i + 1
                        }
                      ),
                      /* @__PURE__ */ jsx159(
                        "div",
                        {
                          className: "text-xs font-sans font-semibold uppercase tracking-[0.1em] mb-2",
                          style: {
                            color: SLIDE_COLORS.accent
                          },
                          children: step.week
                        }
                      ),
                      /* @__PURE__ */ jsx159("h3", { className: "text-base font-sans font-semibold text-primary mb-1 tracking-[-0.02em]", children: step.title }),
                      /* @__PURE__ */ jsx159("p", { className: "text-xs font-serif text-secondary leading-loose", children: step.desc })
                    ] }, i)
                  ) })
                ] })
              ] }),
              /* @__PURE__ */ jsx159(
                "div",
                {
                  className: "px-16 py-4 border-t",
                  style: {
                    borderColor: SLIDE_COLORS.borderLight
                  },
                  children: /* @__PURE__ */ jsxs142("div", { className: "flex justify-between items-center", children: [
                    /* @__PURE__ */ jsx159(
                      "img",
                      {
                        src: "https://cdn.aaim.app/logos/aaim-master-grey.svg",
                        alt: "Aaim",
                        className: "h-5 opacity-50"
                      }
                    ),
                    /* @__PURE__ */ jsx159("div", { className: "text-xs font-sans text-tertiary", children: "9" })
                  ] })
                }
              )
            ]
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxs142("div", { children: [
        /* @__PURE__ */ jsx159("h3", { className: "text-xl font-sans font-semibold text-primary mb-4 tracking-[-0.02em]", children: "10. Contact / Call to Action" }),
        /* @__PURE__ */ jsx159("p", { className: "text-sm font-serif text-secondary mb-6", children: "Closing slide with contact information and next steps." }),
        /* @__PURE__ */ jsx159("div", { className: "aspect-video bg-surface rounded-sm shadow-lg overflow-hidden border border-recessed", children: /* @__PURE__ */ jsxs142(
          "div",
          {
            className: "h-full flex flex-col",
            style: {
              backgroundColor: SLIDE_COLORS.bgPrimary
            },
            children: [
              /* @__PURE__ */ jsx159(
                "div",
                {
                  className: "h-2",
                  style: {
                    backgroundColor: SLIDE_COLORS.accent
                  }
                }
              ),
              /* @__PURE__ */ jsx159("div", { className: "flex-1 flex items-center justify-center px-16 py-10", children: /* @__PURE__ */ jsxs142("div", { className: "text-center", children: [
                /* @__PURE__ */ jsx159(
                  "img",
                  {
                    src: "https://cdn.aaim.app/logos/aaim-master-grey.svg",
                    alt: "Aaim",
                    className: "h-10 mx-auto mb-8"
                  }
                ),
                /* @__PURE__ */ jsx159("h2", { className: "text-4xl font-serif font-semibold text-primary mb-4 tracking-[-0.04em]", children: "Ready to expand into alternative asset lending?" }),
                /* @__PURE__ */ jsx159("p", { className: "text-lg font-serif text-secondary mb-8 max-w-2xl mx-auto", children: "Schedule a platform demo to see how Aaim can help your institution launch new lending products in weeks, not years." }),
                /* @__PURE__ */ jsxs142(
                  "div",
                  {
                    className: "inline-flex items-center gap-2 px-8 py-4 rounded-sm text-white font-sans font-semibold",
                    style: {
                      backgroundColor: SLIDE_COLORS.accent
                    },
                    children: [
                      "Schedule a Demo",
                      /* @__PURE__ */ jsx159(ArrowRightIcon5, { className: "w-5 h-5" })
                    ]
                  }
                ),
                /* @__PURE__ */ jsxs142("div", { className: "mt-10 flex items-center justify-center gap-8 text-sm font-sans text-tertiary", children: [
                  /* @__PURE__ */ jsx159("span", { children: "engage@aaim.com" }),
                  /* @__PURE__ */ jsx159("span", { children: "\u2022" }),
                  /* @__PURE__ */ jsx159("span", { children: "(212) 931-6500" }),
                  /* @__PURE__ */ jsx159("span", { children: "\u2022" }),
                  /* @__PURE__ */ jsx159("span", { children: "aaim.com" })
                ] })
              ] }) })
            ]
          }
        ) })
      ] })
    ] }),
    /* @__PURE__ */ jsxs142("section", { className: "mt-16 bg-surface p-8 rounded-sm shadow-sm", children: [
      /* @__PURE__ */ jsx159("h2", { className: "text-2xl font-sans font-bold text-primary mb-6 tracking-[-0.02em]", children: "Google Slides Implementation Guide" }),
      /* @__PURE__ */ jsxs142("div", { className: "grid md:grid-cols-2 gap-8", children: [
        /* @__PURE__ */ jsxs142("div", { children: [
          /* @__PURE__ */ jsx159("h3", { className: "text-lg font-sans font-semibold text-primary mb-4 tracking-[-0.02em]", children: "Typography Setup" }),
          /* @__PURE__ */ jsx159("ul", { className: "space-y-2", children: [
            "Headlines: Crimson Text (Google Fonts), Semibold, 44-56pt",
            "Subheads: Lato, Bold, 24-28pt",
            "Body text: Crimson Text, Regular, 18-20pt",
            "Labels: Lato, Semibold, 10-12pt, ALL CAPS, 15% letter spacing",
            "Data/metrics: Lato, Medium, tabular figures"
          ].map(
            (item, i) => /* @__PURE__ */ jsxs142("li", { className: "flex items-baseline gap-2", children: [
              /* @__PURE__ */ jsx159(CheckIcon15, { className: "w-4 h-4 text-growth flex-shrink-0 mt-1" }),
              /* @__PURE__ */ jsx159("span", { className: "text-sm font-serif text-secondary leading-[1.6]", children: item })
            ] }, i)
          ) })
        ] }),
        /* @__PURE__ */ jsxs142("div", { children: [
          /* @__PURE__ */ jsx159("h3", { className: "text-lg font-sans font-semibold text-primary mb-4 tracking-[-0.02em]", children: "Slide Master Setup" }),
          /* @__PURE__ */ jsx159("ul", { className: "space-y-2", children: [
            "Slide size: Widescreen 16:9 (default)",
            "Background: #FFFFFF or #F5F1E8 (warm cream)",
            "Top accent bar: 8px height, #D2001F",
            "Footer: Logo (left), page number (right)",
            "Margins: 80px left/right, 60px top/bottom"
          ].map(
            (item, i) => /* @__PURE__ */ jsxs142("li", { className: "flex items-baseline gap-2", children: [
              /* @__PURE__ */ jsx159(CheckIcon15, { className: "w-4 h-4 text-growth flex-shrink-0 mt-1" }),
              /* @__PURE__ */ jsx159("span", { className: "text-sm font-serif text-secondary leading-[1.6]", children: item })
            ] }, i)
          ) })
        ] })
      ] }),
      /* @__PURE__ */ jsxs142(
        "div",
        {
          className: "mt-8 p-6 rounded-sm",
          style: {
            backgroundColor: SLIDE_COLORS.bgPrimary
          },
          children: [
            /* @__PURE__ */ jsx159("h4", { className: "text-base font-sans font-semibold text-primary mb-3 tracking-[-0.02em]", children: "Brand Assets for Download" }),
            /* @__PURE__ */ jsx159("p", { className: "text-sm font-serif text-secondary leading-[1.6] mb-4", children: "Logo files, color swatches, and font files are available at:" }),
            /* @__PURE__ */ jsxs142("div", { className: "flex flex-wrap gap-4", children: [
              /* @__PURE__ */ jsx159("code", { className: "px-3 py-2 bg-surface rounded-sm text-sm font-mono text-primary border border-recessed", children: "https://cdn.aaim.app/logos/aaim-master-grey.svg" }),
              /* @__PURE__ */ jsx159("code", { className: "px-3 py-2 bg-surface rounded-sm text-sm font-mono text-primary border border-recessed", children: "https://cdn.aaim.app/logos/aaim-master-white.svg" })
            ] })
          ]
        }
      )
    ] })
  ] }) });
}

// src/templates/shared.ts
var FONT_SERIF = "'Source Serif 4', Georgia, 'Times New Roman', serif";
var FONT_SANS = "'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
var FONT_MONO = "'Source Code Pro', Monaco, Consolas, monospace";
var COLORS = {
  /** Primary text */
  text: "#000000",
  /** Secondary text (explanatory prose) */
  textSecondary: "#3A3330",
  /** Tertiary text (captions, sources, footnotes) */
  textTertiary: "#6B5D54",
  /** Disabled / placeholder text */
  textDisabled: "#9C9389",
  /** Accent red -- alerts and warnings only, never decorative */
  accent: "#D2001F",
  /** Warm canvas background */
  canvas: "#F5F1E8",
  /** White surface */
  surface: "#FFFFFF",
  /** Recessed background (subtle gray) */
  recessed: "#EBE3D5",
  /** Hairline rule color */
  rule: "#C4B299",
  /** Light rule color for table borders */
  ruleLight: "#D5D1C8",
  /** Positive / growth indicator */
  positive: "#1B7340",
  /** Negative / risk indicator */
  negative: "#B42318",
  /** Warning indicator */
  warning: "#B54708"
};
var DOCUMENT = {
  /** US Letter width */
  width: "8.5in",
  /** US Letter height */
  height: "11in",
  /** Page margin */
  margin: "1in",
  /** Maximum content width for screen preview */
  maxWidth: "8.5in"
};
var SLIDE = {
  /** Standard PPTX width: 13.333in (10in at 75% preview scale) */
  width: "13.333in",
  /** Standard PPTX height: 7.5in */
  height: "7.5in",
  /** Horizontal padding */
  paddingX: "80px",
  /** Vertical padding */
  paddingY: "60px",
  /** Preview max-width for Storybook/screen display */
  maxWidth: "960px",
  /** Accent bar height */
  accentBarHeight: "4px",
  /** Footer height */
  footerHeight: "48px",
  /** Native export width in pixels (1920px at 144dpi = 13.333in) */
  nativeWidth: 1920,
  /** Native export height in pixels (1080px at 144dpi = 7.5in) */
  nativeHeight: 1080
};
function formatDocumentDate(date) {
  const d = date ?? /* @__PURE__ */ new Date();
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric"
  });
}
function getConfidentialityLabel(level) {
  const labels = {
    public: "",
    internal: "INTERNAL USE ONLY",
    confidential: "CONFIDENTIAL",
    "board-only": "BOARD CONFIDENTIAL"
  };
  return labels[level];
}
function documentContainerStyle() {
  return {
    maxWidth: DOCUMENT.maxWidth,
    margin: "0 auto",
    padding: DOCUMENT.margin,
    background: COLORS.surface,
    fontFamily: FONT_SERIF,
    fontSize: "11pt",
    lineHeight: 1.5,
    color: COLORS.text
  };
}
function mastheadStyle() {
  return {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingBottom: "16px",
    marginBottom: "24px",
    borderBottom: `0.5pt solid ${COLORS.rule}`
  };
}
function titleStyle() {
  return {
    fontSize: "22pt",
    fontWeight: 600,
    lineHeight: 1.15,
    margin: 0,
    letterSpacing: "-0.02em",
    color: COLORS.text
  };
}
function sectionHeadingStyle() {
  return {
    fontSize: "11pt",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginTop: "32px",
    marginBottom: "12px",
    color: COLORS.text,
    fontFamily: FONT_SANS
  };
}
function subsectionHeadingStyle() {
  return {
    fontSize: "11pt",
    fontWeight: 600,
    marginTop: "24px",
    marginBottom: "8px",
    color: COLORS.text
  };
}
function paragraphStyle() {
  return {
    fontSize: "11pt",
    lineHeight: 1.5,
    margin: "0 0 12px 0",
    color: COLORS.text
  };
}
function metaTextStyle() {
  return {
    fontFamily: FONT_SANS,
    fontSize: "9pt",
    letterSpacing: "0.15em",
    color: COLORS.textTertiary
  };
}
function footerStyle() {
  return {
    marginTop: "48px",
    paddingTop: "16px",
    borderTop: `0.5pt solid ${COLORS.rule}`,
    textAlign: "center",
    fontFamily: FONT_SANS,
    fontSize: "8pt",
    color: COLORS.textTertiary
  };
}
function metricValueStyle() {
  return {
    fontSize: "22pt",
    fontWeight: 400,
    lineHeight: 1.1,
    color: COLORS.text,
    letterSpacing: "-0.02em",
    fontVariantNumeric: "tabular-nums"
  };
}
function metricLabelStyle() {
  return {
    fontSize: "10pt",
    color: COLORS.textSecondary,
    marginTop: "4px"
  };
}
function tableStyle() {
  return {
    width: "100%",
    fontSize: "10pt",
    borderCollapse: "collapse",
    marginTop: "16px",
    fontVariantNumeric: "tabular-nums"
  };
}
function thStyle(align = "left") {
  return {
    textAlign: align,
    padding: "8px 12px 8px 0",
    fontWeight: 600,
    fontFamily: FONT_SANS,
    fontSize: "9pt",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: COLORS.textSecondary,
    borderBottom: `0.5pt solid ${COLORS.rule}`
  };
}
function tdStyle(align = "left") {
  return {
    textAlign: align,
    padding: "8px 12px 8px 0",
    borderBottom: `0.25pt solid ${COLORS.ruleLight}`,
    verticalAlign: "top"
  };
}
function pageBreakStyle() {
  return {
    pageBreakBefore: "always",
    breakBefore: "page"
  };
}
function avoidBreakStyle() {
  return {
    pageBreakInside: "avoid",
    breakInside: "avoid"
  };
}
function slideContainerStyle(bg) {
  return {
    position: "relative",
    width: "100%",
    aspectRatio: "16/9",
    background: bg ?? COLORS.surface,
    fontFamily: FONT_SANS,
    fontSize: "16px",
    color: COLORS.text,
    display: "flex",
    flexDirection: "column"
  };
}
function slideContentStyle() {
  return {
    flex: 1,
    minHeight: 0,
    padding: `${SLIDE.paddingY} ${SLIDE.paddingX}`,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden"
  };
}
function slideAccentBarStyle(height) {
  return {
    width: "100%",
    height: height ?? SLIDE.accentBarHeight,
    background: COLORS.accent,
    flexShrink: 0
  };
}
function slideFooterStyle() {
  return {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: `0 ${SLIDE.paddingX}`,
    height: SLIDE.footerHeight,
    borderTop: `1px solid ${COLORS.ruleLight}`,
    flexShrink: 0
  };
}
function slideTitleStyle() {
  return {
    fontFamily: FONT_SERIF,
    fontSize: "32px",
    fontWeight: 600,
    lineHeight: 1.15,
    letterSpacing: "-0.04em",
    color: COLORS.text,
    margin: 0
  };
}
function slideBodyStyle() {
  return {
    fontFamily: FONT_SERIF,
    fontSize: "18px",
    lineHeight: 1.6,
    color: COLORS.textSecondary
  };
}
function slideLabelStyle() {
  return {
    fontFamily: FONT_SANS,
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.15em",
    color: COLORS.textTertiary
  };
}

// src/templates/BankersReport.tsx
import { Fragment as Fragment36, jsx as jsx160, jsxs as jsxs143 } from "react/jsx-runtime";
function BankersReportRoot({
  institutionName,
  date,
  confidentiality = "confidential",
  referenceNumber,
  preparedBy,
  children,
  logoUrl = "https://cdn.aaim.app/logos/aaim-master-grey.svg"
}) {
  const confLabel = getConfidentialityLabel(confidentiality);
  const displayDate = date ?? formatDocumentDate();
  const companyAddress = "Aaim, Inc. | 99 Wall Street, Suite 1610, New York, NY 10005";
  return /* @__PURE__ */ jsxs143("article", { style: documentContainerStyle(), children: [
    /* @__PURE__ */ jsx160("div", { className: "no-print", style: { position: "fixed", top: 20, right: 20, zIndex: 100 }, children: /* @__PURE__ */ jsx160(
      "button",
      {
        onClick: () => window.print(),
        style: {
          padding: "8px 16px",
          background: COLORS.textSecondary,
          color: COLORS.surface,
          border: "none",
          borderRadius: "4px",
          fontFamily: FONT_SANS,
          fontSize: "14px",
          cursor: "pointer"
        },
        children: "Print / Export PDF"
      }
    ) }),
    /* @__PURE__ */ jsxs143("header", { style: mastheadStyle(), children: [
      /* @__PURE__ */ jsxs143("div", { children: [
        /* @__PURE__ */ jsx160(
          "img",
          {
            src: logoUrl,
            alt: "Aaim",
            style: { height: "24px", marginBottom: "12px" }
          }
        ),
        /* @__PURE__ */ jsxs143("h1", { style: titleStyle(), children: [
          "Institution Analysis: ",
          institutionName
        ] }),
        referenceNumber && /* @__PURE__ */ jsxs143("div", { style: { ...metaTextStyle(), marginTop: "8px", letterSpacing: "0.08em" }, children: [
          "Ref: ",
          referenceNumber
        ] })
      ] }),
      /* @__PURE__ */ jsxs143("div", { style: { textAlign: "right" }, children: [
        confLabel && /* @__PURE__ */ jsx160("div", { style: { ...metaTextStyle(), marginBottom: "4px" }, children: confLabel }),
        /* @__PURE__ */ jsx160("div", { style: { ...metaTextStyle(), letterSpacing: "0" }, children: displayDate }),
        preparedBy && /* @__PURE__ */ jsxs143("div", { style: { ...metaTextStyle(), letterSpacing: "0", marginTop: "4px" }, children: [
          "Prepared by: ",
          preparedBy
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx160("main", { children }),
    /* @__PURE__ */ jsxs143("footer", { style: footerStyle(), children: [
      confLabel && `${confLabel}  \xB7  `,
      companyAddress
    ] })
  ] });
}
function CoverPage({
  institutionName,
  title,
  subtitle,
  date,
  confidentiality = "confidential",
  preparedBy,
  preparedFor,
  referenceNumber,
  logoUrl = "https://cdn.aaim.app/logos/aaim-master-grey.svg"
}) {
  const confLabel = getConfidentialityLabel(confidentiality);
  const displayDate = date ?? formatDocumentDate();
  return /* @__PURE__ */ jsxs143(
    "div",
    {
      style: {
        minHeight: "9in",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        pageBreakAfter: "always",
        breakAfter: "page"
      },
      children: [
        /* @__PURE__ */ jsx160(
          "img",
          {
            src: logoUrl,
            alt: "Aaim",
            style: { height: "32px", marginBottom: "48px", display: "block" }
          }
        ),
        confLabel && /* @__PURE__ */ jsx160(
          "div",
          {
            style: {
              fontFamily: FONT_SANS,
              fontSize: "10pt",
              letterSpacing: "0.15em",
              color: COLORS.textTertiary,
              marginBottom: "24px",
              textTransform: "uppercase"
            },
            children: confLabel
          }
        ),
        /* @__PURE__ */ jsx160(
          "h1",
          {
            style: {
              fontSize: "32pt",
              fontWeight: 600,
              lineHeight: 1.1,
              letterSpacing: "-0.03em",
              margin: "0 0 16px 0",
              color: COLORS.text,
              maxWidth: "5.5in"
            },
            children: title
          }
        ),
        subtitle && /* @__PURE__ */ jsx160(
          "p",
          {
            style: {
              fontSize: "14pt",
              fontWeight: 400,
              lineHeight: 1.3,
              color: COLORS.textSecondary,
              margin: "0 0 8px 0",
              maxWidth: "5in"
            },
            children: subtitle
          }
        ),
        /* @__PURE__ */ jsx160(
          "p",
          {
            style: {
              fontSize: "16pt",
              fontWeight: 600,
              lineHeight: 1.3,
              color: COLORS.text,
              margin: "24px 0 0 0"
            },
            children: institutionName
          }
        ),
        /* @__PURE__ */ jsx160("div", { style: { marginTop: "48px", borderTop: `0.5pt solid ${COLORS.rule}`, paddingTop: "16px" }, children: /* @__PURE__ */ jsxs143(
          "div",
          {
            style: {
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: "4px 24px",
              fontFamily: FONT_SANS,
              fontSize: "9pt",
              color: COLORS.textTertiary
            },
            children: [
              /* @__PURE__ */ jsx160("span", { children: "Date" }),
              /* @__PURE__ */ jsx160("span", { style: { color: COLORS.textSecondary }, children: displayDate }),
              preparedFor && /* @__PURE__ */ jsxs143(Fragment36, { children: [
                /* @__PURE__ */ jsx160("span", { children: "Prepared for" }),
                /* @__PURE__ */ jsx160("span", { style: { color: COLORS.textSecondary }, children: preparedFor })
              ] }),
              preparedBy && /* @__PURE__ */ jsxs143(Fragment36, { children: [
                /* @__PURE__ */ jsx160("span", { children: "Prepared by" }),
                /* @__PURE__ */ jsx160("span", { style: { color: COLORS.textSecondary }, children: preparedBy })
              ] }),
              referenceNumber && /* @__PURE__ */ jsxs143(Fragment36, { children: [
                /* @__PURE__ */ jsx160("span", { children: "Reference" }),
                /* @__PURE__ */ jsx160("span", { style: { color: COLORS.textSecondary }, children: referenceNumber })
              ] })
            ]
          }
        ) })
      ]
    }
  );
}
function ExecutiveSummary({
  narrative,
  metrics,
  keyFindings,
  recommendation
}) {
  return /* @__PURE__ */ jsxs143("section", { style: avoidBreakStyle(), children: [
    /* @__PURE__ */ jsx160("h2", { style: sectionHeadingStyle(), children: "Executive Summary" }),
    /* @__PURE__ */ jsx160("p", { style: paragraphStyle(), children: narrative }),
    metrics && metrics.length > 0 && /* @__PURE__ */ jsx160("div", { style: { display: "flex", flexWrap: "wrap", gap: "32px", margin: "24px 0" }, children: metrics.map((metric, idx) => /* @__PURE__ */ jsxs143("div", { style: { minWidth: "120px" }, children: [
      /* @__PURE__ */ jsx160("div", { style: metricValueStyle(), children: metric.value }),
      /* @__PURE__ */ jsx160("div", { style: metricLabelStyle(), children: metric.label }),
      metric.context && /* @__PURE__ */ jsx160("div", { style: { fontSize: "9pt", color: COLORS.textTertiary, fontStyle: "italic" }, children: metric.context })
    ] }, idx)) }),
    keyFindings && keyFindings.length > 0 && /* @__PURE__ */ jsxs143("div", { style: { margin: "16px 0" }, children: [
      /* @__PURE__ */ jsx160("h3", { style: subsectionHeadingStyle(), children: "Key Findings" }),
      /* @__PURE__ */ jsx160("ol", { style: { margin: "8px 0", paddingLeft: "20px" }, children: keyFindings.map((finding, idx) => /* @__PURE__ */ jsx160(
        "li",
        {
          style: { ...paragraphStyle(), marginBottom: "8px", paddingLeft: "4px" },
          children: finding
        },
        idx
      )) })
    ] }),
    recommendation && /* @__PURE__ */ jsx160(
      "div",
      {
        style: {
          marginTop: "16px",
          paddingTop: "12px",
          borderTop: `0.25pt solid ${COLORS.ruleLight}`
        },
        children: /* @__PURE__ */ jsxs143("p", { style: { ...paragraphStyle(), fontWeight: 600 }, children: [
          "Recommendation: ",
          recommendation
        ] })
      }
    )
  ] });
}
function PortfolioAnalysis({
  narrative,
  composition,
  projections,
  children
}) {
  return /* @__PURE__ */ jsxs143("section", { children: [
    /* @__PURE__ */ jsx160("h2", { style: sectionHeadingStyle(), children: "Portfolio Analysis" }),
    narrative && /* @__PURE__ */ jsx160("p", { style: paragraphStyle(), children: narrative }),
    composition && composition.length > 0 && /* @__PURE__ */ jsxs143("div", { style: { ...avoidBreakStyle(), marginTop: "16px" }, children: [
      /* @__PURE__ */ jsx160("h3", { style: subsectionHeadingStyle(), children: "Asset Composition" }),
      /* @__PURE__ */ jsxs143("table", { style: tableStyle(), children: [
        /* @__PURE__ */ jsx160("thead", { children: /* @__PURE__ */ jsxs143("tr", { children: [
          /* @__PURE__ */ jsx160("th", { style: thStyle("left"), children: "Asset Class" }),
          /* @__PURE__ */ jsx160("th", { style: thStyle("right"), children: "Allocation" }),
          /* @__PURE__ */ jsx160("th", { style: thStyle("right"), children: "Volume" }),
          /* @__PURE__ */ jsx160("th", { style: thStyle("right"), children: "Yield" }),
          composition.some((c) => c.riskRating) && /* @__PURE__ */ jsx160("th", { style: thStyle("left"), children: "Risk" })
        ] }) }),
        /* @__PURE__ */ jsx160("tbody", { children: composition.map((row, idx) => /* @__PURE__ */ jsxs143("tr", { children: [
          /* @__PURE__ */ jsx160("td", { style: { ...tdStyle("left"), fontWeight: 500 }, children: row.assetClass }),
          /* @__PURE__ */ jsx160("td", { style: tdStyle("right"), children: row.allocation }),
          /* @__PURE__ */ jsx160("td", { style: tdStyle("right"), children: row.volume }),
          /* @__PURE__ */ jsx160("td", { style: tdStyle("right"), children: row.yield }),
          composition.some((c) => c.riskRating) && /* @__PURE__ */ jsx160("td", { style: tdStyle("left"), children: row.riskRating ?? "--" })
        ] }, idx)) })
      ] })
    ] }),
    projections && projections.length > 0 && /* @__PURE__ */ jsxs143("div", { style: { ...avoidBreakStyle(), marginTop: "24px" }, children: [
      /* @__PURE__ */ jsx160("h3", { style: subsectionHeadingStyle(), children: "Year-by-Year Projections" }),
      /* @__PURE__ */ jsxs143("table", { style: tableStyle(), children: [
        /* @__PURE__ */ jsx160("thead", { children: /* @__PURE__ */ jsxs143("tr", { children: [
          /* @__PURE__ */ jsx160("th", { style: thStyle("left"), children: "Year" }),
          /* @__PURE__ */ jsx160("th", { style: thStyle("right"), children: "Origination Volume" }),
          /* @__PURE__ */ jsx160("th", { style: thStyle("right"), children: "Active Loans" }),
          /* @__PURE__ */ jsx160("th", { style: thStyle("right"), children: "Net Interest Income" }),
          /* @__PURE__ */ jsx160("th", { style: thStyle("right"), children: "Default Rate" })
        ] }) }),
        /* @__PURE__ */ jsx160("tbody", { children: projections.map((row, idx) => /* @__PURE__ */ jsxs143("tr", { children: [
          /* @__PURE__ */ jsxs143("td", { style: { ...tdStyle("left"), fontWeight: 500 }, children: [
            "Year ",
            row.year
          ] }),
          /* @__PURE__ */ jsx160("td", { style: tdStyle("right"), children: row.volume }),
          /* @__PURE__ */ jsx160("td", { style: tdStyle("right"), children: row.activeLoans }),
          /* @__PURE__ */ jsx160("td", { style: tdStyle("right"), children: row.nii }),
          /* @__PURE__ */ jsx160("td", { style: tdStyle("right"), children: row.defaultRate })
        ] }, idx)) })
      ] })
    ] }),
    children
  ] });
}
function RiskAssessment({
  narrative,
  factors,
  sensitivities,
  children
}) {
  const severityColors = {
    low: COLORS.positive,
    moderate: COLORS.textSecondary,
    elevated: COLORS.warning,
    high: COLORS.negative
  };
  return /* @__PURE__ */ jsxs143("section", { children: [
    /* @__PURE__ */ jsx160("h2", { style: sectionHeadingStyle(), children: "Risk Assessment" }),
    narrative && /* @__PURE__ */ jsx160("p", { style: paragraphStyle(), children: narrative }),
    factors && factors.length > 0 && /* @__PURE__ */ jsx160("div", { style: { marginTop: "16px" }, children: factors.map((factor, idx) => /* @__PURE__ */ jsxs143(
      "div",
      {
        style: {
          ...avoidBreakStyle(),
          marginBottom: "16px",
          paddingBottom: "12px",
          borderBottom: idx < factors.length - 1 ? `0.25pt solid ${COLORS.ruleLight}` : "none"
        },
        children: [
          /* @__PURE__ */ jsxs143("div", { style: { display: "flex", alignItems: "baseline", gap: "12px" }, children: [
            /* @__PURE__ */ jsx160(
              "h3",
              {
                style: {
                  ...subsectionHeadingStyle(),
                  marginTop: 0,
                  marginBottom: "4px",
                  flex: 1
                },
                children: factor.name
              }
            ),
            /* @__PURE__ */ jsx160(
              "span",
              {
                style: {
                  fontFamily: FONT_SANS,
                  fontSize: "8pt",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: severityColors[factor.severity] ?? COLORS.textTertiary
                },
                children: factor.severity
              }
            )
          ] }),
          /* @__PURE__ */ jsx160("p", { style: { ...paragraphStyle(), marginBottom: "4px" }, children: factor.description }),
          factor.mitigation && /* @__PURE__ */ jsxs143(
            "p",
            {
              style: {
                ...paragraphStyle(),
                fontStyle: "italic",
                color: COLORS.textSecondary,
                marginBottom: 0
              },
              children: [
                "Mitigation: ",
                factor.mitigation
              ]
            }
          )
        ]
      },
      idx
    )) }),
    sensitivities && sensitivities.length > 0 && /* @__PURE__ */ jsxs143("div", { style: { ...avoidBreakStyle(), marginTop: "24px" }, children: [
      /* @__PURE__ */ jsx160("h3", { style: subsectionHeadingStyle(), children: "Sensitivity Analysis" }),
      /* @__PURE__ */ jsx160("p", { style: { ...paragraphStyle(), fontSize: "10pt", color: COLORS.textSecondary }, children: "Impact of parameter variations on projected outcomes. Ranges represent +/- one standard deviation from baseline assumptions." }),
      /* @__PURE__ */ jsxs143("table", { style: tableStyle(), children: [
        /* @__PURE__ */ jsx160("thead", { children: /* @__PURE__ */ jsxs143("tr", { children: [
          /* @__PURE__ */ jsx160("th", { style: thStyle("left"), children: "Parameter" }),
          /* @__PURE__ */ jsx160("th", { style: thStyle("right"), children: "Upside" }),
          /* @__PURE__ */ jsx160("th", { style: thStyle("right"), children: "Downside" })
        ] }) }),
        /* @__PURE__ */ jsx160("tbody", { children: sensitivities.map((row, idx) => /* @__PURE__ */ jsxs143("tr", { children: [
          /* @__PURE__ */ jsx160("td", { style: { ...tdStyle("left"), fontWeight: 500 }, children: row.parameter }),
          /* @__PURE__ */ jsx160("td", { style: { ...tdStyle("right"), color: COLORS.positive }, children: row.upside }),
          /* @__PURE__ */ jsx160("td", { style: { ...tdStyle("right"), color: COLORS.negative }, children: row.downside })
        ] }, idx)) })
      ] })
    ] }),
    children
  ] });
}
function Recommendations({
  narrative,
  items,
  closing,
  children
}) {
  const priorityLabels = {
    immediate: { label: "Immediate", color: COLORS.negative },
    "short-term": { label: "Short-term", color: COLORS.warning },
    "medium-term": { label: "Medium-term", color: COLORS.textSecondary }
  };
  return /* @__PURE__ */ jsxs143("section", { children: [
    /* @__PURE__ */ jsx160("h2", { style: sectionHeadingStyle(), children: "Recommendations" }),
    narrative && /* @__PURE__ */ jsx160("p", { style: paragraphStyle(), children: narrative }),
    items && items.length > 0 && /* @__PURE__ */ jsx160("div", { style: { marginTop: "16px" }, children: items.map((item, idx) => /* @__PURE__ */ jsxs143(
      "div",
      {
        style: {
          ...avoidBreakStyle(),
          marginBottom: "16px",
          paddingLeft: "16px",
          borderLeft: `2pt solid ${COLORS.rule}`
        },
        children: [
          /* @__PURE__ */ jsxs143("div", { style: { display: "flex", alignItems: "baseline", gap: "12px" }, children: [
            /* @__PURE__ */ jsxs143("h3", { style: { ...subsectionHeadingStyle(), marginTop: 0, flex: 1 }, children: [
              idx + 1,
              ". ",
              item.title
            ] }),
            item.priority && /* @__PURE__ */ jsx160(
              "span",
              {
                style: {
                  fontFamily: FONT_SANS,
                  fontSize: "8pt",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: priorityLabels[item.priority]?.color ?? COLORS.textTertiary
                },
                children: priorityLabels[item.priority]?.label ?? item.priority
              }
            )
          ] }),
          /* @__PURE__ */ jsx160("p", { style: paragraphStyle(), children: item.description }),
          item.impact && /* @__PURE__ */ jsxs143(
            "p",
            {
              style: {
                fontFamily: FONT_SANS,
                fontSize: "9pt",
                color: COLORS.textTertiary,
                margin: 0
              },
              children: [
                "Expected impact: ",
                item.impact
              ]
            }
          )
        ]
      },
      idx
    )) }),
    closing && /* @__PURE__ */ jsx160("p", { style: { ...paragraphStyle(), fontStyle: "italic", marginTop: "16px" }, children: closing }),
    children
  ] });
}
function ReportDataTable({ headers, rows, alignments, caption }) {
  return /* @__PURE__ */ jsxs143("div", { style: { ...avoidBreakStyle(), marginTop: "16px" }, children: [
    /* @__PURE__ */ jsxs143("table", { style: tableStyle(), children: [
      /* @__PURE__ */ jsx160("thead", { children: /* @__PURE__ */ jsx160("tr", { children: headers.map((header, idx) => /* @__PURE__ */ jsx160("th", { style: thStyle(alignments?.[idx] ?? (idx === 0 ? "left" : "right")), children: header }, idx)) }) }),
      /* @__PURE__ */ jsx160("tbody", { children: rows.map((row, rowIdx) => /* @__PURE__ */ jsx160("tr", { children: row.map((cell, cellIdx) => /* @__PURE__ */ jsx160(
        "td",
        {
          style: {
            ...tdStyle(alignments?.[cellIdx] ?? (cellIdx === 0 ? "left" : "right")),
            fontWeight: cellIdx === 0 ? 500 : 400
          },
          children: cell
        },
        cellIdx
      )) }, rowIdx)) })
    ] }),
    caption && /* @__PURE__ */ jsx160(
      "div",
      {
        style: {
          fontFamily: FONT_SANS,
          fontSize: "8pt",
          color: COLORS.textTertiary,
          marginTop: "8px"
        },
        children: caption
      }
    )
  ] });
}
function MetricsRow({ metrics }) {
  return /* @__PURE__ */ jsx160("div", { style: { display: "flex", flexWrap: "wrap", gap: "32px", margin: "24px 0" }, children: metrics.map((metric, idx) => /* @__PURE__ */ jsxs143("div", { style: { minWidth: "120px" }, children: [
    /* @__PURE__ */ jsx160("div", { style: metricValueStyle(), children: metric.value }),
    /* @__PURE__ */ jsx160("div", { style: metricLabelStyle(), children: metric.label }),
    metric.change && /* @__PURE__ */ jsx160(
      "div",
      {
        style: {
          fontSize: "9pt",
          color: metric.trend === "up" ? COLORS.positive : metric.trend === "down" ? COLORS.negative : COLORS.textTertiary,
          fontFamily: FONT_MONO
        },
        children: metric.change
      }
    ),
    metric.context && /* @__PURE__ */ jsx160("div", { style: { fontSize: "9pt", color: COLORS.textTertiary, fontStyle: "italic" }, children: metric.context })
  ] }, idx)) });
}
function Disclaimer({ text, additionalNotices }) {
  const defaultText = "This analysis is based on the scenario parameters and assumptions provided. Projections are forward-looking estimates and do not constitute financial advice. Actual results may vary based on market conditions, regulatory changes, and operational execution. Past performance does not guarantee future results.";
  return /* @__PURE__ */ jsxs143(
    "div",
    {
      style: {
        marginTop: "48px",
        paddingTop: "16px",
        borderTop: `0.5pt solid ${COLORS.rule}`
      },
      children: [
        /* @__PURE__ */ jsx160(
          "p",
          {
            style: {
              fontSize: "9pt",
              color: COLORS.textTertiary,
              lineHeight: 1.4,
              fontFamily: FONT_SANS,
              margin: "0 0 8px 0"
            },
            children: text ?? defaultText
          }
        ),
        additionalNotices?.map((notice, idx) => /* @__PURE__ */ jsx160(
          "p",
          {
            style: {
              fontSize: "9pt",
              color: COLORS.textTertiary,
              lineHeight: 1.4,
              fontFamily: FONT_SANS,
              margin: "0 0 4px 0"
            },
            children: notice
          },
          idx
        ))
      ]
    }
  );
}
function ReportPageBreak() {
  return /* @__PURE__ */ jsx160("div", { style: pageBreakStyle() });
}
var BankersReport = Object.assign(BankersReportRoot, {
  CoverPage,
  ExecutiveSummary,
  PortfolioAnalysis,
  RiskAssessment,
  Recommendations,
  PageBreak: ReportPageBreak,
  DataTable: ReportDataTable,
  MetricsRow,
  Disclaimer
});

// src/templates/StrategicAnalysis.tsx
import { jsx as jsx161, jsxs as jsxs144 } from "react/jsx-runtime";
function StrategicAnalysisRoot({
  institutionName,
  reportPeriod,
  date,
  confidentiality = "board-only",
  children,
  logoUrl = "https://cdn.aaim.app/logos/aaim-master-grey.svg"
}) {
  const confLabel = getConfidentialityLabel(confidentiality);
  const displayDate = date ?? formatDocumentDate();
  return /* @__PURE__ */ jsxs144("article", { style: documentContainerStyle(), children: [
    /* @__PURE__ */ jsx161("div", { className: "no-print", style: { position: "fixed", top: 20, right: 20, zIndex: 100 }, children: /* @__PURE__ */ jsx161(
      "button",
      {
        onClick: () => window.print(),
        style: {
          padding: "8px 16px",
          background: COLORS.textSecondary,
          color: COLORS.surface,
          border: "none",
          borderRadius: "4px",
          fontFamily: FONT_SANS,
          fontSize: "14px",
          cursor: "pointer"
        },
        children: "Print / Export PDF"
      }
    ) }),
    /* @__PURE__ */ jsxs144("header", { style: mastheadStyle(), children: [
      /* @__PURE__ */ jsxs144("div", { children: [
        /* @__PURE__ */ jsx161(
          "img",
          {
            src: logoUrl,
            alt: "Aaim",
            style: { height: "24px", marginBottom: "12px" }
          }
        ),
        /* @__PURE__ */ jsxs144("h1", { style: titleStyle(), children: [
          "Strategic Analysis: ",
          institutionName
        ] }),
        reportPeriod && /* @__PURE__ */ jsx161(
          "p",
          {
            style: {
              fontSize: "11pt",
              fontStyle: "italic",
              color: COLORS.textSecondary,
              margin: "8px 0 0 0"
            },
            children: reportPeriod
          }
        )
      ] }),
      /* @__PURE__ */ jsxs144("div", { style: { textAlign: "right" }, children: [
        confLabel && /* @__PURE__ */ jsx161("div", { style: { ...metaTextStyle(), marginBottom: "4px" }, children: confLabel }),
        /* @__PURE__ */ jsx161("div", { style: { ...metaTextStyle(), letterSpacing: "0" }, children: displayDate })
      ] })
    ] }),
    /* @__PURE__ */ jsx161("main", { children }),
    /* @__PURE__ */ jsxs144("footer", { style: footerStyle(), children: [
      confLabel && `${confLabel}  \xB7  `,
      "Aaim, Inc. | 99 Wall Street, Suite 1610, New York, NY 10005"
    ] })
  ] });
}
function MarketPositioning({
  narrative,
  tam,
  sam,
  marketShare,
  metrics,
  segments,
  children
}) {
  return /* @__PURE__ */ jsxs144("section", { children: [
    /* @__PURE__ */ jsx161("h2", { style: sectionHeadingStyle(), children: "Market Positioning" }),
    /* @__PURE__ */ jsx161("p", { style: paragraphStyle(), children: narrative }),
    (tam || sam || marketShare) && /* @__PURE__ */ jsxs144("div", { style: { display: "flex", flexWrap: "wrap", gap: "32px", margin: "24px 0" }, children: [
      tam && /* @__PURE__ */ jsxs144("div", { style: { minWidth: "120px" }, children: [
        /* @__PURE__ */ jsx161("div", { style: metricValueStyle(), children: tam }),
        /* @__PURE__ */ jsx161("div", { style: metricLabelStyle(), children: "Total Addressable Market" })
      ] }),
      sam && /* @__PURE__ */ jsxs144("div", { style: { minWidth: "120px" }, children: [
        /* @__PURE__ */ jsx161("div", { style: metricValueStyle(), children: sam }),
        /* @__PURE__ */ jsx161("div", { style: metricLabelStyle(), children: "Serviceable Market" })
      ] }),
      marketShare && /* @__PURE__ */ jsxs144("div", { style: { minWidth: "120px" }, children: [
        /* @__PURE__ */ jsx161("div", { style: metricValueStyle(), children: marketShare }),
        /* @__PURE__ */ jsx161("div", { style: metricLabelStyle(), children: "Current Share" })
      ] })
    ] }),
    metrics && metrics.length > 0 && /* @__PURE__ */ jsx161("div", { style: { display: "flex", flexWrap: "wrap", gap: "32px", margin: "24px 0" }, children: metrics.map((metric, idx) => /* @__PURE__ */ jsxs144("div", { style: { minWidth: "120px" }, children: [
      /* @__PURE__ */ jsx161("div", { style: metricValueStyle(), children: metric.value }),
      /* @__PURE__ */ jsx161("div", { style: metricLabelStyle(), children: metric.label }),
      metric.context && /* @__PURE__ */ jsx161("div", { style: { fontSize: "9pt", color: COLORS.textTertiary, fontStyle: "italic" }, children: metric.context })
    ] }, idx)) }),
    segments && segments.length > 0 && /* @__PURE__ */ jsxs144("div", { style: { ...avoidBreakStyle(), marginTop: "16px" }, children: [
      /* @__PURE__ */ jsx161("h3", { style: subsectionHeadingStyle(), children: "Market Segments" }),
      /* @__PURE__ */ jsxs144("table", { style: tableStyle(), children: [
        /* @__PURE__ */ jsx161("thead", { children: /* @__PURE__ */ jsxs144("tr", { children: [
          /* @__PURE__ */ jsx161("th", { style: thStyle("left"), children: "Segment" }),
          /* @__PURE__ */ jsx161("th", { style: thStyle("right"), children: "Size" }),
          /* @__PURE__ */ jsx161("th", { style: thStyle("right"), children: "Growth" }),
          /* @__PURE__ */ jsx161("th", { style: thStyle("right"), children: "Penetration" }),
          /* @__PURE__ */ jsx161("th", { style: thStyle("left"), children: "Opportunity" })
        ] }) }),
        /* @__PURE__ */ jsx161("tbody", { children: segments.map((segment, idx) => /* @__PURE__ */ jsxs144("tr", { children: [
          /* @__PURE__ */ jsx161("td", { style: { ...tdStyle("left"), fontWeight: 500 }, children: segment.name }),
          /* @__PURE__ */ jsx161("td", { style: tdStyle("right"), children: segment.size }),
          /* @__PURE__ */ jsx161("td", { style: tdStyle("right"), children: segment.growth }),
          /* @__PURE__ */ jsx161("td", { style: tdStyle("right"), children: segment.penetration }),
          /* @__PURE__ */ jsx161("td", { style: { ...tdStyle("left"), fontSize: "9pt", color: COLORS.textSecondary }, children: segment.opportunity })
        ] }, idx)) })
      ] })
    ] }),
    children
  ] });
}
function CompetitiveLandscape({
  narrative,
  competitors,
  advantages,
  children
}) {
  const threatColors = {
    low: COLORS.positive,
    moderate: COLORS.warning,
    high: COLORS.negative
  };
  return /* @__PURE__ */ jsxs144("section", { children: [
    /* @__PURE__ */ jsx161("h2", { style: sectionHeadingStyle(), children: "Competitive Landscape" }),
    narrative && /* @__PURE__ */ jsx161("p", { style: paragraphStyle(), children: narrative }),
    competitors && competitors.length > 0 && /* @__PURE__ */ jsx161("div", { style: { ...avoidBreakStyle(), marginTop: "16px" }, children: /* @__PURE__ */ jsxs144("table", { style: tableStyle(), children: [
      /* @__PURE__ */ jsx161("thead", { children: /* @__PURE__ */ jsxs144("tr", { children: [
        /* @__PURE__ */ jsx161("th", { style: thStyle("left"), children: "Competitor" }),
        /* @__PURE__ */ jsx161("th", { style: thStyle("right"), children: "Share" }),
        /* @__PURE__ */ jsx161("th", { style: thStyle("left"), children: "Strengths" }),
        /* @__PURE__ */ jsx161("th", { style: thStyle("left"), children: "Weaknesses" }),
        /* @__PURE__ */ jsx161("th", { style: thStyle("left"), children: "Threat" })
      ] }) }),
      /* @__PURE__ */ jsx161("tbody", { children: competitors.map((comp, idx) => /* @__PURE__ */ jsxs144("tr", { children: [
        /* @__PURE__ */ jsx161("td", { style: { ...tdStyle("left"), fontWeight: 500 }, children: comp.name }),
        /* @__PURE__ */ jsx161("td", { style: tdStyle("right"), children: comp.marketShare }),
        /* @__PURE__ */ jsx161("td", { style: { ...tdStyle("left"), fontSize: "9pt" }, children: comp.strengths }),
        /* @__PURE__ */ jsx161("td", { style: { ...tdStyle("left"), fontSize: "9pt" }, children: comp.weaknesses }),
        /* @__PURE__ */ jsx161(
          "td",
          {
            style: {
              ...tdStyle("left"),
              fontFamily: FONT_SANS,
              fontSize: "8pt",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              color: threatColors[comp.threat] ?? COLORS.textTertiary
            },
            children: comp.threat
          }
        )
      ] }, idx)) })
    ] }) }),
    advantages && advantages.length > 0 && /* @__PURE__ */ jsxs144("div", { style: { marginTop: "24px" }, children: [
      /* @__PURE__ */ jsx161("h3", { style: subsectionHeadingStyle(), children: "Competitive Advantages" }),
      /* @__PURE__ */ jsx161("ol", { style: { margin: "8px 0", paddingLeft: "20px" }, children: advantages.map((advantage, idx) => /* @__PURE__ */ jsx161("li", { style: { ...paragraphStyle(), marginBottom: "8px" }, children: advantage }, idx)) })
    ] }),
    children
  ] });
}
function GrowthProjections({
  narrative,
  projections,
  drivers,
  children
}) {
  return /* @__PURE__ */ jsxs144("section", { children: [
    /* @__PURE__ */ jsx161("h2", { style: sectionHeadingStyle(), children: "Growth Projections" }),
    narrative && /* @__PURE__ */ jsx161("p", { style: paragraphStyle(), children: narrative }),
    projections && projections.length > 0 && /* @__PURE__ */ jsx161("div", { style: { ...avoidBreakStyle(), marginTop: "16px" }, children: /* @__PURE__ */ jsxs144("table", { style: tableStyle(), children: [
      /* @__PURE__ */ jsx161("thead", { children: /* @__PURE__ */ jsxs144("tr", { children: [
        /* @__PURE__ */ jsx161("th", { style: thStyle("left"), children: "Period" }),
        /* @__PURE__ */ jsx161("th", { style: thStyle("right"), children: "Revenue" }),
        /* @__PURE__ */ jsx161("th", { style: thStyle("right"), children: "Growth" }),
        /* @__PURE__ */ jsx161("th", { style: thStyle("right"), children: "Margin" }),
        projections.some((p) => p.customers) && /* @__PURE__ */ jsx161("th", { style: thStyle("right"), children: "Customers" })
      ] }) }),
      /* @__PURE__ */ jsx161("tbody", { children: projections.map((row, idx) => /* @__PURE__ */ jsxs144("tr", { children: [
        /* @__PURE__ */ jsx161("td", { style: { ...tdStyle("left"), fontWeight: 500 }, children: row.period }),
        /* @__PURE__ */ jsx161("td", { style: tdStyle("right"), children: row.revenue }),
        /* @__PURE__ */ jsx161(
          "td",
          {
            style: {
              ...tdStyle("right"),
              color: row.growth.startsWith("-") ? COLORS.negative : COLORS.positive
            },
            children: row.growth
          }
        ),
        /* @__PURE__ */ jsx161("td", { style: tdStyle("right"), children: row.margin }),
        projections.some((p) => p.customers) && /* @__PURE__ */ jsx161("td", { style: tdStyle("right"), children: row.customers ?? "--" })
      ] }, idx)) })
    ] }) }),
    drivers && drivers.length > 0 && /* @__PURE__ */ jsxs144("div", { style: { marginTop: "24px" }, children: [
      /* @__PURE__ */ jsx161("h3", { style: subsectionHeadingStyle(), children: "Growth Drivers" }),
      drivers.map((driver, idx) => /* @__PURE__ */ jsxs144(
        "div",
        {
          style: {
            ...avoidBreakStyle(),
            marginBottom: "12px",
            paddingLeft: "16px",
            borderLeft: `2pt solid ${COLORS.rule}`
          },
          children: [
            /* @__PURE__ */ jsxs144("div", { style: { display: "flex", alignItems: "baseline", gap: "12px" }, children: [
              /* @__PURE__ */ jsx161("span", { style: { ...subsectionHeadingStyle(), marginTop: 0, marginBottom: "2px" }, children: driver.name }),
              /* @__PURE__ */ jsx161(
                "span",
                {
                  style: {
                    fontFamily: FONT_MONO,
                    fontSize: "10pt",
                    color: COLORS.positive,
                    fontWeight: 600
                  },
                  children: driver.contribution
                }
              )
            ] }),
            /* @__PURE__ */ jsx161("p", { style: { ...paragraphStyle(), marginBottom: 0, fontSize: "10pt", color: COLORS.textSecondary }, children: driver.description })
          ]
        },
        idx
      ))
    ] }),
    children
  ] });
}
function ComplianceStatus({
  narrative,
  items,
  upcoming,
  children
}) {
  const statusStyles = {
    compliant: { label: "Compliant", color: COLORS.positive },
    "in-progress": { label: "In Progress", color: COLORS.warning },
    "at-risk": { label: "At Risk", color: COLORS.negative },
    "non-compliant": { label: "Non-compliant", color: COLORS.accent }
  };
  return /* @__PURE__ */ jsxs144("section", { children: [
    /* @__PURE__ */ jsx161("h2", { style: sectionHeadingStyle(), children: "Compliance Status" }),
    narrative && /* @__PURE__ */ jsx161("p", { style: paragraphStyle(), children: narrative }),
    items && items.length > 0 && /* @__PURE__ */ jsx161("div", { style: { ...avoidBreakStyle(), marginTop: "16px" }, children: /* @__PURE__ */ jsxs144("table", { style: tableStyle(), children: [
      /* @__PURE__ */ jsx161("thead", { children: /* @__PURE__ */ jsxs144("tr", { children: [
        /* @__PURE__ */ jsx161("th", { style: thStyle("left"), children: "Regulation" }),
        /* @__PURE__ */ jsx161("th", { style: thStyle("left"), children: "Status" }),
        items.some((i) => i.dueDate) && /* @__PURE__ */ jsx161("th", { style: thStyle("left"), children: "Due Date" }),
        items.some((i) => i.notes) && /* @__PURE__ */ jsx161("th", { style: thStyle("left"), children: "Notes" })
      ] }) }),
      /* @__PURE__ */ jsx161("tbody", { children: items.map((item, idx) => {
        const statusConfig = statusStyles[item.status] ?? {
          label: item.status,
          color: COLORS.textTertiary
        };
        return /* @__PURE__ */ jsxs144("tr", { children: [
          /* @__PURE__ */ jsx161("td", { style: { ...tdStyle("left"), fontWeight: 500 }, children: item.regulation }),
          /* @__PURE__ */ jsx161(
            "td",
            {
              style: {
                ...tdStyle("left"),
                fontFamily: FONT_SANS,
                fontSize: "8pt",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                color: statusConfig.color
              },
              children: statusConfig.label
            }
          ),
          items.some((i) => i.dueDate) && /* @__PURE__ */ jsx161("td", { style: { ...tdStyle("left"), fontFamily: FONT_SANS, fontSize: "9pt" }, children: item.dueDate ?? "--" }),
          items.some((i) => i.notes) && /* @__PURE__ */ jsx161("td", { style: { ...tdStyle("left"), fontSize: "9pt", color: COLORS.textSecondary }, children: item.notes ?? "" })
        ] }, idx);
      }) })
    ] }) }),
    upcoming && upcoming.length > 0 && /* @__PURE__ */ jsxs144("div", { style: { ...avoidBreakStyle(), marginTop: "24px" }, children: [
      /* @__PURE__ */ jsx161("h3", { style: subsectionHeadingStyle(), children: "Upcoming Regulatory Changes" }),
      /* @__PURE__ */ jsxs144("table", { style: tableStyle(), children: [
        /* @__PURE__ */ jsx161("thead", { children: /* @__PURE__ */ jsxs144("tr", { children: [
          /* @__PURE__ */ jsx161("th", { style: thStyle("left"), children: "Regulation" }),
          /* @__PURE__ */ jsx161("th", { style: thStyle("left"), children: "Effective" }),
          /* @__PURE__ */ jsx161("th", { style: thStyle("left"), children: "Impact" }),
          /* @__PURE__ */ jsx161("th", { style: thStyle("left"), children: "Readiness" })
        ] }) }),
        /* @__PURE__ */ jsx161("tbody", { children: upcoming.map((item, idx) => /* @__PURE__ */ jsxs144("tr", { children: [
          /* @__PURE__ */ jsx161("td", { style: { ...tdStyle("left"), fontWeight: 500 }, children: item.regulation }),
          /* @__PURE__ */ jsx161("td", { style: { ...tdStyle("left"), fontFamily: FONT_SANS, fontSize: "9pt" }, children: item.effectiveDate }),
          /* @__PURE__ */ jsx161("td", { style: { ...tdStyle("left"), fontSize: "9pt", color: COLORS.textSecondary }, children: item.impact }),
          /* @__PURE__ */ jsx161("td", { style: { ...tdStyle("left"), fontSize: "9pt" }, children: item.readiness })
        ] }, idx)) })
      ] })
    ] }),
    children
  ] });
}
function StrategicImperatives({
  narrative,
  initiatives,
  children
}) {
  return /* @__PURE__ */ jsxs144("section", { children: [
    /* @__PURE__ */ jsx161("h2", { style: sectionHeadingStyle(), children: "Strategic Imperatives" }),
    narrative && /* @__PURE__ */ jsx161("p", { style: paragraphStyle(), children: narrative }),
    initiatives && initiatives.length > 0 && /* @__PURE__ */ jsx161("div", { style: { marginTop: "16px" }, children: initiatives.map((initiative, idx) => /* @__PURE__ */ jsxs144(
      "div",
      {
        style: {
          ...avoidBreakStyle(),
          marginBottom: "20px",
          paddingBottom: "16px",
          borderBottom: idx < initiatives.length - 1 ? `0.25pt solid ${COLORS.ruleLight}` : "none"
        },
        children: [
          /* @__PURE__ */ jsxs144("h3", { style: { ...subsectionHeadingStyle(), marginTop: 0 }, children: [
            idx + 1,
            ". ",
            initiative.title
          ] }),
          /* @__PURE__ */ jsx161("p", { style: paragraphStyle(), children: initiative.objective }),
          /* @__PURE__ */ jsxs144(
            "div",
            {
              style: {
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                gap: "8px 24px",
                fontFamily: FONT_SANS,
                fontSize: "9pt",
                color: COLORS.textTertiary,
                marginTop: "8px"
              },
              children: [
                /* @__PURE__ */ jsxs144("div", { children: [
                  /* @__PURE__ */ jsx161("span", { style: { display: "block" }, children: "Timeline" }),
                  /* @__PURE__ */ jsx161("span", { style: { color: COLORS.textSecondary, fontWeight: 500 }, children: initiative.timeline })
                ] }),
                initiative.investment && /* @__PURE__ */ jsxs144("div", { children: [
                  /* @__PURE__ */ jsx161("span", { style: { display: "block" }, children: "Investment" }),
                  /* @__PURE__ */ jsx161("span", { style: { color: COLORS.textSecondary, fontWeight: 500 }, children: initiative.investment })
                ] }),
                initiative.expectedReturn && /* @__PURE__ */ jsxs144("div", { children: [
                  /* @__PURE__ */ jsx161("span", { style: { display: "block" }, children: "Expected Return" }),
                  /* @__PURE__ */ jsx161("span", { style: { color: COLORS.textSecondary, fontWeight: 500 }, children: initiative.expectedReturn })
                ] }),
                initiative.owner && /* @__PURE__ */ jsxs144("div", { children: [
                  /* @__PURE__ */ jsx161("span", { style: { display: "block" }, children: "Owner" }),
                  /* @__PURE__ */ jsx161("span", { style: { color: COLORS.textSecondary, fontWeight: 500 }, children: initiative.owner })
                ] })
              ]
            }
          )
        ]
      },
      idx
    )) }),
    children
  ] });
}
function Appendix({ title, children }) {
  return /* @__PURE__ */ jsxs144("section", { children: [
    /* @__PURE__ */ jsx161("div", { style: pageBreakStyle() }),
    /* @__PURE__ */ jsxs144("h2", { style: sectionHeadingStyle(), children: [
      "Appendix: ",
      title
    ] }),
    children
  ] });
}
function SAPageBreak() {
  return /* @__PURE__ */ jsx161("div", { style: pageBreakStyle() });
}
function SADataTable({
  headers,
  rows,
  alignments,
  caption
}) {
  return /* @__PURE__ */ jsxs144("div", { style: { ...avoidBreakStyle(), marginTop: "16px" }, children: [
    /* @__PURE__ */ jsxs144("table", { style: tableStyle(), children: [
      /* @__PURE__ */ jsx161("thead", { children: /* @__PURE__ */ jsx161("tr", { children: headers.map((header, idx) => /* @__PURE__ */ jsx161("th", { style: thStyle(alignments?.[idx] ?? (idx === 0 ? "left" : "right")), children: header }, idx)) }) }),
      /* @__PURE__ */ jsx161("tbody", { children: rows.map((row, rowIdx) => /* @__PURE__ */ jsx161("tr", { children: row.map((cell, cellIdx) => /* @__PURE__ */ jsx161(
        "td",
        {
          style: {
            ...tdStyle(alignments?.[cellIdx] ?? (cellIdx === 0 ? "left" : "right")),
            fontWeight: cellIdx === 0 ? 500 : 400
          },
          children: cell
        },
        cellIdx
      )) }, rowIdx)) })
    ] }),
    caption && /* @__PURE__ */ jsx161("div", { style: { fontFamily: FONT_SANS, fontSize: "8pt", color: COLORS.textTertiary, marginTop: "8px" }, children: caption })
  ] });
}
function SAMetricsRow({ metrics }) {
  return /* @__PURE__ */ jsx161("div", { style: { display: "flex", flexWrap: "wrap", gap: "32px", margin: "24px 0" }, children: metrics.map((metric, idx) => /* @__PURE__ */ jsxs144("div", { style: { minWidth: "120px" }, children: [
    /* @__PURE__ */ jsx161("div", { style: metricValueStyle(), children: metric.value }),
    /* @__PURE__ */ jsx161("div", { style: metricLabelStyle(), children: metric.label }),
    metric.context && /* @__PURE__ */ jsx161("div", { style: { fontSize: "9pt", color: COLORS.textTertiary, fontStyle: "italic" }, children: metric.context })
  ] }, idx)) });
}
var StrategicAnalysis = Object.assign(StrategicAnalysisRoot, {
  MarketPositioning,
  CompetitiveLandscape,
  GrowthProjections,
  ComplianceStatus,
  StrategicImperatives,
  Appendix,
  PageBreak: SAPageBreak,
  DataTable: SADataTable,
  MetricsRow: SAMetricsRow
});

// src/templates/RegulatoryReport.tsx
import { jsx as jsx162, jsxs as jsxs145 } from "react/jsx-runtime";
function RegulatoryReportRoot({
  institutionName,
  charterNumber,
  regulator,
  reportType,
  reportingPeriod,
  date,
  confidentiality = "confidential",
  children,
  logoUrl = "https://cdn.aaim.app/logos/aaim-master-grey.svg"
}) {
  const confLabel = getConfidentialityLabel(confidentiality);
  const displayDate = date ?? formatDocumentDate();
  return /* @__PURE__ */ jsxs145("article", { style: documentContainerStyle(), children: [
    /* @__PURE__ */ jsx162("div", { className: "no-print", style: { position: "fixed", top: 20, right: 20, zIndex: 100 }, children: /* @__PURE__ */ jsx162(
      "button",
      {
        onClick: () => window.print(),
        style: {
          padding: "8px 16px",
          background: COLORS.textSecondary,
          color: COLORS.surface,
          border: "none",
          borderRadius: "4px",
          fontFamily: FONT_SANS,
          fontSize: "14px",
          cursor: "pointer"
        },
        children: "Print / Export PDF"
      }
    ) }),
    /* @__PURE__ */ jsx162(
      "header",
      {
        style: {
          paddingBottom: "16px",
          marginBottom: "24px",
          borderBottom: `1pt solid ${COLORS.text}`
        },
        children: /* @__PURE__ */ jsxs145("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" }, children: [
          /* @__PURE__ */ jsxs145("div", { children: [
            /* @__PURE__ */ jsx162(
              "img",
              {
                src: logoUrl,
                alt: "Aaim",
                style: { height: "24px", marginBottom: "12px" }
              }
            ),
            /* @__PURE__ */ jsx162("h1", { style: { ...titleStyle(), fontSize: "18pt" }, children: reportType }),
            /* @__PURE__ */ jsx162(
              "p",
              {
                style: {
                  fontFamily: FONT_SERIF,
                  fontSize: "12pt",
                  fontWeight: 600,
                  color: COLORS.text,
                  margin: "8px 0 0 0"
                },
                children: institutionName
              }
            ),
            charterNumber && /* @__PURE__ */ jsxs145("div", { style: { ...metaTextStyle(), marginTop: "4px", letterSpacing: "0.08em" }, children: [
              "Charter No. ",
              charterNumber
            ] })
          ] }),
          /* @__PURE__ */ jsxs145("div", { style: { textAlign: "right" }, children: [
            confLabel && /* @__PURE__ */ jsx162("div", { style: { ...metaTextStyle(), marginBottom: "4px" }, children: confLabel }),
            /* @__PURE__ */ jsxs145("div", { style: { ...metaTextStyle(), letterSpacing: "0" }, children: [
              "Reporting Period: ",
              reportingPeriod
            ] }),
            /* @__PURE__ */ jsxs145("div", { style: { ...metaTextStyle(), letterSpacing: "0", marginTop: "4px" }, children: [
              "Prepared: ",
              displayDate
            ] }),
            regulator && /* @__PURE__ */ jsx162("div", { style: { ...metaTextStyle(), letterSpacing: "0.08em", marginTop: "4px" }, children: regulator })
          ] })
        ] })
      }
    ),
    /* @__PURE__ */ jsx162("main", { children }),
    /* @__PURE__ */ jsxs145("footer", { style: footerStyle(), children: [
      confLabel && `${confLabel}  \xB7  `,
      institutionName,
      " ",
      charterNumber ? `(Charter: ${charterNumber})` : "",
      " \xB7  ",
      reportType,
      " \xB7 ",
      reportingPeriod
    ] })
  ] });
}
function CallReportSummary({
  narrative,
  totalAssets,
  totalLoans,
  totalDeposits,
  netWorth,
  netIncome,
  additionalMetrics,
  balanceSheet
}) {
  return /* @__PURE__ */ jsxs145("section", { children: [
    /* @__PURE__ */ jsx162("h2", { style: sectionHeadingStyle(), children: "Call Report Summary" }),
    narrative && /* @__PURE__ */ jsx162("p", { style: paragraphStyle(), children: narrative }),
    /* @__PURE__ */ jsxs145("div", { style: { display: "flex", flexWrap: "wrap", gap: "32px", margin: "24px 0" }, children: [
      /* @__PURE__ */ jsxs145("div", { style: { minWidth: "120px" }, children: [
        /* @__PURE__ */ jsx162("div", { style: metricValueStyle(), children: totalAssets }),
        /* @__PURE__ */ jsx162("div", { style: metricLabelStyle(), children: "Total Assets" })
      ] }),
      /* @__PURE__ */ jsxs145("div", { style: { minWidth: "120px" }, children: [
        /* @__PURE__ */ jsx162("div", { style: metricValueStyle(), children: totalLoans }),
        /* @__PURE__ */ jsx162("div", { style: metricLabelStyle(), children: "Total Loans" })
      ] }),
      /* @__PURE__ */ jsxs145("div", { style: { minWidth: "120px" }, children: [
        /* @__PURE__ */ jsx162("div", { style: metricValueStyle(), children: totalDeposits }),
        /* @__PURE__ */ jsx162("div", { style: metricLabelStyle(), children: "Total Deposits" })
      ] }),
      /* @__PURE__ */ jsxs145("div", { style: { minWidth: "120px" }, children: [
        /* @__PURE__ */ jsx162("div", { style: metricValueStyle(), children: netWorth }),
        /* @__PURE__ */ jsx162("div", { style: metricLabelStyle(), children: "Net Worth" })
      ] }),
      netIncome && /* @__PURE__ */ jsxs145("div", { style: { minWidth: "120px" }, children: [
        /* @__PURE__ */ jsx162("div", { style: metricValueStyle(), children: netIncome }),
        /* @__PURE__ */ jsx162("div", { style: metricLabelStyle(), children: "Net Income" })
      ] })
    ] }),
    additionalMetrics && additionalMetrics.length > 0 && /* @__PURE__ */ jsx162("div", { style: { display: "flex", flexWrap: "wrap", gap: "24px", margin: "16px 0" }, children: additionalMetrics.map((metric, idx) => /* @__PURE__ */ jsxs145("div", { style: { minWidth: "100px" }, children: [
      /* @__PURE__ */ jsx162("div", { style: { ...metricValueStyle(), fontSize: "16pt" }, children: metric.value }),
      /* @__PURE__ */ jsx162("div", { style: metricLabelStyle(), children: metric.label }),
      metric.context && /* @__PURE__ */ jsx162("div", { style: { fontSize: "9pt", color: COLORS.textTertiary, fontStyle: "italic" }, children: metric.context })
    ] }, idx)) }),
    balanceSheet && balanceSheet.length > 0 && /* @__PURE__ */ jsxs145("div", { style: { ...avoidBreakStyle(), marginTop: "24px" }, children: [
      /* @__PURE__ */ jsx162("h3", { style: subsectionHeadingStyle(), children: "Balance Sheet Comparison" }),
      /* @__PURE__ */ jsxs145("table", { style: tableStyle(), children: [
        /* @__PURE__ */ jsx162("thead", { children: /* @__PURE__ */ jsxs145("tr", { children: [
          /* @__PURE__ */ jsx162("th", { style: thStyle("left"), children: "Line Item" }),
          /* @__PURE__ */ jsx162("th", { style: thStyle("right"), children: "Current" }),
          /* @__PURE__ */ jsx162("th", { style: thStyle("right"), children: "Prior Period" }),
          /* @__PURE__ */ jsx162("th", { style: thStyle("right"), children: "Change" })
        ] }) }),
        /* @__PURE__ */ jsx162("tbody", { children: balanceSheet.map((row, idx) => /* @__PURE__ */ jsxs145("tr", { children: [
          /* @__PURE__ */ jsx162("td", { style: { ...tdStyle("left"), fontWeight: 500 }, children: row.item }),
          /* @__PURE__ */ jsx162("td", { style: { ...tdStyle("right"), fontFamily: FONT_MONO }, children: row.current }),
          /* @__PURE__ */ jsx162("td", { style: { ...tdStyle("right"), fontFamily: FONT_MONO }, children: row.prior }),
          /* @__PURE__ */ jsx162(
            "td",
            {
              style: {
                ...tdStyle("right"),
                fontFamily: FONT_MONO,
                color: row.change.startsWith("-") ? COLORS.negative : row.change.startsWith("+") ? COLORS.positive : COLORS.text
              },
              children: row.change
            }
          )
        ] }, idx)) })
      ] })
    ] })
  ] });
}
function TrendAnalysis({
  narrative,
  periods,
  metricNames,
  observations
}) {
  return /* @__PURE__ */ jsxs145("section", { children: [
    /* @__PURE__ */ jsx162("h2", { style: sectionHeadingStyle(), children: "Trend Analysis" }),
    narrative && /* @__PURE__ */ jsx162("p", { style: paragraphStyle(), children: narrative }),
    periods.length > 0 && /* @__PURE__ */ jsx162("div", { style: { ...avoidBreakStyle(), marginTop: "16px" }, children: /* @__PURE__ */ jsxs145("table", { style: tableStyle(), children: [
      /* @__PURE__ */ jsx162("thead", { children: /* @__PURE__ */ jsxs145("tr", { children: [
        /* @__PURE__ */ jsx162("th", { style: thStyle("left"), children: "Period" }),
        metricNames.map((name, idx) => /* @__PURE__ */ jsx162("th", { style: thStyle("right"), children: name }, idx))
      ] }) }),
      /* @__PURE__ */ jsx162("tbody", { children: periods.map((period, idx) => /* @__PURE__ */ jsxs145("tr", { children: [
        /* @__PURE__ */ jsx162("td", { style: { ...tdStyle("left"), fontWeight: 500 }, children: period.period }),
        metricNames.map((name, midx) => /* @__PURE__ */ jsx162("td", { style: { ...tdStyle("right"), fontFamily: FONT_MONO }, children: period.metrics[name] ?? "--" }, midx))
      ] }, idx)) })
    ] }) }),
    observations && observations.length > 0 && /* @__PURE__ */ jsxs145("div", { style: { marginTop: "16px" }, children: [
      /* @__PURE__ */ jsx162("h3", { style: subsectionHeadingStyle(), children: "Observations" }),
      /* @__PURE__ */ jsx162("ol", { style: { margin: "8px 0", paddingLeft: "20px" }, children: observations.map((obs, idx) => /* @__PURE__ */ jsx162("li", { style: { ...paragraphStyle(), marginBottom: "8px" }, children: obs }, idx)) })
    ] })
  ] });
}
function PeerComparison({
  narrative,
  peerGroupDescription,
  metrics,
  source
}) {
  return /* @__PURE__ */ jsxs145("section", { children: [
    /* @__PURE__ */ jsx162("h2", { style: sectionHeadingStyle(), children: "Peer Comparison" }),
    narrative && /* @__PURE__ */ jsx162("p", { style: paragraphStyle(), children: narrative }),
    peerGroupDescription && /* @__PURE__ */ jsxs145("p", { style: { ...paragraphStyle(), fontStyle: "italic", color: COLORS.textSecondary, fontSize: "10pt" }, children: [
      "Peer Group: ",
      peerGroupDescription
    ] }),
    metrics.length > 0 && /* @__PURE__ */ jsx162("div", { style: { ...avoidBreakStyle(), marginTop: "16px" }, children: /* @__PURE__ */ jsxs145("table", { style: tableStyle(), children: [
      /* @__PURE__ */ jsx162("thead", { children: /* @__PURE__ */ jsxs145("tr", { children: [
        /* @__PURE__ */ jsx162("th", { style: thStyle("left"), children: "Metric" }),
        /* @__PURE__ */ jsx162("th", { style: thStyle("right"), children: "Institution" }),
        /* @__PURE__ */ jsx162("th", { style: thStyle("right"), children: "Peer Median" }),
        /* @__PURE__ */ jsx162("th", { style: thStyle("right"), children: "Peer Top Quartile" }),
        metrics.some((m) => m.percentile) && /* @__PURE__ */ jsx162("th", { style: thStyle("right"), children: "Percentile" })
      ] }) }),
      /* @__PURE__ */ jsx162("tbody", { children: metrics.map((row, idx) => /* @__PURE__ */ jsxs145("tr", { children: [
        /* @__PURE__ */ jsx162("td", { style: { ...tdStyle("left"), fontWeight: 500 }, children: row.metric }),
        /* @__PURE__ */ jsx162("td", { style: { ...tdStyle("right"), fontFamily: FONT_MONO, fontWeight: 600 }, children: row.institution }),
        /* @__PURE__ */ jsx162("td", { style: { ...tdStyle("right"), fontFamily: FONT_MONO }, children: row.peerMedian }),
        /* @__PURE__ */ jsx162("td", { style: { ...tdStyle("right"), fontFamily: FONT_MONO }, children: row.peerTop }),
        metrics.some((m) => m.percentile) && /* @__PURE__ */ jsx162("td", { style: { ...tdStyle("right"), fontFamily: FONT_MONO }, children: row.percentile ?? "--" })
      ] }, idx)) })
    ] }) }),
    source && /* @__PURE__ */ jsxs145(
      "div",
      {
        style: {
          fontFamily: FONT_SANS,
          fontSize: "8pt",
          color: COLORS.textTertiary,
          marginTop: "8px"
        },
        children: [
          "Source: ",
          source
        ]
      }
    )
  ] });
}
function RiskMetrics({
  narrative,
  camelRatings,
  creditRisk,
  interestRateRisk,
  children
}) {
  const trendLabels = {
    improving: { text: "Improving", color: COLORS.positive },
    stable: { text: "Stable", color: COLORS.textSecondary },
    deteriorating: { text: "Deteriorating", color: COLORS.negative }
  };
  const statusLabels = {
    "within-limits": { text: "Within Limits", color: COLORS.positive },
    watch: { text: "Watch", color: COLORS.warning },
    exceeds: { text: "Exceeds", color: COLORS.negative }
  };
  return /* @__PURE__ */ jsxs145("section", { children: [
    /* @__PURE__ */ jsx162("h2", { style: sectionHeadingStyle(), children: "Risk Metrics" }),
    narrative && /* @__PURE__ */ jsx162("p", { style: paragraphStyle(), children: narrative }),
    camelRatings && camelRatings.length > 0 && /* @__PURE__ */ jsxs145("div", { style: { ...avoidBreakStyle(), marginTop: "16px" }, children: [
      /* @__PURE__ */ jsx162("h3", { style: subsectionHeadingStyle(), children: "Composite Ratings" }),
      /* @__PURE__ */ jsxs145("table", { style: tableStyle(), children: [
        /* @__PURE__ */ jsx162("thead", { children: /* @__PURE__ */ jsxs145("tr", { children: [
          /* @__PURE__ */ jsx162("th", { style: thStyle("left"), children: "Component" }),
          /* @__PURE__ */ jsx162("th", { style: thStyle("left"), children: "Rating" }),
          /* @__PURE__ */ jsx162("th", { style: thStyle("left"), children: "Trend" }),
          camelRatings.some((r) => r.notes) && /* @__PURE__ */ jsx162("th", { style: thStyle("left"), children: "Notes" })
        ] }) }),
        /* @__PURE__ */ jsx162("tbody", { children: camelRatings.map((row, idx) => {
          const trend = trendLabels[row.trend] ?? { text: row.trend, color: COLORS.textTertiary };
          return /* @__PURE__ */ jsxs145("tr", { children: [
            /* @__PURE__ */ jsx162("td", { style: { ...tdStyle("left"), fontWeight: 500 }, children: row.component }),
            /* @__PURE__ */ jsx162("td", { style: { ...tdStyle("left"), fontFamily: FONT_MONO, fontWeight: 600 }, children: row.rating }),
            /* @__PURE__ */ jsx162(
              "td",
              {
                style: {
                  ...tdStyle("left"),
                  fontFamily: FONT_SANS,
                  fontSize: "8pt",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: trend.color
                },
                children: trend.text
              }
            ),
            camelRatings.some((r) => r.notes) && /* @__PURE__ */ jsx162("td", { style: { ...tdStyle("left"), fontSize: "9pt", color: COLORS.textSecondary }, children: row.notes ?? "" })
          ] }, idx);
        }) })
      ] })
    ] }),
    creditRisk && creditRisk.length > 0 && /* @__PURE__ */ jsxs145("div", { style: { ...avoidBreakStyle(), marginTop: "24px" }, children: [
      /* @__PURE__ */ jsx162("h3", { style: subsectionHeadingStyle(), children: "Credit Risk Metrics" }),
      /* @__PURE__ */ jsxs145("table", { style: tableStyle(), children: [
        /* @__PURE__ */ jsx162("thead", { children: /* @__PURE__ */ jsxs145("tr", { children: [
          /* @__PURE__ */ jsx162("th", { style: thStyle("left"), children: "Metric" }),
          /* @__PURE__ */ jsx162("th", { style: thStyle("right"), children: "Actual" }),
          /* @__PURE__ */ jsx162("th", { style: thStyle("right"), children: "Threshold" }),
          /* @__PURE__ */ jsx162("th", { style: thStyle("left"), children: "Status" })
        ] }) }),
        /* @__PURE__ */ jsx162("tbody", { children: creditRisk.map((row, idx) => {
          const status = statusLabels[row.status] ?? { text: row.status, color: COLORS.textTertiary };
          return /* @__PURE__ */ jsxs145("tr", { children: [
            /* @__PURE__ */ jsx162("td", { style: { ...tdStyle("left"), fontWeight: 500 }, children: row.metric }),
            /* @__PURE__ */ jsx162("td", { style: { ...tdStyle("right"), fontFamily: FONT_MONO, fontWeight: 600 }, children: row.value }),
            /* @__PURE__ */ jsx162("td", { style: { ...tdStyle("right"), fontFamily: FONT_MONO }, children: row.threshold }),
            /* @__PURE__ */ jsx162(
              "td",
              {
                style: {
                  ...tdStyle("left"),
                  fontFamily: FONT_SANS,
                  fontSize: "8pt",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: status.color
                },
                children: status.text
              }
            )
          ] }, idx);
        }) })
      ] })
    ] }),
    interestRateRisk && interestRateRisk.length > 0 && /* @__PURE__ */ jsxs145("div", { style: { ...avoidBreakStyle(), marginTop: "24px" }, children: [
      /* @__PURE__ */ jsx162("h3", { style: subsectionHeadingStyle(), children: "Interest Rate Risk" }),
      /* @__PURE__ */ jsxs145("table", { style: tableStyle(), children: [
        /* @__PURE__ */ jsx162("thead", { children: /* @__PURE__ */ jsxs145("tr", { children: [
          /* @__PURE__ */ jsx162("th", { style: thStyle("left"), children: "Scenario" }),
          /* @__PURE__ */ jsx162("th", { style: thStyle("right"), children: "NII Impact" }),
          /* @__PURE__ */ jsx162("th", { style: thStyle("right"), children: "EVE Impact" })
        ] }) }),
        /* @__PURE__ */ jsx162("tbody", { children: interestRateRisk.map((row, idx) => /* @__PURE__ */ jsxs145("tr", { children: [
          /* @__PURE__ */ jsx162("td", { style: { ...tdStyle("left"), fontWeight: 500 }, children: row.scenario }),
          /* @__PURE__ */ jsx162(
            "td",
            {
              style: {
                ...tdStyle("right"),
                fontFamily: FONT_MONO,
                color: row.niiImpact.startsWith("-") ? COLORS.negative : COLORS.positive
              },
              children: row.niiImpact
            }
          ),
          /* @__PURE__ */ jsx162(
            "td",
            {
              style: {
                ...tdStyle("right"),
                fontFamily: FONT_MONO,
                color: row.eveImpact.startsWith("-") ? COLORS.negative : COLORS.positive
              },
              children: row.eveImpact
            }
          )
        ] }, idx)) })
      ] })
    ] }),
    children
  ] });
}
function CapitalAdequacy({
  narrative,
  ratios,
  pcaClassification
}) {
  const statusLabels = {
    "well-capitalized": { text: "Well Capitalized", color: COLORS.positive },
    "adequately-capitalized": { text: "Adequately Capitalized", color: COLORS.warning },
    undercapitalized: { text: "Undercapitalized", color: COLORS.negative }
  };
  return /* @__PURE__ */ jsxs145("section", { children: [
    /* @__PURE__ */ jsx162("h2", { style: sectionHeadingStyle(), children: "Capital Adequacy" }),
    narrative && /* @__PURE__ */ jsx162("p", { style: paragraphStyle(), children: narrative }),
    pcaClassification && /* @__PURE__ */ jsxs145("p", { style: { ...paragraphStyle(), fontWeight: 600 }, children: [
      "PCA Classification:",
      " ",
      /* @__PURE__ */ jsx162(
        "span",
        {
          style: {
            color: statusLabels[pcaClassification.toLowerCase().replace(/\s/g, "-")]?.color ?? COLORS.text
          },
          children: pcaClassification
        }
      )
    ] }),
    ratios.length > 0 && /* @__PURE__ */ jsx162("div", { style: { ...avoidBreakStyle(), marginTop: "16px" }, children: /* @__PURE__ */ jsxs145("table", { style: tableStyle(), children: [
      /* @__PURE__ */ jsx162("thead", { children: /* @__PURE__ */ jsxs145("tr", { children: [
        /* @__PURE__ */ jsx162("th", { style: thStyle("left"), children: "Ratio" }),
        /* @__PURE__ */ jsx162("th", { style: thStyle("right"), children: "Actual" }),
        /* @__PURE__ */ jsx162("th", { style: thStyle("right"), children: "Required" }),
        /* @__PURE__ */ jsx162("th", { style: thStyle("right"), children: "Well Capitalized" }),
        /* @__PURE__ */ jsx162("th", { style: thStyle("left"), children: "Status" })
      ] }) }),
      /* @__PURE__ */ jsx162("tbody", { children: ratios.map((row, idx) => {
        const status = statusLabels[row.status] ?? { text: row.status, color: COLORS.textTertiary };
        return /* @__PURE__ */ jsxs145("tr", { children: [
          /* @__PURE__ */ jsx162("td", { style: { ...tdStyle("left"), fontWeight: 500 }, children: row.ratio }),
          /* @__PURE__ */ jsx162("td", { style: { ...tdStyle("right"), fontFamily: FONT_MONO, fontWeight: 600 }, children: row.actual }),
          /* @__PURE__ */ jsx162("td", { style: { ...tdStyle("right"), fontFamily: FONT_MONO }, children: row.required }),
          /* @__PURE__ */ jsx162("td", { style: { ...tdStyle("right"), fontFamily: FONT_MONO }, children: row.wellCapitalized }),
          /* @__PURE__ */ jsx162(
            "td",
            {
              style: {
                ...tdStyle("left"),
                fontFamily: FONT_SANS,
                fontSize: "8pt",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                color: status.color
              },
              children: status.text
            }
          )
        ] }, idx);
      }) })
    ] }) })
  ] });
}
function RegulatoryFootnote({ number, children }) {
  return /* @__PURE__ */ jsxs145(
    "div",
    {
      style: {
        fontFamily: FONT_SANS,
        fontSize: "8pt",
        color: COLORS.textTertiary,
        lineHeight: 1.4,
        margin: "4px 0",
        paddingLeft: "16px",
        textIndent: "-16px"
      },
      children: [
        /* @__PURE__ */ jsx162("sup", { style: { fontSize: "7pt", marginRight: "4px" }, children: number }),
        children
      ]
    }
  );
}
function Certification({
  officerName,
  officerTitle,
  date,
  institutionName
}) {
  const displayDate = date ?? formatDocumentDate();
  return /* @__PURE__ */ jsxs145(
    "section",
    {
      style: {
        ...avoidBreakStyle(),
        marginTop: "48px",
        paddingTop: "24px",
        borderTop: `1pt solid ${COLORS.text}`
      },
      children: [
        /* @__PURE__ */ jsx162("h2", { style: sectionHeadingStyle(), children: "Officer Certification" }),
        /* @__PURE__ */ jsxs145("p", { style: paragraphStyle(), children: [
          "I, the undersigned officer of ",
          institutionName,
          ", hereby certify that the information contained in this report is true and correct to the best of my knowledge and belief, and that it has been prepared in conformance with applicable regulatory instructions."
        ] }),
        /* @__PURE__ */ jsxs145("div", { style: { marginTop: "32px", display: "flex", gap: "48px" }, children: [
          /* @__PURE__ */ jsxs145("div", { children: [
            /* @__PURE__ */ jsx162(
              "div",
              {
                style: {
                  borderBottom: `1pt solid ${COLORS.text}`,
                  width: "240px",
                  height: "40px",
                  marginBottom: "4px"
                }
              }
            ),
            /* @__PURE__ */ jsx162("div", { style: { fontFamily: FONT_SANS, fontSize: "9pt", color: COLORS.textTertiary }, children: "Signature" }),
            officerName && /* @__PURE__ */ jsx162("div", { style: { fontFamily: FONT_SERIF, fontSize: "10pt", marginTop: "8px" }, children: officerName }),
            officerTitle && /* @__PURE__ */ jsx162("div", { style: { fontFamily: FONT_SANS, fontSize: "9pt", color: COLORS.textSecondary }, children: officerTitle })
          ] }),
          /* @__PURE__ */ jsxs145("div", { children: [
            /* @__PURE__ */ jsx162(
              "div",
              {
                style: {
                  borderBottom: `1pt solid ${COLORS.text}`,
                  width: "160px",
                  height: "40px",
                  marginBottom: "4px"
                },
                children: /* @__PURE__ */ jsx162("span", { style: { fontFamily: FONT_SERIF, fontSize: "10pt", lineHeight: "40px" }, children: displayDate })
              }
            ),
            /* @__PURE__ */ jsx162("div", { style: { fontFamily: FONT_SANS, fontSize: "9pt", color: COLORS.textTertiary }, children: "Date" })
          ] })
        ] })
      ]
    }
  );
}
function RegPageBreak() {
  return /* @__PURE__ */ jsx162("div", { style: pageBreakStyle() });
}
function RegDataTable({
  headers,
  rows,
  alignments,
  caption
}) {
  return /* @__PURE__ */ jsxs145("div", { style: { ...avoidBreakStyle(), marginTop: "16px" }, children: [
    /* @__PURE__ */ jsxs145("table", { style: tableStyle(), children: [
      /* @__PURE__ */ jsx162("thead", { children: /* @__PURE__ */ jsx162("tr", { children: headers.map((header, idx) => /* @__PURE__ */ jsx162("th", { style: thStyle(alignments?.[idx] ?? (idx === 0 ? "left" : "right")), children: header }, idx)) }) }),
      /* @__PURE__ */ jsx162("tbody", { children: rows.map((row, rowIdx) => /* @__PURE__ */ jsx162("tr", { children: row.map((cell, cellIdx) => /* @__PURE__ */ jsx162(
        "td",
        {
          style: {
            ...tdStyle(alignments?.[cellIdx] ?? (cellIdx === 0 ? "left" : "right")),
            fontWeight: cellIdx === 0 ? 500 : 400,
            fontFamily: cellIdx > 0 ? FONT_MONO : FONT_SERIF
          },
          children: cell
        },
        cellIdx
      )) }, rowIdx)) })
    ] }),
    caption && /* @__PURE__ */ jsx162("div", { style: { fontFamily: FONT_SANS, fontSize: "8pt", color: COLORS.textTertiary, marginTop: "8px" }, children: caption })
  ] });
}
function RegMetricsRow({ metrics }) {
  return /* @__PURE__ */ jsx162("div", { style: { display: "flex", flexWrap: "wrap", gap: "32px", margin: "24px 0" }, children: metrics.map((metric, idx) => /* @__PURE__ */ jsxs145("div", { style: { minWidth: "120px" }, children: [
    /* @__PURE__ */ jsx162("div", { style: metricValueStyle(), children: metric.value }),
    /* @__PURE__ */ jsx162("div", { style: metricLabelStyle(), children: metric.label }),
    metric.context && /* @__PURE__ */ jsx162("div", { style: { fontSize: "9pt", color: COLORS.textTertiary, fontStyle: "italic" }, children: metric.context })
  ] }, idx)) });
}
var RegulatoryReport = Object.assign(RegulatoryReportRoot, {
  CallReportSummary,
  TrendAnalysis,
  PeerComparison,
  RiskMetrics,
  CapitalAdequacy,
  RegulatoryFootnote,
  Certification,
  PageBreak: RegPageBreak,
  DataTable: RegDataTable,
  MetricsRow: RegMetricsRow
});

// src/templates/LegalContract.tsx
import { jsx as jsx163, jsxs as jsxs146 } from "react/jsx-runtime";
var LEGAL_BODY = {
  fontFamily: FONT_SERIF,
  fontSize: "11pt",
  fontWeight: 400,
  lineHeight: 1.6,
  color: COLORS.text,
  margin: "0 0 12px 0",
  textAlign: "justify"
};
var LEGAL_HEADING = {
  fontFamily: FONT_SERIF,
  fontSize: "12pt",
  fontWeight: 600,
  lineHeight: 1.3,
  color: COLORS.text,
  margin: "32px 0 12px 0",
  textTransform: "uppercase",
  letterSpacing: "0.04em"
};
var LEGAL_META = {
  fontFamily: FONT_SANS,
  fontSize: "9pt",
  letterSpacing: "0.12em",
  color: COLORS.textTertiary,
  textTransform: "uppercase"
};
function LegalContractRoot({
  title,
  date,
  confidentiality = "confidential",
  partyA,
  partyB,
  referenceNumber,
  children
}) {
  const confLabel = getConfidentialityLabel(confidentiality);
  const displayDate = date ?? formatDocumentDate();
  return /* @__PURE__ */ jsxs146(
    "article",
    {
      style: {
        ...documentContainerStyle(),
        lineHeight: 1.6
        // Legal documents use wider leading
      },
      children: [
        /* @__PURE__ */ jsx163("div", { className: "no-print", style: { position: "fixed", top: 20, right: 20, zIndex: 100 }, children: /* @__PURE__ */ jsx163(
          "button",
          {
            onClick: () => window.print(),
            style: {
              padding: "8px 16px",
              background: COLORS.textSecondary,
              color: COLORS.surface,
              border: "none",
              borderRadius: "4px",
              fontFamily: FONT_SANS,
              fontSize: "14px",
              cursor: "pointer"
            },
            children: "Print / Export PDF"
          }
        ) }),
        /* @__PURE__ */ jsxs146(
          "header",
          {
            style: {
              textAlign: "center",
              paddingBottom: "24px",
              marginBottom: "32px",
              borderBottom: `1pt solid ${COLORS.text}`
            },
            children: [
              confLabel && /* @__PURE__ */ jsx163("div", { style: { ...LEGAL_META, marginBottom: "16px" }, children: confLabel }),
              /* @__PURE__ */ jsx163(
                "h1",
                {
                  style: {
                    fontFamily: FONT_SERIF,
                    fontSize: "18pt",
                    fontWeight: 600,
                    lineHeight: 1.2,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    margin: "0 0 12px 0",
                    color: COLORS.text
                  },
                  children: title
                }
              ),
              /* @__PURE__ */ jsxs146("p", { style: { ...LEGAL_BODY, textAlign: "center", margin: "12px 0 0 0" }, children: [
                "This Agreement is entered into as of ",
                displayDate
              ] }),
              /* @__PURE__ */ jsx163(
                "p",
                {
                  style: {
                    ...LEGAL_BODY,
                    textAlign: "center",
                    margin: "8px 0 0 0",
                    fontWeight: 600
                  },
                  children: "by and between"
                }
              ),
              /* @__PURE__ */ jsxs146("p", { style: { ...LEGAL_BODY, textAlign: "center", margin: "8px 0 0 0" }, children: [
                /* @__PURE__ */ jsx163("strong", { children: partyA }),
                " (hereinafter \u201CLender\u201D)"
              ] }),
              /* @__PURE__ */ jsx163(
                "p",
                {
                  style: {
                    ...LEGAL_BODY,
                    textAlign: "center",
                    margin: "4px 0",
                    fontStyle: "italic"
                  },
                  children: "and"
                }
              ),
              /* @__PURE__ */ jsxs146("p", { style: { ...LEGAL_BODY, textAlign: "center", margin: "0" }, children: [
                /* @__PURE__ */ jsx163("strong", { children: partyB }),
                " (hereinafter \u201CBorrower\u201D)"
              ] }),
              referenceNumber && /* @__PURE__ */ jsxs146("div", { style: { ...LEGAL_META, marginTop: "16px", letterSpacing: "0.08em" }, children: [
                "Agreement No. ",
                referenceNumber
              ] })
            ]
          }
        ),
        /* @__PURE__ */ jsx163("main", { children }),
        /* @__PURE__ */ jsxs146(
          "footer",
          {
            style: {
              marginTop: "48px",
              paddingTop: "12px",
              borderTop: `0.5pt solid ${COLORS.rule}`,
              textAlign: "center",
              fontFamily: FONT_SANS,
              fontSize: "8pt",
              color: COLORS.textTertiary
            },
            children: [
              confLabel && `${confLabel}  \xB7  `,
              title,
              " ",
              referenceNumber ? `(${referenceNumber})` : "",
              " \xB7 Page [_] of [_]"
            ]
          }
        )
      ]
    }
  );
}
function CoverPage2({
  title,
  subtitle,
  date,
  partyA,
  partyB,
  referenceNumber,
  jurisdiction
}) {
  const displayDate = date ?? formatDocumentDate();
  return /* @__PURE__ */ jsx163(
    "div",
    {
      style: {
        minHeight: "9in",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        pageBreakAfter: "always",
        breakAfter: "page"
      },
      children: /* @__PURE__ */ jsxs146("div", { style: { maxWidth: "5in" }, children: [
        /* @__PURE__ */ jsx163(
          "h1",
          {
            style: {
              fontFamily: FONT_SERIF,
              fontSize: "24pt",
              fontWeight: 600,
              lineHeight: 1.2,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              margin: "0 0 16px 0",
              color: COLORS.text
            },
            children: title
          }
        ),
        subtitle && /* @__PURE__ */ jsx163(
          "p",
          {
            style: {
              fontFamily: FONT_SERIF,
              fontSize: "14pt",
              fontWeight: 400,
              color: COLORS.textSecondary,
              margin: "0 0 32px 0"
            },
            children: subtitle
          }
        ),
        /* @__PURE__ */ jsx163(
          "div",
          {
            style: {
              width: "48px",
              height: "1pt",
              background: COLORS.text,
              margin: "32px auto"
            }
          }
        ),
        /* @__PURE__ */ jsx163("p", { style: { ...LEGAL_BODY, textAlign: "center", margin: "0 0 8px 0" }, children: /* @__PURE__ */ jsx163("strong", { children: partyA }) }),
        /* @__PURE__ */ jsx163(
          "p",
          {
            style: {
              ...LEGAL_BODY,
              textAlign: "center",
              margin: "0 0 8px 0",
              fontStyle: "italic",
              color: COLORS.textSecondary
            },
            children: "and"
          }
        ),
        /* @__PURE__ */ jsx163("p", { style: { ...LEGAL_BODY, textAlign: "center", margin: "0 0 32px 0" }, children: /* @__PURE__ */ jsx163("strong", { children: partyB }) }),
        /* @__PURE__ */ jsxs146(
          "div",
          {
            style: {
              marginTop: "48px",
              paddingTop: "16px",
              borderTop: `0.5pt solid ${COLORS.rule}`,
              fontFamily: FONT_SANS,
              fontSize: "9pt",
              color: COLORS.textTertiary
            },
            children: [
              /* @__PURE__ */ jsxs146("div", { style: { marginBottom: "4px" }, children: [
                "Dated: ",
                displayDate
              ] }),
              referenceNumber && /* @__PURE__ */ jsxs146("div", { style: { marginBottom: "4px" }, children: [
                "Reference: ",
                referenceNumber
              ] }),
              jurisdiction && /* @__PURE__ */ jsxs146("div", { children: [
                "Governing Law: ",
                jurisdiction
              ] })
            ]
          }
        )
      ] })
    }
  );
}
function Recitals({ items }) {
  return /* @__PURE__ */ jsxs146("section", { style: { marginBottom: "24px" }, children: [
    /* @__PURE__ */ jsx163("h2", { style: { ...LEGAL_HEADING, textAlign: "center" }, children: "Recitals" }),
    items.map((item, idx) => /* @__PURE__ */ jsxs146("p", { style: { ...LEGAL_BODY, paddingLeft: "24px", textIndent: "-24px" }, children: [
      /* @__PURE__ */ jsx163("strong", { children: "WHEREAS" }),
      ", ",
      item,
      idx < items.length - 1 ? "; and" : "."
    ] }, idx)),
    /* @__PURE__ */ jsxs146("p", { style: { ...LEGAL_BODY, marginTop: "16px" }, children: [
      /* @__PURE__ */ jsx163("strong", { children: "NOW, THEREFORE" }),
      ", in consideration of the mutual covenants and agreements set forth herein, and for other good and valuable consideration, the receipt and sufficiency of which are hereby acknowledged, the parties agree as follows:"
    ] })
  ] });
}
function ClauseSection({ number, title, children }) {
  return /* @__PURE__ */ jsxs146("section", { style: { ...avoidBreakStyle(), marginBottom: "8px" }, children: [
    /* @__PURE__ */ jsxs146("h2", { style: LEGAL_HEADING, children: [
      number,
      ". ",
      title
    ] }),
    /* @__PURE__ */ jsx163("div", { children })
  ] });
}
function SubClause({ number, children }) {
  return /* @__PURE__ */ jsx163(
    "div",
    {
      style: {
        ...avoidBreakStyle(),
        paddingLeft: "24px",
        marginBottom: "8px"
      },
      children: /* @__PURE__ */ jsxs146("p", { style: { ...LEGAL_BODY, textIndent: "-24px", paddingLeft: "24px" }, children: [
        /* @__PURE__ */ jsx163("strong", { children: number }),
        "\u2003",
        children
      ] })
    }
  );
}
function DefinedTerm({ term, children }) {
  return /* @__PURE__ */ jsxs146("span", { children: [
    /* @__PURE__ */ jsxs146("strong", { children: [
      "\u201C",
      term,
      "\u201D"
    ] }),
    " ",
    children
  ] });
}
function SignatureBlock({
  partyName,
  partyRole,
  signatoryName,
  signatoryTitle,
  includeWitness = false,
  includeNotary = false
}) {
  const signatureLine = {
    borderBottom: `1pt solid ${COLORS.text}`,
    width: "240px",
    height: "40px",
    marginBottom: "4px"
  };
  return /* @__PURE__ */ jsxs146(
    "div",
    {
      style: {
        ...avoidBreakStyle(),
        marginTop: "48px",
        marginBottom: "32px"
      },
      children: [
        /* @__PURE__ */ jsx163("div", { style: { ...LEGAL_META, marginBottom: "24px" }, children: partyRole }),
        /* @__PURE__ */ jsx163("p", { style: { ...LEGAL_BODY, fontWeight: 600, marginBottom: "24px" }, children: partyName }),
        /* @__PURE__ */ jsxs146("div", { style: { display: "flex", gap: "48px" }, children: [
          /* @__PURE__ */ jsxs146("div", { children: [
            /* @__PURE__ */ jsx163("div", { style: signatureLine }),
            /* @__PURE__ */ jsx163("div", { style: { fontFamily: FONT_SANS, fontSize: "9pt", color: COLORS.textTertiary }, children: "Signature" })
          ] }),
          /* @__PURE__ */ jsxs146("div", { children: [
            /* @__PURE__ */ jsx163("div", { style: signatureLine }),
            /* @__PURE__ */ jsx163("div", { style: { fontFamily: FONT_SANS, fontSize: "9pt", color: COLORS.textTertiary }, children: "Date" })
          ] })
        ] }),
        /* @__PURE__ */ jsx163("div", { style: { marginTop: "16px" }, children: /* @__PURE__ */ jsxs146("div", { style: { display: "flex", gap: "48px" }, children: [
          /* @__PURE__ */ jsxs146("div", { children: [
            /* @__PURE__ */ jsx163(
              "div",
              {
                style: {
                  ...signatureLine,
                  height: "auto",
                  paddingBottom: "4px"
                },
                children: signatoryName && /* @__PURE__ */ jsx163("span", { style: { fontFamily: FONT_SERIF, fontSize: "11pt" }, children: signatoryName })
              }
            ),
            /* @__PURE__ */ jsx163("div", { style: { fontFamily: FONT_SANS, fontSize: "9pt", color: COLORS.textTertiary }, children: "Printed Name" })
          ] }),
          /* @__PURE__ */ jsxs146("div", { children: [
            /* @__PURE__ */ jsx163(
              "div",
              {
                style: {
                  ...signatureLine,
                  height: "auto",
                  paddingBottom: "4px"
                },
                children: signatoryTitle && /* @__PURE__ */ jsx163("span", { style: { fontFamily: FONT_SERIF, fontSize: "11pt" }, children: signatoryTitle })
              }
            ),
            /* @__PURE__ */ jsx163("div", { style: { fontFamily: FONT_SANS, fontSize: "9pt", color: COLORS.textTertiary }, children: "Title" })
          ] })
        ] }) }),
        includeWitness && /* @__PURE__ */ jsxs146("div", { style: { marginTop: "32px" }, children: [
          /* @__PURE__ */ jsx163("div", { style: { ...LEGAL_META, fontSize: "8pt", marginBottom: "16px" }, children: "Witness" }),
          /* @__PURE__ */ jsxs146("div", { style: { display: "flex", gap: "48px" }, children: [
            /* @__PURE__ */ jsxs146("div", { children: [
              /* @__PURE__ */ jsx163("div", { style: signatureLine }),
              /* @__PURE__ */ jsx163("div", { style: { fontFamily: FONT_SANS, fontSize: "9pt", color: COLORS.textTertiary }, children: "Witness Signature" })
            ] }),
            /* @__PURE__ */ jsxs146("div", { children: [
              /* @__PURE__ */ jsx163("div", { style: signatureLine }),
              /* @__PURE__ */ jsx163("div", { style: { fontFamily: FONT_SANS, fontSize: "9pt", color: COLORS.textTertiary }, children: "Printed Name" })
            ] })
          ] })
        ] }),
        includeNotary && /* @__PURE__ */ jsxs146("div", { style: { marginTop: "32px", padding: "16px", border: `0.5pt solid ${COLORS.rule}` }, children: [
          /* @__PURE__ */ jsx163("div", { style: { ...LEGAL_META, fontSize: "8pt", marginBottom: "12px" }, children: "Notarial Acknowledgment" }),
          /* @__PURE__ */ jsx163("p", { style: { ...LEGAL_BODY, fontSize: "10pt" }, children: "State of _________________ County of _________________" }),
          /* @__PURE__ */ jsx163("p", { style: { ...LEGAL_BODY, fontSize: "10pt" }, children: "On this _______ day of _________________, 20___, before me personally appeared the above-named _________________, known to me (or proved to me on the basis of satisfactory evidence) to be the person whose name is subscribed to this instrument, and acknowledged that they executed the same." }),
          /* @__PURE__ */ jsxs146("div", { style: { marginTop: "24px", display: "flex", gap: "48px" }, children: [
            /* @__PURE__ */ jsxs146("div", { children: [
              /* @__PURE__ */ jsx163("div", { style: signatureLine }),
              /* @__PURE__ */ jsx163("div", { style: { fontFamily: FONT_SANS, fontSize: "9pt", color: COLORS.textTertiary }, children: "Notary Public" })
            ] }),
            /* @__PURE__ */ jsxs146("div", { children: [
              /* @__PURE__ */ jsx163("div", { style: { ...signatureLine, width: "160px" } }),
              /* @__PURE__ */ jsx163("div", { style: { fontFamily: FONT_SANS, fontSize: "9pt", color: COLORS.textTertiary }, children: "Commission Expires" })
            ] })
          ] })
        ] })
      ]
    }
  );
}
function ExhibitSection({ identifier, title, children }) {
  return /* @__PURE__ */ jsxs146("section", { children: [
    /* @__PURE__ */ jsx163("div", { style: pageBreakStyle() }),
    /* @__PURE__ */ jsxs146(
      "h2",
      {
        style: {
          ...LEGAL_HEADING,
          textAlign: "center",
          fontSize: "14pt",
          marginTop: "0"
        },
        children: [
          "Exhibit ",
          identifier
        ]
      }
    ),
    /* @__PURE__ */ jsx163(
      "h3",
      {
        style: {
          fontFamily: FONT_SERIF,
          fontSize: "12pt",
          fontWeight: 600,
          textAlign: "center",
          margin: "8px 0 24px 0",
          color: COLORS.text
        },
        children: title
      }
    ),
    /* @__PURE__ */ jsx163("div", { children })
  ] });
}
function Schedule({ number, title, children }) {
  return /* @__PURE__ */ jsxs146("section", { children: [
    /* @__PURE__ */ jsx163("div", { style: pageBreakStyle() }),
    /* @__PURE__ */ jsxs146(
      "h2",
      {
        style: {
          ...LEGAL_HEADING,
          textAlign: "center",
          fontSize: "14pt",
          marginTop: "0"
        },
        children: [
          "Schedule ",
          number
        ]
      }
    ),
    /* @__PURE__ */ jsx163(
      "h3",
      {
        style: {
          fontFamily: FONT_SERIF,
          fontSize: "12pt",
          fontWeight: 600,
          textAlign: "center",
          margin: "8px 0 24px 0",
          color: COLORS.text
        },
        children: title
      }
    ),
    /* @__PURE__ */ jsx163("div", { children })
  ] });
}
function LegalPageBreak() {
  return /* @__PURE__ */ jsx163("div", { style: pageBreakStyle() });
}
var LegalContract = Object.assign(LegalContractRoot, {
  CoverPage: CoverPage2,
  Recitals,
  ClauseSection,
  SubClause,
  DefinedTerm,
  SignatureBlock,
  ExhibitSection,
  Schedule,
  PageBreak: LegalPageBreak
});

// src/templates/EconomicReport.tsx
import { jsx as jsx164, jsxs as jsxs147 } from "react/jsx-runtime";
function EconomicReportRoot({
  confidentiality = "confidential",
  children
}) {
  const confLabel = getConfidentialityLabel(confidentiality);
  return /* @__PURE__ */ jsxs147(
    "div",
    {
      className: "economic-report print-document",
      style: documentContainerStyle(),
      children: [
        confLabel && /* @__PURE__ */ jsx164(
          "div",
          {
            style: {
              ...metaTextStyle(),
              textAlign: "center",
              marginBottom: "16px",
              color: COLORS.accent,
              fontWeight: 600
            },
            children: confLabel
          }
        ),
        children
      ]
    }
  );
}
function CoverPage3({
  title,
  subtitle,
  institutionName,
  date,
  confidentiality = "confidential",
  referenceNumber,
  preparedBy,
  preparedFor,
  logoUrl,
  modelVersion
}) {
  const confLabel = getConfidentialityLabel(confidentiality);
  return /* @__PURE__ */ jsxs147(
    "div",
    {
      style: {
        minHeight: "9in",
        display: "flex",
        flexDirection: "column",
        ...pageBreakStyle()
      },
      children: [
        /* @__PURE__ */ jsxs147("div", { style: mastheadStyle(), children: [
          /* @__PURE__ */ jsx164("div", { children: logoUrl && /* @__PURE__ */ jsx164(
            "img",
            {
              src: logoUrl,
              alt: "Logo",
              style: { height: "20px", marginBottom: "8px" }
            }
          ) }),
          confLabel && /* @__PURE__ */ jsx164(
            "div",
            {
              style: {
                ...metaTextStyle(),
                color: COLORS.accent,
                fontWeight: 600
              },
              children: confLabel
            }
          )
        ] }),
        /* @__PURE__ */ jsxs147("div", { style: { marginTop: "auto", marginBottom: "48px" }, children: [
          /* @__PURE__ */ jsx164(
            "div",
            {
              style: {
                ...metaTextStyle(),
                color: COLORS.accent,
                marginBottom: "12px"
              },
              children: "ECONOMIC ANALYSIS"
            }
          ),
          /* @__PURE__ */ jsx164("h1", { style: { ...titleStyle(), fontSize: "28pt", marginBottom: "8px" }, children: title }),
          subtitle && /* @__PURE__ */ jsx164(
            "p",
            {
              style: {
                ...paragraphStyle(),
                fontSize: "13pt",
                color: COLORS.textSecondary,
                marginTop: "8px"
              },
              children: subtitle
            }
          )
        ] }),
        /* @__PURE__ */ jsxs147(
          "div",
          {
            style: {
              borderTop: `0.5pt solid ${COLORS.rule}`,
              paddingTop: "16px",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px"
            },
            children: [
              preparedFor && /* @__PURE__ */ jsxs147("div", { children: [
                /* @__PURE__ */ jsx164("div", { style: { ...metaTextStyle(), fontSize: "8pt", marginBottom: "4px" }, children: "PREPARED FOR" }),
                /* @__PURE__ */ jsx164("div", { style: { fontFamily: FONT_SANS, fontSize: "10pt", color: COLORS.text }, children: preparedFor })
              ] }),
              institutionName && !preparedFor && /* @__PURE__ */ jsxs147("div", { children: [
                /* @__PURE__ */ jsx164("div", { style: { ...metaTextStyle(), fontSize: "8pt", marginBottom: "4px" }, children: "INSTITUTION" }),
                /* @__PURE__ */ jsx164("div", { style: { fontFamily: FONT_SANS, fontSize: "10pt", color: COLORS.text }, children: institutionName })
              ] }),
              preparedBy && /* @__PURE__ */ jsxs147("div", { children: [
                /* @__PURE__ */ jsx164("div", { style: { ...metaTextStyle(), fontSize: "8pt", marginBottom: "4px" }, children: "PREPARED BY" }),
                /* @__PURE__ */ jsx164("div", { style: { fontFamily: FONT_SANS, fontSize: "10pt", color: COLORS.text }, children: preparedBy })
              ] }),
              /* @__PURE__ */ jsxs147("div", { children: [
                /* @__PURE__ */ jsx164("div", { style: { ...metaTextStyle(), fontSize: "8pt", marginBottom: "4px" }, children: "DATE" }),
                /* @__PURE__ */ jsx164("div", { style: { fontFamily: FONT_SANS, fontSize: "10pt", color: COLORS.text }, children: date ?? formatDocumentDate() })
              ] }),
              referenceNumber && /* @__PURE__ */ jsxs147("div", { children: [
                /* @__PURE__ */ jsx164("div", { style: { ...metaTextStyle(), fontSize: "8pt", marginBottom: "4px" }, children: "REFERENCE" }),
                /* @__PURE__ */ jsx164("div", { style: { fontFamily: FONT_MONO, fontSize: "10pt", color: COLORS.text }, children: referenceNumber })
              ] }),
              modelVersion && /* @__PURE__ */ jsxs147("div", { children: [
                /* @__PURE__ */ jsx164("div", { style: { ...metaTextStyle(), fontSize: "8pt", marginBottom: "4px" }, children: "MODEL VERSION" }),
                /* @__PURE__ */ jsx164("div", { style: { fontFamily: FONT_MONO, fontSize: "10pt", color: COLORS.text }, children: modelVersion })
              ] })
            ]
          }
        )
      ]
    }
  );
}
var SCENARIO_COLORS = {
  base: COLORS.text,
  optimistic: COLORS.positive,
  pessimistic: COLORS.warning,
  stress: COLORS.negative
};
function ScenarioOverview({
  title = "Scenario Analysis",
  scenarios,
  narrative
}) {
  return /* @__PURE__ */ jsxs147("div", { style: avoidBreakStyle(), children: [
    /* @__PURE__ */ jsx164("h2", { style: sectionHeadingStyle(), children: title }),
    narrative && /* @__PURE__ */ jsx164("p", { style: paragraphStyle(), children: narrative }),
    /* @__PURE__ */ jsx164(
      "div",
      {
        style: {
          display: "grid",
          gridTemplateColumns: `repeat(${Math.min(scenarios.length, 3)}, 1fr)`,
          gap: "16px",
          marginTop: "16px"
        },
        children: scenarios.map((scenario, idx) => {
          const borderColor = SCENARIO_COLORS[scenario.variant ?? "base"] ?? COLORS.text;
          return /* @__PURE__ */ jsxs147(
            "div",
            {
              style: {
                borderTop: `3px solid ${borderColor}`,
                padding: "16px",
                background: COLORS.canvas
              },
              children: [
                /* @__PURE__ */ jsxs147("div", { style: { display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }, children: [
                  /* @__PURE__ */ jsx164("h3", { style: { ...subsectionHeadingStyle(), margin: 0, fontSize: "10pt" }, children: scenario.name }),
                  scenario.probability != null && /* @__PURE__ */ jsxs147(
                    "span",
                    {
                      style: {
                        fontFamily: FONT_MONO,
                        fontSize: "9pt",
                        color: COLORS.textTertiary
                      },
                      children: [
                        "(",
                        (scenario.probability * 100).toFixed(0),
                        "%)"
                      ]
                    }
                  )
                ] }),
                scenario.description && /* @__PURE__ */ jsx164(
                  "p",
                  {
                    style: {
                      fontSize: "9pt",
                      color: COLORS.textSecondary,
                      lineHeight: 1.4,
                      margin: "0 0 12px 0"
                    },
                    children: scenario.description
                  }
                ),
                /* @__PURE__ */ jsx164("div", { style: { display: "flex", flexDirection: "column", gap: "8px" }, children: scenario.metrics.map((metric, mIdx) => /* @__PURE__ */ jsxs147("div", { children: [
                  /* @__PURE__ */ jsx164(
                    "div",
                    {
                      style: {
                        fontFamily: FONT_MONO,
                        fontSize: "16pt",
                        fontWeight: 400,
                        lineHeight: 1.1,
                        color: borderColor,
                        fontVariantNumeric: "tabular-nums"
                      },
                      children: metric.value
                    }
                  ),
                  /* @__PURE__ */ jsx164(
                    "div",
                    {
                      style: {
                        fontFamily: FONT_SANS,
                        fontSize: "8pt",
                        color: COLORS.textTertiary,
                        marginTop: "2px"
                      },
                      children: metric.label
                    }
                  )
                ] }, mIdx)) })
              ]
            },
            idx
          );
        })
      }
    )
  ] });
}
function ProjectionTable({
  title,
  columns,
  rows,
  source,
  unit,
  footnotes
}) {
  return /* @__PURE__ */ jsxs147("div", { style: { ...avoidBreakStyle(), marginTop: "24px" }, children: [
    /* @__PURE__ */ jsxs147("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "baseline" }, children: [
      /* @__PURE__ */ jsx164("h3", { style: { ...subsectionHeadingStyle(), margin: "0 0 4px 0" }, children: title }),
      unit && /* @__PURE__ */ jsx164("span", { style: { fontFamily: FONT_SANS, fontSize: "8pt", color: COLORS.textTertiary }, children: unit })
    ] }),
    /* @__PURE__ */ jsxs147("table", { style: tableStyle(), children: [
      /* @__PURE__ */ jsx164("thead", { children: /* @__PURE__ */ jsx164("tr", { children: columns.map((col, i) => /* @__PURE__ */ jsx164("th", { style: thStyle(col.align ?? (i === 0 ? "left" : "right")), children: col.label }, col.key)) }) }),
      /* @__PURE__ */ jsx164("tbody", { children: rows.map((row, rIdx) => /* @__PURE__ */ jsx164(
        "tr",
        {
          style: row.isTotal ? {
            borderTop: `1px solid ${COLORS.rule}`,
            background: COLORS.canvas,
            fontWeight: 600
          } : void 0,
          children: columns.map((col, cIdx) => /* @__PURE__ */ jsx164(
            "td",
            {
              style: {
                ...tdStyle(col.align ?? (cIdx === 0 ? "left" : "right")),
                fontFamily: cIdx === 0 ? FONT_SANS : FONT_MONO,
                fontWeight: row.isTotal ? 600 : 400,
                fontSize: "10pt"
              },
              children: String(row.values[col.key] ?? "")
            },
            col.key
          ))
        },
        rIdx
      )) })
    ] }),
    source && /* @__PURE__ */ jsxs147(
      "div",
      {
        style: {
          fontFamily: FONT_SANS,
          fontSize: "7pt",
          color: COLORS.textTertiary,
          marginTop: "8px"
        },
        children: [
          "Source: ",
          source
        ]
      }
    ),
    footnotes && footnotes.length > 0 && /* @__PURE__ */ jsx164("div", { style: { marginTop: "8px" }, children: footnotes.map((fn, i) => /* @__PURE__ */ jsxs147(
      "div",
      {
        style: {
          fontFamily: FONT_SANS,
          fontSize: "7pt",
          color: COLORS.textTertiary,
          lineHeight: 1.4
        },
        children: [
          /* @__PURE__ */ jsx164("sup", { children: i + 1 }),
          " ",
          fn
        ]
      },
      i
    )) })
  ] });
}
function SensitivityMatrix({
  title,
  rowLabel,
  columnLabel,
  rowValues,
  columnValues,
  cells,
  baseRow,
  baseCol,
  source,
  outputMetric
}) {
  return /* @__PURE__ */ jsxs147("div", { style: { ...avoidBreakStyle(), marginTop: "24px" }, children: [
    /* @__PURE__ */ jsx164("h3", { style: { ...subsectionHeadingStyle(), margin: "0 0 4px 0" }, children: title }),
    outputMetric && /* @__PURE__ */ jsxs147(
      "div",
      {
        style: {
          fontFamily: FONT_SANS,
          fontSize: "8pt",
          color: COLORS.textTertiary,
          marginBottom: "8px"
        },
        children: [
          "Output: ",
          outputMetric
        ]
      }
    ),
    /* @__PURE__ */ jsxs147("table", { style: { ...tableStyle(), fontSize: "9pt" }, children: [
      /* @__PURE__ */ jsx164("thead", { children: /* @__PURE__ */ jsxs147("tr", { children: [
        /* @__PURE__ */ jsxs147(
          "th",
          {
            style: {
              ...thStyle("left"),
              fontSize: "8pt",
              borderBottom: `1px solid ${COLORS.rule}`
            },
            children: [
              rowLabel,
              " / ",
              columnLabel
            ]
          }
        ),
        columnValues.map((cv, i) => /* @__PURE__ */ jsx164(
          "th",
          {
            style: {
              ...thStyle("right"),
              fontSize: "8pt",
              borderBottom: `1px solid ${COLORS.rule}`,
              fontWeight: i === baseCol ? 700 : 600,
              color: i === baseCol ? COLORS.accent : COLORS.textSecondary
            },
            children: cv
          },
          i
        ))
      ] }) }),
      /* @__PURE__ */ jsx164("tbody", { children: rowValues.map((rv, rIdx) => /* @__PURE__ */ jsxs147("tr", { children: [
        /* @__PURE__ */ jsx164(
          "td",
          {
            style: {
              ...tdStyle("left"),
              fontFamily: FONT_SANS,
              fontSize: "9pt",
              fontWeight: rIdx === baseRow ? 600 : 400,
              color: rIdx === baseRow ? COLORS.accent : COLORS.text
            },
            children: rv
          }
        ),
        cells[rIdx]?.map((cell, cIdx) => {
          const isBase = rIdx === baseRow && cIdx === baseCol;
          return /* @__PURE__ */ jsx164(
            "td",
            {
              style: {
                ...tdStyle("right"),
                fontFamily: FONT_MONO,
                fontSize: "9pt",
                fontWeight: isBase ? 600 : 400,
                background: isBase ? COLORS.canvas : "transparent",
                color: isBase ? COLORS.accent : COLORS.text
              },
              children: cell
            },
            cIdx
          );
        })
      ] }, rIdx)) })
    ] }),
    source && /* @__PURE__ */ jsxs147(
      "div",
      {
        style: {
          fontFamily: FONT_SANS,
          fontSize: "7pt",
          color: COLORS.textTertiary,
          marginTop: "8px"
        },
        children: [
          "Source: ",
          source
        ]
      }
    )
  ] });
}
function CashFlowAnalysis({
  title = "Cash Flow Analysis",
  periods,
  lineItems,
  source
}) {
  return /* @__PURE__ */ jsxs147("div", { style: { ...avoidBreakStyle(), marginTop: "24px" }, children: [
    /* @__PURE__ */ jsx164("h2", { style: sectionHeadingStyle(), children: title }),
    /* @__PURE__ */ jsxs147("table", { style: { ...tableStyle(), fontSize: "9pt" }, children: [
      /* @__PURE__ */ jsx164("thead", { children: /* @__PURE__ */ jsxs147("tr", { children: [
        /* @__PURE__ */ jsx164("th", { style: { ...thStyle("left"), width: "40%" }, children: "" }),
        periods.map((p, i) => /* @__PURE__ */ jsx164("th", { style: thStyle("right"), children: p }, i))
      ] }) }),
      /* @__PURE__ */ jsx164("tbody", { children: lineItems.map((item, idx) => {
        if (item.isSeparator) {
          return /* @__PURE__ */ jsx164("tr", { children: /* @__PURE__ */ jsx164(
            "td",
            {
              colSpan: periods.length + 1,
              style: {
                borderBottom: `1px solid ${COLORS.rule}`,
                height: "8px",
                padding: 0
              }
            }
          ) }, idx);
        }
        return /* @__PURE__ */ jsxs147(
          "tr",
          {
            style: item.isTotal ? {
              borderTop: `1px solid ${COLORS.rule}`,
              background: COLORS.canvas
            } : void 0,
            children: [
              /* @__PURE__ */ jsx164(
                "td",
                {
                  style: {
                    ...tdStyle("left"),
                    fontFamily: FONT_SANS,
                    fontSize: "9pt",
                    fontWeight: item.isTotal ? 600 : 400,
                    paddingLeft: item.indent ? `${(item.indent ?? 0) * 16 + 8}px` : "8px"
                  },
                  children: item.label
                }
              ),
              item.values.map((v, vIdx) => {
                const strVal = String(v);
                const isNegative = strVal.startsWith("-") || strVal.startsWith("(");
                return /* @__PURE__ */ jsx164(
                  "td",
                  {
                    style: {
                      ...tdStyle("right"),
                      fontFamily: FONT_MONO,
                      fontSize: "9pt",
                      fontWeight: item.isTotal ? 600 : 400,
                      color: isNegative ? COLORS.negative : COLORS.text
                    },
                    children: strVal
                  },
                  vIdx
                );
              })
            ]
          },
          idx
        );
      }) })
    ] }),
    source && /* @__PURE__ */ jsxs147(
      "div",
      {
        style: {
          fontFamily: FONT_SANS,
          fontSize: "7pt",
          color: COLORS.textTertiary,
          marginTop: "8px"
        },
        children: [
          "Source: ",
          source
        ]
      }
    )
  ] });
}
function AssumptionPanel({
  title = "Key Assumptions",
  categories
}) {
  return /* @__PURE__ */ jsxs147("div", { style: { ...avoidBreakStyle(), marginTop: "24px" }, children: [
    /* @__PURE__ */ jsx164("h2", { style: sectionHeadingStyle(), children: title }),
    /* @__PURE__ */ jsx164(
      "div",
      {
        style: {
          display: "grid",
          gridTemplateColumns: categories.length > 1 ? "1fr 1fr" : "1fr",
          gap: "16px"
        },
        children: categories.map((cat, idx) => /* @__PURE__ */ jsxs147(
          "div",
          {
            style: {
              background: COLORS.canvas,
              padding: "16px"
            },
            children: [
              /* @__PURE__ */ jsx164(
                "h3",
                {
                  style: {
                    fontFamily: FONT_SANS,
                    fontSize: "9pt",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: COLORS.textSecondary,
                    margin: "0 0 12px 0"
                  },
                  children: cat.name
                }
              ),
              /* @__PURE__ */ jsx164("div", { style: { display: "flex", flexDirection: "column", gap: "8px" }, children: cat.assumptions.map((a, aIdx) => /* @__PURE__ */ jsxs147(
                "div",
                {
                  style: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    borderBottom: `0.25pt solid ${COLORS.ruleLight}`,
                    paddingBottom: "4px"
                  },
                  children: [
                    /* @__PURE__ */ jsx164(
                      "span",
                      {
                        style: {
                          fontFamily: FONT_SANS,
                          fontSize: "9pt",
                          color: COLORS.textSecondary
                        },
                        children: a.label
                      }
                    ),
                    /* @__PURE__ */ jsx164(
                      "span",
                      {
                        style: {
                          fontFamily: FONT_MONO,
                          fontSize: "9pt",
                          color: COLORS.text,
                          fontWeight: 500
                        },
                        children: a.value
                      }
                    )
                  ]
                },
                aIdx
              )) })
            ]
          },
          idx
        ))
      }
    )
  ] });
}
function ChartPlaceholder({
  title,
  description,
  height = "240px",
  source,
  children
}) {
  return /* @__PURE__ */ jsxs147("div", { style: { ...avoidBreakStyle(), marginTop: "24px" }, children: [
    /* @__PURE__ */ jsx164("h3", { style: { ...subsectionHeadingStyle(), margin: "0 0 4px 0" }, children: title }),
    description && /* @__PURE__ */ jsx164(
      "div",
      {
        style: {
          fontFamily: FONT_SANS,
          fontSize: "8pt",
          color: COLORS.textTertiary,
          marginBottom: "8px"
        },
        children: description
      }
    ),
    /* @__PURE__ */ jsx164(
      "div",
      {
        style: {
          height,
          background: children ? "transparent" : COLORS.canvas,
          border: children ? "none" : `1px dashed ${COLORS.ruleLight}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden"
        },
        children: children ?? /* @__PURE__ */ jsxs147(
          "span",
          {
            style: {
              fontFamily: FONT_SANS,
              fontSize: "10pt",
              color: COLORS.textDisabled
            },
            children: [
              "Chart: ",
              title
            ]
          }
        )
      }
    ),
    source && /* @__PURE__ */ jsxs147(
      "div",
      {
        style: {
          fontFamily: FONT_SANS,
          fontSize: "7pt",
          color: COLORS.textTertiary,
          marginTop: "8px"
        },
        children: [
          "Source: ",
          source
        ]
      }
    )
  ] });
}
function Commentary({ heading, children }) {
  return /* @__PURE__ */ jsxs147("div", { style: { ...avoidBreakStyle(), marginTop: "24px" }, children: [
    heading && /* @__PURE__ */ jsx164("h2", { style: sectionHeadingStyle(), children: heading }),
    /* @__PURE__ */ jsx164("div", { style: paragraphStyle(), children })
  ] });
}
function DataTable2({
  title,
  headers,
  rows,
  alignments,
  source
}) {
  return /* @__PURE__ */ jsxs147("div", { style: { ...avoidBreakStyle(), marginTop: "24px" }, children: [
    title && /* @__PURE__ */ jsx164("h3", { style: { ...subsectionHeadingStyle(), margin: "0 0 4px 0" }, children: title }),
    /* @__PURE__ */ jsxs147("table", { style: tableStyle(), children: [
      /* @__PURE__ */ jsx164("thead", { children: /* @__PURE__ */ jsx164("tr", { children: headers.map((h, i) => /* @__PURE__ */ jsx164("th", { style: thStyle(alignments?.[i] ?? (i === 0 ? "left" : "right")), children: h }, i)) }) }),
      /* @__PURE__ */ jsx164("tbody", { children: rows.map((row, rIdx) => {
        const variantColor = row.variant === "positive" ? COLORS.positive : row.variant === "negative" ? COLORS.negative : row.variant === "warning" ? COLORS.warning : void 0;
        return /* @__PURE__ */ jsx164(
          "tr",
          {
            style: row.emphasis ? { background: COLORS.canvas, fontWeight: 600 } : void 0,
            children: row.cells.map((cell, cIdx) => /* @__PURE__ */ jsx164(
              "td",
              {
                style: {
                  ...tdStyle(alignments?.[cIdx] ?? (cIdx === 0 ? "left" : "right")),
                  fontFamily: cIdx === 0 ? FONT_SANS : FONT_MONO,
                  fontSize: "10pt",
                  fontWeight: row.emphasis ? 600 : 400,
                  color: variantColor ?? COLORS.text
                },
                children: cell
              },
              cIdx
            ))
          },
          rIdx
        );
      }) })
    ] }),
    source && /* @__PURE__ */ jsxs147(
      "div",
      {
        style: {
          fontFamily: FONT_SANS,
          fontSize: "7pt",
          color: COLORS.textTertiary,
          marginTop: "8px"
        },
        children: [
          "Source: ",
          source
        ]
      }
    )
  ] });
}
function MetricsRow2({ metrics, columns = 3 }) {
  return /* @__PURE__ */ jsx164(
    "div",
    {
      style: {
        ...avoidBreakStyle(),
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: "16px",
        marginTop: "16px"
      },
      children: metrics.map((m, idx) => /* @__PURE__ */ jsxs147("div", { style: { padding: "12px 0" }, children: [
        /* @__PURE__ */ jsx164("div", { style: metricValueStyle(), children: m.value }),
        /* @__PURE__ */ jsx164("div", { style: metricLabelStyle(), children: m.label }),
        m.change && /* @__PURE__ */ jsx164(
          "div",
          {
            style: {
              fontFamily: FONT_MONO,
              fontSize: "9pt",
              color: m.trend === "up" ? COLORS.positive : m.trend === "down" ? COLORS.negative : COLORS.textTertiary,
              marginTop: "2px"
            },
            children: m.change
          }
        )
      ] }, idx))
    }
  );
}
function PageBreak2() {
  return /* @__PURE__ */ jsx164("div", { style: pageBreakStyle(), "aria-hidden": "true" });
}
function Disclaimer2({ text, methodology }) {
  return /* @__PURE__ */ jsxs147("div", { style: { ...footerStyle(), textAlign: "left" }, children: [
    methodology && /* @__PURE__ */ jsxs147("div", { style: { marginBottom: "8px" }, children: [
      /* @__PURE__ */ jsxs147(
        "span",
        {
          style: {
            fontFamily: FONT_SANS,
            fontSize: "8pt",
            fontWeight: 600,
            color: COLORS.textSecondary
          },
          children: [
            "Methodology:",
            " "
          ]
        }
      ),
      /* @__PURE__ */ jsx164(
        "span",
        {
          style: {
            fontFamily: FONT_SANS,
            fontSize: "8pt",
            color: COLORS.textTertiary
          },
          children: methodology
        }
      )
    ] }),
    /* @__PURE__ */ jsx164(
      "p",
      {
        style: {
          fontFamily: FONT_SANS,
          fontSize: "7pt",
          color: COLORS.textTertiary,
          lineHeight: 1.4,
          margin: 0
        },
        children: text ?? "This economic analysis is generated by the Aaim platform economic modeling engine. Projections are based on stated assumptions and historical data. Actual results may differ materially from projections due to changes in market conditions, regulatory environment, or other factors. This document does not constitute investment advice. Past performance is not indicative of future results."
      }
    )
  ] });
}
var EconomicReport = Object.assign(EconomicReportRoot, {
  CoverPage: CoverPage3,
  ScenarioOverview,
  ProjectionTable,
  SensitivityMatrix,
  CashFlowAnalysis,
  AssumptionPanel,
  ChartPlaceholder,
  Commentary,
  DataTable: DataTable2,
  MetricsRow: MetricsRow2,
  PageBreak: PageBreak2,
  Disclaimer: Disclaimer2
});

// src/templates/WhitepaperReport.tsx
import { jsx as jsx165, jsxs as jsxs148 } from "react/jsx-runtime";
function WhitepaperReportRoot({
  confidentiality,
  children
}) {
  const confLabel = confidentiality ? getConfidentialityLabel(confidentiality) : "";
  return /* @__PURE__ */ jsxs148(
    "div",
    {
      className: "whitepaper-report print-document",
      style: {
        ...documentContainerStyle(),
        fontSize: "11pt",
        lineHeight: 1.6
      },
      children: [
        confLabel && /* @__PURE__ */ jsx165(
          "div",
          {
            style: {
              ...metaTextStyle(),
              textAlign: "center",
              marginBottom: "16px",
              color: COLORS.accent,
              fontWeight: 600
            },
            children: confLabel
          }
        ),
        children
      ]
    }
  );
}
function TitlePage({
  title,
  subtitle,
  documentType = "Whitepaper",
  authors,
  date,
  confidentiality,
  logoUrl,
  abstract,
  version,
  reference
}) {
  const confLabel = confidentiality ? getConfidentialityLabel(confidentiality) : "";
  return /* @__PURE__ */ jsxs148(
    "div",
    {
      style: {
        minHeight: "9in",
        display: "flex",
        flexDirection: "column",
        ...pageBreakStyle()
      },
      children: [
        /* @__PURE__ */ jsxs148("div", { style: mastheadStyle(), children: [
          /* @__PURE__ */ jsx165("div", { children: logoUrl && /* @__PURE__ */ jsx165(
            "img",
            {
              src: logoUrl,
              alt: "Logo",
              style: { height: "20px" }
            }
          ) }),
          confLabel && /* @__PURE__ */ jsx165(
            "div",
            {
              style: {
                ...metaTextStyle(),
                color: COLORS.accent,
                fontWeight: 600
              },
              children: confLabel
            }
          )
        ] }),
        /* @__PURE__ */ jsx165(
          "div",
          {
            style: {
              ...metaTextStyle(),
              color: COLORS.accent,
              marginTop: "auto",
              marginBottom: "16px"
            },
            children: documentType.toUpperCase()
          }
        ),
        /* @__PURE__ */ jsx165(
          "h1",
          {
            style: {
              fontFamily: FONT_SERIF,
              fontSize: "32pt",
              fontWeight: 600,
              lineHeight: 1.1,
              letterSpacing: "-0.045em",
              color: COLORS.text,
              margin: "0 0 12px 0",
              maxWidth: "6in"
            },
            children: title
          }
        ),
        subtitle && /* @__PURE__ */ jsx165(
          "p",
          {
            style: {
              fontFamily: FONT_SERIF,
              fontSize: "14pt",
              lineHeight: 1.4,
              color: COLORS.textSecondary,
              margin: "0 0 24px 0",
              maxWidth: "5.5in"
            },
            children: subtitle
          }
        ),
        /* @__PURE__ */ jsx165(
          "div",
          {
            style: {
              width: "48px",
              height: "3px",
              background: COLORS.accent,
              marginBottom: "24px"
            }
          }
        ),
        authors && authors.length > 0 && /* @__PURE__ */ jsx165("div", { style: { marginBottom: "24px" }, children: authors.map((author, idx) => /* @__PURE__ */ jsxs148("div", { style: { marginBottom: idx < authors.length - 1 ? "8px" : 0 }, children: [
          /* @__PURE__ */ jsx165(
            "div",
            {
              style: {
                fontFamily: FONT_SANS,
                fontSize: "11pt",
                fontWeight: 600,
                color: COLORS.text
              },
              children: author.name
            }
          ),
          (author.title || author.affiliation) && /* @__PURE__ */ jsx165(
            "div",
            {
              style: {
                fontFamily: FONT_SANS,
                fontSize: "9pt",
                color: COLORS.textTertiary
              },
              children: [author.title, author.affiliation].filter(Boolean).join(" | ")
            }
          )
        ] }, idx)) }),
        abstract && /* @__PURE__ */ jsx165(
          "div",
          {
            style: {
              borderLeft: `3px solid ${COLORS.accent}`,
              paddingLeft: "16px",
              marginBottom: "24px",
              maxWidth: "5.5in"
            },
            children: /* @__PURE__ */ jsx165(
              "p",
              {
                style: {
                  fontFamily: FONT_SERIF,
                  fontSize: "10pt",
                  lineHeight: 1.5,
                  color: COLORS.textSecondary,
                  margin: 0,
                  fontStyle: "italic"
                },
                children: abstract
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs148(
          "div",
          {
            style: {
              marginTop: "auto",
              borderTop: `0.5pt solid ${COLORS.rule}`,
              paddingTop: "16px",
              display: "flex",
              gap: "32px"
            },
            children: [
              /* @__PURE__ */ jsxs148("div", { children: [
                /* @__PURE__ */ jsx165("div", { style: { ...metaTextStyle(), fontSize: "8pt", marginBottom: "4px" }, children: "DATE" }),
                /* @__PURE__ */ jsx165("div", { style: { fontFamily: FONT_SANS, fontSize: "10pt", color: COLORS.text }, children: date ?? formatDocumentDate() })
              ] }),
              version && /* @__PURE__ */ jsxs148("div", { children: [
                /* @__PURE__ */ jsx165("div", { style: { ...metaTextStyle(), fontSize: "8pt", marginBottom: "4px" }, children: "VERSION" }),
                /* @__PURE__ */ jsx165("div", { style: { fontFamily: FONT_MONO, fontSize: "10pt", color: COLORS.text }, children: version })
              ] }),
              reference && /* @__PURE__ */ jsxs148("div", { children: [
                /* @__PURE__ */ jsx165("div", { style: { ...metaTextStyle(), fontSize: "8pt", marginBottom: "4px" }, children: "REFERENCE" }),
                /* @__PURE__ */ jsx165("div", { style: { fontFamily: FONT_MONO, fontSize: "10pt", color: COLORS.text }, children: reference })
              ] })
            ]
          }
        )
      ]
    }
  );
}
function TableOfContents({
  entries,
  title = "Contents"
}) {
  return /* @__PURE__ */ jsxs148("div", { style: { ...pageBreakStyle(), paddingTop: "32px" }, children: [
    /* @__PURE__ */ jsx165(
      "h2",
      {
        style: {
          fontFamily: FONT_SERIF,
          fontSize: "18pt",
          fontWeight: 600,
          letterSpacing: "-0.02em",
          color: COLORS.text,
          marginBottom: "24px"
        },
        children: title
      }
    ),
    /* @__PURE__ */ jsx165("div", { style: { display: "flex", flexDirection: "column" }, children: entries.map((entry, idx) => /* @__PURE__ */ jsxs148(
      "div",
      {
        style: {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          padding: `${entry.depth ? "4px" : "8px"} 0`,
          borderBottom: entry.depth === 0 || entry.depth == null ? `0.25pt solid ${COLORS.ruleLight}` : "none",
          paddingLeft: `${(entry.depth ?? 0) * 24}px`
        },
        children: [
          /* @__PURE__ */ jsxs148("div", { style: { display: "flex", gap: "12px", alignItems: "baseline" }, children: [
            /* @__PURE__ */ jsx165(
              "span",
              {
                style: {
                  fontFamily: FONT_MONO,
                  fontSize: entry.depth ? "9pt" : "10pt",
                  color: COLORS.textTertiary,
                  minWidth: "32px"
                },
                children: entry.number
              }
            ),
            /* @__PURE__ */ jsx165(
              "span",
              {
                style: {
                  fontFamily: entry.depth ? FONT_SANS : FONT_SERIF,
                  fontSize: entry.depth ? "10pt" : "11pt",
                  fontWeight: entry.depth ? 400 : 600,
                  color: COLORS.text
                },
                children: entry.title
              }
            )
          ] }),
          entry.page != null && /* @__PURE__ */ jsx165(
            "span",
            {
              style: {
                fontFamily: FONT_MONO,
                fontSize: "9pt",
                color: COLORS.textTertiary
              },
              children: entry.page
            }
          )
        ]
      },
      idx
    )) })
  ] });
}
function Abstract({
  children,
  keywords,
  jelCodes
}) {
  return /* @__PURE__ */ jsxs148("div", { style: { ...avoidBreakStyle(), marginTop: "32px", marginBottom: "32px" }, children: [
    /* @__PURE__ */ jsx165(
      "h2",
      {
        style: {
          ...sectionHeadingStyle(),
          fontSize: "10pt",
          marginTop: 0
        },
        children: "Abstract"
      }
    ),
    /* @__PURE__ */ jsx165(
      "div",
      {
        style: {
          fontFamily: FONT_SERIF,
          fontSize: "10pt",
          lineHeight: 1.5,
          color: COLORS.textSecondary,
          fontStyle: "italic"
        },
        children
      }
    ),
    keywords && keywords.length > 0 && /* @__PURE__ */ jsxs148("div", { style: { marginTop: "12px" }, children: [
      /* @__PURE__ */ jsxs148(
        "span",
        {
          style: {
            fontFamily: FONT_SANS,
            fontSize: "8pt",
            fontWeight: 600,
            color: COLORS.textSecondary
          },
          children: [
            "Keywords:",
            " "
          ]
        }
      ),
      /* @__PURE__ */ jsx165(
        "span",
        {
          style: {
            fontFamily: FONT_SANS,
            fontSize: "8pt",
            color: COLORS.textTertiary
          },
          children: keywords.join(", ")
        }
      )
    ] }),
    jelCodes && jelCodes.length > 0 && /* @__PURE__ */ jsxs148("div", { style: { marginTop: "4px" }, children: [
      /* @__PURE__ */ jsxs148(
        "span",
        {
          style: {
            fontFamily: FONT_SANS,
            fontSize: "8pt",
            fontWeight: 600,
            color: COLORS.textSecondary
          },
          children: [
            "JEL Codes:",
            " "
          ]
        }
      ),
      /* @__PURE__ */ jsx165(
        "span",
        {
          style: {
            fontFamily: FONT_MONO,
            fontSize: "8pt",
            color: COLORS.textTertiary
          },
          children: jelCodes.join(", ")
        }
      )
    ] })
  ] });
}
function Section3({ number, title, children }) {
  return /* @__PURE__ */ jsxs148("div", { style: { marginTop: "32px" }, children: [
    /* @__PURE__ */ jsxs148(
      "h2",
      {
        style: {
          fontFamily: FONT_SERIF,
          fontSize: "16pt",
          fontWeight: 600,
          lineHeight: 1.2,
          letterSpacing: "-0.02em",
          color: COLORS.text,
          marginBottom: "16px",
          display: "flex",
          gap: "12px",
          alignItems: "baseline"
        },
        children: [
          number && /* @__PURE__ */ jsx165(
            "span",
            {
              style: {
                fontFamily: FONT_MONO,
                fontSize: "14pt",
                color: COLORS.textTertiary,
                fontWeight: 400
              },
              children: number
            }
          ),
          /* @__PURE__ */ jsx165("span", { children: title })
        ]
      }
    ),
    /* @__PURE__ */ jsx165("div", { children })
  ] });
}
function Subsection({ number, title, children }) {
  return /* @__PURE__ */ jsxs148("div", { style: { marginTop: "24px" }, children: [
    /* @__PURE__ */ jsxs148(
      "h3",
      {
        style: {
          fontFamily: FONT_SERIF,
          fontSize: "12pt",
          fontWeight: 600,
          lineHeight: 1.3,
          color: COLORS.text,
          marginBottom: "8px",
          display: "flex",
          gap: "8px",
          alignItems: "baseline"
        },
        children: [
          number && /* @__PURE__ */ jsx165(
            "span",
            {
              style: {
                fontFamily: FONT_MONO,
                fontSize: "10pt",
                color: COLORS.textTertiary,
                fontWeight: 400
              },
              children: number
            }
          ),
          /* @__PURE__ */ jsx165("span", { children: title })
        ]
      }
    ),
    /* @__PURE__ */ jsx165("div", { children })
  ] });
}
function Paragraph2({ children, dropCap }) {
  if (dropCap) {
    return /* @__PURE__ */ jsx165(
      "p",
      {
        style: {
          ...paragraphStyle(),
          lineHeight: 1.6
        },
        className: "whitepaper-drop-cap",
        children
      }
    );
  }
  return /* @__PURE__ */ jsx165(
    "p",
    {
      style: {
        ...paragraphStyle(),
        lineHeight: 1.6
      },
      children
    }
  );
}
function PullQuote2({ quote, attribution, source }) {
  return /* @__PURE__ */ jsxs148(
    "div",
    {
      style: {
        ...avoidBreakStyle(),
        margin: "32px 0",
        padding: "24px 0 24px 24px",
        borderLeft: `3px solid ${COLORS.accent}`
      },
      children: [
        /* @__PURE__ */ jsx165(
          "blockquote",
          {
            style: {
              fontFamily: FONT_SERIF,
              fontSize: "14pt",
              lineHeight: 1.4,
              color: COLORS.text,
              fontStyle: "italic",
              margin: 0
            },
            children: quote
          }
        ),
        (attribution || source) && /* @__PURE__ */ jsxs148("div", { style: { marginTop: "12px" }, children: [
          attribution && /* @__PURE__ */ jsx165(
            "span",
            {
              style: {
                fontFamily: FONT_SANS,
                fontSize: "9pt",
                fontWeight: 600,
                color: COLORS.textSecondary
              },
              children: attribution
            }
          ),
          source && /* @__PURE__ */ jsxs148(
            "span",
            {
              style: {
                fontFamily: FONT_SANS,
                fontSize: "9pt",
                color: COLORS.textTertiary,
                fontStyle: "italic"
              },
              children: [
                attribution ? ", " : "",
                source
              ]
            }
          )
        ] })
      ]
    }
  );
}
function Figure({ number, caption, source, children }) {
  return /* @__PURE__ */ jsxs148(
    "div",
    {
      style: {
        ...avoidBreakStyle(),
        margin: "24px 0"
      },
      children: [
        /* @__PURE__ */ jsx165("div", { style: { marginBottom: "8px" }, children }),
        /* @__PURE__ */ jsxs148("div", { children: [
          number && /* @__PURE__ */ jsxs148(
            "span",
            {
              style: {
                fontFamily: FONT_SANS,
                fontSize: "9pt",
                fontWeight: 600,
                color: COLORS.text
              },
              children: [
                "Figure ",
                number,
                ".",
                " "
              ]
            }
          ),
          /* @__PURE__ */ jsx165(
            "span",
            {
              style: {
                fontFamily: FONT_SANS,
                fontSize: "9pt",
                color: COLORS.textSecondary
              },
              children: caption
            }
          )
        ] }),
        source && /* @__PURE__ */ jsxs148(
          "div",
          {
            style: {
              fontFamily: FONT_SANS,
              fontSize: "7pt",
              color: COLORS.textTertiary,
              marginTop: "4px"
            },
            children: [
              "Source: ",
              source
            ]
          }
        )
      ]
    }
  );
}
var CALLOUT_STYLES = {
  insight: { border: COLORS.accent, bg: "#D2001F08" },
  definition: { border: COLORS.textTertiary, bg: COLORS.canvas },
  example: { border: COLORS.positive, bg: "#1B734008" },
  warning: { border: COLORS.warning, bg: "#B5470808" }
};
function Callout({ title, children, variant = "insight" }) {
  const style = CALLOUT_STYLES[variant] ?? CALLOUT_STYLES.insight;
  return /* @__PURE__ */ jsxs148(
    "div",
    {
      style: {
        ...avoidBreakStyle(),
        margin: "24px 0",
        padding: "16px",
        borderLeft: `3px solid ${style.border}`,
        background: style.bg
      },
      children: [
        /* @__PURE__ */ jsx165(
          "h4",
          {
            style: {
              fontFamily: FONT_SANS,
              fontSize: "10pt",
              fontWeight: 600,
              color: COLORS.text,
              margin: "0 0 8px 0"
            },
            children: title
          }
        ),
        /* @__PURE__ */ jsx165(
          "div",
          {
            style: {
              fontFamily: FONT_SERIF,
              fontSize: "10pt",
              lineHeight: 1.5,
              color: COLORS.textSecondary
            },
            children
          }
        )
      ]
    }
  );
}
function Footnotes({ items }) {
  if (items.length === 0) return null;
  return /* @__PURE__ */ jsx165(
    "div",
    {
      style: {
        marginTop: "32px",
        paddingTop: "12px",
        borderTop: `0.5pt solid ${COLORS.rule}`
      },
      children: items.map((fn) => /* @__PURE__ */ jsxs148(
        "div",
        {
          style: {
            fontFamily: FONT_SANS,
            fontSize: "8pt",
            color: COLORS.textTertiary,
            lineHeight: 1.4,
            marginBottom: "4px",
            display: "flex",
            gap: "4px"
          },
          children: [
            /* @__PURE__ */ jsx165("sup", { style: { fontSize: "7pt", lineHeight: 1 }, children: fn.id }),
            /* @__PURE__ */ jsx165("span", { children: fn.text })
          ]
        },
        fn.id
      ))
    }
  );
}
function BibliographySection({
  title = "References",
  entries
}) {
  return /* @__PURE__ */ jsxs148("div", { style: { ...pageBreakStyle(), paddingTop: "32px" }, children: [
    /* @__PURE__ */ jsx165(
      "h2",
      {
        style: {
          fontFamily: FONT_SERIF,
          fontSize: "16pt",
          fontWeight: 600,
          letterSpacing: "-0.02em",
          color: COLORS.text,
          marginBottom: "24px"
        },
        children: title
      }
    ),
    /* @__PURE__ */ jsx165("div", { style: { display: "flex", flexDirection: "column", gap: "12px" }, children: entries.map((entry) => /* @__PURE__ */ jsxs148(
      "div",
      {
        style: {
          fontFamily: FONT_SERIF,
          fontSize: "9pt",
          lineHeight: 1.5,
          color: COLORS.textSecondary,
          paddingLeft: "24px",
          textIndent: "-24px"
        },
        children: [
          /* @__PURE__ */ jsxs148("span", { style: { fontFamily: FONT_MONO, fontSize: "8pt", color: COLORS.textTertiary }, children: [
            "[",
            entry.id,
            "]"
          ] }),
          " ",
          entry.authors,
          " (",
          entry.year,
          ").",
          " ",
          /* @__PURE__ */ jsx165("span", { style: { fontStyle: "italic" }, children: entry.title }),
          ".",
          entry.publication && /* @__PURE__ */ jsxs148("span", { children: [
            " ",
            entry.publication
          ] }),
          entry.volume && /* @__PURE__ */ jsxs148("span", { children: [
            ", ",
            entry.volume
          ] }),
          entry.pages && /* @__PURE__ */ jsxs148("span", { children: [
            ", pp. ",
            entry.pages
          ] }),
          entry.doi && /* @__PURE__ */ jsxs148("span", { style: { fontFamily: FONT_MONO, fontSize: "8pt" }, children: [
            " ",
            "doi:",
            entry.doi
          ] })
        ]
      },
      entry.id
    )) })
  ] });
}
function AuthorBio({ name, title: authorTitle, affiliation, bio, photoUrl }) {
  return /* @__PURE__ */ jsxs148(
    "div",
    {
      style: {
        ...avoidBreakStyle(),
        display: "flex",
        gap: "16px",
        padding: "16px",
        background: COLORS.canvas,
        marginTop: "16px"
      },
      children: [
        photoUrl && /* @__PURE__ */ jsx165(
          "img",
          {
            src: photoUrl,
            alt: name,
            style: {
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              objectFit: "cover",
              flexShrink: 0
            }
          }
        ),
        /* @__PURE__ */ jsxs148("div", { children: [
          /* @__PURE__ */ jsx165(
            "div",
            {
              style: {
                fontFamily: FONT_SANS,
                fontSize: "11pt",
                fontWeight: 600,
                color: COLORS.text
              },
              children: name
            }
          ),
          (authorTitle || affiliation) && /* @__PURE__ */ jsx165(
            "div",
            {
              style: {
                fontFamily: FONT_SANS,
                fontSize: "9pt",
                color: COLORS.textTertiary,
                marginBottom: "8px"
              },
              children: [authorTitle, affiliation].filter(Boolean).join(" | ")
            }
          ),
          /* @__PURE__ */ jsx165(
            "p",
            {
              style: {
                fontFamily: FONT_SERIF,
                fontSize: "9pt",
                lineHeight: 1.5,
                color: COLORS.textSecondary,
                margin: 0
              },
              children: bio
            }
          )
        ] })
      ]
    }
  );
}
function PageBreak3() {
  return /* @__PURE__ */ jsx165("div", { style: pageBreakStyle(), "aria-hidden": "true" });
}
function Disclaimer3({ text, copyright }) {
  return /* @__PURE__ */ jsxs148("div", { style: { ...footerStyle(), textAlign: "left" }, children: [
    /* @__PURE__ */ jsx165(
      "p",
      {
        style: {
          fontFamily: FONT_SANS,
          fontSize: "7pt",
          color: COLORS.textTertiary,
          lineHeight: 1.4,
          margin: 0
        },
        children: text ?? "The views expressed in this paper are those of the authors and do not necessarily reflect the position of Aaim or its affiliates. This document is provided for informational purposes only and does not constitute investment advice, a solicitation, or an offer to buy or sell securities. Information herein is believed to be reliable but is not guaranteed."
      }
    ),
    copyright && /* @__PURE__ */ jsx165(
      "p",
      {
        style: {
          fontFamily: FONT_SANS,
          fontSize: "7pt",
          color: COLORS.textTertiary,
          marginTop: "8px",
          margin: "8px 0 0 0"
        },
        children: copyright
      }
    )
  ] });
}
var WhitepaperReport = Object.assign(WhitepaperReportRoot, {
  TitlePage,
  TableOfContents,
  Abstract,
  Section: Section3,
  Subsection,
  Paragraph: Paragraph2,
  PullQuote: PullQuote2,
  Figure,
  Callout,
  Footnotes,
  Bibliography: BibliographySection,
  AuthorBio,
  PageBreak: PageBreak3,
  Disclaimer: Disclaimer3
});

// src/templates/MarketInsights.tsx
import { jsx as jsx166, jsxs as jsxs149 } from "react/jsx-runtime";
function MarketInsightsRoot({
  confidentiality,
  children
}) {
  const confLabel = confidentiality ? getConfidentialityLabel(confidentiality) : "";
  return /* @__PURE__ */ jsxs149(
    "div",
    {
      className: "market-insights print-document",
      style: documentContainerStyle(),
      children: [
        confLabel && /* @__PURE__ */ jsx166(
          "div",
          {
            style: {
              ...metaTextStyle(),
              textAlign: "center",
              marginBottom: "16px",
              color: COLORS.accent,
              fontWeight: 600
            },
            children: confLabel
          }
        ),
        children
      ]
    }
  );
}
function Header({
  series,
  issue,
  title,
  subtitle,
  date,
  author,
  logoUrl
}) {
  return /* @__PURE__ */ jsxs149("div", { style: { marginBottom: "32px" }, children: [
    /* @__PURE__ */ jsxs149("div", { style: mastheadStyle(), children: [
      /* @__PURE__ */ jsx166("div", { children: logoUrl && /* @__PURE__ */ jsx166(
        "img",
        {
          src: logoUrl,
          alt: "Logo",
          style: { height: "18px" }
        }
      ) }),
      /* @__PURE__ */ jsxs149("div", { style: { textAlign: "right" }, children: [
        series && /* @__PURE__ */ jsxs149("div", { style: { ...metaTextStyle(), marginBottom: "2px" }, children: [
          series,
          issue ? ` | ${issue}` : ""
        ] }),
        /* @__PURE__ */ jsx166(
          "div",
          {
            style: {
              fontFamily: FONT_SANS,
              fontSize: "9pt",
              color: COLORS.textTertiary
            },
            children: date ?? formatDocumentDate()
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsx166(
      "h1",
      {
        style: {
          ...titleStyle(),
          fontSize: "24pt",
          marginBottom: "8px"
        },
        children: title
      }
    ),
    subtitle && /* @__PURE__ */ jsx166(
      "p",
      {
        style: {
          fontFamily: FONT_SERIF,
          fontSize: "12pt",
          lineHeight: 1.4,
          color: COLORS.textSecondary,
          margin: "0 0 8px 0"
        },
        children: subtitle
      }
    ),
    author && /* @__PURE__ */ jsxs149(
      "div",
      {
        style: {
          fontFamily: FONT_SANS,
          fontSize: "9pt",
          color: COLORS.textTertiary
        },
        children: [
          "By ",
          author
        ]
      }
    )
  ] });
}
function KeyTakeaways({ title = "Key Takeaways", items }) {
  return /* @__PURE__ */ jsxs149(
    "div",
    {
      style: {
        ...avoidBreakStyle(),
        background: COLORS.canvas,
        padding: "20px",
        marginBottom: "24px",
        borderLeft: `3px solid ${COLORS.accent}`
      },
      children: [
        /* @__PURE__ */ jsx166(
          "h2",
          {
            style: {
              fontFamily: FONT_SANS,
              fontSize: "10pt",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: COLORS.accent,
              margin: "0 0 12px 0"
            },
            children: title
          }
        ),
        /* @__PURE__ */ jsx166(
          "ul",
          {
            style: {
              margin: 0,
              padding: 0,
              listStyle: "none",
              display: "flex",
              flexDirection: "column",
              gap: "8px"
            },
            children: items.map((item, idx) => /* @__PURE__ */ jsxs149(
              "li",
              {
                style: {
                  fontFamily: FONT_SERIF,
                  fontSize: "10pt",
                  lineHeight: 1.5,
                  color: COLORS.text,
                  paddingLeft: "16px",
                  position: "relative"
                },
                children: [
                  /* @__PURE__ */ jsx166(
                    "span",
                    {
                      style: {
                        position: "absolute",
                        left: 0,
                        top: "3px",
                        width: "6px",
                        height: "6px",
                        borderRadius: "50%",
                        background: COLORS.accent
                      }
                    }
                  ),
                  item
                ]
              },
              idx
            ))
          }
        )
      ]
    }
  );
}
function MetricStrip2({ metrics }) {
  return /* @__PURE__ */ jsx166(
    "div",
    {
      style: {
        ...avoidBreakStyle(),
        display: "flex",
        gap: "0",
        marginBottom: "24px",
        borderTop: `0.5pt solid ${COLORS.rule}`,
        borderBottom: `0.5pt solid ${COLORS.rule}`
      },
      children: metrics.map((m, idx) => /* @__PURE__ */ jsxs149(
        "div",
        {
          style: {
            flex: 1,
            padding: "16px",
            borderRight: idx < metrics.length - 1 ? `0.5pt solid ${COLORS.ruleLight}` : "none",
            textAlign: "center"
          },
          children: [
            /* @__PURE__ */ jsx166(
              "div",
              {
                style: {
                  fontFamily: FONT_MONO,
                  fontSize: "18pt",
                  fontWeight: 400,
                  lineHeight: 1.1,
                  color: COLORS.text,
                  fontVariantNumeric: "tabular-nums"
                },
                children: m.value
              }
            ),
            /* @__PURE__ */ jsx166(
              "div",
              {
                style: {
                  fontFamily: FONT_SANS,
                  fontSize: "8pt",
                  color: COLORS.textTertiary,
                  marginTop: "4px"
                },
                children: m.label
              }
            ),
            m.change && /* @__PURE__ */ jsx166(
              "div",
              {
                style: {
                  fontFamily: FONT_MONO,
                  fontSize: "9pt",
                  color: m.trend === "up" ? COLORS.positive : m.trend === "down" ? COLORS.negative : COLORS.textTertiary,
                  marginTop: "2px"
                },
                children: m.change
              }
            )
          ]
        },
        idx
      ))
    }
  );
}
function Narrative({ heading, children }) {
  return /* @__PURE__ */ jsxs149("div", { style: { marginTop: "24px" }, children: [
    heading && /* @__PURE__ */ jsx166("h2", { style: sectionHeadingStyle(), children: heading }),
    /* @__PURE__ */ jsx166(
      "div",
      {
        style: {
          ...paragraphStyle(),
          lineHeight: 1.6
        },
        children
      }
    )
  ] });
}
var TREND_ARROWS = {
  up: "\u2191",
  down: "\u2193",
  flat: "\u2192"
};
function TrendHighlight({
  title,
  direction,
  value,
  change,
  commentary,
  period,
  source
}) {
  const trendColor = direction === "up" ? COLORS.positive : direction === "down" ? COLORS.negative : COLORS.textTertiary;
  return /* @__PURE__ */ jsxs149(
    "div",
    {
      style: {
        ...avoidBreakStyle(),
        padding: "20px",
        background: COLORS.canvas,
        marginTop: "16px",
        marginBottom: "16px"
      },
      children: [
        /* @__PURE__ */ jsxs149(
          "div",
          {
            style: {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: "12px"
            },
            children: [
              /* @__PURE__ */ jsxs149("div", { children: [
                /* @__PURE__ */ jsx166(
                  "h3",
                  {
                    style: {
                      fontFamily: FONT_SANS,
                      fontSize: "10pt",
                      fontWeight: 600,
                      color: COLORS.text,
                      margin: "0 0 4px 0"
                    },
                    children: title
                  }
                ),
                period && /* @__PURE__ */ jsx166(
                  "div",
                  {
                    style: {
                      fontFamily: FONT_SANS,
                      fontSize: "8pt",
                      color: COLORS.textTertiary
                    },
                    children: period
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs149("div", { style: { textAlign: "right" }, children: [
                /* @__PURE__ */ jsx166(
                  "div",
                  {
                    style: {
                      fontFamily: FONT_MONO,
                      fontSize: "20pt",
                      fontWeight: 400,
                      lineHeight: 1.1,
                      color: COLORS.text,
                      fontVariantNumeric: "tabular-nums"
                    },
                    children: value
                  }
                ),
                /* @__PURE__ */ jsxs149(
                  "div",
                  {
                    style: {
                      fontFamily: FONT_MONO,
                      fontSize: "10pt",
                      color: trendColor,
                      marginTop: "2px"
                    },
                    children: [
                      TREND_ARROWS[direction],
                      " ",
                      change
                    ]
                  }
                )
              ] })
            ]
          }
        ),
        /* @__PURE__ */ jsx166(
          "p",
          {
            style: {
              fontFamily: FONT_SERIF,
              fontSize: "10pt",
              lineHeight: 1.5,
              color: COLORS.textSecondary,
              margin: 0
            },
            children: commentary
          }
        ),
        source && /* @__PURE__ */ jsxs149(
          "div",
          {
            style: {
              fontFamily: FONT_SANS,
              fontSize: "7pt",
              color: COLORS.textTertiary,
              marginTop: "8px"
            },
            children: [
              "Source: ",
              source
            ]
          }
        )
      ]
    }
  );
}
function DataSnapshot({
  title,
  headers,
  rows,
  alignments,
  source,
  asOf
}) {
  return /* @__PURE__ */ jsxs149("div", { style: { ...avoidBreakStyle(), marginTop: "24px" }, children: [
    /* @__PURE__ */ jsxs149(
      "div",
      {
        style: {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: "4px"
        },
        children: [
          /* @__PURE__ */ jsx166(
            "h3",
            {
              style: {
                fontFamily: FONT_SANS,
                fontSize: "10pt",
                fontWeight: 600,
                color: COLORS.text,
                margin: 0
              },
              children: title
            }
          ),
          asOf && /* @__PURE__ */ jsxs149(
            "span",
            {
              style: {
                fontFamily: FONT_SANS,
                fontSize: "8pt",
                color: COLORS.textTertiary
              },
              children: [
                "As of ",
                asOf
              ]
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsxs149("table", { style: { ...tableStyle(), fontSize: "9pt" }, children: [
      /* @__PURE__ */ jsx166("thead", { children: /* @__PURE__ */ jsx166("tr", { children: headers.map((h, i) => /* @__PURE__ */ jsx166("th", { style: thStyle(alignments?.[i] ?? (i === 0 ? "left" : "right")), children: h }, i)) }) }),
      /* @__PURE__ */ jsx166("tbody", { children: rows.map((row, rIdx) => /* @__PURE__ */ jsx166(
        "tr",
        {
          style: row.highlight ? { background: COLORS.canvas, fontWeight: 600 } : void 0,
          children: row.cells.map((cell, cIdx) => /* @__PURE__ */ jsx166(
            "td",
            {
              style: {
                ...tdStyle(alignments?.[cIdx] ?? (cIdx === 0 ? "left" : "right")),
                fontFamily: cIdx === 0 ? FONT_SANS : FONT_MONO,
                fontSize: "9pt",
                fontWeight: row.highlight ? 600 : 400
              },
              children: cell
            },
            cIdx
          ))
        },
        rIdx
      )) })
    ] }),
    source && /* @__PURE__ */ jsxs149(
      "div",
      {
        style: {
          fontFamily: FONT_SANS,
          fontSize: "7pt",
          color: COLORS.textTertiary,
          marginTop: "8px"
        },
        children: [
          "Source: ",
          source
        ]
      }
    )
  ] });
}
var OUTLOOK_LABELS = {
  positive: { label: "POSITIVE", color: COLORS.positive },
  neutral: { label: "NEUTRAL", color: COLORS.textTertiary },
  cautious: { label: "CAUTIOUS", color: COLORS.warning },
  negative: { label: "NEGATIVE", color: COLORS.negative }
};
function AnalystNote({
  title = "Analyst Commentary",
  analyst,
  children,
  outlook
}) {
  const outlookConfig = outlook ? OUTLOOK_LABELS[outlook] : null;
  return /* @__PURE__ */ jsxs149(
    "div",
    {
      style: {
        ...avoidBreakStyle(),
        marginTop: "24px",
        padding: "20px",
        borderTop: `2px solid ${COLORS.text}`,
        background: COLORS.surface
      },
      children: [
        /* @__PURE__ */ jsxs149(
          "div",
          {
            style: {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px"
            },
            children: [
              /* @__PURE__ */ jsxs149("div", { children: [
                /* @__PURE__ */ jsx166(
                  "h3",
                  {
                    style: {
                      fontFamily: FONT_SANS,
                      fontSize: "10pt",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: COLORS.text,
                      margin: 0
                    },
                    children: title
                  }
                ),
                analyst && /* @__PURE__ */ jsx166(
                  "div",
                  {
                    style: {
                      fontFamily: FONT_SANS,
                      fontSize: "8pt",
                      color: COLORS.textTertiary,
                      marginTop: "2px"
                    },
                    children: analyst
                  }
                )
              ] }),
              outlookConfig && /* @__PURE__ */ jsxs149(
                "div",
                {
                  style: {
                    fontFamily: FONT_SANS,
                    fontSize: "8pt",
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    color: outlookConfig.color,
                    padding: "4px 8px",
                    border: `1px solid ${outlookConfig.color}`
                  },
                  children: [
                    "OUTLOOK: ",
                    outlookConfig.label
                  ]
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx166(
          "div",
          {
            style: {
              fontFamily: FONT_SERIF,
              fontSize: "10pt",
              lineHeight: 1.6,
              color: COLORS.textSecondary
            },
            children
          }
        )
      ]
    }
  );
}
function Methodology({ children, sources }) {
  return /* @__PURE__ */ jsxs149(
    "div",
    {
      style: {
        ...avoidBreakStyle(),
        marginTop: "32px",
        paddingTop: "16px",
        borderTop: `0.5pt solid ${COLORS.rule}`
      },
      children: [
        /* @__PURE__ */ jsx166(
          "h3",
          {
            style: {
              fontFamily: FONT_SANS,
              fontSize: "8pt",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: COLORS.textTertiary,
              margin: "0 0 8px 0"
            },
            children: "Methodology"
          }
        ),
        /* @__PURE__ */ jsx166(
          "div",
          {
            style: {
              fontFamily: FONT_SANS,
              fontSize: "8pt",
              lineHeight: 1.5,
              color: COLORS.textTertiary
            },
            children
          }
        ),
        sources && sources.length > 0 && /* @__PURE__ */ jsxs149("div", { style: { marginTop: "8px" }, children: [
          /* @__PURE__ */ jsxs149(
            "span",
            {
              style: {
                fontFamily: FONT_SANS,
                fontSize: "7pt",
                fontWeight: 600,
                color: COLORS.textTertiary
              },
              children: [
                "Data Sources:",
                " "
              ]
            }
          ),
          /* @__PURE__ */ jsx166(
            "span",
            {
              style: {
                fontFamily: FONT_SANS,
                fontSize: "7pt",
                color: COLORS.textTertiary
              },
              children: sources.join(" | ")
            }
          )
        ] })
      ]
    }
  );
}
function PageBreak4() {
  return /* @__PURE__ */ jsx166("div", { style: pageBreakStyle(), "aria-hidden": "true" });
}
function Footer2({
  companyName = "Aaim",
  disclaimer,
  contact
}) {
  return /* @__PURE__ */ jsxs149("div", { style: footerStyle(), children: [
    /* @__PURE__ */ jsx166(
      "p",
      {
        style: {
          fontFamily: FONT_SANS,
          fontSize: "7pt",
          color: COLORS.textTertiary,
          lineHeight: 1.4,
          margin: "0 0 4px 0",
          textAlign: "left"
        },
        children: disclaimer ?? `This report is provided by ${companyName} for informational purposes only. It does not constitute investment advice or a recommendation to buy, sell, or hold any security. Past performance is not indicative of future results.`
      }
    ),
    contact && /* @__PURE__ */ jsx166(
      "p",
      {
        style: {
          fontFamily: FONT_SANS,
          fontSize: "7pt",
          color: COLORS.textTertiary,
          margin: 0,
          textAlign: "left"
        },
        children: contact
      }
    )
  ] });
}
var MarketInsights = Object.assign(MarketInsightsRoot, {
  Header,
  KeyTakeaways,
  MetricStrip: MetricStrip2,
  Narrative,
  TrendHighlight,
  DataSnapshot,
  AnalystNote,
  Methodology,
  PageBreak: PageBreak4,
  Footer: Footer2
});

// src/templates/ExecutiveBrief.tsx
import { jsx as jsx167, jsxs as jsxs150 } from "react/jsx-runtime";
function ExecutiveBriefRoot({
  title,
  subject,
  date,
  confidentiality = "confidential",
  preparedBy,
  preparedFor,
  logoUrl = "https://cdn.aaim.app/logos/aaim-master-grey.svg",
  children
}) {
  const confLabel = getConfidentialityLabel(confidentiality);
  const displayDate = date ?? formatDocumentDate();
  return /* @__PURE__ */ jsxs150("article", { style: documentContainerStyle(), children: [
    /* @__PURE__ */ jsx167("div", { className: "no-print", style: { position: "fixed", top: 20, right: 20, zIndex: 100 }, children: /* @__PURE__ */ jsx167(
      "button",
      {
        onClick: () => window.print(),
        style: {
          padding: "8px 16px",
          background: COLORS.textSecondary,
          color: COLORS.surface,
          border: "none",
          borderRadius: "4px",
          fontFamily: FONT_SANS,
          fontSize: "14px",
          cursor: "pointer"
        },
        children: "Print / Export PDF"
      }
    ) }),
    /* @__PURE__ */ jsxs150("header", { style: mastheadStyle(), children: [
      /* @__PURE__ */ jsxs150("div", { children: [
        /* @__PURE__ */ jsx167(
          "img",
          {
            src: logoUrl,
            alt: "Aaim",
            style: { height: "24px", marginBottom: "12px" }
          }
        ),
        /* @__PURE__ */ jsx167(
          "h1",
          {
            style: {
              fontSize: "18pt",
              fontWeight: 600,
              lineHeight: 1.15,
              margin: "0 0 4px 0",
              letterSpacing: "-0.02em",
              color: COLORS.text
            },
            children: title
          }
        ),
        subject && /* @__PURE__ */ jsx167(
          "p",
          {
            style: {
              fontSize: "11pt",
              fontStyle: "italic",
              color: COLORS.textSecondary,
              margin: "4px 0 0 0"
            },
            children: subject
          }
        )
      ] }),
      /* @__PURE__ */ jsxs150("div", { style: { textAlign: "right" }, children: [
        confLabel && /* @__PURE__ */ jsx167("div", { style: { ...metaTextStyle(), marginBottom: "4px", color: COLORS.accent }, children: confLabel }),
        /* @__PURE__ */ jsx167("div", { style: { ...metaTextStyle(), letterSpacing: "0" }, children: displayDate }),
        preparedFor && /* @__PURE__ */ jsxs150("div", { style: { fontFamily: FONT_SANS, fontSize: "9pt", color: COLORS.textTertiary, marginTop: "4px" }, children: [
          "Prepared for: ",
          preparedFor
        ] }),
        preparedBy && /* @__PURE__ */ jsxs150("div", { style: { fontFamily: FONT_SANS, fontSize: "9pt", color: COLORS.textTertiary, marginTop: "2px" }, children: [
          "By: ",
          preparedBy
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx167("main", { children }),
    /* @__PURE__ */ jsxs150(
      "footer",
      {
        style: {
          marginTop: "32px",
          paddingTop: "12px",
          borderTop: `0.5pt solid ${COLORS.rule}`,
          textAlign: "center",
          fontFamily: FONT_SANS,
          fontSize: "8pt",
          color: COLORS.textTertiary
        },
        children: [
          confLabel && `${confLabel}  \xB7  `,
          "Aaim, Inc. | Executive Brief | ",
          displayDate
        ]
      }
    )
  ] });
}
function KeyMetrics({ metrics }) {
  return /* @__PURE__ */ jsx167(
    "div",
    {
      style: {
        ...avoidBreakStyle(),
        display: "flex",
        flexWrap: "wrap",
        gap: "32px",
        padding: "20px 0",
        margin: "0 0 24px 0",
        borderBottom: `0.25pt solid ${COLORS.ruleLight}`
      },
      children: metrics.map((metric, idx) => /* @__PURE__ */ jsxs150("div", { style: { minWidth: "100px" }, children: [
        /* @__PURE__ */ jsx167("div", { style: metricValueStyle(), children: metric.value }),
        /* @__PURE__ */ jsx167("div", { style: metricLabelStyle(), children: metric.label }),
        metric.change && /* @__PURE__ */ jsx167(
          "div",
          {
            style: {
              fontFamily: FONT_MONO,
              fontSize: "9pt",
              marginTop: "2px",
              color: metric.trend === "up" ? COLORS.positive : metric.trend === "down" ? COLORS.negative : COLORS.textTertiary
            },
            children: metric.change
          }
        )
      ] }, idx))
    }
  );
}
function Summary({ heading = "Executive Summary", children }) {
  return /* @__PURE__ */ jsxs150("section", { children: [
    /* @__PURE__ */ jsx167("h2", { style: sectionHeadingStyle(), children: heading }),
    /* @__PURE__ */ jsx167("div", { style: paragraphStyle(), children })
  ] });
}
function FindingsTable({
  heading = "Key Findings",
  headers,
  rows,
  alignments,
  caption
}) {
  return /* @__PURE__ */ jsxs150("section", { style: avoidBreakStyle(), children: [
    /* @__PURE__ */ jsx167("h2", { style: sectionHeadingStyle(), children: heading }),
    /* @__PURE__ */ jsxs150("table", { style: tableStyle(), children: [
      /* @__PURE__ */ jsx167("thead", { children: /* @__PURE__ */ jsx167("tr", { children: headers.map((header, idx) => /* @__PURE__ */ jsx167("th", { style: thStyle(alignments?.[idx] ?? (idx === 0 ? "left" : "right")), children: header }, idx)) }) }),
      /* @__PURE__ */ jsx167("tbody", { children: rows.map((row, rowIdx) => /* @__PURE__ */ jsx167("tr", { children: row.map((cell, cellIdx) => /* @__PURE__ */ jsx167(
        "td",
        {
          style: {
            ...tdStyle(alignments?.[cellIdx] ?? (cellIdx === 0 ? "left" : "right")),
            fontWeight: cellIdx === 0 ? 500 : 400
          },
          children: cell
        },
        cellIdx
      )) }, rowIdx)) })
    ] }),
    caption && /* @__PURE__ */ jsx167("div", { style: { fontFamily: FONT_SANS, fontSize: "8pt", color: COLORS.textTertiary, marginTop: "8px" }, children: caption })
  ] });
}
function Recommendation({
  heading = "Recommendation",
  priority = "standard",
  children
}) {
  const priorityColors = {
    critical: COLORS.accent,
    high: COLORS.warning,
    standard: COLORS.rule
  };
  return /* @__PURE__ */ jsxs150(
    "section",
    {
      style: {
        ...avoidBreakStyle(),
        marginTop: "24px",
        padding: "16px 0 16px 16px",
        borderLeft: `3pt solid ${priorityColors[priority]}`
      },
      children: [
        /* @__PURE__ */ jsxs150("h2", { style: { ...sectionHeadingStyle(), marginTop: 0 }, children: [
          heading,
          priority !== "standard" && /* @__PURE__ */ jsx167(
            "span",
            {
              style: {
                fontFamily: FONT_SANS,
                fontSize: "8pt",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                marginLeft: "12px",
                color: priorityColors[priority]
              },
              children: priority
            }
          )
        ] }),
        /* @__PURE__ */ jsx167("div", { style: paragraphStyle(), children })
      ]
    }
  );
}
function NextSteps({ heading = "Next Steps", items }) {
  return /* @__PURE__ */ jsxs150("section", { style: { marginTop: "24px" }, children: [
    /* @__PURE__ */ jsx167("h2", { style: sectionHeadingStyle(), children: heading }),
    /* @__PURE__ */ jsxs150("table", { style: tableStyle(), children: [
      /* @__PURE__ */ jsx167("thead", { children: /* @__PURE__ */ jsxs150("tr", { children: [
        /* @__PURE__ */ jsx167("th", { style: thStyle("left"), children: "Action" }),
        items.some((i) => i.owner) && /* @__PURE__ */ jsx167("th", { style: thStyle("left"), children: "Owner" }),
        items.some((i) => i.timeline) && /* @__PURE__ */ jsx167("th", { style: thStyle("left"), children: "Timeline" })
      ] }) }),
      /* @__PURE__ */ jsx167("tbody", { children: items.map((item, idx) => /* @__PURE__ */ jsxs150("tr", { children: [
        /* @__PURE__ */ jsx167("td", { style: { ...tdStyle("left"), fontWeight: 500 }, children: item.action }),
        items.some((i) => i.owner) && /* @__PURE__ */ jsx167("td", { style: { ...tdStyle("left"), fontFamily: FONT_SANS, fontSize: "9pt" }, children: item.owner ?? "--" }),
        items.some((i) => i.timeline) && /* @__PURE__ */ jsx167("td", { style: { ...tdStyle("left"), fontFamily: FONT_SANS, fontSize: "9pt" }, children: item.timeline ?? "--" })
      ] }, idx)) })
    ] })
  ] });
}
function SignOff({ name, title, organization }) {
  return /* @__PURE__ */ jsxs150(
    "div",
    {
      style: {
        marginTop: "32px",
        fontFamily: FONT_SANS,
        fontSize: "10pt"
      },
      children: [
        /* @__PURE__ */ jsx167("div", { style: { fontWeight: 600, color: COLORS.text }, children: name }),
        /* @__PURE__ */ jsx167("div", { style: { color: COLORS.textSecondary }, children: title }),
        organization && /* @__PURE__ */ jsx167("div", { style: { color: COLORS.textTertiary }, children: organization })
      ]
    }
  );
}
function BriefPageBreak() {
  return /* @__PURE__ */ jsx167("div", { style: pageBreakStyle() });
}
var ExecutiveBrief = Object.assign(ExecutiveBriefRoot, {
  KeyMetrics,
  Summary,
  FindingsTable,
  Recommendation,
  NextSteps,
  SignOff,
  PageBreak: BriefPageBreak
});

// src/templates/PressRelease.tsx
import { Fragment as Fragment37, jsx as jsx168, jsxs as jsxs151 } from "react/jsx-runtime";
var PR_BODY = {
  fontFamily: FONT_SERIF,
  fontSize: "11pt",
  fontWeight: 400,
  lineHeight: 1.6,
  color: COLORS.text,
  margin: "0 0 16px 0"
};
var PR_LABEL = {
  fontFamily: FONT_SANS,
  fontSize: "9pt",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: COLORS.textTertiary
};
function PressReleaseRoot({
  companyName = "Aaim, Inc.",
  embargo = "immediate",
  embargoDate,
  confidentiality = "public",
  children
}) {
  const confLabel = getConfidentialityLabel(confidentiality);
  return /* @__PURE__ */ jsxs151("article", { style: documentContainerStyle(), children: [
    /* @__PURE__ */ jsx168("div", { className: "no-print", style: { position: "fixed", top: 20, right: 20, zIndex: 100 }, children: /* @__PURE__ */ jsx168(
      "button",
      {
        onClick: () => window.print(),
        style: {
          padding: "8px 16px",
          background: COLORS.textSecondary,
          color: COLORS.surface,
          border: "none",
          borderRadius: "4px",
          fontFamily: FONT_SANS,
          fontSize: "14px",
          cursor: "pointer"
        },
        children: "Print / Export PDF"
      }
    ) }),
    /* @__PURE__ */ jsx168(
      "div",
      {
        style: {
          ...PR_LABEL,
          marginBottom: "24px",
          color: embargo === "embargoed" ? COLORS.accent : COLORS.textTertiary
        },
        children: embargo === "embargoed" ? `EMBARGOED UNTIL ${embargoDate ?? "TBD"}` : "FOR IMMEDIATE RELEASE"
      }
    ),
    children,
    /* @__PURE__ */ jsx168(
      "div",
      {
        style: {
          textAlign: "center",
          margin: "48px 0 24px 0",
          fontFamily: FONT_SANS,
          fontSize: "14pt",
          letterSpacing: "0.3em",
          color: COLORS.textTertiary
        },
        children: "# # #"
      }
    ),
    /* @__PURE__ */ jsxs151(
      "footer",
      {
        style: {
          marginTop: "24px",
          paddingTop: "12px",
          borderTop: `0.5pt solid ${COLORS.rule}`,
          textAlign: "center",
          fontFamily: FONT_SANS,
          fontSize: "8pt",
          color: COLORS.textTertiary
        },
        children: [
          confLabel && `${confLabel}  \xB7  `,
          companyName,
          " | 99 Wall Street, Suite 1610, New York, NY 10005"
        ]
      }
    )
  ] });
}
function Masthead({
  logoUrl = "https://cdn.aaim.app/logos/aaim-master-grey.svg",
  companyName = "Aaim",
  tagline
}) {
  return /* @__PURE__ */ jsxs151(
    "div",
    {
      style: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingBottom: "20px",
        marginBottom: "24px",
        borderBottom: `0.5pt solid ${COLORS.rule}`
      },
      children: [
        /* @__PURE__ */ jsx168(
          "img",
          {
            src: logoUrl,
            alt: companyName,
            style: { height: "28px" }
          }
        ),
        tagline && /* @__PURE__ */ jsx168(
          "span",
          {
            style: {
              fontFamily: FONT_SANS,
              fontSize: "10pt",
              color: COLORS.textTertiary,
              fontStyle: "italic"
            },
            children: tagline
          }
        )
      ]
    }
  );
}
function Headline({ headline, subheadline }) {
  return /* @__PURE__ */ jsxs151("div", { style: { marginBottom: "24px" }, children: [
    /* @__PURE__ */ jsx168(
      "h1",
      {
        style: {
          fontFamily: FONT_SERIF,
          fontSize: "22pt",
          fontWeight: 600,
          lineHeight: 1.15,
          letterSpacing: "-0.02em",
          color: COLORS.text,
          margin: "0 0 8px 0"
        },
        children: headline
      }
    ),
    subheadline && /* @__PURE__ */ jsx168(
      "p",
      {
        style: {
          fontFamily: FONT_SERIF,
          fontSize: "13pt",
          fontWeight: 400,
          lineHeight: 1.4,
          color: COLORS.textSecondary,
          margin: 0
        },
        children: subheadline
      }
    )
  ] });
}
function Dateline({ city, state, date }) {
  const displayDate = date ?? formatDocumentDate();
  const location = state ? `${city.toUpperCase()}, ${state}` : city.toUpperCase();
  return /* @__PURE__ */ jsxs151("p", { style: PR_BODY, children: [
    /* @__PURE__ */ jsx168("strong", { children: location }),
    " -- ",
    displayDate,
    " --",
    " "
  ] });
}
function Body({ children }) {
  return /* @__PURE__ */ jsx168("div", { style: PR_BODY, children });
}
function Quote({ quote, author, authorTitle, authorCompany }) {
  const attribution = authorCompany ? `${author}, ${authorTitle}, ${authorCompany}` : `${author}, ${authorTitle}`;
  return /* @__PURE__ */ jsxs151(
    "blockquote",
    {
      style: {
        margin: "24px 0",
        padding: "0 0 0 16px",
        borderLeft: `2pt solid ${COLORS.rule}`
      },
      children: [
        /* @__PURE__ */ jsxs151(
          "p",
          {
            style: {
              ...PR_BODY,
              fontStyle: "italic",
              margin: "0 0 8px 0"
            },
            children: [
              "\u201C",
              quote,
              "\u201D"
            ]
          }
        ),
        /* @__PURE__ */ jsxs151(
          "cite",
          {
            style: {
              fontFamily: FONT_SANS,
              fontSize: "10pt",
              fontWeight: 500,
              fontStyle: "normal",
              color: COLORS.textSecondary
            },
            children: [
              "-- ",
              attribution
            ]
          }
        )
      ]
    }
  );
}
function Boilerplate({
  title = "About Aaim",
  description,
  website
}) {
  return /* @__PURE__ */ jsxs151(
    "div",
    {
      style: {
        marginTop: "32px",
        paddingTop: "16px",
        borderTop: `0.5pt solid ${COLORS.ruleLight}`
      },
      children: [
        /* @__PURE__ */ jsx168(
          "h3",
          {
            style: {
              ...PR_LABEL,
              marginBottom: "8px"
            },
            children: title
          }
        ),
        /* @__PURE__ */ jsxs151("p", { style: { ...PR_BODY, fontSize: "10pt", color: COLORS.textSecondary }, children: [
          description,
          website && /* @__PURE__ */ jsxs151(Fragment37, { children: [
            " ",
            "For more information, visit",
            " ",
            /* @__PURE__ */ jsx168("span", { style: { fontWeight: 500, color: COLORS.text }, children: website }),
            "."
          ] })
        ] })
      ]
    }
  );
}
function ContactBlock({
  heading = "Media Contact",
  contacts
}) {
  return /* @__PURE__ */ jsxs151("div", { style: { marginTop: "24px" }, children: [
    /* @__PURE__ */ jsx168("h3", { style: { ...PR_LABEL, marginBottom: "12px" }, children: heading }),
    contacts.map((contact, idx) => /* @__PURE__ */ jsxs151(
      "div",
      {
        style: {
          marginBottom: "8px",
          fontFamily: FONT_SANS,
          fontSize: "10pt",
          color: COLORS.textSecondary
        },
        children: [
          /* @__PURE__ */ jsxs151("div", { style: { fontWeight: 500, color: COLORS.text }, children: [
            contact.name,
            contact.title && `, ${contact.title}`
          ] }),
          /* @__PURE__ */ jsx168("div", { children: contact.email }),
          contact.phone && /* @__PURE__ */ jsx168("div", { children: contact.phone })
        ]
      },
      idx
    ))
  ] });
}
function PRPageBreak() {
  return /* @__PURE__ */ jsx168("div", { style: pageBreakStyle() });
}
var PressRelease = Object.assign(PressReleaseRoot, {
  Masthead,
  Headline,
  Dateline,
  Body,
  Quote,
  Boilerplate,
  ContactBlock,
  PageBreak: PRPageBreak
});

// src/templates/InvestorDeck.tsx
import { jsx as jsx169, jsxs as jsxs152 } from "react/jsx-runtime";
var slideStyle = {
  minHeight: "7.5in",
  padding: "48px",
  display: "flex",
  flexDirection: "column",
  pageBreakAfter: "always",
  breakAfter: "page",
  fontFamily: FONT_SERIF,
  color: COLORS.text,
  background: COLORS.surface,
  position: "relative"
};
var slideHeaderStyle = {
  fontFamily: FONT_SANS,
  fontSize: "8pt",
  letterSpacing: "0.15em",
  textTransform: "uppercase",
  color: COLORS.textTertiary,
  marginBottom: "16px"
};
var slideTitleStyle2 = {
  fontFamily: FONT_SERIF,
  fontSize: "24pt",
  fontWeight: 600,
  lineHeight: 1.15,
  letterSpacing: "-0.02em",
  color: COLORS.text,
  margin: "0 0 8px 0"
};
var slideSubtitleStyle = {
  fontFamily: FONT_SERIF,
  fontSize: "12pt",
  fontWeight: 400,
  lineHeight: 1.3,
  color: COLORS.textSecondary,
  margin: "0 0 24px 0"
};
var slideBodyStyle2 = {
  fontFamily: FONT_SERIF,
  fontSize: "11pt",
  lineHeight: 1.5,
  color: COLORS.text,
  margin: "0 0 12px 0"
};
var slideFooterStyle2 = {
  marginTop: "auto",
  paddingTop: "16px",
  fontFamily: FONT_SANS,
  fontSize: "8pt",
  color: COLORS.textTertiary,
  display: "flex",
  justifyContent: "space-between"
};
function InvestorDeckRoot({
  children
}) {
  return /* @__PURE__ */ jsxs152(
    "div",
    {
      style: {
        maxWidth: "8.5in",
        margin: "0 auto",
        background: COLORS.surface
      },
      children: [
        /* @__PURE__ */ jsx169("div", { className: "no-print", style: { position: "fixed", top: 20, right: 20, zIndex: 100 }, children: /* @__PURE__ */ jsx169(
          "button",
          {
            onClick: () => window.print(),
            style: {
              padding: "8px 16px",
              background: COLORS.textSecondary,
              color: COLORS.surface,
              border: "none",
              borderRadius: "4px",
              fontFamily: FONT_SANS,
              fontSize: "14px",
              cursor: "pointer"
            },
            children: "Print / Export PDF"
          }
        ) }),
        children
      ]
    }
  );
}
function TitleSlide({
  companyName = "Aaim",
  tagline,
  subtitle,
  presenter,
  presenterTitle,
  date,
  confidentiality = "confidential",
  logoUrl = "https://cdn.aaim.app/logos/aaim-master-grey.svg"
}) {
  const confLabel = getConfidentialityLabel(confidentiality);
  const displayDate = date ?? formatDocumentDate();
  return /* @__PURE__ */ jsxs152(
    "div",
    {
      style: {
        ...slideStyle,
        justifyContent: "center",
        alignItems: "flex-start"
      },
      children: [
        confLabel && /* @__PURE__ */ jsx169(
          "div",
          {
            style: {
              position: "absolute",
              top: "24px",
              right: "48px",
              ...slideHeaderStyle
            },
            children: confLabel
          }
        ),
        /* @__PURE__ */ jsx169(
          "img",
          {
            src: logoUrl,
            alt: companyName,
            style: { height: "36px", marginBottom: "48px" }
          }
        ),
        /* @__PURE__ */ jsx169(
          "h1",
          {
            style: {
              fontFamily: FONT_SERIF,
              fontSize: "32pt",
              fontWeight: 600,
              lineHeight: 1.1,
              letterSpacing: "-0.03em",
              color: COLORS.text,
              margin: "0 0 16px 0",
              maxWidth: "6in"
            },
            children: tagline
          }
        ),
        subtitle && /* @__PURE__ */ jsx169(
          "p",
          {
            style: {
              fontFamily: FONT_SERIF,
              fontSize: "14pt",
              fontWeight: 400,
              lineHeight: 1.3,
              color: COLORS.textSecondary,
              margin: "0 0 0 0",
              maxWidth: "5in"
            },
            children: subtitle
          }
        ),
        /* @__PURE__ */ jsxs152("div", { style: slideFooterStyle2, children: [
          /* @__PURE__ */ jsx169("div", { children: presenter && /* @__PURE__ */ jsxs152("span", { style: { fontWeight: 500, color: COLORS.textSecondary }, children: [
            presenter,
            presenterTitle && `, ${presenterTitle}`
          ] }) }),
          /* @__PURE__ */ jsx169("div", { children: displayDate })
        ] })
      ]
    }
  );
}
function MarketSize({
  tam,
  tamDescription,
  sam,
  samDescription,
  som,
  somDescription,
  narrative,
  source
}) {
  return /* @__PURE__ */ jsxs152("div", { style: slideStyle, children: [
    /* @__PURE__ */ jsx169("div", { style: slideHeaderStyle, children: "Market Opportunity" }),
    /* @__PURE__ */ jsx169("h2", { style: slideTitleStyle2, children: "Market Size" }),
    narrative && /* @__PURE__ */ jsx169("p", { style: slideBodyStyle2, children: narrative }),
    /* @__PURE__ */ jsxs152(
      "div",
      {
        style: {
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "32px",
          margin: "32px 0",
          flex: 1,
          alignContent: "center"
        },
        children: [
          /* @__PURE__ */ jsxs152("div", { style: { textAlign: "center" }, children: [
            /* @__PURE__ */ jsx169(
              "div",
              {
                style: {
                  fontFamily: FONT_SERIF,
                  fontSize: "36pt",
                  fontWeight: 400,
                  lineHeight: 1,
                  letterSpacing: "-0.03em",
                  color: COLORS.text,
                  marginBottom: "8px"
                },
                children: tam
              }
            ),
            /* @__PURE__ */ jsx169(
              "div",
              {
                style: {
                  fontFamily: FONT_SANS,
                  fontSize: "10pt",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: COLORS.textSecondary,
                  marginBottom: "8px"
                },
                children: "TAM"
              }
            ),
            /* @__PURE__ */ jsx169(
              "div",
              {
                style: {
                  fontFamily: FONT_SERIF,
                  fontSize: "10pt",
                  color: COLORS.textSecondary,
                  lineHeight: 1.4
                },
                children: tamDescription
              }
            )
          ] }),
          /* @__PURE__ */ jsxs152("div", { style: { textAlign: "center" }, children: [
            /* @__PURE__ */ jsx169(
              "div",
              {
                style: {
                  fontFamily: FONT_SERIF,
                  fontSize: "36pt",
                  fontWeight: 400,
                  lineHeight: 1,
                  letterSpacing: "-0.03em",
                  color: COLORS.text,
                  marginBottom: "8px"
                },
                children: sam
              }
            ),
            /* @__PURE__ */ jsx169(
              "div",
              {
                style: {
                  fontFamily: FONT_SANS,
                  fontSize: "10pt",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: COLORS.textSecondary,
                  marginBottom: "8px"
                },
                children: "SAM"
              }
            ),
            /* @__PURE__ */ jsx169(
              "div",
              {
                style: {
                  fontFamily: FONT_SERIF,
                  fontSize: "10pt",
                  color: COLORS.textSecondary,
                  lineHeight: 1.4
                },
                children: samDescription
              }
            )
          ] }),
          /* @__PURE__ */ jsxs152("div", { style: { textAlign: "center" }, children: [
            /* @__PURE__ */ jsx169(
              "div",
              {
                style: {
                  fontFamily: FONT_SERIF,
                  fontSize: "36pt",
                  fontWeight: 400,
                  lineHeight: 1,
                  letterSpacing: "-0.03em",
                  color: COLORS.text,
                  marginBottom: "8px"
                },
                children: som
              }
            ),
            /* @__PURE__ */ jsx169(
              "div",
              {
                style: {
                  fontFamily: FONT_SANS,
                  fontSize: "10pt",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: COLORS.textSecondary,
                  marginBottom: "8px"
                },
                children: "SOM"
              }
            ),
            /* @__PURE__ */ jsx169(
              "div",
              {
                style: {
                  fontFamily: FONT_SERIF,
                  fontSize: "10pt",
                  color: COLORS.textSecondary,
                  lineHeight: 1.4
                },
                children: somDescription
              }
            )
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsxs152("div", { style: slideFooterStyle2, children: [
      source && /* @__PURE__ */ jsxs152("span", { children: [
        "Source: ",
        source
      ] }),
      /* @__PURE__ */ jsx169("span", {})
    ] })
  ] });
}
function BusinessModel({
  narrative,
  revenueStreams,
  metrics,
  unitEconomics
}) {
  return /* @__PURE__ */ jsxs152("div", { style: slideStyle, children: [
    /* @__PURE__ */ jsx169("div", { style: slideHeaderStyle, children: "How We Make Money" }),
    /* @__PURE__ */ jsx169("h2", { style: slideTitleStyle2, children: "Business Model" }),
    /* @__PURE__ */ jsx169("p", { style: slideBodyStyle2, children: narrative }),
    revenueStreams && revenueStreams.length > 0 && /* @__PURE__ */ jsx169("div", { style: { margin: "16px 0" }, children: revenueStreams.map((stream, idx) => /* @__PURE__ */ jsxs152(
      "div",
      {
        style: {
          display: "flex",
          alignItems: "baseline",
          gap: "16px",
          marginBottom: "12px",
          paddingLeft: "16px",
          borderLeft: `2pt solid ${COLORS.rule}`
        },
        children: [
          /* @__PURE__ */ jsxs152("div", { style: { flex: 1 }, children: [
            /* @__PURE__ */ jsx169("div", { style: { fontWeight: 600, fontSize: "11pt", marginBottom: "2px" }, children: stream.name }),
            /* @__PURE__ */ jsx169("div", { style: { fontSize: "10pt", color: COLORS.textSecondary, lineHeight: 1.4 }, children: stream.description })
          ] }),
          stream.contribution && /* @__PURE__ */ jsx169(
            "div",
            {
              style: {
                fontFamily: FONT_MONO,
                fontSize: "14pt",
                fontWeight: 400,
                color: COLORS.text,
                minWidth: "60px",
                textAlign: "right"
              },
              children: stream.contribution
            }
          )
        ]
      },
      idx
    )) }),
    metrics && metrics.length > 0 && /* @__PURE__ */ jsx169("div", { style: { display: "flex", flexWrap: "wrap", gap: "32px", margin: "24px 0" }, children: metrics.map((metric, idx) => /* @__PURE__ */ jsxs152("div", { style: { minWidth: "100px" }, children: [
      /* @__PURE__ */ jsx169(
        "div",
        {
          style: {
            fontFamily: FONT_SERIF,
            fontSize: "22pt",
            fontWeight: 400,
            lineHeight: 1.1,
            letterSpacing: "-0.02em"
          },
          children: metric.value
        }
      ),
      /* @__PURE__ */ jsx169("div", { style: { fontSize: "9pt", color: COLORS.textSecondary, marginTop: "4px" }, children: metric.label })
    ] }, idx)) }),
    unitEconomics && unitEconomics.length > 0 && /* @__PURE__ */ jsx169(
      "div",
      {
        style: {
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
          gap: "16px",
          marginTop: "16px",
          padding: "16px",
          background: COLORS.canvas
        },
        children: unitEconomics.map((item, idx) => /* @__PURE__ */ jsxs152("div", { children: [
          /* @__PURE__ */ jsx169(
            "div",
            {
              style: {
                fontFamily: FONT_MONO,
                fontSize: "14pt",
                fontWeight: 400,
                color: COLORS.text
              },
              children: item.value
            }
          ),
          /* @__PURE__ */ jsx169("div", { style: { fontFamily: FONT_SANS, fontSize: "9pt", color: COLORS.textTertiary }, children: item.metric })
        ] }, idx))
      }
    ),
    /* @__PURE__ */ jsxs152("div", { style: slideFooterStyle2, children: [
      /* @__PURE__ */ jsx169("span", {}),
      /* @__PURE__ */ jsx169("span", {})
    ] })
  ] });
}
function Financials({
  narrative,
  projections,
  metrics,
  fundingHistory,
  currentRaise
}) {
  return /* @__PURE__ */ jsxs152("div", { style: slideStyle, children: [
    /* @__PURE__ */ jsx169("div", { style: slideHeaderStyle, children: "Financial Overview" }),
    /* @__PURE__ */ jsx169("h2", { style: slideTitleStyle2, children: "Financials" }),
    narrative && /* @__PURE__ */ jsx169("p", { style: slideBodyStyle2, children: narrative }),
    metrics && metrics.length > 0 && /* @__PURE__ */ jsx169("div", { style: { display: "flex", flexWrap: "wrap", gap: "32px", margin: "16px 0" }, children: metrics.map((metric, idx) => /* @__PURE__ */ jsxs152("div", { style: { minWidth: "100px" }, children: [
      /* @__PURE__ */ jsx169(
        "div",
        {
          style: {
            fontFamily: FONT_SERIF,
            fontSize: "22pt",
            fontWeight: 400,
            lineHeight: 1.1,
            letterSpacing: "-0.02em"
          },
          children: metric.value
        }
      ),
      /* @__PURE__ */ jsx169("div", { style: { fontSize: "9pt", color: COLORS.textSecondary, marginTop: "4px" }, children: metric.label })
    ] }, idx)) }),
    projections && projections.length > 0 && /* @__PURE__ */ jsx169("div", { style: { marginTop: "16px" }, children: /* @__PURE__ */ jsxs152("table", { style: { width: "100%", fontSize: "10pt", borderCollapse: "collapse" }, children: [
      /* @__PURE__ */ jsx169("thead", { children: /* @__PURE__ */ jsxs152("tr", { children: [
        /* @__PURE__ */ jsx169("th", { style: thStyle("left"), children: "Period" }),
        /* @__PURE__ */ jsx169("th", { style: thStyle("right"), children: "Revenue" }),
        projections.some((p) => p.expenses) && /* @__PURE__ */ jsx169("th", { style: thStyle("right"), children: "Expenses" }),
        projections.some((p) => p.ebitda) && /* @__PURE__ */ jsx169("th", { style: thStyle("right"), children: "EBITDA" }),
        projections.some((p) => p.arr) && /* @__PURE__ */ jsx169("th", { style: thStyle("right"), children: "ARR" }),
        projections.some((p) => p.customers) && /* @__PURE__ */ jsx169("th", { style: thStyle("right"), children: "Customers" })
      ] }) }),
      /* @__PURE__ */ jsx169("tbody", { children: projections.map((row, idx) => /* @__PURE__ */ jsxs152("tr", { children: [
        /* @__PURE__ */ jsx169("td", { style: { ...tdStyle("left"), fontWeight: 500 }, children: row.period }),
        /* @__PURE__ */ jsx169("td", { style: { ...tdStyle("right"), fontFamily: FONT_MONO }, children: row.revenue }),
        projections.some((p) => p.expenses) && /* @__PURE__ */ jsx169("td", { style: { ...tdStyle("right"), fontFamily: FONT_MONO }, children: row.expenses ?? "--" }),
        projections.some((p) => p.ebitda) && /* @__PURE__ */ jsx169(
          "td",
          {
            style: {
              ...tdStyle("right"),
              fontFamily: FONT_MONO,
              color: row.ebitda?.startsWith("-") ? COLORS.negative : COLORS.positive
            },
            children: row.ebitda ?? "--"
          }
        ),
        projections.some((p) => p.arr) && /* @__PURE__ */ jsx169("td", { style: { ...tdStyle("right"), fontFamily: FONT_MONO }, children: row.arr ?? "--" }),
        projections.some((p) => p.customers) && /* @__PURE__ */ jsx169("td", { style: { ...tdStyle("right"), fontFamily: FONT_MONO }, children: row.customers ?? "--" })
      ] }, idx)) })
    ] }) }),
    currentRaise && /* @__PURE__ */ jsxs152(
      "div",
      {
        style: {
          marginTop: "24px",
          padding: "16px",
          borderLeft: `3pt solid ${COLORS.accent}`,
          background: COLORS.canvas
        },
        children: [
          /* @__PURE__ */ jsxs152("div", { style: { fontWeight: 600, fontSize: "11pt", marginBottom: "4px" }, children: [
            "Current Raise: ",
            currentRaise.amount
          ] }),
          /* @__PURE__ */ jsx169("div", { style: { fontSize: "10pt", color: COLORS.textSecondary }, children: currentRaise.use }),
          currentRaise.valuation && /* @__PURE__ */ jsxs152("div", { style: { fontSize: "9pt", color: COLORS.textTertiary, marginTop: "4px" }, children: [
            "Pre-money valuation: ",
            currentRaise.valuation
          ] })
        ]
      }
    ),
    fundingHistory && fundingHistory.length > 0 && /* @__PURE__ */ jsxs152("div", { style: { marginTop: "16px" }, children: [
      /* @__PURE__ */ jsx169("div", { style: { ...slideHeaderStyle, marginBottom: "8px" }, children: "Funding History" }),
      fundingHistory.map((round, idx) => /* @__PURE__ */ jsxs152(
        "div",
        {
          style: {
            display: "flex",
            alignItems: "baseline",
            gap: "16px",
            marginBottom: "8px",
            fontSize: "10pt"
          },
          children: [
            /* @__PURE__ */ jsx169("span", { style: { fontWeight: 600, minWidth: "60px" }, children: round.round }),
            /* @__PURE__ */ jsx169("span", { style: { fontFamily: FONT_MONO }, children: round.amount }),
            /* @__PURE__ */ jsx169("span", { style: { color: COLORS.textTertiary }, children: round.date }),
            round.investors && /* @__PURE__ */ jsx169("span", { style: { color: COLORS.textSecondary, fontStyle: "italic" }, children: round.investors })
          ]
        },
        idx
      ))
    ] }),
    /* @__PURE__ */ jsxs152("div", { style: slideFooterStyle2, children: [
      /* @__PURE__ */ jsx169("span", {}),
      /* @__PURE__ */ jsx169("span", {})
    ] })
  ] });
}
function TeamSlide({
  narrative,
  members,
  advisors
}) {
  return /* @__PURE__ */ jsxs152("div", { style: slideStyle, children: [
    /* @__PURE__ */ jsx169("div", { style: slideHeaderStyle, children: "Who We Are" }),
    /* @__PURE__ */ jsx169("h2", { style: slideTitleStyle2, children: "Team" }),
    narrative && /* @__PURE__ */ jsx169("p", { style: slideBodyStyle2, children: narrative }),
    /* @__PURE__ */ jsx169(
      "div",
      {
        style: {
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          gap: "24px",
          margin: "24px 0"
        },
        children: members.map((member, idx) => /* @__PURE__ */ jsxs152(
          "div",
          {
            style: {
              ...avoidBreakStyle(),
              paddingLeft: "12px",
              borderLeft: `2pt solid ${COLORS.rule}`
            },
            children: [
              /* @__PURE__ */ jsx169("div", { style: { fontWeight: 600, fontSize: "11pt", marginBottom: "2px" }, children: member.name }),
              /* @__PURE__ */ jsx169(
                "div",
                {
                  style: {
                    fontFamily: FONT_SANS,
                    fontSize: "9pt",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: COLORS.textTertiary,
                    marginBottom: "4px"
                  },
                  children: member.title
                }
              ),
              /* @__PURE__ */ jsx169("div", { style: { fontSize: "9pt", color: COLORS.textSecondary, lineHeight: 1.4 }, children: member.background })
            ]
          },
          idx
        ))
      }
    ),
    advisors && advisors.length > 0 && /* @__PURE__ */ jsxs152("div", { style: { marginTop: "16px" }, children: [
      /* @__PURE__ */ jsx169("div", { style: { ...slideHeaderStyle, marginBottom: "12px" }, children: "Advisors" }),
      /* @__PURE__ */ jsx169(
        "div",
        {
          style: {
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: "16px"
          },
          children: advisors.map((advisor, idx) => /* @__PURE__ */ jsxs152("div", { children: [
            /* @__PURE__ */ jsx169("div", { style: { fontWeight: 600, fontSize: "10pt" }, children: advisor.name }),
            /* @__PURE__ */ jsxs152("div", { style: { fontSize: "9pt", color: COLORS.textSecondary }, children: [
              advisor.role,
              ", ",
              advisor.affiliation
            ] })
          ] }, idx))
        }
      )
    ] }),
    /* @__PURE__ */ jsxs152("div", { style: slideFooterStyle2, children: [
      /* @__PURE__ */ jsx169("span", {}),
      /* @__PURE__ */ jsx169("span", {})
    ] })
  ] });
}
function ContentSlide({ title, subtitle, children }) {
  return /* @__PURE__ */ jsxs152("div", { style: slideStyle, children: [
    /* @__PURE__ */ jsx169("h2", { style: slideTitleStyle2, children: title }),
    subtitle && /* @__PURE__ */ jsx169("p", { style: slideSubtitleStyle, children: subtitle }),
    /* @__PURE__ */ jsx169("div", { style: { flex: 1 }, children }),
    /* @__PURE__ */ jsxs152("div", { style: slideFooterStyle2, children: [
      /* @__PURE__ */ jsx169("span", {}),
      /* @__PURE__ */ jsx169("span", {})
    ] })
  ] });
}
function MetricSlide({ title, metrics, narrative }) {
  return /* @__PURE__ */ jsxs152("div", { style: { ...slideStyle, justifyContent: "center" }, children: [
    /* @__PURE__ */ jsx169("h2", { style: { ...slideTitleStyle2, textAlign: "center", marginBottom: "16px" }, children: title }),
    narrative && /* @__PURE__ */ jsx169("p", { style: { ...slideBodyStyle2, textAlign: "center", maxWidth: "5in", margin: "0 auto 32px" }, children: narrative }),
    /* @__PURE__ */ jsx169(
      "div",
      {
        style: {
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "48px"
        },
        children: metrics.map((metric, idx) => /* @__PURE__ */ jsxs152("div", { style: { textAlign: "center", minWidth: "120px" }, children: [
          /* @__PURE__ */ jsx169(
            "div",
            {
              style: {
                fontFamily: FONT_SERIF,
                fontSize: "36pt",
                fontWeight: 400,
                lineHeight: 1,
                letterSpacing: "-0.03em",
                color: COLORS.text
              },
              children: metric.value
            }
          ),
          /* @__PURE__ */ jsx169(
            "div",
            {
              style: {
                fontFamily: FONT_SANS,
                fontSize: "10pt",
                color: COLORS.textSecondary,
                marginTop: "8px"
              },
              children: metric.label
            }
          ),
          metric.context && /* @__PURE__ */ jsx169(
            "div",
            {
              style: {
                fontFamily: FONT_SERIF,
                fontSize: "9pt",
                color: COLORS.textTertiary,
                fontStyle: "italic",
                marginTop: "4px"
              },
              children: metric.context
            }
          )
        ] }, idx))
      }
    ),
    /* @__PURE__ */ jsxs152("div", { style: slideFooterStyle2, children: [
      /* @__PURE__ */ jsx169("span", {}),
      /* @__PURE__ */ jsx169("span", {})
    ] })
  ] });
}
function ComparisonSlide({
  title,
  leftHeader,
  rightHeader,
  leftItems,
  rightItems,
  leftVariant = "neutral",
  rightVariant = "positive"
}) {
  const leftColor = leftVariant === "negative" ? COLORS.textTertiary : COLORS.text;
  const rightColor = COLORS.text;
  const rightAccent = rightVariant === "positive" ? COLORS.positive : COLORS.rule;
  return /* @__PURE__ */ jsxs152("div", { style: slideStyle, children: [
    /* @__PURE__ */ jsx169("h2", { style: slideTitleStyle2, children: title }),
    /* @__PURE__ */ jsxs152(
      "div",
      {
        style: {
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "48px",
          flex: 1,
          alignContent: "center",
          margin: "32px 0"
        },
        children: [
          /* @__PURE__ */ jsxs152("div", { children: [
            /* @__PURE__ */ jsx169(
              "div",
              {
                style: {
                  fontFamily: FONT_SANS,
                  fontSize: "10pt",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: leftColor,
                  marginBottom: "16px",
                  paddingBottom: "8px",
                  borderBottom: `0.5pt solid ${COLORS.ruleLight}`
                },
                children: leftHeader
              }
            ),
            leftItems.map((item, idx) => /* @__PURE__ */ jsx169(
              "div",
              {
                style: {
                  fontSize: "10pt",
                  lineHeight: 1.5,
                  color: leftColor,
                  marginBottom: "8px",
                  paddingLeft: "12px",
                  borderLeft: leftVariant === "negative" ? `2pt solid ${COLORS.ruleLight}` : `2pt solid ${COLORS.rule}`
                },
                children: item
              },
              idx
            ))
          ] }),
          /* @__PURE__ */ jsxs152("div", { children: [
            /* @__PURE__ */ jsx169(
              "div",
              {
                style: {
                  fontFamily: FONT_SANS,
                  fontSize: "10pt",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: rightColor,
                  marginBottom: "16px",
                  paddingBottom: "8px",
                  borderBottom: `0.5pt solid ${rightAccent}`
                },
                children: rightHeader
              }
            ),
            rightItems.map((item, idx) => /* @__PURE__ */ jsx169(
              "div",
              {
                style: {
                  fontSize: "10pt",
                  lineHeight: 1.5,
                  color: rightColor,
                  marginBottom: "8px",
                  paddingLeft: "12px",
                  borderLeft: `2pt solid ${rightAccent}`
                },
                children: item
              },
              idx
            ))
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsxs152("div", { style: slideFooterStyle2, children: [
      /* @__PURE__ */ jsx169("span", {}),
      /* @__PURE__ */ jsx169("span", {})
    ] })
  ] });
}
function ClosingSlide({
  headline,
  cta,
  contactEmail,
  website,
  logoUrl = "https://cdn.aaim.app/logos/aaim-master-grey.svg"
}) {
  return /* @__PURE__ */ jsxs152(
    "div",
    {
      style: {
        ...slideStyle,
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        pageBreakAfter: "auto"
        // Last slide, no break
      },
      children: [
        /* @__PURE__ */ jsx169(
          "img",
          {
            src: logoUrl,
            alt: "Aaim",
            style: { height: "32px", marginBottom: "48px" }
          }
        ),
        /* @__PURE__ */ jsx169(
          "h2",
          {
            style: {
              fontFamily: FONT_SERIF,
              fontSize: "28pt",
              fontWeight: 600,
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
              color: COLORS.text,
              margin: "0 0 16px 0",
              maxWidth: "5in"
            },
            children: headline
          }
        ),
        cta && /* @__PURE__ */ jsx169(
          "p",
          {
            style: {
              fontFamily: FONT_SERIF,
              fontSize: "12pt",
              color: COLORS.textSecondary,
              margin: "0 0 32px 0"
            },
            children: cta
          }
        ),
        /* @__PURE__ */ jsxs152("div", { style: { fontFamily: FONT_SANS, fontSize: "10pt", color: COLORS.textTertiary }, children: [
          contactEmail && /* @__PURE__ */ jsx169("div", { style: { marginBottom: "4px" }, children: contactEmail }),
          website && /* @__PURE__ */ jsx169("div", { children: website })
        ] })
      ]
    }
  );
}
var InvestorDeck = Object.assign(InvestorDeckRoot, {
  TitleSlide,
  MarketSize,
  BusinessModel,
  Financials,
  Team: TeamSlide,
  ContentSlide,
  MetricSlide,
  ComparisonSlide,
  ClosingSlide
});

// src/templates/SlideDeck.tsx
import { jsx as jsx170, jsxs as jsxs153 } from "react/jsx-runtime";
function SlideFooter({
  logoUrl,
  showPageNumber = true,
  confidentiality,
  companyName,
  pageNumber
}) {
  return /* @__PURE__ */ jsxs153("div", { style: slideFooterStyle(), children: [
    /* @__PURE__ */ jsxs153("div", { style: { display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }, children: [
      logoUrl && /* @__PURE__ */ jsx170(
        "img",
        {
          src: logoUrl,
          alt: companyName ?? "Logo",
          style: {
            height: "16px",
            maxHeight: "16px",
            maxWidth: "120px",
            objectFit: "contain",
            opacity: 0.5,
            flexShrink: 0
          }
        }
      ),
      confidentiality && /* @__PURE__ */ jsx170(
        "span",
        {
          style: {
            ...slideLabelStyle(),
            fontSize: "10px",
            lineHeight: "16px",
            color: COLORS.textTertiary,
            whiteSpace: "nowrap"
          },
          children: confidentiality
        }
      )
    ] }),
    showPageNumber && pageNumber != null && /* @__PURE__ */ jsx170(
      "span",
      {
        style: {
          fontFamily: FONT_SANS,
          fontSize: "11px",
          color: COLORS.textTertiary,
          flexShrink: 0
        },
        children: pageNumber
      }
    )
  ] });
}
var RATIO_MAP = {
  "50-50": "1fr 1fr",
  "60-40": "3fr 2fr",
  "40-60": "2fr 3fr",
  "33-67": "1fr 2fr",
  "67-33": "2fr 1fr"
};
function SlideDeckRoot({
  children
}) {
  return /* @__PURE__ */ jsx170(
    "div",
    {
      className: "slide-deck",
      style: {
        maxWidth: SLIDE.maxWidth,
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: "24px"
      },
      children
    }
  );
}
function TitleSlide2({
  companyName,
  title,
  subtitle,
  presenter,
  presenterTitle,
  date,
  confidentiality,
  logoUrl
}) {
  return /* @__PURE__ */ jsxs153("div", { "data-slide-type": "title", style: { ...slideContainerStyle(COLORS.canvas), ...pageBreakStyle() }, children: [
    /* @__PURE__ */ jsx170("div", { style: slideAccentBarStyle("6px") }),
    /* @__PURE__ */ jsxs153(
      "div",
      {
        style: {
          ...slideContentStyle(),
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center"
        },
        children: [
          logoUrl && /* @__PURE__ */ jsx170(
            "img",
            {
              src: logoUrl,
              alt: companyName ?? "Logo",
              style: { height: "40px", marginBottom: "48px" }
            }
          ),
          /* @__PURE__ */ jsx170(
            "h1",
            {
              "data-editable": "title",
              style: {
                fontFamily: FONT_SERIF,
                fontSize: "42px",
                fontWeight: 600,
                lineHeight: 1.1,
                letterSpacing: "-0.045em",
                color: COLORS.text,
                margin: "0 0 12px 0",
                maxWidth: "80%"
              },
              children: title
            }
          ),
          subtitle && /* @__PURE__ */ jsx170(
            "p",
            {
              "data-editable": "subtitle",
              style: {
                fontFamily: FONT_SANS,
                fontSize: "18px",
                color: COLORS.textSecondary,
                margin: "0 0 32px 0",
                maxWidth: "60%"
              },
              children: subtitle
            }
          ),
          (presenter || date) && /* @__PURE__ */ jsxs153(
            "div",
            {
              style: {
                marginTop: "24px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "4px"
              },
              children: [
                presenter && /* @__PURE__ */ jsxs153("div", { "data-editable": "presenter", style: { fontFamily: FONT_SANS, fontSize: "14px", color: COLORS.textSecondary }, children: [
                  presenter,
                  presenterTitle ? ` | ${presenterTitle}` : ""
                ] }),
                /* @__PURE__ */ jsxs153("div", { "data-editable": "date", style: { fontFamily: FONT_SANS, fontSize: "13px", color: COLORS.textTertiary }, children: [
                  date ?? formatDocumentDate(),
                  confidentiality ? ` | ${getConfidentialityLabel(confidentiality)}` : ""
                ] })
              ]
            }
          )
        ]
      }
    )
  ] });
}
function AgendaSlide({ title = "Agenda", items }) {
  return /* @__PURE__ */ jsxs153("div", { "data-slide-type": "agenda", style: { ...slideContainerStyle(), ...pageBreakStyle() }, children: [
    /* @__PURE__ */ jsx170("div", { style: slideAccentBarStyle() }),
    /* @__PURE__ */ jsxs153("div", { style: slideContentStyle(), children: [
      /* @__PURE__ */ jsx170("h2", { "data-editable": "title", style: { ...slideTitleStyle(), marginBottom: "32px" }, children: title }),
      /* @__PURE__ */ jsx170("div", { style: { display: "flex", flexDirection: "column", gap: "16px", flex: 1 }, children: items.map((item, idx) => /* @__PURE__ */ jsxs153(
        "div",
        {
          style: {
            display: "flex",
            alignItems: "baseline",
            gap: "20px",
            padding: "12px 0",
            borderBottom: idx < items.length - 1 ? `1px solid ${COLORS.ruleLight}` : "none",
            opacity: item.active === false ? 0.4 : 1
          },
          children: [
            /* @__PURE__ */ jsx170(
              "span",
              {
                style: {
                  fontFamily: FONT_MONO,
                  fontSize: "24px",
                  fontWeight: 600,
                  color: item.active ? COLORS.accent : COLORS.textTertiary,
                  minWidth: "36px"
                },
                children: item.number
              }
            ),
            /* @__PURE__ */ jsxs153("div", { children: [
              /* @__PURE__ */ jsx170(
                "div",
                {
                  style: {
                    fontFamily: FONT_SANS,
                    fontSize: "20px",
                    fontWeight: 600,
                    color: COLORS.text,
                    letterSpacing: "-0.02em"
                  },
                  children: item.title
                }
              ),
              item.subtitle && /* @__PURE__ */ jsx170(
                "div",
                {
                  style: {
                    fontFamily: FONT_SANS,
                    fontSize: "14px",
                    color: COLORS.textTertiary,
                    marginTop: "4px"
                  },
                  children: item.subtitle
                }
              )
            ] })
          ]
        },
        idx
      )) })
    ] })
  ] });
}
function SectionDivider({ sectionNumber, title, subtitle }) {
  return /* @__PURE__ */ jsxs153("div", { "data-slide-type": "section-divider", style: { ...slideContainerStyle(COLORS.accent), ...pageBreakStyle() }, children: [
    /* @__PURE__ */ jsxs153("div", { style: { ...slideContentStyle(), justifyContent: "center" }, children: [
      sectionNumber && /* @__PURE__ */ jsxs153(
        "div",
        {
          "data-editable": "section-number",
          style: {
            fontFamily: FONT_SANS,
            fontSize: "12px",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.15em",
            color: "rgba(255,255,255,0.7)",
            marginBottom: "16px"
          },
          children: [
            "Section ",
            sectionNumber
          ]
        }
      ),
      /* @__PURE__ */ jsx170(
        "h2",
        {
          "data-editable": "title",
          style: {
            fontFamily: FONT_SERIF,
            fontSize: "48px",
            fontWeight: 600,
            lineHeight: 1.1,
            letterSpacing: "-0.045em",
            color: "#FFFFFF",
            margin: 0,
            maxWidth: "70%"
          },
          children: title
        }
      ),
      subtitle && /* @__PURE__ */ jsx170(
        "p",
        {
          "data-editable": "subtitle",
          style: {
            fontFamily: FONT_SANS,
            fontSize: "18px",
            color: "rgba(255,255,255,0.8)",
            marginTop: "16px",
            maxWidth: "60%"
          },
          children: subtitle
        }
      )
    ] }),
    sectionNumber && /* @__PURE__ */ jsx170(
      "div",
      {
        style: {
          position: "absolute",
          right: SLIDE.paddingX,
          bottom: "40px",
          fontFamily: FONT_SERIF,
          fontSize: "180px",
          fontWeight: 600,
          color: "rgba(255,255,255,0.08)",
          lineHeight: 1
        },
        children: sectionNumber
      }
    )
  ] });
}
function ContentSlide2({
  label,
  title,
  children,
  pageNumber,
  footer
}) {
  return /* @__PURE__ */ jsxs153("div", { "data-slide-type": "content", style: { ...slideContainerStyle(), ...pageBreakStyle() }, children: [
    /* @__PURE__ */ jsx170("div", { style: slideAccentBarStyle() }),
    /* @__PURE__ */ jsxs153("div", { style: slideContentStyle(), children: [
      label && /* @__PURE__ */ jsx170("div", { "data-editable": "label", style: { ...slideLabelStyle(), color: COLORS.accent, marginBottom: "8px" }, children: label }),
      /* @__PURE__ */ jsx170("h2", { "data-editable": "title", style: { ...slideTitleStyle(), marginBottom: "20px" }, children: title }),
      /* @__PURE__ */ jsx170("div", { style: { flex: 1 }, children })
    ] }),
    footer && /* @__PURE__ */ jsx170(SlideFooter, { ...footer, pageNumber })
  ] });
}
function TwoColumnSlide({
  label,
  title,
  left,
  right,
  ratio = "50-50",
  pageNumber,
  footer
}) {
  return /* @__PURE__ */ jsxs153("div", { "data-slide-type": "two-column", style: { ...slideContainerStyle(), ...pageBreakStyle() }, children: [
    /* @__PURE__ */ jsx170("div", { style: slideAccentBarStyle() }),
    /* @__PURE__ */ jsxs153("div", { style: slideContentStyle(), children: [
      label && /* @__PURE__ */ jsx170("div", { "data-editable": "label", style: { ...slideLabelStyle(), color: COLORS.accent, marginBottom: "8px" }, children: label }),
      /* @__PURE__ */ jsx170("h2", { "data-editable": "title", style: { ...slideTitleStyle(), marginBottom: "20px" }, children: title }),
      /* @__PURE__ */ jsxs153(
        "div",
        {
          style: {
            flex: 1,
            display: "grid",
            gridTemplateColumns: RATIO_MAP[ratio] ?? "1fr 1fr",
            gap: "32px"
          },
          children: [
            /* @__PURE__ */ jsx170("div", { children: left }),
            /* @__PURE__ */ jsx170("div", { children: right })
          ]
        }
      )
    ] }),
    footer && /* @__PURE__ */ jsx170(SlideFooter, { ...footer, pageNumber })
  ] });
}
function DataTableSlide({
  label,
  title,
  headers,
  rows,
  alignments,
  source,
  pageNumber,
  footer
}) {
  return /* @__PURE__ */ jsxs153("div", { "data-slide-type": "data-table", style: { ...slideContainerStyle(), ...pageBreakStyle() }, children: [
    /* @__PURE__ */ jsx170("div", { style: slideAccentBarStyle() }),
    /* @__PURE__ */ jsxs153("div", { style: slideContentStyle(), children: [
      label && /* @__PURE__ */ jsx170("div", { "data-editable": "label", style: { ...slideLabelStyle(), color: COLORS.accent, marginBottom: "8px" }, children: label }),
      /* @__PURE__ */ jsx170("h2", { "data-editable": "title", style: { ...slideTitleStyle(), fontSize: "26px", marginBottom: "16px" }, children: title }),
      /* @__PURE__ */ jsxs153(
        "table",
        {
          style: {
            width: "100%",
            fontSize: "14px",
            borderCollapse: "collapse",
            fontVariantNumeric: "tabular-nums"
          },
          children: [
            /* @__PURE__ */ jsx170("thead", { children: /* @__PURE__ */ jsx170("tr", { children: headers.map((h, i) => /* @__PURE__ */ jsx170(
              "th",
              {
                style: {
                  textAlign: alignments?.[i] ?? (i === 0 ? "left" : "right"),
                  padding: "10px 12px",
                  fontFamily: FONT_SANS,
                  fontSize: "11px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: COLORS.textSecondary,
                  borderBottom: `2px solid ${COLORS.rule}`
                },
                children: h
              },
              i
            )) }) }),
            /* @__PURE__ */ jsx170("tbody", { children: rows.map((row, rIdx) => {
              const variantColor = row.variant === "positive" ? COLORS.positive : row.variant === "negative" ? COLORS.negative : void 0;
              return /* @__PURE__ */ jsx170(
                "tr",
                {
                  style: {
                    background: row.emphasis ? COLORS.canvas : row.variant === "highlight" ? `${COLORS.accent}08` : "transparent"
                  },
                  children: row.cells.map((cell, cIdx) => /* @__PURE__ */ jsx170(
                    "td",
                    {
                      style: {
                        textAlign: alignments?.[cIdx] ?? (cIdx === 0 ? "left" : "right"),
                        padding: "10px 12px",
                        fontFamily: cIdx === 0 ? FONT_SANS : FONT_MONO,
                        fontSize: "14px",
                        fontWeight: row.emphasis ? 600 : 400,
                        color: variantColor ?? COLORS.text,
                        borderBottom: `1px solid ${COLORS.ruleLight}`
                      },
                      children: cell
                    },
                    cIdx
                  ))
                },
                rIdx
              );
            }) })
          ]
        }
      ),
      source && /* @__PURE__ */ jsxs153(
        "div",
        {
          style: {
            fontFamily: FONT_SANS,
            fontSize: "10px",
            color: COLORS.textTertiary,
            marginTop: "12px"
          },
          children: [
            "Source: ",
            source
          ]
        }
      )
    ] }),
    footer && /* @__PURE__ */ jsx170(SlideFooter, { ...footer, pageNumber })
  ] });
}
function ChartSlide({
  label,
  title,
  description,
  children,
  source,
  pageNumber,
  footer
}) {
  return /* @__PURE__ */ jsxs153("div", { "data-slide-type": "chart", style: { ...slideContainerStyle(), ...pageBreakStyle() }, children: [
    /* @__PURE__ */ jsx170("div", { style: slideAccentBarStyle() }),
    /* @__PURE__ */ jsxs153("div", { style: slideContentStyle(), children: [
      label && /* @__PURE__ */ jsx170("div", { "data-editable": "label", style: { ...slideLabelStyle(), color: COLORS.accent, marginBottom: "8px" }, children: label }),
      /* @__PURE__ */ jsx170("h2", { "data-editable": "title", style: { ...slideTitleStyle(), fontSize: "26px", marginBottom: "4px" }, children: title }),
      description && /* @__PURE__ */ jsx170(
        "p",
        {
          style: {
            fontFamily: FONT_SANS,
            fontSize: "13px",
            color: COLORS.textTertiary,
            margin: "0 0 16px 0"
          },
          children: description
        }
      ),
      /* @__PURE__ */ jsx170("div", { style: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }, children }),
      source && /* @__PURE__ */ jsxs153(
        "div",
        {
          style: {
            fontFamily: FONT_SANS,
            fontSize: "10px",
            color: COLORS.textTertiary,
            marginTop: "8px"
          },
          children: [
            "Source: ",
            source
          ]
        }
      )
    ] }),
    footer && /* @__PURE__ */ jsx170(SlideFooter, { ...footer, pageNumber })
  ] });
}
function MetricDashboardSlide({
  label,
  title,
  metrics,
  columns = 3,
  pageNumber,
  footer
}) {
  return /* @__PURE__ */ jsxs153("div", { "data-slide-type": "metric-dashboard", style: { ...slideContainerStyle(), ...pageBreakStyle() }, children: [
    /* @__PURE__ */ jsx170("div", { style: slideAccentBarStyle() }),
    /* @__PURE__ */ jsxs153("div", { style: slideContentStyle(), children: [
      label && /* @__PURE__ */ jsx170("div", { "data-editable": "label", style: { ...slideLabelStyle(), color: COLORS.accent, marginBottom: "8px" }, children: label }),
      /* @__PURE__ */ jsx170("h2", { "data-editable": "title", style: { ...slideTitleStyle(), marginBottom: "24px" }, children: title }),
      /* @__PURE__ */ jsx170(
        "div",
        {
          style: {
            flex: 1,
            display: "grid",
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap: "24px",
            alignContent: "center"
          },
          children: metrics.map((m, idx) => /* @__PURE__ */ jsxs153(
            "div",
            {
              style: {
                padding: "24px",
                border: `1px solid ${COLORS.ruleLight}`,
                textAlign: "center"
              },
              children: [
                /* @__PURE__ */ jsx170(
                  "div",
                  {
                    "data-editable": "metric-value",
                    style: {
                      fontFamily: FONT_SERIF,
                      fontSize: "36px",
                      fontWeight: 600,
                      lineHeight: 1.1,
                      letterSpacing: "-0.02em",
                      color: COLORS.accent,
                      fontVariantNumeric: "tabular-nums"
                    },
                    children: m.value
                  }
                ),
                /* @__PURE__ */ jsx170(
                  "div",
                  {
                    "data-editable": "metric-label",
                    style: {
                      fontFamily: FONT_SANS,
                      fontSize: "13px",
                      color: COLORS.textSecondary,
                      marginTop: "8px"
                    },
                    children: m.label
                  }
                ),
                m.change && /* @__PURE__ */ jsx170(
                  "div",
                  {
                    style: {
                      fontFamily: FONT_MONO,
                      fontSize: "12px",
                      color: m.trend === "up" ? COLORS.positive : m.trend === "down" ? COLORS.negative : COLORS.textTertiary,
                      marginTop: "4px"
                    },
                    children: m.change
                  }
                )
              ]
            },
            idx
          ))
        }
      )
    ] }),
    footer && /* @__PURE__ */ jsx170(SlideFooter, { ...footer, pageNumber })
  ] });
}
function ComparisonSlide2({
  title,
  left,
  right,
  pageNumber,
  footer
}) {
  const leftBorder = left.variant === "negative" ? COLORS.textTertiary : COLORS.ruleLight;
  const rightBorder = right.variant === "positive" ? COLORS.accent : COLORS.ruleLight;
  return /* @__PURE__ */ jsxs153("div", { "data-slide-type": "comparison", style: { ...slideContainerStyle(), ...pageBreakStyle() }, children: [
    /* @__PURE__ */ jsx170("div", { style: slideAccentBarStyle() }),
    /* @__PURE__ */ jsxs153("div", { style: slideContentStyle(), children: [
      /* @__PURE__ */ jsx170("h2", { "data-editable": "title", style: { ...slideTitleStyle(), textAlign: "center", marginBottom: "24px" }, children: title }),
      /* @__PURE__ */ jsx170("div", { style: { flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }, children: [left, right].map((side, sIdx) => {
        const border = sIdx === 0 ? leftBorder : rightBorder;
        return /* @__PURE__ */ jsxs153(
          "div",
          {
            style: {
              padding: "24px",
              borderLeft: `4px solid ${border}`,
              background: sIdx === 1 ? `${COLORS.accent}06` : COLORS.canvas
            },
            children: [
              /* @__PURE__ */ jsxs153(
                "div",
                {
                  style: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px"
                  },
                  children: [
                    /* @__PURE__ */ jsx170(
                      "h3",
                      {
                        style: {
                          fontFamily: FONT_SANS,
                          fontSize: "18px",
                          fontWeight: 600,
                          color: COLORS.text,
                          margin: 0
                        },
                        children: side.title
                      }
                    ),
                    side.highlight && /* @__PURE__ */ jsx170(
                      "span",
                      {
                        style: {
                          fontFamily: FONT_SANS,
                          fontSize: "11px",
                          fontWeight: 600,
                          color: sIdx === 1 ? COLORS.positive : COLORS.textTertiary,
                          padding: "3px 8px",
                          background: sIdx === 1 ? `${COLORS.positive}15` : COLORS.ruleLight,
                          borderRadius: "2px"
                        },
                        children: side.highlight
                      }
                    )
                  ]
                }
              ),
              /* @__PURE__ */ jsx170("ul", { style: { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "8px" }, children: side.items.map((item, iIdx) => /* @__PURE__ */ jsxs153(
                "li",
                {
                  style: {
                    fontFamily: FONT_SANS,
                    fontSize: "14px",
                    color: COLORS.textSecondary,
                    paddingLeft: "16px",
                    position: "relative"
                  },
                  children: [
                    /* @__PURE__ */ jsx170(
                      "span",
                      {
                        style: {
                          position: "absolute",
                          left: 0,
                          top: "8px",
                          width: "5px",
                          height: "5px",
                          borderRadius: "50%",
                          background: border
                        }
                      }
                    ),
                    item
                  ]
                },
                iIdx
              )) })
            ]
          },
          sIdx
        );
      }) })
    ] }),
    footer && /* @__PURE__ */ jsx170(SlideFooter, { ...footer, pageNumber })
  ] });
}
function MatrixSlide({
  title,
  quadrants,
  pageNumber,
  footer
}) {
  const defaultColors2 = ["#1B7340", "#B54708", "#6B5D54", "#B42318"];
  return /* @__PURE__ */ jsxs153("div", { "data-slide-type": "matrix", style: { ...slideContainerStyle(), ...pageBreakStyle() }, children: [
    /* @__PURE__ */ jsx170("div", { style: slideAccentBarStyle() }),
    /* @__PURE__ */ jsxs153("div", { style: slideContentStyle(), children: [
      /* @__PURE__ */ jsx170("h2", { "data-editable": "title", style: { ...slideTitleStyle(), textAlign: "center", marginBottom: "20px" }, children: title }),
      /* @__PURE__ */ jsx170(
        "div",
        {
          style: {
            flex: 1,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gridTemplateRows: "1fr 1fr",
            gap: "2px"
          },
          children: quadrants.slice(0, 4).map((q, idx) => {
            const color = q.color ?? defaultColors2[idx % defaultColors2.length];
            return /* @__PURE__ */ jsxs153(
              "div",
              {
                style: {
                  padding: "20px",
                  background: COLORS.canvas,
                  borderTop: `3px solid ${color}`
                },
                children: [
                  /* @__PURE__ */ jsx170(
                    "h3",
                    {
                      style: {
                        fontFamily: FONT_SANS,
                        fontSize: "13px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        color,
                        margin: "0 0 12px 0"
                      },
                      children: q.label
                    }
                  ),
                  /* @__PURE__ */ jsx170("ul", { style: { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "6px" }, children: q.items.map((item, iIdx) => /* @__PURE__ */ jsxs153(
                    "li",
                    {
                      style: {
                        fontFamily: FONT_SANS,
                        fontSize: "13px",
                        color: COLORS.textSecondary,
                        paddingLeft: "12px",
                        position: "relative"
                      },
                      children: [
                        /* @__PURE__ */ jsx170(
                          "span",
                          {
                            style: {
                              position: "absolute",
                              left: 0,
                              top: "7px",
                              width: "4px",
                              height: "4px",
                              borderRadius: "50%",
                              background: color
                            }
                          }
                        ),
                        item
                      ]
                    },
                    iIdx
                  )) })
                ]
              },
              idx
            );
          })
        }
      )
    ] }),
    footer && /* @__PURE__ */ jsx170(SlideFooter, { ...footer, pageNumber })
  ] });
}
function QuoteSlide({
  quote,
  name,
  title: personTitle,
  organization,
  initials
}) {
  return /* @__PURE__ */ jsxs153("div", { "data-slide-type": "quote", style: { ...slideContainerStyle(COLORS.canvas), ...pageBreakStyle() }, children: [
    /* @__PURE__ */ jsx170("div", { style: slideAccentBarStyle() }),
    /* @__PURE__ */ jsxs153(
      "div",
      {
        style: {
          ...slideContentStyle(),
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center"
        },
        children: [
          /* @__PURE__ */ jsx170(
            "div",
            {
              style: {
                fontFamily: FONT_SERIF,
                fontSize: "72px",
                lineHeight: 1,
                color: COLORS.accent,
                marginBottom: "8px"
              },
              children: "\u201C"
            }
          ),
          /* @__PURE__ */ jsx170(
            "blockquote",
            {
              "data-editable": "quote",
              style: {
                fontFamily: FONT_SERIF,
                fontSize: "26px",
                lineHeight: 1.4,
                letterSpacing: "-0.02em",
                color: COLORS.text,
                margin: "0 0 24px 0",
                maxWidth: "75%"
              },
              children: quote
            }
          ),
          (name || organization) && /* @__PURE__ */ jsxs153("div", { style: { display: "flex", alignItems: "center", gap: "12px" }, children: [
            initials && /* @__PURE__ */ jsx170(
              "div",
              {
                style: {
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  background: `${COLORS.accent}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: FONT_SANS,
                  fontSize: "14px",
                  fontWeight: 600,
                  color: COLORS.accent
                },
                children: initials
              }
            ),
            /* @__PURE__ */ jsxs153("div", { style: { textAlign: "left" }, children: [
              name && /* @__PURE__ */ jsx170("div", { style: { fontFamily: FONT_SANS, fontSize: "15px", fontWeight: 600, color: COLORS.text }, children: name }),
              (personTitle || organization) && /* @__PURE__ */ jsx170("div", { style: { fontFamily: FONT_SANS, fontSize: "13px", color: COLORS.textTertiary }, children: [personTitle, organization].filter(Boolean).join(", ") })
            ] })
          ] })
        ]
      }
    )
  ] });
}
function ImageTextSlide({
  label,
  title,
  children,
  imageUrl,
  imageAlt,
  imagePosition = "right",
  pageNumber,
  footer
}) {
  const textContent = /* @__PURE__ */ jsxs153("div", { style: { display: "flex", flexDirection: "column", justifyContent: "center" }, children: [
    label && /* @__PURE__ */ jsx170("div", { "data-editable": "label", style: { ...slideLabelStyle(), color: COLORS.accent, marginBottom: "8px" }, children: label }),
    /* @__PURE__ */ jsx170("h2", { "data-editable": "title", style: { ...slideTitleStyle(), fontSize: "28px", marginBottom: "16px" }, children: title }),
    /* @__PURE__ */ jsx170("div", { style: slideBodyStyle(), children })
  ] });
  const imageContent = /* @__PURE__ */ jsx170(
    "div",
    {
      style: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: COLORS.canvas,
        overflow: "hidden",
        height: "100%"
      },
      children: /* @__PURE__ */ jsx170(
        "img",
        {
          src: imageUrl,
          alt: imageAlt ?? title,
          style: { maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }
        }
      )
    }
  );
  return /* @__PURE__ */ jsxs153("div", { "data-slide-type": "image-text", style: { ...slideContainerStyle(), ...pageBreakStyle() }, children: [
    /* @__PURE__ */ jsx170("div", { style: slideAccentBarStyle() }),
    /* @__PURE__ */ jsxs153(
      "div",
      {
        style: {
          flex: 1,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "0"
        },
        children: [
          /* @__PURE__ */ jsx170("div", { style: { padding: SLIDE.paddingY + " " + SLIDE.paddingX }, children: imagePosition === "right" ? textContent : imageContent }),
          /* @__PURE__ */ jsx170("div", { style: { padding: imagePosition === "right" ? "0" : SLIDE.paddingY + " " + SLIDE.paddingX }, children: imagePosition === "right" ? imageContent : textContent })
        ]
      }
    ),
    footer && /* @__PURE__ */ jsx170(SlideFooter, { ...footer, pageNumber })
  ] });
}
function TimelineSlide({
  label,
  title,
  steps,
  pageNumber,
  footer
}) {
  return /* @__PURE__ */ jsxs153("div", { "data-slide-type": "timeline", style: { ...slideContainerStyle(), ...pageBreakStyle() }, children: [
    /* @__PURE__ */ jsx170("div", { style: slideAccentBarStyle() }),
    /* @__PURE__ */ jsxs153("div", { style: slideContentStyle(), children: [
      label && /* @__PURE__ */ jsx170("div", { "data-editable": "label", style: { ...slideLabelStyle(), color: COLORS.accent, marginBottom: "8px" }, children: label }),
      /* @__PURE__ */ jsx170("h2", { "data-editable": "title", style: { ...slideTitleStyle(), textAlign: "center", marginBottom: "32px" }, children: title }),
      /* @__PURE__ */ jsx170("div", { style: { flex: 1, display: "flex", alignItems: "center" }, children: /* @__PURE__ */ jsxs153("div", { style: { position: "relative", width: "100%" }, children: [
        /* @__PURE__ */ jsx170(
          "div",
          {
            style: {
              position: "absolute",
              top: "28px",
              left: "5%",
              right: "5%",
              height: "2px",
              background: COLORS.ruleLight
            }
          }
        ),
        /* @__PURE__ */ jsx170(
          "div",
          {
            style: {
              display: "grid",
              gridTemplateColumns: `repeat(${steps.length}, 1fr)`,
              gap: "16px",
              position: "relative"
            },
            children: steps.map((step, idx) => /* @__PURE__ */ jsxs153("div", { style: { textAlign: "center" }, children: [
              /* @__PURE__ */ jsx170(
                "div",
                {
                  style: {
                    width: "56px",
                    height: "56px",
                    borderRadius: "50%",
                    background: step.active ? COLORS.accent : COLORS.ruleLight,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 12px",
                    fontFamily: FONT_SANS,
                    fontSize: "18px",
                    fontWeight: 700,
                    color: step.active ? "#FFFFFF" : COLORS.textSecondary
                  },
                  children: idx + 1
                }
              ),
              /* @__PURE__ */ jsx170(
                "div",
                {
                  style: {
                    fontFamily: FONT_SANS,
                    fontSize: "11px",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: step.active ? COLORS.accent : COLORS.textTertiary,
                    marginBottom: "4px"
                  },
                  children: step.period
                }
              ),
              /* @__PURE__ */ jsx170(
                "div",
                {
                  style: {
                    fontFamily: FONT_SANS,
                    fontSize: "16px",
                    fontWeight: 600,
                    color: COLORS.text,
                    marginBottom: "4px"
                  },
                  children: step.title
                }
              ),
              /* @__PURE__ */ jsx170(
                "div",
                {
                  style: {
                    fontFamily: FONT_SERIF,
                    fontSize: "12px",
                    color: COLORS.textTertiary,
                    lineHeight: 1.4
                  },
                  children: step.description
                }
              )
            ] }, idx))
          }
        )
      ] }) })
    ] }),
    footer && /* @__PURE__ */ jsx170(SlideFooter, { ...footer, pageNumber })
  ] });
}
function BulletSlide({
  label,
  title,
  items,
  aside,
  pageNumber,
  footer
}) {
  return /* @__PURE__ */ jsxs153("div", { "data-slide-type": "bullet", style: { ...slideContainerStyle(), ...pageBreakStyle() }, children: [
    /* @__PURE__ */ jsx170("div", { style: slideAccentBarStyle() }),
    /* @__PURE__ */ jsxs153("div", { style: slideContentStyle(), children: [
      label && /* @__PURE__ */ jsx170("div", { "data-editable": "label", style: { ...slideLabelStyle(), color: COLORS.accent, marginBottom: "8px" }, children: label }),
      /* @__PURE__ */ jsx170("h2", { "data-editable": "title", style: { ...slideTitleStyle(), marginBottom: "20px" }, children: title }),
      /* @__PURE__ */ jsxs153(
        "div",
        {
          style: {
            flex: 1,
            display: aside ? "grid" : "block",
            gridTemplateColumns: aside ? "2fr 1fr" : void 0,
            gap: aside ? "32px" : void 0
          },
          children: [
            /* @__PURE__ */ jsx170("ul", { style: { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "12px" }, children: items.map((item, idx) => /* @__PURE__ */ jsxs153("li", { children: [
              /* @__PURE__ */ jsxs153(
                "div",
                {
                  style: {
                    fontFamily: FONT_SANS,
                    fontSize: "16px",
                    color: COLORS.text,
                    paddingLeft: "20px",
                    position: "relative"
                  },
                  children: [
                    /* @__PURE__ */ jsx170(
                      "span",
                      {
                        style: {
                          position: "absolute",
                          left: 0,
                          top: "8px",
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          background: COLORS.accent
                        }
                      }
                    ),
                    item.text
                  ]
                }
              ),
              item.subItems && /* @__PURE__ */ jsx170("ul", { style: { margin: "6px 0 0 0", padding: 0, listStyle: "none" }, children: item.subItems.map((sub, sIdx) => /* @__PURE__ */ jsxs153(
                "li",
                {
                  style: {
                    fontFamily: FONT_SANS,
                    fontSize: "13px",
                    color: COLORS.textSecondary,
                    paddingLeft: "36px",
                    marginTop: "4px",
                    position: "relative"
                  },
                  children: [
                    /* @__PURE__ */ jsx170(
                      "span",
                      {
                        style: {
                          position: "absolute",
                          left: "20px",
                          top: "7px",
                          width: "4px",
                          height: "4px",
                          borderRadius: "50%",
                          background: COLORS.textTertiary
                        }
                      }
                    ),
                    sub
                  ]
                },
                sIdx
              )) })
            ] }, idx)) }),
            aside && /* @__PURE__ */ jsx170(
              "div",
              {
                style: {
                  padding: "20px",
                  background: COLORS.canvas,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center"
                },
                children: aside
              }
            )
          ]
        }
      )
    ] }),
    footer && /* @__PURE__ */ jsx170(SlideFooter, { ...footer, pageNumber })
  ] });
}
function ThankYouSlide({
  headline = "Thank You",
  message,
  contactName,
  contactEmail,
  contactPhone,
  companyUrl,
  logoUrl,
  ctaLabel
}) {
  return /* @__PURE__ */ jsxs153("div", { "data-slide-type": "thank-you", style: { ...slideContainerStyle(COLORS.canvas), ...pageBreakStyle() }, children: [
    /* @__PURE__ */ jsx170("div", { style: slideAccentBarStyle("6px") }),
    /* @__PURE__ */ jsxs153(
      "div",
      {
        style: {
          ...slideContentStyle(),
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center"
        },
        children: [
          logoUrl && /* @__PURE__ */ jsx170(
            "img",
            {
              src: logoUrl,
              alt: "Logo",
              style: { height: "36px", marginBottom: "32px" }
            }
          ),
          /* @__PURE__ */ jsx170(
            "h2",
            {
              "data-editable": "headline",
              style: {
                fontFamily: FONT_SERIF,
                fontSize: "42px",
                fontWeight: 600,
                letterSpacing: "-0.045em",
                color: COLORS.text,
                margin: "0 0 12px 0"
              },
              children: headline
            }
          ),
          message && /* @__PURE__ */ jsx170(
            "p",
            {
              "data-editable": "message",
              style: {
                fontFamily: FONT_SERIF,
                fontSize: "18px",
                color: COLORS.textSecondary,
                margin: "0 0 24px 0",
                maxWidth: "60%"
              },
              children: message
            }
          ),
          ctaLabel && /* @__PURE__ */ jsx170(
            "div",
            {
              style: {
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "12px 32px",
                background: COLORS.accent,
                color: "#FFFFFF",
                fontFamily: FONT_SANS,
                fontSize: "15px",
                fontWeight: 600,
                borderRadius: "3px",
                marginBottom: "24px"
              },
              children: ctaLabel
            }
          ),
          (contactName || contactEmail || contactPhone || companyUrl) && /* @__PURE__ */ jsxs153(
            "div",
            {
              style: {
                marginTop: "16px",
                display: "flex",
                gap: "24px",
                justifyContent: "center",
                fontFamily: FONT_SANS,
                fontSize: "13px",
                color: COLORS.textTertiary
              },
              children: [
                contactName && /* @__PURE__ */ jsx170("span", { children: contactName }),
                contactEmail && /* @__PURE__ */ jsx170("span", { children: contactEmail }),
                contactPhone && /* @__PURE__ */ jsx170("span", { children: contactPhone }),
                companyUrl && /* @__PURE__ */ jsx170("span", { children: companyUrl })
              ]
            }
          )
        ]
      }
    )
  ] });
}
var SlideDeck = Object.assign(SlideDeckRoot, {
  TitleSlide: TitleSlide2,
  AgendaSlide,
  SectionDivider,
  ContentSlide: ContentSlide2,
  TwoColumnSlide,
  DataTableSlide,
  ChartSlide,
  MetricDashboard: MetricDashboardSlide,
  ComparisonSlide: ComparisonSlide2,
  MatrixSlide,
  QuoteSlide,
  ImageTextSlide,
  TimelineSlide,
  BulletSlide,
  ThankYouSlide
});

// src/templates/NewsletterTemplate.tsx
import { jsx as jsx171, jsxs as jsxs154 } from "react/jsx-runtime";
var NL_MAX_WIDTH = "640px";
var NL_BODY = {
  fontFamily: FONT_SERIF,
  fontSize: "11pt",
  fontWeight: 400,
  lineHeight: 1.6,
  color: COLORS.text
};
function NewsletterTemplateRoot({
  children
}) {
  return /* @__PURE__ */ jsxs154(
    "article",
    {
      style: {
        maxWidth: NL_MAX_WIDTH,
        margin: "0 auto",
        padding: "32px 24px",
        background: COLORS.surface,
        fontFamily: FONT_SERIF,
        fontSize: "11pt",
        lineHeight: 1.6,
        color: COLORS.text
      },
      children: [
        /* @__PURE__ */ jsx171("div", { className: "no-print", style: { position: "fixed", top: 20, right: 20, zIndex: 100 }, children: /* @__PURE__ */ jsx171(
          "button",
          {
            onClick: () => window.print(),
            style: {
              padding: "8px 16px",
              background: COLORS.textSecondary,
              color: COLORS.surface,
              border: "none",
              borderRadius: "4px",
              fontFamily: FONT_SANS,
              fontSize: "14px",
              cursor: "pointer"
            },
            children: "Print / Export PDF"
          }
        ) }),
        children
      ]
    }
  );
}
function Header2({
  logoUrl = "https://cdn.aaim.app/logos/aaim-master-grey.svg",
  title,
  issue,
  date
}) {
  const displayDate = date ?? formatDocumentDate();
  return /* @__PURE__ */ jsxs154("header", { style: { marginBottom: "32px" }, children: [
    /* @__PURE__ */ jsx171(
      "div",
      {
        style: {
          height: "3px",
          background: COLORS.accent,
          marginBottom: "20px"
        }
      }
    ),
    /* @__PURE__ */ jsxs154(
      "div",
      {
        style: {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        },
        children: [
          /* @__PURE__ */ jsxs154("div", { style: { display: "flex", alignItems: "center", gap: "16px" }, children: [
            /* @__PURE__ */ jsx171("img", { src: logoUrl, alt: "Aaim", style: { height: "24px" } }),
            /* @__PURE__ */ jsx171(
              "h1",
              {
                style: {
                  fontFamily: FONT_SERIF,
                  fontSize: "16pt",
                  fontWeight: 600,
                  letterSpacing: "-0.02em",
                  color: COLORS.text,
                  margin: 0
                },
                children: title
              }
            )
          ] }),
          /* @__PURE__ */ jsxs154("div", { style: { textAlign: "right" }, children: [
            issue && /* @__PURE__ */ jsx171(
              "div",
              {
                style: {
                  fontFamily: FONT_SANS,
                  fontSize: "9pt",
                  color: COLORS.textTertiary,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em"
                },
                children: issue
              }
            ),
            /* @__PURE__ */ jsx171(
              "div",
              {
                style: {
                  fontFamily: FONT_SANS,
                  fontSize: "9pt",
                  color: COLORS.textTertiary
                },
                children: displayDate
              }
            )
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsx171(
      "div",
      {
        style: {
          height: "0.5pt",
          background: COLORS.rule,
          marginTop: "16px"
        }
      }
    )
  ] });
}
function HeroArticle({
  headline,
  author,
  imageUrl,
  imageAlt,
  summary,
  readMoreUrl
}) {
  return /* @__PURE__ */ jsxs154("section", { style: { ...avoidBreakStyle(), marginBottom: "32px" }, children: [
    imageUrl && /* @__PURE__ */ jsx171(
      "div",
      {
        style: {
          width: "100%",
          height: "240px",
          overflow: "hidden",
          marginBottom: "16px",
          background: COLORS.recessed
        },
        children: /* @__PURE__ */ jsx171(
          "img",
          {
            src: imageUrl,
            alt: imageAlt ?? headline,
            style: {
              width: "100%",
              height: "100%",
              objectFit: "cover"
            }
          }
        )
      }
    ),
    /* @__PURE__ */ jsx171(
      "h2",
      {
        style: {
          fontFamily: FONT_SERIF,
          fontSize: "20pt",
          fontWeight: 600,
          lineHeight: 1.15,
          letterSpacing: "-0.02em",
          color: COLORS.text,
          margin: "0 0 8px 0"
        },
        children: headline
      }
    ),
    author && /* @__PURE__ */ jsxs154(
      "div",
      {
        style: {
          fontFamily: FONT_SANS,
          fontSize: "9pt",
          color: COLORS.textTertiary,
          marginBottom: "12px"
        },
        children: [
          "By ",
          author
        ]
      }
    ),
    /* @__PURE__ */ jsx171("p", { style: { ...NL_BODY, margin: "0 0 12px 0" }, children: summary }),
    readMoreUrl && /* @__PURE__ */ jsxs154(
      "span",
      {
        style: {
          fontFamily: FONT_SANS,
          fontSize: "10pt",
          fontWeight: 600,
          color: COLORS.accent
        },
        children: [
          "Read more: ",
          readMoreUrl
        ]
      }
    )
  ] });
}
function ArticleCard({
  headline,
  category,
  summary,
  author,
  readMoreUrl
}) {
  return /* @__PURE__ */ jsxs154(
    "section",
    {
      style: {
        ...avoidBreakStyle(),
        marginBottom: "24px",
        paddingBottom: "20px",
        borderBottom: `0.25pt solid ${COLORS.ruleLight}`
      },
      children: [
        category && /* @__PURE__ */ jsx171(
          "div",
          {
            style: {
              fontFamily: FONT_SANS,
              fontSize: "9pt",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.12em",
              color: COLORS.textTertiary,
              marginBottom: "4px"
            },
            children: category
          }
        ),
        /* @__PURE__ */ jsx171(
          "h3",
          {
            style: {
              fontFamily: FONT_SERIF,
              fontSize: "14pt",
              fontWeight: 600,
              lineHeight: 1.2,
              color: COLORS.text,
              margin: "0 0 8px 0"
            },
            children: headline
          }
        ),
        /* @__PURE__ */ jsx171("p", { style: { ...NL_BODY, fontSize: "10pt", margin: "0 0 8px 0", color: COLORS.textSecondary }, children: summary }),
        /* @__PURE__ */ jsxs154("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center" }, children: [
          author && /* @__PURE__ */ jsx171("span", { style: { fontFamily: FONT_SANS, fontSize: "9pt", color: COLORS.textTertiary }, children: author }),
          readMoreUrl && /* @__PURE__ */ jsx171(
            "span",
            {
              style: {
                fontFamily: FONT_SANS,
                fontSize: "9pt",
                fontWeight: 600,
                color: COLORS.accent
              },
              children: "Read more"
            }
          )
        ] })
      ]
    }
  );
}
function MetricHighlight2({ title = "Market Snapshot", metrics }) {
  return /* @__PURE__ */ jsxs154(
    "section",
    {
      style: {
        ...avoidBreakStyle(),
        background: COLORS.canvas,
        padding: "20px 24px",
        marginBottom: "24px"
      },
      children: [
        /* @__PURE__ */ jsx171(
          "h3",
          {
            style: {
              fontFamily: FONT_SANS,
              fontSize: "9pt",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.12em",
              color: COLORS.textTertiary,
              margin: "0 0 16px 0"
            },
            children: title
          }
        ),
        /* @__PURE__ */ jsx171(
          "div",
          {
            style: {
              display: "grid",
              gridTemplateColumns: `repeat(${Math.min(metrics.length, 4)}, 1fr)`,
              gap: "16px"
            },
            children: metrics.map((metric, idx) => {
              const changeColor = metric.trend === "up" ? COLORS.positive : metric.trend === "down" ? COLORS.negative : COLORS.textTertiary;
              return /* @__PURE__ */ jsxs154("div", { children: [
                /* @__PURE__ */ jsx171(
                  "div",
                  {
                    style: {
                      fontFamily: FONT_SERIF,
                      fontSize: "20pt",
                      fontWeight: 600,
                      lineHeight: 1.1,
                      color: COLORS.text,
                      letterSpacing: "-0.02em"
                    },
                    children: metric.value
                  }
                ),
                /* @__PURE__ */ jsx171(
                  "div",
                  {
                    style: {
                      fontFamily: FONT_SANS,
                      fontSize: "9pt",
                      color: COLORS.textSecondary,
                      marginTop: "4px"
                    },
                    children: metric.label
                  }
                ),
                metric.change && /* @__PURE__ */ jsx171(
                  "div",
                  {
                    style: {
                      fontFamily: FONT_SANS,
                      fontSize: "9pt",
                      color: changeColor,
                      marginTop: "2px"
                    },
                    children: metric.change
                  }
                )
              ] }, idx);
            })
          }
        )
      ]
    }
  );
}
function Divider() {
  return /* @__PURE__ */ jsx171(
    "div",
    {
      style: {
        height: "0.5pt",
        background: COLORS.ruleLight,
        margin: "8px 0"
      }
    }
  );
}
function Callout2({ variant = "info", title, children }) {
  const variantStyles = {
    info: { borderColor: COLORS.rule },
    action: { borderColor: COLORS.accent },
    event: { borderColor: COLORS.positive }
  };
  const config = variantStyles[variant];
  return /* @__PURE__ */ jsxs154(
    "div",
    {
      style: {
        ...avoidBreakStyle(),
        padding: "16px 16px 16px 20px",
        borderLeft: `3pt solid ${config.borderColor}`,
        marginBottom: "24px",
        background: COLORS.canvas
      },
      children: [
        /* @__PURE__ */ jsx171(
          "h4",
          {
            style: {
              fontFamily: FONT_SANS,
              fontSize: "10pt",
              fontWeight: 600,
              color: COLORS.text,
              margin: "0 0 8px 0"
            },
            children: title
          }
        ),
        /* @__PURE__ */ jsx171(
          "div",
          {
            style: {
              ...NL_BODY,
              fontSize: "10pt",
              color: COLORS.textSecondary
            },
            children
          }
        )
      ]
    }
  );
}
function Footer3({
  companyName = "Aaim, Inc.",
  address = "99 Wall Street, Suite 1610, New York, NY 10005",
  unsubscribeText = "You are receiving this email because you are a registered member of the Aaim platform.",
  websiteUrl = "aaim.com"
}) {
  return /* @__PURE__ */ jsxs154(
    "footer",
    {
      style: {
        marginTop: "32px",
        paddingTop: "16px",
        borderTop: `0.5pt solid ${COLORS.rule}`,
        fontFamily: FONT_SANS,
        fontSize: "9pt",
        color: COLORS.textTertiary,
        textAlign: "center"
      },
      children: [
        /* @__PURE__ */ jsx171("div", { style: { marginBottom: "8px", fontWeight: 500 }, children: companyName }),
        /* @__PURE__ */ jsx171("div", { style: { marginBottom: "8px" }, children: address }),
        /* @__PURE__ */ jsx171("div", { style: { marginBottom: "8px" }, children: websiteUrl }),
        /* @__PURE__ */ jsx171("div", { style: { fontSize: "8pt", lineHeight: 1.4 }, children: unsubscribeText })
      ]
    }
  );
}
function NLPageBreak() {
  return /* @__PURE__ */ jsx171("div", { style: pageBreakStyle() });
}
var NewsletterTemplate = Object.assign(NewsletterTemplateRoot, {
  Header: Header2,
  HeroArticle,
  ArticleCard,
  MetricHighlight: MetricHighlight2,
  Divider,
  Callout: Callout2,
  Footer: Footer3,
  PageBreak: NLPageBreak
});

// src/portal/IntegrationConfigForm.tsx
import { useState as useState35, useCallback as useCallback27 } from "react";
import { clsx as clsx23 } from "clsx";
import { jsx as jsx172, jsxs as jsxs155 } from "react/jsx-runtime";
function IntegrationConfigForm({
  sections,
  values,
  onChange,
  onSubmit,
  onCancel,
  submitting = false,
  disabled = false,
  submitLabel = "Save Configuration",
  cancelLabel = "Cancel",
  extraActions,
  className
}) {
  const [errors, setErrors] = useState35({});
  const [touched, setTouched] = useState35({});
  const getFieldValue = useCallback27(
    (field) => {
      const val = values[field.id];
      if (val !== void 0) return val;
      if (field.defaultValue !== void 0) return field.defaultValue;
      if (field.type === "switch") return false;
      if (field.type === "number") return 0;
      return "";
    },
    [values]
  );
  const handleFieldChange = useCallback27(
    (fieldId, value) => {
      onChange({ ...values, [fieldId]: value });
      if (errors[fieldId]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[fieldId];
          return next;
        });
      }
    },
    [values, onChange, errors]
  );
  const handleBlur = useCallback27(
    (field) => {
      setTouched((prev) => ({ ...prev, [field.id]: true }));
      if (field.validate) {
        const error = field.validate(values[field.id]);
        if (error) {
          setErrors((prev) => ({ ...prev, [field.id]: error }));
        }
      } else if (field.required) {
        const val = values[field.id];
        if (val === void 0 || val === "" || val === null) {
          setErrors((prev) => ({ ...prev, [field.id]: `${field.label} is required` }));
        }
      }
    },
    [values]
  );
  const validateAll = useCallback27(() => {
    const newErrors = {};
    for (const section of sections) {
      for (const field of section.fields) {
        if (field.validate) {
          const error = field.validate(values[field.id]);
          if (error) newErrors[field.id] = error;
        } else if (field.required) {
          const val = values[field.id];
          if (val === void 0 || val === "" || val === null) {
            newErrors[field.id] = `${field.label} is required`;
          }
        }
      }
    }
    setErrors(newErrors);
    const allTouched = {};
    for (const section of sections) {
      for (const field of section.fields) {
        allTouched[field.id] = true;
      }
    }
    setTouched(allTouched);
    return Object.keys(newErrors).length === 0;
  }, [sections, values]);
  const handleSubmit = useCallback27(
    (e) => {
      e.preventDefault();
      if (validateAll()) {
        onSubmit(values);
      }
    },
    [validateAll, onSubmit, values]
  );
  const renderField = (field) => {
    const value = getFieldValue(field);
    const error = touched[field.id] ? errors[field.id] : void 0;
    const isDisabled = disabled || field.readOnly || submitting;
    switch (field.type) {
      case "switch":
        return /* @__PURE__ */ jsxs155("div", { className: "flex items-center justify-between py-2", children: [
          /* @__PURE__ */ jsxs155("div", { children: [
            /* @__PURE__ */ jsx172(FormLabel, { children: field.label }),
            field.helperText && /* @__PURE__ */ jsx172(FormHelperText, { children: field.helperText })
          ] }),
          /* @__PURE__ */ jsx172(
            Switch,
            {
              isSelected: value,
              onChange: (checked) => handleFieldChange(field.id, checked),
              isDisabled,
              "aria-label": field.label
            }
          )
        ] });
      case "select":
        return /* @__PURE__ */ jsxs155("div", { children: [
          /* @__PURE__ */ jsxs155(FormLabel, { children: [
            field.label,
            field.required ? " *" : ""
          ] }),
          /* @__PURE__ */ jsx172(
            Select,
            {
              options: field.options ?? [],
              value: String(value),
              onChange: (e) => handleFieldChange(field.id, e.target.value),
              disabled: isDisabled,
              "aria-label": field.label
            }
          ),
          field.helperText && !error && /* @__PURE__ */ jsx172(FormHelperText, { children: field.helperText }),
          error && /* @__PURE__ */ jsx172(FormHelperText, { className: "text-error", children: error })
        ] });
      case "textarea":
        return /* @__PURE__ */ jsx172("div", { children: /* @__PURE__ */ jsx172(
          Textarea,
          {
            label: field.label + (field.required ? " *" : ""),
            value: String(value),
            onChange: (val) => handleFieldChange(field.id, val),
            onBlur: () => handleBlur(field),
            placeholder: field.placeholder,
            disabled: isDisabled,
            helperText: !error ? field.helperText : void 0,
            error
          }
        ) });
      default:
        return /* @__PURE__ */ jsxs155("div", { children: [
          /* @__PURE__ */ jsxs155(FormLabel, { children: [
            field.label,
            field.required ? " *" : ""
          ] }),
          /* @__PURE__ */ jsx172(
            Input,
            {
              type: field.type === "number" ? "number" : field.type,
              value: String(value),
              onChange: (e) => handleFieldChange(
                field.id,
                field.type === "number" ? Number(e.target.value) : e.target.value
              ),
              onBlur: () => handleBlur(field),
              placeholder: field.placeholder,
              disabled: isDisabled,
              "aria-label": field.label
            }
          ),
          field.helperText && !error && /* @__PURE__ */ jsx172(FormHelperText, { children: field.helperText }),
          error && /* @__PURE__ */ jsx172(FormHelperText, { className: "text-error", children: error })
        ] });
    }
  };
  return /* @__PURE__ */ jsxs155(
    "form",
    {
      onSubmit: handleSubmit,
      className: clsx23("space-y-8", className),
      noValidate: true,
      children: [
        sections.map((section) => /* @__PURE__ */ jsxs155(Card, { className: "p-6", children: [
          /* @__PURE__ */ jsxs155("div", { className: "mb-6", children: [
            /* @__PURE__ */ jsx172(Heading, { level: 3, size: "card", children: section.title }),
            section.description && /* @__PURE__ */ jsx172("p", { className: "mt-1 text-body-sm font-sans text-secondary leading-relaxed", children: section.description })
          ] }),
          /* @__PURE__ */ jsx172("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5", children: section.fields.map((field) => /* @__PURE__ */ jsx172(
            "div",
            {
              className: clsx23(
                field.colSpan === 2 && "md:col-span-2",
                field.type === "switch" && "md:col-span-2",
                field.type === "textarea" && "md:col-span-2"
              ),
              children: renderField(field)
            },
            field.id
          )) })
        ] }, section.id)),
        /* @__PURE__ */ jsxs155("div", { className: "flex items-center justify-end gap-3 pt-2", children: [
          onCancel && /* @__PURE__ */ jsx172(
            Button,
            {
              type: "button",
              variant: "ghost",
              onClick: onCancel,
              disabled: submitting,
              children: cancelLabel
            }
          ),
          extraActions,
          /* @__PURE__ */ jsx172(Button, { type: "submit", variant: "primary", disabled: submitting || disabled, children: submitting ? "Saving..." : submitLabel })
        ] })
      ]
    }
  );
}

// src/portal/DashboardGrid.tsx
import { clsx as clsx24 } from "clsx";
import { jsx as jsx173, jsxs as jsxs156 } from "react/jsx-runtime";
var COLUMN_CLASSES = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
};
var GAP_CLASSES = {
  compact: "gap-3",
  default: "gap-4",
  spacious: "gap-6"
};
var COL_SPAN_CLASSES = {
  1: "",
  2: "sm:col-span-2",
  3: "sm:col-span-2 lg:col-span-3",
  4: "sm:col-span-2 lg:col-span-4"
};
var ROW_SPAN_CLASSES = {
  1: "",
  2: "row-span-2"
};
function DashboardWidget({
  children,
  colSpan = 1,
  rowSpan = 1,
  className
}) {
  return /* @__PURE__ */ jsx173(
    Card,
    {
      className: clsx24(
        "p-5",
        COL_SPAN_CLASSES[colSpan],
        ROW_SPAN_CLASSES[rowSpan],
        className
      ),
      children
    }
  );
}
function DashboardGrid({
  columns = 4,
  gap = "default",
  children,
  loading = false,
  loadingCount,
  className
}) {
  const skeletonCount = loadingCount ?? columns;
  if (loading) {
    return /* @__PURE__ */ jsx173(
      "div",
      {
        className: clsx24(
          "grid",
          COLUMN_CLASSES[columns],
          GAP_CLASSES[gap],
          className
        ),
        children: Array.from({ length: skeletonCount }).map((_, i) => /* @__PURE__ */ jsxs156(Card, { className: "p-5", children: [
          /* @__PURE__ */ jsx173(Skeleton, { className: "h-4 w-20 mb-3" }),
          /* @__PURE__ */ jsx173(Skeleton, { className: "h-8 w-28 mb-2" }),
          /* @__PURE__ */ jsx173(Skeleton, { className: "h-3 w-16" })
        ] }, i))
      }
    );
  }
  return /* @__PURE__ */ jsx173(
    "div",
    {
      className: clsx24(
        "grid",
        COLUMN_CLASSES[columns],
        GAP_CLASSES[gap],
        className
      ),
      children
    }
  );
}

// src/portal/DataCRUDTemplate.tsx
import { useState as useState36, useCallback as useCallback28 } from "react";
import { clsx as clsx25 } from "clsx";
import { jsx as jsx174, jsxs as jsxs157 } from "react/jsx-runtime";
function DataCRUDTemplate({
  title,
  subtitle,
  columns,
  data,
  loading = false,
  error = null,
  onRetry,
  filters,
  filterValues,
  onFilterChange,
  pagination,
  onPageChange,
  selectedRow = null,
  onRowSelect,
  onDetailClose,
  renderDetail,
  detailActions,
  onCreateClick,
  createLabel = "Create",
  striped = true,
  rowKey,
  emptyMessage,
  className
}) {
  const [internalSearch, setInternalSearch] = useState36("");
  const handleSearchChange = useCallback28(
    (value) => {
      setInternalSearch(value);
      const searchFilter2 = filters?.find((f) => f.type === "search");
      if (searchFilter2 && onFilterChange) {
        onFilterChange(searchFilter2.id, value);
      }
    },
    [filters, onFilterChange]
  );
  const searchFilter = filters?.find((f) => f.type === "search");
  const searchValue = searchFilter && filterValues ? filterValues[searchFilter.id] ?? internalSearch : internalSearch;
  const selectFilters = filters?.filter((f) => f.type === "select") ?? [];
  const totalPages = pagination ? Math.max(1, Math.ceil(pagination.total / pagination.pageSize)) : 1;
  if (error) {
    return /* @__PURE__ */ jsx174("div", { className: clsx25("space-y-6", className), children: /* @__PURE__ */ jsxs157(Card, { className: "p-8 text-center", children: [
      /* @__PURE__ */ jsx174("p", { className: "text-body-sm font-sans text-error mb-4", children: error }),
      onRetry && /* @__PURE__ */ jsx174(Button, { variant: "primary", onClick: onRetry, children: "Retry" })
    ] }) });
  }
  if (loading) {
    return /* @__PURE__ */ jsxs157("div", { className: clsx25("space-y-6", className), children: [
      /* @__PURE__ */ jsxs157("div", { className: "flex items-center justify-between", children: [
        /* @__PURE__ */ jsx174(Skeleton, { className: "h-7 w-48" }),
        onCreateClick && /* @__PURE__ */ jsx174(Skeleton, { className: "h-10 w-28" })
      ] }),
      filters && filters.length > 0 && /* @__PURE__ */ jsx174(Card, { className: "p-4", children: /* @__PURE__ */ jsxs157("div", { className: "flex gap-4", children: [
        /* @__PURE__ */ jsx174(Skeleton, { className: "h-10 flex-1" }),
        selectFilters.map((f) => /* @__PURE__ */ jsx174(Skeleton, { className: "h-10 w-40" }, f.id))
      ] }) }),
      /* @__PURE__ */ jsx174(Card, { children: /* @__PURE__ */ jsx174(Skeleton, { className: "h-64 w-full" }) })
    ] });
  }
  return /* @__PURE__ */ jsxs157("div", { className: clsx25("space-y-6", className), children: [
    /* @__PURE__ */ jsxs157("div", { className: "flex items-start justify-between", children: [
      /* @__PURE__ */ jsxs157("div", { children: [
        /* @__PURE__ */ jsx174(Heading, { level: 1, size: "subsection", children: title }),
        subtitle && /* @__PURE__ */ jsx174("p", { className: "mt-1 text-body-sm font-sans text-tertiary", children: subtitle })
      ] }),
      onCreateClick && /* @__PURE__ */ jsx174(Button, { variant: "primary", onClick: onCreateClick, children: createLabel })
    ] }),
    filters && filters.length > 0 && /* @__PURE__ */ jsx174(Card, { className: "p-4", children: /* @__PURE__ */ jsxs157("div", { className: "flex gap-4 items-end", children: [
      searchFilter && /* @__PURE__ */ jsx174("div", { className: "flex-1", children: /* @__PURE__ */ jsx174(
        Input,
        {
          value: searchValue,
          onChange: (e) => handleSearchChange(e.target.value),
          placeholder: searchFilter.placeholder ?? "Search...",
          "aria-label": searchFilter.label
        }
      ) }),
      selectFilters.map((filter) => /* @__PURE__ */ jsx174("div", { className: "w-48", children: /* @__PURE__ */ jsx174(
        Select,
        {
          options: [
            { label: `All ${filter.label}`, value: "" },
            ...filter.options ?? []
          ],
          value: filterValues?.[filter.id] ?? "",
          onChange: (e) => onFilterChange?.(filter.id, e.target.value),
          "aria-label": filter.label
        }
      ) }, filter.id))
    ] }) }),
    /* @__PURE__ */ jsxs157(
      "div",
      {
        className: clsx25(
          selectedRow && renderDetail ? "grid grid-cols-1 lg:grid-cols-3 gap-6" : ""
        ),
        children: [
          /* @__PURE__ */ jsxs157("div", { className: selectedRow && renderDetail ? "lg:col-span-2" : "", children: [
            /* @__PURE__ */ jsx174(
              DataTable,
              {
                columns,
                data,
                striped,
                hoverable: !!onRowSelect,
                rowKey,
                emptyMessage
              }
            ),
            pagination && totalPages > 1 && /* @__PURE__ */ jsxs157("div", { className: "flex items-center justify-between mt-4 px-1", children: [
              /* @__PURE__ */ jsxs157("p", { className: "text-body-sm font-sans text-tertiary", children: [
                pagination.total,
                " total items"
              ] }),
              /* @__PURE__ */ jsxs157("div", { className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsx174(
                  Button,
                  {
                    variant: "ghost",
                    onClick: () => onPageChange?.(pagination.page - 1),
                    disabled: pagination.page <= 1,
                    children: "Previous"
                  }
                ),
                /* @__PURE__ */ jsxs157("span", { className: "text-body-sm font-sans text-secondary tabular-nums", children: [
                  "Page ",
                  pagination.page,
                  " of ",
                  totalPages
                ] }),
                /* @__PURE__ */ jsx174(
                  Button,
                  {
                    variant: "ghost",
                    onClick: () => onPageChange?.(pagination.page + 1),
                    disabled: pagination.page >= totalPages,
                    children: "Next"
                  }
                )
              ] })
            ] })
          ] }),
          selectedRow && renderDetail && /* @__PURE__ */ jsxs157(Card, { className: "p-5 lg:col-span-1", children: [
            (detailActions || onDetailClose) && /* @__PURE__ */ jsxs157("div", { className: "flex items-center justify-between mb-4 pb-4 border-b border-recessed", children: [
              /* @__PURE__ */ jsx174("div", { className: "flex items-center gap-2", children: detailActions?.map((action) => /* @__PURE__ */ jsx174(
                Button,
                {
                  variant: action.destructive ? "ghost" : action.variant ?? "ghost",
                  onClick: () => action.onClick(selectedRow),
                  disabled: action.disabled,
                  className: action.destructive ? "text-error hover:text-error" : "",
                  children: action.label
                },
                action.id
              )) }),
              onDetailClose && /* @__PURE__ */ jsx174(
                Button,
                {
                  variant: "ghost",
                  onClick: onDetailClose,
                  "aria-label": "Close detail pane",
                  children: "Close"
                }
              )
            ] }),
            renderDetail(selectedRow)
          ] })
        ]
      }
    )
  ] });
}

// src/portal/RoleBasedNav.tsx
import { useState as useState37, useCallback as useCallback29, useMemo as useMemo18, useEffect as useEffect24 } from "react";
import { clsx as clsx26 } from "clsx";
import { jsx as jsx175, jsxs as jsxs158 } from "react/jsx-runtime";
var DENSITY_CLASSES = {
  compact: { item: "py-1.5 px-3 text-body-sm", group: "mb-3", icon: "w-4 h-4" },
  default: { item: "py-2 px-3 text-body-sm", group: "mb-4", icon: "w-4 h-4" },
  spacious: { item: "py-2.5 px-4 text-body-md", group: "mb-5", icon: "w-5 h-5" }
};
function normalizeTargets(literal) {
  if (literal == null) return [];
  if (typeof literal === "string") return [literal.toLowerCase()];
  if (!Array.isArray(literal)) return [];
  const out = [];
  for (const value of literal) {
    if (typeof value === "string") out.push(value.toLowerCase());
  }
  return out;
}
function hasAccess(permissions, roles, requiredPermission, requiredRole) {
  const permTargets = normalizeTargets(requiredPermission);
  if (permTargets.length > 0) {
    const has = permissions.some(
      (p) => typeof p === "string" && permTargets.includes(p.toLowerCase())
    );
    if (!has) return false;
  }
  const roleTargets = normalizeTargets(requiredRole);
  if (roleTargets.length > 0) {
    const has = roles.some(
      (r) => typeof r === "string" && roleTargets.includes(r.toLowerCase())
    );
    if (!has) return false;
  }
  return true;
}
function containsItem(items, id) {
  return items.some(
    (item) => item.id === id || (item.children ? containsItem(item.children, id) : false)
  );
}
function NavItemInternal({
  item,
  activeItemId,
  onSelect,
  permissions,
  roles,
  density,
  depth
}) {
  const childActive = item.children ? containsItem(item.children, activeItemId) : false;
  const [expanded, setExpanded] = useState37(childActive);
  useEffect24(() => {
    if (childActive) setExpanded(true);
  }, [childActive, activeItemId]);
  if (!hasAccess(permissions, roles, item.requiredPermission, item.requiredRole)) {
    return null;
  }
  const Icon = item.icon;
  const isActive = item.id === activeItemId;
  const hasChildren = item.children && item.children.length > 0;
  const classes = DENSITY_CLASSES[density];
  const handleClick = () => {
    if (item.disabled) return;
    if (hasChildren) {
      setExpanded((prev) => !prev);
    } else {
      onSelect(item.id);
    }
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };
  return /* @__PURE__ */ jsxs158("li", { children: [
    /* @__PURE__ */ jsxs158(
      "div",
      {
        role: "treeitem",
        tabIndex: item.disabled ? -1 : 0,
        "aria-selected": isActive,
        "aria-current": isActive ? "page" : void 0,
        "aria-expanded": hasChildren ? expanded : void 0,
        "aria-disabled": item.disabled,
        onClick: handleClick,
        onKeyDown: handleKeyDown,
        className: clsx26(
          classes.item,
          "flex items-center gap-2.5 rounded-sm cursor-pointer select-none",
          "transition-colors font-sans",
          depth > 0 && "ml-6",
          isActive ? "bg-accent/10 text-accent font-medium" : "text-secondary hover:bg-elevated hover:text-primary",
          item.disabled && "opacity-50 cursor-default hover:bg-transparent hover:text-secondary"
        ),
        children: [
          Icon && /* @__PURE__ */ jsx175(
            Icon,
            {
              className: clsx26(
                classes.icon,
                "flex-shrink-0",
                isActive ? "text-accent" : "text-tertiary"
              )
            }
          ),
          /* @__PURE__ */ jsx175("span", { className: "flex-1 truncate", children: item.label }),
          item.badgeNode ? item.badgeNode : item.badge && /* @__PURE__ */ jsx175("span", { className: "px-1.5 py-0.5 text-caption-sm font-sans font-medium rounded bg-recessed text-tertiary", children: item.badge }),
          hasChildren && /* @__PURE__ */ jsx175(
            "svg",
            {
              className: clsx26(
                "w-3.5 h-3.5 text-tertiary transition-transform",
                expanded && "rotate-90"
              ),
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24",
              "aria-hidden": "true",
              children: /* @__PURE__ */ jsx175("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M9 5l7 7-7 7" })
            }
          )
        ]
      }
    ),
    hasChildren && expanded && /* @__PURE__ */ jsx175("ul", { role: "group", className: "mt-0.5", children: item.children.map((child) => /* @__PURE__ */ jsx175(
      NavItemInternal,
      {
        item: child,
        activeItemId,
        onSelect,
        permissions,
        roles,
        density,
        depth: depth + 1
      },
      child.id
    )) })
  ] });
}
function NavGroup({ group, activeItemId, onSelect, permissions, roles, density, classes }) {
  const groupActive = containsItem(group.items, activeItemId);
  const [isOpen, setIsOpen] = useState37(groupActive);
  useEffect24(() => {
    if (groupActive) setIsOpen(true);
  }, [groupActive, activeItemId]);
  return /* @__PURE__ */ jsxs158("div", { className: classes.group, children: [
    /* @__PURE__ */ jsxs158(
      "button",
      {
        type: "button",
        onClick: () => setIsOpen((prev) => !prev),
        className: "w-full flex items-center justify-between px-3 mb-1.5 group cursor-pointer",
        "aria-expanded": isOpen,
        children: [
          /* @__PURE__ */ jsx175("span", { className: "text-label-sm font-sans font-semibold uppercase tracking-widest text-tertiary group-hover:text-secondary transition-colors", children: group.label }),
          /* @__PURE__ */ jsx175(
            "svg",
            {
              className: clsx26(
                "w-3 h-3 text-tertiary transition-transform",
                isOpen && "rotate-90"
              ),
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24",
              "aria-hidden": "true",
              children: /* @__PURE__ */ jsx175("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M9 5l7 7-7 7" })
            }
          )
        ]
      }
    ),
    isOpen && /* @__PURE__ */ jsx175("ul", { role: "group", children: group.items.map((item) => /* @__PURE__ */ jsx175(
      NavItemInternal,
      {
        item,
        activeItemId,
        onSelect,
        permissions,
        roles,
        density,
        depth: 0
      },
      item.id
    )) })
  ] });
}
function RoleBasedNav({
  groups,
  activeItemId,
  onItemSelect,
  permissions = [],
  roles = [],
  density = "default",
  collapsible = false,
  header,
  footer,
  className
}) {
  const [collapsed, setCollapsed] = useState37(false);
  const handleSelect = useCallback29(
    (id) => {
      onItemSelect(id);
    },
    [onItemSelect]
  );
  const visibleGroups = useMemo18(() => {
    return groups.filter((group) => {
      if (!hasAccess(permissions, roles, group.requiredPermission, group.requiredRole)) {
        return false;
      }
      const hasVisibleItem = (items) => items.some(
        (item) => hasAccess(permissions, roles, item.requiredPermission, item.requiredRole) || item.children && hasVisibleItem(item.children)
      );
      return hasVisibleItem(group.items);
    });
  }, [groups, permissions, roles]);
  const classes = DENSITY_CLASSES[density];
  if (collapsible && collapsed) {
    return /* @__PURE__ */ jsx175(
      "nav",
      {
        className: clsx26("flex flex-col", className),
        "aria-label": "Navigation",
        children: /* @__PURE__ */ jsx175(
          "button",
          {
            onClick: () => setCollapsed(false),
            className: "p-3 text-tertiary hover:text-primary transition-colors self-center",
            "aria-label": "Expand navigation",
            children: /* @__PURE__ */ jsx175("svg", { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx175("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M4 6h16M4 12h16M4 18h16" }) })
          }
        )
      }
    );
  }
  return /* @__PURE__ */ jsxs158(
    "nav",
    {
      className: clsx26("flex flex-col h-full", className),
      "aria-label": "Navigation",
      children: [
        header && /* @__PURE__ */ jsx175("div", { className: "flex-shrink-0", children: header }),
        collapsible && /* @__PURE__ */ jsx175(
          "button",
          {
            onClick: () => setCollapsed(true),
            className: "p-2 text-tertiary hover:text-primary transition-colors self-end mr-2 mb-1",
            "aria-label": "Collapse navigation",
            children: /* @__PURE__ */ jsx175("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx175("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M11 19l-7-7 7-7m8 14l-7-7 7-7" }) })
          }
        ),
        /* @__PURE__ */ jsxs158("div", { className: "flex-1 overflow-y-auto px-2 pt-4 pb-2", role: "tree", children: [
          visibleGroups.map((group) => /* @__PURE__ */ jsx175(
            NavGroup,
            {
              group,
              activeItemId,
              onSelect: handleSelect,
              permissions,
              roles,
              density,
              classes
            },
            group.id
          )),
          visibleGroups.length === 0 && /* @__PURE__ */ jsx175("div", { className: "px-3 py-8 text-center", children: /* @__PURE__ */ jsx175("p", { className: "text-body-sm font-sans text-tertiary", children: "No navigation items available for your role" }) })
        ] }),
        footer && /* @__PURE__ */ jsx175("div", { className: "flex-shrink-0", children: footer })
      ]
    }
  );
}

// src/portal/NavBadge.tsx
import { jsx as jsx176 } from "react/jsx-runtime";
function NavBadge2({ pendingCount, overSlaCount }) {
  if (pendingCount <= 0) {
    return null;
  }
  const hasOverSla = overSlaCount > 0;
  const ariaLabel = hasOverSla ? `${pendingCount} pending, ${overSlaCount} over SLA` : `${pendingCount} pending`;
  return /* @__PURE__ */ jsx176("span", { role: "status", "aria-label": ariaLabel, children: /* @__PURE__ */ jsx176(Badge, { variant: hasOverSla ? "error" : "neutral", size: "small", children: pendingCount }) });
}
export {
  Accordion,
  AccordionItem,
  ActionBar,
  Alert,
  AlertBanner,
  AppHeader,
  AppShell,
  AppSidebar,
  AreaChart,
  AspectRatio,
  AssetClassTaxonomy,
  BackLink,
  Badge,
  BankersReport,
  BarChart,
  BeforeAfter,
  Blockquote,
  BlogArticleHeader,
  BlogArticleHeaderCompact,
  BodyText,
  BoxPlot,
  BreadcrumbSeparator,
  Breadcrumbs,
  BubbleChart,
  BuildVersion,
  BulletGraph,
  Button,
  ButtonGroup,
  CTA,
  CTASection,
  CalendarHeatmap,
  Card,
  Checkbox,
  CircularProgress,
  Combobox,
  CommandPalette,
  CommandPaletteTrigger,
  ComparisonBox,
  ComparisonBoxPair,
  ComparisonTable,
  CompetitivePositioning,
  ContentCard,
  CorrelationMatrix,
  DashboardGrid,
  DashboardWidget,
  DataCRUDTemplate,
  DataGrid,
  DataTable,
  DataToolbar,
  DatePicker,
  DateRangePicker,
  DecisionNode,
  DeveloperDocumentation,
  Dialog,
  DonutChart,
  DualStatement,
  EditorialQuote,
  EmailTemplate,
  EmailTemplateCompact,
  EmptyState,
  EnhancedHeroSection,
  FAQSection,
  FeaturesSection,
  FilterBar,
  Footer,
  FormHelperText,
  FormLabel,
  FormattedText,
  GaugeCluster,
  GroupNode,
  GroupedBarChart,
  Heading,
  HeadlineWithEmphasis,
  HeroManifestoTransition,
  HeroStatsSidebar,
  HorizonChart,
  HorizontalBarChart,
  HowWeWorkInfographic,
  Input,
  IntegrationArchitecture,
  IntegrationConfigForm,
  IntegrationSection,
  InvestorDeck,
  LAYOUT_SPACING,
  LeadText,
  LegalContract,
  LineChart,
  LogoCloud,
  METRIC_LABEL_CLASSES,
  METRIC_READ_CAPTIONS,
  MONEY_UNAVAILABLE,
  ManifestoSection,
  MarkdocContent,
  MetricCard,
  MetricCardGrid,
  MetricComparison,
  MetricDelta,
  MetricDisplay,
  MetricGauge,
  MetricGlance,
  MetricHighlight,
  MetricIndex,
  MetricLedger,
  MetricLedgerBlock,
  MetricQuote,
  MetricRow,
  MetricShowcase,
  MetricStrip,
  MetricsBar,
  MetricsBlock,
  NEWSLETTER_HONEYPOT_FIELD,
  NODE_DIMENSIONS,
  NavBadge2 as NavBadge,
  NewsletterForm,
  NotificationCenter,
  NotificationPanel,
  OrgSwitcher,
  OrgSwitcherDialog,
  PORTAL_SIDEBAR_WIDTH,
  PageBreak,
  PageHeader,
  Paragraph,
  ParallelCoordinates,
  Popover,
  PortalFrame,
  PresentationTemplates,
  PresetButtons,
  PrintableDocument,
  PrintableDocumentV2,
  ProcessTimeline,
  Progress,
  PullQuote,
  QuoteCarousel,
  RadarChart,
  RadioGroup,
  RegulatoryFramework,
  RegulatoryHeatMap,
  RegulatoryReport,
  RegulatoryTimeline,
  ReportTemplates,
  RiskGauge,
  RiskHeatMap,
  RoleBasedNav,
  STATUS_BORDER_CLASSES,
  STATUS_DOT_CLASSES,
  STATUS_LABELS2 as STATUS_LABELS,
  STATUS_STROKE_COLORS,
  SankeyDiagram,
  ScatterPlot,
  Section,
  SectionHeading,
  SectionLabel,
  Select,
  Separator,
  ShareButton,
  Sheet,
  ShortcutHelpOverlay,
  Skeleton,
  SkeletonAvatar,
  SkeletonCard,
  SkeletonTable,
  SkeletonText,
  Slider,
  SlopeChart,
  SmallMultiplesGrid,
  SocialAnnouncementBanner,
  SocialArticleCard,
  SocialArticleHeroCard,
  SocialMetricCard,
  SocialQuoteCard,
  Source,
  SparklineTable,
  SplitFeature,
  SplitFeatureStack,
  SplitPane,
  StatementBlock,
  StatusBadge,
  StrategicAnalysis,
  Switch,
  THEME_STORAGE_KEY,
  TabPanel,
  Table,
  TableLink,
  TableScroll,
  Tabs,
  Tag,
  Testimonials,
  Textarea,
  ThemeToggle,
  Timeline,
  TimelineChart,
  Tooltip,
  TooltipCard,
  TreeMap,
  TreeNav,
  TreeNavDivider,
  TreeNavItem,
  TreeNavSection,
  UseCaseSection,
  UserProfileMenu,
  VisuallyHidden,
  WaterfallChart,
  WealthFlowDiagram,
  WealthTransferVisualization,
  WebsiteNav,
  WhitepaperHeader,
  WhitepaperHeaderCompact,
  WorkflowCanvas,
  WorkflowControls,
  WorkflowEdge,
  WorkflowNode,
  applyStoredTheme,
  applyTheme,
  formatCalendarDate,
  formatDateTime,
  formatEventTime,
  formatUsd,
  formatUsdCompact,
  getCurrentTheme,
  getStoredTheme,
  getTabIds,
  prefersDarkScheme,
  resolveTheme,
  setTheme,
  storeTheme,
  subscribeToSystemTheme,
  toggleTheme,
  useTreeNav,
  useTreeNavLevel,
  useWorkflowLayout
};
